import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * §37.1 — Arabic-first support with full RTL.
 *
 * English is the default; Arabic flips `dir` on <html>, which is what drives every
 * logical-property utility in the layout. The dictionary is flat and typed, so a
 * missing key is a compile error rather than a blank string in production.
 */

export type Locale = 'en' | 'ar';

const en = {
  // Shell
  'app.name': 'Viganium System',
  'app.tagline': 'Operations console',
  'nav.dashboard': 'Dashboard',
  'nav.projects': 'Projects',
  'nav.tasks': 'Tasks',
  'nav.kanban': 'Kanban',
  'nav.tickets': 'Tickets',
  'nav.qTasks': 'Q Tasks',
  'nav.shifts': 'Shifts',
  'nav.mySchedule': 'My Schedule',
  'nav.departments': 'Departments',
  'nav.employees': 'Employees',
  'nav.payroll': 'Payroll',
  'nav.clients': 'Clients',
  'nav.expenses': 'Expenses',
  'nav.invoices': 'Invoices',
  'nav.payments': 'Payments',
  'nav.dataMigration': 'Data migration',
  'nav.groupClients': 'Commercial',
  'nav.budgets': 'Budgets',
  'nav.violations': 'Violations & Penalties',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'nav.auditLog': 'Audit Log',
  'nav.groupWork': 'Work',
  'nav.groupWorkforce': 'Workforce',
  'nav.groupFinance': 'Finance',
  'nav.groupAdmin': 'Administration',

  // Common
  'common.search': 'Search',
  'common.searchPlaceholder': 'Search clients, tickets, projects, tasks…',
  'common.filters': 'Filters',
  'common.clear': 'Clear',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.create': 'Create',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'theme.toLight': 'Switch to light theme',
  'theme.toDark': 'Switch to dark theme',
  'common.confirm': 'Confirm',
  'common.apply': 'Apply',
  'common.loading': 'Loading…',
  'common.noResults': 'Nothing to show',
  'common.notFoundHint': 'This page does not exist, or it is not yours to see.',
  'common.noResultsHint': 'Try widening the filters.',
  'common.error': 'Something went wrong',
  'common.retry': 'Retry',
  'common.all': 'All',
  'common.none': 'None',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.actions': 'Actions',
  'common.status': 'Status',
  'common.type': 'Type',
  'common.priority': 'Priority',
  'common.department': 'Department',
  'common.project': 'Project',
  'common.assignee': 'Assignee',
  'common.createdAt': 'Created',
  'common.updatedAt': 'Updated',
  'common.dueDate': 'Due',
  'common.amount': 'Amount',
  'common.currency': 'Currency',
  'common.date': 'Date',
  'common.description': 'Description',
  'common.notes': 'Notes',
  'common.reason': 'Reason',
  'common.total': 'Total',
  'common.name': 'Name',
  'common.nameTooLong': 'Keep it to {max} characters — this is {count}.',
  'common.code': 'Code',
  'common.email': 'Email',
  'common.phone': 'Phone',
  'common.role': 'Role',
  'common.of': 'of',
  'common.page': 'Page',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.general': 'General',
  'common.unassigned': 'Unassigned',
  'common.required': 'Required',
  'common.optional': 'optional',
  'common.back': 'Back',
  'common.view': 'View',
  'common.refresh': 'Refresh',
  'common.export': 'Export',
  'common.from': 'From',
  'common.to': 'To',
  'common.month': 'Month',
  'common.year': 'Year',
  'common.add': 'Add',
  'common.remove': 'Remove',
  'common.submit': 'Submit',
  'common.approve': 'Approve',
  'common.reject': 'Reject',
  'common.never': 'Never',
  'common.you': 'you',

  // Auth
  'auth.signIn': 'Sign in',
  'auth.signOut': 'Sign out',
  'auth.emailLabel': 'Work email',
  'auth.passwordLabel': 'Password',
  'auth.signingIn': 'Signing in…',
  'auth.welcome': 'Welcome back',
  'auth.subtitle': 'Sign in to the operations console.',
  'auth.forgotPassword': 'Forgot your password?',
  'auth.sessionExpired': 'Your session expired. Please sign in again.',
  'auth.myProfile': 'My profile',
  'auth.changePassword': 'Change password',
  'auth.androidApp': 'Android app',
  'auth.androidAppHint': 'Download Viganium Team, the employee app, for Android',
  'auth.currentPassword': 'Current password',
  'auth.newPassword': 'New password',
  'auth.changePasswordSubtitle': 'You stay signed in here. Anywhere else you are signed in will ask for the new password.',
  'auth.confirmPassword': 'New password, again',
  'auth.passwordHint': 'At least {n} characters.',
  'auth.passwordTooShort': 'The new password needs at least {n} characters.',
  'auth.passwordMismatch': 'The two new passwords are not the same.',
  'auth.currentPasswordWrong': 'That is not your current password.',
  'auth.passwordChanged': 'Your password has been changed.',
  'auth.showPassword': 'Show password',
  'auth.hidePassword': 'Hide password',
  'auth.forgotTitle': 'Reset your password',
  'auth.forgotIntro': 'Enter the email you sign in with, and we will email you a link to set a new password.',
  'auth.sendResetLink': 'Email me a link',
  'auth.resetLinkSent':
    'If {email} is the email of an account, a link to set a new password is on its way. It works once, within the hour.',
  'auth.resetLinkSentHint':
    'Nothing after a few minutes? Look in your spam folder, or ask an administrator to set a new password for you.',
  'auth.backToSignIn': 'Back to sign in',
  'auth.resetTitle': 'Set a new password',
  'auth.resetIntro':
    'It becomes your password everywhere you sign in to us, and every device signed in with the old one is signed out.',
  'auth.setNewPassword': 'Set the new password',
  'auth.passwordResetDone': 'Your new password is set. Sign in with it.',
  'auth.resetLinkInvalid': 'This link has expired or was used already. Ask for a new one.',
  'auth.requestNewLink': 'Ask for a new link',

  // Dashboard
  'dash.title': 'Dashboard',
  'dash.myTasks': 'My open tasks',
  'dash.myOverdue': 'My overdue tasks',
  'dash.myTickets': 'My open tickets',
  'dash.myProjects': 'My projects',
  'dash.currentShift': 'Current shift',
  'dash.nextShift': 'Next shift',
  'dash.notOnShift': 'Not on shift',
  'dash.myPenalties': 'My penalties this month',
  'dash.awaitingMyView': 'Awaiting my view',
  'dash.unassignedTickets': 'Unassigned in my department',
  'dash.myAssigned': 'Assigned to me',
  'dash.currentQTask': 'My current Q Task',
  'dash.availableQTasks': 'Available Q Tasks',
  'dash.slaCountdowns': 'SLA countdowns',
  'dash.teamMember': 'My queue',
  'dash.teamLeader': 'Department',
  'dash.admin': 'Company',
  'dash.openMonitoring': 'Open Monitoring',
  'dash.finance': 'Finance',
  'dash.ticketsByStatus': 'Tickets by status',
  'dash.tasksByStatus': 'Tasks by status',
  'dash.urgent': 'Urgent',
  'dash.viewBreaches': 'View SLA breaches',
  'dash.assignmentBreaches': 'Assignment SLA breaches',
  'dash.departmentWorkload': 'Workload by employee',
  'dash.staffOnShift': 'Staff on shift now',
  'dash.qTaskQueue': 'Q Task queue',
  'dash.blockedQTasks': 'Blocked Q Tasks',
  'dash.slaCompliance': 'Assignment SLA compliance',
  'dash.activeDepartments': 'Active departments',
  'dash.activeProjects': 'Active projects',
  'dash.payrollStatus': 'Payroll status',
  'dash.monthExpenses': 'Expenses this month',
  'dash.expenseBreakdown': 'Expenses by category',
  'dash.overdueInvoices': 'Overdue invoices',
  'dash.budgetUtilisation': 'Budget vs actual',
  'dash.incoming': 'Incoming payments',
  'dash.outgoing': 'Outgoing payments',
  'dash.nothingPending': 'Nothing pending — you are clear.',
  // Who is working right now — team leaders and admins (2026-10-09)
  'dash.workingNow': 'Working now',
  'dash.onBreakN': '{n} on a break',
  'dash.nobodyWorking': 'Nobody is working right now.',
  'dash.teamAttendance': 'Team attendance',
  // Working now, the office and remote apart, each working, on a break and not working (2026-10-21)
  'dash.workingOffice': 'In the office',
  'dash.workingRemote': 'Remote',
  'dash.onShiftN': '{n} on shift',
  'dash.nobodyOffice': 'Nobody from the office is on shift right now.',
  'dash.nobodyRemote': 'Nobody remote is on shift right now.',
  // The reader's own urgent tasks still open, by name, whatever their role (2026-10-11; only one's own 2026-10-13)
  'dash.urgentTasks': 'Urgent tasks',
  'dash.urgentTasksHint': 'Given to you or to your department, and not finished yet.',
  'dash.noDeadline': 'No deadline',
  // The reader's own progress through their work today, at the top of everyone's dashboard (2026-11-02)
  'dash.progress.title': 'My progress today',
  'dash.progress.of': '{done} of {total} done',
  'dash.progress.label': 'My progress today: {done} of {total} done',
  'dash.progress.clear': 'Your plate is clear',
  'dash.progress.clearHint': 'Nothing of yours is open right now. New work shows up here as it reaches you.',
  'dash.progress.clearWaiting': 'Nothing for you to do right now — {n} waiting on others.',
  'dash.progress.start': 'Let’s get going',
  'dash.progress.startHint': '{n} on your plate. Finish one and the ring starts to fill.',
  'dash.progress.going': 'Good start',
  'dash.progress.goingHint': '{done} done, {left} to go.',
  'dash.progress.half': 'Past halfway',
  'dash.progress.halfHint': '{left} to go — keep it up.',
  'dash.progress.almost': 'Almost there',
  'dash.progress.almostHint': 'Just {left} left.',
  'dash.progress.done': 'All done!',
  'dash.progress.doneHint': 'Everything on your plate is finished. Great work today.',
  'dash.progress.legendDone': 'Done',
  'dash.progress.legendDoneHint':
    'Finished today: tasks you sent for review or marked done, Leaders Tasks marked done, tickets resolved, Q Tasks done.',
  'dash.progress.legendLeft': 'Left',
  'dash.progress.legendLeftHint': 'Still yours to finish, dated or not, late or not.',
  'dash.progress.legendWaiting': 'Waiting on others',
  'dash.progress.legendWaitingHint':
    'Sent for review before today, or a ticket waiting for its requester. Not counted until it comes back to you.',
  'dash.progress.legendLater': 'Planned for later',
  'dash.progress.legendLaterHint': 'Not started, and planned to begin on a later day. Not counted today.',
  'dash.progress.overdueN': '{n} overdue',
  'dash.progress.next': 'Up next',
  'dash.progress.nothingNext': 'Nothing left on your plate.',
  'dash.progress.week': 'Last 7 days',
  'dash.progress.weekTotal': '{n} finished',
  'dash.progress.dayFinished': '{day}: {n} finished',
  'dash.progress.today': 'Today',
  // The reader's own hours against what their day owes, under the progress card (2026-11-02)
  'dash.hours.title': 'My hours today',
  'dash.hours.of': 'of {required}',
  'dash.hours.label': 'My hours today: {worked} of {required}',
  'dash.hours.target': 'Your target: {required}',
  'dash.hours.none': 'No hours owed today',
  'dash.hours.noneHint': 'No shift on your roster today.',
  'dash.hours.noneOff': 'It’s your day off.',
  'dash.hours.noneLeave': 'You’re on leave today.',
  'dash.hours.noneWorked': '{worked} worked, with no hours owed today.',
  'dash.hours.noneTrack': 'Nothing to reach today — a rostered day shows its hours here.',
  'dash.hours.before': 'Your shift is coming up',
  'dash.hours.beforeHint': '{required} to work today.',
  'dash.hours.start': 'Start your shift',
  'dash.hours.startHint': 'It began at {time} — your hours count from when you start it.',
  'dash.hours.going': 'On the clock',
  'dash.hours.half': 'Past halfway',
  'dash.hours.almost': 'Almost there',
  'dash.hours.goingHint': '{missing} to go — done around {time} if you keep going.',
  'dash.hours.paused': 'Clock paused',
  'dash.hours.pausedHint': 'On a break: {missing} still to go.',
  'dash.hours.away': 'Clock stopped',
  'dash.hours.awayHint': 'Away from your computer — the clock waits until you’re back.',
  'dash.hours.met': 'Target met!',
  'dash.hours.metHint': 'Your {required} are done.',
  'dash.hours.metExtra': '{extra} past your {required}.',
  'dash.hours.metWaived': 'Today’s break and inactivity penalties are dropped.',
  'dash.hours.short': '{missing} short',
  'dash.hours.shortHint': 'Your shift ended before your {required}.',
  'dash.hours.shortCost': 'A short day costs {days} of salary.',
  'dash.hours.shortFree': 'Your Regular Shift is over for today: a Free Shift is tracked apart and doesn’t make these hours up.',
  'dash.hours.freeLine': 'Free Shifts {minutes} · {total} tracked in all',
  'dash.hours.freeRunning': 'A Free Shift is running — tracked apart, unpaid.',
  'dash.hours.absent': 'No shift worked today',
  'dash.hours.absentHint': 'Your shift is over and none was started.',
  'dash.hours.fromShift': 'Your shift’s Standard hours',
  'dash.hours.fromDefault': 'The company’s default daily hours — your shift sets none today',
  'dash.hours.breaks': '{minutes} of breaks',
  'dash.hours.weekTotal': '{worked} · met {met} of {owed}',
  'dash.hours.dayLabel': '{day}: {worked} of {required}',
  'dash.hours.dayNotOwed': '{day}: {worked}, nothing owed',
  // What's new: a tip for each feature shipped lately that the reader may open (2026-11-02)
  'tips.title': 'What’s new',
  'tips.hint': 'Added lately, and open to you.',
  'tips.open': 'Open',
  'tips.gotIt': 'Got it',
  'tips.gotItLabel': 'Got it — hide “{title}”',
  'tips.dismissAll': 'Hide all',
  'tips.showAll': 'Show all ({n})',
  'tips.showLess': 'Show less',
  // A name on Working now opens what that person is working on, today and tomorrow (2026-10-27)
  'dash.workOpen': 'See what they are working on',
  'dash.workNow': 'Working on now',
  'dash.workToday': 'Still to do today',
  'dash.workTomorrow': 'Tomorrow',
  'dash.workNothingNow': 'Nothing started right now.',
  'dash.workNothingToday': 'Nothing else due or planned for today.',
  'dash.workNothingTomorrow': 'Nothing due or planned for tomorrow.',
  'dash.workStarted': 'Started {when}',
  'dash.workPlannedStart': 'Planned start {when}',
  'dash.workAssigned': 'Assigned {when}',
  'dash.workDeadline': 'Deadline {when}',
  'dash.workTodayAt': 'today, {time}',
  'dash.workTomorrowAt': 'tomorrow, {time}',
  'dash.workYesterdayAt': 'yesterday, {time}',
  'dash.workKind.TASK': 'Task',
  'dash.workKind.LEADER_TASK': 'Leaders Task',
  'dash.workKind.TICKET': 'Ticket',
  'dash.workKind.Q_TASK': 'Q Task',
  'dash.workKind.MEETING': 'Meeting',

  // Tickets
  'tickets.title': 'Tickets',
  'tickets.new': 'New ticket',
  'tickets.createNotAllowed':
    'Only administrators, and the people they give Create tickets on the employee page, can create tickets.',
  'tickets.number': 'Number',
  'tickets.subject': 'Subject',
  'tickets.category': 'Category',
  'tickets.targetDepartment': 'Route to department',
  'tickets.viewSla': 'View SLA',
  'tickets.assignmentSla': 'Assignment SLA',
  'tickets.slaState': 'SLA state',
  'tickets.assign': 'Assign',
  'tickets.assignToMe': 'Assign to me',
  'tickets.reassign': 'Reassign',
  'tickets.reroute': 'Reroute',
  'tickets.resolve': 'Resolve',
  'tickets.reopen': 'Reopen',
  'tickets.reply': 'Reply',
  'tickets.internalNote': 'Internal note',
  'tickets.resolution': 'Resolution',
  'tickets.timeline': 'Timeline',
  'tickets.responsibility': 'Responsibility snapshot',
  'tickets.views': 'Views',
  'tickets.conversation': 'Conversation',
  'tickets.details': 'Details',
  'tickets.eligible': 'Eligible',
  'tickets.notEligible': 'Not eligible',
  'tickets.viewedOnTime': 'Viewed on time',
  'tickets.viewedLate': 'Viewed late',
  'tickets.notViewed': 'Not viewed',
  'tickets.onShiftAtCreation': 'On shift at creation',
  'tickets.snapshotNote':
    'Taken once, when the ticket was created. Later shift or membership changes never rewrite it.',
  'tickets.lockedBy': 'is currently handling this ticket',
  'tickets.forceUnlock': 'Force unlock',
  'tickets.lock': 'Take the lock',
  'tickets.slaMet': 'SLA met',
  'tickets.slaMissed': 'SLA missed',
  'tickets.slaStopped': 'SLA stopped',
  'tickets.overdue': 'Overdue',
  'tickets.awaitingMyView': 'Awaiting my view',
  'tickets.mine': 'Assigned to me',
  'tickets.requestedByMe': 'Raised by me',
  'tickets.generalOnly': 'General only',
  'tickets.handover': 'Shift handover',
  'tickets.noLock': 'No one holds the lock.',
  'tickets.viewRecorded': 'Your view has been recorded.',

  // Tasks / Kanban
  'tasks.title': 'Tasks',
  'tasks.new': 'New task',
  'kanban.title': 'Kanban board',
  'kanban.myTasks': 'My tasks',
  'tasks.assignedByMe': 'Assigned by me',
  'kanban.dropHint': 'Drag a card to another column to change its status.',
  'kanban.invalidMove': 'That move is not allowed for this task.',
  'kanban.doneByReviewer':
    'Only whoever created the task or gave it out marks it done. When it is finished, move it to Review.',

  // Q Tasks
  'qtasks.title': 'Q Tasks',
  'qtasks.new': 'New Q Task',
  'qtasks.createAdminsOnly': 'Only administrators add Q Tasks.',
  'qtasks.queue': 'Department queue',
  'qtasks.next': 'Next up',
  'qtasks.availableNow': 'Available now',
  'qtasks.assignedToMe': 'Assigned to me',
  'qtasks.claim': 'Assign to me',
  'qtasks.start': 'Start',
  'qtasks.complete': 'Complete',
  'qtasks.block': 'Block',
  'qtasks.unblock': 'Unblock',
  'qtasks.release': 'Release',
  'qtasks.skip': 'Skip',
  'qtasks.reorder': 'Reorder',
  'qtasks.reorderHint': 'Set the order your team should work in. Only the ones nobody has taken move.',
  'qtasks.poolNote': 'Take any Q Task in your department. The order is your leader’s priority.',
  'qtasks.assign': 'Assign',
  'qtasks.assignTitle': 'Assign the Q Task',
  'qtasks.assignTo': 'Assign to',
  'qtasks.assignHint': 'They are told in the bell and by email. Work already started carries on with them.',
  'qtasks.nowWith': 'Now with {name}. Keep them to change only the dates; work already started carries on with whoever you choose.',
  'qtasks.rescheduled': 'New dates saved.',
  'qtasks.assignedTo': 'Given to {name}.',
  'qtasks.convert': 'Convert to tasks',
  'qtasks.convertHint':
    'Tick each person who will work on it. Each gets a task of their own, with its own start and deadline. The Q Task stays in the queue and is done by itself once all its tasks are done.',
  'qtasks.tasksProgress': '{done} of {total} tasks done',
  'qtasks.followsTasks': 'Done by itself once all its tasks are done. Reopening one of them opens it again.',
  'qtasks.people': 'Who works on it',
  'qtasks.everyone': 'Everyone',
  'qtasks.selectedCount': 'Selected: {n}',
  'qtasks.taskTitle': 'Task title',
  'qtasks.sameDates': 'Use these dates for everyone',
  'qtasks.noWorkers': 'Nobody in this department can work Q Tasks yet.',
  'qtasks.converted': 'Turned into tasks.',
  'qtasks.madeInto': 'Tasks made from it',
  'qtasks.position': 'Position',
  'qtasks.claimed': 'Claimed by another team member.',
  'qtasks.emptyQueue': 'The queue is empty',
  'qtasks.emptyQueueHint': 'New Q Tasks line up here in the order they are added.',
  'qtasks.emptyTabHint': 'Try another tab.',

  // Shifts
  'shifts.title': 'Shifts',
  'shifts.definitions': 'Shift definitions',
  'shifts.roster': 'Monthly roster',
  'shifts.generate': 'Generate roster',
  'shifts.copy': 'Copy a previous range',
  'shifts.legend': 'M Morning · N Night · C Custom · O Off',
  'shifts.crossesMidnight': 'Crosses midnight',
  'shifts.standardHours': 'Standard hours',
  'shifts.standardHoursHint':
    'The working hours a day on this shift owes, breaks excluded — 09:00–17:00 with an hour’s break owes 7. A day short of them costs the short-day penalty.',
  'shifts.startTime': 'Start',
  'shifts.endTime': 'End',
  'shifts.onShiftNow': 'On shift now',
  'shifts.noEvening': 'Morning, Night, Custom and Off are the only shift types.',
  'shifts.mySchedule': 'My schedule',
  'shifts.weekdays': 'Weekdays',
  'shifts.overwrite': 'Overwrite existing days',
  'shifts.holidays': 'Holidays',

  // Workforce
  'employees.title': 'Employees',
  'employees.new': 'New employee',
  'employees.activate': 'Activate',
  'employees.deactivate': 'Deactivate',
  'employees.roles': 'Roles',
  // One role each, and Create tickets beside it (2026-10-07).
  'employees.role': 'Role',
  'employees.roleHintAdmin': 'Everything in the admin, for every department.',
  'employees.roleHintLeader':
    'Assigns tickets and tasks, runs workflows, Q Tasks, shifts and attendance, and reads reports — for their departments.',
  'employees.roleHintMember': 'Handles tickets, takes Q Tasks and creates tasks.',
  'employees.roleHintEmployee': 'Creates tasks and follows their own work.',
  'employees.extraPermissions': 'Extra permissions',
  'employees.addOnTicketsHint': 'Raise new tickets. Without it, only administrators can.',
  'employees.addOnIncluded': 'Included: {role} can always do this.',
  'employees.severalRoles': 'They hold {roles}. An employee has one role now: choose it before saving.',
  'employees.chooseOneRole': 'Choose one role.',
  'employees.payrollProfile': 'Payroll profile',
  'employees.slaOverride': 'SLA override',
  'employees.basicSalary': 'Basic salary',
  'employees.effectiveFrom': 'Effective from',
  'employees.salaryHistory': 'Salary history',
  'employees.active': 'Active',
  'employees.inactive': 'Inactive',
  'departments.title': 'Departments',
  'departments.new': 'New department',
  'departments.members': 'Members',
  'departments.leaders': 'Leaders',
  'departments.slaOverrides': 'SLA & penalty overrides',
  'departments.inheritNote': 'Leave a field empty to inherit the company default.',
  'worklogs.title': 'Work logs',

  // Payroll
  'payroll.title': 'Payroll',
  'payroll.periods': 'Periods',
  'payroll.newPeriod': 'New period',
  'payroll.calculate': 'Calculate',
  'payroll.markPaid': 'Mark paid',
  'payroll.lock': 'Lock',
  'payroll.gross': 'Gross',
  'payroll.deductions': 'Deductions',
  'payroll.net': 'Net',
  'payroll.basic': 'Basic salary',
  'payroll.overtime': 'Overtime',
  'payroll.holidayWork': 'Holiday work',
  'payroll.dayOffWork': 'Day-off work',
  'payroll.nightAllowance': 'Night shift allowance',
  'payroll.otherAllowances': 'Other allowances',
  'payroll.slaPenalties': 'SLA penalties',
  'payroll.lineItems': 'Line items',
  'payroll.employees': 'Employees',
  'payroll.adjustment': 'Adjustment',
  'payroll.myPayslips': 'My payslips',
  'payroll.lockedNote': 'This period is locked. Corrections go in an open period as adjustments.',

  // Finance
  'expenses.title': 'Expenses',
  'expenses.new': 'New expense',
  'expenses.categories': 'Categories',
  'expenses.vendor': 'Vendor',
  'expenses.receipt': 'Receipt number',
  'expenses.markPaid': 'Mark paid',
  // Clients (CR-001)
  'clients.title': 'Clients',
  'clients.new': 'New client',
  'clients.company': 'Company',
  'clients.companyName': 'Company name',
  'clients.companyNameAr': 'Company name (Arabic)',
  'clients.legalName': 'Legal name',
  'clients.code': 'Client code',
  'clients.codeHint': 'Left blank, the next code is generated.',
  'clients.codeFollows': 'The last client code is {code}; this one comes next. Change it if you need to.',
  'clients.codeFirst': 'The first client code. Change it if you need to.',
  'clients.codeEditHint': 'Invoices show the client’s current code, old ones included.',
  'clients.codeTaken': 'The client code {code} belongs to another client.',
  'clients.country': 'Country',
  'clients.currency': 'Transaction currency',
  'clients.currencyHint': 'The currency you bill this client in. It is not derived from the country.',
  'clients.address': 'Address',
  'clients.taxNumber': 'Tax number',
  'clients.registrationNumber': 'Registration number',
  'clients.website': 'Website',
  'clients.logo': 'Logo',
  'clients.logoHint': 'Printed at the top of the client’s contracts, opposite ours. PNG, JPG, WebP or GIF; it is saved as a PNG.',
  'clients.logoAdd': 'Upload a logo',
  'clients.logoReplace': 'Replace',
  'clients.logoRemove': 'Remove',
  'clients.logoUploading': 'Uploading…',
  'clients.logoAlt': 'The logo of {client}',
  'clients.logoUnreadable': 'This picture could not be read. Use a PNG, JPG, WebP or GIF.',
  'clients.logoTooLarge': 'The logo is still larger than {mb} MB after it was made smaller. Use a simpler picture.',
  'clients.logoRefused': 'The logo could not be saved: {reason}',
  'clients.logoStorage': 'The file storage could not be reached. Try again; if it keeps failing, tell an administrator.',
  'clients.contacts': 'Contacts',
  'clients.newContact': 'Add contact',
  'clients.jobTitle': 'Job title',
  'clients.primaryContact': 'Primary contact',
  'clients.primary': 'Primary',
  'clients.overview': 'Overview',
  'clients.financialSummary': 'Financial summary',
  'clients.totalInvoiced': 'Total invoiced',
  'clients.totalPaid': 'Total paid',
  'clients.totalOutstanding': 'Outstanding',
  'clients.overdueAmount': 'Overdue',
  'clients.openInvoices': 'Open invoices',
  'clients.collectedLocal': 'Collected in base currency',
  'clients.deactivate': 'Deactivate',
  'clients.reactivate': 'Reactivate',
  'clients.deactivateConfirm':
    'Deactivating keeps every project, invoice and payment. The client simply stops appearing in pickers.',
  'clients.inactiveWarning': 'This client is inactive and cannot be invoiced.',
  'clients.noClient': 'No client — internal',
  'clients.internalProject': 'Internal',
  'projects.new': 'New project',
  'projects.ownOnlyNote': 'Only projects you own or belong to are listed.',
  'projects.codeFollows': 'The last project code is {code}; this one comes next. Change it if you need to.',
  'projects.codeNoSeries': 'End the code with a number, like {code}, and the next project’s code is filled in for you.',
  'projects.codeTaken': 'The project code {code} belongs to another project.',
  'projects.addMember': 'Add member',
  'projects.addPerson': 'A person',
  'projects.addPersonHint': 'Pick someone by name.',
  'projects.addDepartment': 'A department',
  'projects.addDepartmentHint': 'Everyone in it now.',
  'projects.addPersonMineHint': 'Someone in your departments.',
  'projects.addDepartmentMineHint': 'Everyone in one of your departments now.',
  'projects.noDepartmentOfYours': 'You are not in a department, so there is none you can add.',
  'projects.addOnlyNote': 'Only an administrator can take someone off the project afterwards.',
  'projects.departmentWillAdd': 'Will be added ({n}):',
  'projects.departmentAlready': 'Already on the project: {n}.',
  'projects.departmentLater': 'Whoever joins {department} later is not added automatically.',
  'projects.departmentAllIn': 'Everyone in this department is already on the project.',
  'projects.departmentEmpty': 'Nobody is in this department yet.',
  'projects.departmentRoleHint': 'Everyone added gets this role. People already on the project keep theirs.',
  'projects.departmentAdded': '{n} added from {department}.',
  'projects.departmentAddedNone': 'Nobody was added: everyone in {department} is already on the project.',
  // A project's support period, from its contracts (2026-10-09).
  'projects.support': 'Support',
  'projects.supportUntil': 'until {date}',
  'projects.supportNote':
    'In support until {date}, under contract {contracts}: tasks and tickets can be raised on it. Once no contract runs for it, it is completed.',
  'projects.completedNote':
    'Completed: no support contract covers it today, so it takes no new tasks or tickets. A support contract made for it in Finance puts it back in support from its start date.',
  'projects.supportStartsNote':
    'Completed until contract {contracts} starts on {date}: from that day it is in support, takes tasks and tickets again, and its end date becomes {end}.',
  'projects.contractStartsNote':
    'Contract {contracts} starts on {date}: the project then goes into support, and its end date becomes {end}, the day before.',
  'projects.archivedNote':
    'Archived: it takes no new tasks or tickets. A new contract for it in Finance puts it back in support.',
  'projects.contractWaitingNote': 'Contract {contracts} runs until {date}: once the project is finished, it is in support until then.',
  'projects.statusHintSupport': 'Finished, it is in support while contract {contracts} runs, until {date}, and completed after.',
  'projects.statusHintCompleted':
    'Finished with no contract running, it is completed and takes no new tasks or tickets. A new contract for it in Finance puts it back in support.',
  'projects.statusHintInProgress': 'Contract {contracts} runs until {date}: when you finish the project, it goes into support.',
  'projects.closed':
    'That project is completed and has no active support contract: it takes no new tasks or tickets until a support contract covering today puts it back in support.',
  'projects.closedUntil': 'That project is completed: its support contract starts on {date}, and it takes new tasks and tickets from that day.',
  'projects.closedArchived': 'That project is archived: it takes no new tasks or tickets.',
  'projects.closedCancelled': 'That project is cancelled: it takes no new tasks or tickets.',
  // Change status, beside Edit, and Support chosen by hand (2026-10-09).
  'projects.changeStatus': 'Change status',
  'projects.statusNow': 'Now: {status}',
  'projects.statusChanged': 'Status changed to {status}.',
  'projects.byHand': 'by hand',
  'projects.meaning.DRAFT': 'Being planned. Takes tasks and tickets.',
  'projects.meaning.ACTIVE': 'Being built. Takes tasks and tickets.',
  'projects.meaning.TESTING': 'Being tested before it is delivered. Takes tasks and tickets.',
  'projects.meaning.ON_HOLD': 'Paused. Still takes tasks and tickets.',
  'projects.meaning.SUPPORT': 'Delivered, in its support period under contract {contracts}, until {date}.',
  'projects.meaning.SUPPORT_BY_HAND': 'Delivered, in its support period by hand, until someone changes it. Takes tasks and tickets.',
  'projects.meaning.COMPLETED': 'Delivered and closed. No new tasks or tickets.',
  'projects.meaning.COMPLETED_LOCKED': 'Not while contract {contracts} runs: it is in support until {date}.',
  'projects.meaning.CANCELLED': 'Called off. No new tasks or tickets.',
  'projects.meaning.ARCHIVED': 'Put away, out of the client’s view. No new tasks or tickets.',
  'projects.meaning.contractRuns': 'Contract {contracts} goes on running until {date}: end it in Finance if it should stop.',
  'projects.supportByHandNote':
    'In support by hand: tasks and tickets can be raised on it until someone changes its status. A contract made for it in Finance takes its support over, and once no contract runs for it, it is completed.',
  'projects.cancelledNote':
    'Cancelled: it takes no new tasks or tickets. A new contract for it in Finance puts it back in support.',
  'projects.closedContractNote':
    'It takes no new tasks or tickets, but contract {contracts} goes on running until {date}: end it in Finance if it should stop.',
  'projects.statusHintSupportByHand':
    'No contract runs for it, so it is in support by hand: it takes tasks and tickets until someone changes its status. A contract made for it later takes its support over.',
  'projects.statusHintCancelled': 'Called off: it takes no new tasks or tickets. A new contract for it in Finance puts it back in support.',
  'projects.statusHintArchived':
    'It takes no new tasks or tickets, and the client no longer sees it. A new contract for it in Finance puts it back in support.',
  'projects.completedLocked': 'not while a contract runs',
  // A project's links, each under its own name (2026-10-26).
  'projects.links': 'Links',
  'projects.linksHint':
    'As many as you need, each under its own name — Admin, Website, API. Everyone who sees the project finds them on its page.',
  'projects.linkName': 'Name',
  'projects.linkUrl': 'URL',
  'projects.linkNamePlaceholder': 'Admin',
  'projects.addLink': 'Add link',
  'projects.removeLink': 'Remove link',
  'projects.linkNameRequired': 'Give the link a name, like Admin or Website.',
  'projects.linkUrlRequired': 'Type the link’s URL.',
  'projects.linkUrlInvalid': 'Type a web address, like https://admin.example.com.',
  'projects.linkUrlPassword': 'Leave the user name and password out: everyone on the project reads this.',
  'projects.noLinks': 'No links yet.',
  'projects.addLinks': 'Add links',
  'projects.copyLink': 'Copy URL',
  'projects.linkCopied': 'URL copied.',
  'projects.copyRefused': 'This browser would not copy it. Select the URL and copy it yourself.',
  'tickets.rerouteNote':
    'Rerouting records a routing event. The original responsibility snapshot and the original assignment deadline stay exactly as they were (§11.6).',
  'tickets.reassignNote': 'Reassignment is audited, so a reason is required.',
  'shifts.snapshotNote': 'Editing a day never rewrites an SLA snapshot that already exists (§15.5).',
  'shifts.weekdaysHint': 'Leave all unchecked to apply to every day in the range.',
  // §15.3
  'shifts.midnightHint': 'An end at or before the start means the shift crosses midnight.',
  'shifts.newDefinition': 'New shift definition',
  'shifts.editDefinition': 'Edit shift definition',
  'shifts.typeFixed': 'A shift keeps its type. For another type, create a new one.',
  'shifts.bookedNote': 'Now {current}. The hours below start on {date}.',
  'shifts.applyFrom': 'When do the new hours start?',
  'shifts.applyToday': 'From today, {date}',
  'shifts.applyTodayHint': 'Every saved day from today on moves to the new hours.',
  'shifts.applyNextMonth': 'From next month, {date}',
  'shifts.applyNextMonthHint':
    'This month’s saved days keep the current hours. From {date}, saved days and new ones use the new hours.',
  'shifts.applyKeepsNote': 'Days before that date, and days given hours of their own, stay as they are.',
  'shifts.definitionSaved': 'Shift saved.',
  'shifts.hoursApplied': 'Saved. New hours from {date} — saved days moved: {moved}.',
  'shifts.hoursKept': 'Days with hours of their own, left as they were: {kept}.',
  'shifts.nextHours': '{hours} from {date}',
  // Ending the shift early (2026-11-03): allowed so many minutes before its end, a penalty before that — hours worked or not.
  'shifts.earlyEnd': 'Ending the shift early',
  'shifts.earlyEndOn': 'Ending this shift early costs a penalty',
  'shifts.earlyEndMinutes': 'May end it before its end by up to (minutes)',
  'shifts.earlyEndMinutesHint': 'Ending this close to the end costs nothing: 15 lets a shift ending at 17:00 end from 16:45.',
  'shifts.earlyEndPenalty': 'Penalty for ending earlier (days)',
  'shifts.earlyEndPenaltyHint': 'Days of salary — even when the day’s hours were worked.',
  'shifts.earlyEndNote':
    'Counts for shifts started after you save. End shift says what it costs before it does. A day also short of its hours pays one penalty, the larger; over an hour short, the administrator’s short-day decision covers it. A day off, a leave or a holiday costs nothing.',
  'shifts.earlyEndCol': 'Ending early',
  'shifts.earlyEndRule': '{minutes} before the end · {days}',
  // Removing a definition hides it (2026-11-02): saved days keep it.
  'shifts.removeDefinition': 'Remove shift definition',
  'shifts.removeDefinitionNote':
    'It leaves this list and is no longer offered on the roster. Days already saved with it keep it — their hours, and the hours they owe, stay as they were.',
  'shifts.removeDefinitionUpcoming':
    'Saved days from today on that run this shift: {days}. They keep it until you change them on the roster.',
  'shifts.definitionRemoved': 'Shift removed.',
  // §18.3
  'shifts.newHoliday': 'New holiday',
  'shifts.editHoliday': 'Edit holiday',
  'shifts.deleteHoliday': 'Delete holiday',
  'shifts.holidayAppliesNote':
    'Changes apply from today: leave requests and work logs from today on are counted again. Anything before today stays as it was.',
  'shifts.deleteHolidayNote':
    'From today on, this date is an ordinary working day: leave requests that include it are counted again, and work logged on it stops counting as holiday work. Anything before today stays as it was.',
  'shifts.holidaySaved': 'Holiday saved.',
  'shifts.holidayDeleted': 'Holiday deleted.',
  'shifts.holidayApplied': 'Counted again from {date} — leave requests: {leave}, work logs: {logs}.',
  // Support mode (2026-11-01): not a shift — support hours on chosen days, switched on in the phone app inside them.
  'shifts.support': 'Support mode',
  'shifts.supportIntro':
    'Not a shift. Give someone support hours on chosen days — here, or on a day of the roster. Between those hours they may switch support mode on in the Viganium Team app; while it is on, new tickets of their departments ring on their phone and they answer for them as someone on shift does — viewing or assigning late carries the same penalties. No Desktop Agent, no worked hours.',
  'shifts.supportGive': 'Give support mode',
  'shifts.supportDaysEmpty': 'No support days this month.',
  'shifts.supportHours': 'Hours',
  'shifts.supportOn': 'On until {time}',
  'shifts.supportSince': 'since {time}',
  'shifts.supportUsed': 'Used',
  'shifts.supportNotUsed': 'Not used',
  'shifts.supportNotOn': 'Not switched on',
  'shifts.supportUpcoming': 'Upcoming',
  'shifts.supportAllDay': 'All day',
  'shifts.supportNextDay': 'Next day',
  'shifts.supportEachDay': 'On each day:',
  'shifts.supportNoTicketsRefused': 'Only active employees who handle tickets can be given support mode.',
  'shifts.supportRemoveDayTitle': 'Take away {name}’s support hours on {date}?',
  'shifts.supportRemoveDayNote': 'They can no longer switch support mode on that day.',
  'shifts.supportRemoveEndsNow': 'It is on now: it ends at once, and they are told.',
  'shifts.supportRemoved': 'Support hours taken away.',
  'shifts.supportHistory': 'Support mode lately',
  'shifts.supportHistoryHint':
    'The last 30 days. Tickets: those that arrived while it was on and that support mode alone made them answer for.',
  'shifts.supportNoHistory': 'Nobody used support mode in the last 30 days.',
  'shifts.supportStarted': 'Switched on',
  'shifts.supportEnded': 'Ended',
  'shifts.supportHowEnded': 'How it ended',
  'shifts.supportTickets': 'Tickets',
  'shifts.supportEnd.ENDED': 'Switched off in the app',
  'shifts.supportEnd.WINDOW_ENDED': 'At the end of the hours',
  'shifts.supportEnd.REVOKED': 'Switched off by {name}',
  'shifts.supportEnd.SIGNED_OUT': 'The phone signed out',
  'shifts.supportPersonHint': 'People of your departments who handle tickets.',
  'shifts.supportWeekdaysHint': 'Leave all unchecked to give every day of the range.',
  'shifts.supportFrom': 'From',
  'shifts.supportTo': 'To',
  'shifts.supportHoursHint': 'Company time. A To at or before the From runs past midnight; the same time is all day.',
  'shifts.supportOverwrite': 'Change the hours of days that have support hours already',
  'shifts.supportGiven': '{written} support day(s) given, {skipped} kept as they were.',
  'shifts.supportLegend': '{letter} Support mode',
  'shifts.supportCell': 'Support mode {hours}',
  'shifts.dayShift': 'Shift',
  'shifts.noShift': 'No shift',
  'shifts.daySupport': 'Support hours this day',
  'shifts.daySupportHint': 'Between these hours they may switch support mode on in the Viganium Team app.',
  'shifts.supportNotForThem': 'Support mode is only for people who handle tickets.',
  'shifts.daySaved': 'Day saved.',
  'payroll.adjustmentHint': 'Positive adds to the net; negative deducts from it.',
  'qtasks.queueTailNote': 'A new Q Task joins the end of the queue, and anyone in the department can take it straight away.',
  'tasks.generalHint': 'Leave empty for a general task.',
  'tasks.startDate': 'Start',
  'tasks.deadline': 'Deadline',
  'tasks.dueAfterStart': 'The deadline must be after the start.',
  'tasks.fromQTask': 'From Q Task',
  'tasks.projectMemberHint': 'Only members of this project are listed.',
  'tickets.slaStartsNote': 'The SLA clocks start the moment this ticket is created.',
  'violations.cancelHint':
    'A penalty already applied to payroll cannot be cancelled — post an adjustment instead (§13.4).',
  'violations.voidHint': 'Voiding also cancels any penalty that has not yet reached payroll (§11.6).',
  'projects.clientLocked':
    'This project already has invoices, so its client cannot be changed — the invoices were issued against this pairing.',
  'clients.statement': 'Statement',
  'clients.noneYet': 'No clients yet',
  'clients.noneYetHint': 'Every invoice belongs to a client, so start here.',

  // Invoices & payments (CR-001)
  'invoices.client': 'Client',
  'invoices.project': 'Project',
  'invoices.noProject': 'No project',
  'invoices.projectHint': 'Only this client’s projects are listed.',
  'invoices.pickClientFirst': 'Choose a client first',
  'invoices.items': 'Line items',
  'invoices.addItem': 'Add line',
  'invoices.itemDescription': 'Description',
  'invoices.quantity': 'Qty',
  'invoices.unitPrice': 'Unit price',
  'invoices.lineTotal': 'Line total',
  'invoices.discount': 'Discount',
  'invoices.issue': 'Issue invoice',
  'invoices.cancel': 'Cancel invoice',
  'invoices.cancelReason': 'Why is this invoice being cancelled?',
  'invoices.estimatedRate': 'Estimated exchange rate',
  'invoices.estimatedLocal': 'Estimated value in base currency',
  'invoices.estimateHint': 'An estimate at issue time. Revenue is measured from the payments.',
  'invoices.collectedLocal': 'Actually collected',
  'invoices.currencyDiffers': 'This client is normally billed in {currency}.',
  'invoices.clientCurrency': 'Client bills in',
  'invoices.currencyOverridden': 'differs',
  'invoices.invoiceCurrency': 'Invoiced in',
  'invoices.rateOn': 'Rate to {base} on {date}',
  'invoices.rateHint': '1 {code} = ? {base}. Saved with the invoice, so a later rate change never moves it.',
  'invoices.noRate': 'No rate was recorded, so this invoice has no value in the base currency.',
  'invoices.draftNotice': 'This invoice is a draft. Issue it before recording a payment.',
  'invoices.outstandingOnly': 'With a balance owing',
  'invoices.byCurrency': 'By currency',

  'payments.number': 'Payment number',
  'payments.exchangeRate': 'Exchange rate',
  'payments.exchangeRateSource': 'Rate source',
  'payments.localAmount': 'Amount in base currency',
  'payments.rateHint':
    'The rate that applied on the payment date. It is stored with the payment and never recalculated.',
  'payments.proof': 'Proof of payment',
  'payments.reverse': 'Reverse payment',
  'payments.reverseReason': 'Why is this payment being reversed?',
  'payments.reversed': 'Reversed',
  'payments.includeReversed': 'Include reversed',
  'payments.alreadyPaid': 'Already paid',
  'payments.invoiceTotal': 'Invoice total',
  'payments.collectedTotal': 'Collected in base currency',

  // Data migration (CR-001 §50–§53)
  'migration.title': 'Data migration',
  'migration.subtitle': 'Invoices and payments that predate the client-centric restructure.',
  'migration.complete': 'Nothing left to migrate',
  'migration.completeHint': 'Every invoice has a client and every payment has an invoice.',
  'migration.unlinkedInvoices': 'Unlinked invoices',
  'migration.unlinkedPayments': 'Unlinked payments',
  'migration.reason': 'Why it is here',
  'migration.restore': 'Assign and restore',
  'migration.restoreInvoice': 'Restore invoice',
  'migration.restorePayment': 'Restore payment',
  'migration.discard': 'Discard',
  'migration.discardReason': 'Why is this row being written off instead of restored?',
  'migration.originalInvoice': 'Original invoice',
  'migration.pickInvoice': 'Which invoice does this payment settle?',
  'migration.restoreFirst': 'Restore its invoice first.',
  'migration.noFakeData':
    'Nothing is assigned automatically — an invented client would hide the problem rather than fix it.',

  // Finance reports (CR-001 §38)
  'reports.revenue': 'Revenue',
  'reports.receivables': 'Receivables',
  'reports.byClient': 'By client',
  'reports.byProject': 'By project',
  'reports.byCountry': 'By country',
  'reports.byCurrency': 'By currency',
  'reports.byPeriod': 'By period',
  'reports.aging': 'Aging',
  'reports.agingCurrent': 'Current',
  'reports.aging1_30': '1–30 days',
  'reports.aging31_60': '31–60 days',
  'reports.aging61_90': '61–90 days',
  'reports.aging90': '90+ days',
  'reports.outstandingInvoices': 'Outstanding invoices',
  'reports.daysOverdue': 'Days overdue',
  'reports.clientStatement': 'Client statement',
  'reports.balance': 'Balance',
  'reports.debit': 'Invoiced',
  'reports.credit': 'Paid',
  'reports.collections': 'Collections',
  'reports.collectionsThisMonth': 'Collected this month',
  'reports.baseCurrency': 'Base currency',
  'reports.inBaseCurrency': 'All figures in {currency}',
  'reports.billedIn': 'Billed in',
  'reports.sourceAmount': 'Original amount',
  'reports.rate': 'Rate',
  'reports.noRateWarning':
    'No rate is set for {currencies}, so invoices in it are left out of these totals. Add one in Settings > Currencies.',
  'reports.invoiced': 'Invoiced',
  'reports.collected': 'Collected',
  'reports.outstanding': 'Outstanding',

  'invoices.title': 'Invoices',
  'invoices.new': 'New invoice',
  'invoices.counterparty': 'Counterparty',
  'invoices.issueDate': 'Issued',
  'invoices.dueDate': 'Due',
  'invoices.subtotal': 'Subtotal',
  'invoices.tax': 'Tax',
  'invoices.paid': 'Paid',
  'invoices.remaining': 'Remaining',
  'invoices.recordPayment': 'Record payment',
  'payments.title': 'Payments',
  'payments.new': 'Record payment',
  'payments.method': 'Method',
  'payments.reference': 'Reference',
  'budgets.title': 'Budgets',
  'budgets.new': 'New budget',
  'budgets.planned': 'Planned',
  'budgets.actual': 'Actual',
  'budgets.remaining': 'Remaining',
  'budgets.used': 'used',
  'budgets.overBudget': 'Over budget',
  'budgets.thresholdHit': 'Alert threshold reached',
  'budgets.scope': 'Scope',
  'budgets.threshold': 'Alert threshold',

  // Violations
  'violations.title': 'Violations & Penalties',
  'violations.violations': 'Violations',
  'violations.penalties': 'Penalties',
  'violations.type': 'Type',
  'violations.deadline': 'Deadline',
  'violations.actualAction': 'Actual action',
  'violations.void': 'Void',
  'violations.voidReason': 'Why is this violation being voided?',
  'violations.cancel': 'Cancel penalty',
  'violations.effectiveMonth': 'Payroll month',
  'violations.byEmployee': 'By employee',
  'violations.appliedToPayroll': 'Applied to payroll',

  // Reports
  'reports.title': 'Reports',
  'reports.operations': 'Operations',
  'reports.workforce': 'Workforce',
  'reports.finance': 'Finance',
  'reports.slaCompliance': 'SLA compliance',
  'reports.onTime': 'On time',
  'reports.late': 'Late',
  'reports.avgViewDelay': 'Average first-view delay',
  'reports.qThroughput': 'Q Task throughput',
  'reports.blockedTime': 'Average blocked time',
  'reports.overtimeByEmployee': 'Overtime by employee',
  'reports.penaltiesByEmployee': 'Penalties by employee',
  'reports.overdueTasks': 'Overdue tasks',

  // Settings
  'settings.title': 'Settings',
  'settings.company': 'Company',
  'settings.tickets': 'Tickets',
  'settings.sla': 'SLA & Penalties',
  'settings.shifts': 'Shifts',
  'settings.payroll': 'Payroll',
  'settings.finance': 'Finance',
  'settings.security': 'Security',
  'settings.projects': 'Projects',
  'settings.jobs': 'Background jobs',
  'settings.currencies': 'Currencies',
  'settings.saved': 'Settings saved.',
  'settings.slaNote':
    'These are the company defaults. A department, and then an employee, can override them — in that order.',

  // Settings > Currencies
  'currencies.title': 'Currencies & rates',
  'currencies.note':
    'Switch off the currencies you never bill in — the rest disappear from every currency picker. ' +
    'The rate is only a suggestion on a new invoice or payment: a payment keeps the rate saved with it, ' +
    'so changing a rate here never moves money already recorded.',
  'currencies.currency': 'Currency',
  'currencies.inUse': 'In use',
  'currencies.rateTo': 'Rate to {base}',
  'currencies.rateHint': '1 {code} = ? {base}',
  'currencies.rateUpdated': 'Rate updated',
  'currencies.base': 'Base currency',
  'currencies.search': 'Search a code or name',
  'currencies.inUseCount': '{count} in use',
  'currencies.missingRate': 'No rate yet',
  'currencies.saved': 'Currencies saved.',
  'currencies.noneMatch': 'No currency matches that search.',
  'currencies.switchAllOff': 'Switch all off',

  // Audit
  'audit.title': 'Audit log',
  'audit.actor': 'Actor',
  'audit.action': 'Action',
  'audit.entity': 'Entity',
  'audit.occurredAt': 'When',
  'audit.changes': 'Changes',

  // Notifications
  'notifications.title': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.empty': 'No notifications.',
  'notifications.unread': 'unread',
  'notifications.sound': 'Notification sound',
  'notifications.mute': 'Mute the notification sound',
  'notifications.unmute': 'Play a sound when a notification arrives',
  // A notification with no page to open: its reader may no longer open the record.
  'notifications.noPage': 'Nothing to open',
  // Desktop notifications (2026-10-22): the computer's notification center, through the browser's push service.
  'notifications.desktop': 'Desktop notifications',
  'notifications.desktopTurnOn': 'Show notifications on the desktop too',
  'notifications.desktopTurnOff': 'Desktop notifications are on — turn them off',
  'notifications.desktopBlocked': 'Desktop notifications are blocked in this browser',
  'notifications.desktopBlockedHint':
    'This browser blocks notifications from this site. Allow them in its site settings — the icon beside the address — then reload the page.',
  'notifications.desktopPageOnly':
    'This browser has no push service, so they show only while a page of the system is open.',
  'notifications.desktopPromptTitle': 'Get notifications on your desktop',
  'notifications.desktopPromptBody':
    'New notifications also show in your computer’s notification center — even when this page is in the background or closed, as long as the browser is running.',
  'notifications.desktopPromptOn': 'Turn on',
  'notifications.desktopPromptLater': 'Not now',
  'notifications.desktopSampleTitle': 'Desktop notifications are on',
  'notifications.desktopSampleBody': 'New notifications will show here while a page of the system is open.',

  // The two systems — the admin and sys, the finance system
  'systems.admin': 'Admin',
  'systems.sys': 'Finance',
  'audit.app': 'System',
  'audit.adminSubtitle':
    'Every change to operations, people and settings — who made it, when, and from which system. ' +
    'Finance keeps its own audit log: money, payroll and who has access to it.',
  'employees.sysAccess': 'Finance access',
  'employees.noSysAccess': 'No access to Finance',

  // Attendance, leave, email — 2026-09-30
  'nav.attendance': 'Attendance',
  'nav.leaves': 'Leave',
  'nav.overtime': 'Overtime',
  'nav.remoteWork': 'Remote work',
  'common.employee': 'Employee',
  'common.minutesShort': 'min',
  'attendance.title': 'Attendance',
  'attendance.myShift': 'My shift',
  'attendance.team': 'Team',
  'attendance.offDuty': 'Off duty',
  'attendance.working': 'Working',
  'attendance.onBreak': 'On a break',
  'attendance.startShift': 'Start shift',
  'attendance.endShift': 'End shift',
  'attendance.takeBreak': 'Take a break',
  'attendance.resume': 'Back to work',
  'attendance.since': 'since {time}',
  'attendance.promptTitle': 'You have not started your shift',
  'attendance.promptBody': 'Start it when you begin work, pause it for a break, and end it when you leave.',
  'attendance.promptRoster': 'Your roster today: {shift}, {start}–{end}.',
  'attendance.notNow': 'Not now',
  'attendance.staleNote': 'Your last shift was never ended. Starting a new one closes it at your last activity on your computer.',
  'attendance.today': 'Today',
  'attendance.worked': 'Worked',
  'attendance.breaks': 'Breaks',
  'attendance.overrun': 'Over the allowance',
  'attendance.late': 'Late',
  'attendance.leftEarly': 'Left early',
  'attendance.started': 'Started',
  'attendance.ended': 'Ended',
  'attendance.roster': 'Roster',
  'attendance.noRoster': 'Nothing on the roster',
  'attendance.history': 'Recent shifts',
  'attendance.noShiftsYet': 'No shifts recorded yet.',
  'attendance.autoClosed': 'Closed automatically',
  'attendance.closedBy': 'Ended by {name}',
  // A forgotten shift no Desktop Agent tracked (2026-10-21): closed at its rostered end until a manager sets the real one.
  'attendance.endUnconfirmed': 'End not confirmed',
  'attendance.setEnd': 'Set the end',
  'attendance.setEndFor': 'Set the end of {name}’s shift',
  'attendance.setEndNote':
    'They never ended it and no Desktop Agent tracked it, so it was closed at its rostered end. Say when it really ended: its day is judged against its hours once you do.',
  'attendance.setEndAtHint': 'After it started, and no later than the start of their next shift.',
  'attendance.setEndReason': 'How do you know? The employee reads it',
  'attendance.endSet': 'End set.',
  'attendance.stillOpen': 'Still running',
  'attendance.endFor': 'End {name}’s shift',
  'attendance.endReason': 'Why are you ending it?',
  'attendance.endedAt': 'It ended at',
  'attendance.endedAtHint': 'When they actually stopped. Leave it empty for now.',
  'attendance.endNote': 'The employee is told who ended their shift, and why.',
  'attendance.day': 'Day',
  'attendance.allDepartments': 'All departments',
  'attendance.holiday': 'Holiday: {name}',
  'attendance.noBreakTypes': 'No break types are switched on — add them in Settings › Attendance.',
  'attendance.breakLeadersOnly': 'That break is kept for team leaders and administrators.',
  'attendance.breakAdminsOnly': 'That break is kept for administrators.',
  'attendance.breakDepartmentsOnly': 'That break is kept for other departments.',
  'attendance.breakEmployeesOnly': 'That break is kept for the employees chosen for it.',
  'attendance.breakDailyLimit': 'You have taken that break as many times as it allows today.',
  'attendance.shiftStarted': 'Shift started.',
  'attendance.breakStarted': 'Enjoy your break.',
  'attendance.backToWork': 'Welcome back.',
  'attendance.shiftEnded': 'Shift ended.',
  // One Regular Shift a work date, and Free Shifts after it (CR-006, 2026-11-03).
  'attendance.regularShift': 'Regular Shift',
  'attendance.freeShift': 'Free Shift',
  'attendance.freeShifts': 'Free Shifts',
  'attendance.freeShiftNumber': 'Free Shift #{n}',
  'attendance.startFree': 'Start Free Shift',
  'attendance.endFree': 'End Free Shift',
  'attendance.freeStarted': 'Free Shift started.',
  'attendance.freeEnded': 'Free Shift ended.',
  'attendance.freeSince': 'Free Shift since {time}',
  'attendance.freeUnpaid': 'Tracked, unpaid — never counted in your day’s hours, overtime, penalties or pay.',
  'attendance.regularPaid': 'Paid — your day’s hours',
  'attendance.freeNoBreaks': 'A Free Shift has no breaks: end it, and start another when you are back.',
  'attendance.regularDone': 'Your Regular Shift of today is over',
  'attendance.regularDoneBody':
    'It is started once a work date: your next one opens {time}. Meanwhile you can work Free Shifts — tracked, unpaid, and never counted in your hours.',
  'attendance.regularDoneBodyNoTime':
    'It is started once a work date: your next one opens with your next work day. Meanwhile you can work Free Shifts — tracked, unpaid, and never counted in your hours.',
  'attendance.nextRegularAt': 'Next Regular Shift {time}',
  'attendance.trackedTotal': 'Total tracked',
  'attendance.trackedTotalHint': 'Regular and Free together — information only',
  'attendance.freeTotal': '{n} Free Shifts',
  'attendance.freeOne': '1 Free Shift',
  'attendance.freeMinutesShort': '+ Free {minutes}',
  'attendance.inFreeShift': 'In a Free Shift',
  'attendance.freeShiftNow': 'In a Free Shift · {n}',
  'attendance.freeAfter': 'After the Regular Shift of {date}',
  'attendance.freeShiftsAfter': 'Free Shifts after it',
  'attendance.freeShiftsAfterHint': 'Tracked and unpaid: none of it is in this shift’s worked time or the day’s hours.',
  'attendance.freeSoFar': 'so far',
  'attendance.startConfirmOnce': 'It is started once a work date: once you end it, it can’t be started again today.',
  'attendance.startFreeConfirm': 'Start a Free Shift?',
  'attendance.startFreeConfirmBody':
    'A Free Shift is unpaid work after your Regular Shift. The Desktop Agent tracks it like any shift, but it is never counted in your day’s hours, overtime, penalties or pay — and it has no breaks.',
  'attendance.endFreeConfirm': 'End your Free Shift?',
  'attendance.endFreeConfirmBody': 'You can start another one later, until your next Regular Shift opens.',
  'attendance.regularTaken': 'Your Regular Shift of today was already started. You can start a Free Shift, or wait for your next Regular Shift.',
  'attendance.activeSessionExists': 'You already have a shift running. End it before you start another.',
  'attendance.freeNeedsRegular': 'A Free Shift starts once your Regular Shift of the day is over.',
  'attendance.freeWindowPassed': 'Your next Regular Shift is open: start it first. A Free Shift follows it once it is over.',
  'attendance.wrongSessionType': 'That was for the other kind of shift — your shift controls are up to date now.',
  'attendance.sessionAlreadyEnded': 'That shift has already ended.',
  'attendance.endFreeFor': 'End {name}’s Free Shift',
  'attendance.endConfirm': 'End your shift now?',
  'attendance.status.WORKING': 'Working',
  'attendance.status.ON_BREAK': 'On a break',
  'attendance.status.ENDED': 'Done for the day',
  'attendance.status.ON_LEAVE': 'On leave',
  'attendance.status.OFF': 'Day off',
  'attendance.status.UPCOMING': 'Not due yet',
  'attendance.status.NOT_STARTED': 'Late — not started',
  'attendance.status.ABSENT': 'Absent',
  'attendance.status.NO_SCHEDULE': 'Not rostered',
  'leaves.title': 'Leave',
  'leaves.new': 'Ask for leave',
  'leaves.mine': 'My requests',
  'leaves.review': 'To review',
  'leaves.all': 'All requests',
  'leaves.type': 'Leave type',
  'leaves.dates': 'Dates',
  'leaves.from': 'First day',
  'leaves.to': 'Last day',
  'leaves.days': 'Days',
  'leaves.workingDays': 'Working days: {n}',
  'leaves.daysHint': 'Holidays and your rostered days off are not counted.',
  'leaves.balance': 'Balance {year}',
  'leaves.allowance': 'Allowance',
  'leaves.used': 'Approved',
  'leaves.pending': 'Waiting',
  'leaves.remaining': 'Left',
  'leaves.noAllowance': 'No yearly allowance',
  'leaves.paid': 'Paid',
  'leaves.unpaid': 'Unpaid',
  'leaves.overAllowance': 'This goes past the allowance by {n}.',
  'leaves.cancelRequest': 'Cancel request',
  'leaves.rejectTitle': 'Reject leave request',
  'leaves.rejectReason': 'Why not? The employee reads this.',
  'leaves.reviewedBy': 'Decided by',
  'leaves.requested': 'Leave requested.',
  'leaves.approved': 'Leave approved.',
  'leaves.rejected': 'Leave rejected.',
  'leaves.cancelled': 'Request cancelled.',
  'leaves.none': 'No leave requests',
  'leaves.noneHint': 'Ask for leave here — your department leader or HR decides.',
  'leaves.nothingToReview': 'Nothing is waiting for you.',
  'leaves.slaNote': 'Once approved, you are not held responsible for tickets that arrive on those days.',
  'leaves.send': 'Send request',
  // One request's own page, where its notifications open (2026-10-11).
  'leaves.details': 'The request',
  'leaves.asked': 'Asked',
  'leaves.cancelledOn': 'Cancelled',
  'leaves.note': 'Note',
  'leaves.noteFrom': 'Note from {name}',
  // Overtime (2026-10-21): a department's leader sends it, an administrator approves it, the employee accepts it.
  'overtime.title': 'Overtime',
  'overtime.subtitle': 'Only overtime asked for is paid: a department’s leader sends it, an administrator approves it, the employee accepts it.',
  'overtime.new': 'Send overtime',
  'overtime.team': 'Sent and team',
  'overtime.review': 'To approve',
  'overtime.mine': 'My overtime',
  'overtime.status.PENDING': 'Waiting for approval',
  'overtime.status.APPROVED': 'Waiting for the employee',
  'overtime.statusMine.APPROVED': 'Waiting for your answer',
  'overtime.status.ACCEPTED': 'Accepted',
  'overtime.status.REJECTED': 'Rejected',
  'overtime.status.DECLINED': 'Declined',
  'overtime.status.CANCELLED': 'Cancelled',
  'overtime.approvedToast': 'Overtime approved — it now waits for the employee.',
  'overtime.acceptedToast': 'Overtime accepted.',
  'overtime.cancelledToast': 'Overtime cancelled.',
  'overtime.rejectedToast': 'Overtime rejected.',
  'overtime.declinedToast': 'Overtime declined.',
  'overtime.sentForApproval': 'Overtime sent — an administrator approves it.',
  'overtime.sentToEmployee': 'Overtime sent to {name} to accept or decline.',
  'overtime.nothingToReview': 'No overtime is waiting for your approval.',
  'overtime.none': 'No overtime',
  'overtime.noneMine': 'Overtime sent for you shows here once an administrator approves it — then it is yours to accept or decline.',
  'overtime.noneTeam': 'Send overtime for one of your people: an administrator approves it, then they accept or decline it.',
  'overtime.day': 'Day',
  'overtime.dayHint': 'Any day, past or to come.',
  'overtime.upTo': 'Up to',
  'overtime.upToTime': 'Up to {time}',
  'overtime.upToHint': 'Up to {time}. Payroll pays only the time worked that day beyond its hours, up to this.',
  'overtime.durationRange': 'Between 1 minute and 12 hours.',
  'overtime.hours': 'Hours',
  'overtime.minutes': 'Minutes',
  'overtime.reasonHint': 'The administrator and the employee read this.',
  'overtime.pays': 'Pays {time}',
  'overtime.paysLater': 'Pays once the day is judged',
  'overtime.paysLabel': 'It pays',
  'overtime.sentBy': 'Sent by',
  'overtime.approvedBy': 'Approved by',
  'overtime.rejectedBy': 'Rejected by',
  'overtime.acceptedOn': 'Accepted',
  'overtime.declinedOn': 'Declined',
  'overtime.cancelledBy': 'Cancelled by',
  'overtime.accept': 'Accept',
  'overtime.decline': 'Decline',
  'overtime.cancel': 'Cancel overtime',
  'overtime.on': 'Overtime on {date}',
  'overtime.answerNote': 'This overtime waits for your answer. Accepted, payroll pays the time you work that day beyond its hours, up to the amount above.',
  'overtime.monthSignedOff': 'That month’s payroll is signed off: overtime on it can no longer be paid.',
  'overtime.note': 'Note',
  'overtime.noteFrom': 'Note from {name}',
  'overtime.details': 'The overtime',
  'overtime.theDay': 'The day',
  'overtime.theDayNote': 'Overtime pays the time worked beyond the day’s hours.',
  'overtime.dayIs': 'Roster',
  'overtime.owes': 'Owes',
  'overtime.worked': 'Worked',
  'overtime.beyond': 'Beyond its hours',
  'overtime.notJudged': 'Not judged yet: the day is not over, or a shift of it is still open.',
  'overtime.dayKind.SHIFT': '{shift} — its Standard hours',
  'overtime.dayKind.DEFAULT': 'No shift — the default hours',
  'overtime.dayKind.OFF': 'A day off — owes no hours',
  'overtime.dayKind.LEAVE': 'On leave — owes no hours',
  'overtime.dayKind.HOLIDAY': 'A holiday — owes no hours',
  'overtime.dayKind.HOLIDAY_NAMED': '{name} — owes no hours',
  'overtime.dayKind.NONE': 'Owes no hours',
  'overtime.sendNote': 'An administrator approves it, then the employee accepts or declines it. Payroll pays only the time worked that day beyond its hours, up to the amount set here.',
  'overtime.sendNoteApprover': 'You approve overtime, so this goes straight to the employee to accept or decline. Payroll pays only the time worked that day beyond its hours, up to the amount set here.',
  'overtime.send': 'Send',
  'overtime.noPeople': 'Overtime is sent for the people of the departments you lead.',
  'overtime.exists': 'Overtime of {time} is already on that day ({status}). Cancel it to send another.',
  'overtime.rejectTitle': 'Reject overtime',
  'overtime.rejectReason': 'Why not? Whoever sent it reads this.',
  'overtime.declineTitle': 'Decline overtime',
  'overtime.declineReason': 'Anything to add? Whoever sent it reads this.',
  'overtime.waitingCount': '{n} overtime waiting for you',
  // Remote work (2026-10-28): someone at the office asks; their team leader approves it first, then an administrator.
  'remoteWork.title': 'Remote work',
  'remoteWork.subtitle': 'For those who work from the office: your team leader approves it first, then an administrator.',
  'remoteWork.new': 'Ask to work remotely',
  'remoteWork.mine': 'My requests',
  'remoteWork.review': 'To approve',
  'remoteWork.all': 'All requests',
  'remoteWork.status.PENDING_LEADER': 'Waiting for team leader',
  'remoteWork.status.PENDING_ADMIN': 'Waiting for administrator',
  'remoteWork.status.APPROVED': 'Approved',
  'remoteWork.status.REJECTED': 'Rejected',
  'remoteWork.status.CANCELLED': 'Cancelled',
  'remoteWork.dates': 'Dates',
  'remoteWork.from': 'First day',
  'remoteWork.to': 'Last day',
  'remoteWork.days': 'Working days',
  'remoteWork.workingDays': 'Working days: {n}',
  'remoteWork.daysHint': 'Holidays and your rostered days off are not counted.',
  'remoteWork.noWorkingDays': 'Every day in that range is a holiday or a day off — there is nothing to work remotely.',
  'remoteWork.reasonHint': 'Your team leader and the administrator read this.',
  'remoteWork.send': 'Send request',
  'remoteWork.requested': 'Remote work requested.',
  'remoteWork.approvedLeaderToast': 'Approved — it now waits for an administrator.',
  'remoteWork.approvedToast': 'Remote work approved.',
  'remoteWork.rejectedToast': 'Remote work rejected.',
  'remoteWork.cancelledToast': 'Request cancelled.',
  'remoteWork.cancel': 'Cancel request',
  'remoteWork.none': 'No remote work requests',
  'remoteWork.noneHint': 'Ask to work remotely for a day or more — your team leader approves it first, then an administrator.',
  'remoteWork.nothingToReview': 'Nothing is waiting for your approval.',
  'remoteWork.atRemote': 'You work remotely already — remote work is asked for by those who work from the office.',
  'remoteWork.whoApproves': 'Who approves',
  'remoteWork.whoLeader': 'Your team leader — {names}',
  'remoteWork.whoAdmin': 'Then an administrator',
  'remoteWork.whoNoLeader': 'Nobody leads your department, so an administrator approves it.',
  'remoteWork.overlaps': 'You already have a request for {dates} ({status}).',
  'remoteWork.details': 'The request',
  'remoteWork.asked': 'Asked',
  'remoteWork.cancelledBy': 'Cancelled by',
  'remoteWork.steps': 'Approvals',
  'remoteWork.stepsNote': 'One after the other: the team leader first, then an administrator.',
  'remoteWork.step.LEADER': 'Team leader',
  'remoteWork.step.ADMIN': 'Administrator',
  'remoteWork.stepApproved': 'Approved by {name}',
  'remoteWork.stepRejected': 'Rejected by {name}',
  'remoteWork.stepWaiting': 'Waiting',
  'remoteWork.stepWaitingFor': 'Waiting for {names}',
  'remoteWork.stepAfterLeader': 'After the team leader',
  'remoteWork.stepSkipped': 'No team leader to ask',
  'remoteWork.stepNotNeeded': 'Not needed',
  'remoteWork.stepWithdrawn': 'Withdrawn',
  'remoteWork.decideAsLeader': 'You approve this as its team leader; an administrator gives the second approval after you.',
  'remoteWork.decideAsAdmin': 'The team leader has approved it: yours is the second approval, as an administrator.',
  'remoteWork.decideAsAdminToo': 'You approved it as its team leader. As an administrator too, the second approval is yours as well.',
  'remoteWork.decideNoLeader': 'There was no team leader to ask, so it came straight to the administrators.',
  'remoteWork.rejectTitle': 'Reject remote work',
  'remoteWork.rejectReason': 'Why not? The employee reads this.',
  'remoteWork.rejectAsLeader': 'You reject it as its team leader.',
  'remoteWork.rejectAsAdmin': 'You reject it as an administrator.',
  'remoteWork.waitingCount': '{n} remote work requests waiting for your approval',
  'remoteWork.todayByRequest': 'Remote today — approved request',
  'settings.attendance': 'Attendance',
  'settings.leave': 'Leave',
  'settings.email': 'Email',
  'settings.companyName': 'Company name',
  'settings.companyNameAr': 'Company name (Arabic)',
  'settings.domain': 'System domain',
  'settings.domainHint': 'Used inside the company: admin.*, api.*, sys.* and clients.* live under it, and links in our emails point there.',
  'settings.clientDomain': 'Domain for clients',
  'settings.clientDomainHint': 'The domain our clients know us by. Invoices and payment receipts print it.',
  'settings.timezone': 'Timezone',
  'settings.timezoneHint': 'Drives every shift window and SLA deadline.',
  'settings.weekStart': 'Week starts on',
  'settings.viewSlaMinutes': 'View SLA (minutes)',
  'settings.viewSlaHint': 'If nobody opens a ticket within this, the department’s leader and the admins are told — in the bell and by email.',
  'settings.assignmentSlaMinutes': 'Assignment SLA (minutes)',
  'settings.viewPenaltyDays': 'Missed view — penalty',
  'settings.viewPenaltyHint': 'Days of the employee’s own salary, for everyone responsible for the ticket who did not open it in time. 0.25 is a quarter of a day.',
  'settings.assignmentPenaltyDays': 'Missed assignment — penalty',
  'settings.assignmentPenaltyHint': 'Days of salary, for everyone on shift and responsible when a ticket is not assigned in time.',
  'settings.daysOfSalary': 'days of salary',
  'settings.penaltiesAreCumulative': 'Penalties are cumulative',
  'settings.cumulativeHint': 'When off, someone who already has a view penalty on a ticket is not charged the assignment one too.',
  'settings.penaltyRequiresApproval': 'Penalties need approval',
  'settings.excludeOffShift': 'Only whoever is on shift is responsible',
  'settings.excludeNotWorking': 'Only whoever is working is responsible',
  'settings.excludeNotWorkingHint':
    'Started their shift and not on a break when the ticket arrived. Someone on a break, or who has not started, still hears about it but is never penalised for it.',
  'settings.excludeInactive': 'Leave out inactive employees',
  'settings.excludeAbsence': 'Leave out employees on approved leave',
  'settings.excludeAbsenceHint': 'Approved before the ticket arrived.',
  'settings.allowSelfAssignment': 'Allow “Assign to me”',
  'settings.warningMinutes': 'Warning before the deadline (minutes)',
  'settings.numberPrefix': 'Number prefix',
  'settings.defaultPriority': 'Default priority',
  'settings.lockMinutes': 'Soft lock (minutes)',
  'settings.crossProject': 'Support handles project tickets without project access',
  'settings.crossProjectHint': 'Off by default: whoever handles a project ticket also needs access to the project.',
  'settings.defaultDailyHours': 'Default daily hours',
  'settings.defaultDailyHoursHint':
    'Owed by a worked day with no shift on the roster, or one with times of its own. Each shift’s own are its Standard hours, under Shifts.',
  'settings.passwordMinLength': 'Minimum password length',
  'settings.maxUploadMb': 'Largest upload (MB)',
  'settings.maxUploadMbHint': 'Pictures, documents and voice notes. Videos have their own limit, below.',
  'settings.maxVideoUploadMb': 'Largest video (MB)',
  'settings.maxVideoUploadMbHint':
    'Up to 2,000. Videos go straight to Cloudflare R2; while files are kept on this server instead, its own upload limit applies to both.',
  'settings.promptOnLogin': 'Ask everyone to start their shift when they sign in',
  'settings.promptOnLoginHint': 'Until they start, the shift controls sit at the top of every page.',
  'settings.maxShiftHours': 'Longest possible shift (hours)',
  'settings.maxShiftHoursHint':
    'A shift open longer than this was never ended; starting the next one closes it at the last activity its Desktop Agent saw — with no agent, at its rostered end until a manager sets the real one.',
  // The daily-hours rule (operator, 2026-10-15); the hours are the shift's since 2026-10-21.
  'settings.shortDayPenalty': 'Penalty for not working the Daily hours',
  'settings.shortDayPenaltyHint':
    'Charged once the day is over to anyone who started a shift and worked up to an hour less than their shift’s Standard hours that day (else the default under Shifts); more than an hour short goes to an administrator, who decides (Violations & Penalties › Short days). Never on a day off, on leave or a holiday, nor for a day they did not come. 0 = not charged.',
  'settings.fullDayWaives': 'Working the Daily hours drops the day’s penalties',
  'settings.fullDayWaivesHint':
    'Once the day is over, its break and inactivity penalties not yet in a payroll are cancelled. Ticket SLA penalties stay.',
  'settings.dailyHoursTolerance': 'Minutes short that still count',
  'settings.dailyHoursToleranceHint': 'A day short of its hours by this many minutes or fewer counts as worked in full. 0 = none.',
  'settings.dayStartsAt': 'The attendance day starts at',
  'settings.dayStartsAtHint':
    'A shift started after midnight and before this — with no night shift on the roster running — belongs to the day before, so it can still make up that day’s hours. Each day is judged at this time the next day. Before 12:00.',
  'settings.breakTypes': 'Break types',
  'settings.breakTypesNote':
    'What an employee can pause their shift for. The allowance is how long one break should last; a longer one shows as over and adds the type’s penalty, once an administrator approves it. A type that counts as work, a meeting say, stays in Worked; any other comes off it.',
  'settings.newBreakType': 'Add break type',
  'settings.editBreakType': 'Edit break type',
  'settings.allowanceMinutes': 'Allowance (minutes)',
  'settings.noLimit': 'No limit',
  'settings.allowanceHint': 'Leave empty for no limit.',
  'settings.maxPerDay': 'Times a day',
  'settings.maxPerDayHint': 'How many times each employee may take it a day, every shift of the day counted. Leave empty for no limit.',
  'settings.maxPerDayInvalid': 'A whole number from 1 to {n}, or empty for no limit.',
  'settings.order': 'Order',
  'settings.orderHint': 'Lower numbers come first.',
  'settings.offered': 'Offered',
  'settings.switchedOff': 'Switched off',
  'settings.offeredHint': 'Switched off, it stays on the records that used it but can no longer be chosen.',
  'settings.timesUsed': 'Used',
  'settings.codeHint': 'Capital letters, digits and _. It never changes once in use.',
  'settings.leaveTypes': 'Leave types',
  'settings.leaveTypesNote': 'What an employee can ask for. The yearly allowance shows as a balance; whoever decides can still approve past it.',
  'settings.newLeaveType': 'Add leave type',
  'settings.editLeaveType': 'Edit leave type',
  'settings.annualAllowance': 'Days a year',
  'settings.annualAllowanceHint': 'Leave empty for no yearly allowance.',
  'settings.isPaid': 'Paid leave',
  'settings.emailEnabled': 'Email task assignments and tickets',
  'settings.emailEnabledHint': 'Sent through ZeptoMail, in each person’s own language. The bell rings either way.',
  'email.delivery': 'Delivery',
  'email.configured': 'ZeptoMail is configured.',
  'email.notConfigured': 'Not configured — set {vars} in the API’s environment, then restart it.',
  'email.disabled': 'Email is switched off: nothing is sent, and the bell still rings.',
  'email.from': 'Sent from',
  'email.endpoint': 'ZeptoMail endpoint',
  'email.links': 'Links open',
  'email.sendTest': 'Send me a test email',
  'email.recent': 'Recent emails',
  'email.to': 'To',
  'email.subject': 'Subject',
  'email.lastSevenDays': 'Last 7 days',
  'email.none': 'Nothing has been emailed yet.',
  'email.sentAt': 'Sent',
  'departments.inherit': 'inherit',
  'departments.inherits': 'inherits the company default',
  'departments.companyDefault': 'Company default',
  'departments.companyDefaultIs': 'Company default: {value}',
  'departments.override': 'Override',
  'departments.penaltyDaysHint': 'Days of salary. 0 means this department is not penalised.',
  'violations.days': 'Days',
  'violations.totalDays': 'Days of salary',
  'violations.estimate': 'Estimated until payroll prices it',
  'violations.valueAtRate': 'At the day rate',
  'violations.noSalary': 'No salary that day',
  'violations.noSalaryHint': 'No salary was on record for that day, so these days have no money value yet. The amount is worked out once a salary covers that day.',
  // Only an administrator approves or cancels a penalty; the employee may object to their own (2026-10-22).
  'violations.review': 'To review',
  'violations.nothingToReview': 'Nothing is waiting for you.',
  'violations.approvedToast': 'Penalty approved.',
  'violations.object': 'Object',
  'violations.objectTitle': 'Object to the penalty',
  'violations.objectHint':
    'Say why it is wrong. An administrator reads it, then approves your objection — the penalty is cancelled — or rejects it, and you are told why. One objection per penalty.',
  'violations.objectReason': 'Why is this penalty wrong?',
  'violations.objectSent': 'Objection sent — an administrator decides it.',
  'violations.objection': 'Objection',
  'violations.objectionSent': 'Sent',
  'violations.objectionFrom': 'Objection from {name}',
  'violations.objectionState.PENDING': 'Waiting for an administrator',
  'violations.objectionState.ACCEPTED': 'Approved',
  'violations.objectionState.REJECTED': 'Rejected',
  'violations.objectionState.CLOSED': 'Closed',
  'violations.objectionBadge.PENDING': 'Objection waiting',
  'violations.objectionBadge.ACCEPTED': 'Objection approved',
  'violations.objectionBadge.REJECTED': 'Objection rejected',
  'violations.objectionBadge.CLOSED': 'Objection closed',
  'violations.objectionClosed': 'The penalty was cancelled before an administrator decided the objection.',
  'violations.objectionFirst': 'Decide the objection first: approving it cancels the penalty; rejecting it leaves the penalty as it is.',
  'violations.objectionYours': 'An administrator decides it. You are told either way.',
  'violations.objectionApprovedBy': 'Approved by {name}',
  'violations.objectionRejectedBy': 'Rejected by {name}',
  'violations.approveObjection': 'Approve objection',
  'violations.rejectObjection': 'Reject objection',
  'violations.approveObjectionTitle': 'Approve the objection',
  'violations.approveObjectionHint': 'The penalty is cancelled, and nothing is deducted. The employee reads your reason.',
  'violations.rejectObjectionTitle': 'Reject the objection',
  'violations.rejectObjectionHint': 'The penalty stands as it is. The employee reads your reason.',
  'violations.decisionReason': 'Your reason — the employee reads it',
  'violations.objectionApprovedToast': 'Objection approved — the penalty is cancelled.',
  'violations.objectionRejectedToast': 'Objection rejected — the penalty stands.',
  'violations.breakOver': '{type} — over its limit',
  'violations.breakLength': '{length}, limit {limit}',
  'violations.by': 'by {name}',
  'attendance.penalty': 'Penalty {days}',
  'attendance.overrunNote': 'A break that runs past its limit adds a penalty in days of salary, once an administrator approves it.',
  'attendance.overLimitNow': 'This break is past its limit. When you come back, a penalty of {days} goes to an administrator to decide.',
  'attendance.overLimitShort': 'Past the limit — a penalty of {days} when you come back.',
  // The Viganium Desktop Agent and presence on shift (the attendance agent & idle CR).
  'agent.title': 'Desktop agent',
  'agent.subtitle': 'The Viganium Desktop Agent tells the system when you are at your computer. It never records what you type or see.',
  'agent.status.CONNECTED': 'Connected',
  'agent.status.OFFLINE': 'Not connected',
  'agent.status.NOT_PAIRED': 'Not set up',
  'agent.status.DISABLED': 'Disabled',
  'agent.requiredBadge': 'No agent, no shift',
  'agent.connectedOn': 'Connected on {name} · last report {when}.',
  'agent.offlineOn': '{name} last reported {when}. Open the Viganium Desktop Agent on it.',
  'agent.disabledOn': 'An administrator disabled {name} for the desktop agent.',
  'agent.notPairedBody': 'Install the Viganium Desktop Agent on the computer you work on, then connect it to you here.',
  'agent.requiredNote': 'The company requires it: you start a shift only while the agent is connected on your computer.',
  'agent.notRequiredNote': 'Not required right now. While it is connected, your shift is tracked by it.',
  'agent.policy':
    'After {idle} minutes with no keyboard or mouse you are Not working, counted from your last activity. At {penalty} minutes from it, a penalty of {days} applies. With the agent closed or offline you are Not working too — with no penalty.',
  'agent.policyNoPenalty':
    'After {idle} minutes with no keyboard or mouse you are Not working, counted from your last activity — and while the agent is closed or offline.',
  'agent.downloadWindows': 'Download for Windows',
  'agent.downloadMac': 'Download for Mac',
  'agent.connect': 'Connect this computer',
  'agent.connectAnother': 'Connect another computer',
  'agent.codeTitle': 'Your connection code',
  'agent.codeHint': 'Open the agent on your computer and type this code — or press Open the agent. The code works once and lasts {minutes} minutes.',
  'agent.openAgent': 'Open the agent',
  'agent.copyCode': 'Copy code',
  'agent.copied': 'Copied',
  'agent.codeExpired': 'This code has expired.',
  'agent.newCode': 'New code',
  'agent.waiting': 'Waiting for the agent to connect…',
  'agent.paired': '{name} is connected.',
  'agent.myComputers': 'My computers',
  'agent.noComputers': 'No computer is connected yet.',
  'agent.version': 'agent {version}',
  'agent.disabledBy': 'Disabled by an administrator',
  'agent.remove': 'Remove',
  'agent.removeConfirm': 'Remove {name}? Its agent stops reporting; connecting it again needs a new code.',
  'agent.removed': 'Computer removed.',
  'agent.never': 'never',
  'agent.privacy': 'The agent sends only how long since the keyboard or mouse was last used, whether the screen is locked, and the computer’s name. Never what you type, your screen, or the apps and sites you use.',
  'agent.setUp': 'Set up the desktop agent',
  'agent.startBlocked.NOT_PAIRED': 'You cannot start your shift: the Viganium Desktop Agent is not set up on your computer.',
  'agent.startBlocked.OFFLINE': 'You cannot start your shift: the Viganium Desktop Agent is not running or not connected. Open it on your computer.',
  'agent.startBlocked.DISABLED': 'You cannot start your shift: an administrator disabled your computer for the desktop agent.',
  'presence.WORKING': 'Working',
  'presence.NOT_WORKING': 'Not working',
  'presence.notWorkingAs': 'Not working — {reason}',
  'presence.reason.IDLE_INACTIVITY': 'Idle',
  'presence.reason.DEVICE_LOCKED': 'Screen locked',
  'presence.reason.AGENT_DISCONNECTED': 'Agent disconnected',
  'presence.reason.DEVICE_SLEEPING': 'Computer asleep',
  'presence.reason.UNKNOWN': 'Unknown',
  'presence.trackedOn': 'Tracked on {name} by the desktop agent.',
  'presence.untracked': 'The desktop agent does not track this shift.',
  'presence.penaltyAt': 'A penalty of {days} applies at {time} unless you use your keyboard or mouse.',
  'presence.penaltyAtOther': 'Its penalty applies at {time} if nothing changes.',
  'presence.backHint': 'Move your mouse or press a key to be Working again.',
  'presence.reconnectHint': 'Open the Viganium Desktop Agent on {name} to be Working again.',
  'attendance.notWorking': 'Not working',
  'attendance.shiftSince': 'Shift since {time}',
  'attendance.notWorkingShort': '{minutes} not working',
  'attendance.computers': 'Computers',
  'attendance.details': 'Details',
  'attendance.shiftOn': 'Shift of {date}',
  'attendance.shiftOf': '{name} — shift of {date}',
  'attendance.trackedBy': 'Tracked by the desktop agent on {name}.',
  'attendance.notTracked': 'No desktop agent tracked this shift.',
  'attendance.idlePenalties': 'Inactivity penalties',
  'attendance.workBreaks': '{minutes} counted as work',
  'attendance.countsAsWork': 'counts as work',
  'attendance.dailyHours': 'Daily hours',
  'attendance.targetMet': 'Met',
  'attendance.targetMetExtra': 'Met · {minutes} extra',
  'attendance.targetToGo': '{minutes} to go',
  'attendance.targetShort': '{minutes} short',
  'attendance.targetFromShift': 'The Standard hours of their shift that day',
  'attendance.targetFromSalary': 'The daily hours on their salary record — how days were judged before 21 Oct 2026',
  'attendance.targetFromDefault': 'No shift with Standard hours that day: the company’s default daily hours',
  'attendance.targetJudged': 'As judged once the day was over',
  'attendance.daysOfSalary': 'days of salary',
  'attendance.notWorkingStretches': 'Not working',
  'attendance.notWorkingStretchesNote': 'Each stretch without keyboard or mouse, or without the agent, and what it came to. Never a break.',
  'attendance.noStretches': 'Working the whole shift',
  'attendance.noStretchesHint': 'No stretch of Not working on this shift.',
  'attendance.stretch': 'When',
  'attendance.reason': 'Why',
  'attendance.lastActivity': 'Last activity',
  'attendance.penaltyCol': 'Penalty',
  'attendance.stillNotWorking': 'Still not working',
  'attendance.penaltyDueAt': 'Due at {time}',
  'attendance.endReason.SHIFT_ENDED': 'the shift ended',
  'attendance.endReason.BREAK_STARTED': 'a break began',
  'attendance.endReason.ACTIVITY_RESUMED': 'activity resumed',
  'attendance.endReason.AGENT_RECONNECTED': 'the agent came back',
  'attendance.outcome.APPLIED': 'Penalty applied',
  'attendance.outcome.CAP_REACHED': 'Day’s maximum reached — nothing deducted',
  'attendance.outcome.NOT_REACHED': 'No penalty',
  'attendance.outcome.DISABLED': 'Penalties off',
  'attendance.outcome.HOURS_MET': 'No penalty — the day’s hours were already worked',
  'attendance.outcome.FREE_SHIFT': 'No penalty — a Free Shift never has one',

  // Attendance › Daily report: one day, the whole team, in full (operator, 2026-10-27).
  'attendance.dailyReport': 'Daily report',
  'attendance.report.previousDay': 'Previous day',
  'attendance.report.nextDay': 'Next day',
  'attendance.report.show': 'Show',
  'attendance.report.show.ALL': 'Everyone',
  'attendance.report.show.CAME': 'Came to work',
  'attendance.report.show.LATE': 'Came late',
  'attendance.report.show.LEFT_EARLY': 'Left early',
  'attendance.report.show.ABSENT': 'Absent',
  'attendance.report.show.LEAVE': 'On leave',
  'attendance.report.show.OFF': 'Day off or not rostered',
  'attendance.report.show.SHORT': 'Short of the Daily hours',
  'attendance.report.show.NOT_WORKING': 'With Not working time',
  'attendance.report.show.PENALTIES': 'With penalties',
  'attendance.report.search': 'Name or employee code',
  'attendance.report.asOf': 'As of {time} — the day is still running.',
  'attendance.report.expandAll': 'Expand all',
  'attendance.report.collapseAll': 'Collapse all',
  'attendance.report.csv': 'Download CSV',
  'attendance.report.team': 'Team',
  'attendance.report.rostered': 'Rostered: {count}',
  'attendance.report.came': 'Came',
  'attendance.report.lateCount': 'Late: {count}',
  'attendance.report.absent': 'Absent',
  'attendance.report.notStartedCount': 'Not started yet: {count}',
  'attendance.report.onLeave': 'On leave',
  'attendance.report.dayOffCount': 'Day off: {count}',
  'attendance.report.average': 'Average: {minutes}',
  'attendance.report.overAllowance': 'Over the allowance: {minutes}',
  'attendance.report.stretchCount': 'Stretches: {count}',
  'attendance.report.penalties': 'Penalties',
  'attendance.report.shortCount': 'Short of the Daily hours: {count}',
  'attendance.report.toggle': 'Details of {name}',
  'attendance.report.shiftCount': 'Shifts: {count}',
  'attendance.report.shiftN': 'Shift {n}',
  'attendance.report.break': 'Break',
  'attendance.report.time': 'Time',
  'attendance.report.length': 'Length',
  'attendance.report.allowance': 'Allowance',
  'attendance.report.over': 'Over',
  'attendance.report.noLimit': 'No limit',
  'attendance.report.noBreaks': 'No breaks on this shift.',
  'attendance.report.legend.workBreak': 'Break that counts as work',
  'attendance.report.legend.roster': 'Rostered shift',
  'attendance.report.dayVerdict': 'The day',
  'attendance.report.verdictNotOwed': 'No hours were owed this day.',
  'attendance.report.verdictMet': 'Worked {worked} of the {owed} owed — met.',
  'attendance.report.verdictShort': 'Worked {worked} of the {owed} owed — {missing} short.',
  'attendance.report.verdictRunning': 'Worked {worked} of the {owed} owed so far — {missing} to go.',
  'attendance.report.notJudgedYet': 'The day is judged once it is over.',
  'attendance.report.waived': 'Penalties dropped because the hours were worked: {count}',
  'attendance.report.kind.BREAK': 'Break past its limit',
  'attendance.report.kind.IDLE': 'Not working',
  'attendance.report.kind.SHORT_DAY': 'Short of the Daily hours',
  'attendance.report.kind.EARLY_END': 'Shift ended early',
  'attendance.report.minutesUnit': 'min',
  'attendance.report.rosterStart': 'Rostered start',
  'attendance.report.rosterEnd': 'Rostered end',
  'attendance.report.leave': 'Leave',
  'attendance.report.shifts': 'Shifts',
  'attendance.report.breakCount': 'Breaks taken',
  'attendance.report.result': 'Daily hours result',
  'attendance.report.penaltyDays': 'Penalties (days)',
  'attendance.report.breakDetail': 'Breaks in detail',
  'attendance.report.notWorkingDetail': 'Not working in detail',
  'attendance.report.freeCount': 'Free Shifts',
  'attendance.report.freeWorked': 'Free Shifts worked',
  'attendance.report.freeDetail': 'Free Shifts in detail',
  'attendance.report.notes': 'Notes',

  'timeline.title': 'Timeline',
  'timeline.empty': 'Nothing recorded yet.',
  'timeline.SHIFT_STARTED': 'Shift started',
  'timeline.SHIFT_ENDED': 'Shift ended',
  'timeline.BREAK_STARTED': 'Break — {type}',
  'timeline.BREAK_ENDED': 'Back from {type}',
  'timeline.AGENT_CONNECTED': 'Desktop agent connected — {device}',
  'timeline.AGENT_DISCONNECTED.QUIT': 'Desktop agent closed',
  'timeline.AGENT_DISCONNECTED.LOST': 'Desktop agent stopped reporting',
  'timeline.AGENT_DISCONNECTED.SHUTDOWN': 'Computer shut down',
  'timeline.AGENT_DISCONNECTED.LOGOFF': 'Signed out of the computer',
  'timeline.AGENT_DISCONNECTED.UNPAIR': 'Computer disconnected from the desktop agent',
  'timeline.AGENT_DISCONNECTED.DISABLED': 'Computer disabled by an administrator',
  'timeline.AGENT_DISCONNECTED.REMOVED': 'Computer removed from the desktop agent',
  'timeline.AGENT_RECONNECTED': 'Desktop agent reconnected',
  'timeline.AGENT_SWITCHED': 'Now tracked on {to}',
  'timeline.workingAgain': 'Working — activity resumed',
  'timeline.lastActivity': 'No keyboard or mouse since {time}',
  'timeline.DEVICE_LOCKED': 'Screen locked',
  'timeline.DEVICE_UNLOCKED': 'Screen unlocked',
  'timeline.DEVICE_SLEEP': 'Computer went to sleep',
  'timeline.DEVICE_RESUMED': 'Computer woke up',
  'timeline.IDLE_PENALTY_APPLIED': 'Inactivity penalty — {days}',
  'timeline.IDLE_PENALTY_PENDING': 'Inactivity penalty — {days}, waiting for review',
  'timeline.IDLE_PENALTY_CAP_REACHED': 'Penalty threshold reached — the day’s maximum is met, nothing more deducted',
  'timeline.cutToMaximum': 'Cut to what the day’s maximum had left.',
  'devices.computer': 'Computer',
  'devices.status.ACTIVE': 'In use',
  'devices.status.DISABLED': 'Disabled',
  'devices.status.REVOKED': 'Removed',
  'devices.inUseOrDisabled': 'In use or disabled',
  'devices.online': 'Online',
  'devices.offline': 'Offline',
  'devices.asleep': 'Asleep',
  'devices.lastReport': 'Last report {when}',
  'devices.lastReportCol': 'Last report',
  'devices.connectedSince': 'Connected {date}',
  'devices.counts': '{online} online of {total}',
  'devices.noTelemetry': 'Whether each agent is connected and when it last reported — never anyone’s activity.',
  'devices.none': 'No computers connected yet',
  'devices.noneHint': 'Employees connect theirs from Attendance › Desktop agent.',
  'devices.disable': 'Disable',
  'devices.enable': 'Enable',
  'devices.remove': 'Remove',
  'devices.disableTitle': 'Disable {name}',
  'devices.disableNote': 'Its agent stops counting at once, and {person} can neither start a shift from it nor connect it again until you enable it. They are told why.',
  'devices.removeTitle': 'Remove {name}',
  'devices.removeNote': 'Its secret stops working: {person} must connect it again with a new code.',
  'devices.reason': 'Why?',
  'devices.reasonHint': 'The employee reads this.',
  'devices.disabled': 'Computer disabled.',
  'devices.enabled': 'Computer enabled.',
  'devices.removedToast': 'Computer removed.',
  'devices.notFound': 'This computer is not in your list any more.',
  'dash.notWorkingN': '{n} not working',
  'violations.idle': 'Inactivity — {reason}',
  'violations.idleFrom': 'from {time}',
  'violations.idleShift': 'The shift',
  // One penalty's own page, where its notifications open (2026-10-11).
  'violations.kind.break': 'Break penalty',
  'violations.kind.idle': 'Inactivity penalty',
  'violations.kind.shortDay': 'Daily hours penalty',
  'violations.kind.earlyEnd': 'Early end penalty',
  'violations.kind.sla': 'Ticket SLA penalty',
  'violations.kind.other': 'Penalty',
  'violations.forWhat': 'What it is for',
  'violations.breakType': 'Break',
  'violations.started': 'Started',
  'violations.length': 'Length',
  'violations.openShift': 'Open the shift',
  'violations.raised': 'Raised',
  'violations.decidedBy': 'Decided by',
  'violations.cancelledBy': 'Cancelled by {name}',
  'violations.cancelReason': 'Why it was cancelled',
  // A day worked short of its Daily hours, and the penalties a full day drops (2026-10-15).
  'violations.shortDay': 'Daily hours not completed',
  'violations.shortDayWorked': '{worked} of {owed}',
  'violations.workDate': 'Day',
  'violations.worked': 'Worked',
  'violations.owed': 'Daily hours',
  'violations.owedFromDefault': 'No shift with Standard hours that day: the company’s default.',
  // A shift ended earlier than it allows (2026-11-03).
  'violations.earlyEnd': 'Shift ended early',
  'violations.earlyEndAt': 'Ended {ended}, {early} before {end}',
  'violations.shiftEnds': 'Shift ends',
  'violations.endedAt': 'Ended',
  'violations.earlyBy': 'Early by',
  'violations.earlyAllowed': 'Allowed',
  'violations.earlyAllowedValue': 'Up to {minutes} before the end',
  'violations.waived': 'The Daily hours were worked — {worked} of {owed} on {date} — so the day’s break and inactivity penalties do not apply.',
  'violations.waivedShort': 'Daily hours worked',
  'settings.unsaved': 'unsaved',
  'settings.unitSeconds': 'seconds',
  'settings.unitMinutes': 'minutes',
  'settings.agentTitle': 'Desktop agent & idle policy',
  'settings.agentNote': 'The Viganium Desktop Agent tells the system when someone on shift is at their computer. Idle time is never a break, and Not working never ends a shift.',
  'settings.agentFix': 'Some values do not fit together — see the fields marked below.',
  'settings.agentSection': 'Desktop agent',
  'settings.idleSection': 'Idle and the inactivity penalty',
  'settings.agentRequired': 'Require the desktop agent to start a shift',
  'settings.agentRequiredHint': 'No agent, no shift: nobody starts a shift unless the agent is connected on their computer. Switch it off only if the agent itself cannot be used — while it is off, a shift starts untracked until its employee’s agent connects.',
  'settings.agentDownload': 'Where to download the agent for Windows',
  'settings.agentDownloadHint': 'A link to the Windows installer (.exe): employees see a Download for Windows button. Empty, no button.',
  'settings.agentDownloadMac': 'Where to download the agent for Mac',
  'settings.agentDownloadMacHint': 'A link to the Mac disk image (.dmg): employees see a Download for Mac button. Empty, no button.',
  'settings.agentHeartbeat': 'Agent reports every',
  'settings.agentHeartbeatHint': 'The agent is told on each report, so a change reaches every computer within a report.',
  'settings.agentFreshness': 'A report counts as recent for',
  'settings.agentFreshnessHint': 'With no report for longer, the agent is offline. At least one report interval; two is safer.',
  'settings.agentGrace': 'Offline grace',
  'settings.agentGraceHint':
    'How long an agent may be gone — closed, asleep, off the network — before its employee is Not working. That time never raises a penalty.',
  'settings.idleEnabled': 'Mark people Not working when idle',
  'settings.idleEnabledHint': 'Off: only an agent gone past its grace makes someone Not working.',
  'settings.idleThreshold': 'Not working after',
  'settings.idleThresholdHint': 'Minutes with no keyboard or mouse. Once reached, Not working runs from the last activity. A locked screen counts the same.',
  'settings.penaltyEnabled': 'Apply an inactivity penalty',
  'settings.penaltyEnabledHint': 'Off: people still become Not working; no penalty is raised.',
  'settings.penaltyThreshold': 'Penalty after',
  'settings.penaltyThresholdHint':
    'Minutes from the last keyboard or mouse activity. With 5 and 10: last activity 10:00, Not working from 10:00 once 10:05 confirms it, penalty at 10:10. One penalty per stretch — never for the agent being gone.',
  'settings.penaltyDays': 'Penalty',
  'settings.penaltyMax': 'Most a day can take',
  'settings.penaltyMaxHint': 'Past it, a stretch is still recorded; nothing more is deducted.',
  'settings.penaltyApproval': 'Penalties wait for an administrator',
  'settings.penaltyApprovalHint': 'Off: applied at once. An admin can still cancel one on Violations & Penalties.',
  'settings.problem.NOT_BOOLEAN': 'Yes or no.',
  'settings.problem.NOT_WHOLE_NUMBER': 'A whole number.',
  'settings.problem.NOT_NUMBER': 'A number, with two decimals at most.',
  'settings.problem.TOO_SMALL': 'At least {limit}.',
  'settings.problem.TOO_LARGE': 'At most {limit}.',
  'settings.problem.BELOW_IDLE_THRESHOLD': 'Not under the Not-working threshold ({limit} min): both count from the last activity.',
  'settings.problem.BELOW_HEARTBEAT': 'At least one report interval ({limit} s).',
  'settings.problem.NOT_A_LINK': 'A link starting with https://, or nothing.',
  'settings.problem.NOT_A_TIME': 'A time from 00:00 to 11:59.',
  'settings.problem.NOT_A_VERSION': 'Numbers and dots, as the release names it — 1.0.2 — or nothing.',
  'settings.problem.LINK_REQUIRED': 'The newest version needs this link: a phone told to update downloads it here.',
  'settings.overrunPenalty': 'Penalty when over the limit',
  'settings.overrunPenaltyHint': 'Days of salary, e.g. 0.25. It waits for the employee’s department leader or an admin; a leader’s decline cancels it and the admins are told. 0 = no penalty.',
  'settings.overrunPenaltyInvalid': 'Between 0 and 30 days, to two decimals.',
  'settings.breakTakenBy': 'Who can take it',
  'settings.breakForEveryone': 'Everyone',
  'settings.breakForLeaders': 'Team leaders and administrators',
  'settings.breakForAdmins': 'Administrators',
  'settings.breakForDepartments': 'Chosen departments',
  'settings.breakForEmployees': 'Chosen employees',
  'settings.breakTakenByHint':
    'Kept for team leaders and administrators, for administrators alone, or for the departments or employees you choose, nobody else is offered it or can start one. Its allowance and penalty apply to them as to any break.',
  'settings.breakDepartments': 'Departments',
  'settings.breakDepartmentsHint':
    'Only the people in these departments are offered it and can start one — an administrator in none of them too.',
  'settings.breakPickDepartments': 'Choose at least one department.',
  'settings.breakEmployees': 'Employees',
  'settings.breakEmployeesHint':
    'Only the employees you choose are offered it and can start one — an administrator not chosen too.',
  'settings.breakPickEmployees': 'Choose at least one employee.',
  'settings.breakEmployeesMax': 'At most {n} people.',
  'settings.countsAsWork': 'Counts as work time',
  'settings.countsAsWorkOn': 'Added to worked time',
  'settings.countsAsWorkOff': 'Taken off worked time',
  'settings.countsAsWorkHint':
    'Ticked, time on this break stays in Worked, as for a meeting; unticked, it comes off. Its allowance and penalty apply either way. A change also reads the breaks already taken.',
  'tasks.comments': 'Comments',
  'tasks.addComment': 'Add a comment',
  'tasks.activity': 'Activity',
  'tasks.noDescription': 'No description.',
  'tasks.createdBy': 'Created by',
  'tasks.tags': 'Tags',
  'tasks.noComments': 'No comments yet.',
  'tasks.commentPosted': 'Comment added.',
  'tasks.post': 'Post',
  'tasks.activity.created': 'Created',
  'tasks.activity.updated': 'Updated',
  'tasks.activity.assigned': 'Assigned',
  'tasks.activity.unassigned': 'Unassigned',
  'tasks.activity.commented': 'Commented',
  'tasks.activity.filesAttached': 'Attached files',
  'tasks.activity.attachmentRemoved': 'Removed a file',

  // Attachments and voice notes — tasks, tickets, Q Tasks and their replies
  'attachments.title': 'Attachments',
  'attachments.none': 'No files yet.',
  'attachments.attachFiles': 'Attach files',
  'attachments.recordVoice': 'Voice note',
  'attachments.recordVoiceHint': 'Record a voice note',
  'attachments.recording': 'Recording',
  'attachments.startingMic': 'Starting the microphone…',
  'attachments.stopRecording': 'Stop and keep',
  'attachments.discardRecording': 'Discard',
  'attachments.voiceNote': 'Voice note',
  'attachments.hint': 'Pictures, PDF, Word, Excel, voice notes and videos — up to {mb} MB a file, {videoMb} MB a video.',
  'attachments.dropHere': 'Drop files to attach them',
  'attachments.attach': 'Attach',
  'attachments.uploading': 'Uploading…',
  'attachments.remove': 'Remove',
  'attachments.removeConfirm': 'Remove this file?',
  'attachments.keep': 'Keep',
  'attachments.removed': 'File removed.',
  'attachments.added': 'Files attached.',
  'attachments.download': 'Download',
  'attachments.open': 'Open',
  'attachments.play': 'Play',
  'attachments.pause': 'Pause',
  'attachments.speed': 'Playback speed',
  'attachments.micDenied': 'Your browser blocks the microphone for this site. Click the microphone icon in the address bar, allow it, and try again.',
  'attachments.micUnavailable': 'This browser or device cannot record audio.',
  'attachments.tooLarge': 'Larger than {mb} MB.',
  'attachments.typeNotAllowed': 'This type of file is not accepted.',
  'attachments.tooMany': 'Up to {n} files at a time.',
  'attachments.maxLength': 'A voice note stops at {min} minutes.',
  'attachments.playFailed': 'This recording cannot be played here — download it instead.',
  'attachments.failed': 'Upload failed.',
  'attachments.waitForUploads': 'Wait for the uploads to finish.',
  'attachments.addedBy': 'Added by {name}',
  'attachments.tooLargeVideo': 'Videos can be up to {mb} MB.',
  'attachments.videoUnplayable': 'This video cannot play here — download it instead.',
  'attachments.storageUnreachable': 'The file storage could not be reached. Try again; if it keeps failing, tell an administrator.',
  'attachments.notArrived': 'The file did not arrive whole. Remove it and attach it again.',
  'qtasks.replies': 'Replies',
  'qtasks.noReplies': 'No replies yet.',
  'qtasks.writeReply': 'Write a reply',
  'qtasks.reply': 'Reply',
  'qtasks.replyPosted': 'Reply posted.',
  'qtasks.claimedAt': 'Assigned',

  // Workflows — people in levels a task goes through, built on the task (2026-10-15)
  'workflows.levels': 'Levels',
  'workflows.levelN': 'Level {n}',
  'workflows.levelOf': 'Level {n} of {total}',
  'workflows.together': 'Work on it at the same time',
  'workflows.levelsHint': 'Level 1 gets the task first. People on the same level work on it at the same time; the next level starts once they have all finished, and sets its own deadline. Anyone after level 1 may send their step back to someone earlier to check something: it comes back to them when that is done.',
  'workflows.addPerson': 'Add a person…',
  'workflows.addLevel': 'Add level',
  'workflows.removeLevel': 'Remove level',
  'workflows.moveUp': 'Move up',
  'workflows.moveDown': 'Move down',
  'workflows.removePerson': 'Remove {name}',
  'workflows.levelEmpty': 'Add at least one person to this level.',
  'workflows.run.ACTIVE': 'Running',
  'workflows.run.COMPLETED': 'Finished',
  'workflows.run.STOPPED': 'Stopped',
  'workflows.thisTask': 'This task',
  'workflows.progress.done': 'Done',
  'workflows.progress.current': 'In progress',
  'workflows.progress.waiting': 'Waiting',
  'workflows.progress.skipped': 'Skipped — nobody on it could take work',
  'workflows.progress.stopped': 'Stopped here',
  'workflows.progress.unreached': 'Not reached',
  'workflows.filesTitle': 'Files from the workflow',
  'workflows.filesHint': 'What was attached to the other steps of this task.',
  'workflows.stepBy': 'Level {n} · {name}',
  // A comment a workflow handed on with the work (operator, 2026-10-07)
  'tasks.fromLevel': 'From level {n}',
  'tasks.carriedComment': 'Handed on with the work',
  'tasks.carriedCommentHint':
    'Written on another step of this work and handed on with it. Its writer may not be able to open this task, so they may not see a reply here.',
  // A finished task is closed (operator, 2026-10-19); a workflow's step says how to get more from its person
  'tasks.taskClosed': 'This task is finished, so it is closed: no more comments, files or changes.',
  'tasks.stepClosed':
    'This step is finished, so it is closed: no more comments, files or changes. If someone on a later level needs more from its person, they use “Return to an earlier person” on their own step.',
  'tasks.workflow': 'Workflow',
  'tasks.goesTo': 'Goes to',
  'tasks.eachGetsCopy': 'Each gets their own copy, to work on at the same time.',
  'tasks.workflowDatesHint': 'For level 1. Each next level starts when the one before finishes, and its people set their own deadline.',
  'tasks.yourTurn': 'Your turn — set your deadline',
  // Set before Start: after that nobody changes it (2026-10-25)
  'tasks.yourTurnBody':
    'Level {n} of {workflow} finished on {when}, and your task started then. Set when you will finish it before you press Start: after that, the deadline can no longer be changed.',
  'tasks.noDeadlineBody':
    'This task has no deadline yet. Set when you will finish it before you press Start: after that, the deadline can no longer be changed.',
  'tasks.setDeadline': 'Set deadline',
  // Whoever has a task finishes it; whoever created it or gave it marks it done (2026-10-11).
  'tasks.start': 'Start',
  'tasks.finish': 'Finish',
  'tasks.finishReviewHint': 'It waits in Review for whoever created it or gave it to you, who marks it done.',
  'tasks.finishHandsOnHint': 'It goes on to the next level of the workflow.',
  // A task its own creator finishes is done: nobody else reviews it (2026-10-28).
  'tasks.finishOwnHint': 'You created this task, so it needs no review: finishing it marks it done.',
  'tasks.started': 'Started.',
  'tasks.finishedReview': 'Finished. It waits for review.',
  'tasks.finishedHandedOn': 'Finished. The next level gets the work once everyone on yours has finished.',
  'tasks.finishedOwn': 'Finished and marked done.',
  'tasks.markedDone': 'Marked done.',
  'tasks.sentBack': 'Sent back to work.',
  'tasks.review.yoursTitle': 'Waiting for your review',
  'tasks.review.yoursBody': '{name} finished it. Mark it done, or send it back to work.',
  'tasks.review.yoursBodyNobody': 'It is finished. Mark it done, or send it back to work.',
  'tasks.review.markDone': 'Mark done',
  'tasks.review.sendBack': 'Send back to work',
  'tasks.review.waitingTitle': 'Finished, waiting for review',
  'tasks.review.waitingOne': '{name} reviews it and marks it done.',
  'tasks.review.waitingTwo': '{creator} or {assigner} reviews it and marks it done.',
  'tasks.changeDeadline': 'Change deadline',
  'tasks.change': 'Change',
  'tasks.set': 'Set',
  'tasks.notSet': 'Not set yet',
  'tasks.deadlineSaved': 'Deadline saved.',
  // Nobody sets or changes it once someone has started on the task, admins included (operator, 2026-10-25)
  'tasks.deadlineStartedLocked': 'This task has been started, so its deadline can no longer be set or changed.',
  'tasks.deadlineFinishedLocked': 'This task is finished, so its deadline can no longer be set or changed.',
  'tasks.deadlineNotYours':
    'Only whoever created this task or an admin can change its deadline; its own person sets one while it has none.',
  'tasks.startedWhen': 'When level {n} finished',
  'tasks.activity.workflowReceived': 'Handed on from the level before',
  'tasks.activity.workflowHandedOn': 'Handed on to the next level',
  'tasks.activity.workflowFinished': 'Workflow finished',
  'tasks.activity.workflowStopped': 'Workflow stopped',
  'tasks.activity.workflowReopened': 'Workflow running again',
  'tasks.activity.deadlineSet': 'Deadline set',
  'tasks.activity.deadlineChanged': 'Deadline changed',

  // Announcements — news for everyone, for people picked by name, or for departments
  'nav.announcements': 'Announcements',
  'announcements.title': 'Announcements',
  'announcements.subtitle': 'News for the people in the system. Anyone can post one — to everyone, to people they pick, or to departments.',
  'announcements.subtitleOwn': 'News for the people in the system. You can post one to your departments, or to people in them.',
  'announcements.new': 'New announcement',
  'announcements.edit': 'Edit announcement',
  'announcements.tab.all': 'All',
  'announcements.tab.unread': 'Unread',
  'announcements.tab.mine': 'Posted by me',
  'announcements.empty': 'No announcements yet',
  'announcements.emptyHint': 'Post one to tell everyone, a few people or a department. They get it in the bell and by email.',
  'announcements.emptyUnread': 'You are all caught up',
  'announcements.emptyUnreadHint': 'You have opened every announcement for you.',
  'announcements.emptyMine': 'You have not posted an announcement yet',
  'announcements.newBadge': 'New',
  'announcements.deletedBadge': 'Deleted',
  'announcements.audience.ALL': 'Everyone',
  'announcements.audience.USERS': 'Selected people',
  'announcements.audience.DEPARTMENTS': 'Departments',
  'announcements.files': 'Files',
  'announcements.voiceNotes': 'Voice notes',
  'announcements.seenBy': 'Seen by',
  'announcements.tasksMade': 'Tasks made from it',
  'announcements.titleLabel': 'Title',
  'announcements.details': 'Details',
  'announcements.whoSees': 'Who will see it',
  'announcements.option.ALL': 'Everyone',
  'announcements.option.ALLHint': 'Everyone in the system ({n}).',
  'announcements.option.USERS': 'Selected people',
  'announcements.option.USERSHint': 'Only the people you pick.',
  'announcements.option.DEPARTMENTS': 'People in selected departments',
  'announcements.option.DEPARTMENTSHint': 'Whoever is in the departments you pick. Someone who joins one later sees it too.',
  'announcements.option.USERSHintOwn': 'Only the people you pick, from your departments.',
  'announcements.option.DEPARTMENTSHintOwn':
    'Whoever is in the departments you pick, from the ones you are in. Someone who joins one later sees it too.',
  'announcements.addPerson': 'Add a person…',
  'announcements.removePerson': 'Remove {name}',
  'announcements.pickPeople': 'Pick at least one person.',
  'announcements.pickDepartments': 'Pick at least one department.',
  'announcements.notifyHint': 'They are told in the bell and by email, and see it on their dashboard.',
  'announcements.editNotifyHint': 'Nobody is told about an edit. Anyone you add to who sees it is told, the way everyone was at first.',
  'announcements.everyoneAdmins': 'Only administrators can send to everyone.',
  'announcements.noReach':
    'You are not in a department yet, so there is nobody you can send an announcement to. An administrator can add you to one.',
  'announcements.notAllowed': 'You can send an announcement only to your departments and the people in them.',
  // Everyone but the administrators works within their own departments (operator, 2026-10-07)
  'reach.employeesSubtitle': 'The people in your departments.',
  'reach.departmentsSubtitle': 'The departments you are in.',
  'reach.noDepartment': 'You are not in a department yet, so you see only yourself. An administrator can add you to one.',
  'reach.figuresNote': 'Figures for your departments and the people in them.',
  'reach.allMyDepartments': 'All my departments',
  'reach.outside': 'That person is not in your departments. You can work only with the people in your departments.',
  'reach.outsideDepartment': 'That department is not one of yours. You can give work only to your own departments.',
  'reach.nobodyToAssign': 'Nobody in this ticket’s department is in your departments, so you cannot give it to anyone.',
  'announcements.post': 'Post',
  'announcements.posted': 'Announcement posted. People told: {n}.',
  'announcements.postedNobody': 'Announcement posted. Nobody is in its audience yet.',
  'announcements.saved': 'Announcement saved.',
  'announcements.savedTold': 'Announcement saved. Newly told: {n}.',
  'announcements.delete': 'Delete announcement',
  'announcements.deleteConfirm': '“{title}” will be removed for everyone, with its notifications.',
  'announcements.deleteTasks': 'The tasks made from it ({n}) stay as they are.',
  'announcements.deleted': 'Announcement deleted.',
  'announcements.createTask': 'Create task',
  'announcements.postedBy': 'Posted by {name}',
  'announcements.edited': 'Edited {when}',
  'announcements.noDetails': 'No details were written.',
  'announcements.audienceLabel': 'Who sees it',
  'announcements.tasksTitle': 'Tasks from this announcement',
  'announcements.noTasks': 'No task has been made from it yet.',
  'announcements.reachTitle': 'Seen by',
  'announcements.reachOf': '{seen} of {people}',
  'announcements.seenList': 'Seen',
  'announcements.notSeenList': 'Not yet',
  'announcements.everyoneSaw': 'Everyone has seen it.',
  'announcements.nobodySaw': 'Nobody has opened it yet.',
  'announcements.nobodyInAudience': 'Nobody is in its audience yet.',
  'announcements.showAll': 'Show all ({n})',
  'announcements.showLess': 'Show less',
  'announcements.historyTitle': 'History',
  'announcements.history.created': 'Posted it',
  'announcements.history.seen': 'Opened it',
  'announcements.history.updated': 'Edited it',
  'announcements.history.filesAttached': 'Added files',
  'announcements.history.attachmentRemoved': 'Removed a file',
  'announcements.history.taskCreated': 'Made a task from it',
  'announcements.history.deleted': 'Deleted it',
  'announcements.history.toldN': 'People told: {n}',
  'announcements.history.newlyToldN': 'Newly told: {n}',
  'announcements.history.for': 'for {name}',
  'announcements.field.title': 'title',
  'announcements.field.body': 'details',
  'announcements.field.audience': 'who sees it',
  'dash.announcements': 'Announcements',
  'dash.unreadN': '{n} unread',
  'dash.viewAll': 'View all',
  'tasks.fromAnnouncement': 'From announcement',
  'tasks.fromAnnouncementHint': 'The task links back to it, and its history shows the task.',
  // The client portal (clients.viganium.net — operator's request, 2026-10-03)
  'nav.clientCrs': 'Client CRs',
  'nav.clientComplaints': 'Client complaints',
  'systems.clients': 'Client portal',
  'audit.byClient': 'the client',
  'tickets.client': 'Client',
  'tickets.clientHint': 'The client sees this ticket on the client portal, with every reply that is not an internal note.',
  'tickets.pickClientFirst': 'Pick the client first — the projects are theirs.',
  'tickets.clientProjectsOnly': 'Only this client’s projects.',
  'tickets.fromPortal': 'From clients',
  'tickets.raisedOnPortal': 'Client portal',
  'tickets.noClient': 'No client',
  'tickets.clientBadge': 'Client',
  'tickets.clientSeesReplies': 'The client reads replies on the client portal — tick Internal note for anything only we should see.',
  'clients.portal': 'Company sign-in (owners)',
  'clients.portalHint': 'The company’s own username and password for clients.viganium.net — for its owners. Each contact can also have a sign-in of their own, on their contact form; everyone signed in for the client sees and does the same there.',
  'clients.portalWhere': 'For the company’s owners, at clients.viganium.net.',
  'clients.portalEnabled': 'The company can sign in to the client portal',
  'clients.portalUsername': 'Username',
  'clients.portalUsernameHint': '3–40 letters, digits, dots, dashes or underscores.',
  'clients.portalPassword': 'Password',
  'clients.portalNewPassword': 'New password',
  'clients.portalPasswordHint': 'At least 8 characters. Give it to the client yourself — it is not emailed.',
  'clients.portalKeepPassword': 'Leave it empty to keep the password. A new one signs the client out everywhere.',
  'clients.portalGenerate': 'Generate',
  'clients.portalShow': 'Show the password',
  'clients.portalHide': 'Hide the password',
  'clients.portalNeedsBoth': 'A portal sign-in needs a username and a password.',
  'clients.portalGive': 'Give a sign-in',
  'clients.portalOn': 'Can sign in',
  'clients.portalOff': 'Switched off',
  'clients.portalNone': 'The company has no sign-in of its own yet.',
  'clients.portalLastSignIn': 'Last sign-in',
  'clients.portalNever': 'Never',
  'clients.portalBadge': 'Portal',
  'clients.contactSignIns': 'Contacts with a sign-in of their own: {count}',
  'clients.contactSignInsNone': 'No contact has a sign-in of their own yet — give one on the contact’s form.',
  'clients.contactPortal': 'Portal sign-in',
  'clients.contactPortalHint': 'Optional. Lets this contact sign in to clients.viganium.net with their own username and password. They see and do the same as the company’s own sign-in.',
  'clients.contactPortalEnabled': 'Can sign in to the client portal',
  'clients.contactPortalColumn': 'Portal',
  'clients.contactNoSignIn': 'No sign-in',
  'clients.contactEnglishNote': 'Contacts are kept in English: letters A–Z, digits 0–9 and keyboard symbols.',
  'clients.contactEnglishOnly': 'Write this in English.',
  'clients.contactNotEnglish': 'Not in English',
  'clients.ticketDepartments': 'Departments for its tickets',
  'clients.ticketDepartmentsHint': 'The client may send tickets from the portal to these departments, and chooses one on each ticket. At least one.',
  'clients.ticketDepartmentsWhere': 'Where the client may send tickets from the portal.',
  'clients.ticketDepartmentsNone': 'There are no departments yet. Add one under Departments first.',
  'clients.ticketDepartmentsRequired': 'Choose at least one department.',
  'clients.ticketDepartmentDefault': 'Default',
  'clients.ticketDepartmentOff': 'Switched off',
  'clients.ticketDepartmentsShut': 'The client cannot raise tickets on the portal until a department is chosen here.',
  // Who the client can call from the portal (2026-11-01).
  'clients.callers': 'Who the client can call',
  'clients.callersHint': 'The employees this client can call from the client portal. Nobody picked: the client calls nobody.',
  'clients.callersWhere': 'The employees this client can call from the client portal. Every call is recorded.',
  'clients.callersNone': 'Nobody yet — the client cannot call anyone from the portal.',
  'clients.callersRecorded': 'Every call is recorded, as our own calls are.',
  'clients.callersInactive': 'Someone picked is not active: calls to them are refused until they are back.',
  'clients.callersGone': 'Someone picked is no longer active. Pick again.',
  'clients.callerInactive': 'not active',
  'clients.ticketDepartmentsGone': 'One of those departments was switched off or removed. Look at the list again and save.',
  'clients.changeRequests': 'Change requests',
  'crs.title': 'Client change requests',
  'crs.subtitle': 'What clients asked to change, on the client portal.',
  'crs.searchPlaceholder': 'Search by number, title or client',
  'crs.number': 'Request',
  'crs.titleField': 'Title',
  'crs.client': 'Client',
  'crs.files': 'Files',
  'crs.details': 'Details',
  'crs.history': 'History',
  'crs.reviewedBy': 'Last moved by',
  'crs.lastNote': 'Last note to the client',
  'crs.raisedBy': 'raised by the client',
  'crs.raisedByContact': 'raised by {name}',
  'crs.raisedByLabel': 'Raised by',
  'crs.empty': 'No change requests',
  'crs.emptyHint': 'Clients raise them on the client portal.',
  'crs.changeStatus': 'Change status',
  'crs.moveTo': 'Move to',
  'crs.reviewNote': 'Note to the client',
  'crs.reviewNoteHint': 'The client reads it on the portal, beside the status.',
  'crs.reviewNoteRequired': 'Rejecting needs a reason — the client reads it.',
  'crs.updated': 'Change request updated.',
  'crs.status.NEW': 'New',
  'crs.status.IN_REVIEW': 'In review',
  'crs.status.APPROVED': 'Approved',
  'crs.status.REJECTED': 'Rejected',
  'crs.status.DONE': 'Done',
  'crs.action.NEW': 'New',
  'crs.action.IN_REVIEW': 'Start review',
  'crs.action.APPROVED': 'Approve',
  'crs.action.REJECTED': 'Reject',
  'crs.action.DONE': 'Mark done',
  'crs.messages': 'Messages',
  'crs.conversation': 'Conversation',
  'crs.noMessages': 'No messages yet. Write to the client here — they answer on the portal.',
  'crs.messagePlaceholder': 'Write to the client…',
  'crs.send': 'Send',
  'crs.messageSent': 'Sent — the client reads it on the portal.',
  'crs.clientReadsMessages': 'The client reads every message here on the portal. There are no internal notes on a change request.',
  'crs.conversationClosed': 'This change request is done, so its conversation is closed.',
  'crs.convert': 'Convert to task',
  'crs.convertTitle': 'Convert to a task',
  'crs.converted': 'Converted into a task.',
  'crs.convertedBadge': 'Task',
  'crs.fromRequest': 'From change request',
  'crs.filesGoWithTask': 'Its files go with the task: whoever is given it can open them.',
  'crs.cannotConvert': 'This change request cannot be converted now — it is not approved, or it has been converted already.',
  'crs.projectNotYours': 'It is about {name}, a project you are not on. Choose one of the client’s projects you are on, or leave it general.',
  'crs.assigneeOrWorkflow': 'Choose a person — or build a workflow, the first choice in the list.',
  'crs.tasks': 'Tasks',
  'crs.tasksProgress': '{done} of {total} done',
  'crs.notConverted': 'Not converted into a task yet.',
  'crs.convertHint': 'Converting makes a task with its title, details and files — for a person or a workflow.',
  'crs.workFinished': 'Its work is finished. Mark it done when you are satisfied — the client sees Done on the portal.',
  'crs.workCalledOff': 'The work it became was called off. It can be converted again.',
  'crs.levelOf': 'level {n} of {total}',
  'crs.noDeadline': 'no deadline yet',
  'crs.convertedBy': 'Converted by {name} · {when}',
  'crs.workNotFinished': 'Its task is not finished yet. Marking it done tells the client it is done.',

  // Clients' complaints about our people, from the client portal (2026-10-28)
  'complaints.title': 'Client complaints',
  'complaints.subtitle': 'What clients said about the people on their projects. The person it is about never sees it.',
  'complaints.subtitleLeader': 'Complaints clients made about people in your departments. The person it is about never sees it.',
  'complaints.searchPlaceholder': 'Search by number, title, client or person',
  'complaints.number': 'Complaint',
  'complaints.titleField': 'Title',
  'complaints.client': 'Client',
  'complaints.about': 'About',
  'complaints.files': 'Files',
  'complaints.details': 'Details',
  'complaints.history': 'History',
  'complaints.madeBy': 'Made by',
  'complaints.madeByClient': 'made by the client',
  'complaints.madeByContact': 'made by {name}',
  'complaints.formerEmployee': 'Someone no longer here',
  'complaints.departments': 'Departments',
  'complaints.projects': 'On this client’s projects',
  'complaints.sentTo': 'Sent to',
  'complaints.sentToAdmins': 'The administrators',
  'complaints.sentToLeaders': 'Team leaders: {names}',
  'complaints.aboutLeader': 'The administrators only — {name} is a team leader.',
  'complaints.aboutLeaderGone': 'The administrators only — it is about a team leader.',
  'complaints.noLeaders': 'No team leader — nobody leads their departments.',
  'complaints.neverTheSubject': 'The person it is about never sees it, nor hears of it.',
  'complaints.lastNote': 'Last note to the client',
  'complaints.answer': 'Our answer to the client',
  'complaints.reviewedBy': 'Last moved by',
  'complaints.empty': 'No complaints',
  'complaints.emptyHint': 'Clients make them on the client portal, about the people on their projects.',
  'complaints.changeStatus': 'Change status',
  'complaints.moveTo': 'Move to',
  'complaints.reviewNote': 'Note to the client',
  'complaints.reviewNoteHint': 'The client reads it on the portal, beside the status.',
  'complaints.reviewNoteRequired': 'Closing needs an answer — the client reads it.',
  'complaints.updated': 'Complaint updated.',
  'complaints.status.NEW': 'New',
  'complaints.status.IN_REVIEW': 'In review',
  'complaints.status.CLOSED': 'Closed',
  'complaints.action.NEW': 'New',
  'complaints.action.IN_REVIEW': 'Start review',
  'complaints.action.CLOSED': 'Close',
  'complaints.reopen': 'Reopen',
  'tasks.fromChangeRequest': 'From change request',
  'tasks.changeRequestFiles': 'Files from the change request',
  'tasks.changeRequestFilesHint': 'What {client} sent with {number}.',
  'settings.portal': 'Client portal',
  'settings.mobile': 'Mobile app',
  'settings.mobileNote':
    'Viganium Team, the employee app. Put each new APK where anyone with the link can download it — on R2, say — then its link and its version here: a phone with an older version must update before the app opens again.',
  'settings.androidDownload': 'Where to download the Android app',
  'settings.androidDownloadHint':
    'A link to the APK: everyone sees Android app in the menu under their name. Empty, no item.',
  'settings.androidLatestVersion': 'Newest Android app version',
  'settings.androidLatestVersionHint':
    'The version of the APK at that link, as its release names it: 1.0.2. The app checks it whenever it opens, and a phone with an older version shows only "Update required" until the new one is installed. Upload the APK first. Versions from 1.0.2 on check it. Empty, nobody is asked to update.',
  'settings.fixMarked': 'A value is not right — see the field marked below.',
  'settings.portalNote': 'Clients sign in at clients.viganium.net — the company with the username and password set on its form under Clients, and each contact given one on theirs.',
  'settings.portalDepartment': 'Default department for client tickets',
  'settings.portalDepartmentHint': 'A new client starts with this department ticked on its form; each client’s form says which departments it may send tickets to. The portal’s ticket form starts on it when the client may send there.',
  'settings.portalNoDepartment': '— None —',

  // The employee form and page — the salary is shown only to whoever handles payroll in Finance.
  'employees.code': 'Employee code',
  'employees.codeOnce': 'Given once: it cannot be changed after it is saved.',
  'employees.codeLocked': 'Given already, so it cannot be changed.',
  'employees.codeLockedError': 'This employee already has a code, and it cannot be changed. Reload the page to see it.',
  'employees.codeTaken': 'The employee code {code} belongs to someone else.',
  'employees.jobTitle': 'Job title',
  // Where they work from (2026-10-21)
  'employees.workMode': 'Works from',
  'employees.workMode.OFFICE': 'Office',
  'employees.workMode.REMOTE': 'Remote',
  'employees.workModeHint': 'Working now, on the dashboard, shows the office and remote apart.',
  'employees.language': 'Language',
  'employees.lastSignIn': 'Last sign-in',
  'employees.passwordHint': 'At least 8 characters. It goes to them in their welcome email.',
  'employees.passwordShow': 'Show the password',
  'employees.passwordHide': 'Hide the password',
  'employees.passwordGenerate': 'Generate',
  'employees.languageHint': 'Their welcome email and notifications are in this language.',
  'employees.welcomeQueued': '{name} was added. A welcome email with their sign-in is on its way to {email}.',
  'employees.welcomeOff': '{name} was added. Email is switched off, so no welcome email was sent: give them their sign-in yourself.',
  'employees.welcomeNotConfigured':
    '{name} was added. Email is not set up yet, so no welcome email was sent: give them their sign-in yourself.',
  'employees.resendAccess': 'Resend access email',
  'employees.resendAccessConfirm': 'Resend the access email?',
  'employees.resendAccessBody':
    '{name} gets the welcome email again: the sign-in page, their username and a new password. The first password is not kept anywhere, so it cannot be sent twice — the new one replaces it.',
  'employees.resendAccessSent': 'The access email is on its way to {email}, with a new password.',
  'employees.resendAccessSignedIn': 'They have signed in already, so their password stays as it is: nothing was sent.',
  'employees.resendAccessInactive': 'Their account is switched off: activate it before sending their access email.',
  'employees.resendAccessOff': 'Email is switched off in Settings › Email: nothing was sent, and their password is unchanged.',
  'employees.resendAccessNotConfigured': 'Email is not set up yet: nothing was sent, and their password is unchanged.',
  'employees.delete': 'Delete',
  'employees.deleteTitle': 'Delete {name}',
  'employees.deleteHint': 'They are removed for good, with everything they made and everything that is theirs.',
  'employees.deleteWarning':
    'This cannot be undone. Their account, their records and what they made leave the system for good, files included.',
  'employees.deleteSubmit': 'Delete for good',
  'employees.deleteMade': 'What they made — deleted, with other people’s replies on it',
  'employees.deleteOwn': 'Their own records — deleted',
  'employees.deleteHandOver': 'Passes to whoever takes over',
  'employees.deleteReleased': 'Given to them by others — stays, with no one on it',
  'employees.deleteNothing': 'Nothing',
  'employees.deleteKept':
    'Kept: the invoices, payments, expenses and budgets they entered, with their files. The audit log keeps what they did to records that stay, without their name, and one line saying who deleted them.',
  'employees.deleteSignedOffPayslips':
    'Finance has already signed off {months}. Their payslips go from those months too; each month’s paid total keeps the figure it had.',
  'employees.deleteSuccessor': 'Who takes over',
  'employees.deleteSuccessorHint': 'Their projects, contracts and client sign-ins pass to this person.',
  'employees.deleteConfirm': 'Type their email to confirm',
  'employees.deleteConfirmHint': 'Type:',
  'employees.deleted': '{name} was deleted, with everything that was theirs.',
  'employees.deleteSelf': 'You cannot delete yourself.',
  'employees.deleteLastSysAdmin':
    'Nobody else could give access to Finance. Make someone else a Finance admin there first.',
  'employees.deleteConfirmMismatch': 'The email typed is not theirs: nothing was deleted.',
  'employees.deleteSuccessorRequired': 'Choose who takes over their projects, contracts and client sign-ins.',
  'employees.deleteSuccessorInvalid': 'Whoever takes over must be another active employee.',
  'employees.del.tasks': 'Tasks',
  'employees.del.tickets': 'Tickets',
  'employees.del.qTasks': 'Q Tasks',
  'employees.del.announcements': 'Announcements',
  'employees.del.mails': 'Mail sent',
  'employees.del.workflows': 'Workflows',
  'employees.del.comments': 'Comments and replies on others’ work',
  'employees.del.checklistItems': 'Checklist lines on others’ tasks',
  'employees.del.attendanceDays': 'Shifts worked',
  'employees.del.leaves': 'Leave requests',
  'employees.del.rosterDays': 'Roster days',
  'employees.del.salaryRecords': 'Salary records',
  'employees.del.payslips': 'Payslips',
  'employees.del.penalties': 'Penalties',
  'employees.del.violations': 'Violations',
  'employees.del.mailReceived': 'Mail received',
  'employees.del.chats': 'Chats, with both sides of each',
  'employees.del.groupMessages': 'Their messages in chat groups — the groups stay for the others',
  'employees.del.notes': 'Notes and to-do lines',
  'employees.del.notifications': 'Notifications',
  'employees.del.projects': 'Projects',
  'employees.del.contracts': 'Contracts',
  'employees.del.clientAccounts': 'Client sign-ins',
  'employees.del.openTasks': 'Open tasks',
  'employees.del.openTickets': 'Open tickets — back to be assigned',
  'employees.del.openQTasks': 'Q Tasks — back to the pool',
  'employees.departmentRole': 'Member or leader',
  'employees.addDepartment': 'Add a department',
  'employees.removeDepartment': 'Remove this department',
  'employees.departmentsEditHint': 'Taking someone out of a department ends their membership today; its record is kept.',
  'employees.salary': 'Salary',
  'employees.salaryHint': 'Only people who handle payroll in Finance see this.',
  'employees.salaryCreateHint': 'Required. Once saved, only people who handle payroll in Finance can see or change it.',
  'employees.salaryNewHint': 'Monthly. Overtime and allowances are set in Finance.',
  'employees.salaryFirstHint': 'The day it starts, usually their first day. Payroll pays each month from the salary in force on its last day.',
  'employees.salaryFromHint': 'The new figure starts a new salary record on this day; the one before ends the day before.',
  'employees.salaryNow': 'Current salary: {amount}, since {date}',
  'employees.salaryNext': 'From {date}: {amount}',
  'employees.salarySince': 'since {date}',
  'employees.salaryNone': 'No salary record yet',
  'employees.salaryBookedHint': 'Not started yet: change its figure, or move the day it starts.',
  'employees.salaryStartsLater': 'This salary starts on {date}. Until then there is no salary: payroll leaves this employee out, and their penalties have no money value.',
  // Who may do what to a ticket, and private workflow steps (operator, 2026-10-05)
  'tickets.edit': 'Edit ticket',
  'tickets.saved': 'Ticket saved.',
  'tickets.start': 'Start',
  'tickets.editableUntilStarted': 'It can be edited until someone starts on it.',
  'tickets.startedLocked': 'This ticket has been started, so it can no longer be edited.',
  'tickets.editNotYours': 'Only whoever raised this ticket, a team leader or an admin can edit it.',
  'tickets.departmentByReroute': 'A team leader moves it to another department with Reroute, which keeps its SLA history.',
  'tickets.portalClientFixed': 'The client raised it on the client portal, so it stays theirs.',
  'tickets.statusNotYours': 'Only the ticket’s assignee, a team leader or an admin can change its status.',
  // Who at the client asked for a ticket (operator, 2026-10-09)
  'tickets.contact': 'Requested by',
  'tickets.contactHint': 'Who at the client asked for this ticket — the ticket says it was raised at their request.',
  'tickets.contactPickClient': 'Pick the client first — the person who asked is one of their contacts.',
  'tickets.contactNone': '{client} has no contacts yet.',
  'tickets.contactNotListed': 'Not listed? Add them to the client’s contacts',
  'tickets.contactAddFirst': 'Add the person who asked',
  'tickets.contactNewTab': 'Opens in a new tab: come back here and pick them.',
  'tickets.contactAskAdmin': 'Not listed? An administrator adds them on the client’s page.',
  'tickets.contactRemoved': 'removed from the contacts',
  'tickets.portalNoContact': 'The client raised it on the client portal itself, so it names no contact.',
  'tickets.contactRefusedMissing': 'Pick who at the client asked for this ticket.',
  'tickets.contactRefusedRemoved': 'That contact was just removed from the client’s list — pick another.',
  'tickets.contactRefusedOther': 'Pick one of this client’s contacts.',
  'tickets.raisedBy': 'Raised by',
  'tickets.requestedByName': 'Requested by {name}',
  'workflows.privateStep': 'Someone else’s step: only its own person, team leaders and admins can open it.',
  // Editing a task until it is started, and reassigning it (operator, 2026-10-06)
  'tasks.edit': 'Edit task',
  'tasks.saved': 'Task saved.',
  'tasks.editableUntilStarted': 'It can be edited until someone starts on it.',
  'tasks.startedLocked': 'This task has been started, so it can no longer be edited.',
  // Once started, nobody edits a task — admins included (operator, 2026-10-25)
  'tasks.editableWithList': 'It can be edited until someone starts on it; its to-do list, until it is finished.',
  'tasks.finishedLocked': 'This task is finished, so it can no longer be edited.',
  'tasks.editNotYours': 'Only whoever created this task, a team leader or an admin can edit it.',
  'tasks.assigneeByReassign': 'A team leader or an admin changes it with Reassign, on the task’s page.',
  'tasks.workflowProjectFixed': 'A workflow’s tasks stay on the project the work started on.',
  'tasks.changeRequestProjects': 'One of {client}’s projects, or none.',
  'tasks.assigneeNotOnProject': '{name}, who has this task, is not on this project.',
  'tasks.assigneeNotOnProjectOverride': '{name}, who has this task, is not on this project. You may still go ahead.',
  'tasks.assign': 'Assign',
  'tasks.reassign': 'Reassign',
  'tasks.assignTitle': 'Assign the task',
  'tasks.reassignTitle': 'Reassign the task',
  'tasks.currentAssignee': 'Current assignee',
  'tasks.assignTo': 'Assign to',
  'tasks.newAssignee': 'New assignee',
  'tasks.assignHint': 'They are told in the bell and by email.',
  'tasks.reassignNote': 'Whoever has it now is told why, and the task’s history keeps it.',
  'tasks.reassignStepHint': 'A step of a workflow: whoever you choose takes it over, and whoever had it can no longer open it.',
  'tasks.assignNotYours': 'Only a team leader or an admin can assign or reassign a task.',
  'tasks.finishedNoReassign': 'This task is finished, so it cannot change hands.',
  'tasks.assignedTo': 'Given to {name}.',
  'tasks.activity.reassigned': 'Reassigned',
  'tasks.activity.edited': 'Edited: {fields}',
  'tasks.activity.assignedTo': 'Assigned to {name}',
  'tasks.activity.reassignedTo': 'Reassigned to {name}',
  // Giving a task nobody has to a workflow instead of a person (operator, 2026-10-07)
  'tasks.toPerson': 'A person',
  'tasks.toPersonHint': 'One employee does it.',
  'tasks.toWorkflow': 'A workflow',
  'tasks.toWorkflowHint': 'Level 1 first, then each next level in turn.',
  'tasks.assignWorkflowHint':
    'Level 1 works to this task’s start and deadline. Each next level starts when the one before finishes, and its people set their own deadline.',
  'tasks.keepsThisTask':
    '{name} gets this task itself, with its comments and history; each of the others gets a copy, to work on at the same time.',
  'tasks.sentToWorkflow': 'Sent through the workflow. Level 1: {names}.',
  'tasks.activity.workflowAssigned': 'Assigned to the workflow {workflow}',
  // A workflow built on the task itself (operator, 2026-10-15)
  'tasks.throughWorkflow': 'Through a workflow — people level by level',
  'tasks.workflowBelow': 'Set its levels below.',
  'tasks.workflowOffProject': 'Someone on the workflow is not on this project (marked). Take them off it, or add them to the project.',
  'tasks.workflowOffProjectOverride': 'Someone on the workflow is not on this project (marked). You may still go ahead.',
  'tasks.theWorkflow': 'the workflow',
  'tasks.workflowStartedBy': 'Started by {name}',
  'tasks.activity.workflowSent': 'Sent through a workflow',
  // A step sent back to someone earlier to check something, and back again (2026-10-15)
  'tasks.activity.returned': 'Returned to {name} to check',
  'tasks.activity.returnedPlain': 'Returned for a check',
  'tasks.activity.returnedBack': 'Back with {name} — checked by {from}',
  'tasks.activity.returnedBackPlain': 'Back from a check',
  'tasks.activity.calledBack': 'Taken back to {name} from {from}',
  'tasks.return.action': 'Return to an earlier person',
  'tasks.return.title': 'Return to an earlier person',
  'tasks.return.to': 'Return it to',
  'tasks.return.toHint': 'Someone who did the work on an earlier level of this workflow.',
  'tasks.return.option': '{name} · level {n}',
  'tasks.return.note': 'What should they check?',
  'tasks.return.noteHint': 'They read this first. When they are done, it comes back to you — not on through the levels again.',
  'tasks.return.submit': 'Return',
  'tasks.return.sent': 'Returned to {name}. It comes back to you when they are done.',
  'tasks.return.notYours': 'Only a step’s own person returns it, while working on it, after the first level.',
  'tasks.return.checkTitle': '{name} asks you to check something',
  'tasks.return.checkBody': 'When you are done, it goes back to {name} — not on through the workflow.',
  'tasks.return.outTitle': 'With {name} to check',
  'tasks.return.outBody': 'It comes back to you when they are done. You can take it back sooner.',
  'tasks.return.othersTitle': 'With {checker} to check, for {owner}',
  'tasks.return.othersBody': 'It goes back to {owner} when the check is done.',
  'tasks.return.since': 'returned {when}',
  'tasks.return.done': 'Done — return to {name}',
  'tasks.return.takeBack': 'Take it back',
  'tasks.return.bringBack': 'Return it to {name}',
  'tasks.return.backTitle': 'Return to {name}',
  'tasks.return.takeBackTitle': 'Take it back from {name}',
  'tasks.return.asked': 'What {name} asked',
  'tasks.return.backNote': 'A word for {name}',
  'tasks.return.backNoteHint': 'Optional — what you found. The task’s comments are there too.',
  'tasks.return.back': 'Back with {name}.',
  'tasks.return.notOut': 'This task is not out for a check.',
  'tasks.return.notYoursToEnd': 'Only whoever is checking it, whoever asked, a team leader or an admin can return it.',
  'tasks.return.listHint': 'Out for a check — it goes back to {name}',
  'tasks.return.stepChecking': 'with {name} to check',
  // The levels a running workflow has not reached yet, edited (2026-10-15)
  'tasks.workflowEdit.title': 'Edit the workflow',
  'tasks.workflowEdit.reached': 'Levels reached',
  'tasks.workflowEdit.reachedHint': 'The work has been on these levels, so they stay as they are.',
  'tasks.workflowEdit.ahead': 'Levels ahead',
  'tasks.workflowEdit.aheadHint':
    'Not reached yet: change who is on them, add or remove levels, or put them in another order. Nobody on them is told until it is their turn.',
  'tasks.workflowEdit.none': 'No levels after level {n}: it is the last one, and its finished work waits for review.',
  'tasks.workflowEdit.submit': 'Save the workflow',
  'tasks.workflowEdit.saved': 'Workflow saved.',
  'tasks.workflowEdit.notYours': 'Only whoever started this work, a team leader or an admin can change its workflow.',
  'tasks.workflowEdit.notRunning': 'This workflow is finished or stopped: it has no levels ahead to change.',
  'tasks.workflowEdit.noWorkflow': 'This task is not a step of a workflow.',
  'tasks.workflowEdit.movedOn':
    'The work moved on, or someone else changed its workflow, meanwhile. The levels ahead are shown as they are now: make your change again.',
  'tasks.workflowEdit.inactive': 'No longer active — skipped at their turn',
  'tasks.activity.workflowEdited': 'Workflow changed after level {n}',
  'tasks.activity.workflowEditedPlain': 'Workflow changed',
  'tasks.activity.workflowEditedAdded': 'Added {names}',
  'tasks.activity.workflowEditedRemoved': 'Taken off: {names}',
  'tasks.activity.workflowEditedLevels': 'Levels: {from} → {to}',
  'tasks.activity.workflowEditedMoved': 'Its levels rearranged',
  // Giving a task to a whole department, which shares it (operator, 2026-10-10)
  'tasks.toDepartment': 'A department',
  'tasks.toDepartmentHint': 'Everyone in it works on it together.',
  'tasks.everyoneIn': 'Everyone in {department}',
  'tasks.sharedHint': 'Everyone in the department sees it and works on it.',
  'tasks.assignDepartmentHint':
    'One of your departments. Everyone in it sees the task and works on it — comments, files, the to-do list and moving it — and is told in the bell and by email.',
  'tasks.assignAnyDepartmentHint':
    'Everyone in it sees the task and works on it — comments, files, the to-do list and moving it — and is told in the bell and by email.',
  'tasks.noDepartmentOfYours': 'You are not in a department, so there is none you can give the task to.',
  'tasks.departmentEmpty': 'Nobody is in {department} yet, so nobody would get the task.',
  'tasks.departmentNobodyOnProject':
    'Nobody in {department} is on this task’s project, so none of them could see it. Add them to the project, or choose a person.',
  'tasks.departmentPartlyOnProject': 'Only those on the project see it. Not on the project: {names}.',
  'tasks.sentToDepartment': 'Given to everyone in {department}.',
  'tasks.activity.assignedToDepartment': 'Assigned to everyone in {department}',
  'tasks.activity.reassignedToDepartment': 'Reassigned to everyone in {department}',
  'tasks.people': 'People',
  'tasks.wholeDepartment': 'A whole department',
  'tasks.departmentIsAssignee': 'The department the task is given to, chosen under Assignee.',
  'tasks.departmentShared': 'The whole department shares it: a team leader or an admin gives it to another with Reassign.',
  // Editing a Q Task until someone starts on it (operator, 2026-10-06)
  'qtasks.edit': 'Edit Q Task',
  'qtasks.saved': 'Q Task saved.',
  'qtasks.editableUntilStarted': 'It can be edited until someone starts on it.',
  'qtasks.startedLocked': 'This Q Task has been started, so it can no longer be edited.',
  'qtasks.editNotYours': 'Only whoever created this Q Task, a leader of its department or an admin can edit it.',
  'qtasks.departmentFixed': 'A Q Task stays in its department’s queue.',
  'qtasks.assigneeByAssign': 'A leader changes who has it, and its dates, with Assign on the Q Task’s page.',
  'qtasks.activity.edited': 'Edited: {fields}',
  // Leaders Tasks — from one department leader to another (operator, 2026-10-07)
  'nav.leaderTasks': 'Leaders Tasks',
  'leaderTasks.title': 'Leaders Tasks',
  'leaderTasks.subtitle':
    'From one department leader to another. The leader asked does it, or hands it on to their department or a workflow, and reviews that work when it is finished.',
  'leaderTasks.badge': 'Leaders Task',
  'leaderTasks.new': 'New Leaders Task',
  'leaderTasks.received': 'Asked of me',
  'leaderTasks.sent': 'I asked',
  'leaderTasks.all': 'All',
  'leaderTasks.from': 'From',
  'leaderTasks.to': 'To',
  'leaderTasks.work': 'Work handed on',
  'leaderTasks.work.none': 'Not handed on',
  'leaderTasks.work.underWay': 'Under way',
  'leaderTasks.work.finished': 'Finished',
  'leaderTasks.work.calledOff': 'Called off',
  'leaderTasks.emptyReceived': 'No Leaders Task has been asked of you.',
  'leaderTasks.emptySent': 'You have not sent a Leaders Task yet.',
  'leaderTasks.leader': 'Leader',
  'leaderTasks.leaderHint': 'A department’s leader, and the department it is for. They do it, or hand it on to their department or a workflow.',
  'leaderTasks.leaderOf': '{name} — {department}',
  'leaderTasks.noLeaders': 'There is no other department leader yet. A department’s members list makes someone its Leader.',
  'leaderTasks.onlyLeaders': 'Only department leaders and admins send Leaders Tasks.',
  'leaderTasks.sentTo': 'Sent to {name}.',
  'leaderTasks.handOn': 'Hand on',
  'leaderTasks.reassign': 'Give to another leader',
  'leaderTasks.askedOf': 'Asked of',
  'leaderTasks.sentBy': 'Sent by',
  'leaderTasks.move.start': 'Start',
  'leaderTasks.move.unblock': 'Unblock',
  'leaderTasks.move.backToWork': 'Back to work',
  'leaderTasks.move.block': 'Blocked',
  'leaderTasks.move.review': 'To review',
  'leaderTasks.move.done': 'Mark done',
  'leaderTasks.move.cancel': 'Cancel it',
  'leaderTasks.move.cancelYes': 'Yes, cancel it — it cannot be reopened',
  'leaderTasks.move.cancelNo': 'Keep it',
  'leaderTasks.move.toDo': 'Back to To do',
  'leaderTasks.move.reopen': 'Reopen',
  'leaderTasks.moved': 'Moved to {status}.',
  'leaderTasks.yoursTitle': 'Asked of you by {name}',
  'leaderTasks.yoursBody':
    'Do it yourself, or hand it on to someone in your department or to a workflow. When that work is finished it comes back to you to review.',
  'leaderTasks.reviewTitle': 'The work is finished — it is yours to review',
  'leaderTasks.reviewBody': 'Check the work handed on, then mark this Leaders Task done. {name} is told.',
  'leaderTasks.waitingReview': 'The work is finished. {name} is reviewing it.',
  'leaderTasks.calledOffTitle': 'The work handed on was cancelled',
  'leaderTasks.calledOffBody': 'Hand it on again, or do it yourself.',
  'leaderTasks.workTitle': 'Work handed on',
  'leaderTasks.workNone': 'Not handed on yet.',
  'leaderTasks.workflowLevel': 'Level {n} of {of}',
  'leaderTasks.fromLeaderTask': 'From a Leaders Task',
  'leaderTasks.filesTitle': 'Files from the Leaders Task',
  'leaderTasks.filesHint': 'Sent with “{title}”.',
  'leaderTasks.activity.handedOnTo': 'Handed on to {name}',
  'leaderTasks.activity.handedOnToWorkflow': 'Handed on to the workflow {workflow}',
  'leaderTasks.activity.handedOnThroughWorkflow': 'Handed on through a workflow',
  'leaderTasks.activity.workFinished': 'The work handed on is finished — back for review',
  'leaderTasks.activity.workReopened': 'Work reopened — back to In progress',
  'leaderTasks.activity.workCalledOff': 'The work handed on was cancelled',
  'leaderTasks.activity.departmentChanged': 'Moved to {department}',
  'leaderTasks.activity.createdFrom': 'Created from the Leaders Task “{title}”',
  'leaderTasks.handOnTitle': 'Hand on the Leaders Task',
  'leaderTasks.toPerson': 'Someone in the department',
  'leaderTasks.toPersonHint': 'One person does it, as a task of their own.',
  'leaderTasks.toWorkflow': 'A workflow',
  'leaderTasks.toWorkflowHint': 'Level 1 first, then each next level in turn.',
  'leaderTasks.person': 'Person',
  'leaderTasks.personHint': 'People in the departments {name} leads.',
  'leaderTasks.notOnProject': 'not on the project',
  'leaderTasks.notOnProjectError': '{name} is not on this project. Choose someone on it, or make it a general task.',
  'leaderTasks.nobodyToHandTo': 'There is nobody else in the departments {name} leads.',
  'leaderTasks.handedOnTo': 'Handed on to {name}.',
  'leaderTasks.handedOnThroughWorkflow': 'Handed on through the workflow.',
  'leaderTasks.workflowPeopleHint': 'Everyone on it is in the departments {name} leads.',
  'leaderTasks.filesGoWith': 'Its files go with the work, on every task it becomes.',
  'leaderTasks.datesHint': 'The work’s own start and deadline — level 1’s, for a workflow.',
  'leaderTasks.cannotHandOnUnderWay': 'Its work is under way. It can be handed on again only if that work is cancelled.',
  'leaderTasks.cannotHandOnStatus': 'It is {status}, so it cannot be handed on.',
  'leaderTasks.cannotHandOnNotYours': 'Only the leader who has it, or an admin, hands it on.',
  'leaderTasks.reassignTitle': 'Give to another leader',
  'leaderTasks.currentLeader': 'Now with',
  'leaderTasks.newLeader': 'Give it to',
  'leaderTasks.reassignNote':
    'The leader who has it now is told why. Work already handed on carries on, and the new leader reviews it.',
  'leaderTasks.reassignNotYours': 'Only the leader who sent it, the leader who has it, or an admin can give it to another leader.',
  'leaderTasks.reassigned': 'Given to {name}.',
  'leaderTasks.departmentFixed': 'It is for a department its leader leads: it changes with “Give to another leader”.',
  'leaderTasks.leaderByReassign': 'It goes to another leader with “Give to another leader” on its page.',
  'leaderTasks.editNotYours': 'Only the leader who sent it, or an admin, can edit it.',
  'dash.leaderTasks': 'Leaders Tasks',
  'dash.leaderTasksOpen': '{n} asked of you, still open',
  'dash.leaderTasksReview': '{n} back for your review',
  // A team member sees their own tasks (operator, 2026-10-07)
  'tasks.ownOnlyHint': 'Tasks given to you or your department, or made by you.',
  // A task's to-do list (operator, 2026-10-09)
  'tasks.checklist.title': 'To-do list',
  'tasks.checklist.progress': '{done} of {total} done',
  'tasks.checklist.empty': 'Nothing on the list yet. Add what needs doing, one item at a time.',
  'tasks.checklist.addPlaceholder': 'Add an item',
  'tasks.checklist.editLine': 'Edit the item',
  'tasks.checklist.removeLine': 'Remove the item',
  'tasks.checklist.removed': 'Removed from the to-do list.',
  'tasks.checklist.doneBy': 'Done by {name} · {when}',
  'tasks.checklist.savedAsYouGo':
    'Each change to the list is saved as you make it, whatever the task’s status — Save is for the fields above.',
  'tasks.checklist.whoChanges': 'Whoever created the task, whoever has it or shares it, team leaders and admins change this list.',
  'tasks.checklist.full': 'A to-do list holds up to {n} items.',
  'tasks.checklist.chipTitle': 'To-do list: {done} of {total} done',
  'tasks.activity.checklistAdded': 'Added to the to-do list',
  'tasks.activity.checklistDone': 'Ticked off on the to-do list',
  'tasks.activity.checklistUndone': 'Unticked on the to-do list',
  'tasks.activity.checklistRenamed': 'Reworded on the to-do list',
  'tasks.activity.checklistRemoved': 'Removed from the to-do list',
  'tasks.activity.viewed': 'Viewed',
  // Internal mail between employees, inside the system only (operator, 2026-10-09)
  'nav.mail': 'Mail',
  'mail.title': 'Mail',
  'mail.subtitle': 'Messages between the people in the company — inside the system only, never sent by email.',
  'mail.new': 'New mail',
  'mail.folder.inbox': 'Inbox',
  'mail.folder.sent': 'Sent',
  'mail.folder.trash': 'Trash',
  'mail.search': 'Search subjects, content and names',
  'mail.unreadOnly': 'Unread only',
  'mail.markAllRead': 'Mark all as read',
  'mail.allRead': 'Everything in your Inbox is read.',
  'mail.empty.inbox': 'Your Inbox is empty',
  'mail.empty.inboxHint': 'Mail other employees send you arrives here, and the bell tells you about it.',
  'mail.empty.unread': 'Nothing unread',
  'mail.empty.unreadHint': 'You have read everything in your Inbox.',
  'mail.empty.sent': 'Nothing sent yet',
  'mail.empty.sentHint': 'The mail you send to other employees is kept here.',
  'mail.empty.trash': 'The Trash is empty',
  'mail.empty.trashHint': 'Mail you delete waits here until you restore it or delete it for good.',
  'mail.toNames': 'To {names}',
  'mail.more': '+{n}',
  'mail.readBy': 'Read by {read} of {total}',
  'mail.readByAll': 'Read by everyone',
  'mail.notReadYet': 'Not read yet',
  'mail.read': 'Read',
  'mail.readAt': 'Read {when}',
  'mail.newBadge': 'New',
  'mail.replyBadge': 'Reply',
  'mail.forwardBadge': 'Forward',
  'mail.files': 'Files',
  'mail.voiceNotes': 'Voice notes',
  'mail.unreadCount': '{n} unread',
  'mail.from': 'From',
  'mail.to': 'To',
  'mail.date': 'Date',
  'mail.you': 'You',
  'mail.left': 'No longer an employee',
  'mail.inReplyTo': 'In reply to',
  'mail.forwardOf': 'Forwarded mail',
  'mail.noContent': 'No content — the subject and the files say it all.',
  'mail.inTrash': 'This mail is in your Trash. Restore it to put it back, or delete it for good.',
  'mail.reply': 'Reply',
  'mail.replyAll': 'Reply all',
  'mail.forward': 'Forward',
  'mail.markUnread': 'Mark as unread',
  'mail.markedUnread': 'Marked as unread.',
  'mail.delete': 'Delete',
  'mail.movedToTrash': 'Moved to the Trash.',
  'mail.restore': 'Restore',
  'mail.restoredInbox': 'Back in your Inbox.',
  'mail.restoredSent': 'Back in Sent.',
  'mail.deleteForever': 'Delete for good',
  'mail.deleteForeverConfirm': 'Delete this mail for good? It leaves your mailbox; everyone else on it keeps theirs.',
  'mail.keep': 'Keep it',
  'mail.deletedForever': 'Deleted for good.',
  'mail.composeSubtitle': 'Inside the system only — nobody gets an email. It arrives in their Inbox, and the bell tells them.',
  'mail.replyTitle': 'Reply',
  'mail.forwardTitle': 'Forward',
  'mail.toLabel': 'To',
  'mail.toHint': 'Any employee. Everyone you add sees who else it went to — there is no Cc or Bcc.',
  'mail.toPlaceholder': 'Type a name to add someone',
  'mail.toLoading': 'Loading the people…',
  'mail.toNoMatch': 'Nobody by that name.',
  'mail.toRemove': 'Remove {name}',
  'mail.toMax': 'A mail goes to at most {n} people.',
  'mail.needRecipient': 'Choose at least one person.',
  'mail.subject': 'Subject',
  'mail.needSubject': 'Give the mail a subject.',
  'mail.content': 'Content',
  'mail.send': 'Send',
  'mail.sent': 'Mail sent.',
  'mail.sentTo': 'Mail sent. People it went to: {n}',
  'mail.passedOn': 'Files passed on',
  'mail.passedOnHint': 'The forwarded mail’s files go with this one. Leave out any you do not want to pass on.',
  'mail.leaveOut': 'Leave out',
  'mail.putBack': 'Put back',
  'mail.leftOut': 'Left out',
  'mail.quoteHeader': 'On {date}, {name} wrote:',
  'mail.forwardHeader': '———— Forwarded mail ————',
  'mail.forwardFrom': 'From: {name}',
  'mail.forwardDate': 'Date: {date}',
  'mail.forwardSubject': 'Subject: {subject}',
  'mail.forwardTo': 'To: {names}',
  'mail.someoneLeft': 'No longer employees, so left out: {names}',
  'mail.notEmployee': 'Someone you chose is no longer an active employee. Take them off and send again.',
  'mail.sourceMissing': 'The mail you are answering is no longer in your mailbox.',
  'mail.tooLong': 'The mail is too long. Shorten it, or send the rest as a file.',
  'mail.sendMail': 'Send mail',

  // Chat between two employees, a page like WhatsApp Web under Mail — inside the system only (operator, 2026-10-19).
  'nav.chat': 'Chat',
  'chat.title': 'Chats',
  'chat.with': 'Chat with {name}',
  'chat.new': 'New chat',
  'chat.search': 'Search or start a new chat',
  'chat.searchPeople': 'Search by name, job or department',
  'chat.filter.label': 'Show',
  'chat.filter.all': 'All',
  'chat.filter.unread': 'Unread',
  'chat.people': 'Start a chat with',
  'chat.everyone': 'Everyone in the company',
  'chat.peopleLoading': 'Loading the people…',
  'chat.noPeople': 'Nobody by that name.',
  'chat.noMatch': 'No chat or person by that name.',
  'chat.empty.title': 'No chats yet',
  'chat.empty.hint': 'Start one with anyone in the company, or a group. It stays inside the system — only its own people read it.',
  'chat.empty.unread': 'Nothing unread',
  'chat.empty.unreadHint': 'You have read every chat.',
  'chat.noMessages': 'No messages yet',
  'chat.unreadCount': '{n} unread',
  'chat.unreadChats': 'Chats with unread messages: {n}',
  'chat.intro.title': 'Viganium Chat',
  'chat.intro.body': 'Pick a chat, or start a new one with anyone in the company — or a group.',
  'chat.intro.private': 'Inside the system only — a chat is read by its own people alone.',
  'chat.back': 'Back to the chats',
  'chat.left': 'No longer an employee',
  'chat.cannotSend': '{name} is no longer an employee — you can read this chat, but not write to it.',
  'chat.notFound': 'This chat is not available',
  'chat.notFoundHint': 'It may have been deleted, or it is not yours.',
  'chat.openFailed': 'The chat could not be opened.',
  'chat.start': 'This is the start of your chat with {name}. Only the two of you can read it.',
  'chat.startHint': 'Say hello — a message, a photo or a voice note.',
  'chat.today': 'Today',
  'chat.yesterday': 'Yesterday',
  'chat.unreadOne': '1 unread message',
  'chat.unreadMany': '{n} unread messages',
  'chat.loadingOlder': 'Loading earlier messages…',
  'chat.jumpDown': 'Go to the newest message',
  'chat.placeholder': 'Type a message',
  'chat.send': 'Send',
  'chat.emoji': 'Emoji',
  'chat.attach': 'Send photos or files',
  'chat.record': 'Record a voice note',
  'chat.sending': 'Sending',
  'chat.sent': 'Sent',
  'chat.read': 'Read',
  'chat.failed': 'Not sent',
  'chat.retry': 'Try again',
  'chat.discard': 'Remove',
  'chat.preview.voice': 'Voice note',
  'chat.preview.photo': 'Photo',
  'chat.preview.photos': '{n} photos',
  'chat.preview.video': 'Video',
  'chat.preview.file': 'File',
  'chat.preview.files': '{n} files',
  'chat.preview.attachments': '{n} attachments',
  'chat.viewer.open': 'Open {name}',
  'chat.viewer.position': '{i} of {n}',
  'chat.viewer.previous': 'Previous photo',
  'chat.viewer.next': 'Next photo',
  'chat.emoji.recent': 'Used lately',
  'chat.emoji.noRecent': 'The emoji you use show here.',
  'chat.emoji.smileys': 'Smileys and people',
  'chat.emoji.nature': 'Animals and nature',
  'chat.emoji.food': 'Food and drink',
  'chat.emoji.activities': 'Activities',
  'chat.emoji.travel': 'Travel and places',
  'chat.emoji.objects': 'Objects',
  'chat.emoji.symbols': 'Symbols',
  // Replies, forwards, "online" and what Enter does (operator, 2026-10-19).
  'chat.online': 'online',
  'chat.settings': 'Chat settings',
  'chat.enterSends': 'Enter sends the message',
  'chat.enterSendsOn': 'Shift+Enter starts a new line.',
  'chat.enterSendsOff': 'Off: Enter starts a new line, and Ctrl+Enter sends.',
  'chat.sendCtrl': 'Send (Ctrl+Enter)',
  'chat.menu': 'Message options',
  'chat.reply': 'Reply',
  'chat.forward': 'Forward',
  'chat.forwarded': 'Forwarded',
  'chat.you': 'You',
  'chat.replyingTo': 'Replying to {name}',
  'chat.cancelReply': 'Cancel the reply',
  'chat.quoteJump': 'Go to the message',
  'chat.quoteGone': 'That message is too far back to show here.',
  // Mentions (operator, 2026-10-22: "use @ for mention person and # to mention task or ticket").
  'chat.mention.people': 'Mention someone',
  'chat.mention.references': 'Mention a task or ticket',
  'chat.mention.inChat': 'In this chat',
  'chat.mention.others': 'Everyone else',
  'chat.mention.task': 'Task',
  'chat.mention.leaderTask': 'Leaders task',
  'chat.mention.searching': 'Looking…',
  'chat.mention.noMatch': 'No task or ticket you can open matches “{q}”.',
  'chat.mention.none': 'No task or ticket to mention yet.',
  'chat.mention.keys': '↑ ↓ to choose · Enter or Tab to pick · Esc to close',
  'chat.mention.message': 'Message {name}',
  'chat.mention.you': 'You',
  'chat.mention.left': '{name} is no longer an employee',
  'chat.mention.openTask': 'Open the task: {title} — {status}',
  'chat.mention.openTicket': 'Open ticket {number}: {title} — {status}',
  'chat.mention.cannotOpenTask': 'A task you cannot open',
  'chat.mention.cannotOpenTicket': 'A ticket you cannot open',
  'chat.mention.unread': 'You are mentioned',
  'chat.mention.gone': 'That message is no longer in the chat.',
  'chat.forwardTitle': 'Forward the message',
  'chat.forwardHint': 'Choose up to {n} chats — groups or people. Each gets it, marked forwarded.',
  'chat.forwardDone': 'Forwarded.',
  'chat.forwardDoneMany': 'Forwarded to {n} chats.',
  'chat.forwardMax': 'That is the most: {n} chats.',
  'chat.forwardChosen': 'Chosen: {n}',
  // Reactions (operator, 2026-10-20: "i can put like for example on this message").
  'chat.react': 'React',
  'chat.reactWith': 'React with {emoji}',
  'chat.reactionTakeOff': 'Take off your {emoji}',
  'chat.reactMore': 'More emoji',
  'chat.reactions': 'Reactions',
  'chat.reactionsLabel': 'Reactions: {list}',
  'chat.reactionRemove': 'Click to take it off',
  // Stickers (operator, 2026-10-20: "in chat system i want to add stickers to chats"; the packs are the administrators').
  'chat.stickers': 'Stickers',
  'chat.stickers.recent': 'Sent lately',
  'chat.stickers.noRecent': 'The stickers you send show here.',
  'chat.stickers.none': 'No stickers yet. An administrator adds the sticker packs.',
  'chat.stickers.failed': 'The stickers could not be loaded. Close this and try again.',
  'chat.stickers.send': 'Send the sticker {label}',
  'chat.stickers.manage': 'Manage sticker packs',
  'chat.stickerAlt': 'Sticker: {label}',
  'chat.preview.sticker': 'Sticker',
  // Groups (operator, 2026-10-22: "anyone can create group and add employees … he must accept not add auto no auto option").
  'chat.unreadChatsInvites': 'Chats with unread messages: {n} · Group invitations: {m}',
  'chat.newGroup': 'New group',
  'chat.filter.groups': 'Groups',
  'chat.empty.groups': 'No groups yet',
  'chat.empty.groupsHint': 'Start one and invite anyone in the company. Each joins once they accept.',
  'chat.invites.title': 'Group invitations',
  'chat.invites.from': '{name} invited you',
  'chat.invites.accept': 'Accept',
  'chat.invites.decline': 'Decline',
  'chat.invites.acceptLabel': 'Accept the invitation to {name}',
  'chat.invites.declineLabel': 'Decline the invitation to {name}',
  'chat.invites.accepted': 'You joined {name}.',
  'chat.invites.declined': 'Invitation declined.',
  'chat.invites.hint': 'You are not in a group until you accept. Once you do, you read what was said from the invitation on.',
  'chat.group.memberOne': '1 member',
  'chat.group.membersCount': '{n} members',
  'chat.group.kind': 'Group',
  'chat.group.info': 'Group info',
  'chat.group.openInfo': 'Open the group info',
  'chat.group.closeInfo': 'Close the group info',
  'chat.group.with': 'Group {name}',
  'chat.group.start': 'This is the start of {name}. It stays inside the system — only its members read it, each from when they were invited.',
  'chat.group.startLater': 'Messages in {name} stay inside the system. You read them from when you were invited.',
  'chat.group.startHint': 'Say hello, or invite people from the group info.',
  'chat.group.earlier': 'A message from before you were invited',
  'chat.group.someone': 'A former member',
  'chat.group.createdBy': 'Created by {name} on {date}',
  'chat.group.createdByYou': 'Created by you on {date}',
  'chat.group.createdOn': 'Created on {date}',
  'chat.group.rename': 'Rename the group',
  'chat.group.renamed': 'Group renamed.',
  'chat.group.members': 'Members',
  'chat.group.invite': 'Invite people',
  'chat.group.admin': 'Group admin',
  'chat.group.adminNote': 'Admins rename the group, take people out and make others admins. Anyone in it invites people.',
  'chat.group.memberMenu': 'Options for {name}',
  'chat.group.message': 'Message {name}',
  'chat.group.makeAdmin': 'Make group admin',
  'chat.group.dismissAdmin': 'Dismiss as admin',
  'chat.group.remove': 'Remove from the group',
  'chat.group.removeConfirm': 'Remove {name} from {group}?',
  'chat.group.removeBody': 'They no longer read or write in the group, and are told in their notifications. To come back, someone in it invites them again.',
  'chat.group.removed': '{name} was removed from the group.',
  'chat.group.madeAdmin': '{name} is a group admin now.',
  'chat.group.dismissedAdmin': '{name} is no longer a group admin.',
  'chat.group.invited': 'Invited — waiting for them to accept',
  'chat.group.invitedBy': 'Invited by {name} · {when}',
  'chat.group.invitedByYou': 'Invited by you · {when}',
  'chat.group.takeBack': 'Take back',
  'chat.group.takeBackLabel': 'Take back the invitation to {name}',
  'chat.group.takenBack': 'Invitation taken back.',
  'chat.group.leave': 'Leave the group',
  'chat.group.leaveConfirm': 'Leave {name}?',
  'chat.group.leaveBody': 'You will no longer read or write in it. To come back, someone in it invites you again.',
  'chat.group.leaveLast': 'You are the last one in it: leaving deletes the group, with everything said in it.',
  'chat.group.left': 'You left the group.',
  'chat.group.noLongerEmployee': 'No longer an employee',
  'chat.newGroup.people': 'Invite to the group',
  'chat.newGroup.hint': 'Everyone you choose gets an invitation, and joins only once they accept.',
  'chat.newGroup.next': 'Next',
  'chat.newGroup.chosen': 'Chosen: {n}',
  'chat.newGroup.untick': 'Take {name} off',
  'chat.newGroup.name': 'Group name',
  'chat.newGroup.namePlaceholder': 'Name the group',
  'chat.newGroup.inviting': 'Invitations go to: {names}',
  'chat.newGroup.nobody': 'Nobody invited yet — you can invite people from the group info later.',
  'chat.newGroup.create': 'Create group',
  'chat.newGroup.created': 'Group created. The invitations are sent.',
  'chat.newGroup.createdAlone': 'Group created.',
  'chat.inviteTitle': 'Invite to {name}',
  'chat.inviteHint': 'Each person you choose gets an invitation, and joins only once they accept. They read what is said from now on.',
  'chat.inviteSend': 'Invite',
  'chat.inviteDone': 'Invitations sent: {n}',
  'chat.inviteNobody': 'Everyone is in the group, or invited to it.',
  'chat.forwardGroups': 'Groups',
  'chat.forwardPeople': 'People',
  'chat.event.created': '{who} created the group “{name}”',
  'chat.event.createdYou': 'You created the group “{name}”',
  'chat.event.renamed': '{who} renamed the group to “{name}”',
  'chat.event.renamedYou': 'You renamed the group to “{name}”',
  'chat.event.joined': '{who} joined — invited by {by}',
  'chat.event.joinedYou': 'You joined — invited by {by}',
  'chat.event.joinedByYou': '{who} joined — invited by you',
  'chat.event.joinedAlone': '{who} joined',
  'chat.event.joinedYouAlone': 'You joined',
  'chat.event.left': '{who} left',
  'chat.event.leftYou': 'You left',
  'chat.event.removed': '{who} removed {whom}',
  'chat.event.removedYou': 'You removed {whom}',
  'chat.event.removedYouBy': '{who} removed you',

  // My notes — everyone's own to-do list and sticky notes, which tag tasks and tickets with @ (operator, 2026-10-15).
  'nav.notes': 'My notes',
  'nav.todos': 'My to-do list',
  'notes.title': 'My notes',
  'notes.sticky.subtitle': 'Your own sticky notes. Nobody else sees them — not even an administrator.',
  'notes.todo.title': 'My to-do list',
  'notes.todo.subtitle': 'Your own list of what to get done. Nobody else sees it — not even an administrator.',
  'notes.todo.label': 'A to-do',
  'notes.todo.placeholder': 'Add a to-do — type @ to tag a task or ticket',
  'notes.todo.dueLabel': 'The day it is for',
  'notes.todo.clearDue': 'Clear the day',
  'notes.todo.today': 'Today',
  'notes.todo.tomorrow': 'Tomorrow',
  'notes.todo.overdue': 'Overdue · {date}',
  'notes.todo.empty': 'Nothing to do yet',
  'notes.todo.emptyHint': 'Add what you need to get done. Type @ to tag a task or ticket given to you.',
  'notes.todo.allDone': 'All done — nothing left open.',
  'notes.todo.done': 'Done',
  'notes.todo.doneWhen': 'Done {when}',
  'notes.todo.clearDone': 'Clear done',
  'notes.todo.clearDoneConfirm': 'Clear the done lines?',
  'notes.todo.clearDoneBody': 'Done lines: {n}. They are deleted for good.',
  'notes.todo.cleared': 'Done lines cleared.',
  'notes.todo.edit': 'Edit the line',
  'notes.todo.remove': 'Remove the line',
  'notes.todo.removed': 'Line removed.',
  'notes.todo.drag': 'Drag to reorder',
  'notes.sticky.label': 'Note',
  'notes.sticky.placeholder': 'Take a note — type @ to tag a task or ticket',
  'notes.sticky.done': 'Done',
  'notes.sticky.doneHint': 'Saved as you type',
  'notes.sticky.doneKeys': 'Ctrl+Enter or Esc',
  'notes.sticky.saving': 'Saving…',
  'notes.sticky.saved': 'Saved',
  'notes.sticky.notSaved': 'Not saved: {reason}',
  'notes.sticky.emptyNotKept': 'An empty note is not kept — write something, tag something or add a picture.',
  'notes.sticky.emptyDeleteConfirm': 'Delete this empty note?',
  'notes.sticky.emptyDeleteBody': 'It has nothing left in it: no words, tags or pictures.',
  'notes.sticky.gone': 'This note was deleted in another tab.',
  'notes.sticky.characters': '{n} of {max} characters',
  'notes.sticky.addImage': 'Add a picture',
  'notes.sticky.removeImage': 'Remove the picture',
  'notes.sticky.openImage': 'Open {name}',
  'notes.sticky.uploading': 'Uploading {name}…',
  'notes.sticky.uploadFailed': 'Could not add {name}: {reason}',
  'notes.sticky.waitForUpload': 'Wait for the pictures to finish uploading.',
  'notes.sticky.pinned': 'Pinned',
  'notes.sticky.others': 'Others',
  'notes.sticky.pin': 'Pin',
  'notes.sticky.unpin': 'Unpin',
  'notes.sticky.color': 'Colour',
  'notes.sticky.edit': 'Edit the note',
  'notes.sticky.delete': 'Delete the note',
  'notes.sticky.drag': 'Drag to move the note',
  'notes.sticky.deleteConfirm': 'Delete this note?',
  'notes.sticky.deleteBody': 'It is deleted for good.',
  'notes.sticky.deleted': 'Note deleted.',
  'notes.sticky.added': 'Added {when}',
  'notes.sticky.edited': 'Edited {when}',
  'notes.sticky.empty': 'No notes yet',
  'notes.sticky.emptyHint': 'Jot down whatever you want to keep in view. Type @ to tag a task or ticket given to you.',
  'notes.color.AMBER': 'Amber',
  'notes.color.CRIMSON': 'Crimson',
  'notes.color.CYAN': 'Cyan',
  'notes.color.GREEN': 'Green',
  'notes.color.VIOLET': 'Violet',
  'notes.color.GRAPHITE': 'Graphite',
  'notes.tag.button': 'Tag a task or ticket (@)',
  'notes.tag.loading': 'Loading your tasks and tickets…',
  'notes.tag.none': 'Nothing open is given to you right now.',
  'notes.tag.noMatch': 'None of your open tasks or tickets matches.',
  'notes.tag.max': 'Up to {n} tags.',
  'notes.tag.group.TASK': 'Your tasks',
  'notes.tag.group.Q_TASK': 'Your Q Tasks',
  'notes.tag.group.TICKET': 'Your tickets',
  'notes.tag.kind.TASK': 'Task',
  'notes.tag.kind.LEADER_TASK': 'Leaders Task',
  'notes.tag.kind.Q_TASK': 'Q Task',
  'notes.tag.kind.TICKET': 'Ticket',
  'notes.tag.due': 'due {date}',
  'notes.tag.unavailable': '{kind} not available',
  'notes.tag.unavailableHint': 'You can no longer open it: it was taken from you, or removed.',
  'notes.tag.notYours': 'no longer given to you',
  'notes.tag.remove': 'Remove the tag',
  'notes.problem.EMPTY': 'Write something, or tag a task or ticket.',
  'notes.problem.EMPTY_NOTE': 'Write something, tag a task or ticket, or add a picture.',
  'notes.problem.NOT_AVAILABLE': 'You can tag only tasks and tickets you can open.',
  'notes.problem.NOT_A_DAY': 'That is not a day on the calendar.',
  'notes.problem.TOO_LONG': 'A note holds up to {n} characters.',
  'notes.problem.TOO_MANY_IMAGES': 'A note shows up to {n} pictures.',
  'notes.problem.NOT_AN_IMAGE': 'Only pictures go on a note: PNG, JPG, GIF or WebP.',
  'notes.problem.NOT_ATTACHABLE': 'That picture cannot go on this note. Add it again.',
  'notes.reloaded': 'Your notes changed in another tab, so they were reloaded. Try again.',
  'notes.drag.instructions': 'To move it, press Space, then the arrow keys. Space puts it down; Escape puts it back.',
  'notes.drag.picked': 'Picked up: {name}.',
  'notes.drag.over': '{name}: place {position} of {total}.',
  'notes.drag.dropped': '{name} is now at place {position} of {total}.',
  'notes.drag.cancelled': '{name} was put back where it was.',
  'editor.toolbar': 'Formatting',
  'editor.bold': 'Bold',
  'editor.italic': 'Italic',
  'editor.underline': 'Underline',
  'editor.strike': 'Strikethrough',
  'editor.heading2': 'Heading',
  'editor.heading3': 'Subheading',
  'editor.bulletList': 'Bulleted list',
  'editor.orderedList': 'Numbered list',
  'editor.quote': 'Quote',
  'editor.rule': 'Divider',
  'editor.link': 'Link',
  'editor.unlink': 'Remove link',
  'editor.undo': 'Undo',
  'editor.redo': 'Redo',
  'editor.linkUrl': 'Link address',
  'editor.linkApply': 'Apply',
  'editor.linkInvalid': 'Enter a web address (https://…) or an email address.',
  // The employee mobile app’s activity log (administrators).
  'nav.mobileActivity': 'Mobile activity',
  'nav.chatStickers': 'Chat stickers',
  // The chat's sticker packs — the administrators' (`chat.stickers.manage`, 2026-10-20).
  'stickers.title': 'Chat stickers',
  'stickers.subtitle': 'The sticker packs everyone can send in the chat, in the order the chat shows them.',
  'stickers.newPack': 'New pack',
  'stickers.editPack': 'Edit the pack',
  'stickers.formNote': 'Name the pack as the chat’s sticker tray shows it.',
  'stickers.packName': 'Name in English',
  'stickers.packNameAr': 'Name in Arabic',
  'stickers.packNameArHint': 'Without one, the Arabic chat shows the English name.',
  'stickers.inChat': 'In the chat',
  'stickers.shown': 'Shown in the chat',
  'stickers.shownHint': 'Hidden, its stickers can no longer be sent. Those already sent still show.',
  'stickers.hidden': 'Hidden',
  'stickers.hide': 'Hide from the chat',
  'stickers.show': 'Show in the chat',
  'stickers.count': '{n} of {max} stickers',
  'stickers.add': 'Add stickers',
  'stickers.addHint': 'PNG, WebP, GIF or JPEG, up to {mb} MB each. Large still pictures are made 512 px. Each sticker is named after its file.',
  'stickers.uploading': 'Uploading…',
  'stickers.addedOne': 'Sticker added.',
  'stickers.addedMany': 'Stickers added: {n}.',
  'stickers.full': 'This pack is full: {max} stickers at most.',
  'stickers.room': 'Only {n} more fit in this pack, so the first {n} were added.',
  'stickers.tooLarge': '{name} is larger than {mb} MB, even made smaller.',
  'stickers.notPicture': '{name} is not a PNG, WebP, GIF or JPEG picture.',
  'stickers.unreadable': '{name} could not be read as a picture.',
  'stickers.storage': 'The file storage could not be reached. Try again; if it keeps failing, check the storage settings.',
  'stickers.sentMany': 'Sent {n} times',
  'stickers.sentOnce': 'Sent once',
  'stickers.neverSent': 'Not sent yet',
  'stickers.delete': 'Delete {label}',
  'stickers.deleteConfirm': 'Delete this sticker?',
  'stickers.deleteBodySent': 'It leaves the pack, and nobody can send it again. The messages it was sent on still show it.',
  'stickers.deleteBodyUnsent': 'It has never been sent, so it is deleted for good.',
  'stickers.deleted': 'Sticker deleted.',
  'stickers.deletePack': 'Delete the pack',
  'stickers.deletePackConfirm': 'Delete {name}?',
  'stickers.deletePackBody': 'Its stickers leave the chat. Those already sent still show in their messages; the rest are deleted for good.',
  'stickers.packDeleted': 'Sticker pack deleted.',
  'stickers.packCreated': 'Sticker pack created. Now add its stickers.',
  'stickers.packSaved': 'Sticker pack saved.',
  'stickers.packShown': '{name} is shown in the chat.',
  'stickers.packHidden': '{name} is hidden from the chat.',
  'stickers.moveUp': 'Move up',
  'stickers.moveDown': 'Move down',
  'stickers.orderSaved': 'Order saved.',
  'stickers.empty': 'No sticker packs yet',
  'stickers.emptyHint': 'Create a pack, then add its pictures. Everyone can send them in the chat.',
  'stickers.emptyPack': 'No stickers in this pack yet. The chat shows it once it has one.',
  'stickers.packsMax': 'There are {max} packs, the most there may be. Delete one to make another.',
  'mobile.title': 'Mobile app activity',
  'mobile.subtitle': 'Everything done in the employee mobile app — sign-ins, what was opened, replies, mail sent, leave asked for — and everything it refused. Administrators only.',
  'mobile.tabActivity': 'Activity',
  'mobile.tabPeople': 'People',
  'mobile.tabPhones': 'Phones',
  'mobile.metricActions': 'Actions today',
  'mobile.metricPeople': 'People active today',
  'mobile.metricRefused': 'Refused today',
  'mobile.metricSignedIn': 'Signed in on a phone',
  'mobile.person': 'Person',
  'mobile.allPeople': 'Everyone',
  'mobile.area': 'Area',
  'mobile.allAreas': 'Every area',
  'mobile.outcome': 'Outcome',
  'mobile.allOutcomes': 'Every outcome',
  'mobile.outcomeSUCCESS': 'Done',
  'mobile.outcomeREFUSED': 'Refused',
  'mobile.outcomeFAILED': 'Failed',
  'mobile.searchPlaceholder': 'Search a name, a ticket number, a title…',
  'mobile.when': 'When',
  'mobile.what': 'What',
  'mobile.phone': 'Phone',
  'mobile.result': 'Result',
  'mobile.noActivity': 'Nothing has been done in the mobile app yet — or nothing matches these filters.',
  'mobile.noPeople': 'Nobody has used the mobile app yet.',
  'mobile.noPhones': 'No phone has signed in to the mobile app yet.',
  'mobile.activities': 'Actions',
  'mobile.lastActivity': 'Last action',
  'mobile.phonesSignedIn': 'Phones signed in',
  'mobile.showActivity': 'Show their activity',
  'mobile.appVersion': 'App version',
  'mobile.signedIn': 'Signed in',
  'mobile.signedOut': 'Signed out',
  'mobile.lastSeen': 'Last seen',
  'mobile.status': 'Status',
  'mobile.unknownPerson': 'Nobody — {email}',
  'mobile.details': 'Details',
  'mobile.request': 'Request',
  'mobile.duration': 'Took',
  'mobile.errorCode': 'Error',
  'mobile.openRecord': 'Open',
  'mobile.reportedByApp': 'Reported by the app',
  'mobile.privacyNote':
    'A mail’s subject, words and recipients, the words of replies and comments, and what a chat said or with whom are never kept here.',
  'mobile.area.auth': 'Signing in and out',
  'mobile.area.app': 'The app',
  'mobile.area.dashboard': 'Dashboard',
  'mobile.area.notification': 'Notifications',
  'mobile.area.mail': 'Mail',
  'mobile.area.chat': 'Chat',
  'mobile.area.file': 'Files',
  'mobile.area.attachment': 'Opening files',
  'mobile.area.ticket': 'Tickets',
  'mobile.area.task': 'Tasks',
  'mobile.area.leader_task': 'Leaders Tasks',
  'mobile.area.announcement': 'Announcements',
  'mobile.area.client_cr': 'Clients’ change requests',
  'mobile.area.schedule': 'My schedule',
  'mobile.area.leave': 'Leave',
  'mobile.area.payslip': 'Payslips',
  'mobile.area.penalty': 'Violations & penalties',
  'mobile.area.wallet': 'Points wallet',
  'mobile.area.overtime': 'Overtime',
  'mobile.area.remote_work': 'Remote work',
  'mobile.area.meeting': 'Meetings',
  'mobile.area.call': 'Calls',
  'mobile.area.support': 'Support mode',
  'mobile.action.call.history': 'Opened their calls',
  'mobile.action.call.view': 'Opened a call',
  'mobile.action.call.start': 'Made a call',
  'mobile.action.call.invite': 'Added people to a call',
  'mobile.action.call.accept': 'Answered a call',
  'mobile.action.call.decline': 'Declined a call',
  'mobile.action.call.hangup': 'Left a call',
  'mobile.action.call.join': 'Joined a call’s audio',
  'mobile.action.call.recording_play': 'Played a call’s recording',
  'mobile.action.call.status': 'Read whether calls are available',
  'mobile.action.call.people': 'Read whom to call',
  'mobile.action.call.live': 'Read their calls now',
  'mobile.action.call.events': 'Listened for calls',
  'mobile.area.settings': 'Settings',
  'mobile.area.screen': 'Screens',
  'mobile.area.request': 'Refused requests',
  'mobile.action.auth.login': 'Signed in',
  'mobile.action.auth.login_failed': 'Sign-in refused',
  'mobile.action.auth.logout': 'Signed out',
  'mobile.action.app.open': 'Opened the app',
  'mobile.action.app.resume': 'Came back to the app',
  'mobile.action.dashboard.view': 'Opened the dashboard',
  'mobile.action.notification.list': 'Opened notifications',
  'mobile.action.notification.read': 'Marked notifications read',
  'mobile.action.notification.open': 'Opened a notification',
  'mobile.action.notification.count': 'Checked for notifications',
  'mobile.action.mail.list': 'Opened mail',
  'mobile.action.mail.view': 'Opened a mail',
  'mobile.action.mail.send': 'Sent a mail',
  'mobile.action.mail.reply': 'Replied to a mail',
  'mobile.action.mail.forward': 'Forwarded a mail',
  'mobile.action.mail.count': 'Checked for mail',
  'mobile.action.mail.recipients': 'Loaded the people to write to',
  'mobile.action.chat.list': 'Loaded the chats',
  'mobile.action.chat.count': 'Checked for chat messages',
  'mobile.action.chat.people': 'Loaded the people to chat with',
  'mobile.action.chat.events': 'Waited for chat messages',
  'mobile.action.chat.start': 'Started a chat',
  'mobile.action.chat.view': 'Opened a chat',
  'mobile.action.chat.messages': 'Loaded a chat’s messages',
  'mobile.action.chat.send': 'Sent a chat message',
  'mobile.action.chat.reply': 'Replied in a chat',
  'mobile.action.chat.sticker': 'Sent a sticker in a chat',
  'mobile.action.chat.stickers': 'Read the stickers',
  'mobile.action.chat.references': 'Looked for a task or ticket to mention',
  'mobile.action.chat.forward': 'Forwarded a chat message',
  'mobile.action.chat.read': 'Read a chat',
  'mobile.action.chat.react': 'Reacted to a chat message',
  'mobile.action.chat.unreact': 'Took a reaction off a chat message',
  // Groups in the app (2026-10-22): what was done, never which group, its name nor who.
  'mobile.action.chat.invitations': 'Loaded the group invitations',
  'mobile.action.chat.group.create': 'Started a chat group',
  'mobile.action.chat.group.rename': 'Renamed a chat group',
  'mobile.action.chat.group.invite': 'Invited people to a chat group',
  'mobile.action.chat.group.uninvite': 'Took back an invitation to a chat group',
  'mobile.action.chat.group.accept': 'Joined a chat group',
  'mobile.action.chat.group.decline': 'Declined an invitation to a chat group',
  'mobile.action.chat.group.leave': 'Left a chat group',
  'mobile.action.chat.group.remove': 'Took someone out of a chat group',
  'mobile.action.chat.group.admin': 'Made someone a chat group’s admin',
  'mobile.action.chat.group.unadmin': 'Dismissed a chat group’s admin',
  'mobile.action.file.upload': 'Uploaded a file',
  'mobile.action.file.upload_start': 'Started an upload',
  'mobile.action.file.policy': 'Loaded the upload limits',
  'mobile.action.attachment.open': 'Opened a file',
  'mobile.action.ticket.list': 'Opened tickets',
  'mobile.action.ticket.view': 'Opened a ticket',
  'mobile.action.ticket.reply': 'Replied on a ticket',
  'mobile.action.ticket.claim': 'Took a ticket (Assign to me)',
  'mobile.action.support.view': 'Opened support mode',
  'mobile.action.support.start': 'Switched support mode on',
  'mobile.action.support.end': 'Switched support mode off',
  'mobile.action.task.list': 'Opened tasks',
  'mobile.action.task.view': 'Opened a task',
  'mobile.action.task.comment': 'Commented on a task',
  'mobile.action.leader_task.list': 'Opened Leaders Tasks',
  'mobile.action.leader_task.view': 'Opened a Leaders Task',
  'mobile.action.leader_task.comment': 'Commented on a Leaders Task',
  'mobile.action.announcement.list': 'Opened announcements',
  'mobile.action.announcement.view': 'Opened an announcement',
  'mobile.action.client_cr.list': 'Opened clients’ change requests',
  'mobile.action.client_cr.view': 'Opened a change request',
  'mobile.action.schedule.view': 'Opened my schedule',
  'mobile.action.schedule.holidays': 'Loaded the holidays',
  'mobile.action.leave.list': 'Opened leave',
  'mobile.action.leave.view': 'Opened a leave request',
  'mobile.action.leave.request': 'Asked for leave',
  'mobile.action.leave.types': 'Loaded the leave types',
  'mobile.action.leave.balance': 'Loaded the leave balance',
  'mobile.action.leave.preview': 'Counted the days of a leave',
  'mobile.action.payslip.list': 'Opened payslips',
  'mobile.action.payslip.view': 'Opened a payslip',
  'mobile.action.penalty.list': 'Opened violations & penalties',
  'mobile.action.penalty.view': 'Opened a penalty',
  'mobile.action.penalty.object': 'Objected to a penalty',
  'mobile.action.wallet.view': 'Opened the points wallet',
  'mobile.action.wallet.history': 'Loaded the points history',
  'mobile.action.wallet.penalties': 'Loaded the deductions points can cancel',
  'mobile.action.wallet.redeem': 'Redeemed points',
  'mobile.action.overtime.list': 'Opened overtime',
  'mobile.action.overtime.count': 'Loaded the overtime waiting',
  'mobile.action.overtime.view': 'Opened an overtime',
  'mobile.action.overtime.accept': 'Accepted overtime',
  'mobile.action.overtime.decline': 'Declined overtime',
  'mobile.action.remote_work.list': 'Opened remote work',
  'mobile.action.remote_work.count': 'Loaded the remote work waiting',
  'mobile.action.remote_work.preview': 'Previewed remote work days',
  'mobile.action.remote_work.view': 'Opened a remote work request',
  'mobile.action.remote_work.request': 'Asked to work remotely',
  'mobile.action.remote_work.approve': 'Approved remote work',
  'mobile.action.remote_work.reject': 'Rejected remote work',
  'mobile.action.remote_work.cancel': 'Cancelled remote work',
  'mobile.action.meeting.list': 'Opened the meetings',
  'mobile.action.meeting.invitations': 'Loaded the meeting invitations',
  'mobile.action.meeting.count': 'Loaded the meeting invitations waiting',
  'mobile.action.meeting.view': 'Opened a meeting',
  'mobile.action.meeting.accept': 'Accepted a meeting',
  'mobile.action.meeting.decline': 'Declined a meeting',
  'mobile.upTo': 'up to {time}',
  'mobile.action.settings.language': 'Changed the language',
  'mobile.action.settings.theme': 'Changed the theme',
  'mobile.action.screen.view': 'Opened a screen',
  'mobile.action.request.refused': 'Tried something the app does not do',
  'mobile.toPeople': 'to {count} people',
  'mobile.toPerson': 'to 1 person',
  'mobile.toGroups': 'into {count} groups',
  'mobile.toGroup': 'into 1 group',
  'mobile.invitedPeople': '{count} people invited',
  'mobile.invitedPerson': '1 person invited',
  'mobile.withFiles': '{count} files',
  'mobile.inChat': 'in a chat',
  'mobile.internalNote': 'internal note',
  'mobile.days': '{count} days',
  'mobile.oneDay': '1 day',
  'mobile.points': '{count} points',
  'mobile.onePoint': '1 point',
  'mobile.bundles': '{count} bundles',
  'mobile.oneBundle': '1 bundle',
  'mobile.total': '{count} found',
  'mobile.page': 'page {page}',
  'mobile.languageAr': 'Arabic',
  'mobile.languageEn': 'English',
  'mobile.themeLight': 'Light',
  'mobile.themeDark': 'Dark',
  'mobile.themeSystem': 'As the phone',
  'mobile.wrongPassword': 'wrong email or password',
  'mobile.accountDisabled': 'account switched off',
  'mobile.route': 'route',
  'mobile.screen.settings': 'Settings',
  'mobile.screen.compose': 'New mail',
  'mobile.screen.more': 'More',
  'mobile.screen.leaveForm': 'Ask for leave',
  'mobile.screen.remoteWorkForm': 'Ask to work remotely',
  'mobile.screen.chats': 'Chats',
  'mobile.screen.chatNew': 'New chat',
  'mobile.screen.walletRedeem': 'Redeem points',
  'mobile.screen.penaltyObjection': 'Object to a penalty',

  // Every shift action asks first, in a dialog (operator, 2026-10-15).
  'attendance.startConfirm': 'Start your shift now?',
  'attendance.startConfirmBody': 'Your worked time counts from this moment.',
  'attendance.breakConfirm': 'Start {name}?',
  'attendance.breakConfirmAction': 'Start',
  'attendance.breakConfirmLimit': 'Up to {limit}.',
  'attendance.breakConfirmPenalty':
    'Up to {limit}. Past that, a penalty of {days} goes to an administrator to decide.',
  'attendance.breakConfirmPerDay': 'Up to {max} a day: {left} left today.',
  'attendance.breakConfirmWork': 'This time counts as work.',
  'attendance.breakConfirmRest': 'This time comes off your worked time.',
  'attendance.resumeConfirm': 'Back to work now?',
  'attendance.resumeConfirmBody': '{name} ends now.',
  'attendance.endConfirmBody':
    'Your worked time stops counting now, and today’s Regular Shift is over for good: it can’t be started again today. After it you may work Free Shifts — tracked, unpaid.',
  'attendance.endConfirmBreak': '{name} ends with it.',

  // Pause work — for administrators and the employees picked in Settings › Attendance (operator,
  // 2026-10-24). Paused time is not worked time: the day's hours are still owed.
  'attendance.pauseWork': 'Pause work',
  'attendance.paused': 'Paused',
  'attendance.status.PAUSED': 'Paused',
  'attendance.pausedSince': 'Paused since {time}',
  'attendance.pausedFor': 'Paused {minutes}',
  'attendance.workPaused': 'Work paused. Get back to work when you are ready.',
  'attendance.pauseConfirm': 'Pause work now?',
  'attendance.pauseConfirmBody': 'The clock stops until you get back to work. A pause has no limit, but paused time is not worked time.',
  'attendance.unpauseConfirmBody': 'Your pause ends now, and the clock runs again.',
  'attendance.endConfirmPause': 'Your pause ends with it.',
  'attendance.hoursLeft': 'You have worked {worked} of today’s {owed}: {missing} still to work.',
  'attendance.hoursDone': 'Today’s {owed} are worked.',
  'attendance.hoursLeftShort': '{missing} of today’s hours still to work',
  'attendance.hoursDoneShort': 'Today’s hours are worked',
  'attendance.shortDayCosts': 'If the day ends short of its hours, {days} of salary is deducted.',
  'attendance.pauseOwesHours': 'Paused time is not worked time. If the day ends short of its hours, {days} of salary is deducted.',
  'attendance.endShortWarning':
    'You have worked {worked} of today’s {owed}. If the day ends short of its hours, {days} of salary is deducted — and once ended, today’s Regular Shift can’t be started again: a Free Shift doesn’t make them up.',
  'attendance.endEarlyWarning':
    'Your shift ends at {end}. Ending it before {from} costs {days} of salary — even with today’s hours worked.',
  'attendance.endOnePenalty': 'If both apply, only one is charged: the larger.',
  'attendance.pausedFirst': 'Your work is paused: get back to work first.',
  'attendance.pauseNotAllowed': 'Pausing work is kept for administrators and the employees picked in Settings › Attendance.',
  'attendance.endReason.WORK_PAUSED': 'work was paused',
  'timeline.PAUSE_STARTED': 'Paused work',
  'timeline.PAUSE_ENDED': 'Back to work',
  'settings.pauseWork': 'Pause work',
  'settings.pauseWorkNote': 'Who may pause their shift. Administrators always can.',
  'settings.pausers': 'Employees who may pause',
  'settings.pausersMax': 'At most {n} people.',
  'settings.pausersHint':
    'Administrators always can. Add the employees who may pause too: they get Pause work beside Take a break, and get back to work when they are ready.',
  'settings.pauseWorkRule':
    'A pause has no limit and no penalty of its own, but paused time is not worked time: whoever pauses still owes the day’s hours. A day that ends short of them costs {days} of salary — the short-day penalty above, as anyone’s does.',
  'settings.pauseWorkRuleNoPenalty':
    'A pause has no limit and no penalty of its own, but paused time is not worked time: whoever pauses still owes the day’s hours. The short-day penalty above is 0, so a short day costs nothing now.',

  // Break types that need approval (operator, 2026-10-18), and who approves them beside the
  // administrators (2026-10-19).
  'settings.needsApproval': 'Needs approval',
  'settings.needsApprovalLabel': 'Needs approval',
  'settings.needsApprovalOn': 'Asked for approval first',
  'settings.needsApprovalOff': 'Starts when pressed',
  'settings.needsApprovalHint':
    'Ticked, pressing it sends a request to the administrators and to the employees chosen below, and the break starts when one of them approves it; a rejection comes with a reason. They take it themselves at once. Its allowance and penalty count from the approval.',
  'settings.breakApprovers': 'Who approves it',
  'settings.breakApproversHint':
    'Administrators always can. Add the employees who may approve it too: they decide every request for this break, from anyone, and take it themselves without asking.',
  'settings.breakApproversMax': 'At most {n} people.',
  'settings.administrators': 'Administrators',
  'attendance.needsApproval': 'Needs approval',
  'attendance.waitingShort': 'Waiting',
  // What is left of a break type's daily limit (2026-10-28).
  'attendance.leftShort': '{n} left',
  'attendance.leftToday': '{n} left today',
  'attendance.usedUpToday': 'None left today',
  'attendance.breakRequests': 'Break requests',
  'attendance.requestConfirm': 'Ask for {name}?',
  'attendance.requestConfirmAction': 'Ask for approval',
  'attendance.requestConfirmBody':
    'It is sent for approval, and it starts when someone approves it. Until then you are on shift and working as usual — stay at your computer.',
  'attendance.requestSent': 'Sent for approval. Your break starts when it is approved.',
  'attendance.requestWaiting': '{name} — waiting for approval',
  'attendance.requestWaitingBody': 'It starts when it is approved. Until then you are working.',
  'attendance.askedAt': 'asked at {time}',
  'attendance.withdrawRequest': 'Withdraw request',
  'attendance.withdrawConfirm': 'Withdraw your {name} request?',
  'attendance.withdrawConfirmBody': 'Nobody can approve it after that. You can ask again later.',
  'attendance.requestWithdrawn': 'Request withdrawn.',
  'attendance.breakWithdrawsRequest': 'This withdraws your {name} request.',
  'attendance.breakNeedsApproval': 'That break needs approval. Ask for it instead.',
  'attendance.requestAlreadyWaiting': 'You already have a break request waiting for approval.',
  'attendance.approvalNote': 'A break marked “Needs approval” is asked for first, and starts when someone approves it.',
  'breakRequest.break': 'Break',
  'breakRequest.asked': 'Asked',
  'breakRequest.decidedBy': 'by {name}',
  'breakRequest.cancel.WITHDRAWN': 'Withdrawn by the employee',
  'breakRequest.cancel.OTHER_BREAK': 'Another break was started',
  'breakRequest.cancel.SHIFT_ENDED': 'The shift ended first',
  'breakRequest.penaltyPast': 'penalty {days} if over',
  'breakRequest.countsAsWork': 'counts as work',
  'breakRequest.offWorked': 'off worked time',
  'breakRequest.perDay': '{n} a day',
  'breakRequest.dailyLimit': 'They have taken that break as many times as it allows today. Reject the request instead.',
  'breakRequest.boardNote':
    'Approving starts the break at once, and the employee is told. Rejecting asks for the reason they read. Nobody decides their own.',
  'breakRequest.noneWaiting': 'No break requests are waiting.',
  'breakRequest.approvedToast': 'Approved — the break has started.',
  'breakRequest.rejectedToast': 'Rejected — they have been told.',
  'breakRequest.rejectTitle': 'Reject the break request',
  'breakRequest.rejectReason': 'Why not? The employee reads this.',
  'breakRequest.noLongerWaiting': 'This request is no longer waiting, so it cannot be rejected.',
  'breakRequest.waitingCount': '{n} break requests waiting',
  'timeline.BREAK_REQUESTED': 'Asked for {type}',
  'timeline.BREAK_REQUEST_APPROVED': '{type} approved by {name}',
  'timeline.BREAK_REQUEST_REJECTED': '{type} not approved by {name}',
  'timeline.BREAK_REQUEST_CANCELLED': '{type} request withdrawn',
  // Monitoring — servers, the projects on them, and their URLs (2026-10-20).
  'nav.groupMonitoring': 'Monitoring',
  'nav.monitoringServers': 'Servers',
  'nav.monitoringUrls': 'URLs',
  'monitoring.servers.title': 'Servers',
  'monitoring.servers.subtitle': 'Our servers and VPSs, and the projects each one runs.',
  'monitoring.servers.new': 'Add server',
  'monitoring.servers.search': 'Search by name, IP, host or provider',
  'monitoring.servers.empty': 'No servers yet',
  'monitoring.servers.emptyHint': 'Add the VPS your projects run on, then the URLs to check on it.',
  'monitoring.server.details': 'Details',
  'monitoring.server.ip': 'IP address',
  'monitoring.server.ipHint': 'IPv4 or IPv6.',
  'monitoring.server.hostname': 'Hostname',
  'monitoring.server.provider': 'Provider',
  'monitoring.server.location': 'Location',
  'monitoring.server.locationPlaceholder': 'Frankfurt, Germany',
  'monitoring.server.projects': 'Projects on this server',
  'monitoring.server.projectsNote': 'A URL on this server may be tagged with one of these.',
  'monitoring.server.projectsHint': 'Tick the projects that run here. With none, it is a general server. A URL on this server may then be tagged with one of them.',
  'monitoring.server.general': 'General server',
  'monitoring.server.generalHint': 'No projects are picked, so its URLs have none.',
  'monitoring.server.selectedCount': '{n} picked',
  'monitoring.server.filterProjects': 'Find a project',
  'monitoring.server.noProjects': 'There are no projects to pick yet.',
  'monitoring.server.deletedProject': 'Deleted',
  'monitoring.server.untagWarning': '{project} is taken off the URLs on this server that name it ({n}). They keep being checked, with no project.',
  'monitoring.server.urls': 'URLs',
  'monitoring.server.noUrls': 'No URLs',
  'monitoring.server.noUrlsHint': 'Add the websites, APIs and ports to check on this server.',
  'monitoring.server.remove': 'Remove server',
  'monitoring.server.removeConfirm': 'Remove {name}?',
  'monitoring.server.removeBody': 'Its URLs ({n}) stop being checked. The server and its URLs are kept, with their history.',
  'monitoring.server.removed': 'Server removed.',
  'monitoring.type.VPS': 'VPS',
  'monitoring.type.CLOUD': 'Cloud',
  'monitoring.type.DEDICATED': 'Dedicated',
  'monitoring.type.OTHER': 'Other',
  'monitoring.health.ONLINE': 'Online',
  'monitoring.health.WARNING': 'Warning',
  'monitoring.health.OFFLINE': 'Offline',
  'monitoring.health.PENDING': 'Not checked yet',
  'monitoring.health.NOT_MONITORED': 'Not monitored',
  'monitoring.status.PENDING': 'Not checked yet',
  'monitoring.status.UP': 'Up',
  'monitoring.status.DEGRADED': 'Degraded',
  'monitoring.status.DOWN': 'Down',
  'monitoring.status.PAUSED': 'Paused',
  'monitoring.count.up': 'up',
  'monitoring.count.degraded': 'degraded',
  'monitoring.count.down': 'down',
  'monitoring.count.pending': 'not checked yet',
  'monitoring.count.paused': 'paused',
  'monitoring.severity.LOW': 'Low',
  'monitoring.severity.MEDIUM': 'Medium',
  'monitoring.severity.HIGH': 'High',
  'monitoring.severity.CRITICAL': 'Critical',
  'monitoring.kind.HTTP': 'Website / API',
  'monitoring.kind.TCP': 'Port',
  'monitoring.urls.title': 'URLs',
  'monitoring.urls.subtitle': 'The websites, APIs and ports checked on our servers.',
  'monitoring.urls.new': 'Add URL',
  'monitoring.urls.search': 'Search by name or address',
  'monitoring.urls.empty': 'No URLs yet',
  'monitoring.urls.emptyHint': 'Add a website, an API or a port on one of the servers.',
  'monitoring.filter.allServers': 'All servers',
  'monitoring.filter.allProjects': 'All projects',
  'monitoring.filter.allStatuses': 'All statuses',
  'monitoring.filter.allKinds': 'Websites, APIs and ports',
  'monitoring.url.server': 'Server',
  'monitoring.url.noProject': 'No project',
  'monitoring.url.pickServerFirst': 'Pick the server first: only its projects are offered.',
  'monitoring.url.serverHasNoProjects': 'This server runs no projects yet.',
  'monitoring.url.addProjectsOnServer': 'Add them on the server.',
  'monitoring.url.projectHint': 'Only this server’s projects are offered.',
  'monitoring.url.kind': 'Type',
  'monitoring.url.address': 'Address',
  'monitoring.url.url': 'URL',
  'monitoring.url.urlHint': 'A website or an API endpoint. Typed without https://, it is added.',
  'monitoring.url.method': 'Method',
  'monitoring.url.expectedStatus': 'Status codes that count as up',
  'monitoring.url.expectedStatusHint': 'For example 200-299, or 200,301. Empty: any 2xx.',
  'monitoring.url.any2xx': 'Any 2xx',
  'monitoring.url.keyword': 'Must contain',
  'monitoring.url.keywordHint': 'Text the answer must include — it catches an error page that still answers 200.',
  'monitoring.url.headers': 'Headers',
  'monitoring.url.headersHint': 'One per line, as Name: value — an API key, say.',
  'monitoring.url.body': 'Body',
  'monitoring.url.bodyHint': 'What the POST sends, as typed.',
  'monitoring.url.host': 'Host',
  'monitoring.url.hostHint': 'A name or an IP address — 127.0.0.1 for the server itself.',
  'monitoring.url.port': 'Port',
  'monitoring.url.severity': 'Importance',
  'monitoring.url.severityHint': 'Who is alerted when it goes down depends on this.',
  'monitoring.url.checks': 'Checks',
  'monitoring.url.checksHint': 'Leave a field empty to take the Monitoring default.',
  'monitoring.url.interval': 'Check every',
  'monitoring.url.everyMinute': 'Every minute',
  'monitoring.url.everyMinutes': 'Every {n} minutes',
  'monitoring.url.everyMinutesMany': 'Every {n} minutes',
  'monitoring.url.default': 'Default',
  'monitoring.url.defaultOption': 'Default — {value}',
  'monitoring.url.defaultIs': 'Default: {value}',
  'monitoring.url.timeout': 'Wait for an answer',
  'monitoring.url.seconds': '{n} s',
  'monitoring.url.failures': 'Failed checks before it is down',
  'monitoring.url.failuresHint': 'One failure is not an outage: it takes this many in a row.',
  'monitoring.url.slowMs': 'Slow after',
  'monitoring.url.ms': '{n} ms',
  'monitoring.url.sslWarnDays': 'Certificate warning',
  'monitoring.url.sslWarnDaysHint': 'Days before an HTTPS certificate expires.',
  'monitoring.url.days': '{n} days',
  'monitoring.url.whatIsChecked': 'What is checked',
  'monitoring.url.where': 'Where',
  'monitoring.url.pause': 'Pause',
  'monitoring.url.resume': 'Resume',
  'monitoring.url.pausedNote': 'Paused: it is not checked, and nothing alerts about it, until it is resumed.',
  'monitoring.url.pausedToast': 'Paused.',
  'monitoring.url.resumedToast': 'Resumed — it is checked again.',
  'monitoring.url.remove': 'Remove URL',
  'monitoring.url.removeConfirm': 'Remove {name}?',
  'monitoring.url.removeBody': 'It stops being checked. It is kept, with its history.',
  'monitoring.url.removed': 'URL removed.',
  'monitoring.monitor.notRunning': 'The monitor is not running, so nothing is being checked. It was last seen {when}.',
  'monitoring.monitor.neverRan': 'The monitor has not run yet, so nothing is being checked. It starts with the API on the next deploy (pm2: viganium-monitor).',
  'monitoring.monitor.noWatchdog': 'No watchdog is set. The monitor runs on the server it watches, so if that server goes down nobody is told by email or Telegram until it is back.',
  'monitoring.monitor.watchdogStopped': 'The Cloudflare watchdog stopped reading this server {when}. Until it reads again, nobody is told while this server is down.',
  'monitoring.monitor.setWatchdog': 'Set the watchdog',
  'monitoring.url.response': 'Response',
  'monitoring.url.uptimeDay': 'Uptime 24 h',
  'monitoring.url.uptimeWeek': 'Uptime 7 days',
  'monitoring.url.uptimeMonth': 'Uptime 30 days',
  'monitoring.url.checkNow': 'Check now',
  'monitoring.url.checkNowToast': 'It is checked within a few seconds.',
  'monitoring.url.downSince': 'Down since {when} ({duration}) — {cause}.',
  'monitoring.url.lastCheck': 'Last check',
  'monitoring.url.answered': 'Answered {code}',
  'monitoring.url.connected': 'The port took the connection',
  'monitoring.url.statusSince': 'Status changed {when}',
  'monitoring.url.certificate': 'Certificate',
  'monitoring.url.expiresOn': 'Expires on',
  'monitoring.url.daysLeft': '{n} days left',
  'monitoring.url.issuer': 'Issued by',
  'monitoring.url.incidents': 'Incidents',
  'monitoring.url.incidentsHint': 'An outage starts at its first failed check and ends at its first good one.',
  'monitoring.url.noIncidents': 'No incidents.',
  'monitoring.url.recentChecks': 'Recent checks',
  'monitoring.url.noChecks': 'Not checked yet.',
  'monitoring.url.checkedAt': 'Checked',
  'monitoring.url.result': 'Result',
  'monitoring.url.statusCode': 'Code',
  'monitoring.url.started': 'Started',
  'monitoring.url.ended': 'Ended',
  'monitoring.url.duration': 'Lasted',
  'monitoring.url.cause': 'Cause',
  'monitoring.url.ongoing': 'Ongoing',
  'monitoring.endReason.RECOVERED': 'recovered',
  'monitoring.endReason.CHANGED': 'ended by a change to it',
  'monitoring.endReason.PAUSED': 'ended by a pause',
  'monitoring.endReason.REMOVED': 'ended when it was removed',
  'monitoring.error.TIMEOUT': 'No answer within {detail} s',
  'monitoring.error.DNS': 'The name does not resolve',
  'monitoring.error.REFUSED': 'Connection refused — nothing listens there',
  'monitoring.error.RESET': 'The connection was dropped',
  'monitoring.error.UNREACHABLE': 'The host cannot be reached',
  'monitoring.error.TLS': 'Certificate problem: {detail}',
  'monitoring.error.STATUS': 'Answered {detail}',
  'monitoring.error.KEYWORD': 'The text it must contain is missing',
  'monitoring.error.REDIRECTS': 'Too many redirects',
  'monitoring.error.BLOCKED': 'It points at an address checks never go to',
  'monitoring.error.NETWORK': 'Network error: {detail}',
  'monitoring.error.SLOW': 'Slow: {detail} ms',
  'monitoring.error.SSL_EXPIRING': 'Certificate expires in {detail} days',
  'nav.monitoringAlerts': 'Alerts',
  'nav.monitoringSettings': 'Settings',
  'monitoring.alerts.title': 'Alerts',
  'monitoring.alerts.subtitle': 'Who is told when a URL goes down or comes back, and every alert sent.',
  'monitoring.alerts.new': 'Add recipient',
  'monitoring.alerts.off': 'Alerts are switched off in Monitoring › Settings: outages are recorded, and nobody is told.',
  'monitoring.alerts.emailReady': 'Ready — alerts go through ZeptoMail, like the system’s other mail.',
  'monitoring.alerts.emailMissing': 'Not set up on this server: {missing} is empty in the API’s .env.',
  'monitoring.alerts.telegramMissing': 'Not set up: make a bot with @BotFather and put its token in TELEGRAM_BOT_TOKEN in the API’s .env.',
  'monitoring.alerts.telegramReady': 'Ready — alerts are sent by {bot}. Add the bot to a group, or start a chat with it, to get its chat id.',
  'monitoring.alerts.recipients': 'Recipients',
  'monitoring.alerts.recipientsHint': 'Each hears about the URLs that matter at least as much as it asks, on the servers or projects it covers.',
  'monitoring.alerts.empty': 'Nobody is alerted yet',
  'monitoring.alerts.emptyHint': 'Add a Telegram chat or an email address. Administrators also see outages in their bell.',
  'monitoring.alerts.sendTo': 'Sent to',
  'monitoring.alerts.from': 'From importance',
  'monitoring.alerts.covers': 'Covers',
  'monitoring.alerts.coversHint': 'Pick servers, projects or both — a URL on any server or tagged with any project picked. “All” takes in the ones added later too.',
  'monitoring.alerts.allServers': 'All servers',
  'monitoring.alerts.allProjects': 'All projects',
  'monitoring.alerts.allServersNote': 'All servers takes in every URL, whatever its project.',
  'monitoring.alerts.coversNothing': 'Pick “All”, or at least one server or project.',
  'monitoring.alerts.everything': 'Everything',
  'monitoring.alerts.active': 'Active',
  'monitoring.alerts.inactive': 'Off',
  'monitoring.alerts.noRecovery': 'no Back up alerts',
  'monitoring.alerts.test': 'Send test',
  'monitoring.alerts.testSent': 'Test sent.',
  'monitoring.alerts.log': 'Sent alerts',
  'monitoring.alerts.logHint': 'Every alert to every recipient, and whether it got there. A failed send is tried again, five times in all.',
  'monitoring.alerts.allKinds': 'All alerts',
  'monitoring.alerts.logEmpty': 'No alerts sent yet.',
  'monitoring.alerts.when': 'When',
  'monitoring.alerts.recipient': 'Recipient',
  'monitoring.alerts.what': 'Alert',
  'monitoring.alerts.attempts': '{n} tries',
  'monitoring.alerts.namePlaceholder': 'Ops group',
  'monitoring.alerts.channel': 'Send by',
  'monitoring.alerts.chatId': 'Telegram chat id',
  'monitoring.alerts.chatIdHint': 'A person’s or a group’s id (a group’s starts with -100). Write to the bot or add it to the group, then Find chats.',
  'monitoring.alerts.findChats': 'Find chats',
  'monitoring.alerts.noChats': 'No chat has written to the bot in the last day. Send it a message, or add it to the group, and look again.',
  'monitoring.alerts.language': 'Language of its alerts',
  'monitoring.alerts.minSeverity': 'From importance',
  'monitoring.alerts.minSeverityHint': 'Only URLs at least this important. Each URL’s importance is set on its form, and is Medium unless changed — so from High, a Medium URL going down tells nobody.',
  'monitoring.coverage.title': 'Down alerts reach nobody for {count} of {total} watched URLs',
  'monitoring.coverage.IMPORTANCE': 'Their importance is below every recipient’s “From importance”. Lower it on a recipient, or raise the URL’s importance on its form:',
  'monitoring.coverage.SCOPE': 'They are on none of the recipients’ servers or projects:',
  'monitoring.coverage.NO_RECIPIENTS': 'There is no active recipient to tell:',
  'monitoring.coverage.more': '+{count} more',
  'monitoring.alerts.notifyRecovery': 'Also tell it when a URL is back up',
  'monitoring.alerts.isActive': 'Active — untick to stop its alerts for a while',
  'monitoring.alerts.remove': 'Remove recipient',
  'monitoring.alerts.removeConfirm': 'Remove {name}?',
  'monitoring.alerts.removeBody': 'It hears nothing more. The alerts already sent to it stay in the log.',
  'monitoring.alerts.removed': 'Recipient removed.',
  'monitoring.channel.EMAIL': 'Email',
  'monitoring.channel.TELEGRAM': 'Telegram',
  'monitoring.delivery.SENT': 'Sent',
  'monitoring.delivery.QUEUED': 'Waiting to send',
  'monitoring.delivery.FAILED': 'Failed',
  'monitoring.delivery.SKIPPED': 'Not set up',
  'monitoring.alertKind.DOWN': 'Down',
  'monitoring.alertKind.RECOVERED': 'Back up',
  'monitoring.alertKind.REMINDER': 'Still down',
  'monitoring.alertKind.SSL_EXPIRING': 'Certificate',
  'monitoring.alertKind.TEST': 'Test',
  'monitoring.alertKind.MONITOR_OFFLINE': 'Monitoring offline',
  'monitoring.settings.title': 'Monitoring settings',
  'monitoring.settings.subtitle': 'What every URL takes unless it sets its own, and how alerts behave.',
  'monitoring.settings.checks': 'Checks',
  'monitoring.settings.interval': 'Check every',
  'monitoring.settings.timeout': 'Wait for an answer (seconds)',
  'monitoring.settings.failures': 'Failed checks before it is down',
  'monitoring.settings.failuresHint': 'In a row. One failure is never an outage.',
  'monitoring.settings.retry': 'Check again after a failure (seconds)',
  'monitoring.settings.retryHint': 'So an outage is confirmed in moments, not minutes.',
  'monitoring.settings.successes': 'Good checks before it is back',
  'monitoring.settings.successesHint': 'In a row, so one lucky answer does not end an outage.',
  'monitoring.settings.slow': 'Slow after (ms)',
  'monitoring.settings.sslWarn': 'Warn before a certificate expires (days)',
  'monitoring.settings.sslWarnHint': 'Alerted then, and again at 7, 3 and 1 days and on the day.',
  'monitoring.settings.retention': 'Keep each check (days)',
  'monitoring.settings.retentionHint': 'Each day’s figures stay after that.',
  'monitoring.settings.alerts': 'Alerts',
  'monitoring.settings.alertsEnabled': 'Send alerts — by Email, Telegram and the bell',
  'monitoring.settings.bellEnabled': 'Also in every administrator’s bell',
  'monitoring.settings.reminder': '“Still down” reminder after (minutes)',
  'monitoring.settings.reminderHint': 'Repeated at that pace while it stays down. 0: never.',
  'monitoring.settings.watchdog': 'Watchdog',
  'monitoring.settings.watchdogHint': 'Everything runs on one VPS, so the monitor cannot report the box itself dying. The watchdog runs on Cloudflare, reads this server every minute, and tells the recipients on Monitoring › Alerts by email and Telegram while it is down. It is set up once, from deploy/watchdog in the repository.',
  'monitoring.settings.watchdogReading': 'The Cloudflare watchdog is reading this server: last read {when}, from {from}.',
  'monitoring.settings.watchdogStopped': 'The Cloudflare watchdog stopped reading this server {when}. Its key may have changed, or the Worker was removed.',
  'monitoring.settings.watchdogNever': 'The Cloudflare watchdog has not read this server yet.',
  'monitoring.settings.heartbeatUrl': 'Or a ping URL, for another heartbeat service (optional)',
  'monitoring.settings.saved': 'Monitoring settings saved.',
  'nav.monitoringOverview': 'Overview',
  'nav.monitoringIncidents': 'Incidents',
  'monitoring.overview.title': 'Monitoring',
  'monitoring.overview.subtitle': 'Every server and URL at a glance: what is up, what is down and since when.',
  'monitoring.overview.servers': 'Servers online',
  'monitoring.overview.serversHint': '{warning} with a warning · {offline} offline',
  'monitoring.overview.urls': 'URLs up',
  'monitoring.overview.urlsHint': '{degraded} degraded · {paused} paused',
  'monitoring.overview.downNow': 'Down now',
  'monitoring.overview.downNowHint': 'Outages still open',
  'monitoring.overview.allGood': 'Nothing is down',
  'monitoring.overview.certificates': 'Certificates expiring',
  'monitoring.overview.certificatesHint': 'Within their warning days',
  'monitoring.overview.downNowSubtitle': 'Longest first. Each opens its URL.',
  'monitoring.overview.allIncidents': 'All incidents',
  'monitoring.overview.allServers': 'All servers',
  'monitoring.overview.since': 'Down since',
  'monitoring.overview.downFor': 'Down for',
  'monitoring.overview.nothingDown': 'Everything checked is up',
  'monitoring.overview.nothingDownHint': 'An outage shows here the moment it is confirmed.',
  'monitoring.overview.recent': 'Recent incidents',
  'monitoring.overview.certificatesList': 'Certificates',
  'monitoring.overview.certificatesListHint': 'The HTTPS certificates that expire soonest.',
  'monitoring.overview.noCertificates': 'No certificate expires in the next 30 days.',
  'monitoring.overview.slowest': 'Slowest right now',
  'monitoring.overview.slowestHint': 'Each URL’s last response time.',
  'monitoring.incidents.title': 'Incidents',
  'monitoring.incidents.all': 'Open and over',
  'monitoring.incidents.open': 'Still open',
  'monitoring.incidents.over': 'Over',
  'monitoring.incidents.empty': 'No incidents',
  'monitoring.incidents.emptyHint': 'An outage is recorded once a URL fails its checks in a row.',
  'monitoring.chart.title': 'Response time',
  'monitoring.chart.subtitle': 'How fast it answered, and when its checks failed.',
  'monitoring.chart.range.24h': '24 hours',
  'monitoring.chart.range.7d': '7 days',
  'monitoring.chart.range.30d': '30 days',
  'monitoring.chart.range.90d': '90 days',
  'monitoring.chart.noChecks': 'No checks in this range yet.',
  'monitoring.chart.average': 'average',
  'monitoring.chart.slowest': 'slowest {ms}',
  'monitoring.chart.failedOf': 'failed of {n} checks',
  'monitoring.chart.noneHere': 'Not checked in this time',
  'monitoring.chart.averageBucket': 'Average response, per slot',
  'monitoring.chart.averageDaily': 'Average response, per day',
  'monitoring.chart.failed': 'Failed checks',
  'monitoring.chart.ninety': 'Uptime, last 90 days',
  'monitoring.chart.ninetySummary': 'Up {pct} of the checks in the last 90 days',
  'monitoring.chart.noData': 'No checks',
  'monitoring.chart.dayUp': '{pct} up',
  'monitoring.chart.dayChecks': '{n} checks',
  'monitoring.chart.today': 'Today',
  'monitoring.chart.key.up': 'Up all day',
  'monitoring.chart.key.some': 'A little downtime',
  'monitoring.chart.key.down': 'Down for longer',
  'monitoring.chart.key.none': 'Not checked',

  // The points wallet (CR-WALLET-001, operator 2026-10-20) — from the menu under one's name.
  'wallet.menu': 'My wallet',
  'wallet.title': 'My wallet',
  'wallet.subtitle': 'Your points, what they buy, and every movement of them.',
  'wallet.balance': 'Your points',
  'wallet.pointUnit': 'point',
  'wallet.pointsUnit': 'points',
  'wallet.zeroBalance':
    'Your balance is 0 points. When points are added to your wallet in future, you can redeem them for the rewards below.',
  'wallet.balanceHint': 'Points leave your balance only when you redeem them, and each redemption asks you to confirm first.',
  'wallet.redeemTitle': 'Redeem points',
  'wallet.redeemSubtitle':
    'Five rewards, at the prices set now. Redeem shows the cost, your balance after and exactly what you get — nothing is spent until you confirm.',
  'wallet.type.REMOVE_QUARTER_DAY_PENALTY': 'Cancel a quarter-day deduction',
  'wallet.type.REMOVE_HALF_DAY_PENALTY': 'Cancel a half-day deduction',
  'wallet.type.REMOVE_FULL_DAY_PENALTY': 'Cancel a full-day deduction',
  'wallet.type.ADD_ONE_LEAVE_DAY': 'Add a day of leave',
  'wallet.type.CASH_REWARD': 'Points for cash',
  'wallet.desc.REMOVE_QUARTER_DAY_PENALTY':
    'Cancels one quarter-day deduction of yours that is charged and no payroll has taken yet. You choose which.',
  'wallet.desc.REMOVE_HALF_DAY_PENALTY':
    'Cancels one half-day deduction of yours that is charged and no payroll has taken yet. You choose which.',
  'wallet.desc.REMOVE_FULL_DAY_PENALTY':
    'Cancels one full-day deduction of yours that is charged and no payroll has taken yet. You choose which.',
  'wallet.desc.ADD_ONE_LEAVE_DAY': 'Adds one day to your {type} balance for {year}. You ask for it as any leave, approved as usual.',
  'wallet.desc.CASH_REWARD': 'Turns bundles of points into money, paid with your salary.',
  'wallet.annualLeave': 'annual leave',
  'wallet.price': 'Price',
  'wallet.rate': 'Rate',
  'wallet.cashRate': '{points} = {amount}',
  'wallet.notPriced': 'The cost of this reward will be set by management later.',
  'wallet.redeem': 'Redeem',
  'wallet.eligibleCount': 'Can be cancelled: {n}',
  'wallet.held.awaitingDecision': 'Waiting for a decision: {n}',
  'wallet.held.dayNotOver': 'Day not over yet: {n}',
  'wallet.held.onPayroll': 'On a payroll already: {n}',
  'wallet.reason.NOT_CONFIGURED': 'The cost of this reward has not been set yet.',
  'wallet.reason.INSUFFICIENT_POINTS': 'Your current balance is not enough for this reward.',
  'wallet.reason.NO_ELIGIBLE_PENALTY.REMOVE_QUARTER_DAY_PENALTY': 'You have no quarter-day deduction that can be cancelled now.',
  'wallet.reason.NO_ELIGIBLE_PENALTY.REMOVE_HALF_DAY_PENALTY': 'You have no half-day deduction that can be cancelled now.',
  'wallet.reason.NO_ELIGIBLE_PENALTY.REMOVE_FULL_DAY_PENALTY': 'You have no full-day deduction that can be cancelled now.',
  'wallet.reason.LEAVE_GRANT_UNAVAILABLE': 'Days of leave cannot be added right now.',
  'wallet.reason.OTHER_CURRENCY': 'Cash rewards are paid in {currency} with the salary, and yours is paid in another currency.',
  'wallet.reason.NO_SALARY': 'Cash rewards are paid with the salary, and you have none on record yet.',
  'wallet.howTitle': 'How does the points wallet work?',
  'wallet.howText':
    'Here you follow your points balance and when points were added or redeemed. Every new movement appears in the history with its date and number of points. With enough points, you can redeem them to cancel a deduction you already have, add a day to your leave balance, or take a cash reward — at the prices set in the system’s settings.',
  'wallet.howNeeds': 'Needs {points}',
  'wallet.howCash': 'Every {points} = {amount}',
  'wallet.howUnpriced': 'The cost of this reward will be set by management later',
  'wallet.howExample': 'For example: {points} would be {amount} in cash.',
  'wallet.historyTitle': 'Points history',
  'wallet.historyEmpty': 'No points activity yet.',
  'wallet.kind.CREDIT': 'Points added',
  'wallet.kind.REDEMPTION_DEBIT': 'Redeemed: {reward}',
  'wallet.kind.REFUND_CREDIT': 'Points returned from a redemption',
  'wallet.date': 'Date',
  'wallet.time': 'Time',
  'wallet.status.COMPLETED': 'Done',
  'wallet.status.PENDING_SETTLEMENT': 'Cash pending',
  'wallet.status.PAID': 'Paid',
  'wallet.status.CANCELLED': 'Cancelled',
  'wallet.penaltyKind.SLA_VIEW': 'Ticket not viewed in time',
  'wallet.penaltyKind.SLA_ASSIGNMENT': 'Ticket not assigned in time',
  'wallet.penaltyKind.BREAK': 'Break over its limit',
  'wallet.penaltyKind.IDLE': 'Not working on shift',
  'wallet.penaltyKind.SHORT_DAY': 'Daily hours not completed',
  'wallet.penaltyKind.EARLY_END': 'Shift ended early',
  'wallet.penaltyKind.OTHER': 'Deduction',
  'wallet.penaltyDays': '{days} day',
  'wallet.leaveAdded': '1 day added to {type}, {year}',
  'wallet.cashPending': 'Cash reward pending: {amount}',
  'wallet.cashOnPayroll': 'Cash reward pending: {amount} · on the {month} payroll',
  'wallet.cashPaid': 'Paid: {amount}',
  'wallet.cashPaidWith': 'Paid: {amount} · with the {month} salary',
  'wallet.cashCancelled': '{amount} cancelled — the points came back',
  'wallet.redeemPageHint': 'Check what it costs and what you get, then confirm. Nothing is spent before.',
  'wallet.noSuchReward': 'There is no such reward.',
  'wallet.cost': 'Cost',
  'wallet.youGet': 'You get',
  'wallet.balanceNow': 'Your balance now',
  'wallet.balanceAfterRedeem': 'After redeeming',
  'wallet.get.penalty': 'One {days}-day deduction cancelled',
  'wallet.get.leave': '1 day of {type}, {year}',
  'wallet.get.cash': '{amount}, paid with your salary',
  'wallet.pickPenalty': 'The deduction to cancel',
  'wallet.pickPenaltyHint': 'Your charged deductions of this size that no payroll has taken yet, oldest first.',
  'wallet.bundles': 'Bundles',
  'wallet.bundlesHint': 'Each bundle is {points} for {amount}. Your balance buys {max}.',
  'wallet.bundlesInvalid': 'A whole number from 1 to {max}.',
  'wallet.cashNote': 'It is paid with your salary, on the next payroll Finance runs, and shows as pending until it is paid.',
  'wallet.leaveBalance': 'Your annual leave left: {before} now, {after} after.',
  'wallet.confirmQuestion': 'Do you confirm redeeming {points} for “{reward}”?',
  'wallet.confirmSubmit': 'Redeem {points}',
  'wallet.priceChanged': 'The price changed. Check the new cost and confirm again.',
  'wallet.done.penalty': 'The deduction was cancelled, and the points taken from your wallet.',
  'wallet.done.leave': 'A day of leave was added to your balance.',
  'wallet.done.cash': 'Your cash reward is recorded, and is pending payment.',
  'wallet.done.replayed': 'This was redeemed already — nothing more was spent.',
  'wallet.error.INSUFFICIENT_POINTS': 'Your current balance is not enough for this reward. No points were taken.',
  'wallet.error.REWARD_NOT_CONFIGURED': 'This reward has no price yet. No points were taken.',
  'wallet.error.NO_ELIGIBLE_PENALTY': 'That deduction cannot be cancelled now. No points were taken.',
  'wallet.error.NOT_CHARGED': 'That deduction is still waiting for a decision, which may drop it. No points were taken.',
  'wallet.error.DAY_NOT_OVER':
    'That deduction’s day is not over: working your Daily hours may still drop it. No points were taken.',
  'wallet.error.PENALTY_ALREADY_REVERSED': 'That deduction was cancelled already. No points were taken.',
  'wallet.error.PAYROLL_PERIOD_LOCKED':
    'That deduction is on a payroll already, so it can no longer be cancelled. No points were taken.',
  'wallet.error.LEAVE_GRANT_UNAVAILABLE': 'A day of leave cannot be added right now. No points were taken.',
  'wallet.error.PRICE_CHANGED': 'The price changed. Check the new cost and confirm again. No points were taken.',
  'wallet.error.IDEMPOTENCY_CONFLICT': 'This confirmation clashed with another. No points were taken — confirm again.',
  'wallet.error.CONCURRENT_CHANGE': 'Your wallet is busy with another request. No points were taken — try again in a moment.',
  'wallet.error.general': 'The redemption could not be completed. No points were taken.',
  'wallet.error.network': 'No answer came back. Press Redeem again: the same confirmation is never charged twice.',
  'settings.wallet': 'Wallet & Points',
  'settings.walletNote':
    'What each reward in the employees’ wallet costs in points. A reward left empty is not offered. A change applies to redemptions from now on; one already made keeps the price it had.',
  'settings.wallet.quarterDayPenaltyPoints': 'Points to cancel a quarter-day deduction',
  'settings.wallet.halfDayPenaltyPoints': 'Points to cancel a half-day deduction',
  'settings.wallet.fullDayPenaltyPoints': 'Points to cancel a full-day deduction',
  'settings.wallet.leaveDayPoints': 'Points for a day of leave',
  'settings.wallet.cashPointsPerBundle': 'Points for each cash bundle',
  'settings.wallet.cashAmountPerBundle': 'Cash for each bundle',
  'settings.wallet.cashCurrency': 'Currency of the cash reward',
  'settings.wallet.unsetHint': 'Empty: not offered.',
  'settings.wallet.leaveHint': 'The day is added to Annual leave, in the year it is redeemed. Empty: not offered.',
  'settings.wallet.cashHint':
    'Cash is paid with the salary, on the next payroll Finance calculates — only to employees whose salary is in this currency; no exchange rate is used. Finance › Points rewards lists what is owed.',
  'settings.wallet.cashExample': 'For example: {points} = {amount}.',
  'settings.wallet.noCurrency': '— Not set —',
  'settings.wallet.offered': 'Offered',
  'settings.wallet.notOffered': 'Not offered',
  'settings.wallet.earning':
    'How employees earn points is not set up yet — a later change. Nothing adds points for now, so every wallet stays at its balance.',
  'settings.wallet.cashReach':
    'Cash is paid in {currency}: {n} active employees are paid in another currency ({list}) and {m} have no salary on record. They cannot take cash.',
  'settings.wallet.ledger': 'Ledger check',
  'settings.wallet.ledgerOk': 'All {n} wallets match their history ({points} held in all).',
  'settings.wallet.ledgerBad': '{bad} of {n} wallets do not match their history. Tell the developers before anyone redeems.',
  'settings.problem.TOO_MANY_DECIMALS': '{limit} decimals at most.',
  'settings.problem.UNKNOWN_CURRENCY': 'A currency we use (Finance › Settings › Currencies).',
  'violations.walletCancelled': 'Cancelled with {points} wallet points',
  'leaves.grantedDays': '+{n} added with points',
  'employees.del.walletRedemptions': 'Points redemptions, with their wallet and history',
  // The meeting calendar (2026-10-21), under My to-do list: everyone asked accepts or declines; a reminder before it.
  'nav.meetings': 'Meeting Calendar',
  'meetings.title': 'Meeting Calendar',
  'meetings.subtitle': 'Your meetings, and every invitation waiting for your answer.',
  'meetings.subtitleLeader': 'Your meetings — and, with My team’s, those of your departments’ people.',
  'meetings.subtitleAdmin': 'Your meetings — and, with Everyone’s, every meeting in the company.',
  'meetings.new': 'New meeting',
  'meetings.newShort': 'New',
  'meetings.newAt': 'New meeting at {time}',
  'meetings.today': 'Today',
  'meetings.previous': 'Previous',
  'meetings.next': 'Next',
  'meetings.viewLabel': 'View',
  'meetings.view.month': 'Month',
  'meetings.view.week': 'Week',
  'meetings.view.list': 'List',
  'meetings.scopeLabel': 'Whose meetings',
  'meetings.scope.mine': 'Mine',
  'meetings.scope.team': 'My team’s',
  'meetings.scope.all': 'Everyone’s',
  'meetings.person': 'One person’s meetings',
  'meetings.personAll': 'Everyone',
  'meetings.waitingTitle': 'Waiting for your answer',
  'meetings.waitingHint': 'Everyone asked to a meeting accepts or declines it.',
  'meetings.waitingCount': 'Meeting invitations waiting for your answer: {n}',
  'meetings.fromName': 'from {name}',
  'meetings.accept': 'Accept',
  'meetings.decline': 'Decline',
  'meetings.acceptedToast': 'Meeting accepted.',
  'meetings.declinedToast': 'Meeting declined — whoever called it was told.',
  'meetings.calledToast': 'Meeting called — everyone asked was told.',
  'meetings.savedToast': 'Meeting saved.',
  'meetings.cancelledToast': 'Meeting cancelled — everyone asked was told.',
  'meetings.more': '+{n} more',
  'meetings.dayCount': 'meetings: {n}',
  'meetings.dayAgenda': 'The day’s meetings',
  'meetings.noneDay': 'No meetings this day.',
  'meetings.noneMonth': 'No meetings this month',
  'meetings.noneMonthHint': 'The meetings you call or are asked to show here.',
  'meetings.legend': 'What the colours say',
  'meetings.look.organizer': 'You called it',
  'meetings.look.accepted': 'You accepted',
  'meetings.look.pending': 'Waiting for your answer',
  'meetings.look.declined': 'You declined',
  'meetings.look.viewer': 'Someone else’s',
  'meetings.look.cancelled': 'Cancelled',
  'meetings.answersShort': '{accepted} of {total} accepted',
  'meetings.place.OFFICE': 'At the office',
  'meetings.place.OUTSIDE': 'Outside the office',
  'meetings.place.ONLINE': 'Online',
  'meetings.placeHint.OFFICE': 'Inside the company — a meeting room, say.',
  'meetings.placeHint.OUTSIDE': 'At a client’s, or anywhere away from the office.',
  'meetings.placeHint.ONLINE': 'A video call.',
  'meetings.locationLabel.OFFICE': 'Room or place (optional)',
  'meetings.locationLabel.OUTSIDE': 'Address',
  'meetings.locationLabel.ONLINE': 'Meeting link (optional)',
  'meetings.linkHint': 'It starts with https:// — leave it empty and add it later if you do not have it yet.',
  'meetings.locationRequired': 'A meeting outside the office needs its address.',
  'meetings.linkInvalid': 'A link starting with https://',
  'meetings.audience.ONE_TO_ONE': 'One to one',
  'meetings.audience.PEOPLE': 'Some employees',
  'meetings.audience.DEPARTMENTS': 'A department',
  'meetings.audience.EVERYONE': 'Everyone',
  'meetings.audienceHint.ONE_TO_ONE': 'You and one other person.',
  'meetings.audienceHint.PEOPLE': 'The people you pick.',
  'meetings.audienceHint.DEPARTMENTS': 'Everyone in the departments you pick.',
  'meetings.audienceHint.EVERYONE': 'All {n} employees.',
  'meetings.audienceHintOwn.PEOPLE': 'People you pick from your departments.',
  'meetings.audienceHintOwn.DEPARTMENTS': 'Everyone in the departments of yours you pick.',
  'meetings.answer.ACCEPTED': 'Accepted',
  'meetings.answer.PENDING': 'Not answered',
  'meetings.answer.DECLINED': 'Declined',
  'meetings.mine.ACCEPTED': 'You accepted',
  'meetings.mine.PENDING': 'Waiting for your answer',
  'meetings.mine.DECLINED': 'You declined',
  'meetings.yourAnswer.PENDING': 'You are asked to this meeting: accept or decline it. Whoever called it sees your answer.',
  'meetings.yourAnswer.ACCEPTED': 'You accepted. You can still decline it until it ends.',
  'meetings.yourAnswer.DECLINED': 'You declined. You can still accept it until it ends.',
  'meetings.yourNote': 'Your note',
  'meetings.status.SCHEDULED': 'Scheduled',
  'meetings.status.STARTED': 'Started',
  'meetings.status.IN_PROGRESS': 'In progress',
  'meetings.status.DONE': 'Done',
  'meetings.status.CANCELLED': 'Cancelled',
  'meetings.status.OVER': 'Over',
  // A scheduled meeting whose time has come — whoever called it starts it.
  'meetings.status.NOW': 'Its time has come',
  'meetings.statusTitle': 'Status',
  'meetings.statusNotStarted': 'Not started yet.',
  'meetings.statusHintRun': 'Move it on as the meeting goes — its status moves forward only.',
  'meetings.statusStartsFrom': 'You can start it from {time} — half an hour before its time.',
  'meetings.statusSince': 'Since {when}',
  'meetings.statusSinceBy': 'Since {when} · by {name}',
  'meetings.statusClosedAtEnd': 'Closed at its end time · {when}',
  'meetings.statusToast': 'Meeting status: {status}.',
  'meetings.move.STARTED': 'Start',
  'meetings.move.IN_PROGRESS': 'Mark in progress',
  'meetings.move.DONE': 'Mark done',
  'meetings.moveTitle': 'Move the meeting to “{status}”?',
  'meetings.moveStartNote': 'Once it has started, the meeting can no longer be changed or cancelled. Its status moves forward only.',
  'meetings.moveForwardNote': 'Its status moves forward only: it cannot be moved back.',
  'meetings.moveDoneNote': 'Done closes the meeting: answers stop, and it cannot be moved back.',
  'meetings.editLockedStarted': 'This meeting has started: it can no longer be changed or cancelled. Mark it done when it ends.',
  'meetings.cancelledBanner': 'Cancelled by {name} · {when}',
  'meetings.edit': 'Change',
  'meetings.cancel': 'Cancel meeting',
  'meetings.details': 'The meeting',
  'meetings.date': 'Date',
  'meetings.time': 'Time',
  'meetings.where': 'Where',
  'meetings.calledBy': 'Called by',
  'meetings.forLabel': 'Who it is for',
  'meetings.reminder': 'Reminder',
  'meetings.reminderBefore': '{time} before it starts',
  'meetings.reminderOff': 'None — reminders are off in Settings › Meetings',
  'meetings.agenda': 'Agenda',
  'meetings.noAgenda': 'No agenda.',
  'meetings.answers': 'Answers',
  'meetings.you': 'you',
  'meetings.newTitle': 'New meeting',
  'meetings.editTitle': 'Change the meeting',
  'meetings.formSubtitle': 'Everyone you ask is told, and accepts or declines it.',
  'meetings.titleLabel': 'Title',
  'meetings.titleHint': 'What it is about.',
  'meetings.whoFor': 'Who it is for',
  'meetings.pickPerson': 'Pick the person.',
  'meetings.pickPeople': 'Pick at least one person.',
  'meetings.pickDepartments': 'Pick at least one department.',
  'meetings.personFull': 'One person — take them off to pick someone else.',
  'meetings.noDepartments': 'You are in no department.',
  'meetings.noReach': 'You are in no department, so there is nobody you can ask. An administrator adds you to one.',
  'meetings.everyoneAdmins': 'You ask the people of your departments. Only administrators call a meeting for everyone.',
  'meetings.starts': 'Starts',
  'meetings.ends': 'Ends',
  'meetings.length': 'Length',
  'meetings.reminderNote': 'A reminder goes {time} before it starts, to you and everyone who has not declined (Settings › Meetings).',
  'meetings.reminderNoteOff': 'No reminder goes: reminders are off in Settings › Meetings.',
  'meetings.busyTitle': 'Busy then',
  'meetings.busyMeeting': 'another meeting',
  'meetings.busyLeave': 'on leave',
  'meetings.busyYou': 'You',
  'meetings.busyHint': 'You can still call it: everyone answers for themselves.',
  'meetings.agendaLabel': 'Agenda (optional)',
  'meetings.agendaHint': 'What it is for, what to bring.',
  'meetings.askAgainNote': 'A new time or place asks everyone again: their answers start over.',
  'meetings.call': 'Call the meeting',
  'meetings.declineTitle': 'Decline the meeting',
  'meetings.declineNote': 'Why not? (optional)',
  'meetings.declineNoteHint': 'Whoever called it reads this.',
  'meetings.cancelTitle': 'Cancel the meeting',
  'meetings.cancelNote': 'Why? (optional)',
  'meetings.cancelNoteHint': 'Everyone asked reads this.',
  'meetings.notAllowed': 'This meeting no longer takes that: it was cancelled, it is done or over, or it is not yours to change.',
  'meetings.refusal.CANCELLED': 'This meeting was cancelled.',
  'meetings.refusal.OVER': 'This meeting is over.',
  'meetings.refusal.DONE': 'This meeting is done.',
  'meetings.refusal.STARTED': 'This meeting has started: it is no longer changed or cancelled.',
  'meetings.refusal.BACKWARDS': 'A meeting’s status moves forward only.',
  'meetings.refusal.TOO_EARLY': 'A meeting is started from half an hour before its time.',
  'meetings.refusal.NOT_STARTED': 'Start the meeting before marking it done.',
  'meetings.refusal.NOBODY': 'There is nobody else to ask in what you picked.',
  'meetings.refusal.INACTIVE': 'Someone you picked is not an active employee any more.',
  'meetings.refusal.DEPARTMENT': 'A department you picked does not exist or is switched off.',
  'meetings.refusal.EVERYONE': 'Only administrators call a meeting for everyone.',
  'meetings.refusal.INVALID': 'Give it a date, a start and an end.',
  'meetings.refusal.ENDS_BEFORE_START': 'It ends after it starts.',
  'meetings.refusal.TOO_SHORT': '5 minutes at least.',
  'meetings.refusal.TOO_LONG': 'A day at most.',
  'meetings.refusal.IN_THE_PAST': 'That time has passed: a meeting is called for later.',
  'meetings.refusal.CONCURRENT_CHANGE': 'This meeting changed while you were looking at it. Open it again to see it as it is now.',
  'meetings.refusal.outside': 'That person is not in your departments: you ask the people in your departments.',
  'meetings.refusal.outsideDepartment': 'That department is not one of yours: you call meetings for your own departments.',
  'settings.meetings': 'Meetings',
  'settings.meetingsNote': 'The Meeting Calendar, under My to-do list: everyone asked accepts or declines, and a reminder goes before each meeting.',
  'settings.meetingReminder': 'Reminder before a meeting, in minutes',
  'settings.meetingReminderHint': '15 by default; 0 sends none. It goes to whoever called it and everyone who has not declined — to the bell, which the phone app shows too.',
  'settings.meetingEmailInvitations': 'Email invitations, changes and cancellations',
  'settings.meetingEmailInvitationsHint': 'Off, they go to the bell only.',
  'settings.meetingEmailReminders': 'Email the reminder too',
  'employees.del.meetings': 'Meetings they called, with everyone’s answers',
  // Booking links (operator, 2026-11-05): Meeting Calendar › Booking link, and a guest's meeting.
  'meetings.audience.GUEST': 'A guest (booked)',
  'meetings.refusal.GUEST': 'A meeting a guest booked keeps the time they picked: cancel it, and they can book another.',
  'settings.meetingBookingLinks': 'Booking links',
  'settings.meetingBookingLinksHint': 'Everyone may switch on a booking link in Meeting Calendar › Booking link. Off, no link takes bookings.',
  'booking.button': 'Booking link',
  'booking.title': 'Booking link',
  'booking.subtitle': 'Send your link to anyone: they pick a free time in your hours, with no sign-in, and the meeting goes into your calendar.',
  'booking.offEverywhere': 'Booking links are switched off in Settings › Meetings, so your link takes no bookings now. Your hours are kept.',
  'booking.linkTitle': 'Your link',
  'booking.linkHint': 'Anyone who has it can book a meeting with you. Make a new link to stop the old one.',
  'booking.enabled': 'Take bookings through my link',
  'booking.enabledHint': 'Off, the link says you are not taking bookings now.',
  'booking.copy': 'Copy link',
  'booking.copied': 'Link copied.',
  'booking.open': 'Open',
  'booking.newLink': 'New link',
  'booking.newLinkTitle': 'Make a new link?',
  'booking.newLinkBody': 'Your current link stops working at once. Meetings booked through it stay in your calendar.',
  'booking.newLinkToast': 'New link made: the old one no longer opens.',
  'booking.upcoming': 'Booked through your link and still to come: {n}',
  'booking.hoursTitle': 'When you take meetings',
  'booking.hoursHint': 'In the company’s time ({timezone}). Visitors see the times in their own time zone.',
  'booking.dayOff': 'No meetings',
  'booking.addHours': 'Add hours',
  'booking.removeHours': 'Remove these hours',
  'booking.copyToAll': 'Copy to every day',
  'booking.copiedToAll': 'Copied to every day. Remove the days you do not want.',
  'booking.from': 'From',
  'booking.to': 'To',
  'booking.hoursOrder': 'It ends after it starts.',
  'booking.hoursShort': 'Too short for one meeting of {time}.',
  'booking.hoursOverlap': 'It overlaps the hours before it.',
  'booking.noHours': 'Add the hours you take meetings before you switch your link on.',
  'booking.meetingTitle': 'The meetings',
  'booking.pageTitle': 'Title on the page (optional)',
  'booking.pageTitleHint': 'Empty, it says “{title}”.',
  'booking.intro': 'A few words for visitors (optional)',
  'booking.introHint': 'What the meeting is for, what to prepare.',
  'booking.duration': 'Length of each meeting',
  'booking.place': 'Where',
  'booking.locationHint': 'Visitors see it only once they have booked.',
  'booking.buffer': 'Free time around your meetings',
  'booking.bufferHint': 'Kept before and after every meeting of yours, booked or not.',
  'booking.notice': 'Booked at least',
  'booking.noticeHint': 'How soon before a meeting it may be booked.',
  'booking.noticeNone': 'Any time before',
  'booking.noticeBefore': '{time} before',
  'booking.daysAhead': 'Booked up to',
  'booking.daysAheadValue': '{days} ahead',
  'booking.none': 'None',
  'booking.saved': 'Booking page saved.',
  'booking.saveFailed': 'Your booking page was not saved. Try again.',
  'booking.unsaved': 'You have changes that are not saved.',
  'booking.save': 'Save',
  'booking.guest': 'Guest',
  'booking.guestBooked': 'Booked through the booking link · {when}',
  'booking.guestCancelled': 'Cancelled by the guest · {when}',
  'booking.guestLocked': 'A guest picked this time, so it is not changed. If you cancel it, they are told by email and can book another time.',

  // An employee's month of shifts, from their name on the team board (operator, 2026-10-27).
  'attendance.monthShifts': 'Month shifts',
  'attendance.noShiftsInMonth': 'No shifts in {month}.',
  'attendance.daysWorked': 'Days worked',
  'attendance.previousMonth': 'Previous month',
  'attendance.nextMonth': 'Next month',

  // CR-004 (2026-10-28): a day more than an hour short goes to an administrator; rewards of part of a
  // day's pay; points an administrator adds.
  'nav.rewards': 'Rewards & points',
  'shortfall.tab': 'Short days',
  'shortfall.waitingCount': '{n} short days waiting for a decision',
  'shortfall.queueNote':
    'Days worked more than an hour short of their hours. Nothing is deducted until an administrator decides: a part of a day’s pay, one day of annual leave instead, or an excuse with no deduction. Up to an hour short is still the automatic quarter day.',
  'shortfall.filter.PENDING': 'Waiting',
  'shortfall.filter.RESOLVED': 'Decided',
  'shortfall.filter.ALL': 'All',
  'shortfall.search': 'Employee name or code',
  'shortfall.from': 'From',
  'shortfall.to': 'To',
  'shortfall.workDate': 'Day',
  'shortfall.required': 'Owed',
  'shortfall.worked': 'Worked',
  'shortfall.missing': 'Short by',
  'shortfall.decisionCol': 'Decision',
  'shortfall.nothingWaiting': 'No short day is waiting for a decision.',
  'shortfall.none': 'No short days.',
  'shortfall.decide': 'Decide',
  'shortfall.reverse': 'Reverse the decision',
  'shortfall.status.PENDING_ADMIN_REVIEW': 'Waiting for a decision',
  'shortfall.status.RESOLVED_SALARY': 'Salary deduction',
  'shortfall.status.RESOLVED_LEAVE': 'Leave day',
  'shortfall.status.RESOLVED_EXCUSED': 'Excused',
  'shortfall.decision.salary': '{percent}% of a day’s pay',
  'shortfall.decision.leave': 'One day of annual leave',
  'shortfall.decision.excused': 'Excused — no deduction',
  'shortfall.title': 'Short day',
  'shortfall.theDay': 'The day',
  'shortfall.rule': 'More than {minutes} short: an administrator decides — nothing is deducted until then.',
  'shortfall.inForce': 'Decision in force',
  'shortfall.noDecision': 'No decision yet — nothing is deducted from the salary or the leave.',
  'shortfall.decidedBy': 'Decided by {name}, {date}',
  'shortfall.note': 'Administrators’ note',
  'shortfall.noteHint': 'Optional. Only administrators read it — never the employee.',
  'shortfall.posting': 'What it posted',
  'shortfall.openPenalty': 'Open the penalty',
  'shortfall.penaltyOnPayroll': 'Deducted by the {month} payroll',
  'shortfall.penaltyWaiting': 'To be deducted by the {month} payroll',
  'shortfall.penaltyCancelled': 'Cancelled since: {reason}',
  'shortfall.penaltyWallet': 'Cancelled since with {points} wallet points',
  'shortfall.leaveTaken': '1 day of {type} taken, {year}',
  'shortfall.history': 'History',
  'shortfall.action.RESOLVED': 'Decided',
  'shortfall.action.REVERSED': 'Reversed',
  'shortfall.noHistory': 'Nothing decided yet.',
  'shortfall.ownDay': 'This is your own day: another administrator decides it.',
  'shortfall.context': 'For deciding',
  'shortfall.dayRate': 'One day’s pay',
  'shortfall.decideTitle': 'Decide a short day',
  'shortfall.choose': 'Decision',
  'shortfall.salaryOption': 'Salary deduction (%)',
  'shortfall.salaryOptionHint': 'A percentage of one day’s pay: 25 is a quarter day, 100 a whole day, 200 two days.',
  'shortfall.leaveOption': 'Deduct 1 leave day',
  'shortfall.leaveOptionHint': 'One day of annual leave instead — and nothing from the salary.',
  'shortfall.excuseOption': 'Excuse — no deduction',
  'shortfall.excuseOptionHint': 'Nothing from the salary or the leave. Say why below.',
  'shortfall.excuseReason': 'Why it is excused',
  'shortfall.excuseReasonHint': 'Required, 3 to 1,000 characters. Only administrators read it — the employee is told the day is excused, not why.',
  'shortfall.confirmExcuse': 'I confirm: excuse {name}’s day on {date} — nothing from the salary or the leave.',
  'shortfall.percent': 'Percentage of one day’s pay',
  'shortfall.percentHint': 'A whole number from 1 to {max}.',
  'shortfall.equivalent': '= {days} of pay',
  'shortfall.amountPreview': '≈ {amount} at the day rate on that day. Payroll prices it at its month’s rate.',
  'shortfall.noSalaryPreview': 'The money is shown to whoever handles salaries. Payroll prices it at its month’s day rate.',
  'shortfall.leaveBalance': 'Annual leave {year}: {remaining} days left',
  'shortfall.leavePending': '{n} more waiting for approval',
  'shortfall.leaveProblem.INSUFFICIENT_BALANCE': 'Less than a day of annual leave is left for {year}: decide a part of a day’s pay instead.',
  'shortfall.leaveProblem.LEAVE_TYPE_UNAVAILABLE':
    'Annual leave is switched off, unpaid or has no yearly allowance (Settings › Leave): no day can be taken from it.',
  'shortfall.monthSignedOff': 'The {month} payroll is signed off: no salary deduction can be added to it. Take a day of leave instead.',
  'shortfall.confirmSalary': 'I confirm: deduct {percent}% of {name}’s day’s pay for {date}.',
  'shortfall.confirmLeave': 'I confirm: take one day of {name}’s annual leave for {date}, and nothing from the salary.',
  'shortfall.submit': 'Decide',
  'shortfall.decidedToast': 'Decided — {name} is told.',
  'shortfall.changed': 'This day changed while you were looking at it. Look again.',
  'shortfall.reverseTitle': 'Reverse the decision',
  'shortfall.reverseReason': 'Why',
  'shortfall.reverseHint': 'Kept in the history. The employee is told the decision was withdrawn — not why.',
  'shortfall.reverseSalaryNote': 'The deduction is cancelled — unless a payroll has already taken it.',
  'shortfall.reverseLeaveNote': 'The day of leave is given back.',
  'shortfall.reverseExcuseNote': 'The excuse is withdrawn.',
  'shortfall.reverseThen': 'The day then waits for a new decision.',
  'shortfall.reversedToast': 'Reversed — the day waits for a decision again.',
  'shortfall.mineTitle': 'My short days',
  'shortfall.minePending': 'Waiting for an administrator — nothing is deducted until then.',
  'shortfall.mineSalary': 'Decided: {percent}% of a day’s pay',
  'shortfall.mineLeave': 'Decided: one day of annual leave, nothing from the salary',
  'shortfall.mineExcused': 'Excused — nothing is deducted',
  'violations.kind.shortfall': 'Short day — an administrator’s decision',
  'violations.shortfallDecision': 'An administrator’s decision: {percent}% of a day’s pay',
  'violations.openShortDay': 'Open the short day',
  'rewards.title': 'Rewards & points',
  'rewards.tabRewards': 'Day rewards',
  'rewards.tabPoints': 'Points added',
  'rewards.grant': 'Grant a reward',
  'rewards.addPoints': 'Add points',
  'rewards.size': 'Reward',
  'rewards.size.quarter': 'Quarter day',
  'rewards.size.half': 'Half day',
  'rewards.status.GRANTED': 'To be paid',
  'rewards.status.PAID': 'Paid',
  'rewards.status.CANCELLED': 'Withdrawn',
  'rewards.month': 'Payroll month',
  'rewards.grantedBy': 'Granted by',
  'rewards.note': 'Note to the employee',
  'rewards.noteHint': 'Optional. The employee reads it in the notice and beside their payslips.',
  'rewards.none': 'No rewards yet.',
  'rewards.withdraw': 'Withdraw',
  'rewards.paidWith': 'Paid with the {month} salary',
  'rewards.onPayroll': 'On the {month} payroll',
  'rewards.employee': 'Employee',
  'rewards.pickEmployee': 'Choose an employee',
  'rewards.sizeHint': 'Paid with the salary at their day rate. Not hours worked, leave or points.',
  'rewards.preview': '≈ {amount} at today’s day rate. Payroll prices it at its month’s rate.',
  'rewards.previewHidden': 'Its money is shown to whoever handles salaries.',
  'rewards.confirm': 'I confirm: grant {name} this reward — {size} of their pay.',
  'rewards.grantedToast': 'Reward granted — {name} is told.',
  'rewards.alreadyGranted': 'Already granted — nothing more was added.',
  'rewards.cannot.SELF': 'Nobody grants themselves a reward.',
  'rewards.cannot.INACTIVE': 'This employee is switched off.',
  'rewards.cancelTitle': 'Withdraw a reward',
  'rewards.cancelReason': 'Why',
  'rewards.cancelHint': 'Kept by the administrators. The employee is told it was withdrawn — not why.',
  'rewards.cancelledToast': 'Reward withdrawn.',
  'rewards.mineTitle': 'My rewards',
  'rewards.mineGranted': 'To be paid with your salary',
  'points.grantTitle': 'Add points to a wallet',
  'points.points': 'Points',
  'points.pointsHint': 'A whole number, 1 to 1,000,000.',
  'points.reason': 'Reason',
  'points.reasonHint': 'Required, 3 to 500 characters. Only administrators read it — the employee sees how many points and when, never why.',
  'points.confirm': 'I confirm: add {points} points to {name}’s wallet.',
  'points.addedToast': '{points} points added to {name}’s wallet.',
  'points.alreadyAdded': 'Already added — nothing more was added.',
  'points.none': 'No points added by hand yet.',
  'points.balanceAfter': 'Balance after',
  'points.by': 'Added by',
  'points.self': 'Nobody adds points to their own wallet.',
  // AI daily points (CR-007): the administrators' review of the 06:00 proposals.
  'nav.aiPoints': 'AI Daily Points',
  'aiPoints.title': 'AI Daily Points',
  'aiPoints.subtitle': 'Expected work = 0 bonus points. Only verified above-normal contributions earn bonus points.',
  'aiPoints.tabReview': 'To review',
  'aiPoints.tabHistory': 'Daily history',
  'aiPoints.tabSettings': 'Settings',
  'aiPoints.runNow': 'Run yesterday now',
  'aiPoints.runStarted': 'Run started for {date} — proposals appear as the AI makes them.',
  'aiPoints.recentRuns': 'Recent runs',
  'aiPoints.pendingCount': 'To review: {n}',
  'aiPoints.noRuns': 'No runs yet',
  'aiPoints.noRunsHint': 'The first run evaluates yesterday as soon as the feature starts; then one runs every morning at 06:00.',
  'aiPoints.noRunForDate': 'No run for {date}',
  'aiPoints.noRunForDateHint': 'Pick another date above.',
  'aiPoints.noRows': 'Nothing here',
  'aiPoints.noRowsHint': 'No employee matches these filters for this date.',
  'aiPoints.noHistory': 'No records in this range',
  'aiPoints.noHistoryHint': 'Widen the dates or clear a filter.',
  'aiPoints.blockedTitle': 'Blocked: configure the existing OpenAI integration',
  'aiPoints.blockedBody': 'The server has no OpenAI key, so no proposal can be made — none is ever made up. Set OPENAI_API_KEY in apps/backend/.env (the key Calls › AI summary uses), run ./deploy.sh api, then Retry the run here.',
  'aiPoints.disabledBody': 'AI daily points are switched off in their settings: no run is made and nothing is sent to OpenAI. The wallet works as before.',
  'aiPoints.nextRun': 'Runs daily at {time} ({timezone}) — next {at}.',
  'aiPoints.serverReady': 'OpenAI is configured on the server',
  'aiPoints.serverReadyBody': 'The same connection Calls › AI summary uses. Proposals are made with it; nothing is credited until an administrator approves.',
  'aiPoints.scheduleLine': 'Runs every day at {time} company time ({timezone}), for the work date before.',
  'aiPoints.jobsOff': 'Background jobs are off on this server (JOBS_ENABLED=false): another server must run them.',
  'aiPoints.runTimes': 'Started {started} · data as of {cutoff}',
  'aiPoints.runUsage': 'AI calls: {calls} · tokens {tokensIn} in, {tokensOut} out',
  'aiPoints.runCredited': 'Points credited: {n}',
  'aiPoints.retryRun': 'Retry deferred / failed ({n})',
  'aiPoints.regenerateUnapproved': 'Regenerate unapproved',
  'aiPoints.stat.evaluated': 'Evaluated',
  'aiPoints.stat.pending': 'To review',
  'aiPoints.stat.approved': 'Approved',
  'aiPoints.stat.zeroApprovals': '0-point approvals',
  'aiPoints.stat.rejected': 'Rejected',
  'aiPoints.stat.deferred': 'Deferred',
  'aiPoints.stat.failed': 'Failed',
  'aiPoints.stat.notEligible': 'Not eligible',
  'aiPoints.stat.inProgress': 'In progress',
  'aiPoints.searchEmployee': 'Name or employee code',
  'aiPoints.minScore': 'Score',
  'aiPoints.anyScore': 'Any score',
  'aiPoints.scoreAtLeast': 'Score {n} or more',
  'aiPoints.col.hours': 'Worked / standard',
  'aiPoints.col.free': 'Free Shifts',
  'aiPoints.col.work': 'Tasks · tickets',
  'aiPoints.col.collab': 'Calls · reviews · comments',
  'aiPoints.col.score': 'Score',
  'aiPoints.col.date': 'Work date',
  'aiPoints.col.decision': 'Decided by',
  'aiPoints.notOwed': 'none owed',
  'aiPoints.sessions': 'Sessions: {n}',
  'aiPoints.tasksShort': 'Tasks: {n} ({v} verified)',
  'aiPoints.opsShort': 'Tickets: {k} · Q Tasks: {q}',
  'aiPoints.collabShort': '{c} · {r} · {m}',
  'aiPoints.credited': 'Credited: {n}',
  'aiPoints.nothingCredited': 'Nothing credited',
  'aiPoints.approve': 'Approve',
  'aiPoints.reject': 'Reject',
  'aiPoints.retry': 'Retry',
  'aiPoints.regenerate': 'Regenerate',
  'aiPoints.approveTitle': 'Approve these points?',
  'aiPoints.approveAll': 'Approve all ({n})',
  'aiPoints.approveAllTitle': 'Approve all proposals for {date}?',
  'aiPoints.approveAllFigures': 'Proposals: {n} · points in all: {points} · employees with points: {people} · at 0: {zero}',
  'aiPoints.approveAllWithPoints': 'With points',
  'aiPoints.approveAllMore': '+{n} more',
  'aiPoints.approveAllLow': 'Low confidence: {n} — read their reasons first, or approve only the confident ones.',
  'aiPoints.approveAllLeft': 'Left as they are — {list}.',
  'aiPoints.approveAllLeftSelf': 'your own: {n}',
  'aiPoints.approveAllLeftAdmin': 'administrators: {n}',
  'aiPoints.approveAllLeftInactive': 'switched off, with points: {n}',
  'aiPoints.approveAllEach': 'Each is approved on its own, exactly as by hand: every employee with points gets one thank-you notice; those at 0 get no wallet entry and no notice. Decisions are final.',
  'aiPoints.approveAllNothing': 'Nothing here is waiting for your approval.',
  'aiPoints.approveAllConfirm': 'Approve all ({n})',
  'aiPoints.approveConfidentOnly': 'Only the confident ({n})',
  'aiPoints.approveAllDone': 'Approved: {n} · points credited: {points}.',
  'aiPoints.approveAllPartial': 'Approved: {n} · points credited: {points} · left as they were: {failed} — changed or decided meanwhile.',
  'aiPoints.reason.ADMINISTRATOR': 'Administrator — no points',
  'aiPoints.approveCredit': 'Approve and credit {n}',
  'aiPoints.approveZero': 'Approve 0 points',
  'aiPoints.approveBody': 'Credit {n} to {name}’s wallet for the work on {date}? A decision is final.',
  'aiPoints.approveZeroBody': 'Approve {name}’s record for {date} at 0 — no wallet entry and no notice. A decision is final.',
  'aiPoints.approveNotice': 'They get one notice: how many points, for which day, and thanks — never the reasons.',
  'aiPoints.lowConfidenceWarning': 'Low confidence: the AI or the data behind this proposal is uncertain. Read its reasons and evidence before approving.',
  'aiPoints.approvedToast': '{name}: {n} credited.',
  'aiPoints.approvedZeroToast': '{name}: approved at 0 — nothing credited.',
  'aiPoints.alreadyApproved': 'Already approved — nothing more was credited.',
  'aiPoints.changed': 'The proposal was regenerated since you opened it — look at the new one first.',
  'aiPoints.uncertainty.LOW': 'Low uncertainty',
  'aiPoints.uncertainty.MEDIUM': 'Some uncertainty',
  'aiPoints.uncertainty.HIGH': 'High uncertainty',
  'aiPoints.confidence.HIGH': 'high',
  'aiPoints.confidence.MEDIUM': 'medium',
  'aiPoints.confidence.LOW': 'low',
  'aiPoints.confidenceLine': 'AI confidence {confidence} · {uncertainty}',
  'aiPoints.self.OTHER_REVIEWER': 'Your own — another reviewer decides it',
  'aiPoints.self.NO_OTHER_REVIEWER': 'Your own — no other reviewer can decide it',
  'aiPoints.selfLong.OTHER_REVIEWER': 'This is your own day. Nobody approves their own points: another administrator with AI daily points review decides it.',
  'aiPoints.selfLong.NO_OTHER_REVIEWER': 'This is your own day, and nobody else may review AI daily points, so it stays pending. Give another administrator the review permission to decide it.',
  'aiPoints.runKindLabel': 'Run',
  'aiPoints.runKind.SCHEDULED': '06:00 run',
  'aiPoints.runKind.INITIAL_YESTERDAY_TRIAL': 'Initial yesterday trial',
  'aiPoints.runKind.MANUAL': 'Run by hand',
  'aiPoints.runStatus.EVALUATING': 'Evaluating',
  'aiPoints.runStatus.READY': 'Ready',
  'aiPoints.runStatus.BLOCKED_CONFIGURATION': 'Blocked',
  'aiPoints.status.QUEUED': 'Queued',
  'aiPoints.status.EVALUATING': 'Evaluating',
  'aiPoints.status.PENDING_ADMIN_APPROVAL': 'To review',
  'aiPoints.status.APPROVED': 'Approved',
  'aiPoints.status.REJECTED': 'Rejected',
  'aiPoints.status.DEFERRED': 'Deferred',
  'aiPoints.status.FAILED_AI': 'AI failed',
  'aiPoints.status.BLOCKED_CONFIGURATION': 'Blocked',
  'aiPoints.status.NOT_ELIGIBLE': 'Not eligible',
  'aiPoints.reason.REGULAR_SHIFT_OPEN': 'Waiting: the Regular Shift is still open',
  'aiPoints.reason.REGULAR_END_UNCONFIRMED': 'Waiting: the shift’s end is not confirmed',
  'aiPoints.reason.DAY_NOT_JUDGED': 'Waiting: the day’s hours are not judged yet',
  'aiPoints.reason.FREE_SHIFT_OPEN': 'Waiting: a Free Shift is still open',
  'aiPoints.reason.FREE_END_UNCONFIRMED': 'Waiting: a Free Shift’s end is not confirmed',
  'aiPoints.reason.ON_LEAVE': 'On leave',
  'aiPoints.reason.HOLIDAY': 'Public holiday',
  'aiPoints.reason.DAY_OFF': 'Day off',
  'aiPoints.reason.NO_WORK_RECORDED': 'No shift or work recorded',
  'aiPoints.reason.NOT_EMPLOYED_YET': 'Not yet employed that day',
  'aiPoints.reason.OPENAI_NOT_CONFIGURED': 'OpenAI is not configured on the server',
  'aiPoints.reason.NO_MODEL': 'No AI model is offered by the server',
  'aiPoints.reason.OPENAI_KEY_REFUSED': 'OpenAI refused the server’s key',
  'aiPoints.reason.MODEL_UNAVAILABLE': 'The AI model is not available to the key',
  'aiPoints.reason.INVALID_OUTPUT': 'The AI kept answering outside the rules',
  'aiPoints.reason.PROVIDER_RATE_LIMITED': 'OpenAI was busy — tried again later',
  'aiPoints.reason.PROVIDER_UNAVAILABLE': 'OpenAI did not answer',
  'aiPoints.reason.PROVIDER_TIMEOUT': 'OpenAI answered too slowly',
  'aiPoints.reason.PROVIDER_QUOTA': 'The OpenAI budget is used up',
  'aiPoints.reason.PROVIDER_REJECTED': 'OpenAI refused the request',
  'aiPoints.reason.JOB_TIMEOUT': 'The evaluation took too long',
  'aiPoints.reason.INTERNAL': 'An error on the server',
  'aiPoints.reason.NO_SNAPSHOT': 'The day’s facts were not kept',
  'aiPoints.adminOnly': 'Administrators only — the employee sees only the points credited and a thank-you, never these reasons.',
  'aiPoints.proposal': 'Proposal',
  'aiPoints.dimension': 'Dimension',
  'aiPoints.points': 'Points',
  'aiPoints.reasonCol': 'Why',
  'aiPoints.capOfMax': 'cap {cap} of {max}',
  'aiPoints.noProposal': 'No proposal yet.',
  'aiPoints.summary': 'Summary',
  'aiPoints.excluded': 'Left out, and why',
  'aiPoints.dim.REGULAR_EXTRA_HOURS': 'Hours above standard',
  'aiPoints.dim.FREE_SHIFT': 'Free Shift contribution',
  'aiPoints.dim.TASKS_ABOVE_BASELINE': 'Tasks above baseline',
  'aiPoints.dim.EXTRA_OPERATIONAL': 'Extra operational work',
  'aiPoints.dim.CALLS_COLLABORATION': 'Calls & collaboration',
  'aiPoints.capWhy.NO_SHIFT': 'No Regular Shift that day',
  'aiPoints.capWhy.NO_SURPLUS': 'No minutes past the standard hours',
  'aiPoints.capWhy.SURPLUS_BELOW_STEP': 'Too few extra minutes for a point',
  'aiPoints.capWhy.SURPLUS_HOURS': 'Verified minutes past the standard hours',
  'aiPoints.capWhy.LONG_SHIFT_LOW_OUTPUT': 'A long day with little to show — at most 1',
  'aiPoints.capWhy.NO_FREE_SHIFT': 'No Free Shift that day',
  'aiPoints.capWhy.FREE_BELOW_STEP': 'Too few Free Shift minutes for a point',
  'aiPoints.capWhy.FREE_MINUTES': 'Verified Free Shift minutes',
  'aiPoints.capWhy.AT_OR_BELOW_BASELINE': 'At or below their usual output',
  'aiPoints.capWhy.NOT_VERIFIED': 'Above usual, but nothing verified yet',
  'aiPoints.capWhy.ABOVE_BASELINE': 'More verified work than usual',
  'aiPoints.capWhy.NO_TRUSTED_BASELINE': 'Too little history for a baseline — verified facts only',
  'aiPoints.capWhy.MICRO_TASKS': 'Many tiny self-made tasks — at most 1',
  'aiPoints.capWhy.NO_OPERATIONAL_WORK': 'No tickets, Q Tasks or cross-team help',
  'aiPoints.capWhy.NO_COLLABORATION': 'No meaningful calls, reviews or comments',
  'aiPoints.capWhy.COLLABORATION': 'Calls, reviews or comments on others’ work',
  'aiPoints.evidence': 'Evidence',
  'aiPoints.evidenceHint': 'What the system recorded, as of {at}. Each record opens on its own page while it still exists.',
  'aiPoints.noEvidence': 'No evidence was read.',
  'aiPoints.sourceGone': 'Record no longer available — kept as it was',
  'aiPoints.openSource': 'Open the record',
  'aiPoints.kind.ATTENDANCE': 'Regular Shift',
  'aiPoints.kind.FREE_SHIFT': 'Free Shift',
  'aiPoints.kind.TASK': 'Task',
  'aiPoints.kind.TICKET': 'Ticket',
  'aiPoints.kind.Q_TASK': 'Q Task',
  'aiPoints.kind.CALL': 'Call',
  'aiPoints.kind.REVIEW': 'Review',
  'aiPoints.kind.COMMENTS': 'Comments',
  'aiPoints.kind.BASELINE': 'Baseline',
  'aiPoints.ev.attendance': 'Worked {worked} of {standard} — {surplus} extra',
  'aiPoints.ev.attendanceOff': 'Breaks {breaks}, paused {paused}, not working {idle}',
  'aiPoints.ev.free': 'Worked {worked}, not working {idle}',
  'aiPoints.ev.onTime': 'on time',
  'aiPoints.ev.late': 'late',
  'aiPoints.ev.sentBack': 'sent back {n}×',
  'aiPoints.ev.crossTeam': 'for another team',
  'aiPoints.ev.selfCreated': 'self-made',
  'aiPoints.ev.open': 'open {time}',
  'aiPoints.ev.call': '{time} · participants: {n}',
  'aiPoints.ev.callSummary': 'AI summary — decisions: {d}, action items: {a}',
  'aiPoints.ev.shortCall': 'too short to count',
  'aiPoints.ev.reviewApproved': 'Reviewed and approved someone else’s task',
  'aiPoints.ev.reviewSentBack': 'Reviewed and sent back someone else’s task',
  'aiPoints.ev.comments': 'Comments on others’ work: {t} tasks, {k} tickets, {q} Q Tasks',
  'aiPoints.ev.baseline': 'Usual output over {days} worked days: {tasks} tasks and {ops} tickets / Q Tasks a day',
  'aiPoints.ev.baselineThin': 'Worked days before: {days} — too few for a baseline',
  'aiPoints.ev.team': 'Colleagues ({n}): {tasks} a day',
  'aiPoints.verification.APPROVED': 'Approved by the reviewer',
  'aiPoints.verification.WORKFLOW_STEP': 'Workflow step handed on',
  'aiPoints.verification.AWAITING_REVIEW': 'Waiting for review',
  'aiPoints.verification.SELF_MARKED_DONE': 'Marked done by themselves',
  'aiPoints.priority.LOW': 'Low',
  'aiPoints.priority.MEDIUM': 'Medium',
  'aiPoints.priority.HIGH': 'High',
  'aiPoints.priority.URGENT': 'Urgent',
  'aiPoints.attempts': 'Attempts and revisions',
  'aiPoints.attemptsHint': 'Every look at this day — the AI’s answers, those refused and why, and who asked for a retry.',
  'aiPoints.noAttempts': 'No attempts yet.',
  'aiPoints.when': 'When',
  'aiPoints.outcome': 'Outcome',
  'aiPoints.detailsCol': 'Details',
  'aiPoints.revisionAttempt': 'revision {r}, attempt {a}',
  'aiPoints.askedBy': 'Asked by {name}',
  'aiPoints.tokens': 'tokens {i} in, {o} out',
  'aiPoints.outcome.PROPOSED': 'Proposed',
  'aiPoints.outcome.INVALID_OUTPUT': 'Answer refused',
  'aiPoints.outcome.PROVIDER_ERROR': 'OpenAI error',
  'aiPoints.outcome.BLOCKED_CONFIGURATION': 'Blocked',
  'aiPoints.outcome.DEFERRED': 'Deferred',
  'aiPoints.outcome.NOT_ELIGIBLE': 'Not eligible',
  'aiPoints.outcome.REQUEUED': 'Retry asked',
  'aiPoints.trigger.SCHEDULED': '06:00 run',
  'aiPoints.trigger.TRIAL': 'initial trial',
  'aiPoints.trigger.MANUAL': 'run by hand',
  'aiPoints.trigger.RECHECK': 'automatic recheck',
  'aiPoints.trigger.RETRY': 'retry',
  'aiPoints.decision': 'Decision',
  'aiPoints.proposed': 'Proposed points',
  'aiPoints.creditedLabel': 'Credited points',
  'aiPoints.reviewer': 'Decided by',
  'aiPoints.decidedAt': 'Decided at',
  'aiPoints.walletTx': 'Wallet entry',
  'aiPoints.rejectNote': 'Rejection note',
  'aiPoints.figures': 'Figures',
  'aiPoints.fig.day': 'Day',
  'aiPoints.fig.standard': 'Standard hours',
  'aiPoints.fig.worked': 'Worked (net)',
  'aiPoints.fig.surplus': 'Past the standard',
  'aiPoints.fig.free': 'Free Shifts',
  'aiPoints.fig.tasks': 'Tasks finished',
  'aiPoints.fig.tasksValue': '{f} — {v} verified, {r} waiting for review',
  'aiPoints.fig.ops': 'Tickets and Q Tasks',
  'aiPoints.fig.calls': 'Meaningful calls',
  'aiPoints.fig.callsValue': '{n} ({time})',
  'aiPoints.fig.collab': 'Collaboration',
  'aiPoints.fig.collabValue': 'Reviews: {r} · comments on others’ work: {c}',
  'aiPoints.fig.baseline': 'Baseline',
  'aiPoints.dayKind.WORKDAY': 'Working day',
  'aiPoints.dayKind.DAY_OFF': 'Worked on a day off',
  'aiPoints.dayKind.HOLIDAY': 'Worked on a holiday',
  'aiPoints.dayKind.LEAVE': 'Worked while on leave',
  'aiPoints.dayKind.NO_SHIFT': 'No shift recorded',
  'aiPoints.flag.INSUFFICIENT_BASELINE': 'Thin baseline',
  'aiPoints.flag.NO_SHIFT_RECORDED': 'No shift recorded',
  'aiPoints.flag.AGENT_UNTRACKED': 'Not tracked by the Desktop Agent',
  'aiPoints.flag.AUTO_CLOSED_SHIFT': 'Shift closed automatically',
  'aiPoints.flag.WORKED_ON_DAY_OFF': 'Worked on a day off',
  'aiPoints.flag.NEW_EMPLOYEE': 'New employee',
  'aiPoints.flag.CALL_SUMMARIES_OFF': 'Call summaries not read',
  'aiPoints.flag.MICRO_TASKS': 'Many tiny tasks',
  'aiPoints.flag.SELF_CREATED_TASKS': 'Mostly self-made tasks',
  'aiPoints.flag.LONG_SHIFT_LOW_OUTPUT': 'Long day, little output',
  'aiPoints.flag.CALL_TIME_NO_OUTCOME': 'Long calls, no outcome',
  'aiPoints.flag.REPEATED_MOVES': 'Repeated status changes',
  'aiPoints.flag.EVIDENCE_TRUNCATED': 'Evidence list shortened',
  'aiPoints.flagHint.INSUFFICIENT_BASELINE': 'Too few worked days before this one to know their usual output.',
  'aiPoints.flagHint.NO_SHIFT_RECORDED': 'Work was finished without a shift: the hours are unknown.',
  'aiPoints.flagHint.AGENT_UNTRACKED': 'The Desktop Agent did not track the shift, so Not working time is unknown.',
  'aiPoints.flagHint.AUTO_CLOSED_SHIFT': 'The shift was never ended; the system closed it.',
  'aiPoints.flagHint.WORKED_ON_DAY_OFF': 'Nothing was owed that day, so all of it counts as extra.',
  'aiPoints.flagHint.NEW_EMPLOYEE': 'Joined less than 30 days before this day.',
  'aiPoints.flagHint.CALL_SUMMARIES_OFF': 'Settings say not to read calls’ AI summaries.',
  'aiPoints.flagHint.MICRO_TASKS': 'Several tasks they made themselves were finished within minutes of being made.',
  'aiPoints.flagHint.SELF_CREATED_TASKS': 'Most finished tasks were made by the employee themselves.',
  'aiPoints.flagHint.LONG_SHIFT_LOW_OUTPUT': 'Two or more extra hours with nothing finished, reviewed or commented.',
  'aiPoints.flagHint.CALL_TIME_NO_OUTCOME': 'An hour and a half or more on calls with nothing finished.',
  'aiPoints.flagHint.REPEATED_MOVES': 'A task was moved to finished three or more times that day.',
  'aiPoints.flagHint.EVIDENCE_TRUNCATED': 'Only the first 25 of a kind were sent to the AI; the counts are complete.',
  'aiPoints.snapshot': 'Made under',
  'aiPoints.rubricVersion': 'Settings version',
  'aiPoints.defaultsVersion': 'The defaults (none saved yet)',
  'aiPoints.model': 'AI model',
  'aiPoints.promptVersion': 'Prompt version',
  'aiPoints.dataCutoff': 'Data as of',
  'aiPoints.generated': 'Proposed at',
  'aiPoints.revision': 'Revision',
  'aiPoints.timezone': 'Time zone · day starts',
  'aiPoints.digest': 'Input digest',
  'aiPoints.backToProposal': 'The proposal',
  'aiPoints.rejectTitle': 'Reject this proposal',
  'aiPoints.rejectNoteLabel': 'Note',
  'aiPoints.rejectNoteHint': 'Optional. Kept by the administrators; the employee is never told of a rejection.',
  'aiPoints.rejectedToast': 'Rejected — nothing credited to {name}.',
  'aiPoints.alreadyRejected': 'Already rejected.',
  'aiPoints.notRejectable': 'Only a proposal waiting for a decision can be rejected.',
  'aiPoints.retryTitle': 'Evaluate this day again',
  'aiPoints.regenerateTitle': 'Regenerate this proposal',
  'aiPoints.retryRunTitle': 'Retry this run',
  'aiPoints.retrySubtitle': 'The day’s facts are read again and sent to the AI. A new revision; the old one stays in its history. Nothing is credited.',
  'aiPoints.retryReason': 'Why',
  'aiPoints.retryReasonHint': 'Required, 3 to 500 characters — kept with the retry.',
  'aiPoints.retriedToast': 'Sent back to be evaluated again.',
  'aiPoints.runRetriedToast': 'Sent back to be evaluated again: {n}.',
  'aiPoints.includePending': 'Also regenerate the proposals still waiting for a decision',
  'aiPoints.includePendingHint': 'Decided ones are never touched, nor your own.',
  'aiPoints.settingsTitle': 'AI daily points settings',
  'aiPoints.settingsSubtitle': 'Version {v}. A save applies to the next runs only.',
  'aiPoints.settingsSaved': 'Saved as version {v} — it applies to the next runs.',
  'aiPoints.enabledLabel': 'Make daily proposals',
  'aiPoints.enabledHint': 'Off: no run is made and nothing is sent to OpenAI. The wallet and calls’ AI summaries are not affected.',
  'aiPoints.rubricLabel': 'Points per dimension',
  'aiPoints.rubricHint': 'The most each dimension may give — whole points adding up to exactly 10. The AI may give less, never more.',
  'aiPoints.rubricSum': 'Total: {n} of 10',
  'aiPoints.setting.regularMinutesPerPoint': 'Minutes past standard hours per point',
  'aiPoints.setting.regularMinutesPerPointHint': 'Verified minutes of the Regular Shift past the shift’s own standard hours.',
  'aiPoints.setting.freeMinutesPerPoint': 'Free Shift minutes per point',
  'aiPoints.setting.freeMinutesPerPointHint': 'Verified worked minutes in the work date’s Free Shifts.',
  'aiPoints.setting.baselineWindowDays': 'Baseline: days to look back',
  'aiPoints.setting.baselineWindowDaysHint': 'Their usual output is read from the worked days in this many days before.',
  'aiPoints.setting.minBaselineDays': 'Baseline: worked days needed',
  'aiPoints.setting.minBaselineDaysHint': 'With fewer, only directly verified facts can earn task points.',
  'aiPoints.setting.minCallMinutes': 'Shortest meaningful call (minutes)',
  'aiPoints.setting.minCallMinutesHint': 'Shorter connected calls are not counted.',
  'aiPoints.setting.model': 'AI model',
  'aiPoints.setting.modelHint': 'One of the models the server offers for AI summaries.',
  'aiPoints.setting.modelDefault': 'The server’s default ({model})',
  'aiPoints.setting.includeCallSummaries': 'Read calls’ existing AI summaries',
  'aiPoints.setting.includeCallSummariesHint': 'Only a summary’s title and how many decisions and action items it lists — never its text. No call is transcribed for this.',
  'aiPoints.resetDefaults': 'Back to the defaults',
  'aiPoints.versions': 'Versions',
  'aiPoints.versionsHint': 'Each save is kept; every run says which version it used.',
  'aiPoints.noVersions': 'The defaults are in use — nothing saved yet.',
  'aiPoints.version': 'Version',
  'aiPoints.savedBy': 'Saved by',
  'leaves.deductedDays': '−{n} for short days',
  // Calls (2026-10-29): voice calls between employees, every connected one recorded.
  'nav.calls': 'Calls',
  'settings.calls': 'Calls',
  'calls.title': 'Calls',
  'calls.subtitle': 'Voice calls with any employee — one to one or in a group. Every call is recorded.',
  'calls.newCall': 'New call',
  'calls.newCallSubtitle': 'Pick one person for a voice call, or several for a group call.',
  'calls.myHistory': 'My history',
  'calls.allCalls': 'All calls',
  'calls.clientCalls': 'Client calls',
  'calls.employeeCalls': 'Employee calls',
  'calls.client': 'Client',
  'calls.anyClient': 'Any client',
  'calls.clientBadge': 'Client',
  'calls.fromClient': 'Client call',
  'calls.noClientCalls': 'No client calls match these filters',
  'calls.unavailableOff': 'Calls are not switched on yet. The history below still opens.',
  'calls.unavailableNotReady': 'Calls cannot be made right now: the recording service is not ready, and no call is made without its recording.',
  'calls.unavailableMissing': 'Missing on the server:',
  'calls.filter.all': 'All',
  'calls.filter.missed': 'Missed',
  'calls.filter.outgoing': 'Outgoing',
  'calls.filter.incoming': 'Incoming',
  'calls.noneYet': 'No calls yet',
  'calls.noneMatch': 'No calls match these filters',
  'calls.recordedHint': 'Every call is recorded. Whoever was connected to a call can play its recording here.',
  'calls.employee': 'Employee',
  'calls.everyone': 'Everyone',
  'calls.status': 'Status',
  'calls.anyStatus': 'Any status',
  'calls.type': 'Type',
  'calls.anyType': 'Any type',
  'calls.from': 'From',
  'calls.to': 'To',
  'calls.with': 'With',
  'calls.people': 'People',
  'calls.outcome': 'Outcome',
  'calls.when': 'When',
  'calls.duration': 'Duration',
  'calls.recording': 'Recording',
  'calls.loadMore': 'Load more',
  'calls.outgoing': 'Outgoing call',
  'calls.incoming': 'Incoming call',
  'calls.voiceCall': 'Voice call',
  'calls.groupVoiceCall': 'Group voice call',
  'calls.joinUnderway': 'Asks you into a call in progress',
  'calls.peopleCount': '{n} people',
  'calls.incomingFrom': 'Incoming call from {name}',
  'calls.accept': 'Accept',
  'calls.decline': 'Decline',
  'calls.moreRinging': '{n} more calling',
  'calls.recordedNotice': 'This call is recorded',
  'calls.recordingNow': 'This call is being recorded',
  'calls.recordingBadge': 'Recording',
  'calls.currentCall': 'Your call',
  'calls.move': 'Move the call',
  'calls.moveHint': 'Drag it anywhere, or use the arrow keys. Double-click puts it back in the corner.',
  'calls.state.calling': 'Calling…',
  'calls.state.preparing': 'Starting the recording…',
  'calls.state.connecting': 'Connecting…',
  'calls.state.reconnecting': 'Reconnecting…',
  'calls.state.ending': 'Ending…',
  'calls.state.otherDevice': 'Answered on another tab or device',
  'calls.endingSoon': 'This call ends in 5 minutes — the longest a call may last.',
  'calls.micDenied': 'Your browser blocks the microphone: allow it in the address bar to be heard.',
  'calls.enableAudio': 'Click to hear the call',
  'calls.inCall': 'In the call',
  'calls.joining': 'Joining…',
  'calls.mute': 'Mute',
  'calls.unmute': 'Unmute',
  'calls.addPeople': 'Add people',
  'calls.addPeopleSubtitle': 'They are asked into this same call — the same recording.',
  'calls.whoToAdd': 'Who to add',
  'calls.add': 'Add to the call',
  'calls.added': 'Asked into the call.',
  'calls.notInCall': 'You are not in a call.',
  'calls.screensShared': 'Screens shared',
  'calls.screensSharedHint': 'Who shared a screen during the call, and when. What it showed is not recorded: the recording is the call’s sound.',
  'calls.shareFrom': 'From',
  'calls.shareUntil': 'Until',
  'calls.share.start': 'Share your screen',
  'calls.share.stop': 'Stop sharing your screen',
  'calls.share.yours': 'You are sharing your screen',
  'calls.share.failed': 'Your screen could not be shared. Try again, or pick another window or tab.',
  'calls.screen.label': 'Shared screens',
  'calls.screen.of': '{name}’s screen',
  'calls.screen.show': 'Show {name}’s screen',
  'calls.screen.smaller': 'Make it smaller',
  'calls.screen.larger': 'Make it larger',
  'calls.screen.full': 'Full screen',
  'calls.screen.exitFull': 'Leave full screen',
  'calls.screen.hide': 'Put it away',
  'calls.screen.float': 'Float it over other windows',
  'calls.screen.move': 'Move the shared screen',
  'calls.screen.moveHint': 'Drag it anywhere by its top or its picture, or use the arrow keys. Double-click puts it back.',
  'calls.screen.resize': 'Resize the shared screen',
  'calls.screen.resizeHint': 'Drag this corner, or use the arrow keys.',
  'calls.screen.notRecorded': 'The call’s sound is recorded — the shared screen is not.',
  'calls.screen.waiting': 'Waiting for the picture…',
  'calls.hangUp': 'Hang up',
  'calls.cancel': 'Cancel',
  'calls.openCall': 'Open the call',
  'calls.whoToCall': 'Who to call',
  'calls.whoToCallHint': 'Up to {n} people.',
  'calls.full': 'A call holds {n} people at most',
  'calls.startCall': 'Call',
  'calls.startGroupCall': 'Start group call',
  'calls.notFound': 'There is no such call.',
  'calls.participants': 'People',
  'calls.participantsHint': 'Who was asked, who answered, and when each was in the call.',
  'calls.person': 'Person',
  'calls.invitation': 'Invitation',
  'calls.joined': 'Joined',
  'calls.left': 'Left',
  'calls.caller': 'Caller',
  'calls.neverJoined': 'Never joined',
  'calls.dropped': 'connection lost',
  'calls.details': 'Details',
  'calls.madeBy': 'Made by',
  'calls.formerEmployee': 'A former employee',
  'calls.madeAt': 'Made',
  'calls.started': 'Started',
  'calls.ended': 'Ended',
  'calls.why': 'Why it ended',
  'calls.keptUntil': 'Kept until {date}.',
  'calls.keptForever': 'Kept until an administrator changes how long recordings are kept.',
  'calls.noRecordingHint': 'Calls nobody answered, or that ended before the recording started, have no recording.',
  'calls.partialWarning': 'This recording does not hold the whole call: the call ended because its recording could not be confirmed.',
  'calls.playRecording': 'Play the recording',
  'calls.cannotPlay': 'The recording could not be played. Try again.',
  'calls.accessLog': 'Who asked to play it',
  'calls.accessLogHint': 'Every request to play this recording, let through or refused. Only whoever oversees these calls sees this.',
  'calls.accessLogEmpty': 'Nobody has asked to play it yet.',
  'calls.accessResult': 'Result',
  'calls.accessFrom': 'From',
  'calls.accessAllowed': 'Played',
  'calls.accessRefused': 'Refused',
  'calls.viaPhone': 'Phone app',
  'calls.viaWeb': 'Web',
  'calls.outcome.LIVE': 'In progress',
  'calls.outcome.ANSWERED': 'Answered',
  'calls.outcome.NO_ANSWER': 'No answer',
  'calls.outcome.MISSED': 'Missed',
  'calls.outcome.DECLINED': 'Declined',
  'calls.outcome.BUSY': 'Busy',
  'calls.outcome.CANCELLED': 'Cancelled',
  'calls.outcome.FAILED': 'Failed',
  'calls.recording.NONE': 'No recording',
  'calls.recording.RECORDING': 'Recording',
  'calls.recording.PROCESSING': 'Processing',
  'calls.recording.READY': 'Ready',
  'calls.recording.PARTIAL': 'Incomplete',
  'calls.recording.FAILED': 'Failed',
  'calls.recording.EXPIRED': 'Expired',
  'calls.participant.RINGING': 'Ringing',
  'calls.participant.ACCEPTED': 'Answered',
  'calls.participant.DECLINED': 'Declined',
  'calls.participant.MISSED': 'Missed',
  'calls.participant.BUSY': 'Busy',
  'calls.participant.CANCELLED': 'Called off',
  'calls.participant.LEFT': 'Left',
  'calls.callStatus.RINGING': 'Ringing',
  'calls.callStatus.PREPARING': 'Starting',
  'calls.callStatus.ACTIVE': 'In progress',
  'calls.callStatus.ENDING': 'Ending',
  'calls.callStatus.COMPLETED': 'Completed',
  'calls.callStatus.MISSED': 'Missed',
  'calls.callStatus.REJECTED': 'Declined',
  'calls.callStatus.CANCELLED': 'Cancelled',
  'calls.callStatus.BUSY': 'Busy',
  'calls.callStatus.FAILED': 'Failed',
  'calls.ended.normal': 'The call ended.',
  'calls.ended.recordingNotStarted': 'The call could not start: its recording did not start, and no call goes on without one.',
  'calls.ended.recordingLost': 'The call ended because its recording could not be confirmed.',
  'calls.ended.maxDuration': 'The call reached the longest a call may last.',
  'calls.ended.lostServer': 'The connection to Viganium was lost, so the call was ended: its recording could not be confirmed.',
  'calls.ended.lostServerRinging': 'The connection to Viganium was lost while the call was ringing, so it was called off.',
  'calls.refusal.NOT_JOINED': 'Only the people who were connected to this call can play its recording.',
  'calls.refusal.INCOMPLETE': 'This recording does not hold the whole call; it is kept for the administrators.',
  'calls.refusal.NOT_READY': 'The recording is still being processed.',
  'calls.refusal.EXPIRED': 'The recording was deleted at the end of its retention period. The call stays in the history.',
  'calls.refusal.EXPIRED_ON': 'The recording was deleted on {date}, at the end of its retention period. The call stays in the history.',
  'calls.refusal.FAILED': 'This call has no usable recording.',
  'calls.refusal.NO_RECORDING': 'This call has no recording.',
  'calls.refusalShort.NOT_A_PARTICIPANT': 'not on the call',
  'calls.refusalShort.NOT_JOINED': 'never connected',
  'calls.refusalShort.NO_RECORDING': 'no recording',
  'calls.refusalShort.NOT_READY': 'not ready',
  'calls.refusalShort.INCOMPLETE': 'incomplete',
  'calls.refusalShort.EXPIRED': 'expired',
  'calls.refusalShort.FAILED': 'failed',
  'calls.error.unavailable': 'Calls cannot be made right now: the recording service is not ready.',
  'calls.error.alreadyInCall': 'You are in another call. Hang up first.',
  'calls.error.full': 'A call holds {n} people at most.',
  'calls.error.otherDevice': 'This call was answered on another of your tabs or devices.',
  'calls.error.answeredElsewhere': 'You answered this call on another tab or device.',
  'calls.error.declined': 'You declined this call.',
  'calls.error.noLongerRinging': 'This call is no longer ringing.',
  'calls.error.micBlocked': 'Your browser blocks the microphone for this site. Click the microphone icon in the address bar, allow it, and try again.',
  'calls.error.micMissing': 'No microphone could be opened. Check that one is connected and not in use by another app, then try again.',
  'calls.retention.title': 'Call recording retention',
  'calls.retention.subtitle': 'How long each recording is kept after its call ended.',
  'calls.retention.forever': 'Keep forever',
  'calls.retention.days': '{n} days',
  'calls.retention.custom': 'Custom',
  'calls.retention.customDays': 'Days',
  'calls.retention.customHint': 'A whole number of days, 1 to {max}. Longer than a year is fine.',
  'calls.retention.invalid': 'Enter a whole number of days, 1 or more.',
  'calls.retention.explain': 'When a recording is older than this, its audio is deleted for good. The call itself is never deleted: it stays in everyone’s history with who was on it, when and for how long. A shorter period also deletes the recordings already older than it.',
  'calls.retention.confirmTitle': 'Delete older recordings?',
  'calls.retention.confirmBody': 'Keeping recordings for {days} days deletes the audio of {n} recording(s) older than that, now and for good.',
  'calls.retention.confirmKeep': 'The calls stay in the history; only their audio goes. This cannot be undone.',
  'calls.retention.confirmButton': 'Delete {n} and save',
  'calls.settings.callsTitle': 'Calls',
  'calls.settings.callsSubtitle': 'How long a call rings, how many people it holds, and how long it may last.',
  'calls.settings.ring': 'Ring for (seconds)',
  'calls.settings.ringHint': '15 to 120. Then the call is missed.',
  'calls.settings.size': 'People in a call, at most',
  'calls.settings.sizeHint': '2 to 16, the caller included.',
  'calls.settings.length': 'Longest call (minutes)',
  'calls.settings.lengthHint': '10 to 720. A call ends by itself then, with a warning 5 minutes before; keep it under the recorder’s own limit.',
  // Calls › AI summary (2026-10-29)
  'callAi.title': 'AI call summary',
  'callAi.disclaimer': 'Written by AI from the call’s recording. It may contain mistakes — check anything important against the recording.',
  'callAi.notJoined': 'Only the people who were connected to this call can read its AI summary.',
  'callAi.regenerate': 'New version',
  'callAi.retryAt': 'The last attempt did not finish. It is tried again around {date}.',
  'callAi.retrying': 'The last attempt did not finish. It is tried again shortly.',
  'callAi.failed': 'The AI summary could not be made for this call.',
  'callAi.noSpeech': 'No meaningful speech was detected in this call, so there is nothing to summarize.',
  'callAi.attempts': 'attempt {n} of {max}',
  'callAi.newVersionBeingMade': 'A new version is being made: {step}.',
  'callAi.keyPoints': 'Key discussion points',
  'callAi.noKeyPoints': 'No key points were recorded.',
  'callAi.decisions': 'Decisions made',
  'callAi.noDecisions': 'No decisions were recorded.',
  'callAi.actionItems': 'Action items',
  'callAi.actionItemsHint': 'For information only — none of these is created as a task.',
  'callAi.noActionItems': 'No action items were recorded.',
  'callAi.owner': 'Owner',
  'callAi.due': 'Due',
  'callAi.notSpecified': 'not specified',
  'callAi.openQuestions': 'Open questions',
  'callAi.versionUpdated': 'Version {n}, updated {date}',
  'callAi.writtenAt': 'Written {date}',
  'callAi.progress': 'Progress of the AI summary',
  'callAi.transcript': 'Full transcript',
  'callAi.transcriptDisclaimer': 'Transcribed by AI, in the languages spoken. It may contain mistakes, and it does not say who is speaking.',
  'callAi.transcriptExpired': 'Transcript expired: it was deleted with the recording at the end of its retention period. The summary stays.',
  'callAi.transcriptExpiredOn': 'Transcript expired: it was deleted with the recording on {date}, at the end of its retention period. The summary stays.',
  'callAi.transcriptSilent': '(nothing was said in this part)',
  'callAi.revisions': 'Earlier versions',
  'callAi.version': 'Version {n}',
  'callAi.current': 'Current',
  'callAi.automatic': 'Automatic',
  'callAi.reason': 'Reason',
  'callAi.regenerateTitle': 'Make a new version of the summary?',
  'callAi.regenerateBody': 'A new version is written from this call’s transcript. The current one is kept as an earlier version, with your reason.',
  'callAi.reasonHint': 'Why a new version is needed — 3 to 500 characters. Kept with it, for the administrators.',
  'callAi.retranscribe': 'Transcribe the recording again first (slower, and it costs more)',
  'callAi.retranscribeForced': 'The recording is transcribed again first: this call has no transcript kept',
  'callAi.regenerateQueued': 'A new version of the summary is being made.',
  'callAi.accessLog': 'Who read the AI summary',
  'callAi.accessLogHint': 'Every read of the summary or the transcript, and every request for a new version — let through or refused.',
  'callAi.accessWhat': 'What',
  'callAi.access.SUMMARY_VIEWED': 'Summary read',
  'callAi.access.SUMMARY_DENIED': 'Summary refused',
  'callAi.access.TRANSCRIPT_VIEWED': 'Transcript read',
  'callAi.access.TRANSCRIPT_DENIED': 'Transcript refused',
  'callAi.access.REGENERATE_REQUESTED': 'New version asked',
  'callAi.access.REGENERATE_DENIED': 'New version refused',
  'callAi.accessReason.NOT_A_PARTICIPANT': 'not on the call',
  'callAi.accessReason.NOT_JOINED': 'never connected',
  'callAi.accessReason.NOT_ADMIN': 'not an administrator',
  'callAi.accessReason.NOT_ELIGIBLE': 'made before AI summaries',
  'callAi.accessReason.EXPIRED': 'expired',
  'callAi.accessReason.NONE': 'no transcript',
  'callAi.accessReason.JOB_ACTIVE': 'already being made',
  'callAi.accessReason.LIMIT': 'daily limit',
  'callAi.accessReason.UNAVAILABLE': 'not available on the server',
  'callAi.accessReason.OTHER': 'not possible',
  'callAi.summaryReady': 'AI summary ready',
  'callAi.state.NOT_AVAILABLE': 'Not available',
  'callAi.state.WAITING_FOR_RECORDING': 'Waiting for the recording',
  'callAi.state.QUEUED': 'Queued',
  'callAi.state.TRANSCRIBING': 'Transcribing',
  'callAi.state.SUMMARIZING': 'Generating summary',
  'callAi.state.RETRY_PENDING': 'Retry pending',
  'callAi.state.FAILED': 'Failed',
  'callAi.state.READY': 'Ready',
  'callAi.state.NO_SPEECH': 'No meaningful speech',
  'callAi.na.NOT_ELIGIBLE': 'This call was made before AI summaries were switched on, so it is not summarized.',
  'callAi.na.NO_WHOLE_RECORDING': 'There is no complete recording of this call to summarize.',
  'callAi.na.RECORDING_EXPIRED': 'The recording was deleted at the end of its retention period before a summary was made.',
  'callAi.na.NOT_MADE': 'No summary was made for this call.',
  'callAi.error.AUDIO_INVALID': 'The recording could not be read as audio.',
  'callAi.error.AUDIO_MISSING': 'The recording’s file is missing from storage.',
  'callAi.error.AUDIO_TOOLS_MISSING': 'ffmpeg is not installed on the server (apt-get install -y ffmpeg).',
  'callAi.error.AUDIO_PROCESSING_FAILED': 'The recording could not be prepared on the server (ffmpeg failed, or the disk is full).',
  'callAi.error.STORAGE_UNAVAILABLE': 'The recordings’ storage did not answer.',
  'callAi.error.PROVIDER_UNAVAILABLE': 'OpenAI was busy or did not answer in time.',
  'callAi.error.PROVIDER_AUTH': 'OpenAI refused the server’s API key (OPENAI_API_KEY).',
  'callAi.error.PROVIDER_QUOTA': 'The OpenAI account has run out of quota or credit.',
  'callAi.error.PROVIDER_REJECTED': 'OpenAI refused the request — check the models chosen in Settings › Calls.',
  'callAi.error.SUMMARY_INVALID': 'The summary that came back was incomplete or not in the expected form.',
  'callAi.error.RECORDING_EXPIRED': 'The recording was deleted while the summary was being made.',
  'callAi.error.INTERRUPTED': 'The job was interrupted (a restart, or it took too long).',
  'callAi.error.INTERNAL': 'Something went wrong on the server.',
  'callAi.blocker.CALLS_AI_DISABLED': 'CALLS_AI_ENABLED is not true in the API’s .env.',
  'callAi.blocker.OPENAI_KEY_MISSING': 'OPENAI_API_KEY is not set in the API’s .env.',
  'callAi.blocker.STORAGE_NOT_SET_UP': 'The recordings’ storage (R2) is not set up.',
  'callAi.blocker.AUDIO_TOOLS_MISSING': 'ffmpeg is not installed on the server (apt-get install -y ffmpeg).',
  'callAi.blocker.MODELS_NOT_CONFIGURED': 'No transcription or summary model is configured.',
  'callAi.blocker.SETTING_OFF': 'AI summaries are switched off here.',
  'callAi.settings.title': 'AI call summary',
  'callAi.settings.subtitle': 'An automatic transcript and summary of every new call, once its recording is ready.',
  'callAi.settings.notReady': 'AI summaries cannot run on this server yet:',
  'callAi.settings.running': 'AI summaries are on: every new call is summarized.',
  'callAi.settings.ready': 'The server is ready for AI summaries.',
  'callAi.settings.enabled': 'Summarize new calls automatically',
  'callAi.settings.enabledHint': 'Only calls made from the moment this is switched on — never earlier ones. Switching it off stops new calls; summaries already made stay.',
  'callAi.settings.firstEnabledAt': 'First switched on {date}.',
  'callAi.settings.queue': 'Queue: {queued} waiting, {running} running, {retrying} to retry, {failed} failed in the last day',
  'callAi.settings.howTitle': 'Language and models',
  'callAi.settings.howSubtitle': 'The transcript keeps the languages spoken; the summary is written in the language chosen here.',
  'callAi.settings.language': 'Summary language',
  'callAi.settings.languageHint': 'English technical terms are kept as spoken.',
  'callAi.settings.arabic': 'Arabic',
  'callAi.settings.english': 'English',
  'callAi.settings.transcriptionModel': 'Transcription model',
  'callAi.settings.provider': 'Provider: OpenAI.',
  'callAi.settings.summaryModel': 'Summary model',
  'callAi.settings.modelsHint': 'The models the server offers (its .env).',
  'callAi.settings.retry': 'Retry failed jobs',
  'callAi.settings.retryHint': 'A few times at most, waiting longer each time.',
  'callAi.settings.concurrent': 'Jobs at once',
  'callAi.settings.concurrentHint': '{min} to {max}.',
  'callAi.settings.keywords': 'Words to expect',
  'callAi.settings.keywordsHint': 'Company and product terms, one per line (at most {max}) — hints for the transcription.',
  'callAi.settings.keywordsInvalid': 'At most {max} words, each one line of {length} characters at most, without < or >.',
  'callAi.settings.pricesTitle': 'Prices for the cost estimate',
  'callAi.settings.pricesSubtitle': 'Copy them from the provider’s pricing page, with the day you read them. Without prices, the usage report shows no estimate.',
  'callAi.settings.pricesAsOf': 'Prices read on',
  'callAi.settings.priceTranscription': 'Transcription, USD per minute',
  'callAi.settings.priceInput': 'Summary input, USD per 1M tokens',
  'callAi.settings.priceOutput': 'Summary output, USD per 1M tokens',
  'callAi.settings.priceInvalid': 'A price from 0 to 1,000, or empty.',
  'callAi.settings.confirmTitle': 'Switch on AI call summaries?',
  'callAi.settings.confirmBody': 'From now on, the recording of every new call is sent to OpenAI to be transcribed and summarized. Confirm that employees were told this in the recording and privacy notices, and that sending call audio to an outside provider was approved.',
  'callAi.settings.confirmNewOnly': 'Calls made before now are never summarized.',
  'callAi.settings.confirmButton': 'Confirm and switch on',
  'callAi.usage.title': 'AI usage',
  'callAi.usage.subtitle': 'What the AI jobs used. Counts only — nothing said in a call.',
  'callAi.usage.period': 'Period',
  'callAi.usage.lastDays': 'Last {n} days',
  'callAi.usage.summaries': 'Summaries written',
  'callAi.usage.noSpeech': '{n} with no meaningful speech',
  'callAi.usage.jobs': 'Jobs',
  'callAi.usage.jobsHint': '{failed} failed · {cancelled} cancelled · {retries} retries',
  'callAi.usage.minutes': 'Minutes transcribed',
  'callAi.usage.tokens': 'Summary tokens: {input} in, {output} out',
  'callAi.usage.cost': 'Estimated cost',
  'callAi.usage.costAtTheTime': 'Each job at the prices entered when it finished',
  'callAi.usage.costPartial': '{n} job(s) without a price are not counted',
  'callAi.usage.noPrices': 'Enter the prices above for an estimate',
  'callAi.usage.estimateNote': 'An estimate from the prices entered — the provider’s bill is what counts.',
  // Viganium Drive (2026-11-02): pages/Drive.tsx, pages/DriveForms.tsx.
  'nav.drive': 'Drive',
  'drive.title': 'Viganium Drive',
  'drive.tab': 'Drive',
  'drive.myDrive': 'My Drive',
  'drive.shared': 'Shared with me',
  'drive.trash': 'Trash',
  'drive.newFolder': 'New folder',
  'drive.upload': 'Upload files',
  'drive.usage': 'Files: {files} · {size}',
  'drive.searchPlaceholder': 'Search your drive and what is shared with you',
  'drive.searchResults': 'Results for “{q}”',
  'drive.clearSearch': 'Clear the search',
  'drive.where': 'Where it is',
  'drive.col.name': 'Name',
  'drive.col.owner': 'Owner',
  'drive.col.where': 'Where',
  'drive.col.about': 'About',
  'drive.col.modified': 'Modified',
  'drive.col.trashed': 'In the Trash since',
  'drive.col.size': 'Size',
  'drive.items': 'Items: {n}',
  'drive.you': 'You',
  'drive.menu': 'Actions',
  'drive.emptyMine': 'Your drive is empty',
  'drive.emptyMineHint': 'Make a folder or upload files. They stay yours until you share them.',
  'drive.emptyShared': 'Nothing is shared with you yet',
  'drive.emptySharedHint': 'When someone shares a folder or a file with you, it shows here.',
  'drive.emptyTrash': 'The Trash is empty',
  'drive.emptyTrashHint': 'What is put in the Trash stays here until you restore it or delete it for good.',
  'drive.emptySearch': 'Nothing matches',
  'drive.emptyFolder': 'This folder is empty',
  'drive.emptyFolderHint': 'Drop files here, or use Upload files.',
  'drive.emptyFolderViewOnly': 'Nothing is in it yet.',
  'drive.dropTop': 'your drive',
  'drive.dropHere': 'Drop to upload into {folder}',
  'drive.dropHint': 'Drop files anywhere on this page to upload them here — any kind of file, up to {size} each.',
  'drive.uploading': 'Uploading {n}',
  'drive.uploadsDone': 'Uploads',
  'drive.uploaded': 'Uploaded',
  'drive.uploadFailed': 'Not uploaded',
  'drive.waiting': 'Waiting',
  'drive.dismiss': 'Put away',
  'drive.fileTooLarge': 'Larger than {mb} MB, the most Drive takes.',
  'drive.storageUnreachable': 'The file storage could not be reached. Try again in a moment.',
  'drive.open': 'Open',
  'drive.download': 'Download',
  // A folder downloaded as one .zip (2026-11-03).
  'drive.downloadZip': 'Download as .zip',
  'drive.downloadFolderHint': 'Download this folder, with everything in it, as a .zip',
  'drive.downloadRefused.EMPTY': 'There are no files in this folder to download.',
  'drive.downloadRefused.TOO_BIG': 'A download holds at most 4 GB of files. Download the folders inside it one at a time.',
  'drive.downloadRefused.TOO_MANY': 'A download holds at most 65,535 folders and files. Download the folders inside it one at a time.',
  'drive.openInTab': 'Open in a new tab',
  'drive.share': 'Share',
  'drive.edit': 'Rename and details',
  'drive.move': 'Move',
  'drive.toTrash': 'Move to Trash',
  'drive.restore': 'Restore',
  'drive.purge': 'Delete for good',
  'drive.emptyTrashAction': 'Empty the Trash',
  'drive.confirmTrashTitle': 'Move “{name}” to the Trash?',
  'drive.confirmTrashFolder': 'Everything in it goes with it. Whoever it is shared with no longer sees it, and its links stop working until it is restored.',
  'drive.confirmTrashFile': 'Whoever it is shared with no longer sees it, and its links stop working until it is restored.',
  'drive.confirmTrashShared': 'It goes to the Trash of its owner, {owner}, who alone can restore it.',
  'drive.confirmRestoreTitle': 'Restore “{name}”?',
  'drive.confirmRestoreBody': 'It goes back where it was, with everything in it, and its shares and links work again.',
  'drive.confirmPurgeTitle': 'Delete “{name}” for good?',
  'drive.confirmPurgeBody': 'It and everything in it are deleted, files included. This cannot be undone.',
  'drive.confirmEmptyTitle': 'Empty the Trash?',
  'drive.confirmEmptyBody': 'Everything in your Trash is deleted for good, files included. This cannot be undone.',
  'drive.trashed': 'Moved to the Trash.',
  'drive.restored': 'Restored.',
  'drive.restoredToTop': 'Restored to the top of your drive — the folder it was in is in the Trash.',
  'drive.purged': 'Deleted for good.',
  'drive.emptied': 'The Trash is empty.',
  'drive.inTrashNotice': 'This is in your Trash. Restore it to use it again.',
  'drive.inTrashFolderNotice': 'The folder this is in is in your Trash. Restore that folder to use it again.',
  'drive.access': 'Your access',
  'drive.access.OWNER': 'Yours',
  'drive.access.EDIT': 'Can edit',
  'drive.access.VIEW': 'Can view',
  'drive.myItem': 'In your drive',
  'drive.ownedBy': 'In {name}’s drive',
  'drive.changed': 'Changed {when}',
  'drive.sharedPeople': 'Shared with {n}',
  'drive.sharedEveryone': 'Everyone',
  'drive.publicLinkOn': 'Public link',
  'drive.contactsOn': 'Emailed to {n}',
  'drive.details': 'Details',
  'drive.size': 'Size',
  'drive.owner': 'Owner',
  'drive.addedBy': 'Added by',
  'drive.created': 'Added',
  'drive.modified': 'Modified',
  'drive.about': 'About',
  'drive.aboutNothing': 'No client or project',
  'drive.pdfHint': 'Open the PDF in a new tab, or download it.',
  'drive.downloadOnly': 'This kind of file is not shown here — download it to open it.',
  'drive.sharing': 'Sharing',
  'drive.notShared': 'Only you.',
  'drive.manageSharing': 'Manage sharing',
  'drive.through': 'Also shared through {folder}',
  'drive.relatedEmptyClient': 'No Drive folder or file is about this client yet',
  'drive.relatedEmptyProject': 'No Drive folder or file is about this project yet',
  'drive.relatedHint': 'Only what you may open shows here. A folder or a file says what it is about on its Rename and details page.',
  'drive.newFolderFor': 'New folder about it',
  'drive.folderIn': 'In {folder}',
  'drive.name': 'Name',
  'drive.client': 'Client',
  'drive.project': 'Project',
  'drive.noClient': 'No client',
  'drive.noProject': 'No project',
  'drive.aboutHint': 'Optional. A project brings its client. What is put in a folder is about what the folder is about.',
  'drive.createFolder': 'Create folder',
  'drive.folderCreated': 'Folder created.',
  'drive.renameFileHint': 'It downloads under the new name. Without an extension, it keeps the one it had.',
  'drive.save': 'Save',
  'drive.saved': 'Saved.',
  'drive.viewOnly': 'It is shared with you to view, not to change.',
  'drive.moveTitle': 'Move “{name}”',
  'drive.moveHere': 'Move here',
  'drive.moveTop': 'The top of the drive',
  'drive.moveCurrent': 'It is here now',
  'drive.moved': 'Moved.',
  'drive.noFolders': 'There is no other folder to move it into.',
  'drive.moveRefused.INTO_ITSELF': 'A folder cannot go inside itself, or inside something in it.',
  'drive.moveRefused.NOT_A_FOLDER': 'Things go in folders, not in files.',
  'drive.moveRefused.OTHER_DRIVE': 'It can only move within its owner’s drive.',
  'drive.moveRefused.TOO_DEEP': 'Folders go at most 30 deep.',
  'drive.shareTitle': 'Share “{name}”',
  'drive.shareSubtitle': 'Who else sees what is yours. Whoever you let edit it can share it too.',
  'drive.editorsShare': 'It is shared with you to view. Its owner shares it, and so does whoever it is shared with to edit.',
  'drive.people': 'People',
  'drive.peopleHint': 'Any employee, whatever their department. A folder shared is everything in it shared.',
  'drive.addPerson': 'Add people by name',
  'drive.roleView': 'Can view',
  'drive.roleEdit': 'Can edit',
  'drive.roleFor': 'What {name} may do',
  'drive.remove': 'Remove',
  'drive.removePerson': 'Take {name} off',
  'drive.inactive': 'No longer active',
  'drive.everyone': 'Everyone in the company',
  'drive.everyoneOff': 'Not shared with everyone',
  'drive.everyoneView': 'Everyone can view',
  'drive.everyoneEdit': 'Everyone can edit',
  'drive.roleHint': 'Can view: open and download. Can edit: also add, rename, move, move to the Trash, and share.',
  'drive.saveSharing': 'Save sharing',
  'drive.sharingSaved': 'Sharing saved.',
  'drive.publicLink': 'Public link',
  'drive.publicLinkHint': 'Anyone who has the link can open it and download, without signing in.',
  'drive.makePublicLink': 'Create public link',
  'drive.publicLinkExpired': 'Its last link expired. A new one is a new address.',
  'drive.copyLink': 'Copy link',
  'drive.linkCopied': 'Link copied.',
  'drive.stopLink': 'Stop link',
  'drive.confirmStopTitle': 'Stop this link?',
  'drive.confirmStopBody': 'Whoever has it can no longer open it. A new link would be a new address.',
  'drive.linkStopped': 'The link was stopped.',
  'drive.linkCreated': 'Public link created.',
  'drive.linkUpdated': 'Saved.',
  'drive.availableUntil': 'Available until',
  'drive.noEnd': 'No end date.',
  'drive.untilHint': 'Optional. It stops working after this day.',
  'drive.setEnd': 'Set',
  'drive.endsOn': 'Until {date}',
  'drive.linkStats': 'Opens: {opens} · Downloads: {downloads}',
  'drive.lastOpened': 'last {when}',
  'drive.outsideNeedsPermission': 'Public links and emails to clients’ contacts are for administrators, and for whoever is given Share Drive outside the company on their employee form.',
  'drive.contacts': 'Client contacts',
  'drive.contactsHint': 'Email it to a client’s contacts. Each gets a link of their own, which you can stop at any time.',
  'drive.sendToContacts': 'Email to client contacts',
  'drive.noContactLinks': 'Not sent to anyone yet.',
  'drive.linkLive': 'Opens',
  'drive.linkExpired': 'Expired',
  'drive.contactRemoved': 'Contact removed',
  'drive.sentOn': 'First sent {when}',
  'drive.sendTitle': 'Email “{name}” to client contacts',
  'drive.pickClient': 'Pick a client',
  'drive.sendContacts': 'Contacts',
  'drive.sendContactsHint': 'Up to {n} at once. A contact sent it before keeps the same link.',
  'drive.noContacts': 'This client has no contacts. Add them on the client’s page.',
  'drive.noEmail': 'No email address',
  'drive.message': 'Message',
  'drive.messageHint': 'Optional. It goes in the email, above the link.',
  'drive.emailLanguage': 'Email language',
  'drive.emailLanguageHint': 'A client’s contacts are kept in English, so English unless you choose otherwise.',
  'drive.send': 'Send',
  'drive.sentQueued': 'Sent to {n} — each with a link of their own.',
  'drive.sentOff': 'Email is switched off in Settings › Email. The links were made: copy each from this page and send it yourself.',
  'drive.sentNotConfigured': 'Email is not set up on this server. The links were made: copy each from this page and send it yourself.',
  'settings.maxDriveFileMb': 'Largest Drive file (MB)',
  'settings.maxDriveFileMbHint': 'Any kind of file put in Viganium Drive.',
  'employees.del.driveItems': 'Drive folders and files',
  'employees.addOnDriveHint': 'Make a Drive item’s public link, and email it to a client’s contacts. Without it, only administrators can.',
  'drive.everyoneTab': 'Everyone’s drives',
  'drive.everyoneHint': 'Every employee’s drive. As an administrator you open what is in it and share it; changing it, and its Trash, stay its owner’s.',
  'drive.everyoneEmpty': 'Nobody has anything in Drive yet',
  'drive.col.employee': 'Employee',
  'drive.col.files': 'Files',
  'drive.col.lastChange': 'Last change',
  'drive.driveOf': '{name}’s drive',
  'drive.access.ADMIN': 'As an administrator',
  'drive.shareSubtitleAdmin': 'In {name}’s drive. As an administrator you see it and share it; its owner sees whom it is shared with.',
  'drive.shareSubtitleEditor': 'In {name}’s drive. It is shared with you to edit, so you share it too; its owner sees whom it is shared with.',
  'drive.ownPlace': 'Your own access is its owner’s to change.',
  'drive.ownerDriveHint': 'You see this drive as an administrator: open and share what is in it. Only its owner, and those it is shared with to edit, change it.',
  'drive.ownerDriveEmpty': 'Nothing is in this drive',
  // Drive shared with departments (2026-11-03).
  'drive.departments': 'Departments',
  'drive.departmentsHint': 'Whoever is in one now opens it — someone who joins it later too, someone who leaves it no longer.',
  'drive.addDepartment': 'Add a department',
  'drive.departmentPeople': '{n} in it now',
  'drive.yourDepartment': 'Your department',
  'drive.sharedDepartments': 'Departments: {n}',
  // Sites (2026-11-03): websites in folders of their own, at the sites address.
  'nav.sites': 'Sites',
  'sites.title': 'Sites',
  'sites.subtitle': 'Websites, each published in a folder of its own, at',
  'sites.whoPublishes': 'Who may publish',
  'sites.new': 'New site',
  'sites.empty': 'No sites yet',
  'sites.emptyHint': 'Make a site, then put its pages, styles, scripts and pictures in it.',
  'sites.site': 'Site',
  'sites.owner': 'Looked after by',
  'sites.files': 'Files',
  'sites.status': 'Status',
  'sites.updated': 'Updated',
  'sites.fileCount': 'Files: {n}',
  'sites.live': 'Live',
  'sites.offline': 'Offline',
  'sites.noFrontPage': 'No index.html',
  'sites.copied': 'Address copied.',
  'sites.where': 'Where you are in the site',
  'sites.copyAddress': 'Copy the address',
  'sites.lookedAfterBy': 'Looked after by {name}',
  'sites.changed': 'changed {when}',
  'sites.open': 'Open site',
  'sites.menu': 'More',
  'sites.settings': 'Settings',
  'sites.delete': 'Delete site',
  'sites.offlineNotice': 'This site is switched off: its address answers “Page not found” until it is switched on again in its settings.',
  'sites.noIndexNotice': 'Add an index.html at the top of the site: it is the page the address opens. Until then the address answers “Page not found”.',
  'sites.summary': 'Files: {n} · {size} — a site holds at most {max} files.',
  'sites.newMenu': 'New',
  'sites.newFile': 'New file',
  'sites.newFolder': 'New folder',
  'sites.upload': 'Upload',
  'sites.uploadFiles': 'Files…',
  'sites.uploadFolder': 'A whole folder…',
  'sites.uploadZip': 'A .zip file…',
  'sites.emptySite': 'This site is empty',
  'sites.emptyFolder': 'This folder is empty',
  'sites.emptyHintDrop': 'Drop files, a folder or a .zip here, or use Upload. A folder is there once a file is in it.',
  'sites.name': 'Name',
  'sites.kind': 'Type',
  'sites.size': 'Size',
  'sites.dropHint':
    'Drop files, a folder or a .zip anywhere on this page — they go into the folder you are in, and a file already there is replaced. Link between pages with relative paths (css/site.css, not /css/site.css).',
  'sites.openFolder': 'Open',
  'sites.deleteFolder': 'Delete folder',
  'sites.by': 'By {name}',
  'sites.openFile': 'Open in the site',
  'sites.editFile': 'Edit',
  'sites.moveFile': 'Rename or move',
  'sites.deleteFile': 'Delete',
  'sites.download': 'Download',
  'sites.downloadSiteHint': 'Download the whole site as a .zip',
  'sites.downloadFolderHint': 'Download this folder, with everything in it, as a .zip',
  'sites.downloadZip': 'Download as .zip',
  'sites.downloadFile': 'Download',
  'sites.downloadTooBig': 'A download holds at most 4 GB of files. Download the site’s folders one at a time.',
  'sites.deleted': 'Deleted.',
  'sites.confirmDeleteFile': 'Delete {name}?',
  'sites.confirmDeleteFolder': 'Delete the folder {name}?',
  'sites.confirmDeleteFileBody': 'The file is deleted for good, and its address answers “Page not found”.',
  'sites.confirmDeleteFolderBody': 'Everything in it is deleted for good — files: {n}.',
  'sites.skipHidden': 'Hidden files are not part of a site.',
  'sites.skipType': 'Not a kind of file a site takes: pages, styles, scripts, pictures, fonts, data, sound and video only.',
  'sites.skipName': 'A name cannot have \\ : * ? " < > | # or %, nor start or end with a space.',
  'sites.zipNotZip': 'That is not a .zip file.',
  'sites.zipTooBig': 'That .zip is too large to open here (over 4 GB, or 65,535 files).',
  'sites.zipEncrypted': 'That .zip has a password. Zip the site again without one.',
  'sites.zipMethod': 'That .zip is packed in a way the browser cannot open. Zip it again, or upload the folder.',
  'sites.zipBroken': 'That .zip is damaged.',
  'sites.zipUnsupported': 'This browser cannot open .zip files. Upload the folder instead.',
  'sites.tooLarge': 'Larger than {mb} MB, the most a file may be.',
  'sites.uploadFailed': 'Not uploaded',
  'sites.readFailed': 'What was dropped could not be read. Try again.',
  'sites.reading': 'Reading the files…',
  'sites.dropHere': 'Drop to upload into {folder}',
  'sites.uploading': 'Uploading {n}',
  'sites.uploadsDone': 'Uploaded: {n}',
  'sites.skippedCount': 'Skipped: {n}',
  'sites.failedCount': 'Not uploaded: {n}',
  'sites.uploaded': 'Uploaded',
  'sites.replaced': 'Replaced',
  'sites.skipped': 'Skipped',
  'sites.folderEmpty': 'Give the site a folder.',
  'sites.folderTooShort': 'At least 2 characters.',
  'sites.folderTooLong': 'At most 63 characters.',
  'sites.folderShape': 'Lower case English letters, digits and hyphens only — no hyphen first or last.',
  'sites.folderReserved': 'That name is kept for the sites address itself. Choose another.',
  'sites.folderTaken': 'Another site has that folder. Choose another.',
  'sites.newTitle': 'New site',
  'sites.newSubtitle': 'A website of its own, in a folder: its name is the end of the site’s address.',
  'sites.settingsTitle': '{name} — settings',
  'sites.create': 'Create site',
  'sites.created': 'Site created.',
  'sites.saved': 'Saved.',
  'sites.folder': 'Folder',
  'sites.folderHint': 'The end of the address: lower case English letters, digits and hyphens.',
  'sites.addressWillBe': 'Its address:',
  'sites.folderMoves': 'Changing the folder moves the site: the old address stops working at once.',
  'sites.description': 'Description',
  'sites.descriptionHint': 'What the site is for, for whoever looks after it. Visitors never see it.',
  'sites.isLive': 'The site is live',
  'sites.isLiveHint': 'Switched off, its address answers “Page not found”, and its files stay here.',
  'sites.searchable': 'Let search engines list it',
  'sites.searchableHint': 'Off, every page tells search engines not to list it — for a draft or a site for one client.',
  'sites.siteDeleted': 'Site deleted.',
  'sites.deleteTitle': 'Delete {name}',
  'sites.deleteSubmit': 'Delete for good',
  'sites.deleteWarning': 'The site and every file in it are deleted for good, and its address stops working at once. This cannot be undone.',
  'sites.deleteConfirm': 'Type its folder, {folder}, to delete it',
  'sites.inFolder': 'In {folder}',
  'sites.createFolder': 'Open the folder',
  'sites.folderName': 'Folder name',
  'sites.newFolderHint': 'The folder opens empty: drop files in it, or make one there. It is kept once a file is in it.',
  'sites.pathTaken': 'Something is at that path already.',
  'sites.moveTitle': 'Rename or move {name}',
  'sites.path': 'Path in the site',
  'sites.pathHint': 'Folders and name, with / between them: images/logo.png. Links to it from the pages do not change by themselves.',
  'sites.typeChanges': 'A new extension changes what the file is served as.',
  'sites.fileGone': 'This file is no longer in the site.',
  'sites.fileSaved': 'File saved.',
  'sites.fileChanged': 'Someone saved this file since you opened it. Open it again to see their change before saving yours.',
  'sites.unsaved': 'Not saved',
  'sites.newFileHint': 'A page, a style sheet, a script or another text file, at the top of the site.',
  'sites.fileName': 'File name',
  'sites.fileNameHint': 'With its extension: index.html, style.css, script.js.',
  'sites.notText': 'The editor writes text files: .html, .css, .js, .json, .svg, .txt and the like.',
  'sites.content': 'Content',
  'sites.editorHint': '{size} of at most 1 MB. Ctrl+S saves.',
  // Site analytics (2026-11-04): each site's visitors, everything its address answered, and a check of its pages.
  'analytics.button': 'Analytics',
  'analytics.buttonHint': 'Visitors, pages, sources, robots and a check of this site',
  'analytics.title': 'Analytics',
  'analytics.pageTitle': '{name} — analytics',
  'analytics.siteFiles': 'Files',
  'analytics.offline':
    'This site is switched off: nobody can visit it, so its figures stand still until it is switched on again in its settings.',
  'analytics.tab.traffic': 'Traffic',
  'analytics.tab.requests': 'Requests',
  'analytics.tab.site': 'Site check',
  'analytics.privacy':
    'Visitors are counted without cookies and without keeping their address: each is known for one day by a code made from their address and browser, which cannot be traced back — someone who comes back tomorrow is counted again. Robots — search engines, link previews, scripts — are counted apart, on Requests.',
  'analytics.liveHint': 'Visitors who opened a page in the last {n} minutes.',
  'analytics.liveNow': '{n} on the site now',
  'analytics.lastVisit': 'Last visit {when}',
  'analytics.noVisitsYet': 'No visits yet',
  'analytics.liveTitle': 'On the site now',
  'analytics.liveEmptyLast': 'Nobody right now. The last visit was {when}, on {page}.',
  'analytics.rangeLabel': 'Period',
  'analytics.range.24h': '24 hours',
  'analytics.range.7d': '7 days',
  'analytics.range.30d': '30 days',
  'analytics.range.90d': '90 days',
  'analytics.range.12m': '12 months',
  'analytics.vsPrevious.24h': 'vs the 24 hours before',
  'analytics.vsPrevious.7d': 'vs the 7 days before',
  'analytics.vsPrevious.30d': 'vs the 30 days before',
  'analytics.vsPrevious.90d': 'vs the 90 days before',
  'analytics.vsPrevious.12m': 'vs the 12 months before',
  'analytics.nothingBefore': 'Nothing before to compare with',
  'analytics.noChange': 'No change',
  'analytics.pointsChange': '{n} pts',
  'analytics.countingSince': 'Counting since {date}: visits before then were not recorded.',
  'analytics.notCountingYet': 'No visits counted yet. Every page opened from now on is counted — open the site to see the first one.',
  'analytics.visitors': 'Visitors',
  'analytics.visits': 'Visits',
  'analytics.pageViews': 'Page views',
  'analytics.viewsPerVisit': 'Pages per visit',
  'analytics.bounceRate': 'Bounce rate',
  'analytics.bounceHint': 'Visits that left after one page',
  'analytics.visitDuration': 'Visit duration',
  'analytics.visitDurationHint': 'On average, from a visit’s first page to its last',
  'analytics.chartTitle': 'Visitors and page views',
  'analytics.chartSubtitle': 'People only, on {zone} time.',
  'analytics.chartEmpty': 'No visits in this period.',
  'analytics.none': 'Nothing in this period.',
  'analytics.showAll': 'Show all ({n})',
  'analytics.showFewer': 'Show fewer',
  'analytics.openPage': 'Open {page} on the site',
  'analytics.pages': 'Pages',
  'analytics.pages.top': 'Top',
  'analytics.pages.entry': 'Entry',
  'analytics.pages.exit': 'Exit',
  'analytics.timeHint':
    'Time: on average, before the next page of the same visit. Entry: where visits began, and how many left from there at once. Exit: where they ended.',
  'analytics.sources': 'Sources',
  'analytics.sources.referrers': 'Sources',
  'analytics.sources.campaigns': 'Campaigns',
  'analytics.sourcesHint': 'Where each visit came from. Campaigns are links tagged with utm_source, utm_medium and utm_campaign.',
  'analytics.direct': 'Direct or unknown',
  'analytics.directHint': 'Typed in, a bookmark, a chat app that names no page, or a link from a page that hides where it is.',
  'analytics.noCampaigns': 'No tagged links in this period. Add ?utm_source=… to the links you share to see them here.',
  'analytics.countries': 'Countries',
  'analytics.geoCredit': 'Found from the visitor’s address, which is not kept — IP2Location LITE data, lite.ip2location.com.',
  'analytics.unknown': 'Unknown',
  'analytics.devices': 'Devices',
  'analytics.devices.devices': 'Devices',
  'analytics.devices.browsers': 'Browsers',
  'analytics.devices.systems': 'Systems',
  'analytics.device.desktop': 'Computer',
  'analytics.device.mobile': 'Phone',
  'analytics.device.tablet': 'Tablet',
  'analytics.inApp': 'In-app browser',
  'analytics.languages': 'Languages',
  'analytics.languagesHint': 'The first language each browser asks for.',
  'analytics.hours': 'When visitors come',
  'analytics.hoursHint': 'Page views by day and hour, on {zone} time.',
  'analytics.hoursShortRange': 'Pick 7 days or more to see the days of the week.',
  'analytics.fewer': 'Fewer',
  'analytics.more': 'More',
  'analytics.cell': '{day} {hour}:00 — page views: {n}',
  'analytics.col.page': 'Page',
  'analytics.col.visitors': 'Visitors',
  'analytics.col.views': 'Views',
  'analytics.col.time': 'Time',
  'analytics.col.visits': 'Visits',
  'analytics.col.bounce': 'Bounce',
  'analytics.col.source': 'Source',
  'analytics.col.share': 'Share',
  'analytics.col.campaign': 'Source / medium / campaign',
  'analytics.col.path': 'Address',
  'analytics.col.kind': 'Kind',
  'analytics.col.times': 'Times',
  'analytics.col.lastSeen': 'Last',
  'analytics.col.robot': 'Robot',
  'analytics.col.file': 'File',
  'analytics.col.requests': 'Requests',
  'analytics.col.data': 'Data',
  'analytics.col.cached': 'Cached',
  'analytics.col.address': 'Address',
  'analytics.col.pages': 'Pages',
  'analytics.col.links': 'Links',
  'analytics.ms': '{n} ms',
  'analytics.req.requests': 'Requests',
  'analytics.req.requestsHint': 'Pages and the files they load, robots too',
  'analytics.req.data': 'Data sent',
  'analytics.req.cached': 'From the browser’s copy',
  'analytics.req.cachedHint': 'Answered “not changed”: nothing sent again',
  'analytics.req.speed': 'Page answer time',
  'analytics.req.speedHint': 'Half within this; 95% within {ms}',
  'analytics.req.notFound': 'Not found',
  'analytics.req.notFoundHint': 'Asked for by people, not there',
  'analytics.req.robots': 'Robot page views',
  'analytics.req.robotsHint': 'Files they fetched besides: {n}',
  'analytics.req.daily': 'Data sent per day',
  'analytics.req.fileDays': 'Pages and the files they load, robots too, {from} to {to}.',
  'analytics.req.dailyRequests': 'Requests: {n}',
  'analytics.req.nothingSent': 'Nothing was sent in this period.',
  'analytics.req.notFoundTitle': 'Asked for and not there',
  'analytics.req.notFoundNote':
    'Pages and files people asked for that the site does not have: a broken link, an old address, a missing picture. Robots’ guesses are left out.',
  'analytics.req.notFoundEmpty': 'Nothing missing was asked for in this period.',
  'analytics.req.kindPage': 'Page',
  'analytics.req.kindFile': 'File',
  'analytics.req.robotsTitle': 'Robots and link previews',
  'analytics.req.robotsNote': 'Search engines, AI crawlers, monitors and scripts. A link preview means someone shared the site’s link in that app.',
  'analytics.req.robotsEmpty': 'No robots came in this period.',
  'analytics.req.botPages': 'Different pages: {n}',
  'analytics.req.filesTitle': 'Files served',
  'analytics.req.filesNote': 'Styles, scripts, pictures and the rest, the most asked for first. Cached: answered from the browser’s own copy.',
  'analytics.req.filesEmpty': 'No files were served in this period.',
  'analytics.bot.search': 'Search engine',
  'analytics.bot.preview': 'Link preview',
  'analytics.bot.ai': 'AI',
  'analytics.bot.seo': 'SEO tool',
  'analytics.bot.monitor': 'Monitor',
  'analytics.bot.script': 'Script',
  'analytics.bot.other': 'Other',
  'analytics.botName.noBrowser': 'No browser named',
  'analytics.botName.other': 'Other crawler',
  'analytics.botName.headless': 'Headless browser',
  'analytics.botName.script': 'Script',
  'analytics.botName.previewService': 'Link preview service',
  'analytics.check.reading': 'Reading the site’s pages…',
  'analytics.check.errors': 'Problems',
  'analytics.check.warnings': 'Warnings',
  'analytics.check.suggestions': 'Suggestions',
  'analytics.check.passed': 'Passed',
  'analytics.check.title': 'Site check',
  'analytics.check.scanned': 'Pages read: {pages} · style sheets: {styles} · {when}',
  'analytics.check.again': 'Check again',
  'analytics.check.skipped': 'Not read: {n} — larger than 1 MB, or past the most one check reads.',
  'analytics.check.more': 'and {n} more',
  'analytics.check.front-page.title': 'Front page',
  'analytics.check.front-page.pass': 'index.html is at the top of the site: its address opens it.',
  'analytics.check.front-page.fail': 'There is no index.html at the top: the site’s address answers “Page not found”.',
  'analytics.check.broken-links.title': 'Broken links',
  'analytics.check.broken-links.pass': 'Every page, picture, style and script the pages name is in the site.',
  'analytics.check.broken-links.fail': 'Links and files the pages name that are not in the site: {n}.',
  'analytics.check.outside-links.title': 'Paths that leave the site',
  'analytics.check.outside-links.pass': 'No path starts from the top of the sites address.',
  'analytics.check.outside-links.fail':
    'Paths that start with / and so leave the site’s folder: {n}. Write them relative — css/site.css, not /css/site.css.',
  'analytics.check.insecure.title': 'Loaded over plain http',
  'analytics.check.insecure.pass': 'Everything the pages load comes over https.',
  'analytics.check.insecure.fail': 'Pictures, scripts or styles loaded over plain http: {n}. Browsers block or flag them on an https page.',
  'analytics.check.titles.title': 'Page titles',
  'analytics.check.titles.pass': 'Every page has a title.',
  'analytics.check.titles.fail': 'Pages with no <title>: {n}. The browser tab and search results show the address instead.',
  'analytics.check.duplicate-titles.title': 'Repeated titles',
  'analytics.check.duplicate-titles.pass': 'Each page’s title is its own.',
  'analytics.check.duplicate-titles.fail': 'Titles shared by several pages: {n}. Search results cannot tell those pages apart.',
  'analytics.check.descriptions.title': 'Descriptions',
  'analytics.check.descriptions.pass': 'Every page has a meta description.',
  'analytics.check.descriptions.fail': 'Pages with no meta description: {n}. Search engines and link previews make one up.',
  'analytics.check.viewport.title': 'Fits phones',
  'analytics.check.viewport.pass': 'Every page sets its width for phones (meta viewport).',
  'analytics.check.viewport.fail': 'Pages with no meta viewport: {n}. Phones show them shrunk to fit.',
  'analytics.check.lang.title': 'Language',
  'analytics.check.lang.pass': 'Every page says its language.',
  'analytics.check.lang.fail': 'Pages that do not say their language (<html lang>): {n}. Screen readers and translators guess.',
  'analytics.check.alt.title': 'Pictures without a description',
  'analytics.check.alt.pass': 'Every picture has alt text.',
  'analytics.check.alt.fail': 'Pictures with no alt text: {n} — for screen readers, and for when the picture does not load.',
  'analytics.check.icon.title': 'Tab icon',
  'analytics.check.icon.pass': 'Every page names its icon.',
  'analytics.check.icon.fail': 'Pages that name no icon (<link rel="icon">): {n}. Their browser tab shows none.',
  'analytics.check.large-files.title': 'Large files',
  'analytics.check.large-files.pass': 'No picture, script, style or font is over 1 MB.',
  'analytics.check.large-files.fail': 'Files over 1 MB: {n}. They are slow to load on a phone.',
  'analytics.check.not-found-page.title': 'Its own “not found” page',
  'analytics.check.not-found-page.pass': 'The site has its own 404.html for addresses it does not have.',
  'analytics.check.not-found-page.fail': 'There is no 404.html: a wrong address shows a plain “Page not found”.',
  'analytics.check.unused.title': 'Files nothing uses',
  'analytics.check.unused.pass': 'Every file is named by a page, a style sheet or a script.',
  'analytics.check.unused.fail': 'Files no page, style sheet or script names: {n}. They may be left over.',
  'analytics.content.title': 'What the site is made of',
  'analytics.content.summary': 'Files: {files} · {size} · folders: {folders}',
  'analytics.content.created': 'Created {when} by {name}.',
  'analytics.content.largest': 'Largest files',
  'analytics.content.pagesTitle': 'Pages ({n})',
  'analytics.content.noPages': 'No pages yet',
  'analytics.content.pageTitle': 'Title',
  'analytics.content.noTitle': 'No title',
  'analytics.content.thirdParty': 'Loads from other addresses',
  'analytics.content.thirdPartyHint': 'Fonts, scripts, analytics, maps and video the pages bring in from elsewhere.',
  'analytics.content.thirdPartyNone': 'The pages load nothing from other addresses.',
  'analytics.content.http': 'plain http',
  'analytics.content.outbound': 'Links to other sites',
  'analytics.content.outboundNone': 'The pages link to no other site.',
  'analytics.kind.pages': 'Pages',
  'analytics.kind.styles': 'Style sheets',
  'analytics.kind.scripts': 'Scripts',
  'analytics.kind.images': 'Pictures',
  'analytics.kind.fonts': 'Fonts',
  'analytics.kind.media': 'Sound and video',
  'analytics.kind.documents': 'Documents',
  'analytics.kind.data': 'Data and other',
  'analytics.ref.link': 'links',
  'analytics.ref.style': 'styles',
  'analytics.ref.script': 'scripts',
  'analytics.ref.image': 'pictures',
  'analytics.ref.media': 'sound and video',
  'analytics.ref.frame': 'frames',
  'analytics.ref.font': 'fonts',
  'analytics.ref.icon': 'icons',
  'analytics.ref.data': 'data',
  'analytics.ref.other': 'other',
  'analytics.changes.title': 'Changes',
  'analytics.changes.subtitle': 'The latest changes to the site and its files, from the audit trail.',
  'analytics.changes.none': 'No changes recorded.',
  'analytics.changes.system': 'the system',
  'analytics.change.created': 'Site created',
  'analytics.change.updated': 'Settings changed',
  'analytics.change.added': 'File added',
  'analytics.change.replaced': 'File replaced',
  'analytics.change.moved': 'File moved',
  'analytics.change.deleted': 'Files deleted: {n}',
  'analytics.field.isLive': 'Live',
  'analytics.field.searchable': 'Search engines',
  'settings.sites': 'Sites',
  'settings.sitesPublishers': 'Who may publish sites',
  'settings.sitesPublishersNote':
    'Administrators publish sites and look after every one. Pick who else may — whole departments, or employees by name. Each looks after the sites they make; taken off the list, their sites stay up for an administrator to look after.',
  'settings.sitesDepartments': 'Departments',
  'settings.sitesDepartmentsHint': 'Everyone in a department picked may publish — whoever joins it later too.',
  'settings.sitesNoDepartments': 'No departments yet.',
  'settings.sitesPeople': 'Employees',
  'settings.sitesPeopleMax': 'At most {n} people.',
  'settings.sitesPeopleHint': 'Picked by name, whatever their department.',
  'settings.sitesAddress': 'Sites are published at',
  'employees.del.sites': 'Sites they look after',
  'settings.executive': 'Executive Dashboard',
  'exec.title': 'Executive Dashboard — dashboard.viganium.net',
  'exec.intro': 'A read-only portal of the company’s finance, work, people, projects and AI briefings. It uses the same accounts and passwords as the admin.',
  'exec.rule': 'Only an active Administrator who has been given Dashboard Access here can sign in to it — both, always. Finance access or a partner record is never enough, and nobody is given access automatically.',
  'exec.open': 'Open the Executive Dashboard',
  'exec.accessTitle': 'Dashboard Access',
  'exec.accessSubtitle': 'Every active Administrator, and anyone who held access before. Granting and revoking take effect at once.',
  'exec.searchPeople': 'Search by name or email',
  'exec.noPeople': 'Nobody to show',
  'exec.noPeopleHint': 'Only active Administrators can be given Dashboard Access.',
  'exec.col.role': 'Admin role',
  'exec.col.email': 'Email',
  'exec.col.access': 'Dashboard Access',
  'exec.col.grantedBy': 'Granted by',
  'exec.col.lastSignIn': 'Last dashboard sign-in',
  'exec.col.sessions': 'Open sessions',
  'exec.state.GRANTED': 'Granted',
  'exec.state.NOT_GRANTED': 'Not granted',
  'exec.state.ENDED': 'Ended',
  'exec.stale': 'No longer an active Administrator — refused, and ended on their next request.',
  'exec.inactive': 'Inactive',
  'exec.event.GRANTED': 'Granted',
  'exec.event.REVOKED': 'Revoked',
  'exec.event.ROLE_REMOVED': 'Ended: no longer an Administrator',
  'exec.event.DEACTIVATED': 'Ended: account switched off',
  'exec.event.INELIGIBLE': 'Ended: found no longer eligible',
  'exec.grant': 'Grant access',
  'exec.revoke': 'Revoke',
  'exec.back': 'Back to Settings',
  'exec.grantTitle': 'Grant Dashboard Access',
  'exec.grantSubtitle': 'They sign in at the Executive Dashboard with their admin email and password.',
  'exec.grantReason': 'Reason (optional)',
  'exec.grantReasonHint': 'Kept with the grant and in the audit trail.',
  'exec.granted': 'Dashboard Access granted to {name}.',
  'exec.alreadyGranted': 'They already have Dashboard Access.',
  'exec.notEligible': 'Only an active Administrator can be given Dashboard Access.',
  'exec.revokeTitle': 'Revoke Dashboard Access',
  'exec.revokeSubtitle': 'Their dashboard sessions end at once. Their admin and Finance access are not touched.',
  'exec.revokeReason': 'Reason',
  'exec.revokeReasonHint': 'Required — kept with the grant and in the audit trail.',
  'exec.revoked': 'Dashboard Access revoked for {name}.',
  'exec.historyTitle': 'History',
  'exec.settingsTitle': 'Dashboard Settings',
  'exec.settingsSubtitle': 'In force: version {v} · newest saved: version {latest}. A save starts a new version from its day; finalized reports are never scored again.',
  'exec.saved': 'Saved as version {v}, in force from {day}.',
  'exec.scoring': 'Work-performance weights',
  'exec.scoringHint': 'Starting defaults, not a validated standard. The enabled dimensions must add up to 100. Attendance, shifts, Free Shifts, leave, idle time, pay and points are never part of the score.',
  'exec.dim.completion': 'Completion',
  'exec.dim.deadline': 'Deadlines',
  'exec.dim.quality': 'Review & quality',
  'exec.dim.tickets': 'Tickets & SLA',
  'exec.dim.collaboration': 'Collaboration',
  'exec.enabled': 'Counted',
  'exec.sum': 'Total: {n}% (must be 100%)',
  'exec.minEvents': 'Fewest work events',
  'exec.minEventsHint': 'Below this a score is “Not enough evidence”.',
  'exec.minWeight': 'Least measurable weight (%)',
  'exec.minWeightHint': 'The share of the weight that must have evidence.',
  'exec.reviewHours': 'Review waits (hours)',
  'exec.reviewHoursHint': 'A review waiting longer counts against its reviewer.',
  'exec.label.excellent': 'Excellent from',
  'exec.label.veryGood': 'Very good from',
  'exec.label.good': 'Good from',
  'exec.overrides': 'Department weights',
  'exec.overridesHint': 'A department’s own weights score the people whose primary department it is.',
  'exec.addOverride': 'Add a department',
  'exec.health': 'Company health',
  'exec.healthHint': 'Pillar weights (must add up to 100). A pillar without enough data is left out — never counted as zero.',
  'exec.pillar.financial': 'Financial',
  'exec.pillar.work': 'Team work',
  'exec.pillar.delivery': 'Project delivery',
  'exec.pillar.service': 'Client service & SLA',
  'exec.health.minMeasurableWeightPercent': 'Least measurable weight (%)',
  'exec.health.minScoredEmployees': 'Fewest scored employees',
  'exec.health.minTickets': 'Fewest tickets',
  'exec.health.minDatedTasks': 'Fewest dated project tasks',
  'exec.risks': 'Risk thresholds',
  'exec.risk.blockedQTaskDays': 'Q Task blocked for (days)',
  'exec.risk.stalledWorkflowDays': 'Workflow idle for (days)',
  'exec.risk.contractExpiryDays': 'Contract ends within (days)',
  'exec.risk.overdueReceivableSharePercent': 'Overdue share of receivables (%)',
  'exec.risk.expenseSpikePercent': 'Expense rise over the period before (%)',
  'exec.risk.receivableConcentrationPercent': 'One client’s share of receivables (%)',
  'exec.ai': 'AI explanations',
  'exec.aiNoticeTitle': 'Before switching AI on',
  'exec.aiNotice': 'When on, work-related aggregated and attributed metadata — figures, counts, dates and pseudonymous ids, never names, titles, messages, calls, salaries or wallet reasons — is sent to OpenAI to write the briefings. Handle your employee notices and approvals first. AI only explains: it can never change a figure or a score.',
  'exec.aiServerOn': 'The server allows AI (EXECUTIVE_AI_ENABLED)',
  'exec.aiServerOff': 'The server keeps AI off (EXECUTIVE_AI_ENABLED is not true)',
  'exec.aiKeySet': 'OpenAI key set',
  'exec.aiKeyMissing': 'No OpenAI key on the server',
  'exec.aiBudgetServer': 'server budget {n} tokens a month',
  'exec.aiEnabled': 'Write AI explanations',
  'exec.aiAcknowledge': 'I confirm the employee notices and approvals for sending this metadata to OpenAI are handled.',
  'exec.aiConsentAt': 'Confirmed {at}.',
  'exec.aiModel': 'Model',
  'exec.aiModelDefault': 'The server’s default ({model})',
  'exec.aiBudget': 'Monthly token budget',
  'exec.aiBudgetHint': 'Empty = the server’s. Spent, new explanations wait until next month.',
  'exec.aiPriceIn': 'Estimated price, input (per million tokens)',
  'exec.aiPriceOut': 'Estimated price, output (per million tokens)',
  'exec.aiPriceAsOf': 'Prices as of',
  'exec.aiPriceHint': 'Only for an estimated cost — empty shows none.',
  'exec.aiReport.daily': 'Daily briefing',
  'exec.aiReport.weekly': 'Weekly briefing',
  'exec.aiReport.monthly': 'Monthly report',
  'exec.aiReport.employees': 'Employees’ monthly explanations',
  'exec.dailyAt': 'Daily report at',
  'exec.dailyAtHint': 'Company time, for the day before.',
  'exec.monthlyAt': 'Monthly report at (on the 1st)',
  'exec.settleDays': 'Settle after (days)',
  'exec.settleDaysHint': 'A provisional report is finalized once its period has been closed this long.',
  'exec.weekly': 'Weekly reports',
  'exec.retainSnapshots': 'Keep reports (months)',
  'exec.retainHint': '0 keeps them for ever.',
  'exec.retainExports': 'Keep the export log (months)',
  'exec.effectiveFrom': 'In force from',
  'exec.effectiveFromHint': 'Today or later.',
  'exec.history': 'Versions',
  'exec.historyRow': 'Version {v} · from {day} · {who} · {at}',
  'exec.problem.WHOLE_PERCENT': 'a whole number from 0 to 100',
  'exec.problem.ENABLED_WEIGHTS_SUM_100': 'the counted weights must add up to 100',
  'exec.problem.WEIGHTS_SUM_100': 'the weights must add up to 100',
  'exec.problem.ONE_DIMENSION_AT_LEAST': 'count one dimension at least',
  'exec.problem.DESCENDING_PERCENTS': 'Excellent > Very good > Good',
  'exec.problem.CONSENT_REQUIRED': 'confirm the notice to switch AI on',
  'exec.problem.UNKNOWN_MODEL': 'a model the server offers',
  'exec.problem.TIME': 'a time (HH:MM)',
  'exec.problem.DUPLICATE': 'each department once',
} as const;

export type TranslationKey = keyof typeof en;

const ar: Record<TranslationKey, string> = {
  'app.name': 'نظام فيجانيوم',
  'app.tagline': 'لوحة العمليات',
  'nav.dashboard': 'لوحة المعلومات',
  'nav.projects': 'المشاريع',
  'nav.tasks': 'المهام',
  'nav.kanban': 'لوحة كانبان',
  'nav.tickets': 'التذاكر',
  'nav.qTasks': 'مهام الطابور',
  'nav.shifts': 'الورديات',
  'nav.mySchedule': 'جدولي',
  'nav.departments': 'الأقسام',
  'nav.employees': 'الموظفون',
  'nav.payroll': 'الرواتب',
  'nav.clients': 'العملاء',
  'nav.expenses': 'المصروفات',
  'nav.invoices': 'الفواتير',
  'nav.payments': 'المدفوعات',
  'nav.dataMigration': 'ترحيل البيانات',
  'nav.groupClients': 'التجاري',
  'nav.budgets': 'الميزانيات',
  'nav.violations': 'المخالفات والجزاءات',
  'nav.reports': 'التقارير',
  'nav.settings': 'الإعدادات',
  'nav.auditLog': 'سجل التدقيق',
  'nav.groupWork': 'العمل',
  'nav.groupWorkforce': 'القوى العاملة',
  'nav.groupFinance': 'المالية',
  'nav.groupAdmin': 'الإدارة',

  'common.search': 'بحث',
  'common.searchPlaceholder': 'ابحث في العملاء والتذاكر والمشاريع والمهام…',
  'common.filters': 'الفلاتر',
  'common.clear': 'مسح',
  'common.save': 'حفظ',
  'common.cancel': 'إلغاء',
  'common.create': 'إنشاء',
  'common.edit': 'تعديل',
  'common.delete': 'حذف',
  'common.close': 'إغلاق',
  'theme.toLight': 'التبديل إلى الوضع الفاتح',
  'theme.toDark': 'التبديل إلى الوضع الداكن',
  'common.confirm': 'تأكيد',
  'common.apply': 'تطبيق',
  'common.loading': 'جاري التحميل…',
  'common.noResults': 'لا توجد بيانات',
  'common.notFoundHint': 'الصفحة غير موجودة، أو ليست لديك صلاحية لفتحها.',
  'common.noResultsHint': 'جرّب تخفيف الفلاتر.',
  'common.error': 'حدث خطأ ما',
  'common.retry': 'إعادة المحاولة',
  'common.all': 'الكل',
  'common.none': 'لا شيء',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.actions': 'إجراءات',
  'common.status': 'الحالة',
  'common.type': 'النوع',
  'common.priority': 'الأولوية',
  'common.department': 'القسم',
  'common.project': 'المشروع',
  'common.assignee': 'المسؤول',
  'common.createdAt': 'تاريخ الإنشاء',
  'common.updatedAt': 'آخر تحديث',
  'common.dueDate': 'موعد التسليم',
  'common.amount': 'المبلغ',
  'common.currency': 'العملة',
  'common.date': 'التاريخ',
  'common.description': 'الوصف',
  'common.notes': 'ملاحظات',
  'common.reason': 'السبب',
  'common.total': 'الإجمالي',
  'common.name': 'الاسم',
  'common.nameTooLong': 'الحد الأقصى {max} من الأحرف، وما كتبته {count}.',
  'common.code': 'الرمز',
  'common.email': 'البريد الإلكتروني',
  'common.phone': 'الهاتف',
  'common.role': 'الدور',
  'common.of': 'من',
  'common.page': 'صفحة',
  'common.previous': 'السابق',
  'common.next': 'التالي',
  'common.general': 'عام',
  'common.unassigned': 'غير مسند',
  'common.required': 'مطلوب',
  'common.optional': 'اختياري',
  'common.back': 'رجوع',
  'common.view': 'عرض',
  'common.refresh': 'تحديث',
  'common.export': 'تصدير',
  'common.from': 'من',
  'common.to': 'إلى',
  'common.month': 'الشهر',
  'common.year': 'السنة',
  'common.add': 'إضافة',
  'common.remove': 'إزالة',
  'common.submit': 'إرسال',
  'common.approve': 'اعتماد',
  'common.reject': 'رفض',
  'common.never': 'أبدًا',
  'common.you': 'أنت',

  'auth.signIn': 'تسجيل الدخول',
  'auth.signOut': 'تسجيل الخروج',
  'auth.emailLabel': 'بريد العمل',
  'auth.passwordLabel': 'كلمة المرور',
  'auth.signingIn': 'جاري تسجيل الدخول…',
  'auth.welcome': 'أهلًا بعودتك',
  'auth.subtitle': 'سجّل الدخول إلى لوحة العمليات.',
  'auth.forgotPassword': 'هل نسيت كلمة المرور؟',
  'auth.sessionExpired': 'انتهت جلستك، سجّل دخولك مرة ثانية.',
  'auth.myProfile': 'ملفي الشخصي',
  'auth.changePassword': 'تغيير كلمة المرور',
  'auth.androidApp': 'تطبيق أندرويد',
  'auth.androidAppHint': 'تنزيل «فريق فيجانيوم»، تطبيق الموظفين على أندرويد',
  'auth.currentPassword': 'كلمة المرور الحالية',
  'auth.newPassword': 'كلمة المرور الجديدة',
  'auth.changePasswordSubtitle': 'تبقى مسجّل الدخول هنا، وكل مكان آخر سجّلت الدخول فيه سيطلب كلمة المرور الجديدة.',
  'auth.confirmPassword': 'تأكيد كلمة المرور الجديدة',
  'auth.passwordHint': 'لا تقل عن {n} من الأحرف.',
  'auth.passwordTooShort': 'يجب ألا تقل كلمة المرور الجديدة عن {n} من الأحرف.',
  'auth.passwordMismatch': 'كلمتا المرور الجديدتان غير متطابقتين.',
  'auth.currentPasswordWrong': 'كلمة المرور الحالية غير صحيحة.',
  'auth.passwordChanged': 'تم تغيير كلمة المرور.',
  'auth.showPassword': 'إظهار كلمة المرور',
  'auth.hidePassword': 'إخفاء كلمة المرور',
  'auth.forgotTitle': 'إعادة تعيين كلمة المرور',
  'auth.forgotIntro': 'أدخل البريد الذي تسجّل الدخول به، وسنرسل إليك رابطًا لتعيين كلمة مرور جديدة.',
  'auth.sendResetLink': 'أرسل لي الرابط',
  'auth.resetLinkSent': 'إذا كان {email} بريد حساب لدينا، فقد أرسلنا إليه رابطًا لتعيين كلمة مرور جديدة. يعمل الرابط مرة واحدة فقط، وتنتهي صلاحيته بعد ساعة.',
  'auth.resetLinkSentHint':
    'لم يصلك شيء بعد دقائق؟ افحص مجلد الرسائل غير المرغوب فيها، أو اطلب من مدير النظام تعيين كلمة مرور جديدة لك.',
  'auth.backToSignIn': 'العودة إلى تسجيل الدخول',
  'auth.resetTitle': 'تعيين كلمة مرور جديدة',
  'auth.resetIntro': 'ستصبح هذه كلمة مرورك في كل مكان تسجّل فيه الدخول لدينا، وسيتم تسجيل خروج كل جهاز مسجّل الدخول بكلمة المرور القديمة.',
  'auth.setNewPassword': 'تعيين كلمة المرور الجديدة',
  'auth.passwordResetDone': 'تم تعيين كلمة المرور الجديدة. سجّل الدخول بها.',
  'auth.resetLinkInvalid': 'انتهت صلاحية هذا الرابط أو سبق استخدامه. اطلب رابطًا جديدًا.',
  'auth.requestNewLink': 'اطلب رابطًا جديدًا',

  'dash.title': 'لوحة المعلومات',
  'dash.myTasks': 'مهامي المفتوحة',
  'dash.myOverdue': 'مهامي المتأخرة',
  'dash.myTickets': 'تذاكري المفتوحة',
  'dash.myProjects': 'مشاريعي',
  'dash.currentShift': 'الوردية الحالية',
  'dash.nextShift': 'الوردية القادمة',
  'dash.notOnShift': 'خارج الوردية',
  'dash.myPenalties': 'جزاءاتي هذا الشهر',
  'dash.awaitingMyView': 'بانتظار اطلاعي',
  'dash.unassignedTickets': 'غير مسندة في قسمي',
  'dash.myAssigned': 'مسندة إليّ',
  'dash.currentQTask': 'مهمة الطابور الحالية',
  'dash.availableQTasks': 'مهام طابور متاحة',
  'dash.slaCountdowns': 'العدّ التنازلي للمهل',
  'dash.teamMember': 'طابوري',
  'dash.teamLeader': 'القسم',
  'dash.admin': 'الشركة',
  'dash.openMonitoring': 'فتح المراقبة',
  'dash.finance': 'المالية',
  'dash.ticketsByStatus': 'التذاكر حسب الحالة',
  'dash.tasksByStatus': 'المهام حسب الحالة',
  'dash.urgent': 'عاجلة',
  'dash.viewBreaches': 'تجاوزات مهلة الاطلاع',
  'dash.assignmentBreaches': 'تجاوزات مهلة الإسناد',
  'dash.departmentWorkload': 'حجم العمل لكل موظف',
  'dash.staffOnShift': 'الموظفون في الوردية الآن',
  'dash.qTaskQueue': 'طابور المهام',
  'dash.blockedQTasks': 'مهام طابور معلّقة',
  'dash.slaCompliance': 'الالتزام بمهلة الإسناد',
  'dash.activeDepartments': 'الأقسام النشطة',
  'dash.activeProjects': 'المشاريع النشطة',
  'dash.payrollStatus': 'حالة الرواتب',
  'dash.monthExpenses': 'مصروفات هذا الشهر',
  'dash.expenseBreakdown': 'المصروفات حسب الفئة',
  'dash.overdueInvoices': 'فواتير متأخرة',
  'dash.budgetUtilisation': 'الميزانية مقابل الفعلي',
  'dash.incoming': 'مدفوعات واردة',
  'dash.outgoing': 'مدفوعات صادرة',
  'dash.nothingPending': 'لا شيء معلّق، كل شيء على ما يرام.',
  'dash.workingNow': 'في العمل الآن',
  'dash.onBreakN': '{n} في استراحة',
  'dash.nobodyWorking': 'لا أحد في العمل الآن.',
  'dash.teamAttendance': 'حضور الفريق',
  'dash.workingOffice': 'في المكتب',
  'dash.workingRemote': 'عن بُعد',
  'dash.onShiftN': 'في الوردية: {n}',
  'dash.nobodyOffice': 'لا أحد من فريق المكتب في ورديته الآن.',
  'dash.nobodyRemote': 'لا أحد ممن يعملون عن بُعد في ورديته الآن.',
  'dash.urgentTasks': 'المهام العاجلة',
  'dash.urgentTasksHint': 'المسندة إليك أو إلى قسمك، ولم تكتمل بعد.',
  'dash.noDeadline': 'بلا موعد نهائي',
  'dash.progress.title': 'تقدّمي اليوم',
  'dash.progress.of': 'تم إنجاز {done} من {total}',
  'dash.progress.label': 'تقدّمي اليوم: تم إنجاز {done} من {total}',
  'dash.progress.clear': 'لا شيء أمامك',
  'dash.progress.clearHint': 'ليس لديك عمل مفتوح الآن. يظهر هنا كل عمل جديد فور وصوله إليك.',
  'dash.progress.clearWaiting': 'لا شيء مطلوب منك الآن. بانتظار غيرك: {n}.',
  'dash.progress.start': 'لنبدأ',
  'dash.progress.startHint': 'أمامك: {n}. أكمل أي واحد منها لتبدأ الدائرة بالامتلاء.',
  'dash.progress.going': 'بداية موفّقة',
  'dash.progress.goingHint': 'أنجزت {done}، والمتبقي {left}.',
  'dash.progress.half': 'تجاوزت المنتصف',
  'dash.progress.halfHint': 'المتبقي {left}، واصل.',
  'dash.progress.almost': 'اقتربت من النهاية',
  'dash.progress.almostHint': 'بقي {left} فقط.',
  'dash.progress.done': 'أنجزت كل شيء!',
  'dash.progress.doneHint': 'أنهيت كل ما كان أمامك. عمل رائع اليوم.',
  'dash.progress.legendDone': 'المنجز',
  'dash.progress.legendDoneHint':
    'ما أنهيته اليوم: مهام أرسلتها للمراجعة أو أنهيتها، ومهام قادة أنهيتها، وتذاكر حللتها، ومهام طابور أنجزتها.',
  'dash.progress.legendLeft': 'المتبقي',
  'dash.progress.legendLeftHint': 'ما زال عليك إنهاؤه، بموعد أو بدونه، متأخرًا أو لا.',
  'dash.progress.legendWaiting': 'بانتظار غيرك',
  'dash.progress.legendWaitingHint': 'ما أرسلته للمراجعة قبل اليوم، أو تذكرة بانتظار مقدّم الطلب. لا يدخل في الحساب حتى يعود إليك.',
  'dash.progress.legendLater': 'مخطط لوقت لاحق',
  'dash.progress.legendLaterHint': 'لم يبدأ بعد، وموعد بدئه في يوم لاحق. لا يدخل في حساب اليوم.',
  'dash.progress.overdueN': 'متأخر: {n}',
  'dash.progress.next': 'التالي',
  'dash.progress.nothingNext': 'لا يوجد شيء آخر أمامك.',
  'dash.progress.week': 'آخر 7 أيام',
  'dash.progress.weekTotal': 'المنجز: {n}',
  'dash.progress.dayFinished': '{day}: تم إنجاز {n}',
  'dash.progress.today': 'اليوم',
  'dash.hours.title': 'ساعاتي اليوم',
  'dash.hours.of': 'من {required}',
  'dash.hours.label': 'ساعاتي اليوم: {worked} من {required}',
  'dash.hours.target': 'هدفك: {required}',
  'dash.hours.none': 'لا ساعات مطلوبة اليوم',
  'dash.hours.noneHint': 'لا وردية لك في الجدول اليوم.',
  'dash.hours.noneOff': 'اليوم يوم راحتك.',
  'dash.hours.noneLeave': 'أنت في إجازة اليوم.',
  'dash.hours.noneWorked': 'عملت {worked}، ولا ساعات مطلوبة اليوم.',
  'dash.hours.noneTrack': 'لا هدف اليوم. في الأيام التي لك فيها وردية، تظهر ساعاتها هنا.',
  'dash.hours.before': 'ورديتك قادمة',
  'dash.hours.beforeHint': 'عليك العمل {required} اليوم.',
  'dash.hours.start': 'ابدأ ورديتك',
  'dash.hours.startHint': 'بدأت الوردية الساعة {time}، وساعاتك تبدأ عندما تبدأها أنت.',
  'dash.hours.going': 'الساعة تعمل',
  'dash.hours.half': 'تجاوزت المنتصف',
  'dash.hours.almost': 'اقتربت من النهاية',
  'dash.hours.goingHint': 'بقي {missing}، وتنتهي نحو الساعة {time} إذا واصلت.',
  'dash.hours.paused': 'الساعة متوقفة مؤقتًا',
  'dash.hours.pausedHint': 'أنت في استراحة، وبقي {missing}.',
  'dash.hours.away': 'الساعة متوقفة',
  'dash.hours.awayHint': 'لست على جهازك، والساعة تنتظر حتى تعود.',
  'dash.hours.met': 'أكملت ساعاتك!',
  'dash.hours.metHint': 'أنهيت {required}.',
  'dash.hours.metExtra': '{extra} زيادة على {required}.',
  'dash.hours.metWaived': 'تم إلغاء جزاءات الاستراحة وعدم النشاط لهذا اليوم.',
  'dash.hours.short': 'نقص {missing}',
  'dash.hours.shortHint': 'انتهت ورديتك قبل إكمال {required}.',
  'dash.hours.shortCost': 'اليوم الناقص يكلّفك {days} من الراتب.',
  'dash.hours.shortFree': 'انتهت ورديتك الأساسية لليوم. الوردية الحرة نحسبها وحدها، ولا تعوّض هذه الساعات.',
  'dash.hours.freeLine': 'الورديات الحرة {minutes} · إجمالي الوقت المسجل {total}',
  'dash.hours.freeRunning': 'لديك وردية حرة جارية، نحسبها وحدها وبلا أجر.',
  'dash.hours.absent': 'لم تعمل أي وردية اليوم',
  'dash.hours.absentHint': 'انتهت ورديتك دون أن تبدأها.',
  'dash.hours.fromShift': 'الساعات المعيارية لورديتك',
  'dash.hours.fromDefault': 'ساعات العمل اليومية الافتراضية للشركة، لأن ورديتك لا تحدد ساعات اليوم',
  'dash.hours.breaks': 'استراحات {minutes}',
  'dash.hours.weekTotal': '{worked} · أكملت {met} من {owed}',
  'dash.hours.dayLabel': '{day}: {worked} من {required}',
  'dash.hours.dayNotOwed': '{day}: {worked}، بلا ساعات مطلوبة',
  'tips.title': 'ما الجديد',
  'tips.hint': 'ميزات أضفناها مؤخرًا، ومتاحة لك.',
  'tips.open': 'افتح',
  'tips.gotIt': 'فهمت',
  'tips.gotItLabel': 'فهمت، إخفاء «{title}»',
  'tips.dismissAll': 'إخفاء الكل',
  'tips.showAll': 'عرض الكل ({n})',
  'tips.showLess': 'عرض أقل',
  'dash.workOpen': 'اعرض ما يعمل عليه',
  'dash.workNow': 'يعمل عليه الآن',
  'dash.workToday': 'المتبقي لليوم',
  'dash.workTomorrow': 'غدًا',
  'dash.workNothingNow': 'لا يعمل على شيء الآن.',
  'dash.workNothingToday': 'لا شيء آخر مستحق أو مخطط لليوم.',
  'dash.workNothingTomorrow': 'لا شيء مستحق أو مخطط للغد.',
  'dash.workStarted': 'بدأ العمل {when}',
  'dash.workPlannedStart': 'موعد البدء {when}',
  'dash.workAssigned': 'تم الإسناد {when}',
  'dash.workDeadline': 'الموعد النهائي {when}',
  'dash.workTodayAt': 'اليوم الساعة {time}',
  'dash.workTomorrowAt': 'غدًا الساعة {time}',
  'dash.workYesterdayAt': 'أمس الساعة {time}',
  'dash.workKind.TASK': 'مهمة',
  'dash.workKind.LEADER_TASK': 'مهمة قادة',
  'dash.workKind.TICKET': 'تذكرة',
  'dash.workKind.Q_TASK': 'مهمة طابور',
  'dash.workKind.MEETING': 'اجتماع',

  'tickets.title': 'التذاكر',
  'tickets.new': 'تذكرة جديدة',
  'tickets.createNotAllowed': 'لا ينشئ التذاكر إلا مديرو النظام، ومن يمنحونه صلاحية «إنشاء التذاكر» في صفحة الموظف.',
  'tickets.number': 'الرقم',
  'tickets.subject': 'الموضوع',
  'tickets.category': 'الفئة',
  'tickets.targetDepartment': 'التوجيه إلى قسم',
  'tickets.viewSla': 'مهلة الاطلاع',
  'tickets.assignmentSla': 'مهلة الإسناد',
  'tickets.slaState': 'حالة المهلة',
  'tickets.assign': 'إسناد',
  'tickets.assignToMe': 'إسناد إليّ',
  'tickets.reassign': 'إعادة الإسناد',
  'tickets.reroute': 'إعادة التوجيه',
  'tickets.resolve': 'حل',
  'tickets.reopen': 'إعادة فتح',
  'tickets.reply': 'رد',
  'tickets.internalNote': 'ملاحظة داخلية',
  'tickets.resolution': 'الحل',
  'tickets.timeline': 'الخط الزمني',
  'tickets.responsibility': 'المسؤولية عند الإنشاء',
  'tickets.views': 'الاطلاع',
  'tickets.conversation': 'المحادثة',
  'tickets.details': 'التفاصيل',
  'tickets.eligible': 'مشمول',
  'tickets.notEligible': 'غير مشمول',
  'tickets.viewedOnTime': 'اطّلع في الوقت',
  'tickets.viewedLate': 'اطّلع متأخرًا',
  'tickets.notViewed': 'لم يطّلع',
  'tickets.onShiftAtCreation': 'في الوردية عند الإنشاء',
  'tickets.snapshotNote':
    'نسجلها مرة واحدة عند إنشاء التذكرة، ولا تتغير بعد ذلك مهما تغيرت الورديات أو العضوية.',
  'tickets.lockedBy': 'يتعامل حاليًا مع هذه التذكرة',
  'tickets.forceUnlock': 'فك القفل إجباريًا',
  'tickets.lock': 'أخذ القفل',
  'tickets.slaMet': 'تم الالتزام بالمهلة',
  'tickets.slaMissed': 'تم تجاوز المهلة',
  'tickets.slaStopped': 'تم إيقاف المهلة',
  'tickets.overdue': 'متأخرة',
  'tickets.awaitingMyView': 'بانتظار اطلاعي',
  'tickets.mine': 'مسندة إليّ',
  'tickets.requestedByMe': 'أنشأتها',
  'tickets.generalOnly': 'العامة فقط',
  'tickets.handover': 'تسليم الوردية',
  'tickets.noLock': 'لم يأخذ أحد القفل.',
  'tickets.viewRecorded': 'تم تسجيل اطلاعك.',

  'tasks.title': 'المهام',
  'tasks.new': 'مهمة جديدة',
  'kanban.title': 'لوحة كانبان',
  'kanban.myTasks': 'مهامي',
  'tasks.assignedByMe': 'أسندتها',
  'kanban.dropHint': 'اسحب البطاقة إلى عمود آخر لتغيير حالتها.',
  'kanban.invalidMove': 'هذا النقل غير مسموح لهذه المهمة.',
  'kanban.doneByReviewer': 'لا يعتمد إنجاز المهمة إلا من أنشأها أو أسندها. عندما تنتهي منها، انقلها إلى «مراجعة».',

  'qtasks.title': 'مهام الطابور',
  'qtasks.new': 'مهمة طابور جديدة',
  'qtasks.createAdminsOnly': 'لا يضيف مهام الطابور إلا مديرو النظام.',
  'qtasks.queue': 'طابور القسم',
  'qtasks.next': 'التالية',
  'qtasks.availableNow': 'متاحة الآن',
  'qtasks.assignedToMe': 'مسندة إليّ',
  'qtasks.claim': 'إسناد إليّ',
  'qtasks.start': 'بدء',
  'qtasks.complete': 'إنهاء',
  'qtasks.block': 'تعليق',
  'qtasks.unblock': 'رفع التعليق',
  'qtasks.release': 'إرجاع للطابور',
  'qtasks.skip': 'تخطّي',
  'qtasks.reorder': 'إعادة الترتيب',
  'qtasks.reorderHint': 'حدّد الترتيب الذي يعمل به فريقك. لا تتحرك إلا المهام التي لم يأخذها أحد.',
  'qtasks.poolNote': 'خذ أي مهمة طابور في قسمك. الترتيب حسب أولوية قائد فريقك.',
  'qtasks.assign': 'إسناد',
  'qtasks.assignTitle': 'إسناد مهمة الطابور',
  'qtasks.assignTo': 'إسناد إلى',
  'qtasks.assignHint': 'سيصله إشعار ورسالة بالبريد الإلكتروني، والعمل الذي بدأ عليها يستمر معه.',
  'qtasks.nowWith': 'المهمة الآن مع {name}. اتركه كما هو إذا أردت تغيير المواعيد فقط، والعمل الذي بدأ يستمر مع من تختاره.',
  'qtasks.rescheduled': 'تم حفظ المواعيد الجديدة.',
  'qtasks.assignedTo': 'تم الإسناد إلى {name}.',
  'qtasks.convert': 'تحويل إلى مهام',
  'qtasks.convertHint':
    'حدّد كل من سيعمل عليها. سيحصل كل واحد على مهمة خاصة به، بتاريخ بدء وموعد نهائي. تبقى مهمة الطابور في الطابور، وتكتمل تلقائيًا عندما تنتهي كل مهامها.',
  'qtasks.tasksProgress': 'المنجز: {done} من {total}',
  'qtasks.followsTasks': 'تكتمل تلقائيًا عندما تنتهي كل مهامها، وتعود مفتوحة إذا تمت إعادة فتح أي منها.',
  'qtasks.people': 'من سيعمل عليها',
  'qtasks.everyone': 'الجميع',
  'qtasks.selectedCount': 'المحدد: {n}',
  'qtasks.taskTitle': 'عنوان المهمة',
  'qtasks.sameDates': 'استخدم هذه التواريخ للجميع',
  'qtasks.noWorkers': 'لا يوجد في هذا القسم من يعمل على مهام الطابور بعد.',
  'qtasks.converted': 'تم تحويلها إلى مهام.',
  'qtasks.madeInto': 'المهام الناتجة عنها',
  'qtasks.position': 'الترتيب',
  'qtasks.claimed': 'أخذها عضو آخر في الفريق.',
  'qtasks.emptyQueue': 'الطابور فارغ',
  'qtasks.emptyQueueHint': 'تظهر مهام الطابور الجديدة هنا بترتيب إضافتها.',
  'qtasks.emptyTabHint': 'جرّب تبويبًا آخر.',

  'shifts.title': 'الورديات',
  'shifts.definitions': 'تعريفات الورديات',
  'shifts.roster': 'جدول الشهر',
  'shifts.generate': 'توليد الجدول',
  'shifts.copy': 'نسخ فترة سابقة',
  'shifts.legend': 'ص صباحي · ل ليلي · خ مخصص · إ إجازة',
  'shifts.crossesMidnight': 'تمتد بعد منتصف الليل',
  'shifts.standardHours': 'الساعات المعيارية',
  'shifts.standardHoursHint':
    'عدد ساعات العمل المطلوبة في اليوم على هذه الوردية، بدون الاستراحات. مثلًا: من 09:00 إلى 17:00 مع ساعة استراحة = 7 ساعات. وأي يوم ينقص عنها عليه جزاء عدم إكمال الساعات.',
  'shifts.startTime': 'البداية',
  'shifts.endTime': 'النهاية',
  'shifts.onShiftNow': 'في الوردية الآن',
  'shifts.noEvening': 'الأنواع المعتمدة هي صباحي وليلي ومخصص وإجازة فقط.',
  'shifts.mySchedule': 'جدولي',
  'shifts.weekdays': 'أيام الأسبوع',
  'shifts.overwrite': 'استبدال الأيام الموجودة',
  'shifts.holidays': 'العطلات',

  'employees.title': 'الموظفون',
  'employees.new': 'موظف جديد',
  'employees.activate': 'تفعيل',
  'employees.deactivate': 'تعطيل',
  'employees.roles': 'الأدوار',
  'employees.role': 'الدور',
  'employees.roleHintAdmin': 'كل شيء في لوحة الإدارة، لكل الأقسام.',
  'employees.roleHintLeader':
    'في أقسامه: يسند التذاكر والمهام، ويدير سير العمل ومهام الطابور والورديات والحضور، ويطّلع على التقارير.',
  'employees.roleHintMember': 'يعالج التذاكر، ويأخذ مهام الطابور، وينشئ المهام.',
  'employees.roleHintEmployee': 'ينشئ المهام ويتابع عمله.',
  'employees.extraPermissions': 'صلاحيات إضافية',
  'employees.addOnTicketsHint': 'إنشاء تذاكر جديدة. بدونها لا ينشئها إلا مديرو النظام.',
  'employees.addOnIncluded': 'مشمولة: دور «{role}» يستطيع ذلك دائمًا.',
  'employees.severalRoles': 'لديه الأدوار: {roles}. للموظف الآن دور واحد فقط، فاختره قبل الحفظ.',
  'employees.chooseOneRole': 'اختر دورًا واحدًا.',
  'employees.payrollProfile': 'ملف الراتب',
  'employees.slaOverride': 'استثناء المهلة',
  'employees.basicSalary': 'الراتب الأساسي',
  'employees.effectiveFrom': 'ساري من',
  'employees.salaryHistory': 'سجل الرواتب',
  'employees.active': 'نشط',
  'employees.inactive': 'غير نشط',
  'departments.title': 'الأقسام',
  'departments.new': 'قسم جديد',
  'departments.members': 'الأعضاء',
  'departments.leaders': 'القادة',
  'departments.slaOverrides': 'استثناءات المهل والجزاءات',
  'departments.inheritNote': 'اترك الحقل فارغًا لاستخدام القيمة الافتراضية للشركة.',
  'worklogs.title': 'سجلات العمل',

  'payroll.title': 'الرواتب',
  'payroll.periods': 'الفترات',
  'payroll.newPeriod': 'فترة جديدة',
  'payroll.calculate': 'احتساب',
  'payroll.markPaid': 'تحديد كمدفوع',
  'payroll.lock': 'إقفال',
  'payroll.gross': 'الإجمالي',
  'payroll.deductions': 'الخصومات',
  'payroll.net': 'الصافي',
  'payroll.basic': 'الراتب الأساسي',
  'payroll.overtime': 'العمل الإضافي',
  'payroll.holidayWork': 'العمل في العطلات',
  'payroll.dayOffWork': 'العمل في يوم الإجازة',
  'payroll.nightAllowance': 'بدل الوردية الليلية',
  'payroll.otherAllowances': 'بدلات أخرى',
  'payroll.slaPenalties': 'جزاءات المهل',
  'payroll.lineItems': 'البنود',
  'payroll.employees': 'الموظفون',
  'payroll.adjustment': 'تسوية',
  'payroll.myPayslips': 'قسائم راتبي',
  'payroll.lockedNote': 'هذه الفترة مقفلة. أضف أي تصحيح كتسوية في فترة مفتوحة.',

  'expenses.title': 'المصروفات',
  'expenses.new': 'مصروف جديد',
  'expenses.categories': 'الفئات',
  'expenses.vendor': 'المورّد',
  'expenses.receipt': 'رقم الإيصال',
  'expenses.markPaid': 'تحديد كمدفوع',
  // Clients (CR-001)
  'clients.title': 'العملاء',
  'clients.new': 'عميل جديد',
  'clients.company': 'الشركة',
  'clients.companyName': 'اسم الشركة',
  'clients.companyNameAr': 'اسم الشركة بالعربية',
  'clients.legalName': 'الاسم القانوني',
  'clients.code': 'كود العميل',
  'clients.codeHint': 'إذا تركته فارغًا، ننشئ الكود التالي تلقائيًا.',
  'clients.codeFollows': 'آخر كود عميل هو {code}، وهذا الكود الذي يليه. غيّره إن احتجت.',
  'clients.codeFirst': 'هذا أول كود عميل. غيّره إن احتجت.',
  'clients.codeEditHint': 'تعرض الفواتير الكود الحالي للعميل، حتى القديمة منها.',
  'clients.codeTaken': 'كود العميل {code} يخص عميلًا آخر.',
  'clients.country': 'الدولة',
  'clients.currency': 'عملة التعامل',
  'clients.currencyHint': 'العملة التي تصدر بها فواتير هذا العميل، ولا تتحدد تلقائيًا من الدولة.',
  'clients.address': 'العنوان',
  'clients.taxNumber': 'الرقم الضريبي',
  'clients.registrationNumber': 'رقم السجل التجاري',
  'clients.website': 'الموقع الإلكتروني',
  'clients.logo': 'الشعار',
  'clients.logoHint': 'يظهر أعلى عقود العميل مقابل شعارنا. PNG أو JPG أو WebP أو GIF، ويتم حفظه بصيغة PNG.',
  'clients.logoAdd': 'رفع شعار',
  'clients.logoReplace': 'استبدال',
  'clients.logoRemove': 'إزالة',
  'clients.logoUploading': 'جاري الرفع…',
  'clients.logoAlt': 'شعار {client}',
  'clients.logoUnreadable': 'لم نتمكن من قراءة هذه الصورة. استخدم PNG أو JPG أو WebP أو GIF.',
  'clients.logoTooLarge': 'ما زال الشعار أكبر من {mb} ميجابايت بعد تصغيره. استخدم صورة أبسط.',
  'clients.logoRefused': 'لم نتمكن من حفظ الشعار: {reason}',
  'clients.logoStorage': 'لا يمكن الوصول إلى مخزن الملفات. حاول مرة ثانية، وإذا تكررت المشكلة فأبلغ أحد مديري النظام.',
  'clients.contacts': 'جهات الاتصال',
  'clients.newContact': 'إضافة جهة اتصال',
  'clients.jobTitle': 'المسمى الوظيفي',
  'clients.primaryContact': 'جهة الاتصال الرئيسية',
  'clients.primary': 'رئيسية',
  'clients.overview': 'نظرة عامة',
  'clients.financialSummary': 'الملخص المالي',
  'clients.totalInvoiced': 'إجمالي الفواتير',
  'clients.totalPaid': 'إجمالي المدفوع',
  'clients.totalOutstanding': 'المتبقي',
  'clients.overdueAmount': 'المتأخر',
  'clients.openInvoices': 'فواتير مفتوحة',
  'clients.collectedLocal': 'المحصّل بالعملة الأساسية',
  'clients.deactivate': 'تعطيل',
  'clients.reactivate': 'إعادة التفعيل',
  'clients.deactivateConfirm':
    'عند تعطيل العميل تبقى كل مشاريعه وفواتيره ومدفوعاته، لكنه لا يظهر بعد ذلك في قوائم الاختيار.',
  'clients.inactiveWarning': 'هذا العميل غير نشط ولا يمكن إصدار فواتير له.',
  'clients.noClient': 'بدون عميل — مشروع داخلي',
  'clients.internalProject': 'داخلي',
  'projects.new': 'مشروع جديد',
  'projects.ownOnlyNote': 'تظهر هنا فقط المشاريع التي تملكها أو أنت عضو فيها.',
  'projects.codeFollows': 'آخر كود مشروع هو {code}، وهذا هو الكود التالي. غيّره إذا احتجت.',
  'projects.codeNoSeries': 'اجعل الكود ينتهي برقم، مثل {code}، وسنملأ لك كود المشروع التالي تلقائيًا.',
  'projects.codeTaken': 'كود المشروع {code} يخص مشروعًا آخر.',
  'projects.addMember': 'إضافة عضو',
  'projects.addPerson': 'شخص',
  'projects.addPersonHint': 'اختر شخصًا بالاسم.',
  'projects.addDepartment': 'قسم',
  'projects.addDepartmentHint': 'كل من فيه الآن.',
  'projects.addPersonMineHint': 'شخص من أقسامك.',
  'projects.addDepartmentMineHint': 'كل من في أحد أقسامك الآن.',
  'projects.noDepartmentOfYours': 'لست في أي قسم، فلا يوجد قسم يمكنك إضافته.',
  'projects.addOnlyNote': 'بعد الإضافة، لا يستطيع إزالة أحد من المشروع إلا مدير النظام.',
  'projects.departmentWillAdd': 'من ستتم إضافتهم ({n}):',
  'projects.departmentAlready': 'موجودون في المشروع بالفعل: {n}.',
  'projects.departmentLater': 'من ينضم إلى {department} لاحقًا لن تتم إضافته تلقائيًا.',
  'projects.departmentAllIn': 'كل من في هذا القسم موجود في المشروع بالفعل.',
  'projects.departmentEmpty': 'لا يوجد أحد في هذا القسم بعد.',
  'projects.departmentRoleHint': 'يأخذ كل من تتم إضافته هذا الدور، ويحتفظ الموجودون في المشروع بأدوارهم.',
  'projects.departmentAdded': 'تمت إضافة {n} من {department}.',
  'projects.departmentAddedNone': 'لم تتم إضافة أحد، فكل من في {department} موجود في المشروع بالفعل.',
  'projects.support': 'الدعم',
  'projects.supportUntil': 'حتى {date}',
  'projects.supportNote':
    'قيد الدعم حتى {date} بموجب العقد {contracts}، ويمكن إضافة المهام والتذاكر إليه. وعندما لا يسري عليه أي عقد، يصبح مكتملًا.',
  'projects.completedNote':
    'مكتمل: لا يشمله أي عقد دعم اليوم، لذلك لا يمكن إضافة مهام أو تذاكر جديدة إليه. وإذا تم إبرام عقد دعم له في المالية، يعود إلى فترة الدعم من تاريخ بداية العقد.',
  'projects.supportStartsNote': 'مكتمل حتى يبدأ العقد {contracts} في {date}. ومن ذلك اليوم يصبح قيد الدعم، ويمكن إضافة المهام والتذاكر إليه من جديد، ويتغير تاريخ انتهاء المشروع إلى {end}.',
  'projects.contractStartsNote': 'يبدأ العقد {contracts} في {date}، وعندها يدخل المشروع فترة الدعم، ويصبح تاريخ انتهائه {end}، أي اليوم السابق.',
  'projects.archivedNote': 'مؤرشف: لا يمكن إضافة مهام أو تذاكر جديدة إليه. وعقد جديد له في المالية يعيده إلى فترة الدعم.',
  'projects.contractWaitingNote': 'يسري العقد {contracts} حتى {date}، فعند الانتهاء من المشروع يبقى قيد الدعم حتى ذلك اليوم.',
  'projects.statusHintSupport': 'إذا انتهيت منه يبقى قيد الدعم ما دام العقد {contracts} ساريًا، أي حتى {date}، ثم يصبح مكتملًا.',
  'projects.statusHintCompleted':
    'إذا انتهيت منه ولا يسري عليه أي عقد، يصبح مكتملًا ولا يمكن إضافة مهام أو تذاكر جديدة إليه. وعقد جديد له في المالية يعيده إلى فترة الدعم.',
  'projects.statusHintInProgress': 'يسري العقد {contracts} حتى {date}، فعندما تنتهي من المشروع يدخل فترة الدعم.',
  'projects.closed': 'هذا المشروع مكتمل ولا يسري عليه أي عقد دعم، فلا يمكن إضافة مهام أو تذاكر جديدة إليه. ويعود إلى فترة الدعم عندما يكون له عقد دعم يشمل تاريخ اليوم.',
  'projects.closedUntil': 'هذا المشروع مكتمل، ويبدأ عقد دعمه في {date}، ومن ذلك اليوم يمكن إضافة مهام وتذاكر جديدة إليه.',
  'projects.closedArchived': 'هذا المشروع مؤرشف، فلا يمكن إضافة مهام أو تذاكر جديدة إليه.',
  'projects.closedCancelled': 'هذا المشروع ملغى، فلا يمكن إضافة مهام أو تذاكر جديدة إليه.',
  'projects.changeStatus': 'تغيير الحالة',
  'projects.statusNow': 'الآن: {status}',
  'projects.statusChanged': 'تغيّرت الحالة إلى {status}.',
  'projects.byHand': 'يدويًا',
  'projects.meaning.DRAFT': 'قيد التخطيط. يمكن إضافة المهام والتذاكر إليه.',
  'projects.meaning.ACTIVE': 'قيد التنفيذ. يمكن إضافة المهام والتذاكر إليه.',
  'projects.meaning.TESTING': 'قيد الاختبار قبل التسليم. يمكن إضافة المهام والتذاكر إليه.',
  'projects.meaning.ON_HOLD': 'متوقف مؤقتًا، ومع ذلك يمكن إضافة المهام والتذاكر إليه.',
  'projects.meaning.SUPPORT': 'تم تسليمه، وهو في فترة الدعم بموجب العقد {contracts} حتى {date}.',
  'projects.meaning.SUPPORT_BY_HAND': 'تم تسليمه، وهو في فترة الدعم يدويًا حتى يغيّر أحد حالته. يمكن إضافة المهام والتذاكر إليه.',
  'projects.meaning.COMPLETED': 'تم تسليمه وإغلاقه. لا يمكن إضافة مهام أو تذاكر جديدة إليه.',
  'projects.meaning.COMPLETED_LOCKED': 'لا يكتمل ما دام العقد {contracts} ساريًا، فهو قيد الدعم حتى {date}.',
  'projects.meaning.CANCELLED': 'تم إلغاؤه. لا يمكن إضافة مهام أو تذاكر جديدة إليه.',
  'projects.meaning.ARCHIVED': 'في الأرشيف، ولا يراه العميل. لا يمكن إضافة مهام أو تذاكر جديدة إليه.',
  'projects.meaning.contractRuns': 'يبقى العقد {contracts} ساريًا حتى {date}. وإذا كان يجب أن يتوقف، فيمكن إنهاؤه من المالية.',
  'projects.supportByHandNote':
    'قيد الدعم يدويًا: يمكن إضافة المهام والتذاكر إليه حتى يغيّر أحد حالته. وإذا تم إبرام عقد له في المالية، يصبح دعمه بموجب العقد، وعندما لا يسري عليه أي عقد يصبح مكتملًا.',
  'projects.cancelledNote': 'ملغى: لا يمكن إضافة مهام أو تذاكر جديدة إليه. وعقد جديد له في المالية يعيده إلى فترة الدعم.',
  'projects.closedContractNote':
    'لا يمكن إضافة مهام أو تذاكر جديدة إليه، لكن العقد {contracts} يبقى ساريًا حتى {date}. وإذا كان يجب أن يتوقف، فيمكن إنهاؤه من المالية.',
  'projects.statusHintSupportByHand':
    'لا يسري عليه أي عقد، لذلك يكون قيد الدعم يدويًا: يمكن إضافة المهام والتذاكر إليه حتى يغيّر أحد حالته. وإذا تم إبرام عقد له لاحقًا، يصبح دعمه بموجب العقد.',
  'projects.statusHintCancelled': 'بعد الإلغاء لا يمكن إضافة مهام أو تذاكر جديدة إليه. وعقد جديد له في المالية يعيده إلى فترة الدعم.',
  'projects.statusHintArchived':
    'لا يمكن إضافة مهام أو تذاكر جديدة إليه، ولن يراه العميل بعد ذلك. وعقد جديد له في المالية يعيده إلى فترة الدعم.',
  'projects.completedLocked': 'غير متاح أثناء سريان عقد',
  'projects.links': 'الروابط',
  'projects.linksHint':
    'أضف ما تحتاجه من روابط، ولكل رابط اسمه، مثل: لوحة التحكم، الموقع، API. ويجدها كل من يرى المشروع في صفحته.',
  'projects.linkName': 'الاسم',
  'projects.linkUrl': 'الرابط',
  'projects.linkNamePlaceholder': 'لوحة التحكم',
  'projects.addLink': 'إضافة رابط',
  'projects.removeLink': 'حذف الرابط',
  'projects.linkNameRequired': 'اكتب اسمًا للرابط، مثل لوحة التحكم أو الموقع.',
  'projects.linkUrlRequired': 'اكتب عنوان الرابط.',
  'projects.linkUrlInvalid': 'اكتب عنوان موقع، مثل https://admin.example.com.',
  'projects.linkUrlPassword': 'لا تكتب اسم المستخدم وكلمة المرور في الرابط، لأن كل من في المشروع يراه.',
  'projects.noLinks': 'لا توجد روابط بعد.',
  'projects.addLinks': 'إضافة روابط',
  'projects.copyLink': 'نسخ الرابط',
  'projects.linkCopied': 'تم نسخ الرابط.',
  'projects.copyRefused': 'لم يسمح المتصفح بالنسخ. حدّد الرابط وانسخه بنفسك.',
  'tickets.rerouteNote':
    'نسجّل إعادة التوجيه في سجل توجيه التذكرة، وتبقى قائمة المسؤولين الأصلية وموعد الإسناد الأصلي كما كانا تمامًا (§11.6).',
  'tickets.reassignNote': 'تظهر إعادة الإسناد في سجل التدقيق، لذلك يجب كتابة السبب.',
  'shifts.snapshotNote': 'تعديل اليوم لا يغيّر أبدًا بيانات المهل المحفوظة سابقًا (§15.5).',
  'shifts.weekdaysHint': 'لا تحدّد أي يوم إذا أردت التطبيق على كل أيام الفترة.',
  'shifts.midnightHint': 'إذا كانت النهاية عند البداية أو قبلها، فالوردية تمتد إلى ما بعد منتصف الليل.',
  'shifts.newDefinition': 'تعريف وردية جديد',
  'shifts.editDefinition': 'تعديل تعريف الوردية',
  'shifts.typeFixed': 'لا يمكن تغيير نوع الوردية. لنوع آخر، أنشئ وردية جديدة.',
  'shifts.bookedNote': 'حاليًا {current}، وتبدأ المواعيد أدناه في {date}.',
  'shifts.applyFrom': 'متى تبدأ المواعيد الجديدة؟',
  'shifts.applyToday': 'من اليوم، {date}',
  'shifts.applyTodayHint': 'تنتقل كل الأيام المحفوظة من اليوم فصاعدًا إلى المواعيد الجديدة.',
  'shifts.applyNextMonth': 'من الشهر القادم، {date}',
  'shifts.applyNextMonthHint':
    'تبقى أيام هذا الشهر المحفوظة على المواعيد الحالية. ومن {date} تسري المواعيد الجديدة على الأيام المحفوظة والجديدة.',
  'shifts.applyKeepsNote': 'الأيام السابقة لهذا التاريخ، والأيام التي لها مواعيد خاصة بها، تبقى كما هي.',
  'shifts.definitionSaved': 'تم حفظ الوردية.',
  'shifts.hoursApplied': 'تم الحفظ. المواعيد الجديدة من {date}، وعدد الأيام المحفوظة التي انتقلت إليها: {moved}.',
  'shifts.hoursKept': 'أيام لها مواعيد خاصة بها بقيت كما هي: {kept}.',
  'shifts.nextHours': '{hours} من {date}',
  'shifts.earlyEnd': 'إنهاء الوردية مبكرًا',
  'shifts.earlyEndOn': 'إنهاء هذه الوردية مبكرًا عليه جزاء',
  'shifts.earlyEndMinutes': 'يمكن إنهاؤها قبل موعدها بحد أقصى (دقائق)',
  'shifts.earlyEndMinutesHint': 'إنهاء الوردية خلال هذه المدة قبل نهايتها بلا جزاء. مثلًا، 15 تعني أن الوردية التي تنتهي 17:00 يمكن إنهاؤها من 16:45.',
  'shifts.earlyEndPenalty': 'جزاء الإنهاء قبل ذلك (أيام)',
  'shifts.earlyEndPenaltyHint': 'أيام من الراتب، حتى لو اكتملت ساعات اليوم.',
  'shifts.earlyEndNote':
    'ينطبق على الورديات التي تبدأ بعد الحفظ. ويعرض زر «إنهاء الوردية» قيمة الجزاء قبل الإنهاء. وإذا نقصت ساعات اليوم أيضًا، فعليه جزاء واحد فقط، وهو الأكبر؛ وإذا نقص اليوم أكثر من ساعة، يشمله قرار مدير النظام في اليوم الناقص. ولا جزاء في يوم الراحة أو الإجازة أو العطلة.',
  'shifts.earlyEndCol': 'الإنهاء المبكر',
  'shifts.earlyEndRule': '{minutes} قبل النهاية · {days}',
  'shifts.removeDefinition': 'إزالة تعريف الوردية',
  'shifts.removeDefinitionNote':
    'تختفي من هذه القائمة ولن تظهر بعد ذلك في جدول الورديات. والأيام المحفوظة عليها تبقى عليها، بمواعيدها والساعات المطلوبة فيها كما كانت.',
  'shifts.removeDefinitionUpcoming':
    'الأيام المحفوظة على هذه الوردية من اليوم فصاعدًا: {days}. وتبقى عليها حتى تغيّرها في جدول الورديات.',
  'shifts.definitionRemoved': 'تمت إزالة الوردية.',
  'shifts.newHoliday': 'عطلة جديدة',
  'shifts.editHoliday': 'تعديل العطلة',
  'shifts.deleteHoliday': 'حذف العطلة',
  'shifts.holidayAppliesNote':
    'تسري التغييرات من اليوم، فنعيد احتساب طلبات الإجازة وسجلات العمل من اليوم فصاعدًا. ويبقى ما قبل اليوم كما هو.',
  'shifts.deleteHolidayNote':
    'من اليوم فصاعدًا يصبح هذا التاريخ يوم عمل عاديًا: نعيد احتساب طلبات الإجازة التي تشمله، ولا يعود العمل المسجّل فيه محسوبًا عملًا في عطلة. ويبقى ما قبل اليوم كما هو.',
  'shifts.holidaySaved': 'تم حفظ العطلة.',
  'shifts.holidayDeleted': 'تم حذف العطلة.',
  'shifts.holidayApplied': 'تمت إعادة الاحتساب من {date}. طلبات الإجازة: {leave}، سجلات العمل: {logs}.',
  // وضع الدعم (2026-11-01): ساعات دعم في أيام مختارة.
  'shifts.support': 'وضع الدعم',
  'shifts.supportIntro':
    'ليس وردية. امنح أحد الموظفين ساعات دعم في أيام تختارها، من هنا أو من يوم في جدول الورديات. خلال هذه الساعات يستطيع تشغيل وضع الدعم من تطبيق Viganium Team، وما دام مشغّلًا ترنّ التذاكر الجديدة لأقسامه على هاتفه، ويكون مسؤولًا عنها مثل من في الوردية، فالتأخر في الاطلاع عليها أو إسنادها له الجزاءات نفسها. لا يحتاج إلى التطبيق المكتبي، ولا يدخل في ساعات العمل.',
  'shifts.supportGive': 'منح وضع الدعم',
  'shifts.supportDaysEmpty': 'لا توجد أيام دعم في هذا الشهر.',
  'shifts.supportHours': 'الساعات',
  'shifts.supportOn': 'مشغّل حتى {time}',
  'shifts.supportSince': 'منذ {time}',
  'shifts.supportUsed': 'تم استخدامه',
  'shifts.supportNotUsed': 'لم يستخدمه',
  'shifts.supportNotOn': 'لم يشغّله',
  'shifts.supportUpcoming': 'قادم',
  'shifts.supportAllDay': 'طوال اليوم',
  'shifts.supportNextDay': 'اليوم التالي',
  'shifts.supportEachDay': 'في كل يوم:',
  'shifts.supportNoTicketsRefused': 'يمكن منح وضع الدعم للموظفين النشطين الذين يتعاملون مع التذاكر فقط.',
  'shifts.supportRemoveDayTitle': 'إلغاء ساعات دعم {name} يوم {date}؟',
  'shifts.supportRemoveDayNote': 'لن يستطيع تشغيل وضع الدعم في ذلك اليوم.',
  'shifts.supportRemoveEndsNow': 'وضع الدعم مشغّل الآن، فسينتهي فورًا وسنبلغه بذلك.',
  'shifts.supportRemoved': 'تم إلغاء ساعات الدعم.',
  'shifts.supportHistory': 'وضع الدعم مؤخرًا',
  'shifts.supportHistoryHint':
    'آخر 30 يومًا. التذاكر: التي وصلت أثناء تشغيله، وكان مسؤولًا عنها بسبب وضع الدعم وحده.',
  'shifts.supportNoHistory': 'لم يستخدم أحد وضع الدعم خلال آخر 30 يومًا.',
  'shifts.supportStarted': 'التشغيل',
  'shifts.supportEnded': 'الانتهاء',
  'shifts.supportHowEnded': 'كيف انتهى',
  'shifts.supportTickets': 'التذاكر',
  'shifts.supportEnd.ENDED': 'أوقفه من التطبيق',
  'shifts.supportEnd.WINDOW_ENDED': 'بانتهاء ساعات الدعم',
  'shifts.supportEnd.REVOKED': 'أوقفه {name}',
  'shifts.supportEnd.SIGNED_OUT': 'تم تسجيل الخروج من الهاتف',
  'shifts.supportPersonHint': 'موظفو أقسامك الذين يتعاملون مع التذاكر.',
  'shifts.supportWeekdaysHint': 'لا تحدّد أي يوم إذا أردت منح ساعات الدعم في كل أيام الفترة.',
  'shifts.supportFrom': 'من',
  'shifts.supportTo': 'إلى',
  'shifts.supportHoursHint': 'بتوقيت الشركة. إذا كان «إلى» عند «من» أو قبله، يمتد إلى ما بعد منتصف الليل، والوقت نفسه يعني طوال اليوم.',
  'shifts.supportOverwrite': 'تغيير ساعات الأيام التي لها ساعات دعم بالفعل',
  'shifts.supportGiven': 'أيام الدعم الممنوحة: {written}، والأيام التي بقيت كما هي: {skipped}.',
  'shifts.supportLegend': '{letter} وضع الدعم',
  'shifts.supportCell': 'وضع الدعم {hours}',
  'shifts.dayShift': 'الوردية',
  'shifts.noShift': 'بلا وردية',
  'shifts.daySupport': 'ساعات دعم في هذا اليوم',
  'shifts.daySupportHint': 'خلال هذه الساعات يستطيع تشغيل وضع الدعم من تطبيق Viganium Team.',
  'shifts.supportNotForThem': 'وضع الدعم لمن يتعاملون مع التذاكر فقط.',
  'shifts.daySaved': 'تم حفظ اليوم.',
  'payroll.adjustmentHint': 'القيمة الموجبة تزيد الصافي، والسالبة تخصم منه.',
  'qtasks.queueTailNote': 'تنضم مهمة الطابور الجديدة إلى آخر الطابور، ويستطيع أي شخص في القسم أخذها فورًا.',
  'tasks.generalHint': 'اتركه فارغًا لمهمة عامة.',
  'tasks.startDate': 'البدء',
  'tasks.deadline': 'الموعد النهائي',
  'tasks.dueAfterStart': 'يجب أن يكون الموعد النهائي بعد موعد البدء.',
  'tasks.fromQTask': 'من مهمة الطابور',
  'tasks.projectMemberHint': 'تعرض القائمة أعضاء هذا المشروع فقط.',
  'tickets.slaStartsNote': 'تبدأ مؤقتات المهل لحظة إنشاء التذكرة.',
  'violations.cancelHint':
    'لا يمكن إلغاء الجزاء بعد دخوله كشف الرواتب. سجّل تسوية بدلًا من ذلك (§13.4).',
  'violations.voidHint': 'إلغاء المخالفة يلغي أيضًا أي جزاء لم يدخل كشف الرواتب بعد (§11.6).',
  'projects.clientLocked':
    'لهذا المشروع فواتير صدرت باسم هذا العميل، لذلك لا يمكن تغيير عميله.',
  'clients.statement': 'كشف الحساب',
  'clients.noneYet': 'لا يوجد عملاء بعد',
  'clients.noneYetHint': 'كل فاتورة لها عميل، لذلك ابدأ من هنا.',

  // Invoices & payments (CR-001)
  'invoices.client': 'العميل',
  'invoices.project': 'المشروع',
  'invoices.noProject': 'بدون مشروع',
  'invoices.projectHint': 'تظهر مشاريع هذا العميل فقط.',
  'invoices.pickClientFirst': 'اختر العميل أولًا',
  'invoices.items': 'بنود الفاتورة',
  'invoices.addItem': 'إضافة بند',
  'invoices.itemDescription': 'الوصف',
  'invoices.quantity': 'الكمية',
  'invoices.unitPrice': 'سعر الوحدة',
  'invoices.lineTotal': 'إجمالي البند',
  'invoices.discount': 'الخصم',
  'invoices.issue': 'إصدار الفاتورة',
  'invoices.cancel': 'إلغاء الفاتورة',
  'invoices.cancelReason': 'ما سبب إلغاء هذه الفاتورة؟',
  'invoices.estimatedRate': 'سعر الصرف التقديري',
  'invoices.estimatedLocal': 'القيمة التقديرية بالعملة الأساسية',
  'invoices.estimateHint': 'قيمة تقديرية وقت الإصدار، أما الإيراد الفعلي فنحسبه من المدفوعات.',
  'invoices.collectedLocal': 'المحصّل فعليًا',
  'invoices.currencyDiffers': 'تصدر فواتير هذا العميل عادةً بـ {currency}.',
  'invoices.clientCurrency': 'عملة العميل',
  'invoices.currencyOverridden': 'مختلفة',
  'invoices.invoiceCurrency': 'عملة الفاتورة',
  'invoices.rateOn': 'السعر مقابل {base} بتاريخ {date}',
  'invoices.rateHint': '1 {code} = ؟ {base}. نحفظه مع الفاتورة، فلا يتغير إذا تغيّر السعر لاحقًا.',
  'invoices.noRate': 'لا يوجد سعر صرف مسجّل، لذلك ليست لهذه الفاتورة قيمة بالعملة الأساسية.',
  'invoices.draftNotice': 'هذه الفاتورة مسودة. أصدرها قبل تسجيل أي دفعة.',
  'invoices.outstandingOnly': 'التي عليها رصيد',
  'invoices.byCurrency': 'حسب العملة',

  'payments.number': 'رقم الدفعة',
  'payments.exchangeRate': 'سعر الصرف',
  'payments.exchangeRateSource': 'مصدر السعر',
  'payments.localAmount': 'المبلغ بالعملة الأساسية',
  'payments.rateHint': 'السعر الساري يوم الدفع. نحفظه مع الدفعة ولا نعيد حسابه أبدًا.',
  'payments.proof': 'إثبات الدفع',
  'payments.reverse': 'عكس الدفعة',
  'payments.reverseReason': 'ما سبب عكس هذه الدفعة؟',
  'payments.reversed': 'معكوسة',
  'payments.includeReversed': 'إظهار المعكوسة',
  'payments.alreadyPaid': 'المدفوع سابقًا',
  'payments.invoiceTotal': 'إجمالي الفاتورة',
  'payments.collectedTotal': 'المحصّل بالعملة الأساسية',

  // Data migration (CR-001 §50–§53)
  'migration.title': 'ترحيل البيانات',
  'migration.subtitle': 'فواتير ومدفوعات أقدم من إعادة تنظيم النظام حول العملاء.',
  'migration.complete': 'لم يعد هناك شيء للترحيل',
  'migration.completeHint': 'كل فاتورة لها عميل، وكل دفعة لها فاتورة.',
  'migration.unlinkedInvoices': 'فواتير غير مرتبطة',
  'migration.unlinkedPayments': 'مدفوعات غير مرتبطة',
  'migration.reason': 'سبب وجودها هنا',
  'migration.restore': 'ربط واستعادة',
  'migration.restoreInvoice': 'استعادة الفاتورة',
  'migration.restorePayment': 'استعادة الدفعة',
  'migration.discard': 'استبعاد',
  'migration.discardReason': 'ما سبب استبعاد هذا السجل بدلًا من استعادته؟',
  'migration.originalInvoice': 'الفاتورة الأصلية',
  'migration.pickInvoice': 'أي فاتورة تسدّدها هذه الدفعة؟',
  'migration.restoreFirst': 'استعد فاتورتها أولًا.',
  'migration.noFakeData': 'لا نربط أي شيء تلقائيًا، لأن العميل الوهمي يخفي المشكلة ولا يحلّها.',

  // Finance reports (CR-001 §38)
  'reports.revenue': 'الإيرادات',
  'reports.receivables': 'المستحقات',
  'reports.byClient': 'حسب العميل',
  'reports.byProject': 'حسب المشروع',
  'reports.byCountry': 'حسب الدولة',
  'reports.byCurrency': 'حسب العملة',
  'reports.byPeriod': 'حسب الفترة',
  'reports.aging': 'أعمار الديون',
  'reports.agingCurrent': 'جارية',
  'reports.aging1_30': '1–30 يومًا',
  'reports.aging31_60': '31–60 يومًا',
  'reports.aging61_90': '61–90 يومًا',
  'reports.aging90': 'أكثر من 90 يومًا',
  'reports.outstandingInvoices': 'الفواتير غير المسددة',
  'reports.daysOverdue': 'أيام التأخير',
  'reports.clientStatement': 'كشف حساب العميل',
  'reports.balance': 'الرصيد',
  'reports.debit': 'مدين',
  'reports.credit': 'دائن',
  'reports.collections': 'التحصيلات',
  'reports.collectionsThisMonth': 'المحصّل هذا الشهر',
  'reports.baseCurrency': 'العملة الأساسية',
  'reports.inBaseCurrency': 'كل المبالغ بـ {currency}',
  'reports.billedIn': 'عملة الفوترة',
  'reports.sourceAmount': 'المبلغ الأصلي',
  'reports.rate': 'سعر الصرف',
  'reports.noRateWarning':
    'لا يوجد سعر صرف لـ {currencies}، لذلك لم تدخل فواتيرها في هذه الإجماليات. أضف سعرًا من «الإعدادات > العملات».',
  'reports.invoiced': 'إجمالي الفواتير',
  'reports.collected': 'المحصّل',
  'reports.outstanding': 'المتبقي',

  'invoices.title': 'الفواتير',
  'invoices.new': 'فاتورة جديدة',
  'invoices.counterparty': 'الطرف الآخر',
  'invoices.issueDate': 'تاريخ الإصدار',
  'invoices.dueDate': 'موعد التسليم',
  'invoices.subtotal': 'المجموع الفرعي',
  'invoices.tax': 'الضريبة',
  'invoices.paid': 'المدفوع',
  'invoices.remaining': 'المتبقي',
  'invoices.recordPayment': 'تسجيل دفعة',
  'payments.title': 'المدفوعات',
  'payments.new': 'تسجيل دفعة',
  'payments.method': 'طريقة الدفع',
  'payments.reference': 'المرجع',
  'budgets.title': 'الميزانيات',
  'budgets.new': 'ميزانية جديدة',
  'budgets.planned': 'المخطط',
  'budgets.actual': 'الفعلي',
  'budgets.remaining': 'المتبقي',
  'budgets.used': 'مستخدم',
  'budgets.overBudget': 'تجاوز الميزانية',
  'budgets.thresholdHit': 'تم بلوغ حد التنبيه',
  'budgets.scope': 'النطاق',
  'budgets.threshold': 'حد التنبيه',

  'violations.title': 'المخالفات والجزاءات',
  'violations.violations': 'المخالفات',
  'violations.penalties': 'الجزاءات',
  'violations.type': 'النوع',
  'violations.deadline': 'الموعد النهائي',
  'violations.actualAction': 'الإجراء الفعلي',
  'violations.void': 'إلغاء المخالفة',
  'violations.voidReason': 'ما سبب إلغاء هذه المخالفة؟',
  'violations.cancel': 'إلغاء الجزاء',
  'violations.effectiveMonth': 'شهر الراتب',
  'violations.byEmployee': 'حسب الموظف',
  'violations.appliedToPayroll': 'دخل كشف الرواتب',

  'reports.title': 'التقارير',
  'reports.operations': 'العمليات',
  'reports.workforce': 'القوى العاملة',
  'reports.finance': 'المالية',
  'reports.slaCompliance': 'الالتزام بالمهل',
  'reports.onTime': 'في الموعد',
  'reports.late': 'متأخر',
  'reports.avgViewDelay': 'متوسط زمن أول اطلاع',
  'reports.qThroughput': 'إنتاجية مهام الطابور',
  'reports.blockedTime': 'متوسط زمن التعليق',
  'reports.overtimeByEmployee': 'العمل الإضافي لكل موظف',
  'reports.penaltiesByEmployee': 'الجزاءات لكل موظف',
  'reports.overdueTasks': 'المهام المتأخرة',

  'settings.title': 'الإعدادات',
  'settings.company': 'الشركة',
  'settings.tickets': 'التذاكر',
  'settings.sla': 'المهل والجزاءات',
  'settings.shifts': 'الورديات',
  'settings.payroll': 'الرواتب',
  'settings.finance': 'المالية',
  'settings.security': 'الأمان',
  'settings.projects': 'المشاريع',
  'settings.jobs': 'العمليات الخلفية',
  'settings.currencies': 'العملات',
  'settings.saved': 'تم حفظ الإعدادات.',
  'settings.slaNote':
    'هذه هي القيم الافتراضية للشركة. ويمكن تغييرها للقسم، ثم للموظف، بهذا الترتيب.',

  'currencies.title': 'العملات وأسعار الصرف',
  'currencies.note':
    'أوقف العملات التي لا نتعامل بها أبدًا، فتختفي من كل قوائم اختيار العملة. السعر هنا مجرد اقتراح عند إنشاء فاتورة أو دفعة جديدة. وكل دفعة تحتفظ بالسعر المحفوظ معها، لذلك تعديل السعر هنا لا يغيّر أي مبلغ مسجّل سابقًا.',
  'currencies.currency': 'العملة',
  'currencies.inUse': 'قيد الاستخدام',
  'currencies.rateTo': 'السعر مقابل {base}',
  'currencies.rateHint': '1 {code} = ؟ {base}',
  'currencies.rateUpdated': 'آخر تحديث للسعر',
  'currencies.base': 'العملة الأساسية',
  'currencies.search': 'ابحث برمز العملة أو اسمها',
  'currencies.inUseCount': '{count} قيد الاستخدام',
  'currencies.missingRate': 'لا يوجد سعر بعد',
  'currencies.saved': 'تم حفظ العملات.',
  'currencies.noneMatch': 'لا توجد عملة تطابق البحث.',
  'currencies.switchAllOff': 'إيقاف الكل',

  'audit.title': 'سجل التدقيق',
  'audit.actor': 'المستخدم',
  'audit.action': 'الإجراء',
  'audit.entity': 'العنصر',
  'audit.occurredAt': 'الوقت',
  'audit.changes': 'التغييرات',

  'notifications.title': 'الإشعارات',
  'notifications.markAllRead': 'تعليم الكل كمقروء',
  'notifications.empty': 'لا توجد إشعارات.',
  'notifications.unread': 'غير مقروء',
  'notifications.sound': 'صوت الإشعارات',
  'notifications.mute': 'كتم صوت الإشعارات',
  'notifications.unmute': 'تشغيل صوت عند وصول إشعار',
  'notifications.noPage': 'لا توجد صفحة لفتحها',
  'notifications.desktop': 'إشعارات سطح المكتب',
  'notifications.desktopTurnOn': 'إظهار الإشعارات على سطح المكتب أيضًا',
  'notifications.desktopTurnOff': 'إشعارات سطح المكتب مفعّلة، اضغط لإيقافها',
  'notifications.desktopBlocked': 'إشعارات سطح المكتب محظورة في هذا المتصفح',
  'notifications.desktopBlockedHint':
    'يحظر هذا المتصفح إشعارات هذا الموقع. اسمح بها من إعدادات الموقع (الرمز بجوار العنوان)، ثم أعد تحميل الصفحة.',
  'notifications.desktopPageOnly': 'هذا المتصفح لا يدعم الإشعارات الفورية، لذلك تظهر فقط عندما تكون صفحة من النظام مفتوحة.',
  'notifications.desktopPromptTitle': 'استقبل الإشعارات على سطح المكتب',
  'notifications.desktopPromptBody':
    'تظهر الإشعارات الجديدة أيضًا في مركز الإشعارات بجهازك، حتى عندما تكون هذه الصفحة في الخلفية أو مغلقة، ما دام المتصفح يعمل.',
  'notifications.desktopPromptOn': 'تفعيل',
  'notifications.desktopPromptLater': 'ليس الآن',
  'notifications.desktopSampleTitle': 'إشعارات سطح المكتب مفعّلة',
  'notifications.desktopSampleBody': 'ستظهر الإشعارات الجديدة هنا ما دامت صفحة من النظام مفتوحة.',

  'systems.admin': 'الإدارة',
  'systems.sys': 'المالية',
  'audit.app': 'النظام',
  'audit.adminSubtitle':
    'كل تغيير في العمليات والموظفين والإعدادات: من أجراه، ومتى، ومن أي نظام. وللمالية سجل تدقيق خاص بها للأموال والرواتب وصلاحيات الدخول إليها.',
  'employees.sysAccess': 'صلاحية الدخول إلى المالية',
  'employees.noSysAccess': 'ليست لديه صلاحية الدخول إلى المالية',

  // Attendance, leave, email — 2026-09-30
  'nav.attendance': 'الحضور',
  'nav.leaves': 'الإجازات',
  'nav.overtime': 'العمل الإضافي',
  'nav.remoteWork': 'العمل عن بُعد',
  'common.employee': 'الموظف',
  'common.minutesShort': 'د',
  'attendance.title': 'الحضور والانصراف',
  'attendance.myShift': 'ورديتي',
  'attendance.team': 'الفريق',
  'attendance.offDuty': 'خارج الدوام',
  'attendance.working': 'في العمل',
  'attendance.onBreak': 'في استراحة',
  'attendance.startShift': 'بدء الوردية',
  'attendance.endShift': 'إنهاء الوردية',
  'attendance.takeBreak': 'أخذ استراحة',
  'attendance.resume': 'العودة إلى العمل',
  'attendance.since': 'منذ {time}',
  'attendance.promptTitle': 'لم تبدأ ورديتك بعد',
  'attendance.promptBody': 'ابدأها عندما تبدأ العمل، وأوقفها مؤقتًا في الاستراحة، ثم اضغط «إنهاء الوردية» عند الانصراف.',
  'attendance.promptRoster': 'جدولك اليوم: {shift}، {start}–{end}.',
  'attendance.notNow': 'ليس الآن',
  'attendance.staleNote': 'لم يتم إنهاء ورديتك السابقة. عند بدء وردية جديدة، سنغلقها عند آخر نشاط لك على جهازك.',
  'attendance.today': 'اليوم',
  'attendance.worked': 'مدة العمل',
  'attendance.breaks': 'الاستراحات',
  'attendance.overrun': 'تجاوز المسموح',
  'attendance.late': 'تأخير',
  'attendance.leftEarly': 'انصراف مبكر',
  'attendance.started': 'البداية',
  'attendance.ended': 'النهاية',
  'attendance.roster': 'الجدول',
  'attendance.noRoster': 'لا شيء في الجدول',
  'attendance.history': 'الورديات الأخيرة',
  'attendance.noShiftsYet': 'لا توجد ورديات مسجلة بعد.',
  'attendance.autoClosed': 'تم إغلاقها تلقائيًا',
  'attendance.closedBy': 'أنهاها {name}',
  'attendance.endUnconfirmed': 'النهاية غير مؤكدة',
  'attendance.setEnd': 'حدّد النهاية',
  'attendance.setEndFor': 'حدّد نهاية وردية {name}',
  'attendance.setEndNote':
    'لم يقم الموظف بإنهائها ولم يتتبعها التطبيق المكتبي، لذلك تم إغلاقها عند نهايتها في الجدول. حدّد متى انتهت فعلًا، وبعدها نقارن ذلك اليوم بساعاته المطلوبة.',
  'attendance.setEndAtHint': 'بعد بدايتها، وفي موعد أقصاه بداية ورديته التالية.',
  'attendance.setEndReason': 'كيف عرفت ذلك؟ سيقرؤه الموظف',
  'attendance.endSet': 'تم تحديد النهاية.',
  'attendance.stillOpen': 'ما زالت مستمرة',
  'attendance.endFor': 'إنهاء وردية {name}',
  'attendance.endReason': 'ما سبب إنهائها؟',
  'attendance.endedAt': 'وقت الانتهاء',
  'attendance.endedAtHint': 'الوقت الذي توقف فيه فعلًا. اتركه فارغًا لتكون النهاية الآن.',
  'attendance.endNote': 'سنبلغ الموظف بمن أنهى ورديته ولماذا.',
  'attendance.day': 'اليوم',
  'attendance.allDepartments': 'كل الأقسام',
  'attendance.holiday': 'عطلة: {name}',
  'attendance.noBreakTypes': 'لا توجد أنواع استراحات مفعّلة. أضفها من «الإعدادات › الحضور».',
  'attendance.breakLeadersOnly': 'هذه الاستراحة مخصصة لقادة الفرق ومديري النظام.',
  'attendance.breakAdminsOnly': 'هذه الاستراحة مخصصة لمديري النظام.',
  'attendance.breakDepartmentsOnly': 'هذه الاستراحة مخصصة لأقسام أخرى.',
  'attendance.breakEmployeesOnly': 'هذه الاستراحة مخصصة للموظفين المختارين لها.',
  'attendance.breakDailyLimit': 'استنفدت عدد المرات المسموح بها لهذه الاستراحة اليوم.',
  'attendance.shiftStarted': 'بدأت الوردية.',
  'attendance.breakStarted': 'استمتع باستراحتك.',
  'attendance.backToWork': 'مرحبًا بعودتك.',
  'attendance.shiftEnded': 'انتهت الوردية.',
  'attendance.regularShift': 'الوردية الأساسية',
  'attendance.freeShift': 'وردية حرة',
  'attendance.freeShifts': 'الورديات الحرة',
  'attendance.freeShiftNumber': 'وردية حرة رقم {n}',
  'attendance.startFree': 'بدء وردية حرة',
  'attendance.endFree': 'إنهاء الوردية الحرة',
  'attendance.freeStarted': 'بدأت الوردية الحرة.',
  'attendance.freeEnded': 'انتهت الوردية الحرة.',
  'attendance.freeSince': 'وردية حرة منذ {time}',
  'attendance.freeUnpaid': 'مسجّلة بلا أجر، ولا تدخل أبدًا في ساعات يومك أو العمل الإضافي أو الجزاءات أو الراتب.',
  'attendance.regularPaid': 'مدفوعة — ساعات يومك',
  'attendance.freeNoBreaks': 'لا توجد استراحات في الوردية الحرة، فأنهها وابدأ وردية أخرى عندما تعود.',
  'attendance.regularDone': 'انتهت ورديتك الأساسية لليوم',
  'attendance.regularDoneBody':
    'الوردية الأساسية مرة واحدة في كل يوم عمل، وتصبح ورديتك التالية متاحة في {time}. حتى ذلك الوقت يمكنك العمل في ورديات حرة، وهي مسجّلة بلا أجر ولا تدخل في ساعاتك أبدًا.',
  'attendance.regularDoneBodyNoTime':
    'الوردية الأساسية مرة واحدة في كل يوم عمل، وتصبح ورديتك التالية متاحة مع يوم عملك التالي. حتى ذلك الوقت يمكنك العمل في ورديات حرة، وهي مسجّلة بلا أجر ولا تدخل في ساعاتك أبدًا.',
  'attendance.nextRegularAt': 'الوردية الأساسية التالية في {time}',
  'attendance.trackedTotal': 'إجمالي الوقت المسجّل',
  'attendance.trackedTotalHint': 'الأساسية والحرة معًا — للاطلاع فقط',
  'attendance.freeTotal': 'الورديات الحرة: {n}',
  'attendance.freeOne': 'وردية حرة واحدة',
  'attendance.freeMinutesShort': '+ حرة {minutes}',
  'attendance.inFreeShift': 'في وردية حرة',
  'attendance.freeShiftNow': 'في وردية حرة · {n}',
  'attendance.freeAfter': 'بعد الوردية الأساسية ليوم {date}',
  'attendance.freeShiftsAfter': 'الورديات الحرة بعدها',
  'attendance.freeShiftsAfterHint': 'مسجّلة بلا أجر، ولا يدخل شيء منها في مدة عمل هذه الوردية ولا في ساعات اليوم.',
  'attendance.freeSoFar': 'حتى الآن',
  'attendance.startConfirmOnce': 'الوردية الأساسية مرة واحدة في كل يوم عمل: إذا أنهيتها فلن تستطيع أن تبدأها مرة ثانية اليوم.',
  'attendance.startFreeConfirm': 'بدء وردية حرة؟',
  'attendance.startFreeConfirmBody':
    'الوردية الحرة عمل بلا أجر بعد ورديتك الأساسية. يتابعها التطبيق المكتبي مثل أي وردية، لكنها لا تدخل أبدًا في ساعات يومك أو العمل الإضافي أو الجزاءات أو الراتب، ولا توجد فيها استراحات.',
  'attendance.endFreeConfirm': 'إنهاء الوردية الحرة؟',
  'attendance.endFreeConfirmBody': 'يمكنك بدء وردية حرة أخرى لاحقًا، حتى تصبح ورديتك الأساسية التالية متاحة.',
  'attendance.regularTaken': 'سبق أن بدأت ورديتك الأساسية لليوم. يمكنك بدء وردية حرة، أو انتظار ورديتك الأساسية التالية.',
  'attendance.activeSessionExists': 'لديك وردية جارية بالفعل. أنهها قبل أن تبدأ وردية أخرى.',
  'attendance.freeNeedsRegular': 'تبدأ الوردية الحرة بعد انتهاء ورديتك الأساسية لليوم.',
  'attendance.freeWindowPassed': 'ورديتك الأساسية التالية متاحة الآن، فابدأها أولًا، ثم يمكنك بدء وردية حرة بعد انتهائها.',
  'attendance.wrongSessionType': 'هذا الإجراء كان لنوع آخر من الورديات، وتم تحديث أدوات ورديتك الآن.',
  'attendance.sessionAlreadyEnded': 'انتهت هذه الوردية بالفعل.',
  'attendance.endFreeFor': 'إنهاء الوردية الحرة لـ{name}',
  'attendance.endConfirm': 'إنهاء ورديتك الآن؟',
  'attendance.status.WORKING': 'في العمل',
  'attendance.status.ON_BREAK': 'في استراحة',
  'attendance.status.ENDED': 'أنهى يومه',
  'attendance.status.ON_LEAVE': 'في إجازة',
  'attendance.status.OFF': 'يوم راحة',
  'attendance.status.UPCOMING': 'لم يحن موعده',
  'attendance.status.NOT_STARTED': 'متأخر — لم يبدأ',
  'attendance.status.ABSENT': 'غائب',
  'attendance.status.NO_SCHEDULE': 'غير مدرج في الجدول',
  'leaves.title': 'الإجازات',
  'leaves.new': 'طلب إجازة',
  'leaves.mine': 'طلباتي',
  'leaves.review': 'بانتظار قراري',
  'leaves.all': 'كل الطلبات',
  'leaves.type': 'نوع الإجازة',
  'leaves.dates': 'التواريخ',
  'leaves.from': 'أول يوم',
  'leaves.to': 'آخر يوم',
  'leaves.days': 'الأيام',
  'leaves.workingDays': 'أيام العمل: {n}',
  'leaves.daysHint': 'العطلات وأيام راحتك في الجدول لا تدخل في الحساب.',
  'leaves.balance': 'الرصيد {year}',
  'leaves.allowance': 'المستحق',
  'leaves.used': 'المعتمد',
  'leaves.pending': 'قيد الانتظار',
  'leaves.remaining': 'المتبقي',
  'leaves.noAllowance': 'بلا رصيد سنوي',
  'leaves.paid': 'مدفوعة',
  'leaves.unpaid': 'غير مدفوعة',
  'leaves.overAllowance': 'يتجاوز هذا الطلب الرصيد بمقدار {n}.',
  'leaves.cancelRequest': 'إلغاء الطلب',
  'leaves.rejectTitle': 'رفض طلب الإجازة',
  'leaves.rejectReason': 'لماذا الرفض؟ سيقرأ الموظف ما تكتبه.',
  'leaves.reviewedBy': 'صاحب القرار',
  'leaves.requested': 'تم إرسال طلب الإجازة.',
  'leaves.approved': 'تمت الموافقة على الإجازة.',
  'leaves.rejected': 'تم رفض الإجازة.',
  'leaves.cancelled': 'تم إلغاء الطلب.',
  'leaves.none': 'لا توجد طلبات إجازة',
  'leaves.noneHint': 'اطلب إجازتك من هنا، والقرار لقائد قسمك أو للموارد البشرية.',
  'leaves.nothingToReview': 'لا شيء بانتظار قرارك.',
  'leaves.slaNote': 'بعد الموافقة لن تكون مسؤولًا عن التذاكر التي تصل في تلك الأيام.',
  'leaves.send': 'إرسال الطلب',
  'leaves.details': 'الطلب',
  'leaves.asked': 'تاريخ الطلب',
  'leaves.cancelledOn': 'تاريخ الإلغاء',
  'leaves.note': 'ملاحظة',
  'leaves.noteFrom': 'ملاحظة من {name}',
  'overtime.title': 'العمل الإضافي',
  'overtime.subtitle': 'لا أجر إلا للعمل الإضافي المطلوب: يرسله قائد القسم، ويوافق عليه مدير النظام، ثم يقبله الموظف.',
  'overtime.new': 'إرسال عمل إضافي',
  'overtime.team': 'ما أرسلته وفريقي',
  'overtime.review': 'بانتظار موافقتي',
  'overtime.mine': 'عملي الإضافي',
  'overtime.status.PENDING': 'بانتظار الموافقة',
  'overtime.status.APPROVED': 'بانتظار الموظف',
  'overtime.statusMine.APPROVED': 'بانتظار ردّك',
  'overtime.status.ACCEPTED': 'مقبول',
  'overtime.status.REJECTED': 'مرفوض',
  'overtime.status.DECLINED': 'رفضه الموظف',
  'overtime.status.CANCELLED': 'ملغى',
  'overtime.approvedToast': 'تمت الموافقة على العمل الإضافي، وهو الآن بانتظار رد الموظف.',
  'overtime.acceptedToast': 'تم قبول العمل الإضافي.',
  'overtime.cancelledToast': 'تم إلغاء العمل الإضافي.',
  'overtime.rejectedToast': 'تم رفض العمل الإضافي.',
  'overtime.declinedToast': 'تم رفض العمل الإضافي.',
  'overtime.sentForApproval': 'تم إرسال العمل الإضافي، وهو بانتظار موافقة مدير النظام.',
  'overtime.sentToEmployee': 'تم إرسال العمل الإضافي إلى {name} لقبوله أو رفضه.',
  'overtime.nothingToReview': 'لا يوجد عمل إضافي بانتظار موافقتك.',
  'overtime.none': 'لا يوجد عمل إضافي',
  'overtime.noneMine': 'يظهر هنا العمل الإضافي المطلوب منك بعد موافقة مدير النظام عليه، ثم تقرر أنت قبوله أو رفضه.',
  'overtime.noneTeam': 'أرسل عملًا إضافيًا لأحد أفراد فريقك: يوافق عليه مدير النظام، ثم يقبله الموظف أو يرفضه.',
  'overtime.day': 'اليوم',
  'overtime.dayHint': 'أي يوم، سابق أو قادم.',
  'overtime.upTo': 'الحد الأقصى',
  'overtime.upToTime': 'بحد أقصى {time}',
  'overtime.upToHint': 'بحد أقصى {time}. ندفع في الرواتب فقط وقت العمل الزائد على ساعات ذلك اليوم، في حدود هذه المدة.',
  'overtime.durationRange': 'بين دقيقة واحدة و12 ساعة.',
  'overtime.hours': 'الساعات',
  'overtime.minutes': 'الدقائق',
  'overtime.reasonHint': 'سيقرؤه مدير النظام والموظف.',
  'overtime.pays': 'المدفوع {time}',
  'overtime.paysLater': 'الدفع بعد احتساب اليوم',
  'overtime.paysLabel': 'المدفوع',
  'overtime.sentBy': 'أرسله',
  'overtime.approvedBy': 'وافق عليه',
  'overtime.rejectedBy': 'رفضه',
  'overtime.acceptedOn': 'قبله الموظف',
  'overtime.declinedOn': 'رفضه الموظف',
  'overtime.cancelledBy': 'ألغاه',
  'overtime.accept': 'قبول',
  'overtime.decline': 'رفض',
  'overtime.cancel': 'إلغاء العمل الإضافي',
  'overtime.on': 'عمل إضافي يوم {date}',
  'overtime.answerNote': 'هذا العمل الإضافي بانتظار ردّك. إذا قبلته، ندفع لك في الرواتب وقت عملك الزائد على ساعات ذلك اليوم، في حدود المدة المذكورة أعلاه.',
  'overtime.monthSignedOff': 'تم اعتماد رواتب ذلك الشهر، فلا يمكن دفع عمل إضافي فيه الآن.',
  'overtime.note': 'ملاحظة',
  'overtime.noteFrom': 'ملاحظة من {name}',
  'overtime.details': 'العمل الإضافي',
  'overtime.theDay': 'اليوم',
  'overtime.theDayNote': 'أجر العمل الإضافي عن وقت العمل الزائد على ساعات اليوم.',
  'overtime.dayIs': 'الجدول',
  'overtime.owes': 'الساعات المطلوبة',
  'overtime.worked': 'مدة العمل',
  'overtime.beyond': 'الزائد على ساعاته',
  'overtime.notJudged': 'لم يتم احتساب اليوم بعد، لأنه لم ينته أو لأن إحدى وردياته ما زالت مفتوحة.',
  'overtime.dayKind.SHIFT': '{shift} — ساعاتها المعيارية',
  'overtime.dayKind.DEFAULT': 'بلا وردية — الساعات الافتراضية',
  'overtime.dayKind.OFF': 'يوم راحة — لا ساعات مطلوبة',
  'overtime.dayKind.LEAVE': 'في إجازة — لا ساعات مطلوبة',
  'overtime.dayKind.HOLIDAY': 'عطلة — لا ساعات مطلوبة',
  'overtime.dayKind.HOLIDAY_NAMED': '{name} — لا ساعات مطلوبة',
  'overtime.dayKind.NONE': 'لا ساعات مطلوبة',
  'overtime.sendNote': 'يوافق عليه مدير النظام، ثم يقبله الموظف أو يرفضه. ندفع في الرواتب فقط وقت العمل الزائد على ساعات ذلك اليوم، في حدود المدة التي تحددها هنا.',
  'overtime.sendNoteApprover': 'لديك صلاحية الموافقة على العمل الإضافي، لذلك يصل هذا مباشرة إلى الموظف ليقبله أو يرفضه. ندفع في الرواتب فقط وقت العمل الزائد على ساعات ذلك اليوم، في حدود المدة التي تحددها هنا.',
  'overtime.send': 'إرسال',
  'overtime.noPeople': 'ترسل العمل الإضافي لأفراد الأقسام التي تقودها فقط.',
  'overtime.exists': 'يوجد بالفعل عمل إضافي بمقدار {time} في ذلك اليوم ({status}). ألغه أولًا لترسل غيره.',
  'overtime.rejectTitle': 'رفض العمل الإضافي',
  'overtime.rejectReason': 'لماذا الرفض؟ سيقرأ من أرسله ما تكتبه.',
  'overtime.declineTitle': 'رفض العمل الإضافي',
  'overtime.declineReason': 'هل تريد إضافة شيء؟ سيقرأ من أرسله ما تكتبه.',
  'overtime.waitingCount': 'عمل إضافي بانتظارك: {n}',
  'remoteWork.title': 'العمل عن بُعد',
  'remoteWork.subtitle': 'لمن يعملون من المكتب: يوافق قائد الفريق أولًا، ثم مدير النظام.',
  'remoteWork.new': 'طلب عمل عن بُعد',
  'remoteWork.mine': 'طلباتي',
  'remoteWork.review': 'بانتظار موافقتي',
  'remoteWork.all': 'كل الطلبات',
  'remoteWork.status.PENDING_LEADER': 'بانتظار قائد الفريق',
  'remoteWork.status.PENDING_ADMIN': 'بانتظار مدير النظام',
  'remoteWork.status.APPROVED': 'تمت الموافقة',
  'remoteWork.status.REJECTED': 'مرفوض',
  'remoteWork.status.CANCELLED': 'ملغى',
  'remoteWork.dates': 'التواريخ',
  'remoteWork.from': 'اليوم الأول',
  'remoteWork.to': 'اليوم الأخير',
  'remoteWork.days': 'أيام العمل',
  'remoteWork.workingDays': 'أيام العمل: {n}',
  'remoteWork.daysHint': 'العطلات وأيام راحتك في الجدول لا تدخل في الحساب.',
  'remoteWork.noWorkingDays': 'كل أيام هذه الفترة عطلات أو أيام راحة، فلا توجد أيام للعمل عن بُعد.',
  'remoteWork.reasonHint': 'سيقرؤه قائد فريقك ومدير النظام.',
  'remoteWork.send': 'إرسال الطلب',
  'remoteWork.requested': 'تم إرسال طلب العمل عن بُعد.',
  'remoteWork.approvedLeaderToast': 'تمت الموافقة، والطلب الآن بانتظار مدير النظام.',
  'remoteWork.approvedToast': 'تمت الموافقة على العمل عن بُعد.',
  'remoteWork.rejectedToast': 'تم رفض طلب العمل عن بُعد.',
  'remoteWork.cancelledToast': 'تم إلغاء الطلب.',
  'remoteWork.cancel': 'إلغاء الطلب',
  'remoteWork.none': 'لا توجد طلبات عمل عن بُعد',
  'remoteWork.noneHint': 'اطلب العمل عن بُعد ليوم أو أكثر، ويوافق عليه قائد فريقك أولًا، ثم مدير النظام.',
  'remoteWork.nothingToReview': 'لا شيء بانتظار موافقتك.',
  'remoteWork.atRemote': 'أنت تعمل عن بُعد بالفعل، وطلب العمل عن بُعد لمن يعملون من المكتب فقط.',
  'remoteWork.whoApproves': 'من يوافق',
  'remoteWork.whoLeader': 'قائد فريقك — {names}',
  'remoteWork.whoAdmin': 'ثم مدير النظام',
  'remoteWork.whoNoLeader': 'لا يوجد قائد فريق لقسمك، فيوافق مدير النظام على الطلب.',
  'remoteWork.overlaps': 'لديك طلب لهذه الأيام بالفعل: {dates} ({status}).',
  'remoteWork.details': 'الطلب',
  'remoteWork.asked': 'تاريخ الطلب',
  'remoteWork.cancelledBy': 'ألغاه',
  'remoteWork.steps': 'الموافقات',
  'remoteWork.stepsNote': 'واحدة بعد الأخرى: قائد الفريق أولًا، ثم مدير النظام.',
  'remoteWork.step.LEADER': 'قائد الفريق',
  'remoteWork.step.ADMIN': 'مدير النظام',
  'remoteWork.stepApproved': 'وافق عليه {name}',
  'remoteWork.stepRejected': 'رفضه {name}',
  'remoteWork.stepWaiting': 'قيد الانتظار',
  'remoteWork.stepWaitingFor': 'بانتظار {names}',
  'remoteWork.stepAfterLeader': 'بعد موافقة قائد الفريق',
  'remoteWork.stepSkipped': 'لا يوجد قائد فريق للموافقة',
  'remoteWork.stepNotNeeded': 'غير مطلوبة',
  'remoteWork.stepWithdrawn': 'تم سحب الطلب',
  'remoteWork.decideAsLeader': 'توافق على هذا الطلب بصفتك قائد الفريق، ثم يعطي مدير النظام الموافقة الثانية بعدك.',
  'remoteWork.decideAsAdmin': 'وافق قائد الفريق على الطلب، والموافقة الثانية لك بصفتك مدير النظام.',
  'remoteWork.decideAsAdminToo': 'وافقت عليه بصفتك قائد الفريق. وبما أنك مدير النظام أيضًا، فالموافقة الثانية لك كذلك.',
  'remoteWork.decideNoLeader': 'لم يكن هناك قائد فريق للموافقة، فوصل الطلب مباشرة إلى مديري النظام.',
  'remoteWork.rejectTitle': 'رفض طلب العمل عن بُعد',
  'remoteWork.rejectReason': 'لماذا الرفض؟ سيقرأ الموظف ما تكتبه.',
  'remoteWork.rejectAsLeader': 'ترفضه بصفتك قائد الفريق.',
  'remoteWork.rejectAsAdmin': 'ترفضه بصفتك مدير النظام.',
  'remoteWork.waitingCount': 'طلبات عمل عن بُعد بانتظار موافقتك: {n}',
  'remoteWork.todayByRequest': 'عن بُعد اليوم — بطلب موافق عليه',
  'settings.attendance': 'الحضور',
  'settings.leave': 'الإجازات',
  'settings.email': 'البريد الإلكتروني',
  'settings.companyName': 'اسم الشركة',
  'settings.companyNameAr': 'اسم الشركة بالعربية',
  'settings.domain': 'نطاق النظام',
  'settings.domainHint': 'نستخدمه داخل الشركة: تحته عناوين admin وapi وsys وclients، وإليه تشير الروابط في رسائلنا.',
  'settings.clientDomain': 'النطاق الظاهر للعملاء',
  'settings.clientDomainHint': 'النطاق الذي يعرفنا به عملاؤنا، ويظهر مطبوعًا على الفواتير وإيصالات الدفع.',
  'settings.timezone': 'المنطقة الزمنية',
  'settings.timezoneHint': 'عليها تعتمد أوقات كل الورديات ومواعيد انتهاء المهل.',
  'settings.weekStart': 'بداية الأسبوع',
  'settings.viewSlaMinutes': 'مهلة الاطلاع (بالدقائق)',
  'settings.viewSlaHint': 'إذا لم يفتح أحد التذكرة خلال هذه المهلة، نبلّغ قائد القسم ومديري النظام بإشعار وبالبريد الإلكتروني.',
  'settings.assignmentSlaMinutes': 'مهلة الإسناد (بالدقائق)',
  'settings.viewPenaltyDays': 'جزاء عدم الاطلاع',
  'settings.viewPenaltyHint': 'بالأيام من راتب الموظف نفسه، على كل مسؤول عن التذكرة لم يفتحها في الوقت المحدد. 0.25 تعني ربع يوم.',
  'settings.assignmentPenaltyDays': 'جزاء تأخر الإسناد',
  'settings.assignmentPenaltyHint': 'بالأيام من الراتب، على كل مسؤول في الوردية إذا لم يتم إسناد التذكرة في الوقت المحدد.',
  'settings.daysOfSalary': 'يوم من الراتب',
  'settings.penaltiesAreCumulative': 'الجزاءات تراكمية',
  'settings.cumulativeHint': 'عند إيقاف هذا الخيار، من عليه جزاء عدم الاطلاع على تذكرة لا يتحمل جزاء الإسناد عليها أيضًا.',
  'settings.penaltyRequiresApproval': 'الجزاءات تحتاج إلى اعتماد',
  'settings.excludeOffShift': 'المسؤولية على من في الوردية فقط',
  'settings.excludeNotWorking': 'المسؤولية على من يعمل فقط',
  'settings.excludeNotWorkingHint':
    'أي من بدأ ورديته ولم يكن في استراحة عند وصول التذكرة. ومن كان في استراحة أو لم يبدأ ورديته يصله إشعار بها، لكن لا يتحمل أي جزاء عليها أبدًا.',
  'settings.excludeInactive': 'استبعاد الموظفين غير النشطين',
  'settings.excludeAbsence': 'استبعاد من لديهم إجازة معتمدة',
  'settings.excludeAbsenceHint': 'إذا تم اعتمادها قبل وصول التذكرة.',
  'settings.allowSelfAssignment': 'السماح بـ«إسناد إليّ»',
  'settings.warningMinutes': 'تنبيه قبل الموعد (بالدقائق)',
  'settings.numberPrefix': 'بادئة الرقم',
  'settings.defaultPriority': 'الأولوية الافتراضية',
  'settings.lockMinutes': 'مدة القفل المؤقت (بالدقائق)',
  'settings.crossProject': 'الدعم يتعامل مع تذاكر المشاريع دون صلاحية المشروع',
  'settings.crossProjectHint': 'متوقف افتراضيًا: من يتعامل مع تذكرة مشروع يحتاج أيضًا إلى صلاحية المشروع.',
  'settings.defaultDailyHours': 'ساعات العمل اليومية الافتراضية',
  'settings.defaultDailyHoursHint':
    'الساعات المطلوبة في يوم عمل ليست له وردية في الجدول، أو له أوقات خاصة به. أما ساعات كل وردية فهي «الساعات المعيارية» الخاصة بها في «الورديات».',
  'settings.passwordMinLength': 'أقل طول لكلمة المرور',
  'settings.maxUploadMb': 'أكبر حجم للرفع (ميجابايت)',
  'settings.maxUploadMbHint': 'الصور والمستندات والرسائل الصوتية. أما الفيديو فله حد خاص أدناه.',
  'settings.maxVideoUploadMb': 'أكبر حجم للفيديو (ميجابايت)',
  'settings.maxVideoUploadMbHint':
    'حتى 2000. يتم رفع الفيديو مباشرة إلى Cloudflare R2، أما إذا كانت الملفات محفوظة على هذا الخادم بدلًا من ذلك، فحد الرفع الخاص بالخادم يسري على النوعين.',
  'settings.promptOnLogin': 'تذكير الجميع ببدء الوردية عند الدخول',
  'settings.promptOnLoginHint': 'تظهر أدوات الوردية أعلى كل صفحة حتى يبدأ الموظف ورديته.',
  'settings.maxShiftHours': 'أطول وردية ممكنة (بالساعات)',
  'settings.maxShiftHoursHint':
    'إذا بقيت الوردية مفتوحة أكثر من ذلك، نعتبر أن صاحبها نسي إنهاءها. عند بدء الوردية التالية نغلقها عند آخر نشاط سجّله التطبيق المكتبي، وإذا لم يكن هناك تطبيق فعند نهايتها في الجدول، حتى يحدد أحد المديرين نهايتها الفعلية.',
  'settings.shortDayPenalty': 'جزاء عدم إكمال ساعات العمل اليومية',
  'settings.shortDayPenaltyHint':
    'نطبقه بعد انتهاء اليوم على كل من بدأ وردية وعمل أقل من «الساعات المعيارية» لورديته في ذلك اليوم (أو الساعات الافتراضية في «الورديات») بساعة أو أقل. وإذا كان النقص أكثر من ساعة، يقرر فيه مدير النظام («المخالفات والجزاءات» › «الأيام الناقصة»). لا نطبقه أبدًا في يوم راحة أو إجازة أو عطلة، ولا عن يوم لم يحضر فيه. 0 = لا جزاء.',
  'settings.fullDayWaives': 'إكمال ساعات العمل اليومية يلغي جزاءات اليوم',
  'settings.fullDayWaivesHint': 'بعد انتهاء اليوم، يتم إلغاء جزاءات الاستراحة وعدم النشاط فيه إذا لم تدخل بعد في كشف رواتب. أما جزاءات مهلة التذاكر فتبقى.',
  'settings.dailyHoursTolerance': 'دقائق النقص المسموح بها',
  'settings.dailyHoursToleranceHint': 'إذا نقص اليوم عن ساعاته بهذا العدد من الدقائق أو أقل، نعدّه يومًا كاملًا. 0 = بلا سماح.',
  'settings.dayStartsAt': 'يبدأ يوم الحضور عند',
  'settings.dayStartsAtHint':
    'الوردية التي تبدأ بعد منتصف الليل وقبل هذه الساعة، دون وردية ليلية جارية في الجدول، تتبع اليوم السابق، فيمكن أن تكمل ساعاته. ونحتسب كل يوم في هذه الساعة من اليوم التالي. يجب أن تكون قبل 12:00.',
  'settings.breakTypes': 'أنواع الاستراحات',
  'settings.breakTypesNote':
    'ما يمكن للموظف أن يوقف ورديته من أجله. «المدة المسموحة» هي أقصى مدة للاستراحة الواحدة، وما يزيد عليها يظهر كتجاوز ويضيف جزاء هذا النوع بعد أن يعتمده مدير النظام. النوع المحسوب من العمل، مثل الاجتماع، يبقى وقته ضمن «مدة العمل»، وأي نوع آخر نخصم وقته منها.',
  'settings.newBreakType': 'إضافة نوع استراحة',
  'settings.editBreakType': 'تعديل نوع الاستراحة',
  'settings.allowanceMinutes': 'المدة المسموحة (بالدقائق)',
  'settings.noLimit': 'بلا حد',
  'settings.allowanceHint': 'اتركه فارغًا ليكون بلا حد.',
  'settings.maxPerDay': 'عدد المرات في اليوم',
  'settings.maxPerDayHint': 'عدد المرات التي يمكن لكل موظف أن يأخذها في اليوم، في كل ورديات اليوم معًا. اتركه فارغًا ليكون بلا حد.',
  'settings.maxPerDayInvalid': 'أدخل عددًا صحيحًا من 1 إلى {n}، أو اتركه فارغًا ليكون بلا حد.',
  'settings.order': 'الترتيب',
  'settings.orderHint': 'الأرقام الأصغر تظهر أولًا.',
  'settings.offered': 'متاح',
  'settings.switchedOff': 'متوقف',
  'settings.offeredHint': 'عند إيقافه يبقى في السجلات التي استخدمته، لكن لا يمكن اختياره بعد ذلك.',
  'settings.timesUsed': 'مرات الاستخدام',
  'settings.codeHint': 'حروف إنجليزية كبيرة وأرقام و_. لا يتغير بعد استخدامه.',
  'settings.leaveTypes': 'أنواع الإجازات',
  'settings.leaveTypesNote': 'ما يمكن للموظف طلبه. يظهر للموظف رصيده السنوي، ويمكن لصاحب القرار أن يوافق على ما يتجاوزه.',
  'settings.newLeaveType': 'إضافة نوع إجازة',
  'settings.editLeaveType': 'تعديل نوع الإجازة',
  'settings.annualAllowance': 'الأيام في السنة',
  'settings.annualAllowanceHint': 'اتركه فارغًا إن لم يكن له رصيد سنوي.',
  'settings.isPaid': 'إجازة مدفوعة',
  'settings.emailEnabled': 'إرسال المهام والتذاكر بالبريد',
  'settings.emailEnabledHint': 'نرسل الرسائل عبر ZeptoMail بلغة كل شخص، والإشعار داخل النظام يصل في الحالتين.',
  'email.delivery': 'الإرسال',
  'email.configured': 'تم إعداد ZeptoMail.',
  'email.notConfigured': 'غير معدّ. اضبط {vars} في بيئة الـAPI، ثم أعد تشغيله.',
  'email.disabled': 'البريد الإلكتروني متوقف، فلا نرسل شيئًا، لكن الإشعارات داخل النظام تصل كالمعتاد.',
  'email.from': 'المرسل',
  'email.endpoint': 'عنوان ZeptoMail',
  'email.links': 'الروابط تفتح',
  'email.sendTest': 'أرسل لي رسالة تجريبية',
  'email.recent': 'آخر الرسائل',
  'email.to': 'إلى',
  'email.subject': 'الموضوع',
  'email.lastSevenDays': 'آخر 7 أيام',
  'email.none': 'لم نرسل أي رسالة بعد.',
  'email.sentAt': 'تم الإرسال',
  'departments.inherit': 'افتراضي',
  'departments.inherits': 'يتبع القيمة الافتراضية للشركة',
  'departments.companyDefault': 'افتراضي الشركة',
  'departments.companyDefaultIs': 'افتراضي الشركة: {value}',
  'departments.override': 'استثناء',
  'departments.penaltyDaysHint': 'بالأيام من الراتب. 0 يعني عدم تطبيق جزاء في هذا القسم.',
  'violations.days': 'الأيام',
  'violations.totalDays': 'أيام من الراتب',
  'violations.estimate': 'تقديري حتى يتم احتسابه في الرواتب',
  'violations.valueAtRate': 'بالأجر اليومي',
  'violations.noSalary': 'لا راتب في ذلك اليوم',
  'violations.noSalaryHint': 'لم يكن هناك راتب مسجّل لذلك اليوم، فلا قيمة مالية لهذه الأيام بعد. سنحسب المبلغ عندما يتم تسجيل راتب يشمل ذلك اليوم.',
  'violations.review': 'للمراجعة',
  'violations.nothingToReview': 'لا شيء بانتظارك.',
  'violations.approvedToast': 'تم اعتماد الجزاء.',
  'violations.object': 'اعتراض',
  'violations.objectTitle': 'الاعتراض على الجزاء',
  'violations.objectHint':
    'اكتب لماذا ترى الجزاء خطأ. يقرأ المدير اعتراضك، ثم يقبله فيتم إلغاء الجزاء، أو يرفضه، ويخبرك بالسبب في الحالتين. لكل جزاء اعتراض واحد فقط.',
  'violations.objectReason': 'لماذا هذا الجزاء خطأ؟',
  'violations.objectSent': 'تم إرسال اعتراضك، والقرار للمدير.',
  'violations.objection': 'الاعتراض',
  'violations.objectionSent': 'تم الإرسال',
  'violations.objectionFrom': 'اعتراض من {name}',
  'violations.objectionState.PENDING': 'بانتظار قرار المدير',
  'violations.objectionState.ACCEPTED': 'مقبول',
  'violations.objectionState.REJECTED': 'مرفوض',
  'violations.objectionState.CLOSED': 'مغلق',
  'violations.objectionBadge.PENDING': 'اعتراض بانتظار القرار',
  'violations.objectionBadge.ACCEPTED': 'الاعتراض مقبول',
  'violations.objectionBadge.REJECTED': 'الاعتراض مرفوض',
  'violations.objectionBadge.CLOSED': 'الاعتراض مغلق',
  'violations.objectionClosed': 'تم إلغاء الجزاء قبل قرار المدير في الاعتراض.',
  'violations.objectionFirst': 'قرّر في الاعتراض أولًا: قبوله يلغي الجزاء، ورفضه يترك الجزاء كما هو.',
  'violations.objectionYours': 'القرار للمدير، وسنخبرك به في الحالتين.',
  'violations.objectionApprovedBy': 'قبله {name}',
  'violations.objectionRejectedBy': 'رفضه {name}',
  'violations.approveObjection': 'قبول الاعتراض',
  'violations.rejectObjection': 'رفض الاعتراض',
  'violations.approveObjectionTitle': 'قبول الاعتراض',
  'violations.approveObjectionHint': 'سيتم إلغاء الجزاء دون أي خصم. سيقرأ الموظف سببك.',
  'violations.rejectObjectionTitle': 'رفض الاعتراض',
  'violations.rejectObjectionHint': 'يبقى الجزاء كما هو. سيقرأ الموظف سببك.',
  'violations.decisionReason': 'سببك — يقرؤه الموظف',
  'violations.objectionApprovedToast': 'تم قبول الاعتراض وإلغاء الجزاء.',
  'violations.objectionRejectedToast': 'تم رفض الاعتراض، ويبقى الجزاء.',
  'violations.breakOver': '{type} — تجاوز الوقت المسموح',
  'violations.breakLength': '{length}، والحد {limit}',
  'violations.by': 'بواسطة {name}',
  'attendance.penalty': 'جزاء {days}',
  'attendance.overrunNote': 'الاستراحة التي تتجاوز وقتها المسموح تضيف جزاءً بالأيام من الراتب، بعد أن يعتمده المدير.',
  'attendance.overLimitNow': 'تجاوزت هذه الاستراحة وقتها المسموح. عند عودتك، سيتم إرسال جزاء {days} إلى المدير ليقرر فيه.',
  'attendance.overLimitShort': 'تجاوزت الحد، والجزاء {days} عند عودتك.',
  // تطبيق فيجانيوم المكتبي والتواجد خلال الوردية.
  'agent.title': 'التطبيق المكتبي',
  'agent.subtitle': 'يخبر تطبيق فيجانيوم المكتبي النظام عندما تكون على جهازك، ولا يسجّل أبدًا ما تكتبه أو ما تراه.',
  'agent.status.CONNECTED': 'متصل',
  'agent.status.OFFLINE': 'غير متصل',
  'agent.status.NOT_PAIRED': 'غير معدّ',
  'agent.status.DISABLED': 'موقوف',
  'agent.requiredBadge': 'لا وردية بدون التطبيق',
  'agent.connectedOn': 'متصل على {name} · آخر تقرير {when}.',
  'agent.offlineOn': 'آخر تقرير من {name} {when}. افتح تطبيق فيجانيوم المكتبي عليه.',
  'agent.disabledOn': 'أوقف أحد المديرين {name} عن التطبيق المكتبي.',
  'agent.notPairedBody': 'ثبّت تطبيق فيجانيوم المكتبي على الجهاز الذي تعمل عليه، ثم اربطه بحسابك من هنا.',
  'agent.requiredNote': 'الشركة تشترط التطبيق، فلا يمكنك بدء ورديتك إلا وهو متصل على جهازك.',
  'agent.notRequiredNote': 'غير مطلوب الآن، لكنه يتابع ورديتك ما دام متصلًا.',
  'agent.policy':
    'بعد {idle} د من آخر استخدام للوحة المفاتيح أو الفأرة، تصبح حالتك «لا يعمل». وبعد {penalty} د من آخر نشاط نطبق جزاء {days}. وعندما يكون التطبيق مغلقًا أو غير متصل تكون حالتك «لا يعمل» أيضًا، لكن بلا جزاء.',
  'agent.policyNoPenalty': 'بعد {idle} د من آخر استخدام للوحة المفاتيح أو الفأرة، تصبح حالتك «لا يعمل»، وكذلك عندما يكون التطبيق مغلقًا أو غير متصل.',
  'agent.downloadWindows': 'تنزيل لويندوز',
  'agent.downloadMac': 'تنزيل لماك',
  'agent.connect': 'ربط هذا الجهاز',
  'agent.connectAnother': 'ربط جهاز آخر',
  'agent.codeTitle': 'رمز الربط',
  'agent.codeHint': 'افتح التطبيق على جهازك واكتب هذا الرمز، أو اضغط «افتح التطبيق». الرمز صالح لمرة واحدة فقط، ولمدة {minutes} د.',
  'agent.openAgent': 'افتح التطبيق',
  'agent.copyCode': 'نسخ الرمز',
  'agent.copied': 'تم النسخ',
  'agent.codeExpired': 'انتهت صلاحية هذا الرمز.',
  'agent.newCode': 'رمز جديد',
  'agent.waiting': 'بانتظار اتصال التطبيق…',
  'agent.paired': 'تم ربط {name}.',
  'agent.myComputers': 'أجهزتي',
  'agent.noComputers': 'لم يتصل أي جهاز بعد.',
  'agent.version': 'الإصدار {version}',
  'agent.disabledBy': 'أوقفه أحد المديرين',
  'agent.remove': 'إزالة',
  'agent.removeConfirm': 'إزالة {name}؟ سيتوقف تطبيقه عن الإرسال، ولربطه من جديد تحتاج إلى رمز جديد.',
  'agent.removed': 'تمت إزالة الجهاز.',
  'agent.never': 'لم يصل بعد',
  'agent.privacy': 'يرسل التطبيق فقط المدة منذ آخر استخدام للوحة المفاتيح أو الفأرة، وما إذا كانت الشاشة مقفلة، واسم الجهاز. ولا يرسل أبدًا ما تكتبه، ولا ما على شاشتك، ولا التطبيقات والمواقع التي تستخدمها.',
  'agent.setUp': 'إعداد التطبيق المكتبي',
  'agent.startBlocked.NOT_PAIRED': 'لا يمكنك بدء ورديتك: تطبيق فيجانيوم المكتبي غير معدّ على جهازك.',
  'agent.startBlocked.OFFLINE': 'لا يمكنك بدء ورديتك: تطبيق فيجانيوم المكتبي لا يعمل أو غير متصل. افتحه على جهازك.',
  'agent.startBlocked.DISABLED': 'لا يمكنك بدء ورديتك: أوقف أحد المديرين جهازك عن التطبيق المكتبي.',
  'presence.WORKING': 'يعمل',
  'presence.NOT_WORKING': 'لا يعمل',
  'presence.notWorkingAs': 'لا يعمل — {reason}',
  'presence.reason.IDLE_INACTIVITY': 'بلا نشاط',
  'presence.reason.DEVICE_LOCKED': 'الشاشة مقفلة',
  'presence.reason.AGENT_DISCONNECTED': 'التطبيق غير متصل',
  'presence.reason.DEVICE_SLEEPING': 'الجهاز في وضع السكون',
  'presence.reason.UNKNOWN': 'غير معروف',
  'presence.trackedOn': 'يتابعها التطبيق المكتبي على {name}.',
  'presence.untracked': 'لا يتابع التطبيق المكتبي هذه الوردية.',
  'presence.penaltyAt': 'سيتم تطبيق جزاء {days} عند {time} إذا لم تستخدم لوحة المفاتيح أو الفأرة.',
  'presence.penaltyAtOther': 'سيتم تطبيق الجزاء عند {time} إذا لم يتغير شيء.',
  'presence.backHint': 'حرّك الفأرة أو اضغط أي مفتاح لتعود حالتك «يعمل».',
  'presence.reconnectHint': 'افتح تطبيق فيجانيوم المكتبي على {name} لتعود حالتك «يعمل».',
  'attendance.notWorking': 'لا يعمل',
  'attendance.shiftSince': 'الوردية منذ {time}',
  'attendance.notWorkingShort': '{minutes} دون عمل',
  'attendance.computers': 'الأجهزة',
  'attendance.details': 'التفاصيل',
  'attendance.shiftOn': 'وردية {date}',
  'attendance.shiftOf': '{name} — وردية {date}',
  'attendance.trackedBy': 'تابعها التطبيق المكتبي على {name}.',
  'attendance.notTracked': 'لم يتابع التطبيق المكتبي هذه الوردية.',
  'attendance.idlePenalties': 'جزاءات عدم النشاط',
  'attendance.workBreaks': '{minutes} ضمن مدة العمل',
  'attendance.countsAsWork': 'ضمن مدة العمل',
  'attendance.dailyHours': 'ساعات العمل اليومية',
  'attendance.targetMet': 'مكتملة',
  'attendance.targetMetExtra': 'مكتملة · زيادة {minutes}',
  'attendance.targetToGo': 'يتبقى {minutes}',
  'attendance.targetShort': 'نقص {minutes}',
  'attendance.targetFromShift': '«الساعات المعيارية» لورديته في ذلك اليوم',
  'attendance.targetFromSalary': 'ساعات العمل اليومية في سجل راتب الموظف، وهي طريقة احتساب الأيام قبل 21 أكتوبر 2026',
  'attendance.targetFromDefault': 'لا توجد وردية بساعات معيارية في ذلك اليوم: ساعات العمل اليومية الافتراضية للشركة',
  'attendance.targetJudged': 'كما تم احتسابه بعد انتهاء اليوم',
  'attendance.daysOfSalary': 'يوم من الراتب',
  'attendance.notWorkingStretches': 'فترات عدم العمل',
  'attendance.notWorkingStretchesNote': 'كل فترة دون استخدام لوحة المفاتيح أو الفأرة، أو دون التطبيق، وما ترتب عليها، ولا تشمل الاستراحات أبدًا.',
  'attendance.noStretches': 'يعمل طوال الوردية',
  'attendance.noStretchesHint': 'لا توجد فترة «لا يعمل» في هذه الوردية.',
  'attendance.stretch': 'الوقت',
  'attendance.reason': 'السبب',
  'attendance.lastActivity': 'آخر نشاط',
  'attendance.penaltyCol': 'الجزاء',
  'attendance.stillNotWorking': 'ما زال لا يعمل',
  'attendance.penaltyDueAt': 'يستحق عند {time}',
  'attendance.endReason.SHIFT_ENDED': 'انتهت الوردية',
  'attendance.endReason.BREAK_STARTED': 'بدأت استراحة',
  'attendance.endReason.ACTIVITY_RESUMED': 'عاد النشاط',
  'attendance.endReason.AGENT_RECONNECTED': 'عاد اتصال التطبيق',
  'attendance.outcome.APPLIED': 'تم تطبيق الجزاء',
  'attendance.outcome.CAP_REACHED': 'بلغ الحد اليومي — لا خصم إضافي',
  'attendance.outcome.NOT_REACHED': 'بلا جزاء',
  'attendance.outcome.DISABLED': 'الجزاءات متوقفة',
  'attendance.outcome.HOURS_MET': 'بلا جزاء — اكتملت ساعات اليوم',
  'attendance.outcome.FREE_SHIFT': 'بلا جزاء — الوردية الحرة لا جزاء فيها',

  // الحضور › التقرير اليومي: يوم واحد، الفريق كله، بكل التفاصيل (2026-10-27).
  'attendance.dailyReport': 'التقرير اليومي',
  'attendance.report.previousDay': 'اليوم السابق',
  'attendance.report.nextDay': 'اليوم التالي',
  'attendance.report.show': 'عرض',
  'attendance.report.show.ALL': 'الجميع',
  'attendance.report.show.CAME': 'من حضر',
  'attendance.report.show.LATE': 'المتأخرون',
  'attendance.report.show.LEFT_EARLY': 'من انصرف مبكرًا',
  'attendance.report.show.ABSENT': 'الغائبون',
  'attendance.report.show.LEAVE': 'من في إجازة',
  'attendance.report.show.OFF': 'يوم راحة أو غير مدرج في الجدول',
  'attendance.report.show.SHORT': 'من لم يكمل ساعات العمل اليومية',
  'attendance.report.show.NOT_WORKING': 'من لديه وقت «لا يعمل»',
  'attendance.report.show.PENALTIES': 'من عليه جزاءات',
  'attendance.report.search': 'الاسم أو الرقم الوظيفي',
  'attendance.report.asOf': 'حتى {time}، واليوم لم ينته بعد.',
  'attendance.report.expandAll': 'توسيع الكل',
  'attendance.report.collapseAll': 'طيّ الكل',
  'attendance.report.csv': 'تنزيل CSV',
  'attendance.report.team': 'الفريق',
  'attendance.report.rostered': 'في الجدول: {count}',
  'attendance.report.came': 'حضروا',
  'attendance.report.lateCount': 'متأخرون: {count}',
  'attendance.report.absent': 'غائبون',
  'attendance.report.notStartedCount': 'لم يبدؤوا بعد: {count}',
  'attendance.report.onLeave': 'في إجازة',
  'attendance.report.dayOffCount': 'يوم راحة: {count}',
  'attendance.report.average': 'المتوسط: {minutes}',
  'attendance.report.overAllowance': 'تجاوز المسموح: {minutes}',
  'attendance.report.stretchCount': 'عدد الفترات: {count}',
  'attendance.report.penalties': 'الجزاءات',
  'attendance.report.shortCount': 'لم يكملوا ساعات اليوم: {count}',
  'attendance.report.toggle': 'تفاصيل {name}',
  'attendance.report.shiftCount': 'عدد الورديات: {count}',
  'attendance.report.shiftN': 'الوردية {n}',
  'attendance.report.break': 'الاستراحة',
  'attendance.report.time': 'الوقت',
  'attendance.report.length': 'المدة',
  'attendance.report.allowance': 'المسموح',
  'attendance.report.over': 'التجاوز',
  'attendance.report.noLimit': 'بلا حد',
  'attendance.report.noBreaks': 'لا توجد استراحات في هذه الوردية.',
  'attendance.report.legend.workBreak': 'استراحة تدخل ضمن وقت العمل',
  'attendance.report.legend.roster': 'الوردية في الجدول',
  'attendance.report.dayVerdict': 'حصيلة اليوم',
  'attendance.report.verdictNotOwed': 'لا توجد ساعات مطلوبة في هذا اليوم.',
  'attendance.report.verdictMet': 'عمل {worked} من {owed} المطلوبة، واكتملت الساعات.',
  'attendance.report.verdictShort': 'عمل {worked} من {owed} المطلوبة، بنقص {missing}.',
  'attendance.report.verdictRunning': 'عمل {worked} من {owed} المطلوبة حتى الآن، والمتبقي {missing}.',
  'attendance.report.notJudgedYet': 'تظهر نتيجة اليوم بعد انتهائه.',
  'attendance.report.waived': 'جزاءات سقطت لاكتمال الساعات: {count}',
  'attendance.report.kind.BREAK': 'استراحة تجاوزت حدها',
  'attendance.report.kind.IDLE': 'لا يعمل',
  'attendance.report.kind.SHORT_DAY': 'نقص ساعات العمل اليومية',
  'attendance.report.kind.EARLY_END': 'إنهاء الوردية مبكرًا',
  'attendance.report.minutesUnit': 'دقيقة',
  'attendance.report.rosterStart': 'بداية الوردية في الجدول',
  'attendance.report.rosterEnd': 'نهاية الوردية في الجدول',
  'attendance.report.leave': 'الإجازة',
  'attendance.report.shifts': 'الورديات',
  'attendance.report.breakCount': 'عدد الاستراحات',
  'attendance.report.result': 'نتيجة ساعات العمل اليومية',
  'attendance.report.penaltyDays': 'الجزاءات (أيام)',
  'attendance.report.breakDetail': 'تفاصيل الاستراحات',
  'attendance.report.notWorkingDetail': 'تفاصيل «لا يعمل»',
  'attendance.report.freeCount': 'الورديات الحرة',
  'attendance.report.freeWorked': 'مدة العمل في الورديات الحرة',
  'attendance.report.freeDetail': 'تفاصيل الورديات الحرة',
  'attendance.report.notes': 'ملاحظات',

  'timeline.title': 'السجل الزمني',
  'timeline.empty': 'لا يوجد شيء مسجل بعد.',
  'timeline.SHIFT_STARTED': 'بدأت الوردية',
  'timeline.SHIFT_ENDED': 'انتهت الوردية',
  'timeline.BREAK_STARTED': 'استراحة — {type}',
  'timeline.BREAK_ENDED': 'العودة من {type}',
  'timeline.AGENT_CONNECTED': 'اتصل التطبيق المكتبي — {device}',
  'timeline.AGENT_DISCONNECTED.QUIT': 'تم إغلاق التطبيق المكتبي',
  'timeline.AGENT_DISCONNECTED.LOST': 'توقف التطبيق المكتبي عن الإرسال',
  'timeline.AGENT_DISCONNECTED.SHUTDOWN': 'تم إطفاء الجهاز',
  'timeline.AGENT_DISCONNECTED.LOGOFF': 'تم تسجيل الخروج من الجهاز',
  'timeline.AGENT_DISCONNECTED.UNPAIR': 'تم فصل الجهاز عن التطبيق المكتبي',
  'timeline.AGENT_DISCONNECTED.DISABLED': 'أوقف مدير النظام الجهاز',
  'timeline.AGENT_DISCONNECTED.REMOVED': 'تمت إزالة الجهاز من التطبيق المكتبي',
  'timeline.AGENT_RECONNECTED': 'اتصل التطبيق المكتبي من جديد',
  'timeline.AGENT_SWITCHED': 'المتابعة الآن على {to}',
  'timeline.workingAgain': 'يعمل — عاد النشاط',
  'timeline.lastActivity': 'لا نشاط للوحة المفاتيح أو الفأرة منذ {time}',
  'timeline.DEVICE_LOCKED': 'تم قفل الشاشة',
  'timeline.DEVICE_UNLOCKED': 'تم فتح قفل الشاشة',
  'timeline.DEVICE_SLEEP': 'دخل الجهاز وضع السكون',
  'timeline.DEVICE_RESUMED': 'استيقظ الجهاز',
  'timeline.IDLE_PENALTY_APPLIED': 'جزاء عدم النشاط — {days}',
  'timeline.IDLE_PENALTY_PENDING': 'جزاء عدم النشاط — {days}، بانتظار المراجعة',
  'timeline.IDLE_PENALTY_CAP_REACHED': 'بلغ حد الجزاء — وصل اليوم إلى حده الأقصى، فلا خصم إضافي',
  'timeline.cutToMaximum': 'تم تخفيضه إلى ما تبقى من الحد الأقصى لليوم.',
  'devices.computer': 'الجهاز',
  'devices.status.ACTIVE': 'قيد الاستخدام',
  'devices.status.DISABLED': 'موقوف',
  'devices.status.REVOKED': 'تمت إزالته',
  'devices.inUseOrDisabled': 'قيد الاستخدام أو موقوف',
  'devices.online': 'متصل',
  'devices.offline': 'غير متصل',
  'devices.asleep': 'في وضع السكون',
  'devices.lastReport': 'آخر تقرير {when}',
  'devices.lastReportCol': 'آخر تقرير',
  'devices.connectedSince': 'تم الربط في {date}',
  'devices.counts': '{online} متصل من {total}',
  'devices.noTelemetry': 'نعرض هنا هل كل تطبيق متصل ومتى أرسل آخر تقرير، ولا نعرض نشاط أي شخص أبدًا.',
  'devices.none': 'لم يتم ربط أي جهاز بعد',
  'devices.noneHint': 'يربط الموظفون أجهزتهم من «الحضور › التطبيق المكتبي».',
  'devices.disable': 'إيقاف',
  'devices.enable': 'تفعيل',
  'devices.remove': 'إزالة',
  'devices.disableTitle': 'إيقاف {name}',
  'devices.disableNote': 'يتوقف التطبيق على هذا الجهاز عن الاحتساب فورًا، ولن يستطيع {person} بدء وردية منه أو ربطه من جديد حتى تفعّله. وسنخبره بالسبب.',
  'devices.removeTitle': 'إزالة {name}',
  'devices.removeNote': 'يتوقف مفتاحه السري عن العمل، وعلى {person} ربطه من جديد برمز جديد.',
  'devices.reason': 'السبب؟',
  'devices.reasonHint': 'سيظهر هذا للموظف.',
  'devices.disabled': 'تم إيقاف الجهاز.',
  'devices.enabled': 'تم تفعيل الجهاز.',
  'devices.removedToast': 'تمت إزالة الجهاز.',
  'devices.notFound': 'لم يعد هذا الجهاز في قائمتك.',
  'dash.notWorkingN': 'لا يعمل: {n}',
  'violations.idle': 'عدم نشاط — {reason}',
  'violations.idleFrom': 'منذ {time}',
  'violations.idleShift': 'الوردية',
  'violations.kind.break': 'جزاء الاستراحة',
  'violations.kind.idle': 'جزاء عدم النشاط',
  'violations.kind.shortDay': 'جزاء ساعات العمل اليومية',
  'violations.kind.earlyEnd': 'جزاء إنهاء الوردية مبكرًا',
  'violations.kind.sla': 'جزاء مهلة التذكرة',
  'violations.kind.other': 'جزاء',
  'violations.forWhat': 'سبب الجزاء',
  'violations.breakType': 'الاستراحة',
  'violations.started': 'البداية',
  'violations.length': 'المدة',
  'violations.openShift': 'فتح الوردية',
  'violations.raised': 'تاريخ الجزاء',
  'violations.decidedBy': 'صاحب القرار',
  'violations.cancelledBy': 'ألغاه {name}',
  'violations.cancelReason': 'سبب الإلغاء',
  'violations.shortDay': 'لم تكتمل ساعات العمل اليومية',
  'violations.shortDayWorked': '{worked} من {owed}',
  'violations.workDate': 'اليوم',
  'violations.worked': 'ساعات العمل الفعلية',
  'violations.owed': 'ساعات العمل اليومية',
  'violations.owedFromDefault': 'لا توجد في ذلك اليوم وردية لها ساعات معيارية، لذلك نستخدم الساعات الافتراضية للشركة.',
  'violations.earlyEnd': 'إنهاء الوردية مبكرًا',
  'violations.earlyEndAt': 'انتهت في {ended}، قبل {end} بـ {early}',
  'violations.shiftEnds': 'نهاية الوردية',
  'violations.endedAt': 'انتهت',
  'violations.earlyBy': 'مبكرًا بـ',
  'violations.earlyAllowed': 'المسموح',
  'violations.earlyAllowedValue': 'حتى {minutes} قبل النهاية',
  'violations.waived': 'اكتملت ساعات العمل اليومية يوم {date} ({worked} من {owed})، لذلك لا تسري جزاءات الاستراحة وعدم النشاط في ذلك اليوم.',
  'violations.waivedShort': 'اكتملت ساعات اليوم',
  'settings.unsaved': 'غير محفوظ',
  'settings.unitSeconds': 'ثانية',
  'settings.unitMinutes': 'دقيقة',
  'settings.agentTitle': 'التطبيق المكتبي وسياسة الخمول',
  'settings.agentNote': 'يخبر تطبيق فيجانيوم المكتبي النظام متى يكون من في ورديته أمام جهازه. وقت الخمول ليس استراحة أبدًا، وحالة «لا يعمل» لا تنهي الوردية أبدًا.',
  'settings.agentFix': 'بعض القيم لا تتوافق مع بعضها، راجع الحقول المعلّمة بالأسفل.',
  'settings.agentSection': 'التطبيق المكتبي',
  'settings.idleSection': 'الخمول وجزاء عدم النشاط',
  'settings.agentRequired': 'اشتراط التطبيق المكتبي لبدء الوردية',
  'settings.agentRequiredHint': 'لا وردية بدون التطبيق: لا يبدأ أحد ورديته إلا إذا كان التطبيق متصلًا على جهازه. أوقف هذا الخيار فقط إذا كان التطبيق نفسه لا يمكن استخدامه، وما دام موقوفًا تبدأ الوردية دون متابعة حتى يتصل تطبيق صاحبها.',
  'settings.agentDownload': 'رابط تنزيل التطبيق لويندوز',
  'settings.agentDownloadHint': 'رابط برنامج التثبيت لويندوز (ملف exe)، ويظهر للموظفين زر «تنزيل لويندوز». إذا تركته فارغًا لا يظهر الزر.',
  'settings.agentDownloadMac': 'رابط تنزيل التطبيق لماك',
  'settings.agentDownloadMacHint': 'رابط صورة القرص لماك (ملف dmg)، ويظهر للموظفين زر «تنزيل لماك». إذا تركته فارغًا لا يظهر الزر.',
  'settings.agentHeartbeat': 'يرسل التطبيق تقريرًا كل',
  'settings.agentHeartbeatHint': 'نخبر التطبيق بهذه القيمة مع كل تقرير، فيصل أي تغيير إلى كل جهاز خلال تقرير واحد.',
  'settings.agentFreshness': 'يبقى التقرير حديثًا لمدة',
  'settings.agentFreshnessHint': 'إذا مرت هذه المدة دون تقرير، يصبح التطبيق غير متصل. اجعلها بطول فاصل تقرير واحد على الأقل، وضعفه أكثر أمانًا.',
  'settings.agentGrace': 'مهلة الانقطاع',
  'settings.agentGraceHint': 'المدة التي يمكن أن يغيب فيها التطبيق (مغلقًا أو في وضع السكون أو خارج الشبكة) قبل أن يصبح صاحبه «لا يعمل». ولا يترتب على هذا الوقت أي جزاء أبدًا.',
  'settings.idleEnabled': 'اعتبار الموظف «لا يعمل» عند الخمول',
  'settings.idleEnabledHint': 'عند إيقافه: لا يصبح أحد «لا يعمل» إلا إذا غاب التطبيق أكثر من مهلته.',
  'settings.idleThreshold': '«لا يعمل» بعد',
  'settings.idleThresholdHint': 'عدد الدقائق دون استخدام لوحة المفاتيح أو الفأرة. عند الوصول إليه تبدأ حالة «لا يعمل» من وقت آخر نشاط. والشاشة المقفلة لها الحكم نفسه.',
  'settings.penaltyEnabled': 'تطبيق جزاء عدم النشاط',
  'settings.penaltyEnabledHint': 'عند إيقافه: يصبح الموظف «لا يعمل» دون أي جزاء.',
  'settings.penaltyThreshold': 'الجزاء بعد',
  'settings.penaltyThresholdHint':
    'عدد الدقائق من آخر استخدام للوحة المفاتيح أو الفأرة. مثال: مع 5 و10 وآخر نشاط 10:00، تبدأ حالة «لا يعمل» من 10:00 بعد تأكيدها عند 10:05، والجزاء عند 10:10. جزاء واحد لكل فترة، ولا جزاء أبدًا على غياب التطبيق.',
  'settings.penaltyDays': 'الجزاء',
  'settings.penaltyMax': 'أقصى خصم في اليوم',
  'settings.penaltyMaxHint': 'بعد بلوغه نسجل الفترة كالمعتاد، لكن لا نخصم أي شيء إضافي.',
  'settings.penaltyApproval': 'تنتظر الجزاءات قرار مدير النظام',
  'settings.penaltyApprovalHint': 'عند إيقافه: تسري الجزاءات فورًا، ويمكن لمدير النظام إلغاء أي منها من «المخالفات والجزاءات».',
  'settings.problem.NOT_BOOLEAN': 'نعم أو لا.',
  'settings.problem.NOT_WHOLE_NUMBER': 'عدد صحيح.',
  'settings.problem.NOT_NUMBER': 'رقم بمنزلتين عشريتين على الأكثر.',
  'settings.problem.TOO_SMALL': 'لا يقل عن {limit}.',
  'settings.problem.TOO_LARGE': 'لا يزيد على {limit}.',
  'settings.problem.BELOW_IDLE_THRESHOLD': 'لا يقل عن حد «لا يعمل» ({limit} د)، لأن الاثنين يبدآن من آخر نشاط.',
  'settings.problem.BELOW_HEARTBEAT': 'فاصل تقرير واحد على الأقل ({limit} ث).',
  'settings.problem.NOT_A_LINK': 'رابط يبدأ بـ https://، أو اتركه فارغًا.',
  'settings.problem.NOT_A_TIME': 'وقت من 00:00 إلى 11:59.',
  'settings.problem.NOT_A_VERSION': 'أرقام ونقاط كما في اسم الإصدار، مثل 1.0.2، أو اتركه فارغًا.',
  'settings.problem.LINK_REQUIRED': 'يحتاج أحدث إصدار إلى هذا الرابط، فمنه ينزّل الهاتف التحديث عندما نطلب منه ذلك.',
  'settings.overrunPenalty': 'الجزاء عند تجاوز الوقت',
  'settings.overrunPenaltyHint': 'بالأيام من الراتب، مثل 0.25. ينتظر الجزاء قرار قائد قسم الموظف أو مدير النظام، وإذا رفضه القائد نلغيه ونخبر مديري النظام. 0 = بلا جزاء.',
  'settings.overrunPenaltyInvalid': 'بين 0 و30 يومًا، بمنزلتين عشريتين على الأكثر.',
  'settings.breakTakenBy': 'من يمكنه أخذها',
  'settings.breakForEveryone': 'الجميع',
  'settings.breakForLeaders': 'قادة الفرق ومديرو النظام',
  'settings.breakForAdmins': 'مديرو النظام',
  'settings.breakForDepartments': 'أقسام محددة',
  'settings.breakForEmployees': 'موظفون محددون',
  'settings.breakTakenByHint':
    'إذا خصصتها لقادة الفرق ومديري النظام، أو لمديري النظام وحدهم، أو للأقسام أو الموظفين الذين تختارهم، فلن تظهر لغيرهم ولن يستطيع غيرهم بدءها. وتسري عليهم مدتها المسموحة وجزاؤها مثل أي استراحة.',
  'settings.breakDepartments': 'الأقسام',
  'settings.breakDepartmentsHint':
    'تظهر لموظفي هذه الأقسام فقط، ولا يستطيع غيرهم بدءها، حتى مدير النظام إذا لم يكن في أحدها.',
  'settings.breakPickDepartments': 'اختر قسمًا واحدًا على الأقل.',
  'settings.breakEmployees': 'الموظفون',
  'settings.breakEmployeesHint':
    'تظهر للموظفين الذين تختارهم فقط، ولا يستطيع غيرهم بدءها، حتى مدير النظام إذا لم تختره.',
  'settings.breakPickEmployees': 'اختر موظفًا واحدًا على الأقل.',
  'settings.breakEmployeesMax': '{n} شخصًا على الأكثر.',
  'settings.countsAsWork': 'تدخل ضمن وقت العمل',
  'settings.countsAsWorkOn': 'نضيفها إلى مدة العمل',
  'settings.countsAsWorkOff': 'نخصمها من مدة العمل',
  'settings.countsAsWorkHint':
    'عند التحديد يبقى وقت هذه الاستراحة ضمن «مدة العمل»، مثل الاجتماع، ودون التحديد نخصمه منها. وتسري مدتها المسموحة وجزاؤها في الحالتين. والتغيير يشمل الاستراحات السابقة أيضًا.',
  'tasks.comments': 'التعليقات',
  'tasks.addComment': 'إضافة تعليق',
  'tasks.activity': 'النشاط',
  'tasks.noDescription': 'لا يوجد وصف.',
  'tasks.createdBy': 'المنشئ',
  'tasks.tags': 'الوسوم',
  'tasks.noComments': 'لا توجد تعليقات بعد.',
  'tasks.commentPosted': 'تمت إضافة التعليق.',
  'tasks.post': 'نشر',
  'tasks.activity.created': 'تم الإنشاء',
  'tasks.activity.updated': 'تم التعديل',
  'tasks.activity.assigned': 'تم الإسناد',
  'tasks.activity.unassigned': 'تم إلغاء الإسناد',
  'tasks.activity.commented': 'تمت إضافة تعليق',
  'tasks.activity.filesAttached': 'تم إرفاق ملفات',
  'tasks.activity.attachmentRemoved': 'تمت إزالة ملف',
  'attachments.title': 'المرفقات',
  'attachments.none': 'لا توجد ملفات بعد.',
  'attachments.attachFiles': 'إرفاق ملفات',
  'attachments.recordVoice': 'رسالة صوتية',
  'attachments.recordVoiceHint': 'تسجيل رسالة صوتية',
  'attachments.recording': 'جاري التسجيل',
  'attachments.startingMic': 'جاري تشغيل الميكروفون…',
  'attachments.stopRecording': 'إيقاف وحفظ',
  'attachments.discardRecording': 'حذف',
  'attachments.voiceNote': 'رسالة صوتية',
  'attachments.hint': 'صور وملفات PDF وWord وExcel ورسائل صوتية وفيديوهات، بحد أقصى {mb} ميجابايت للملف و{videoMb} ميجابايت للفيديو.',
  'attachments.dropHere': 'أفلت الملفات هنا لإرفاقها',
  'attachments.attach': 'إرفاق',
  'attachments.uploading': 'جاري الرفع…',
  'attachments.remove': 'إزالة',
  'attachments.removeConfirm': 'إزالة هذا الملف؟',
  'attachments.keep': 'إبقاء',
  'attachments.removed': 'تمت إزالة الملف.',
  'attachments.added': 'تم إرفاق الملفات.',
  'attachments.download': 'تنزيل',
  'attachments.open': 'فتح',
  'attachments.play': 'تشغيل',
  'attachments.pause': 'إيقاف مؤقت',
  'attachments.speed': 'سرعة التشغيل',
  'attachments.micDenied': 'متصفحك يمنع هذا الموقع من استخدام الميكروفون. اضغط على أيقونة الميكروفون في شريط العنوان، واسمح باستخدامه، ثم حاول مرة ثانية.',
  'attachments.micUnavailable': 'لا يدعم هذا المتصفح أو الجهاز تسجيل الصوت.',
  'attachments.tooLarge': 'أكبر من {mb} ميجابايت.',
  'attachments.typeNotAllowed': 'هذا النوع من الملفات غير مقبول.',
  'attachments.tooMany': 'حتى {n} ملفًا في المرة الواحدة.',
  'attachments.maxLength': 'تتوقف الرسالة الصوتية عند {min} دقائق.',
  'attachments.playFailed': 'لا يمكن تشغيل هذا التسجيل هنا، لكن يمكنك تنزيله.',
  'attachments.failed': 'فشل الرفع.',
  'attachments.waitForUploads': 'انتظر حتى يكتمل الرفع.',
  'attachments.addedBy': 'أضافه {name}',
  'attachments.tooLargeVideo': 'الحد الأقصى للفيديو {mb} ميجابايت.',
  'attachments.videoUnplayable': 'لا يمكن تشغيل هذا الفيديو هنا، لكن يمكنك تنزيله.',
  'attachments.storageUnreachable': 'لا يمكن الوصول إلى مخزن الملفات. حاول مرة ثانية، وإذا تكررت المشكلة فأبلغ أحد مديري النظام.',
  'attachments.notArrived': 'لم يصل الملف كاملًا. أزله ثم أرفقه من جديد.',
  'qtasks.replies': 'الردود',
  'qtasks.noReplies': 'لا توجد ردود بعد.',
  'qtasks.writeReply': 'اكتب ردًا',
  'qtasks.reply': 'رد',
  'qtasks.replyPosted': 'تم نشر الرد.',
  'qtasks.claimedAt': 'تاريخ الإسناد',

  // سير العمل — إلى من تنتقل المهمة بعد ذلك، مستوى بعد مستوى
  'workflows.levels': 'المستويات',
  'workflows.levelN': 'المستوى {n}',
  'workflows.levelOf': 'المستوى {n} من {total}',
  'workflows.together': 'يعملون عليها في الوقت نفسه',
  'workflows.levelsHint': 'يحصل المستوى 1 على المهمة أولًا. ومن في المستوى نفسه يعملون عليها في الوقت نفسه، ويبدأ المستوى التالي بعد أن ينتهوا جميعًا، ويحدّد موعده النهائي بنفسه. ويمكن لأي شخص بعد المستوى 1 أن يعيد خطوته إلى شخص قبله ليراجع شيئًا، ثم تعود إليه عندما ينتهي.',
  'workflows.addPerson': 'أضف شخصًا…',
  'workflows.addLevel': 'إضافة مستوى',
  'workflows.removeLevel': 'إزالة المستوى',
  'workflows.moveUp': 'نقل لأعلى',
  'workflows.moveDown': 'نقل لأسفل',
  'workflows.removePerson': 'إزالة {name}',
  'workflows.levelEmpty': 'أضف شخصًا واحدًا على الأقل إلى هذا المستوى.',
  'workflows.run.ACTIVE': 'مستمر',
  'workflows.run.COMPLETED': 'اكتمل',
  'workflows.run.STOPPED': 'توقّف',
  'workflows.thisTask': 'هذه المهمة',
  'workflows.progress.done': 'اكتمل',
  'workflows.progress.current': 'قيد التنفيذ',
  'workflows.progress.waiting': 'قيد الانتظار',
  'workflows.progress.skipped': 'تم تخطيه — لا أحد فيه متاح للعمل',
  'workflows.progress.stopped': 'توقّف هنا',
  'workflows.progress.unreached': 'لم يصل إليه',
  'workflows.filesTitle': 'ملفات سير العمل',
  'workflows.filesHint': 'الملفات المرفقة بالخطوات الأخرى لهذه المهمة.',
  'workflows.stepBy': 'المستوى {n} · {name}',
  'tasks.fromLevel': 'من المستوى {n}',
  'tasks.carriedComment': 'وصل مع العمل',
  'tasks.carriedCommentHint':
    'هذا التعليق من خطوة أخرى في هذا العمل ووصل معه. قد لا يستطيع كاتبه فتح هذه المهمة، فقد لا يرى الرد هنا.',
  'tasks.taskClosed': 'انتهت هذه المهمة وتم إغلاقها، فلا يمكن إضافة تعليقات أو ملفات أو تغييرات بعد الآن.',
  'tasks.stepClosed':
    'انتهت هذه الخطوة وتم إغلاقها، فلا يمكن إضافة تعليقات أو ملفات أو تغييرات بعد الآن. وإذا احتاج شخص في مستوى لاحق شيئًا آخر من صاحبها، يستخدم «إعادة إلى شخص سابق» في خطوته هو.',
  'tasks.workflow': 'سير العمل',
  'tasks.goesTo': 'تذهب إلى',
  'tasks.eachGetsCopy': 'يحصل كل منهم على نسخة خاصة به ليعملوا عليها في الوقت نفسه.',
  'tasks.workflowDatesHint': 'للمستوى 1. يبدأ كل مستوى بعد انتهاء المستوى الذي قبله، ويحدّد أصحابه موعدهم النهائي بأنفسهم.',
  'tasks.yourTurn': 'جاء دورك، حدّد موعدك النهائي',
  'tasks.yourTurnBody':
    'اكتمل المستوى {n} من {workflow} في {when}، وبدأت مهمتك من وقتها. حدّد متى ستنهيها قبل أن تضغط «ابدأ»، لأنك لن تستطيع تغيير الموعد النهائي بعد ذلك.',
  'tasks.noDeadlineBody':
    'لا يوجد موعد نهائي لهذه المهمة بعد. حدّد متى ستنهيها قبل أن تضغط «ابدأ»، لأنك لن تستطيع تغيير الموعد النهائي بعد ذلك.',
  'tasks.setDeadline': 'تحديد الموعد النهائي',
  'tasks.start': 'ابدأ',
  'tasks.finish': 'إنهاء',
  'tasks.finishReviewHint': 'تبقى في حالة «مراجعة» حتى يعتمدها منجزة من أنشأها أو أسندها إليك.',
  'tasks.finishHandsOnHint': 'تنتقل إلى المستوى التالي من سير العمل.',
  'tasks.finishOwnHint': 'أنت من أنشأ هذه المهمة، فلا تحتاج إلى مراجعة، وعند إنهائها تصبح منجزة.',
  'tasks.started': 'بدأت المهمة.',
  'tasks.finishedReview': 'تم إنهاء المهمة، وهي بانتظار المراجعة.',
  'tasks.finishedHandedOn': 'تم إنهاء المهمة. ينتقل العمل إلى المستوى التالي عندما ينتهي كل من في مستواك.',
  'tasks.finishedOwn': 'تم إنهاء المهمة واعتمادها منجزة.',
  'tasks.markedDone': 'تم اعتمادها منجزة.',
  'tasks.sentBack': 'تمت إعادتها إلى العمل.',
  'tasks.review.yoursTitle': 'بانتظار مراجعتك',
  'tasks.review.yoursBody': 'أنهاها {name}. اعتمدها منجزة أو أعدها إلى العمل.',
  'tasks.review.yoursBodyNobody': 'انتهت المهمة. اعتمدها منجزة أو أعدها إلى العمل.',
  'tasks.review.markDone': 'اعتمدها منجزة',
  'tasks.review.sendBack': 'أعدها إلى العمل',
  'tasks.review.waitingTitle': 'انتهت، بانتظار المراجعة',
  'tasks.review.waitingOne': 'يراجعها {name} ويعتمدها منجزة.',
  'tasks.review.waitingTwo': 'يراجعها {creator} أو {assigner} ويعتمدها منجزة.',
  'tasks.changeDeadline': 'تغيير الموعد النهائي',
  'tasks.change': 'تغيير',
  'tasks.set': 'تحديد',
  'tasks.notSet': 'غير محدد بعد',
  'tasks.deadlineSaved': 'تم حفظ الموعد النهائي.',
  'tasks.deadlineStartedLocked': 'بدأ العمل على هذه المهمة، فلا يمكن تحديد موعدها النهائي أو تغييره الآن.',
  'tasks.deadlineFinishedLocked': 'انتهت هذه المهمة، فلا يمكن تحديد موعدها النهائي أو تغييره الآن.',
  'tasks.deadlineNotYours': 'لا يغيّر موعدها النهائي إلا من أنشأها أو مدير النظام، أما المسؤول عنها فيحدده إذا لم يكن لها موعد بعد.',
  'tasks.startedWhen': 'عند اكتمال المستوى {n}',
  'tasks.activity.workflowReceived': 'وصلت من المستوى السابق',
  'tasks.activity.workflowHandedOn': 'انتقلت إلى المستوى التالي',
  'tasks.activity.workflowFinished': 'اكتمل سير العمل',
  'tasks.activity.workflowStopped': 'توقّف سير العمل',
  'tasks.activity.workflowReopened': 'عاد سير العمل إلى التنفيذ',
  'tasks.activity.deadlineSet': 'تم تحديد الموعد النهائي',
  'tasks.activity.deadlineChanged': 'تم تغيير الموعد النهائي',

  // الإعلانات — أخبار للجميع، أو لأشخاص بأسمائهم، أو لأقسام
  'nav.announcements': 'الإعلانات',
  'announcements.title': 'الإعلانات',
  'announcements.subtitle': 'أخبار للعاملين في النظام. يستطيع أي شخص نشر إعلان للجميع، أو لأشخاص يختارهم، أو لأقسام.',
  'announcements.subtitleOwn': 'أخبار للعاملين في النظام. يمكنك نشر إعلان لأقسامك، أو لأشخاص منها.',
  'announcements.new': 'إعلان جديد',
  'announcements.edit': 'تعديل الإعلان',
  'announcements.tab.all': 'الكل',
  'announcements.tab.unread': 'غير المقروءة',
  'announcements.tab.mine': 'منشوراتي',
  'announcements.empty': 'لا توجد إعلانات بعد',
  'announcements.emptyHint': 'انشر إعلانًا لتخبر الجميع أو بعض الأشخاص أو قسمًا. يصلهم في جرس الإشعارات وبالبريد الإلكتروني.',
  'announcements.emptyUnread': 'لا جديد لديك',
  'announcements.emptyUnreadHint': 'فتحت كل الإعلانات الموجّهة إليك.',
  'announcements.emptyMine': 'لم تنشر أي إعلان بعد',
  'announcements.newBadge': 'جديد',
  'announcements.deletedBadge': 'محذوف',
  'announcements.audience.ALL': 'الجميع',
  'announcements.audience.USERS': 'أشخاص محددون',
  'announcements.audience.DEPARTMENTS': 'أقسام',
  'announcements.files': 'الملفات',
  'announcements.voiceNotes': 'الرسائل الصوتية',
  'announcements.seenBy': 'شاهده',
  'announcements.tasksMade': 'المهام المنشأة منه',
  'announcements.titleLabel': 'العنوان',
  'announcements.details': 'التفاصيل',
  'announcements.whoSees': 'من سيراه',
  'announcements.option.ALL': 'الجميع',
  'announcements.option.ALLHint': 'كل العاملين في النظام ({n}).',
  'announcements.option.USERS': 'أشخاص محددون',
  'announcements.option.USERSHint': 'الأشخاص الذين تختارهم فقط.',
  'announcements.option.DEPARTMENTS': 'العاملون في أقسام محددة',
  'announcements.option.DEPARTMENTSHint': 'كل من في الأقسام التي تختارها. ومن ينضم إلى أحدها لاحقًا يراه أيضًا.',
  'announcements.option.USERSHintOwn': 'الأشخاص الذين تختارهم فقط، من أقسامك.',
  'announcements.option.DEPARTMENTSHintOwn': 'كل من في الأقسام التي تختارها من أقسامك. ومن ينضم إلى أحدها لاحقًا يراه أيضًا.',
  'announcements.addPerson': 'أضف شخصًا…',
  'announcements.removePerson': 'إزالة {name}',
  'announcements.pickPeople': 'اختر شخصًا واحدًا على الأقل.',
  'announcements.pickDepartments': 'اختر قسمًا واحدًا على الأقل.',
  'announcements.notifyHint': 'يصلهم تنبيه في جرس الإشعارات وبالبريد الإلكتروني، ويرونه في لوحة المعلومات.',
  'announcements.editNotifyHint': 'لا نرسل تنبيهًا لأحد عند التعديل. أما من تضيفه إلى قائمة من يرونه فيصله تنبيه، كما وصل للجميع أول مرة.',
  'announcements.everyoneAdmins': 'الإرسال إلى الجميع متاح لمديري النظام فقط.',
  'announcements.noReach': 'لست في أي قسم بعد، فلا يوجد من ترسل إليه إعلانًا. يمكن لمدير النظام أن يضيفك إلى قسم.',
  'announcements.notAllowed': 'يمكنك إرسال الإعلان إلى أقسامك والعاملين فيها فقط.',
  'reach.employeesSubtitle': 'العاملون في أقسامك.',
  'reach.departmentsSubtitle': 'الأقسام التي أنت فيها.',
  'reach.noDepartment': 'لست في أي قسم بعد، لذا لا ترى إلا نفسك. يمكن لمدير النظام إضافتك إلى قسم.',
  'reach.figuresNote': 'أرقام أقسامك والعاملين فيها.',
  'reach.allMyDepartments': 'كل أقسامي',
  'reach.outside': 'هذا الشخص ليس في أقسامك. يمكنك العمل مع العاملين في أقسامك فقط.',
  'reach.outsideDepartment': 'هذا القسم ليس من أقسامك. يمكنك إسناد العمل إلى أقسامك فقط.',
  'reach.nobodyToAssign': 'لا أحد من قسم هذه التذكرة ضمن أقسامك، فلا يمكنك إسنادها إلى أحد.',
  'announcements.post': 'نشر',
  'announcements.posted': 'تم نشر الإعلان. عدد من وصلهم: {n}.',
  'announcements.postedNobody': 'تم نشر الإعلان. لا يوجد أحد ضمن من سيرونه بعد.',
  'announcements.saved': 'تم حفظ الإعلان.',
  'announcements.savedTold': 'تم حفظ الإعلان. عدد من وصلهم الآن: {n}.',
  'announcements.delete': 'حذف الإعلان',
  'announcements.deleteConfirm': 'سيتم حذف «{title}» عند الجميع مع إشعاراته.',
  'announcements.deleteTasks': 'تبقى المهام المنشأة منه ({n}) كما هي.',
  'announcements.deleted': 'تم حذف الإعلان.',
  'announcements.createTask': 'إنشاء مهمة',
  'announcements.postedBy': 'نشره {name}',
  'announcements.edited': 'تم التعديل {when}',
  'announcements.noDetails': 'لا توجد تفاصيل.',
  'announcements.audienceLabel': 'من يراه',
  'announcements.tasksTitle': 'المهام المنشأة من هذا الإعلان',
  'announcements.noTasks': 'لم يتم إنشاء أي مهمة منه بعد.',
  'announcements.reachTitle': 'من شاهده',
  'announcements.reachOf': '{seen} من {people}',
  'announcements.seenList': 'شاهدوه',
  'announcements.notSeenList': 'لم يشاهدوه بعد',
  'announcements.everyoneSaw': 'شاهده الجميع.',
  'announcements.nobodySaw': 'لم يفتحه أحد بعد.',
  'announcements.nobodyInAudience': 'لا يوجد أحد ضمن من سيرونه بعد.',
  'announcements.showAll': 'عرض الكل ({n})',
  'announcements.showLess': 'عرض أقل',
  'announcements.historyTitle': 'السجل',
  'announcements.history.created': 'نشره',
  'announcements.history.seen': 'فتحه',
  'announcements.history.updated': 'عدّله',
  'announcements.history.filesAttached': 'أضاف ملفات',
  'announcements.history.attachmentRemoved': 'أزال ملفًا',
  'announcements.history.taskCreated': 'أنشأ مهمة منه',
  'announcements.history.deleted': 'حذفه',
  'announcements.history.toldN': 'عدد من وصلهم: {n}',
  'announcements.history.newlyToldN': 'عدد من وصلهم الآن: {n}',
  'announcements.history.for': 'مسندة إلى {name}',
  'announcements.field.title': 'العنوان',
  'announcements.field.body': 'التفاصيل',
  'announcements.field.audience': 'من يراه',
  'dash.announcements': 'الإعلانات',
  'dash.unreadN': 'غير المقروءة: {n}',
  'dash.viewAll': 'عرض الكل',
  'tasks.fromAnnouncement': 'من الإعلان',
  'tasks.fromAnnouncementHint': 'ستكون المهمة مرتبطة بالإعلان، وتظهر في سجله.',
  'nav.clientCrs': 'طلبات التغيير من العملاء',
  'nav.clientComplaints': 'شكاوى العملاء',
  'systems.clients': 'بوابة العملاء',
  'audit.byClient': 'العميل',
  'tickets.client': 'العميل',
  'tickets.clientHint': 'يرى العميل هذه التذكرة في بوابة العملاء، مع كل ردّ ليس ملاحظة داخلية.',
  'tickets.pickClientFirst': 'اختر العميل أولًا، لأن المشاريع تابعة له.',
  'tickets.clientProjectsOnly': 'مشاريع هذا العميل فقط.',
  'tickets.fromPortal': 'من العملاء',
  'tickets.raisedOnPortal': 'بوابة العملاء',
  'tickets.noClient': 'بدون عميل',
  'tickets.clientBadge': 'العميل',
  'tickets.clientSeesReplies': 'يقرأ العميل الردود في بوابة العملاء، فحدّد «ملاحظة داخلية» لكل ما يجب ألا يراه العميل.',
  'clients.portal': 'حساب دخول الشركة (للمالكين)',
  'clients.portalHint': 'اسم المستخدم وكلمة المرور الخاصان بالشركة على clients.viganium.net، وهما لمالكيها. ويمكن أن يكون لكل جهة اتصال حساب دخول خاص بها من نموذج جهة الاتصال، وكل من يدخل باسم العميل يرى ويفعل الشيء نفسه هناك.',
  'clients.portalWhere': 'لمالكي الشركة، على clients.viganium.net.',
  'clients.portalEnabled': 'يمكن للشركة الدخول إلى بوابة العملاء',
  'clients.portalUsername': 'اسم المستخدم',
  'clients.portalUsernameHint': 'من 3 إلى 40 حرفًا أو رقمًا أو نقطة أو شرطة أو شرطة سفلية.',
  'clients.portalPassword': 'كلمة المرور',
  'clients.portalNewPassword': 'كلمة مرور جديدة',
  'clients.portalPasswordHint': '8 أحرف على الأقل. سلّمها للعميل بنفسك، لأننا لا نرسلها بالبريد الإلكتروني.',
  'clients.portalKeepPassword': 'اتركها فارغة لتبقى كلمة المرور كما هي. وأي كلمة مرور جديدة تسجّل خروج العميل من كل الأجهزة.',
  'clients.portalGenerate': 'توليد',
  'clients.portalShow': 'إظهار كلمة المرور',
  'clients.portalHide': 'إخفاء كلمة المرور',
  'clients.portalNeedsBoth': 'يحتاج حساب دخول البوابة إلى اسم مستخدم وكلمة مرور.',
  'clients.portalGive': 'إنشاء حساب دخول',
  'clients.portalOn': 'يمكنه الدخول',
  'clients.portalOff': 'موقوف',
  'clients.portalNone': 'لا يوجد للشركة حساب دخول خاص بها بعد.',
  'clients.portalLastSignIn': 'آخر دخول',
  'clients.portalNever': 'لم يدخل بعد',
  'clients.portalBadge': 'البوابة',
  'clients.contactSignIns': 'جهات اتصال لها حساب دخول خاص: {count}',
  'clients.contactSignInsNone': 'لا توجد جهة اتصال لها حساب دخول خاص بعد. يمكنك إنشاء حساب من نموذج جهة الاتصال.',
  'clients.contactPortal': 'حساب الدخول إلى البوابة',
  'clients.contactPortalHint': 'اختياري. يتيح لجهة الاتصال هذه الدخول إلى clients.viganium.net باسم مستخدم وكلمة مرور خاصين بها، فترى وتفعل ما يتيحه حساب الشركة نفسه.',
  'clients.contactPortalEnabled': 'يمكنها الدخول إلى بوابة العملاء',
  'clients.contactPortalColumn': 'البوابة',
  'clients.contactNoSignIn': 'بلا حساب دخول',
  'clients.contactEnglishNote': 'اكتب بيانات جهات الاتصال بالإنجليزية فقط: الحروف من A إلى Z، والأرقام من 0 إلى 9، ورموز لوحة المفاتيح.',
  'clients.contactEnglishOnly': 'اكتب هذا بالإنجليزية.',
  'clients.contactNotEnglish': 'ليست بالإنجليزية',
  'clients.ticketDepartments': 'أقسام تذاكره',
  'clients.ticketDepartmentsHint': 'يستطيع العميل إرسال التذاكر من البوابة إلى هذه الأقسام، ويختار أحدها في كل تذكرة. قسم واحد على الأقل.',
  'clients.ticketDepartmentsWhere': 'الأقسام التي يستطيع العميل إرسال التذاكر إليها من البوابة.',
  'clients.ticketDepartmentsNone': 'لا توجد أقسام بعد. أضف قسمًا من صفحة «الأقسام» أولًا.',
  'clients.ticketDepartmentsRequired': 'اختر قسمًا واحدًا على الأقل.',
  'clients.ticketDepartmentDefault': 'الافتراضي',
  'clients.ticketDepartmentOff': 'موقوف',
  'clients.ticketDepartmentsShut': 'لا يستطيع العميل فتح تذاكر من البوابة حتى تختار قسمًا هنا.',
  'clients.callers': 'من يمكن للعميل الاتصال به',
  'clients.callersHint': 'الموظفون الذين يمكن لهذا العميل الاتصال بهم من بوابة العملاء. إن لم تختر أحدًا فلن يتصل العميل بأحد.',
  'clients.callersWhere': 'الموظفون الذين يمكن لهذا العميل الاتصال بهم من بوابة العملاء. كل مكالمة مسجّلة.',
  'clients.callersNone': 'لا أحد بعد، فلا يستطيع العميل الاتصال بأحد من البوابة.',
  'clients.callersRecorded': 'كل مكالمة مسجّلة، كمكالماتنا الداخلية.',
  'clients.callersInactive': 'أحد المختارين غير نشط، ولا يمكن الاتصال به حتى يعود.',
  'clients.callersGone': 'أحد المختارين لم يعد نشطًا. اختر من جديد.',
  'clients.callerInactive': 'غير نشط',
  'clients.ticketDepartmentsGone': 'تم إيقاف أحد هذه الأقسام أو حذفه. راجع القائمة مرة أخرى ثم احفظ.',
  'clients.changeRequests': 'طلبات التغيير',
  'crs.title': 'طلبات التغيير من العملاء',
  'crs.subtitle': 'ما طلب العملاء تغييره من بوابة العملاء.',
  'crs.searchPlaceholder': 'ابحث بالرقم أو العنوان أو العميل',
  'crs.number': 'الطلب',
  'crs.titleField': 'العنوان',
  'crs.client': 'العميل',
  'crs.files': 'الملفات',
  'crs.details': 'التفاصيل',
  'crs.history': 'السجل',
  'crs.reviewedBy': 'آخر من غيّر حالته',
  'crs.lastNote': 'آخر ملاحظة للعميل',
  'crs.raisedBy': 'أرسله العميل',
  'crs.raisedByContact': 'قدّمه {name}',
  'crs.raisedByLabel': 'قدّمه',
  'crs.empty': 'لا توجد طلبات تغيير',
  'crs.emptyHint': 'يرسلها العملاء من بوابة العملاء.',
  'crs.changeStatus': 'تغيير الحالة',
  'crs.moveTo': 'نقل إلى',
  'crs.reviewNote': 'ملاحظة للعميل',
  'crs.reviewNoteHint': 'يقرؤها العميل في البوابة بجانب الحالة.',
  'crs.reviewNoteRequired': 'الرفض يحتاج إلى سبب، وسيقرؤه العميل.',
  'crs.updated': 'تم تحديث طلب التغيير.',
  'crs.status.NEW': 'جديد',
  'crs.status.IN_REVIEW': 'قيد المراجعة',
  'crs.status.APPROVED': 'معتمد',
  'crs.status.REJECTED': 'مرفوض',
  'crs.status.DONE': 'منجز',
  'crs.action.NEW': 'جديد',
  'crs.action.IN_REVIEW': 'بدء المراجعة',
  'crs.action.APPROVED': 'اعتماد',
  'crs.action.REJECTED': 'رفض',
  'crs.action.DONE': 'تعليمه كمنجز',
  'crs.messages': 'الرسائل',
  'crs.conversation': 'المحادثة',
  'crs.noMessages': 'لا توجد رسائل بعد. اكتب إلى العميل هنا، وسيرد عليك من البوابة.',
  'crs.messagePlaceholder': 'اكتب إلى العميل…',
  'crs.send': 'إرسال',
  'crs.messageSent': 'تم الإرسال، وسيقرؤها العميل في البوابة.',
  'crs.clientReadsMessages': 'يقرأ العميل كل رسالة هنا في البوابة، ولا توجد ملاحظات داخلية على طلبات التغيير.',
  'crs.conversationClosed': 'تم تنفيذ طلب التغيير هذا، فالمحادثة مغلقة.',
  'crs.convert': 'تحويل إلى مهمة',
  'crs.convertTitle': 'تحويل إلى مهمة',
  'crs.converted': 'تم التحويل إلى مهمة.',
  'crs.convertedBadge': 'مهمة',
  'crs.fromRequest': 'من طلب التغيير',
  'crs.filesGoWithTask': 'تنتقل ملفاته مع المهمة، ويستطيع فتحها كل من تصل إليه المهمة.',
  'crs.cannotConvert': 'لا يمكن تحويل طلب التغيير هذا الآن، لأنه غير معتمد أو تم تحويله بالفعل.',
  'crs.projectNotYours': 'يخص الطلب مشروع {name}، وأنت لست من فريقه. اختر أحد مشاريع العميل التي أنت فيها، أو اجعلها مهمة عامة.',
  'crs.assigneeOrWorkflow': 'اختر شخصًا، أو أنشئ سير عمل من أول خيار في القائمة.',
  'crs.tasks': 'المهام',
  'crs.tasksProgress': 'تم إنجاز {done} من {total}',
  'crs.notConverted': 'لم يتم تحويله إلى مهمة بعد.',
  'crs.convertHint': 'التحويل ينشئ مهمة بعنوان الطلب وتفاصيله وملفاته، لشخص أو لسير عمل.',
  'crs.workFinished': 'اكتمل العمل عليه. علّمه كمنجز عندما تكون راضيًا عن النتيجة، وسيرى العميل «منجز» في البوابة.',
  'crs.workCalledOff': 'تم إلغاء العمل الذي تحوّل إليه، ويمكن تحويله من جديد.',
  'crs.levelOf': 'المستوى {n} من {total}',
  'crs.noDeadline': 'بلا موعد نهائي بعد',
  'crs.convertedBy': 'حوّله {name} · {when}',
  'crs.workNotFinished': 'لم تكتمل مهمته بعد. إذا علّمته كمنجز، فسيظهر للعميل أنه منجز.',

  'complaints.title': 'شكاوى العملاء',
  'complaints.subtitle': 'ما قاله العملاء عن العاملين على مشاريعهم. الشخص المعني بالشكوى لا يراها أبدًا.',
  'complaints.subtitleLeader': 'شكاوى العملاء بشأن العاملين في أقسامك. الشخص المعني بالشكوى لا يراها أبدًا.',
  'complaints.searchPlaceholder': 'ابحث بالرقم أو العنوان أو العميل أو الشخص',
  'complaints.number': 'الشكوى',
  'complaints.titleField': 'العنوان',
  'complaints.client': 'العميل',
  'complaints.about': 'بشأن',
  'complaints.files': 'الملفات',
  'complaints.details': 'التفاصيل',
  'complaints.history': 'السجل',
  'complaints.madeBy': 'قدّمها',
  'complaints.madeByClient': 'قدّمها العميل',
  'complaints.madeByContact': 'قدّمها {name}',
  'complaints.formerEmployee': 'شخص لم يعد معنا',
  'complaints.departments': 'الأقسام',
  'complaints.projects': 'في مشاريع هذا العميل',
  'complaints.sentTo': 'تم إرسالها إلى',
  'complaints.sentToAdmins': 'مديري النظام',
  'complaints.sentToLeaders': 'قادة الفرق: {names}',
  'complaints.aboutLeader': 'مديري النظام فقط، لأن {name} قائد فريق.',
  'complaints.aboutLeaderGone': 'مديري النظام فقط، لأنها عن قائد فريق.',
  'complaints.noLeaders': 'لا يوجد قائد فريق، فلا أحد يقود أقسامه.',
  'complaints.neverTheSubject': 'الشخص المعني بالشكوى لا يراها ولا يعلم بها أبدًا.',
  'complaints.lastNote': 'آخر ملاحظة للعميل',
  'complaints.answer': 'ردّنا على العميل',
  'complaints.reviewedBy': 'آخر من غيّر حالتها',
  'complaints.empty': 'لا توجد شكاوى',
  'complaints.emptyHint': 'يقدّمها العملاء من بوابة العملاء بشأن العاملين على مشاريعهم.',
  'complaints.changeStatus': 'تغيير الحالة',
  'complaints.moveTo': 'نقل إلى',
  'complaints.reviewNote': 'ملاحظة للعميل',
  'complaints.reviewNoteHint': 'يقرؤها العميل في البوابة بجانب الحالة.',
  'complaints.reviewNoteRequired': 'الإغلاق يحتاج إلى رد، وسيقرؤه العميل.',
  'complaints.updated': 'تم تحديث الشكوى.',
  'complaints.status.NEW': 'جديدة',
  'complaints.status.IN_REVIEW': 'قيد المراجعة',
  'complaints.status.CLOSED': 'مغلقة',
  'complaints.action.NEW': 'جديدة',
  'complaints.action.IN_REVIEW': 'بدء المراجعة',
  'complaints.action.CLOSED': 'إغلاق',
  'complaints.reopen': 'إعادة فتح',
  'tasks.fromChangeRequest': 'من طلب التغيير',
  'tasks.changeRequestFiles': 'ملفات طلب التغيير',
  'tasks.changeRequestFilesHint': 'ما أرسله {client} مع {number}.',
  'settings.portal': 'بوابة العملاء',
  'settings.mobile': 'تطبيق الجوال',
  'settings.mobileNote':
    'فريق فيجانيوم، تطبيق الموظفين. ضع كل ملف APK جديد في مكان يستطيع أي شخص معه الرابط تنزيله منه، مثل R2، ثم ضع رابطه ورقم إصداره هنا. يجب تحديث التطبيق على أي هاتف عليه إصدار أقدم قبل أن يفتح مرة أخرى.',
  'settings.androidDownload': 'رابط تنزيل تطبيق أندرويد',
  'settings.androidDownloadHint': 'رابط ملف APK: يظهر للجميع «تطبيق أندرويد» في القائمة تحت أسمائهم. إذا تركته فارغًا، فلن يظهر هذا الخيار.',
  'settings.androidLatestVersion': 'أحدث إصدار لتطبيق أندرويد',
  'settings.androidLatestVersionHint':
    'إصدار ملف APK الموجود على ذلك الرابط، كما هو في اسم الإصدار، مثل 1.0.2. يتحقق التطبيق منه في كل مرة يفتح فيها، والهاتف الذي عليه إصدار أقدم لا يعرض إلا «التحديث مطلوب» حتى يتم تثبيت الإصدار الجديد. ارفع ملف APK أولًا. الإصدارات من 1.0.2 فما فوق هي التي تتحقق منه. إذا تركته فارغًا، فلن يطلب التطبيق من أحد التحديث.',
  'settings.fixMarked': 'إحدى القيم غير صحيحة، راجع الحقل المشار إليه بالأسفل.',
  'settings.portalNote': 'يسجّل العملاء الدخول من clients.viganium.net. الشركة تدخل باسم المستخدم وكلمة المرور المحددين في نموذجها في «العملاء»، وكل جهة اتصال لها حساب تدخل بالبيانات المحددة في نموذجها.',
  'settings.portalDepartment': 'القسم الافتراضي لتذاكر العملاء',
  'settings.portalDepartmentHint': 'يكون هذا القسم محددًا مسبقًا في نموذج كل عميل جديد، ونموذج كل عميل يحدد الأقسام التي يستطيع إرسال التذاكر إليها. ويكون هذا القسم مختارًا في نموذج التذكرة في البوابة إذا كان العميل يستطيع الإرسال إليه.',
  'settings.portalNoDepartment': '— لا قسم —',

  'employees.code': 'الرقم الوظيفي',
  'employees.codeOnce': 'يتم إدخاله مرة واحدة فقط، ولا يمكن تغييره بعد الحفظ.',
  'employees.codeLocked': 'تم إدخاله سابقًا، فلا يمكن تغييره.',
  'employees.codeLockedError': 'لهذا الموظف رقم وظيفي بالفعل، ولا يمكن تغييره. أعد تحميل الصفحة لتراه.',
  'employees.codeTaken': 'الرقم الوظيفي {code} يخص موظفًا آخر.',
  'employees.jobTitle': 'المسمى الوظيفي',
  'employees.workMode': 'مكان العمل',
  'employees.workMode.OFFICE': 'المكتب',
  'employees.workMode.REMOTE': 'عن بُعد',
  'employees.workModeHint': 'بطاقة «في العمل الآن» في لوحة المعلومات تعرض موظفي المكتب وموظفي العمل عن بُعد بشكل منفصل.',
  'employees.language': 'اللغة',
  'employees.lastSignIn': 'آخر دخول',
  'employees.passwordHint': '8 أحرف على الأقل، وتصله في رسالة الترحيب.',
  'employees.passwordShow': 'إظهار كلمة المرور',
  'employees.passwordHide': 'إخفاء كلمة المرور',
  'employees.passwordGenerate': 'توليد',
  'employees.languageHint': 'تصله رسالة الترحيب والإشعارات بهذه اللغة.',
  'employees.welcomeQueued': 'تمت إضافة {name}. رسالة الترحيب ببيانات الدخول في طريقها إلى {email}.',
  'employees.welcomeOff': 'تمت إضافة {name}. البريد الإلكتروني متوقف، فلم نرسل رسالة الترحيب. أعطه بيانات الدخول بنفسك.',
  'employees.welcomeNotConfigured': 'تمت إضافة {name}. لم يتم إعداد البريد الإلكتروني بعد، فلم نرسل رسالة الترحيب. أعطه بيانات الدخول بنفسك.',
  'employees.resendAccess': 'إعادة إرسال بيانات الدخول',
  'employees.resendAccessConfirm': 'إعادة إرسال بيانات الدخول؟',
  'employees.resendAccessBody':
    'ستصل رسالة الترحيب إلى {name} من جديد، وفيها صفحة الدخول واسم المستخدم وكلمة مرور جديدة. لا نحتفظ بكلمة المرور الأولى في أي مكان، لذلك لا يمكن إرسالها مرتين، وستحل الجديدة محلها.',
  'employees.resendAccessSent': 'رسالة بيانات الدخول في طريقها إلى {email}، ومعها كلمة مرور جديدة.',
  'employees.resendAccessSignedIn': 'سجّل الموظف دخوله بالفعل، لذلك تبقى كلمة المرور كما هي، ولم نرسل شيئًا.',
  'employees.resendAccessInactive': 'الحساب موقوف: فعّله قبل إرسال بيانات الدخول.',
  'employees.resendAccessOff': 'البريد الإلكتروني متوقف من «الإعدادات › البريد الإلكتروني»، فلم نرسل شيئًا، وكلمة المرور كما هي.',
  'employees.resendAccessNotConfigured': 'لم يتم إعداد البريد الإلكتروني بعد، فلم نرسل شيئًا، وكلمة المرور كما هي.',
  'employees.delete': 'حذف',
  'employees.deleteTitle': 'حذف {name}',
  'employees.deleteHint': 'سيتم حذفه نهائيًا مع كل ما أنشأه وكل ما يخصه.',
  'employees.deleteWarning':
    'لا يمكن التراجع عن هذا. سيتم حذف حسابه وسجلاته وما أنشأه من النظام نهائيًا، ومعها الملفات.',
  'employees.deleteSubmit': 'حذف نهائي',
  'employees.deleteMade': 'ما أنشأه — سيتم حذفه مع ردود الآخرين عليه',
  'employees.deleteOwn': 'سجلاته الخاصة — سيتم حذفها',
  'employees.deleteHandOver': 'ينتقل إلى من يتولى بعده',
  'employees.deleteReleased': 'ما أسنده إليه الآخرون — يبقى بلا مسؤول',
  'employees.deleteNothing': 'لا شيء',
  'employees.deleteKept':
    'يبقى في النظام: ما أدخله من فواتير ودفعات ومصروفات وميزانيات، مع ملفاتها. ويحتفظ سجل التدقيق بما فعله في السجلات الباقية بدون اسمه، وبسطر واحد يذكر من حذفه.',
  'employees.deleteSignedOffPayslips':
    'اعتمدت المالية رواتب {months} بالفعل. وسيتم حذف قسائم راتبه من هذه الأشهر أيضًا، ويبقى إجمالي المدفوع في كل شهر كما كان.',
  'employees.deleteSuccessor': 'من يتولى بعده',
  'employees.deleteSuccessorHint': 'تنتقل مشاريعه وعقوده وحسابات دخول العملاء التي أنشأها إلى هذا الشخص.',
  'employees.deleteConfirm': 'اكتب بريده الإلكتروني للتأكيد',
  'employees.deleteConfirmHint': 'اكتب:',
  'employees.deleted': 'تم حذف {name} مع كل ما كان يخصه.',
  'employees.deleteSelf': 'لا يمكنك حذف نفسك.',
  'employees.deleteLastSysAdmin':
    'لن يبقى أحد يستطيع إعطاء صلاحية الدخول إلى المالية. اجعل شخصًا آخر مديرًا للمالية هناك أولًا.',
  'employees.deleteConfirmMismatch': 'البريد الذي كتبته ليس بريده، فلم يتم حذف أي شيء.',
  'employees.deleteSuccessorRequired': 'اختر من يتولى مشاريعه وعقوده وحسابات دخول العملاء.',
  'employees.deleteSuccessorInvalid': 'يجب أن يكون من يتولى بعده موظفًا آخر نشطًا.',
  'employees.del.tasks': 'المهام',
  'employees.del.tickets': 'التذاكر',
  'employees.del.qTasks': 'مهام الطابور',
  'employees.del.announcements': 'الإعلانات',
  'employees.del.mails': 'الرسائل المرسلة',
  'employees.del.workflows': 'سير العمل',
  'employees.del.comments': 'تعليقاته وردوده على عمل الآخرين',
  'employees.del.checklistItems': 'بنود المطلوب إنجازه في مهام الآخرين',
  'employees.del.attendanceDays': 'ورديات الحضور',
  'employees.del.leaves': 'طلبات الإجازة',
  'employees.del.rosterDays': 'أيام جدول الورديات',
  'employees.del.salaryRecords': 'سجلات الراتب',
  'employees.del.payslips': 'قسائم الراتب',
  'employees.del.penalties': 'الجزاءات',
  'employees.del.violations': 'المخالفات',
  'employees.del.mailReceived': 'الرسائل الواردة',
  'employees.del.chats': 'المحادثات، برسائل الطرفين',
  'employees.del.groupMessages': 'رسائله في مجموعات المحادثة، وتبقى المجموعات للآخرين',
  'employees.del.notes': 'الملاحظات وبنود قائمة المطلوب إنجازه',
  'employees.del.notifications': 'الإشعارات',
  'employees.del.projects': 'المشاريع',
  'employees.del.contracts': 'العقود',
  'employees.del.clientAccounts': 'حسابات دخول العملاء',
  'employees.del.openTasks': 'المهام المفتوحة',
  'employees.del.openTickets': 'التذاكر المفتوحة — تعود لإسنادها من جديد',
  'employees.del.openQTasks': 'مهام الطابور — تعود إلى الطابور',
  'employees.departmentRole': 'عضو أو قائد',
  'employees.addDepartment': 'إضافة قسم',
  'employees.removeDepartment': 'إزالة هذا القسم',
  'employees.departmentsEditHint': 'إخراج الموظف من قسم ينهي عضويته فيه اليوم، ويبقى سجل العضوية محفوظًا.',
  'employees.salary': 'الراتب',
  'employees.salaryHint': 'لا يراه إلا من يتولى الرواتب في المالية.',
  'employees.salaryCreateHint': 'مطلوب. بعد الحفظ، لا يراه ولا يغيّره إلا من يتولى الرواتب في المالية.',
  'employees.salaryNewHint': 'شهريًا. يتم ضبط العمل الإضافي والبدلات في المالية.',
  'employees.salaryFirstHint': 'اليوم الذي يبدأ فيه الراتب، وهو عادةً أول يوم عمل له. ويتم صرف راتب كل شهر حسب الراتب الساري في آخر يوم منه.',
  'employees.salaryFromHint': 'يبدأ المبلغ الجديد سجل راتب جديدًا من هذا اليوم، وينتهي السجل السابق في اليوم الذي قبله.',
  'employees.salaryNow': 'الراتب الحالي: {amount} منذ {date}',
  'employees.salaryNext': 'اعتبارًا من {date}: {amount}',
  'employees.salarySince': 'منذ {date}',
  'employees.salaryNone': 'لا يوجد سجل راتب بعد',
  'employees.salaryBookedHint': 'لم يبدأ بعد، فيمكنك تغيير مبلغه أو يوم بدايته.',
  'employees.salaryStartsLater': 'يبدأ هذا الراتب في {date}. وحتى ذلك اليوم لا يوجد راتب، فلن يظهر هذا الموظف في كشف الرواتب، ولن يكون لجزاءاته قيمة مالية.',
  'tickets.edit': 'تعديل التذكرة',
  'tickets.saved': 'تم حفظ التذكرة.',
  'tickets.start': 'بدء العمل',
  'tickets.editableUntilStarted': 'يمكن تعديلها إلى أن يبدأ أحد العمل عليها.',
  'tickets.startedLocked': 'بدأ العمل على هذه التذكرة، فلا يمكن تعديلها الآن.',
  'tickets.editNotYours': 'لا يعدّل هذه التذكرة إلا من فتحها أو قائد فريق أو مدير النظام.',
  'tickets.departmentByReroute': 'ينقلها قائد الفريق إلى قسم آخر من خلال «إعادة التوجيه»، ويبقى سجل المهل كما هو.',
  'tickets.portalClientFixed': 'فتحها العميل من بوابة العملاء، فتبقى له.',
  'tickets.statusNotYours': 'لا يغيّر حالة التذكرة إلا المسؤول عنها أو قائد فريق أو مدير النظام.',
  'tickets.contact': 'بطلب من',
  'tickets.contactHint': 'الشخص الذي طلب هذه التذكرة لدى العميل، وستذكر التذكرة أنها بطلب منه.',
  'tickets.contactPickClient': 'اختر العميل أولًا، لأن من طلبها من جهات اتصاله.',
  'tickets.contactNone': 'لا توجد جهات اتصال لدى {client} بعد.',
  'tickets.contactNotListed': 'ليس في القائمة؟ أضفه إلى جهات اتصال العميل',
  'tickets.contactAddFirst': 'أضف من طلبها',
  'tickets.contactNewTab': 'يفتح في تبويب جديد. بعد الإضافة ارجع إلى هنا واختره.',
  'tickets.contactAskAdmin': 'ليس في القائمة؟ يضيفه مدير النظام في صفحة العميل.',
  'tickets.contactRemoved': 'تمت إزالتها من جهات الاتصال',
  'tickets.portalNoContact': 'فتحها العميل بنفسه من بوابة العملاء، لذلك لا توجد جهة اتصال.',
  'tickets.contactRefusedMissing': 'اختر من طلب هذه التذكرة لدى العميل.',
  'tickets.contactRefusedRemoved': 'تمت إزالة جهة الاتصال هذه من قائمة العميل للتو. اختر غيرها.',
  'tickets.contactRefusedOther': 'اختر جهة اتصال من جهات هذا العميل.',
  'tickets.raisedBy': 'فتحها',
  'tickets.requestedByName': 'بطلب من {name}',
  'workflows.privateStep': 'خطوة لشخص آخر: لا يفتحها إلا صاحبها وقادة الفرق ومديرو النظام.',
  'tasks.edit': 'تعديل المهمة',
  'tasks.saved': 'تم حفظ المهمة.',
  'tasks.editableUntilStarted': 'يمكن تعديلها إلى أن يبدأ أحد العمل عليها.',
  'tasks.startedLocked': 'بدأ العمل على هذه المهمة، فلا يمكن تعديلها الآن.',
  'tasks.editableWithList': 'يمكن تعديلها حتى يبدأ أحد العمل عليها، ويمكن تعديل «المطلوب إنجازه» حتى تنتهي.',
  'tasks.finishedLocked': 'انتهت هذه المهمة، فلا يمكن تعديلها الآن.',
  'tasks.editNotYours': 'لا يعدّل هذه المهمة إلا من أنشأها أو قائد فريق أو مدير النظام.',
  'tasks.assigneeByReassign': 'يغيّره قائد الفريق أو مدير النظام من خلال «إعادة الإسناد» في صفحة المهمة.',
  'tasks.workflowProjectFixed': 'تبقى مهام سير العمل على المشروع الذي بدأ عليه العمل.',
  'tasks.changeRequestProjects': 'أحد مشاريع {client}، أو بلا مشروع.',
  'tasks.assigneeNotOnProject': '{name}، المسؤول عن هذه المهمة، ليس ضمن هذا المشروع.',
  'tasks.assigneeNotOnProjectOverride': '{name}، المسؤول عن هذه المهمة، ليس ضمن هذا المشروع. يمكنك المتابعة مع ذلك.',
  'tasks.assign': 'إسناد',
  'tasks.reassign': 'إعادة الإسناد',
  'tasks.assignTitle': 'إسناد المهمة',
  'tasks.reassignTitle': 'إعادة إسناد المهمة',
  'tasks.currentAssignee': 'المسؤول الحالي',
  'tasks.assignTo': 'إسناد إلى',
  'tasks.newAssignee': 'المسؤول الجديد',
  'tasks.assignHint': 'سيصله إشعار ورسالة على البريد الإلكتروني.',
  'tasks.reassignNote': 'سيصل السبب إلى المسؤول الحالي، ويبقى محفوظًا في سجل المهمة.',
  'tasks.reassignStepHint': 'هذه خطوة في سير عمل: يتولاها من تختاره، ولن يستطيع صاحبها السابق فتحها بعد ذلك.',
  'tasks.assignNotYours': 'لا يسند المهمة أو يعيد إسنادها إلا قائد فريق أو مدير النظام.',
  'tasks.finishedNoReassign': 'انتهت هذه المهمة، فلا يمكن نقلها إلى شخص آخر.',
  'tasks.assignedTo': 'تم الإسناد إلى {name}.',
  'tasks.activity.reassigned': 'تمت إعادة الإسناد',
  'tasks.activity.edited': 'تم تعديل: {fields}',
  'tasks.activity.assignedTo': 'تم إسنادها إلى {name}',
  'tasks.activity.reassignedTo': 'تمت إعادة إسنادها إلى {name}',
  'tasks.toPerson': 'شخص',
  'tasks.toPersonHint': 'يتولاها موظف واحد.',
  'tasks.toWorkflow': 'سير عمل',
  'tasks.toWorkflowHint': 'المستوى 1 أولًا، ثم المستويات التالية بالترتيب.',
  'tasks.assignWorkflowHint':
    'يعمل المستوى 1 حسب تاريخ بدء هذه المهمة وموعدها النهائي. ويبدأ كل مستوى بعده عندما ينتهي المستوى الذي قبله، ويحدّد أفراده موعدهم النهائي بأنفسهم.',
  'tasks.keepsThisTask':
    'يحصل {name} على هذه المهمة نفسها بتعليقاتها وسجلها، ويحصل كل من الآخرين على نسخة ليعملوا عليها في الوقت نفسه.',
  'tasks.sentToWorkflow': 'تم إرسالها عبر سير العمل. المستوى 1: {names}.',
  'tasks.activity.workflowAssigned': 'تم إسنادها إلى سير العمل {workflow}',
  'tasks.throughWorkflow': 'عبر سير عمل — أشخاص مستوى بعد مستوى',
  'tasks.workflowBelow': 'حدّد مستوياته في الأسفل.',
  'tasks.workflowOffProject': 'في سير العمل شخص ليس ضمن هذا المشروع (عليه علامة). احذفه من سير العمل، أو أضفه إلى المشروع.',
  'tasks.workflowOffProjectOverride': 'في سير العمل شخص ليس ضمن هذا المشروع (عليه علامة). يمكنك المتابعة مع ذلك.',
  'tasks.theWorkflow': 'سير العمل',
  'tasks.workflowStartedBy': 'بدأه {name}',
  'tasks.activity.workflowSent': 'تم إرسالها عبر سير عمل',
  'tasks.activity.returned': 'تمت إعادتها إلى {name} للمراجعة',
  'tasks.activity.returnedPlain': 'تمت إعادتها للمراجعة',
  'tasks.activity.returnedBack': 'عادت إلى {name} بعد أن راجعها {from}',
  'tasks.activity.returnedBackPlain': 'عادت بعد المراجعة',
  'tasks.activity.calledBack': 'تم استرجاعها إلى {name} من {from}',
  'tasks.return.action': 'إعادتها إلى شخص سابق',
  'tasks.return.title': 'إعادتها إلى شخص سابق',
  'tasks.return.to': 'أعدها إلى',
  'tasks.return.toHint': 'شخص أنجز العمل في مستوى سابق من سير العمل هذا.',
  'tasks.return.option': '{name} · المستوى {n}',
  'tasks.return.note': 'ماذا يراجع؟',
  'tasks.return.noteHint': 'سيقرأ هذه الملاحظة أولًا. وعندما ينتهي، تعود المهمة إليك، ولا تمر بالمستويات مرة أخرى.',
  'tasks.return.submit': 'إعادة',
  'tasks.return.sent': 'تمت إعادتها إلى {name}، وستعود إليك عندما ينتهي.',
  'tasks.return.notYours': 'لا يعيد الخطوة إلا صاحبها، أثناء عمله عليها، وبعد المستوى الأول فقط.',
  'tasks.return.checkTitle': 'يطلب منك {name} مراجعة أمر',
  'tasks.return.checkBody': 'عندما تنتهي، تعود المهمة إلى {name}، ولا تنتقل إلى بقية سير العمل.',
  'tasks.return.outTitle': 'لدى {name} للمراجعة',
  'tasks.return.outBody': 'تعود إليك حين ينتهي. ويمكنك استرجاعها قبل ذلك.',
  'tasks.return.othersTitle': 'لدى {checker} للمراجعة، من أجل {owner}',
  'tasks.return.othersBody': 'تعود إلى {owner} حين تنتهي المراجعة.',
  'tasks.return.since': 'تمت إعادتها {when}',
  'tasks.return.done': 'انتهيت، أعدها إلى {name}',
  'tasks.return.takeBack': 'استرجاعها',
  'tasks.return.bringBack': 'أعدها إلى {name}',
  'tasks.return.backTitle': 'إعادتها إلى {name}',
  'tasks.return.takeBackTitle': 'استرجاعها من {name}',
  'tasks.return.asked': 'ما طلبه {name}',
  'tasks.return.backNote': 'ملاحظة إلى {name}',
  'tasks.return.backNoteHint': 'اختياري: ما وجدته. ويمكنك أيضًا استخدام تعليقات المهمة.',
  'tasks.return.back': 'عادت إلى {name}.',
  'tasks.return.notOut': 'هذه المهمة ليست قيد المراجعة لدى أحد.',
  'tasks.return.notYoursToEnd': 'لا يعيدها إلا من يراجعها، أو من طلب المراجعة، أو قائد فريق، أو مدير النظام.',
  'tasks.return.listHint': 'قيد المراجعة، وستعود إلى {name}',
  'tasks.return.stepChecking': 'لدى {name} للمراجعة',
  'tasks.workflowEdit.title': 'تعديل سير العمل',
  'tasks.workflowEdit.reached': 'المستويات التي وصلها العمل',
  'tasks.workflowEdit.reachedHint': 'وصل العمل إلى هذه المستويات، فتبقى كما هي.',
  'tasks.workflowEdit.ahead': 'المستويات القادمة',
  'tasks.workflowEdit.aheadHint':
    'لم يصلها العمل بعد: غيّر من فيها، أو أضف مستويات أو احذفها، أو غيّر ترتيبها. ولن يصل إشعار إلى أي شخص فيها حتى يأتي دوره.',
  'tasks.workflowEdit.none': 'لا توجد مستويات بعد المستوى {n}، فهو الأخير، وعمله ينتظر المراجعة عندما يكتمل.',
  'tasks.workflowEdit.submit': 'حفظ سير العمل',
  'tasks.workflowEdit.saved': 'تم حفظ سير العمل.',
  'tasks.workflowEdit.notYours': 'لا يغيّر سير العمل إلا من بدأ هذا العمل، أو قائد فريق، أو مدير النظام.',
  'tasks.workflowEdit.notRunning': 'سير العمل هذا اكتمل أو توقّف: لا مستويات قادمة لتغييرها.',
  'tasks.workflowEdit.noWorkflow': 'هذه المهمة ليست خطوة في سير عمل.',
  'tasks.workflowEdit.movedOn':
    'في هذه الأثناء تقدّم العمل، أو غيّر شخص آخر سير العمل. تظهر المستويات القادمة الآن كما هي، فأعد التغيير الذي تريده.',
  'tasks.workflowEdit.inactive': 'لم يعد نشطًا، وسيتم تخطيه عند دوره',
  'tasks.activity.workflowEdited': 'تغيّر سير العمل بعد المستوى {n}',
  'tasks.activity.workflowEditedPlain': 'تغيّر سير العمل',
  'tasks.activity.workflowEditedAdded': 'تمت إضافة {names}',
  'tasks.activity.workflowEditedRemoved': 'تمت إزالة {names}',
  'tasks.activity.workflowEditedLevels': 'عدد المستويات: {from} ← {to}',
  'tasks.activity.workflowEditedMoved': 'تمت إعادة ترتيب مستوياته',
  'tasks.toDepartment': 'قسم',
  'tasks.toDepartmentHint': 'يعمل عليها كل من فيه معًا.',
  'tasks.everyoneIn': 'كل من في {department}',
  'tasks.sharedHint': 'يراها كل من في القسم ويعمل عليها.',
  'tasks.assignDepartmentHint':
    'أحد أقسامك. كل من فيه يرى المهمة ويعمل عليها: التعليقات والملفات و«المطلوب إنجازه» وتغيير حالتها، ويصله إشعار ورسالة على البريد الإلكتروني.',
  'tasks.assignAnyDepartmentHint':
    'كل من فيه يرى المهمة ويعمل عليها: التعليقات والملفات و«المطلوب إنجازه» وتغيير حالتها، ويصله إشعار ورسالة على البريد الإلكتروني.',
  'tasks.noDepartmentOfYours': 'لست في أي قسم، فلا يوجد قسم يمكنك إسناد المهمة إليه.',
  'tasks.departmentEmpty': 'لا أحد في {department} بعد، فلن تصل المهمة إلى أحد.',
  'tasks.departmentNobodyOnProject':
    'لا أحد من {department} ضمن مشروع هذه المهمة، فلن يراها أحد منهم. أضفهم إلى المشروع، أو اختر شخصًا.',
  'tasks.departmentPartlyOnProject': 'لا يراها إلا من هم ضمن المشروع. خارج المشروع: {names}.',
  'tasks.sentToDepartment': 'تم إسنادها إلى كل من في {department}.',
  'tasks.activity.assignedToDepartment': 'تم إسنادها إلى كل من في {department}',
  'tasks.activity.reassignedToDepartment': 'تمت إعادة إسنادها إلى كل من في {department}',
  'tasks.people': 'أشخاص',
  'tasks.wholeDepartment': 'قسم بأكمله',
  'tasks.departmentIsAssignee': 'القسم الذي تسند إليه المهمة، وتختاره في خانة «المسؤول».',
  'tasks.departmentShared': 'يشترك فيها القسم كله، ويمكن لقائد الفريق أو مدير النظام إسنادها إلى قسم آخر من خلال «إعادة الإسناد».',
  'qtasks.edit': 'تعديل مهمة الطابور',
  'qtasks.saved': 'تم حفظ مهمة الطابور.',
  'qtasks.editableUntilStarted': 'يمكن تعديلها إلى أن يبدأ أحد العمل عليها.',
  'qtasks.startedLocked': 'بدأ العمل على مهمة الطابور هذه، فلا يمكن تعديلها الآن.',
  'qtasks.editNotYours': 'لا يعدّل مهمة الطابور هذه إلا من أنشأها أو قائد قسمها أو مدير النظام.',
  'qtasks.departmentFixed': 'تبقى مهمة الطابور في طابور قسمها.',
  'qtasks.assigneeByAssign': 'يغيّر قائد القسم المسؤول عنها ومواعيدها من خلال «إسناد» في صفحة مهمة الطابور.',
  'qtasks.activity.edited': 'تم تعديل: {fields}',
  'nav.leaderTasks': 'مهام القادة',
  'leaderTasks.title': 'مهام القادة',
  'leaderTasks.subtitle':
    'من قائد قسم إلى قائد آخر. ينجزها القائد المطلوبة منه، أو يسندها إلى قسمه أو إلى سير عمل، ثم يراجع ذلك العمل حين يكتمل.',
  'leaderTasks.badge': 'مهمة قادة',
  'leaderTasks.new': 'مهمة قادة جديدة',
  'leaderTasks.received': 'مطلوبة مني',
  'leaderTasks.sent': 'طلبتها أنا',
  'leaderTasks.all': 'الكل',
  'leaderTasks.from': 'من',
  'leaderTasks.to': 'إلى',
  'leaderTasks.work': 'العمل المسند',
  'leaderTasks.work.none': 'لم يتم الإسناد',
  'leaderTasks.work.underWay': 'قيد التنفيذ',
  'leaderTasks.work.finished': 'اكتمل',
  'leaderTasks.work.calledOff': 'ملغى',
  'leaderTasks.emptyReceived': 'لا توجد مهام قادة مطلوبة منك.',
  'leaderTasks.emptySent': 'لم ترسل أي مهمة قادة بعد.',
  'leaderTasks.leader': 'القائد',
  'leaderTasks.leaderHint': 'قائد قسم، والقسم الذي تخصه. ينجزها بنفسه، أو يسندها إلى قسمه أو إلى سير عمل.',
  'leaderTasks.leaderOf': '{name} — {department}',
  'leaderTasks.noLeaders': 'لا يوجد قائد قسم آخر بعد. يمكنك تعيين قائد القسم من قائمة أعضائه باختيار «قائد».',
  'leaderTasks.onlyLeaders': 'لا يرسل مهام القادة إلا قادة الأقسام ومديرو النظام.',
  'leaderTasks.sentTo': 'تم إرسالها إلى {name}.',
  'leaderTasks.handOn': 'إسناد',
  'leaderTasks.reassign': 'إسنادها إلى قائد آخر',
  'leaderTasks.askedOf': 'مطلوبة من',
  'leaderTasks.sentBy': 'أرسلها',
  'leaderTasks.move.start': 'بدء',
  'leaderTasks.move.unblock': 'رفع التعليق',
  'leaderTasks.move.backToWork': 'العودة إلى العمل',
  'leaderTasks.move.block': 'تعليق',
  'leaderTasks.move.review': 'إلى المراجعة',
  'leaderTasks.move.done': 'اعتمادها منجزة',
  'leaderTasks.move.cancel': 'إلغاؤها',
  'leaderTasks.move.cancelYes': 'نعم، ألغها — لا يمكن إعادة فتحها',
  'leaderTasks.move.cancelNo': 'أبقها',
  'leaderTasks.move.toDo': 'إعادتها إلى «قيد الانتظار»',
  'leaderTasks.move.reopen': 'إعادة فتحها',
  'leaderTasks.moved': 'تم نقلها إلى {status}.',
  'leaderTasks.yoursTitle': 'طلبها منك {name}',
  'leaderTasks.yoursBody':
    'أنجزها بنفسك، أو أسندها إلى أحد أفراد قسمك أو إلى سير عمل. حين يكتمل ذلك العمل تعود إليك لتراجعه.',
  'leaderTasks.reviewTitle': 'اكتمل العمل، والمراجعة عليك',
  'leaderTasks.reviewBody': 'راجع العمل المسند، ثم اعتمد مهمة القادة هذه منجزة، وسيصل إشعار إلى {name}.',
  'leaderTasks.waitingReview': 'اكتمل العمل، ويراجعه {name}.',
  'leaderTasks.calledOffTitle': 'تم إلغاء العمل المسند',
  'leaderTasks.calledOffBody': 'أسندها من جديد، أو أنجزها بنفسك.',
  'leaderTasks.workTitle': 'العمل المسند',
  'leaderTasks.workNone': 'لم يتم إسنادها بعد.',
  'leaderTasks.workflowLevel': 'المستوى {n} من {of}',
  'leaderTasks.fromLeaderTask': 'من مهمة قادة',
  'leaderTasks.filesTitle': 'ملفات مهمة القادة',
  'leaderTasks.filesHint': 'تم إرسالها مع «{title}».',
  'leaderTasks.activity.handedOnTo': 'تم إسنادها إلى {name}',
  'leaderTasks.activity.handedOnToWorkflow': 'تم إسنادها إلى سير العمل {workflow}',
  'leaderTasks.activity.handedOnThroughWorkflow': 'تم إسنادها عبر سير عمل',
  'leaderTasks.activity.workFinished': 'اكتمل العمل المسند، وعادت للمراجعة',
  'leaderTasks.activity.workReopened': 'تمت إعادة فتح العمل، وعادت إلى «قيد التنفيذ»',
  'leaderTasks.activity.workCalledOff': 'تم إلغاء العمل المسند',
  'leaderTasks.activity.departmentChanged': 'تم نقلها إلى {department}',
  'leaderTasks.activity.createdFrom': 'تم إنشاؤها من مهمة القادة «{title}»',
  'leaderTasks.handOnTitle': 'إسناد مهمة القادة',
  'leaderTasks.toPerson': 'أحد أفراد القسم',
  'leaderTasks.toPersonHint': 'ينجزها شخص واحد كمهمة خاصة به.',
  'leaderTasks.toWorkflow': 'سير عمل',
  'leaderTasks.toWorkflowHint': 'المستوى 1 أولًا، ثم المستويات التالية بالترتيب.',
  'leaderTasks.person': 'الشخص',
  'leaderTasks.personHint': 'أفراد الأقسام التي يقودها {name}.',
  'leaderTasks.notOnProject': 'ليس في المشروع',
  'leaderTasks.notOnProjectError': '{name} ليس في هذا المشروع. اختر أحد أعضائه، أو اجعلها مهمة عامة.',
  'leaderTasks.nobodyToHandTo': 'لا يوجد أحد آخر في الأقسام التي يقودها {name}.',
  'leaderTasks.handedOnTo': 'تم إسنادها إلى {name}.',
  'leaderTasks.handedOnThroughWorkflow': 'تم إسنادها عبر سير العمل.',
  'leaderTasks.workflowPeopleHint': 'كل من فيه من الأقسام التي يقودها {name}.',
  'leaderTasks.filesGoWith': 'تنتقل ملفاتها مع العمل إلى كل مهمة تنشأ منها.',
  'leaderTasks.datesHint': 'تاريخ بدء العمل وموعده النهائي، وفي سير العمل هما للمستوى 1.',
  'leaderTasks.cannotHandOnUnderWay': 'عملها قيد التنفيذ، ولا يمكن إسنادها من جديد إلا إذا تم إلغاء ذلك العمل.',
  'leaderTasks.cannotHandOnStatus': 'حالتها {status}، فلا يمكن إسنادها.',
  'leaderTasks.cannotHandOnNotYours': 'لا يسندها إلا القائد المطلوبة منه أو مدير النظام.',
  'leaderTasks.reassignTitle': 'إسنادها إلى قائد آخر',
  'leaderTasks.currentLeader': 'لدى',
  'leaderTasks.newLeader': 'أسندها إلى',
  'leaderTasks.reassignNote':
    'سيصل السبب إلى القائد الذي لديه المهمة الآن. ويستمر العمل الذي تم إسناده سابقًا، ويراجعه القائد الجديد.',
  'leaderTasks.reassignNotYours': 'لا يسندها إلى قائد آخر إلا من أرسلها أو القائد المطلوبة منه أو مدير النظام.',
  'leaderTasks.reassigned': 'تم الإسناد إلى {name}.',
  'leaderTasks.departmentFixed': 'تخص قسمًا يقوده قائدها، ويتغير القسم من خلال «إسنادها إلى قائد آخر».',
  'leaderTasks.leaderByReassign': 'تنتقل إلى قائد آخر من خلال «إسنادها إلى قائد آخر» في صفحتها.',
  'leaderTasks.editNotYours': 'لا يعدّلها إلا من أرسلها أو مدير النظام.',
  'dash.leaderTasks': 'مهام القادة',
  'dash.leaderTasksOpen': '{n} مطلوبة منك ولم تنته بعد',
  'dash.leaderTasksReview': '{n} عادت لمراجعتك',
  'tasks.ownOnlyHint': 'المهام المسندة إليك أو إلى قسمك، أو التي أنشأتها.',
  'tasks.checklist.title': 'المطلوب إنجازه',
  'tasks.checklist.progress': 'تم إنجاز {done} من {total}',
  'tasks.checklist.empty': 'لا شيء في القائمة بعد. أضف المطلوب إنجازه، بندًا بندًا.',
  'tasks.checklist.addPlaceholder': 'إضافة بند',
  'tasks.checklist.editLine': 'تعديل البند',
  'tasks.checklist.removeLine': 'إزالة البند',
  'tasks.checklist.removed': 'تمت إزالة البند من القائمة.',
  'tasks.checklist.doneBy': 'أنجزه {name} · {when}',
  'tasks.checklist.savedAsYouGo':
    'يتم حفظ كل تغيير في القائمة فورًا، مهما كانت حالة المهمة. أما زر «حفظ» فهو للحقول التي في الأعلى.',
  'tasks.checklist.whoChanges': 'يستطيع تعديل هذه القائمة: من أنشأ المهمة، والمسؤول عنها أو من يشترك فيها، وقادة الفرق، ومديرو النظام.',
  'tasks.checklist.full': 'لا تتسع القائمة لأكثر من {n} بند.',
  'tasks.checklist.chipTitle': 'المطلوب إنجازه: تم إنجاز {done} من {total}',
  'tasks.activity.checklistAdded': 'تمت إضافة بند إلى «المطلوب إنجازه»',
  'tasks.activity.checklistDone': 'تم تعليم بند في القائمة كمنجز',
  'tasks.activity.checklistUndone': 'تمت إزالة علامة الإنجاز عن بند في القائمة',
  'tasks.activity.checklistRenamed': 'تم تعديل بند في القائمة',
  'tasks.activity.checklistRemoved': 'تمت إزالة بند من «المطلوب إنجازه»',
  'tasks.activity.viewed': 'اطّلع عليها',
  'nav.mail': 'البريد',
  'mail.title': 'البريد',
  'mail.subtitle': 'رسائل بين موظفي الشركة داخل النظام فقط، ولا تصل إلى البريد الإلكتروني أبدًا.',
  'mail.new': 'رسالة جديدة',
  'mail.folder.inbox': 'الوارد',
  'mail.folder.sent': 'المرسلة',
  'mail.folder.trash': 'المحذوفات',
  'mail.search': 'ابحث في المواضيع والمحتوى والأسماء',
  'mail.unreadOnly': 'غير المقروءة فقط',
  'mail.markAllRead': 'تعليم الكل كمقروء',
  'mail.allRead': 'كل الرسائل في الوارد مقروءة الآن.',
  'mail.empty.inbox': 'صندوق الوارد فارغ',
  'mail.empty.inboxHint': 'تصل إلى هنا الرسائل التي يرسلها إليك الموظفون، ويصلك إشعار بها.',
  'mail.empty.unread': 'لا رسائل غير مقروءة',
  'mail.empty.unreadHint': 'قرأت كل الرسائل في صندوق الوارد.',
  'mail.empty.sent': 'لم ترسل شيئًا بعد',
  'mail.empty.sentHint': 'هنا تجد الرسائل التي ترسلها إلى الموظفين.',
  'mail.empty.trash': 'سلة المحذوفات فارغة',
  'mail.empty.trashHint': 'تبقى هنا الرسائل التي تحذفها حتى تستعيدها أو تحذفها نهائيًا.',
  'mail.toNames': 'إلى {names}',
  'mail.more': '+{n}',
  'mail.readBy': 'قرأها {read} من {total}',
  'mail.readByAll': 'قرأها الجميع',
  'mail.notReadYet': 'لم تتم قراءتها بعد',
  'mail.read': 'مقروءة',
  'mail.readAt': 'قرأها في {when}',
  'mail.newBadge': 'جديدة',
  'mail.replyBadge': 'رد',
  'mail.forwardBadge': 'إعادة توجيه',
  'mail.files': 'الملفات',
  'mail.voiceNotes': 'الرسائل الصوتية',
  'mail.unreadCount': 'غير المقروءة: {n}',
  'mail.from': 'من',
  'mail.to': 'إلى',
  'mail.date': 'التاريخ',
  'mail.you': 'أنت',
  'mail.left': 'لم يعد موظفًا',
  'mail.inReplyTo': 'ردًا على',
  'mail.forwardOf': 'الرسالة المعاد توجيهها',
  'mail.noContent': 'لا يوجد محتوى، فالموضوع والمرفقات يكفيان.',
  'mail.inTrash': 'هذه الرسالة في سلة المحذوفات. استعدها لإرجاعها، أو احذفها نهائيًا.',
  'mail.reply': 'رد',
  'mail.replyAll': 'رد على الكل',
  'mail.forward': 'إعادة توجيه',
  'mail.markUnread': 'تعليم كغير مقروءة',
  'mail.markedUnread': 'تم تعليمها كغير مقروءة.',
  'mail.delete': 'حذف',
  'mail.movedToTrash': 'تم نقلها إلى سلة المحذوفات.',
  'mail.restore': 'استعادة',
  'mail.restoredInbox': 'عادت إلى الوارد.',
  'mail.restoredSent': 'عادت إلى «المرسلة».',
  'mail.deleteForever': 'حذف نهائي',
  'mail.deleteForeverConfirm': 'حذف هذه الرسالة نهائيًا؟ ستختفي من بريدك فقط، ويحتفظ باقي المشاركين فيها بنسخهم.',
  'mail.keep': 'الاحتفاظ بها',
  'mail.deletedForever': 'تم الحذف نهائيًا.',
  'mail.composeSubtitle': 'داخل النظام فقط، فلا يستلم أحد بريدًا إلكترونيًا. تصل الرسالة إلى صندوق «الوارد» عندهم، وينبّههم الجرس.',
  'mail.replyTitle': 'رد',
  'mail.forwardTitle': 'إعادة توجيه',
  'mail.toLabel': 'إلى',
  'mail.toHint': 'أي موظف. يرى كل من تضيفه بقية المستلمين، فلا توجد نسخة (Cc) ولا نسخة مخفية (Bcc).',
  'mail.toPlaceholder': 'اكتب اسمًا لإضافة شخص',
  'mail.toLoading': 'جاري تحميل الأسماء…',
  'mail.toNoMatch': 'لا يوجد أحد بهذا الاسم.',
  'mail.toRemove': 'إزالة {name}',
  'mail.toMax': 'يمكن إرسال الرسالة إلى {n} شخص على الأكثر.',
  'mail.needRecipient': 'اختر شخصًا واحدًا على الأقل.',
  'mail.subject': 'الموضوع',
  'mail.needSubject': 'اكتب موضوع الرسالة.',
  'mail.content': 'المحتوى',
  'mail.send': 'إرسال',
  'mail.sent': 'تم إرسال الرسالة.',
  'mail.sentTo': 'تم إرسال الرسالة. عدد المستلمين: {n}',
  'mail.passedOn': 'المرفقات المعاد توجيهها',
  'mail.passedOnHint': 'مرفقات الرسالة الأصلية تذهب مع هذه الرسالة. استبعد منها ما لا تريد إرساله.',
  'mail.leaveOut': 'استبعاد',
  'mail.putBack': 'إرجاع',
  'mail.leftOut': 'مستبعد',
  'mail.quoteHeader': 'في {date}، كتب {name}:',
  'mail.forwardHeader': '———— رسالة معاد توجيهها ————',
  'mail.forwardFrom': 'من: {name}',
  'mail.forwardDate': 'التاريخ: {date}',
  'mail.forwardSubject': 'الموضوع: {subject}',
  'mail.forwardTo': 'إلى: {names}',
  'mail.someoneLeft': 'تم استبعاد من لم يعودوا موظفين: {names}',
  'mail.notEmployee': 'أحد من اخترتهم لم يعد موظفًا نشطًا. أزله ثم أرسل من جديد.',
  'mail.sourceMissing': 'الرسالة التي ترد عليها لم تعد في بريدك.',
  'mail.tooLong': 'الرسالة طويلة جدًا. اختصرها أو أرسل الباقي في ملف.',
  'mail.sendMail': 'إرسال رسالة',

  'nav.chat': 'المحادثات',
  'chat.title': 'المحادثات',
  'chat.with': 'محادثة مع {name}',
  'chat.new': 'محادثة جديدة',
  'chat.search': 'ابحث أو ابدأ محادثة جديدة',
  'chat.searchPeople': 'ابحث بالاسم أو الوظيفة أو القسم',
  'chat.filter.label': 'عرض',
  'chat.filter.all': 'الكل',
  'chat.filter.unread': 'غير المقروءة',
  'chat.people': 'ابدأ محادثة مع',
  'chat.everyone': 'كل من في الشركة',
  'chat.peopleLoading': 'جاري تحميل الموظفين…',
  'chat.noPeople': 'لا يوجد أحد بهذا الاسم.',
  'chat.noMatch': 'لا توجد محادثة أو موظف بهذا الاسم.',
  'chat.empty.title': 'لا توجد محادثات بعد',
  'chat.empty.hint': 'ابدأ محادثة مع أي موظف في الشركة أو مع مجموعة. المحادثة تبقى داخل النظام، ولا يقرؤها إلا أصحابها.',
  'chat.empty.unread': 'لا شيء غير مقروء',
  'chat.empty.unreadHint': 'قرأت كل المحادثات.',
  'chat.noMessages': 'لا رسائل بعد',
  'chat.unreadCount': 'غير المقروءة: {n}',
  'chat.unreadChats': 'محادثات بها رسائل غير مقروءة: {n}',
  'chat.intro.title': 'محادثات فيجانيوم',
  'chat.intro.body': 'اختر محادثة، أو ابدأ محادثة جديدة مع أي موظف في الشركة أو مع مجموعة.',
  'chat.intro.private': 'داخل النظام فقط، ولا يقرأ المحادثة إلا أصحابها.',
  'chat.back': 'العودة إلى المحادثات',
  'chat.left': 'لم يعد موظفًا',
  'chat.cannotSend': '{name} لم يعد موظفًا، لذلك يمكنك قراءة هذه المحادثة دون الكتابة فيها.',
  'chat.notFound': 'هذه المحادثة غير متاحة',
  'chat.notFoundHint': 'ربما تم حذفها، أو أنها ليست محادثتك.',
  'chat.openFailed': 'لم نتمكن من فتح المحادثة.',
  'chat.start': 'هذه بداية محادثتك مع {name}. لا يقرؤها أحد غيركما.',
  'chat.startHint': 'ابدأ بالتحية برسالة أو صورة أو رسالة صوتية.',
  'chat.today': 'اليوم',
  'chat.yesterday': 'أمس',
  'chat.unreadOne': 'رسالة واحدة غير مقروءة',
  'chat.unreadMany': 'رسائل غير مقروءة: {n}',
  'chat.loadingOlder': 'جاري تحميل الرسائل السابقة…',
  'chat.jumpDown': 'الانتقال إلى أحدث رسالة',
  'chat.placeholder': 'اكتب رسالة',
  'chat.send': 'إرسال',
  'chat.emoji': 'الرموز التعبيرية',
  'chat.attach': 'إرسال صور أو ملفات',
  'chat.record': 'تسجيل رسالة صوتية',
  'chat.sending': 'جاري الإرسال',
  'chat.sent': 'تم الإرسال',
  'chat.read': 'مقروءة',
  'chat.failed': 'لم يتم الإرسال',
  'chat.retry': 'إعادة المحاولة',
  'chat.discard': 'إزالة',
  'chat.preview.voice': 'رسالة صوتية',
  'chat.preview.photo': 'صورة',
  'chat.preview.photos': 'صور: {n}',
  'chat.preview.video': 'فيديو',
  'chat.preview.file': 'ملف',
  'chat.preview.files': 'ملفات: {n}',
  'chat.preview.attachments': 'مرفقات: {n}',
  'chat.viewer.open': 'فتح {name}',
  'chat.viewer.position': '{i} من {n}',
  'chat.viewer.previous': 'الصورة السابقة',
  'chat.viewer.next': 'الصورة التالية',
  'chat.emoji.recent': 'المستخدمة مؤخرًا',
  'chat.emoji.noRecent': 'تظهر هنا الرموز التي تستخدمها.',
  'chat.emoji.smileys': 'الوجوه والأشخاص',
  'chat.emoji.nature': 'الحيوانات والطبيعة',
  'chat.emoji.food': 'الطعام والشراب',
  'chat.emoji.activities': 'الأنشطة',
  'chat.emoji.travel': 'السفر والأماكن',
  'chat.emoji.objects': 'الأشياء',
  'chat.emoji.symbols': 'الرموز',
  'chat.online': 'متصل الآن',
  'chat.settings': 'إعدادات المحادثات',
  'chat.enterSends': 'زر Enter يرسل الرسالة',
  'chat.enterSendsOn': 'وShift+Enter يبدأ سطرًا جديدًا.',
  'chat.enterSendsOff': 'عند إيقافه: Enter يبدأ سطرًا جديدًا، وCtrl+Enter يرسل.',
  'chat.sendCtrl': 'إرسال (Ctrl+Enter)',
  'chat.menu': 'خيارات الرسالة',
  'chat.reply': 'رد',
  'chat.forward': 'إعادة توجيه',
  'chat.forwarded': 'معاد توجيهها',
  'chat.you': 'أنت',
  'chat.replyingTo': 'الرد على {name}',
  'chat.cancelReply': 'إلغاء الرد',
  'chat.quoteJump': 'الانتقال إلى الرسالة',
  'chat.quoteGone': 'هذه الرسالة قديمة جدًا، ولا يمكن عرضها هنا.',
  'chat.mention.people': 'الإشارة إلى شخص',
  'chat.mention.references': 'الإشارة إلى مهمة أو تذكرة',
  'chat.mention.inChat': 'في هذه المحادثة',
  'chat.mention.others': 'بقية الموظفين',
  'chat.mention.task': 'مهمة',
  'chat.mention.leaderTask': 'مهمة قادة',
  'chat.mention.searching': 'جاري البحث…',
  'chat.mention.noMatch': 'لا توجد مهمة أو تذكرة يمكنك فتحها تطابق «{q}».',
  'chat.mention.none': 'لا توجد مهمة أو تذكرة للإشارة إليها بعد.',
  'chat.mention.keys': '↑ ↓ للتنقل · Enter أو Tab للاختيار · Esc للإغلاق',
  'chat.mention.message': 'مراسلة {name}',
  'chat.mention.you': 'أنت',
  'chat.mention.left': '{name} لم يعد موظفًا',
  'chat.mention.openTask': 'فتح المهمة: {title} — {status}',
  'chat.mention.openTicket': 'فتح التذكرة {number}: {title} — {status}',
  'chat.mention.cannotOpenTask': 'مهمة لا يمكنك فتحها',
  'chat.mention.cannotOpenTicket': 'تذكرة لا يمكنك فتحها',
  'chat.mention.unread': 'تمت الإشارة إليك',
  'chat.mention.gone': 'تلك الرسالة لم تعد في المحادثة.',
  'chat.forwardTitle': 'إعادة توجيه الرسالة',
  'chat.forwardHint': 'اختر حتى {n} محادثات، مجموعات أو أشخاصًا. تصل الرسالة إلى كل منها وعليها علامة «معاد توجيهها».',
  'chat.forwardDone': 'تمت إعادة توجيه الرسالة.',
  'chat.forwardDoneMany': 'تمت إعادة توجيه الرسالة. عدد المحادثات: {n}',
  'chat.forwardMax': 'هذا هو الحد الأقصى: {n} محادثات.',
  'chat.forwardChosen': 'تم اختيار {n}',
  'chat.react': 'تفاعل',
  'chat.reactWith': 'التفاعل بـ {emoji}',
  'chat.reactionTakeOff': 'إزالة تفاعلك {emoji}',
  'chat.reactMore': 'رموز أخرى',
  'chat.reactions': 'التفاعلات',
  'chat.reactionsLabel': 'التفاعلات: {list}',
  'chat.reactionRemove': 'انقر لإزالته',
  'chat.stickers': 'الملصقات',
  'chat.stickers.recent': 'المرسلة مؤخرًا',
  'chat.stickers.noRecent': 'تظهر هنا الملصقات التي ترسلها.',
  'chat.stickers.none': 'لا توجد ملصقات بعد. يضيف مدير النظام حزم الملصقات.',
  'chat.stickers.failed': 'لم نتمكن من تحميل الملصقات. أغلق هذه النافذة وحاول مرة ثانية.',
  'chat.stickers.send': 'إرسال الملصق {label}',
  'chat.stickers.manage': 'إدارة حزم الملصقات',
  'chat.stickerAlt': 'ملصق: {label}',
  'chat.preview.sticker': 'ملصق',
  // Groups (operator, 2026-10-22). Gender-neutral wording: a noun or a passive where a verb would agree.
  'chat.unreadChatsInvites': 'محادثات بها رسائل غير مقروءة: {n} · دعوات المجموعات: {m}',
  'chat.newGroup': 'مجموعة جديدة',
  'chat.filter.groups': 'المجموعات',
  'chat.empty.groups': 'لا توجد مجموعات بعد',
  'chat.empty.groupsHint': 'أنشئ مجموعة وادع إليها أي موظف في الشركة. ينضم كل شخص عندما يقبل الدعوة.',
  'chat.invites.title': 'دعوات المجموعات',
  'chat.invites.from': 'دعوة من {name}',
  'chat.invites.accept': 'قبول',
  'chat.invites.decline': 'اعتذار',
  'chat.invites.acceptLabel': 'قبول الدعوة إلى {name}',
  'chat.invites.declineLabel': 'الاعتذار عن الدعوة إلى {name}',
  'chat.invites.accepted': 'انضممت إلى {name}.',
  'chat.invites.declined': 'اعتذرت عن الدعوة.',
  'chat.invites.hint': 'لن تكون عضوًا في المجموعة حتى تقبل الدعوة. وبعد القبول، تقرأ الرسائل من وقت دعوتك.',
  'chat.group.memberOne': 'عضو واحد',
  'chat.group.membersCount': 'الأعضاء: {n}',
  'chat.group.kind': 'مجموعة',
  'chat.group.info': 'معلومات المجموعة',
  'chat.group.openInfo': 'فتح معلومات المجموعة',
  'chat.group.closeInfo': 'إغلاق معلومات المجموعة',
  'chat.group.with': 'المجموعة {name}',
  'chat.group.start': 'هذه بداية {name}. تبقى المجموعة داخل النظام، ولا يقرؤها إلا أعضاؤها، كل عضو من وقت دعوته.',
  'chat.group.startLater': 'رسائل {name} تبقى داخل النظام، وتقرؤها من وقت دعوتك.',
  'chat.group.startHint': 'ابدأ بالتحية، أو ادع أشخاصًا من «معلومات المجموعة».',
  'chat.group.earlier': 'رسالة من قبل دعوتك',
  'chat.group.someone': 'عضو سابق',
  'chat.group.createdBy': 'أنشأها {name} في {date}',
  'chat.group.createdByYou': 'أنشأتها في {date}',
  'chat.group.createdOn': 'تم إنشاؤها في {date}',
  'chat.group.rename': 'تغيير اسم المجموعة',
  'chat.group.renamed': 'تم تغيير اسم المجموعة.',
  'chat.group.members': 'الأعضاء',
  'chat.group.invite': 'دعوة أشخاص',
  'chat.group.admin': 'مشرف المجموعة',
  'chat.group.adminNote': 'يغيّر المشرفون اسم المجموعة، ويزيلون الأعضاء منها، ويعيّنون مشرفين آخرين. وأي عضو فيها يمكنه دعوة أشخاص.',
  'chat.group.memberMenu': 'خيارات {name}',
  'chat.group.message': 'مراسلة {name}',
  'chat.group.makeAdmin': 'تعيينه مشرفًا للمجموعة',
  'chat.group.dismissAdmin': 'إلغاء الإشراف',
  'chat.group.remove': 'إزالة من المجموعة',
  'chat.group.removeConfirm': 'إزالة {name} من {group}؟',
  'chat.group.removeBody': 'لن يتمكن من القراءة أو الكتابة في المجموعة، وسيصله إشعار بذلك. وللعودة، يجب أن يدعوه أحد أعضائها من جديد.',
  'chat.group.removed': 'تمت إزالة {name} من المجموعة.',
  'chat.group.madeAdmin': 'تمت إضافة {name} إلى مشرفي المجموعة.',
  'chat.group.dismissedAdmin': 'تمت إزالة {name} من مشرفي المجموعة.',
  'chat.group.invited': 'مدعوّون — بانتظار القبول',
  'chat.group.invitedBy': 'دعوة من {name} · {when}',
  'chat.group.invitedByYou': 'دعوة منك · {when}',
  'chat.group.takeBack': 'سحب الدعوة',
  'chat.group.takeBackLabel': 'سحب الدعوة الموجّهة إلى {name}',
  'chat.group.takenBack': 'تم سحب الدعوة.',
  'chat.group.leave': 'مغادرة المجموعة',
  'chat.group.leaveConfirm': 'مغادرة {name}؟',
  'chat.group.leaveBody': 'لن تتمكن من القراءة أو الكتابة فيها. وللعودة، يجب أن يدعوك أحد أعضائها من جديد.',
  'chat.group.leaveLast': 'أنت آخر عضو فيها. إذا غادرت فسيتم حذف المجموعة بكل رسائلها.',
  'chat.group.left': 'غادرت المجموعة.',
  'chat.group.noLongerEmployee': 'لم يعد موظفًا',
  'chat.newGroup.people': 'دعوة إلى المجموعة',
  'chat.newGroup.hint': 'تصل الدعوة إلى كل من تختاره، ولا ينضم إلا إذا قبلها.',
  'chat.newGroup.next': 'التالي',
  'chat.newGroup.chosen': 'تم اختيار {n}',
  'chat.newGroup.untick': 'إزالة {name} من الاختيار',
  'chat.newGroup.name': 'اسم المجموعة',
  'chat.newGroup.namePlaceholder': 'اكتب اسم المجموعة',
  'chat.newGroup.inviting': 'ستصل الدعوات إلى: {names}',
  'chat.newGroup.nobody': 'لا يوجد مدعوون بعد. يمكنك دعوة أشخاص لاحقًا من «معلومات المجموعة».',
  'chat.newGroup.create': 'إنشاء المجموعة',
  'chat.newGroup.created': 'تم إنشاء المجموعة وإرسال الدعوات.',
  'chat.newGroup.createdAlone': 'تم إنشاء المجموعة.',
  'chat.inviteTitle': 'دعوة إلى {name}',
  'chat.inviteHint': 'تصل الدعوة إلى كل من تختاره، ولا ينضم إلا إذا قبلها. ويقرأ الرسائل من الآن فصاعدًا.',
  'chat.inviteSend': 'دعوة',
  'chat.inviteDone': 'الدعوات المرسلة: {n}',
  'chat.inviteNobody': 'الجميع في المجموعة أو مدعوّون إليها.',
  'chat.forwardGroups': 'المجموعات',
  'chat.forwardPeople': 'الأشخاص',
  'chat.event.created': 'أنشأ {who} المجموعة «{name}»',
  'chat.event.createdYou': 'أنشأت المجموعة «{name}»',
  'chat.event.renamed': 'غيّر {who} اسم المجموعة إلى «{name}»',
  'chat.event.renamedYou': 'غيّرت اسم المجموعة إلى «{name}»',
  'chat.event.joined': 'انضم {who} بدعوة من {by}',
  'chat.event.joinedYou': 'انضممت بدعوة من {by}',
  'chat.event.joinedByYou': 'انضم {who} بدعوة منك',
  'chat.event.joinedAlone': 'انضم {who}',
  'chat.event.joinedYouAlone': 'انضممت إلى المجموعة',
  'chat.event.left': 'غادر {who} المجموعة',
  'chat.event.leftYou': 'غادرت المجموعة',
  'chat.event.removed': 'أزال {who} العضو {whom}',
  'chat.event.removedYou': 'أزلت {whom} من المجموعة',
  'chat.event.removedYouBy': 'أزالك {who} من المجموعة',

  // My notes — everyone's own to-do list and sticky notes, which tag tasks and tickets with @ (operator, 2026-10-15).
  'nav.notes': 'ملاحظاتي',
  'nav.todos': 'قائمة المطلوب إنجازه',
  'notes.title': 'ملاحظاتي',
  'notes.sticky.subtitle': 'ملاحظاتك اللاصقة الخاصة بك. لا يراها أحد غيرك، ولا حتى مدير النظام.',
  'notes.todo.title': 'قائمة المطلوب إنجازه',
  'notes.todo.subtitle': 'قائمتك الخاصة بما عليك إنجازه. لا يراها أحد غيرك، ولا حتى مدير النظام.',
  'notes.todo.label': 'بند',
  'notes.todo.placeholder': 'أضف بندًا، واكتب @ للإشارة إلى مهمة أو تذكرة',
  'notes.todo.dueLabel': 'اليوم المحدد له',
  'notes.todo.clearDue': 'إزالة اليوم',
  'notes.todo.today': 'اليوم',
  'notes.todo.tomorrow': 'غدًا',
  'notes.todo.overdue': 'متأخر · {date}',
  'notes.todo.empty': 'لا بنود بعد',
  'notes.todo.emptyHint': 'أضف ما تريد إنجازه، واكتب @ للإشارة إلى مهمة أو تذكرة مسندة إليك.',
  'notes.todo.allDone': 'أنجزت كل شيء، ولا توجد بنود مفتوحة.',
  'notes.todo.done': 'المنجزة',
  'notes.todo.doneWhen': 'أنجزته {when}',
  'notes.todo.clearDone': 'مسح المنجزة',
  'notes.todo.clearDoneConfirm': 'مسح البنود المنجزة؟',
  'notes.todo.clearDoneBody': 'البنود المنجزة: {n}. سيتم حذفها نهائيًا.',
  'notes.todo.cleared': 'تم مسح البنود المنجزة.',
  'notes.todo.edit': 'تعديل البند',
  'notes.todo.remove': 'حذف البند',
  'notes.todo.removed': 'تم حذف البند.',
  'notes.todo.drag': 'اسحب لتغيير الترتيب',
  'notes.sticky.label': 'الملاحظة',
  'notes.sticky.placeholder': 'دوّن ملاحظة، واكتب @ للإشارة إلى مهمة أو تذكرة',
  'notes.sticky.done': 'تم',
  'notes.sticky.doneHint': 'يتم الحفظ أثناء الكتابة',
  'notes.sticky.doneKeys': 'Ctrl+Enter أو Esc',
  'notes.sticky.saving': 'جاري الحفظ…',
  'notes.sticky.saved': 'تم الحفظ',
  'notes.sticky.notSaved': 'لم يتم الحفظ: {reason}',
  'notes.sticky.emptyNotKept': 'لا نحتفظ بملاحظة فارغة. اكتب شيئًا، أو أشر إلى مهمة أو تذكرة، أو أضف صورة.',
  'notes.sticky.emptyDeleteConfirm': 'حذف هذه الملاحظة الفارغة؟',
  'notes.sticky.emptyDeleteBody': 'لم يعد فيها شيء: لا كلمات ولا إشارات ولا صور.',
  'notes.sticky.gone': 'تم حذف هذه الملاحظة في تبويب آخر.',
  'notes.sticky.characters': '{n} من {max} حرف',
  'notes.sticky.addImage': 'إضافة صورة',
  'notes.sticky.removeImage': 'إزالة الصورة',
  'notes.sticky.openImage': 'فتح {name}',
  'notes.sticky.uploading': 'جاري رفع {name}…',
  'notes.sticky.uploadFailed': 'لم نتمكن من إضافة {name}: {reason}',
  'notes.sticky.waitForUpload': 'انتظر حتى يكتمل رفع الصور.',
  'notes.sticky.pinned': 'المثبّتة',
  'notes.sticky.others': 'الأخرى',
  'notes.sticky.pin': 'تثبيت',
  'notes.sticky.unpin': 'إلغاء التثبيت',
  'notes.sticky.color': 'اللون',
  'notes.sticky.edit': 'تعديل الملاحظة',
  'notes.sticky.delete': 'حذف الملاحظة',
  'notes.sticky.drag': 'اسحب لنقل الملاحظة',
  'notes.sticky.deleteConfirm': 'حذف هذه الملاحظة؟',
  'notes.sticky.deleteBody': 'سيتم حذفها نهائيًا.',
  'notes.sticky.deleted': 'تم حذف الملاحظة.',
  'notes.sticky.added': 'أضفتها {when}',
  'notes.sticky.edited': 'عدّلتها {when}',
  'notes.sticky.empty': 'لا ملاحظات بعد',
  'notes.sticky.emptyHint': 'دوّن ما تريد أن يبقى أمام عينيك، واكتب @ للإشارة إلى مهمة أو تذكرة مسندة إليك.',
  'notes.color.AMBER': 'كهرماني',
  'notes.color.CRIMSON': 'قرمزي',
  'notes.color.CYAN': 'سماوي',
  'notes.color.GREEN': 'أخضر',
  'notes.color.VIOLET': 'بنفسجي',
  'notes.color.GRAPHITE': 'رمادي',
  'notes.tag.button': 'الإشارة إلى مهمة أو تذكرة (@)',
  'notes.tag.loading': 'جاري تحميل مهامك وتذاكرك…',
  'notes.tag.none': 'لا يوجد عمل مفتوح مسند إليك الآن.',
  'notes.tag.noMatch': 'لا يوجد ما يطابق البحث في مهامك وتذاكرك المفتوحة.',
  'notes.tag.max': 'حتى {n} إشارات.',
  'notes.tag.group.TASK': 'مهامك',
  'notes.tag.group.Q_TASK': 'مهام الطابور المسندة إليك',
  'notes.tag.group.TICKET': 'تذاكرك',
  'notes.tag.kind.TASK': 'مهمة',
  'notes.tag.kind.LEADER_TASK': 'مهمة قادة',
  'notes.tag.kind.Q_TASK': 'مهمة طابور',
  'notes.tag.kind.TICKET': 'تذكرة',
  'notes.tag.due': 'موعدها {date}',
  'notes.tag.unavailable': '{kind} غير متاحة',
  'notes.tag.unavailableHint': 'لا يمكنك فتحها الآن: لم تعد مسندة إليك، أو تم حذفها.',
  'notes.tag.notYours': 'لم تعد مسندة إليك',
  'notes.tag.remove': 'إزالة الإشارة',
  'notes.problem.EMPTY': 'اكتب شيئًا، أو أشر إلى مهمة أو تذكرة.',
  'notes.problem.EMPTY_NOTE': 'اكتب شيئًا، أو أشر إلى مهمة أو تذكرة، أو أضف صورة.',
  'notes.problem.NOT_AVAILABLE': 'لا يمكنك الإشارة إلا إلى المهام والتذاكر التي يمكنك فتحها.',
  'notes.problem.NOT_A_DAY': 'هذا ليس يومًا صحيحًا في التقويم.',
  'notes.problem.TOO_LONG': 'تتسع الملاحظة لـ {n} حرف على الأكثر.',
  'notes.problem.TOO_MANY_IMAGES': 'تعرض الملاحظة {n} صور على الأكثر.',
  'notes.problem.NOT_AN_IMAGE': 'يمكنك إضافة الصور فقط إلى الملاحظة: PNG أو JPG أو GIF أو WebP.',
  'notes.problem.NOT_ATTACHABLE': 'لا يمكن إضافة هذه الصورة إلى الملاحظة. أضفها مرة أخرى.',
  'notes.reloaded': 'تغيّرت ملاحظاتك في تبويب آخر، فأعدنا تحميلها. حاول مرة ثانية.',
  'notes.drag.instructions': 'لنقله، اضغط مفتاح المسافة ثم مفاتيح الأسهم. اضغط المسافة مرة ثانية لوضعه، أو Escape لإرجاعه إلى مكانه.',
  'notes.drag.picked': 'تم التقاط {name}.',
  'notes.drag.over': '{name}: الموضع {position} من {total}.',
  'notes.drag.dropped': 'أصبح {name} في الموضع {position} من {total}.',
  'notes.drag.cancelled': 'عاد {name} إلى مكانه.',
  'editor.toolbar': 'التنسيق',
  'editor.bold': 'غامق',
  'editor.italic': 'مائل',
  'editor.underline': 'تسطير',
  'editor.strike': 'يتوسطه خط',
  'editor.heading2': 'عنوان',
  'editor.heading3': 'عنوان فرعي',
  'editor.bulletList': 'قائمة نقطية',
  'editor.orderedList': 'قائمة مرقّمة',
  'editor.quote': 'اقتباس',
  'editor.rule': 'خط فاصل',
  'editor.link': 'رابط',
  'editor.unlink': 'إزالة الرابط',
  'editor.undo': 'تراجع',
  'editor.redo': 'إعادة',
  'editor.linkUrl': 'عنوان الرابط',
  'editor.linkApply': 'تطبيق',
  'editor.linkInvalid': 'أدخل عنوان موقع (https://…) أو بريدًا إلكترونيًا.',
  'nav.mobileActivity': 'نشاط الجوال',
  'nav.chatStickers': 'ملصقات المحادثة',
  'stickers.title': 'ملصقات المحادثة',
  'stickers.subtitle': 'حزم الملصقات التي يستطيع الجميع إرسالها في المحادثة، بالترتيب الذي تظهر به هناك.',
  'stickers.newPack': 'حزمة جديدة',
  'stickers.editPack': 'تعديل الحزمة',
  'stickers.formNote': 'اكتب اسم الحزمة كما سيظهر في لوحة ملصقات المحادثة.',
  'stickers.packName': 'الاسم بالإنجليزية',
  'stickers.packNameAr': 'الاسم بالعربية',
  'stickers.packNameArHint': 'إذا تركته فارغًا، يظهر الاسم الإنجليزي في الواجهة العربية.',
  'stickers.inChat': 'في المحادثة',
  'stickers.shown': 'ظاهرة في المحادثة',
  'stickers.shownHint': 'إذا أخفيتها، لا يمكن إرسال ملصقاتها بعد ذلك، وتبقى الملصقات التي تم إرسالها ظاهرة.',
  'stickers.hidden': 'مخفية',
  'stickers.hide': 'إخفاء من المحادثة',
  'stickers.show': 'إظهار في المحادثة',
  'stickers.count': 'الملصقات: {n} من {max}',
  'stickers.add': 'إضافة ملصقات',
  'stickers.addHint': 'صور PNG أو WebP أو GIF أو JPEG، بحجم {mb} ميغابايت على الأكثر لكل صورة. نصغّر الصور الثابتة الكبيرة إلى 512 بكسل، ويأخذ كل ملصق اسم ملفه.',
  'stickers.uploading': 'جاري الرفع…',
  'stickers.addedOne': 'تمت إضافة الملصق.',
  'stickers.addedMany': 'الملصقات المضافة: {n}.',
  'stickers.full': 'هذه الحزمة ممتلئة: {max} ملصقًا على الأكثر.',
  'stickers.room': 'لا تتسع هذه الحزمة إلا لـ {n} أخرى، فأضفنا أول {n}.',
  'stickers.tooLarge': 'حجم {name} أكبر من {mb} ميغابايت حتى بعد تصغيره.',
  'stickers.notPicture': '{name} ليس صورة PNG أو WebP أو GIF أو JPEG.',
  'stickers.unreadable': 'لم نتمكن من قراءة {name} كصورة.',
  'stickers.storage': 'لا يمكن الوصول إلى مخزن الملفات. حاول مرة ثانية، وإذا تكرر الخطأ فراجع إعدادات التخزين.',
  'stickers.sentMany': 'مرات الإرسال: {n}',
  'stickers.sentOnce': 'تم إرساله مرة واحدة',
  'stickers.neverSent': 'لم يتم إرساله بعد',
  'stickers.delete': 'حذف {label}',
  'stickers.deleteConfirm': 'حذف هذا الملصق؟',
  'stickers.deleteBodySent': 'سيخرج من الحزمة، ولن يتمكن أحد من إرساله مرة ثانية، لكنه يبقى ظاهرًا في الرسائل التي تم إرساله فيها.',
  'stickers.deleteBodyUnsent': 'لم يتم إرساله أبدًا، لذلك سيتم حذفه نهائيًا.',
  'stickers.deleted': 'تم حذف الملصق.',
  'stickers.deletePack': 'حذف الحزمة',
  'stickers.deletePackConfirm': 'حذف {name}؟',
  'stickers.deletePackBody': 'ستخرج ملصقاتها من المحادثة. الملصقات التي تم إرسالها تبقى ظاهرة في رسائلها، والباقي سيتم حذفه نهائيًا.',
  'stickers.packDeleted': 'تم حذف حزمة الملصقات.',
  'stickers.packCreated': 'تم إنشاء حزمة الملصقات. أضف ملصقاتها الآن.',
  'stickers.packSaved': 'تم حفظ حزمة الملصقات.',
  'stickers.packShown': '{name} ظاهرة الآن في المحادثة.',
  'stickers.packHidden': '{name} مخفية الآن من المحادثة.',
  'stickers.moveUp': 'نقل إلى الأعلى',
  'stickers.moveDown': 'نقل إلى الأسفل',
  'stickers.orderSaved': 'تم حفظ الترتيب.',
  'stickers.empty': 'لا توجد حزم ملصقات بعد',
  'stickers.emptyHint': 'أنشئ حزمة ثم أضف صورها، ويستطيع الجميع إرسالها في المحادثة.',
  'stickers.emptyPack': 'لا توجد ملصقات في هذه الحزمة بعد. ستظهر في المحادثة عندما تضيف إليها ملصقًا.',
  'stickers.packsMax': 'عدد الحزم {max}، وهو الحد الأقصى. احذف واحدة لإنشاء أخرى.',
  'mobile.title': 'نشاط تطبيق الجوال',
  'mobile.subtitle': 'كل ما يتم في تطبيق الموظفين على الجوال (تسجيل الدخول، وما تم فتحه، والردود، والرسائل المرسلة، وطلبات الإجازة)، وكل ما رفضه التطبيق. لمديري النظام فقط.',
  'mobile.tabActivity': 'النشاط',
  'mobile.tabPeople': 'الأشخاص',
  'mobile.tabPhones': 'الأجهزة',
  'mobile.metricActions': 'إجراءات اليوم',
  'mobile.metricPeople': 'أشخاص نشطون اليوم',
  'mobile.metricRefused': 'مرفوضة اليوم',
  'mobile.metricSignedIn': 'سجّلوا الدخول من جوال',
  'mobile.person': 'الشخص',
  'mobile.allPeople': 'الجميع',
  'mobile.area': 'المجال',
  'mobile.allAreas': 'كل المجالات',
  'mobile.outcome': 'النتيجة',
  'mobile.allOutcomes': 'كل النتائج',
  'mobile.outcomeSUCCESS': 'تم',
  'mobile.outcomeREFUSED': 'مرفوض',
  'mobile.outcomeFAILED': 'فشل',
  'mobile.searchPlaceholder': 'ابحث باسم أو رقم تذكرة أو عنوان…',
  'mobile.when': 'الوقت',
  'mobile.what': 'الإجراء',
  'mobile.phone': 'الجهاز',
  'mobile.result': 'النتيجة',
  'mobile.noActivity': 'لم يحدث شيء في تطبيق الجوال بعد، أو لا يوجد ما يطابق هذه الفلاتر.',
  'mobile.noPeople': 'لم يستخدم أحد تطبيق الجوال بعد.',
  'mobile.noPhones': 'لم يتم تسجيل الدخول إلى التطبيق من أي جوال بعد.',
  'mobile.activities': 'الإجراءات',
  'mobile.lastActivity': 'آخر إجراء',
  'mobile.phonesSignedIn': 'أجهزة مسجّلة الدخول',
  'mobile.showActivity': 'عرض نشاطه',
  'mobile.appVersion': 'إصدار التطبيق',
  'mobile.signedIn': 'مسجّل الدخول',
  'mobile.signedOut': 'سجّل الخروج',
  'mobile.lastSeen': 'آخر ظهور',
  'mobile.status': 'الحالة',
  'mobile.unknownPerson': 'لا يوجد حساب — {email}',
  'mobile.details': 'التفاصيل',
  'mobile.request': 'الطلب',
  'mobile.duration': 'المدة',
  'mobile.errorCode': 'الخطأ',
  'mobile.openRecord': 'فتح',
  'mobile.reportedByApp': 'أبلغ عنه التطبيق',
  'mobile.privacyNote':
    'لا نحفظ هنا أبدًا موضوع الرسالة ولا نصها ولا مستلميها، ولا نص الردود والتعليقات، ولا ما قيل في المحادثات ولا مع من.',
  'mobile.area.auth': 'تسجيل الدخول والخروج',
  'mobile.area.app': 'التطبيق',
  'mobile.area.dashboard': 'لوحة التحكم',
  'mobile.area.notification': 'الإشعارات',
  'mobile.area.mail': 'البريد',
  'mobile.area.chat': 'المحادثات',
  'mobile.area.file': 'الملفات',
  'mobile.area.attachment': 'فتح الملفات',
  'mobile.area.ticket': 'التذاكر',
  'mobile.area.task': 'المهام',
  'mobile.area.leader_task': 'مهام القادة',
  'mobile.area.announcement': 'الإعلانات',
  'mobile.area.client_cr': 'طلبات التغيير من العملاء',
  'mobile.area.schedule': 'جدولي',
  'mobile.area.leave': 'الإجازات',
  'mobile.area.payslip': 'قسائم الراتب',
  'mobile.area.penalty': 'المخالفات والجزاءات',
  'mobile.area.wallet': 'محفظة النقاط',
  'mobile.area.overtime': 'العمل الإضافي',
  'mobile.area.remote_work': 'العمل عن بُعد',
  'mobile.area.meeting': 'الاجتماعات',
  'mobile.area.call': 'المكالمات',
  'mobile.area.support': 'وضع الدعم',
  'mobile.action.call.history': 'فتح مكالماته',
  'mobile.action.call.view': 'فتح مكالمة',
  'mobile.action.call.start': 'أجرى مكالمة',
  'mobile.action.call.invite': 'أضاف أشخاصًا إلى مكالمة',
  'mobile.action.call.accept': 'ردّ على مكالمة',
  'mobile.action.call.decline': 'رفض مكالمة',
  'mobile.action.call.hangup': 'غادر مكالمة',
  'mobile.action.call.join': 'انضم صوتيًا إلى مكالمة',
  'mobile.action.call.recording_play': 'شغّل تسجيل مكالمة',
  'mobile.action.call.status': 'اطّلع على إتاحة المكالمات',
  'mobile.action.call.people': 'اطّلع على من يمكنه الاتصال بهم',
  'mobile.action.call.live': 'اطّلع على مكالماته الجارية',
  'mobile.action.call.events': 'انتظر المكالمات الواردة',
  'mobile.area.settings': 'الإعدادات',
  'mobile.area.screen': 'الشاشات',
  'mobile.area.request': 'طلبات مرفوضة',
  'mobile.action.auth.login': 'سجّل الدخول',
  'mobile.action.auth.login_failed': 'تم رفض تسجيل الدخول',
  'mobile.action.auth.logout': 'سجّل الخروج',
  'mobile.action.app.open': 'فتح التطبيق',
  'mobile.action.app.resume': 'عاد إلى التطبيق',
  'mobile.action.dashboard.view': 'فتح لوحة التحكم',
  'mobile.action.notification.list': 'فتح الإشعارات',
  'mobile.action.notification.read': 'علّم الإشعارات بأنها مقروءة',
  'mobile.action.notification.open': 'فتح إشعارًا',
  'mobile.action.notification.count': 'تحقق من الإشعارات',
  'mobile.action.mail.list': 'فتح البريد',
  'mobile.action.mail.view': 'فتح رسالة',
  'mobile.action.mail.send': 'أرسل رسالة',
  'mobile.action.mail.reply': 'ردّ على رسالة',
  'mobile.action.mail.forward': 'أعاد توجيه رسالة',
  'mobile.action.mail.count': 'تحقق من البريد',
  'mobile.action.mail.recipients': 'حمّل قائمة من يكتب إليهم',
  'mobile.action.chat.list': 'حمّل المحادثات',
  'mobile.action.chat.count': 'تحقق من رسائل المحادثات',
  'mobile.action.chat.people': 'حمّل قائمة من يحادثهم',
  'mobile.action.chat.events': 'انتظر رسائل المحادثات',
  'mobile.action.chat.start': 'بدأ محادثة',
  'mobile.action.chat.view': 'فتح محادثة',
  'mobile.action.chat.messages': 'حمّل رسائل محادثة',
  'mobile.action.chat.send': 'أرسل رسالة في محادثة',
  'mobile.action.chat.reply': 'ردّ في محادثة',
  'mobile.action.chat.sticker': 'أرسل ملصقًا في محادثة',
  'mobile.action.chat.stickers': 'حمّل الملصقات',
  'mobile.action.chat.references': 'بحث عن مهمة أو تذكرة للإشارة إليها',
  'mobile.action.chat.forward': 'أعاد توجيه رسالة محادثة',
  'mobile.action.chat.read': 'قرأ محادثة',
  'mobile.action.chat.react': 'تفاعل مع رسالة في محادثة',
  'mobile.action.chat.unreact': 'أزال تفاعله من رسالة في محادثة',
  'mobile.action.chat.invitations': 'حمّل دعوات المجموعات',
  'mobile.action.chat.group.create': 'أنشأ مجموعة محادثة',
  'mobile.action.chat.group.rename': 'غيّر اسم مجموعة محادثة',
  'mobile.action.chat.group.invite': 'دعا أشخاصًا إلى مجموعة محادثة',
  'mobile.action.chat.group.uninvite': 'سحب دعوة إلى مجموعة محادثة',
  'mobile.action.chat.group.accept': 'انضم إلى مجموعة محادثة',
  'mobile.action.chat.group.decline': 'اعتذر عن دعوة إلى مجموعة محادثة',
  'mobile.action.chat.group.leave': 'غادر مجموعة محادثة',
  'mobile.action.chat.group.remove': 'أزال شخصًا من مجموعة محادثة',
  'mobile.action.chat.group.admin': 'عيّن مشرفًا لمجموعة محادثة',
  'mobile.action.chat.group.unadmin': 'سحب الإشراف من عضو في مجموعة محادثة',
  'mobile.action.file.upload': 'رفع ملفًا',
  'mobile.action.file.upload_start': 'بدأ رفع ملف',
  'mobile.action.file.policy': 'حمّل حدود الرفع',
  'mobile.action.attachment.open': 'فتح ملفًا',
  'mobile.action.ticket.list': 'فتح التذاكر',
  'mobile.action.ticket.view': 'فتح تذكرة',
  'mobile.action.ticket.reply': 'ردّ على تذكرة',
  'mobile.action.ticket.claim': 'أسند تذكرة إلى نفسه',
  'mobile.action.support.view': 'فتح وضع الدعم',
  'mobile.action.support.start': 'شغّل وضع الدعم',
  'mobile.action.support.end': 'أوقف وضع الدعم',
  'mobile.action.task.list': 'فتح المهام',
  'mobile.action.task.view': 'فتح مهمة',
  'mobile.action.task.comment': 'علّق على مهمة',
  'mobile.action.leader_task.list': 'فتح مهام القادة',
  'mobile.action.leader_task.view': 'فتح مهمة من مهام القادة',
  'mobile.action.leader_task.comment': 'علّق على مهمة من مهام القادة',
  'mobile.action.announcement.list': 'فتح الإعلانات',
  'mobile.action.announcement.view': 'فتح إعلانًا',
  'mobile.action.client_cr.list': 'فتح طلبات التغيير من العملاء',
  'mobile.action.client_cr.view': 'فتح طلب تغيير',
  'mobile.action.schedule.view': 'فتح جدوله',
  'mobile.action.schedule.holidays': 'حمّل العطلات',
  'mobile.action.leave.list': 'فتح الإجازات',
  'mobile.action.leave.view': 'فتح طلب إجازة',
  'mobile.action.leave.request': 'طلب إجازة',
  'mobile.action.leave.types': 'حمّل أنواع الإجازات',
  'mobile.action.leave.balance': 'حمّل رصيد الإجازات',
  'mobile.action.leave.preview': 'احتسب أيام إجازة',
  'mobile.action.payslip.list': 'فتح قسائم الراتب',
  'mobile.action.payslip.view': 'فتح قسيمة راتب',
  'mobile.action.penalty.list': 'فتح المخالفات والجزاءات',
  'mobile.action.penalty.view': 'فتح جزاءً',
  'mobile.action.penalty.object': 'اعترض على جزاء',
  'mobile.action.wallet.view': 'فتح محفظة النقاط',
  'mobile.action.wallet.history': 'حمّل سجل النقاط',
  'mobile.action.wallet.penalties': 'حمّل الخصومات التي يمكن إلغاؤها بالنقاط',
  'mobile.action.wallet.redeem': 'استبدل نقاطًا',
  'mobile.action.overtime.list': 'فتح العمل الإضافي',
  'mobile.action.overtime.count': 'حمّل طلبات العمل الإضافي المنتظرة',
  'mobile.action.overtime.view': 'فتح طلب عمل إضافي',
  'mobile.action.overtime.accept': 'وافق على عمل إضافي',
  'mobile.action.overtime.decline': 'رفض عملًا إضافيًا',
  'mobile.action.remote_work.list': 'فتح العمل عن بُعد',
  'mobile.action.remote_work.count': 'حمّل طلبات العمل عن بُعد المنتظرة',
  'mobile.action.remote_work.preview': 'عاين أيام العمل عن بُعد',
  'mobile.action.remote_work.view': 'فتح طلب عمل عن بُعد',
  'mobile.action.remote_work.request': 'طلب العمل عن بُعد',
  'mobile.action.remote_work.approve': 'وافق على طلب عمل عن بُعد',
  'mobile.action.remote_work.reject': 'رفض طلب عمل عن بُعد',
  'mobile.action.remote_work.cancel': 'ألغى طلب عمل عن بُعد',
  'mobile.action.meeting.list': 'فتح الاجتماعات',
  'mobile.action.meeting.invitations': 'حمّل دعوات الاجتماعات',
  'mobile.action.meeting.count': 'حمّل دعوات الاجتماعات المنتظرة',
  'mobile.action.meeting.view': 'فتح اجتماعًا',
  'mobile.action.meeting.accept': 'وافق على حضور اجتماع',
  'mobile.action.meeting.decline': 'اعتذر عن حضور اجتماع',
  'mobile.upTo': 'بحد أقصى {time}',
  'mobile.action.settings.language': 'غيّر اللغة',
  'mobile.action.settings.theme': 'غيّر المظهر',
  'mobile.action.screen.view': 'فتح شاشة',
  'mobile.action.request.refused': 'حاول تنفيذ إجراء لا يقوم به التطبيق',
  'mobile.toPeople': 'إلى {count} أشخاص',
  'mobile.toPerson': 'إلى شخص واحد',
  'mobile.toGroups': 'إلى مجموعات: {count}',
  'mobile.toGroup': 'إلى مجموعة واحدة',
  'mobile.invitedPeople': 'المدعوون: {count}',
  'mobile.invitedPerson': 'دعوة شخص واحد',
  'mobile.withFiles': '{count} ملفات',
  'mobile.inChat': 'في محادثة',
  'mobile.internalNote': 'ملاحظة داخلية',
  'mobile.days': '{count} أيام',
  'mobile.oneDay': 'يوم واحد',
  'mobile.points': '{count} نقطة',
  'mobile.onePoint': 'نقطة واحدة',
  'mobile.bundles': 'عدد الحزم: {count}',
  'mobile.oneBundle': 'حزمة واحدة',
  'mobile.total': 'النتائج: {count}',
  'mobile.page': 'صفحة {page}',
  'mobile.languageAr': 'العربية',
  'mobile.languageEn': 'الإنجليزية',
  'mobile.themeLight': 'فاتح',
  'mobile.themeDark': 'داكن',
  'mobile.themeSystem': 'كإعداد الجوال',
  'mobile.wrongPassword': 'البريد أو كلمة المرور غير صحيحة',
  'mobile.accountDisabled': 'الحساب موقوف',
  'mobile.route': 'المسار',
  'mobile.screen.settings': 'الإعدادات',
  'mobile.screen.compose': 'رسالة جديدة',
  'mobile.screen.more': 'المزيد',
  'mobile.screen.leaveForm': 'طلب إجازة',
  'mobile.screen.remoteWorkForm': 'طلب عمل عن بُعد',
  'mobile.screen.chats': 'المحادثات',
  'mobile.screen.chatNew': 'محادثة جديدة',
  'mobile.screen.walletRedeem': 'استبدال النقاط',
  'mobile.screen.penaltyObjection': 'الاعتراض على جزاء',

  // Every shift action asks first, in a dialog (operator, 2026-10-15).
  'attendance.startConfirm': 'بدء ورديتك الآن؟',
  'attendance.startConfirmBody': 'يبدأ احتساب مدة عملك من هذه اللحظة.',
  'attendance.breakConfirm': 'بدء {name}؟',
  'attendance.breakConfirmAction': 'بدء',
  'attendance.breakConfirmLimit': 'المدة المسموحة {limit}.',
  'attendance.breakConfirmPenalty':
    'المدة المسموحة {limit}. وإذا تجاوزتها، سيتم إرسال جزاء {days} إلى المدير ليقرر فيه.',
  'attendance.breakConfirmPerDay': 'عدد المرات المسموحة في اليوم {max}، والمتبقي اليوم {left}.',
  'attendance.breakConfirmWork': 'هذا الوقت محسوب من وقت العمل.',
  'attendance.breakConfirmRest': 'سيتم خصم هذا الوقت من مدة عملك.',
  'attendance.resumeConfirm': 'العودة إلى العمل الآن؟',
  'attendance.resumeConfirmBody': 'سيتم إنهاء {name} الآن.',
  'attendance.endConfirmBody':
    'يتوقف احتساب مدة عملك الآن، وتنتهي ورديتك الأساسية لليوم نهائيًا، فلا يمكن بدؤها مرة أخرى اليوم. وبعدها يمكنك العمل في ورديات حرة، يتم تسجيلها لكن بلا أجر.',
  'attendance.endConfirmBreak': 'وسيتم إنهاء {name} معها.',

  // إيقاف العمل مؤقتًا — للمديرين وللموظفين المختارين في الإعدادات › الحضور (2026-10-24).
  'attendance.pauseWork': 'إيقاف العمل مؤقتًا',
  'attendance.paused': 'متوقف مؤقتًا',
  'attendance.status.PAUSED': 'متوقف مؤقتًا',
  'attendance.pausedSince': 'متوقف مؤقتًا منذ {time}',
  'attendance.pausedFor': 'إيقاف مؤقت {minutes}',
  'attendance.workPaused': 'تم إيقاف العمل مؤقتًا. ارجع إلى العمل عندما تكون جاهزًا.',
  'attendance.pauseConfirm': 'إيقاف العمل مؤقتًا الآن؟',
  'attendance.pauseConfirmBody': 'يتوقف احتساب الوقت حتى تعود إلى العمل. لا حد لمدة الإيقاف، لكن وقت الإيقاف لا يدخل في وقت العمل.',
  'attendance.unpauseConfirmBody': 'ينتهي الإيقاف المؤقت الآن، ويعود احتساب الوقت.',
  'attendance.endConfirmPause': 'وسينتهي الإيقاف المؤقت معها.',
  'attendance.hoursLeft': 'عملت {worked} من {owed} اليوم، وبقي عليك {missing}.',
  'attendance.hoursDone': 'اكتملت ساعات اليوم ({owed}).',
  'attendance.hoursLeftShort': 'بقي {missing} من ساعات اليوم',
  'attendance.hoursDoneShort': 'اكتملت ساعات اليوم',
  'attendance.shortDayCosts': 'إذا انتهى اليوم دون إكمال ساعاته، سيتم خصم {days} من الراتب.',
  'attendance.pauseOwesHours': 'وقت الإيقاف المؤقت لا يدخل في وقت العمل. إذا انتهى اليوم دون إكمال ساعاته، سيتم خصم {days} من الراتب.',
  'attendance.endShortWarning':
    'عملت {worked} من {owed} اليوم. إذا انتهى اليوم دون إكمال ساعاته، سيتم خصم {days} من الراتب. وإذا أنهيت ورديتك الأساسية فلا يمكن بدؤها مرة أخرى اليوم، والوردية الحرة لا تعوّض الساعات الناقصة.',
  'attendance.endEarlyWarning':
    'تنتهي ورديتك الساعة {end}. إذا أنهيتها قبل {from}، سيتم خصم {days} من الراتب، حتى لو أكملت ساعات اليوم.',
  'attendance.endOnePenalty': 'إذا انطبق الاثنان، سيتم احتساب جزاء واحد فقط، وهو الأكبر.',
  'attendance.pausedFirst': 'عملك متوقف مؤقتًا، فارجع إلى العمل أولًا.',
  'attendance.pauseNotAllowed': 'إيقاف العمل مؤقتًا متاح فقط لمديري النظام وللموظفين المختارين في «الإعدادات › الحضور».',
  'attendance.endReason.WORK_PAUSED': 'تم إيقاف العمل مؤقتًا',
  'timeline.PAUSE_STARTED': 'إيقاف العمل مؤقتًا',
  'timeline.PAUSE_ENDED': 'العودة إلى العمل',
  'settings.pauseWork': 'إيقاف العمل مؤقتًا',
  'settings.pauseWorkNote': 'من يمكنه إيقاف ورديته مؤقتًا. ومديرو النظام يمكنهم ذلك دائمًا.',
  'settings.pausers': 'الموظفون الذين يمكنهم الإيقاف المؤقت',
  'settings.pausersMax': '{n} شخصًا على الأكثر.',
  'settings.pausersHint':
    'مديرو النظام يمكنهم ذلك دائمًا. أضف الموظفين الذين يمكنهم الإيقاف المؤقت أيضًا، فيظهر لهم زر «إيقاف العمل مؤقتًا» بجانب «أخذ استراحة»، ويعودون إلى العمل عندما يكونون جاهزين.',
  'settings.pauseWorkRule':
    'لا حد لمدة الإيقاف المؤقت ولا جزاء خاص به، لكن وقته لا يدخل في وقت العمل، فيبقى على من يوقف عمله إكمال ساعات اليوم. وإذا انتهى اليوم دون إكمالها، سيتم خصم {days} من الراتب، وهو جزاء عدم إكمال الساعات المذكور أعلاه، مثل أي موظف.',
  'settings.pauseWorkRuleNoPenalty':
    'لا حد لمدة الإيقاف المؤقت ولا جزاء خاص به، لكن وقته لا يدخل في وقت العمل، فيبقى على من يوقف عمله إكمال ساعات اليوم. وجزاء عدم إكمال الساعات المذكور أعلاه 0، فلا خصم الآن على اليوم الناقص.',

  // Break types that need approval (operator, 2026-10-18), and who approves them beside the
  // administrators (2026-10-19).
  'settings.needsApproval': 'تحتاج موافقة',
  'settings.needsApprovalLabel': 'تحتاج موافقة',
  'settings.needsApprovalOn': 'يتم طلب الموافقة أولًا',
  'settings.needsApprovalOff': 'تبدأ عند الضغط عليها',
  'settings.needsApprovalHint':
    'عند تحديد هذا الخيار، يصبح الضغط عليها طلبًا يصل إلى مديري النظام والموظفين المختارين أدناه، وتبدأ الاستراحة عندما يوافق أحدهم، والرفض يأتي مع سببه. أما هم فيأخذونها فورًا. ويبدأ حساب مدتها المسموحة وجزائها من لحظة الموافقة.',
  'settings.breakApprovers': 'من يوافق عليها',
  'settings.breakApproversHint':
    'مديرو النظام يمكنهم الموافقة دائمًا. أضف الموظفين الذين يمكنهم الموافقة عليها أيضًا، فيوافقون على كل طلب لهذه الاستراحة أو يرفضونه، من أي موظف، ويأخذونها هم دون طلب.',
  'settings.breakApproversMax': '{n} شخصًا على الأكثر.',
  'settings.administrators': 'مديرو النظام',
  'attendance.needsApproval': 'تحتاج موافقة',
  'attendance.waitingShort': 'بانتظار الموافقة',
  'attendance.leftShort': 'المتبقي {n}',
  'attendance.leftToday': 'المتبقي اليوم {n}',
  'attendance.usedUpToday': 'نفدت اليوم',
  'attendance.breakRequests': 'طلبات الاستراحة',
  'attendance.requestConfirm': 'طلب {name}؟',
  'attendance.requestConfirmAction': 'طلب الموافقة',
  'attendance.requestConfirmBody':
    'سيتم إرسال طلبك للموافقة، وتبدأ الاستراحة عندما يوافق عليها أحد. وحتى ذلك الوقت أنت في ورديتك وتعمل كالمعتاد، فلا تترك جهازك.',
  'attendance.requestSent': 'تم إرسال الطلب. تبدأ استراحتك عند الموافقة عليها.',
  'attendance.requestWaiting': '{name} — بانتظار الموافقة',
  'attendance.requestWaitingBody': 'تبدأ عند الموافقة عليها، وحتى ذلك الوقت أنت تعمل.',
  'attendance.askedAt': 'تم الطلب الساعة {time}',
  'attendance.withdrawRequest': 'سحب الطلب',
  'attendance.withdrawConfirm': 'سحب طلب {name}؟',
  'attendance.withdrawConfirmBody': 'لن يتمكن أحد من الموافقة عليه بعد ذلك. ويمكنك الطلب مرة أخرى لاحقًا.',
  'attendance.requestWithdrawn': 'تم سحب الطلب.',
  'attendance.breakWithdrawsRequest': 'وبذلك سيتم سحب طلب {name}.',
  'attendance.breakNeedsApproval': 'هذه الاستراحة تحتاج موافقة، فاطلبها بدلًا من بدئها مباشرة.',
  'attendance.requestAlreadyWaiting': 'لديك بالفعل طلب استراحة بانتظار الموافقة.',
  'attendance.approvalNote': 'الاستراحة المعلّمة بـ«تحتاج موافقة» تطلبها أولًا، وتبدأ عندما يوافق عليها أحد.',
  'breakRequest.break': 'الاستراحة',
  'breakRequest.asked': 'وقت الطلب',
  'breakRequest.decidedBy': 'بواسطة {name}',
  'breakRequest.cancel.WITHDRAWN': 'سحبه الموظف',
  'breakRequest.cancel.OTHER_BREAK': 'بدأ الموظف استراحة أخرى',
  'breakRequest.cancel.SHIFT_ENDED': 'انتهت الوردية قبل القرار',
  'breakRequest.penaltyPast': 'جزاء {days} عند التجاوز',
  'breakRequest.countsAsWork': 'محسوبة من وقت العمل',
  'breakRequest.offWorked': 'مخصومة من مدة العمل',
  'breakRequest.perDay': '{n} في اليوم',
  'breakRequest.dailyLimit': 'أخذ الموظف هذه الاستراحة اليوم كل المرات المسموح بها، فارفض الطلب بدلًا من الموافقة عليه.',
  'breakRequest.boardNote':
    'عند الموافقة تبدأ الاستراحة فورًا، ويصل الموظف إشعار بذلك. والرفض يحتاج سببًا يقرؤه الموظف. ولا يقرر أحد في طلبه الخاص.',
  'breakRequest.noneWaiting': 'لا توجد طلبات استراحة بانتظار الموافقة.',
  'breakRequest.approvedToast': 'تمت الموافقة وبدأت الاستراحة.',
  'breakRequest.rejectedToast': 'تم رفض الطلب وإبلاغ الموظف.',
  'breakRequest.rejectTitle': 'رفض طلب الاستراحة',
  'breakRequest.rejectReason': 'سبب الرفض — يقرؤه الموظف',
  'breakRequest.noLongerWaiting': 'لم يعد هذا الطلب بانتظار الموافقة، فلا يمكن رفضه.',
  'breakRequest.waitingCount': 'طلبات استراحة بانتظار الموافقة: {n}',
  'timeline.BREAK_REQUESTED': 'طلب {type}',
  'timeline.BREAK_REQUEST_APPROVED': 'وافق {name} على {type}',
  'timeline.BREAK_REQUEST_REJECTED': 'لم يوافق {name} على {type}',
  'timeline.BREAK_REQUEST_CANCELLED': 'تم سحب طلب {type}',
  // المراقبة
  'nav.groupMonitoring': 'المراقبة',
  'nav.monitoringServers': 'السيرفرات',
  'nav.monitoringUrls': 'الروابط',
  'monitoring.servers.title': 'السيرفرات',
  'monitoring.servers.subtitle': 'سيرفراتنا وسيرفرات VPS، والمشاريع التي يشغّلها كل منها.',
  'monitoring.servers.new': 'إضافة سيرفر',
  'monitoring.servers.search': 'ابحث بالاسم أو IP أو اسم المضيف أو المزوّد',
  'monitoring.servers.empty': 'لا توجد سيرفرات بعد',
  'monitoring.servers.emptyHint': 'أضف الـVPS الذي تعمل عليه مشاريعك، ثم الروابط التي تريد فحصها عليه.',
  'monitoring.server.details': 'التفاصيل',
  'monitoring.server.ip': 'عنوان IP',
  'monitoring.server.ipHint': 'IPv4 أو IPv6.',
  'monitoring.server.hostname': 'اسم المضيف',
  'monitoring.server.provider': 'مزوّد الاستضافة',
  'monitoring.server.location': 'الموقع',
  'monitoring.server.locationPlaceholder': 'فرانكفورت، ألمانيا',
  'monitoring.server.projects': 'المشاريع على هذا السيرفر',
  'monitoring.server.projectsNote': 'يمكن ربط أي رابط على هذا السيرفر بأحد هذه المشاريع.',
  'monitoring.server.projectsHint': 'حدّد المشاريع التي تعمل هنا، وإذا لم تحدد أي مشروع فهو سيرفر عام. وبعدها يمكن ربط أي رابط على هذا السيرفر بأحدها.',
  'monitoring.server.general': 'سيرفر عام',
  'monitoring.server.generalHint': 'لم يتم تحديد أي مشروع له، فروابطه بلا مشروع.',
  'monitoring.server.selectedCount': 'المحدد: {n}',
  'monitoring.server.filterProjects': 'ابحث عن مشروع',
  'monitoring.server.noProjects': 'لا توجد مشاريع لاختيارها بعد.',
  'monitoring.server.deletedProject': 'محذوف',
  'monitoring.server.untagWarning': 'سيتم إزالة {project} من روابط هذا السيرفر المرتبطة به ({n})، وسيستمر فحصها بلا مشروع.',
  'monitoring.server.urls': 'الروابط',
  'monitoring.server.noUrls': 'لا توجد روابط',
  'monitoring.server.noUrlsHint': 'أضف المواقع وواجهات API والمنافذ التي تريد فحصها على هذا السيرفر.',
  'monitoring.server.remove': 'حذف السيرفر',
  'monitoring.server.removeConfirm': 'حذف {name}؟',
  'monitoring.server.removeBody': 'سيتوقف فحص روابطه ({n})، وسنحتفظ بالسيرفر وروابطه مع سجلها.',
  'monitoring.server.removed': 'تم حذف السيرفر.',
  'monitoring.type.VPS': 'VPS',
  'monitoring.type.CLOUD': 'سحابي',
  'monitoring.type.DEDICATED': 'مخصص',
  'monitoring.type.OTHER': 'أخرى',
  'monitoring.health.ONLINE': 'متصل',
  'monitoring.health.WARNING': 'تحذير',
  'monitoring.health.OFFLINE': 'غير متصل',
  'monitoring.health.PENDING': 'لم يتم فحصه بعد',
  'monitoring.health.NOT_MONITORED': 'بلا مراقبة',
  'monitoring.status.PENDING': 'لم يتم فحصه بعد',
  'monitoring.status.UP': 'يعمل',
  'monitoring.status.DEGRADED': 'أداء متراجع',
  'monitoring.status.DOWN': 'معطّل',
  'monitoring.status.PAUSED': 'موقوف مؤقتًا',
  'monitoring.count.up': 'يعمل',
  'monitoring.count.degraded': 'متراجع',
  'monitoring.count.down': 'معطّل',
  'monitoring.count.pending': 'بانتظار الفحص',
  'monitoring.count.paused': 'موقوف',
  'monitoring.severity.LOW': 'منخفضة',
  'monitoring.severity.MEDIUM': 'متوسطة',
  'monitoring.severity.HIGH': 'عالية',
  'monitoring.severity.CRITICAL': 'حرجة',
  'monitoring.kind.HTTP': 'موقع / API',
  'monitoring.kind.TCP': 'منفذ',
  'monitoring.urls.title': 'الروابط',
  'monitoring.urls.subtitle': 'المواقع وواجهات API والمنافذ التي نفحصها على سيرفراتنا.',
  'monitoring.urls.new': 'إضافة رابط',
  'monitoring.urls.search': 'ابحث بالاسم أو العنوان',
  'monitoring.urls.empty': 'لا توجد روابط بعد',
  'monitoring.urls.emptyHint': 'أضف موقعًا أو واجهة API أو منفذًا على أحد السيرفرات.',
  'monitoring.filter.allServers': 'كل السيرفرات',
  'monitoring.filter.allProjects': 'كل المشاريع',
  'monitoring.filter.allStatuses': 'كل الحالات',
  'monitoring.filter.allKinds': 'المواقع وواجهات API والمنافذ',
  'monitoring.url.server': 'السيرفر',
  'monitoring.url.noProject': 'بدون مشروع',
  'monitoring.url.pickServerFirst': 'اختر السيرفر أولًا، فلا تظهر إلا مشاريعه.',
  'monitoring.url.serverHasNoProjects': 'لا يشغّل هذا السيرفر أي مشروع بعد.',
  'monitoring.url.addProjectsOnServer': 'أضفها من صفحة السيرفر.',
  'monitoring.url.projectHint': 'تظهر مشاريع هذا السيرفر فقط.',
  'monitoring.url.kind': 'النوع',
  'monitoring.url.address': 'العنوان',
  'monitoring.url.url': 'الرابط',
  'monitoring.url.urlHint': 'موقع أو رابط API. إذا لم تكتب https:// فسنضيفها تلقائيًا.',
  'monitoring.url.method': 'الطريقة',
  'monitoring.url.expectedStatus': 'رموز الحالة المقبولة',
  'monitoring.url.expectedStatusHint': 'مثل 200-299 أو 200,301. وإذا تركته فارغًا، فأي رمز 2xx.',
  'monitoring.url.any2xx': 'أي 2xx',
  'monitoring.url.keyword': 'يجب أن يحتوي على',
  'monitoring.url.keywordHint': 'نص يجب أن يظهر في الاستجابة، فيكشف صفحة الخطأ التي ترد بالرمز 200.',
  'monitoring.url.headers': 'الترويسات',
  'monitoring.url.headersHint': 'ترويسة في كل سطر بصيغة Name: value، مثل مفتاح API.',
  'monitoring.url.body': 'المحتوى',
  'monitoring.url.bodyHint': 'ما يرسله طلب POST، كما تكتبه تمامًا.',
  'monitoring.url.host': 'المضيف',
  'monitoring.url.hostHint': 'اسم أو عنوان IP، واكتب 127.0.0.1 للسيرفر نفسه.',
  'monitoring.url.port': 'المنفذ',
  'monitoring.url.severity': 'الأهمية',
  'monitoring.url.severityHint': 'تحدد من يصله التنبيه عند تعطّله.',
  'monitoring.url.checks': 'الفحوصات',
  'monitoring.url.checksHint': 'اترك الحقل فارغًا ليأخذ القيمة الافتراضية من إعدادات المراقبة.',
  'monitoring.url.interval': 'الفحص كل',
  'monitoring.url.everyMinute': 'كل دقيقة',
  'monitoring.url.everyMinutes': 'كل {n} دقائق',
  'monitoring.url.everyMinutesMany': 'كل {n} دقيقة',
  'monitoring.url.default': 'افتراضي',
  'monitoring.url.defaultOption': 'الافتراضي — {value}',
  'monitoring.url.defaultIs': 'الافتراضي: {value}',
  'monitoring.url.timeout': 'مهلة انتظار الاستجابة',
  'monitoring.url.seconds': '{n} ث',
  'monitoring.url.failures': 'الفحوصات الفاشلة قبل اعتباره معطّلًا',
  'monitoring.url.failuresHint': 'فشل واحد لا يعني عطلًا، بل يلزم هذا العدد من الفحوصات الفاشلة المتتالية.',
  'monitoring.url.slowMs': 'بطيء إذا تجاوز',
  'monitoring.url.ms': '{n} ملي ثانية',
  'monitoring.url.sslWarnDays': 'التنبيه قبل انتهاء الشهادة',
  'monitoring.url.sslWarnDaysHint': 'عدد الأيام قبل انتهاء صلاحية شهادة HTTPS.',
  'monitoring.url.days': '{n} يوم',
  'monitoring.url.whatIsChecked': 'ما الذي نفحصه',
  'monitoring.url.where': 'المكان',
  'monitoring.url.pause': 'إيقاف مؤقت',
  'monitoring.url.resume': 'استئناف',
  'monitoring.url.pausedNote': 'موقوف مؤقتًا، فلن نفحصه ولن يصل عنه أي تنبيه حتى يتم استئنافه.',
  'monitoring.url.pausedToast': 'تم الإيقاف المؤقت.',
  'monitoring.url.resumedToast': 'تم الاستئناف، وسنفحصه من جديد.',
  'monitoring.url.remove': 'حذف الرابط',
  'monitoring.url.removeConfirm': 'حذف {name}؟',
  'monitoring.url.removeBody': 'سيتوقف فحصه، وسنحتفظ به مع سجله.',
  'monitoring.url.removed': 'تم حذف الرابط.',
  'monitoring.monitor.notRunning': 'خدمة المراقبة لا تعمل، فلا يتم فحص أي شيء الآن. آخر ظهور لها {when}.',
  'monitoring.monitor.neverRan': 'لم تعمل خدمة المراقبة بعد، فلا يتم فحص أي شيء الآن. ستبدأ مع الـAPI في النشر القادم (pm2: viganium-monitor).',
  'monitoring.monitor.noWatchdog': 'لا يوجد مراقب خارجي. خدمة المراقبة تعمل على السيرفر نفسه الذي تراقبه، فإذا توقف هذا السيرفر لن يصل أي تنبيه بالبريد أو تيليجرام حتى يعود.',
  'monitoring.monitor.watchdogStopped': 'توقف المراقب الخارجي على Cloudflare عن قراءة هذا السيرفر {when}. وحتى يعود للقراءة، لن يصل أي تنبيه إذا توقف هذا السيرفر.',
  'monitoring.monitor.setWatchdog': 'اضبط المراقب الخارجي',
  'monitoring.url.response': 'زمن الاستجابة',
  'monitoring.url.uptimeDay': 'التوفر 24 س',
  'monitoring.url.uptimeWeek': 'التوفر 7 أيام',
  'monitoring.url.uptimeMonth': 'التوفر 30 يومًا',
  'monitoring.url.checkNow': 'افحص الآن',
  'monitoring.url.checkNowToast': 'سنفحصه خلال ثوان قليلة.',
  'monitoring.url.downSince': 'معطّل منذ {when} ({duration})، والسبب: {cause}.',
  'monitoring.url.lastCheck': 'آخر فحص',
  'monitoring.url.answered': 'ردّ بالرمز {code}',
  'monitoring.url.connected': 'نجح الاتصال بالمنفذ',
  'monitoring.url.statusSince': 'تغيّرت الحالة {when}',
  'monitoring.url.certificate': 'الشهادة',
  'monitoring.url.expiresOn': 'تنتهي في',
  'monitoring.url.daysLeft': 'المتبقي: {n} يوم',
  'monitoring.url.issuer': 'الجهة المصدرة',
  'monitoring.url.incidents': 'الأعطال',
  'monitoring.url.incidentsHint': 'يبدأ العطل عند أول فحص فاشل، وينتهي عند أول فحص ناجح.',
  'monitoring.url.noIncidents': 'لا توجد أعطال.',
  'monitoring.url.recentChecks': 'آخر الفحوصات',
  'monitoring.url.noChecks': 'لم يتم فحصه بعد.',
  'monitoring.url.checkedAt': 'وقت الفحص',
  'monitoring.url.result': 'النتيجة',
  'monitoring.url.statusCode': 'الرمز',
  'monitoring.url.started': 'البداية',
  'monitoring.url.ended': 'النهاية',
  'monitoring.url.duration': 'المدة',
  'monitoring.url.cause': 'السبب',
  'monitoring.url.ongoing': 'مستمر',
  'monitoring.endReason.RECOVERED': 'عاد للعمل',
  'monitoring.endReason.CHANGED': 'انتهى بتعديله',
  'monitoring.endReason.PAUSED': 'انتهى بإيقافه مؤقتًا',
  'monitoring.endReason.REMOVED': 'انتهى بحذفه',
  'monitoring.error.TIMEOUT': 'لا استجابة خلال {detail} ث',
  'monitoring.error.DNS': 'لا يمكن العثور على عنوان لهذا الاسم',
  'monitoring.error.REFUSED': 'تم رفض الاتصال، فلا شيء يستمع هناك',
  'monitoring.error.RESET': 'انقطع الاتصال',
  'monitoring.error.UNREACHABLE': 'لا يمكن الوصول إلى المضيف',
  'monitoring.error.TLS': 'مشكلة في الشهادة: {detail}',
  'monitoring.error.STATUS': 'ردّ بالرمز {detail}',
  'monitoring.error.KEYWORD': 'النص المطلوب غير موجود في الاستجابة',
  'monitoring.error.REDIRECTS': 'عمليات تحويل كثيرة جدًا',
  'monitoring.error.BLOCKED': 'يشير إلى عنوان لا تذهب إليه الفحوصات',
  'monitoring.error.NETWORK': 'خطأ في الشبكة: {detail}',
  'monitoring.error.SLOW': 'بطيء: {detail} ملي ثانية',
  'monitoring.error.SSL_EXPIRING': 'الأيام المتبقية لانتهاء الشهادة: {detail}',
  'nav.monitoringAlerts': 'التنبيهات',
  'nav.monitoringSettings': 'الإعدادات',
  'monitoring.alerts.title': 'التنبيهات',
  'monitoring.alerts.subtitle': 'من يصله تنبيه عند تعطّل رابط أو عودته للعمل، وسجل كل التنبيهات المرسلة.',
  'monitoring.alerts.new': 'إضافة مستلم',
  'monitoring.alerts.off': 'التنبيهات متوقفة من «المراقبة › الإعدادات»، فنسجل الأعطال لكن لا يصل أي تنبيه لأحد.',
  'monitoring.alerts.emailReady': 'جاهز — نرسل التنبيهات عبر ZeptoMail مثل باقي رسائل النظام.',
  'monitoring.alerts.emailMissing': 'لم يتم إعداده على هذا السيرفر: {missing} فارغ في ملف .env الخاص بالـAPI.',
  'monitoring.alerts.telegramMissing': 'لم يتم إعداده: أنشئ بوتًا عبر @BotFather وضع رمزه في TELEGRAM_BOT_TOKEN في ملف .env الخاص بالـAPI.',
  'monitoring.alerts.telegramReady': 'جاهز — يرسل التنبيهات {bot}. أضف البوت إلى مجموعة أو ابدأ محادثة معه لتحصل على معرّف المحادثة.',
  'monitoring.alerts.recipients': 'المستلمون',
  'monitoring.alerts.recipientsHint': 'يصل كل مستلم تنبيهات الروابط التي لا تقل أهميتها عما اختاره، على السيرفرات أو المشاريع التي يغطيها.',
  'monitoring.alerts.empty': 'لا يوجد مستلمون للتنبيهات بعد',
  'monitoring.alerts.emptyHint': 'أضف محادثة تيليجرام أو بريدًا إلكترونيًا. ويرى مديرو النظام الأعطال في الجرس أيضًا.',
  'monitoring.alerts.sendTo': 'الإرسال إلى',
  'monitoring.alerts.from': 'من أهمية',
  'monitoring.alerts.covers': 'يغطي',
  'monitoring.alerts.coversHint': 'اختر سيرفرات أو مشاريع أو كليهما، فيشمل ذلك أي رابط على أحد السيرفرات المختارة أو مرتبط بأحد المشاريع المختارة. و«الكل» يشمل أيضًا ما تضيفه لاحقًا.',
  'monitoring.alerts.allServers': 'كل السيرفرات',
  'monitoring.alerts.allProjects': 'كل المشاريع',
  'monitoring.alerts.allServersNote': 'خيار «كل السيرفرات» يشمل كل الروابط، أيًّا كان مشروعها.',
  'monitoring.alerts.coversNothing': 'اختر «الكل»، أو سيرفرًا أو مشروعًا واحدًا على الأقل.',
  'monitoring.alerts.everything': 'كل شيء',
  'monitoring.alerts.active': 'مفعّل',
  'monitoring.alerts.inactive': 'متوقف',
  'monitoring.alerts.noRecovery': 'بدون تنبيهات العودة للعمل',
  'monitoring.alerts.test': 'إرسال تنبيه تجريبي',
  'monitoring.alerts.testSent': 'تم إرسال تنبيه تجريبي.',
  'monitoring.alerts.log': 'التنبيهات المرسلة',
  'monitoring.alerts.logHint': 'كل تنبيه لكل مستلم، وهل وصل أم لا. وإذا فشل الإرسال نعيد المحاولة، حتى خمس محاولات في المجموع.',
  'monitoring.alerts.allKinds': 'كل التنبيهات',
  'monitoring.alerts.logEmpty': 'لم يتم إرسال أي تنبيه بعد.',
  'monitoring.alerts.when': 'الوقت',
  'monitoring.alerts.recipient': 'المستلم',
  'monitoring.alerts.what': 'التنبيه',
  'monitoring.alerts.attempts': 'المحاولات: {n}',
  'monitoring.alerts.namePlaceholder': 'مجموعة العمليات',
  'monitoring.alerts.channel': 'طريقة الإرسال',
  'monitoring.alerts.chatId': 'معرّف محادثة تيليجرام',
  'monitoring.alerts.chatIdHint': 'معرّف شخص أو مجموعة (يبدأ معرّف المجموعة بـ -100). راسل البوت أو أضفه إلى المجموعة ثم اضغط «البحث عن المحادثات».',
  'monitoring.alerts.findChats': 'البحث عن المحادثات',
  'monitoring.alerts.noChats': 'لم تصل إلى البوت أي رسالة خلال آخر يوم. أرسل له رسالة أو أضفه إلى المجموعة، ثم ابحث مرة ثانية.',
  'monitoring.alerts.language': 'لغة التنبيهات',
  'monitoring.alerts.minSeverity': 'ابتداءً من الأهمية',
  'monitoring.alerts.minSeverityHint': 'فقط الروابط التي لا تقل أهميتها عن هذه. وأهمية كل رابط تحددها في نموذجه، وهي «متوسطة» ما لم تغيّرها، فإذا اخترت «عالية» هنا لن يصل أي تنبيه عن تعطّل رابط أهميته متوسطة.',
  'monitoring.coverage.title': 'لا يصل تنبيه التعطّل إلى أحد في {count} من أصل {total} من الروابط المراقبة',
  'monitoring.coverage.IMPORTANCE': 'أهميتها أقل من «ابتداءً من الأهمية» عند كل المستلمين. اخفضها عند أحد المستلمين، أو ارفع أهمية الرابط من نموذجه:',
  'monitoring.coverage.SCOPE': 'لا تقع ضمن سيرفرات أو مشاريع أي مستلم:',
  'monitoring.coverage.NO_RECIPIENTS': 'لا يوجد مستلم مفعّل لإبلاغه:',
  'monitoring.coverage.more': '+{count} أخرى',
  'monitoring.alerts.notifyRecovery': 'أبلغه أيضًا عند عودة الرابط للعمل',
  'monitoring.alerts.isActive': 'مفعّل — أزل التحديد لإيقاف تنبيهاته مؤقتًا',
  'monitoring.alerts.remove': 'حذف المستلم',
  'monitoring.alerts.removeConfirm': 'حذف {name}؟',
  'monitoring.alerts.removeBody': 'لن تصله تنبيهات بعد الآن، وتبقى التنبيهات المرسلة إليه في السجل.',
  'monitoring.alerts.removed': 'تم حذف المستلم.',
  'monitoring.channel.EMAIL': 'البريد الإلكتروني',
  'monitoring.channel.TELEGRAM': 'تيليجرام',
  'monitoring.delivery.SENT': 'تم الإرسال',
  'monitoring.delivery.QUEUED': 'بانتظار الإرسال',
  'monitoring.delivery.FAILED': 'فشل',
  'monitoring.delivery.SKIPPED': 'غير معدّ',
  'monitoring.alertKind.DOWN': 'تعطّل',
  'monitoring.alertKind.RECOVERED': 'عودة للعمل',
  'monitoring.alertKind.REMINDER': 'ما زال معطّلًا',
  'monitoring.alertKind.SSL_EXPIRING': 'الشهادة',
  'monitoring.alertKind.TEST': 'تجربة',
  'monitoring.alertKind.MONITOR_OFFLINE': 'توقف المراقبة',
  'monitoring.settings.title': 'إعدادات المراقبة',
  'monitoring.settings.subtitle': 'ما يأخذه كل رابط ما لم يحدد قيمه الخاصة، وطريقة عمل التنبيهات.',
  'monitoring.settings.checks': 'الفحوصات',
  'monitoring.settings.interval': 'الفحص كل',
  'monitoring.settings.timeout': 'مهلة انتظار الاستجابة (بالثواني)',
  'monitoring.settings.failures': 'الفحوصات الفاشلة قبل اعتباره معطّلًا',
  'monitoring.settings.failuresHint': 'متتالية. فشل واحد لا يعني عطلًا أبدًا.',
  'monitoring.settings.retry': 'إعادة الفحص بعد الفشل (بالثواني)',
  'monitoring.settings.retryHint': 'حتى نتأكد من العطل خلال لحظات، لا دقائق.',
  'monitoring.settings.successes': 'الفحوصات الناجحة قبل اعتباره عائدًا للعمل',
  'monitoring.settings.successesHint': 'متتالية، حتى لا ينتهي العطل باستجابة واحدة جاءت بالصدفة.',
  'monitoring.settings.slow': 'بطيء إذا تجاوز (بالملي ثانية)',
  'monitoring.settings.sslWarn': 'التنبيه قبل انتهاء الشهادة (بالأيام)',
  'monitoring.settings.sslWarnHint': 'نرسل تنبيهًا عندها، ثم مرة أخرى قبل الانتهاء بـ7 أيام و3 أيام ويوم واحد، وفي يوم الانتهاء نفسه.',
  'monitoring.settings.retention': 'الاحتفاظ بكل فحص (بالأيام)',
  'monitoring.settings.retentionHint': 'وتبقى أرقام كل يوم بعد ذلك.',
  'monitoring.settings.alerts': 'التنبيهات',
  'monitoring.settings.alertsEnabled': 'إرسال التنبيهات — بالبريد وتيليجرام والجرس',
  'monitoring.settings.bellEnabled': 'وفي جرس كل مدير نظام أيضًا',
  'monitoring.settings.reminder': 'تذكير «ما زال معطّلًا» بعد (دقيقة)',
  'monitoring.settings.reminderHint': 'يتكرر بالفاصل نفسه ما دام معطّلًا. 0: بلا تذكير.',
  'monitoring.settings.watchdog': 'المراقب الخارجي',
  'monitoring.settings.watchdogHint': 'كل شيء يعمل على VPS واحد، لذلك لا يستطيع نظام المراقبة أن ينبّه إذا توقف السيرفر نفسه. يعمل المراقب الخارجي على Cloudflare، ويقرأ هذا السيرفر كل دقيقة، ويرسل تنبيهًا بالبريد الإلكتروني وتيليجرام إلى المستلمين في «المراقبة › التنبيهات» ما دام السيرفر متوقفًا. يتم إعداده مرة واحدة من deploy/watchdog في المستودع.',
  'monitoring.settings.watchdogReading': 'المراقب الخارجي على Cloudflare يقرأ هذا السيرفر: آخر قراءة {when}، من {from}.',
  'monitoring.settings.watchdogStopped': 'توقف المراقب الخارجي على Cloudflare عن قراءة هذا السيرفر {when}. ربما تغيّر مفتاحه، أو تم حذف الـWorker.',
  'monitoring.settings.watchdogNever': 'لم يقرأ المراقب الخارجي على Cloudflare هذا السيرفر بعد.',
  'monitoring.settings.heartbeatUrl': 'أو رابط نبض لخدمة مراقبة أخرى (اختياري)',
  'monitoring.settings.saved': 'تم حفظ إعدادات المراقبة.',
  'nav.monitoringOverview': 'نظرة عامة',
  'nav.monitoringIncidents': 'الأعطال',
  'monitoring.overview.title': 'المراقبة',
  'monitoring.overview.subtitle': 'كل السيرفرات والروابط في لمحة: ما يعمل، وما تعطّل ومنذ متى.',
  'monitoring.overview.servers': 'السيرفرات المتصلة',
  'monitoring.overview.serversHint': 'بتحذير: {warning} · غير متصلة: {offline}',
  'monitoring.overview.urls': 'الروابط العاملة',
  'monitoring.overview.urlsHint': 'بأداء متراجع: {degraded} · موقوفة: {paused}',
  'monitoring.overview.downNow': 'معطّل الآن',
  'monitoring.overview.downNowHint': 'أعطال ما زالت مستمرة',
  'monitoring.overview.allGood': 'لا يوجد أي عطل',
  'monitoring.overview.certificates': 'شهادات تقترب من الانتهاء',
  'monitoring.overview.certificatesHint': 'ضمن أيام التنبيه الخاصة بها',
  'monitoring.overview.downNowSubtitle': 'الأطول أولًا، ويفتح كل صف صفحة رابطه.',
  'monitoring.overview.allIncidents': 'كل الأعطال',
  'monitoring.overview.allServers': 'كل السيرفرات',
  'monitoring.overview.since': 'معطّل منذ',
  'monitoring.overview.downFor': 'مدة العطل',
  'monitoring.overview.nothingDown': 'كل ما نفحصه يعمل',
  'monitoring.overview.nothingDownHint': 'يظهر العطل هنا لحظة تأكيده.',
  'monitoring.overview.recent': 'آخر الأعطال',
  'monitoring.overview.certificatesList': 'الشهادات',
  'monitoring.overview.certificatesListHint': 'شهادات HTTPS الأقرب إلى الانتهاء.',
  'monitoring.overview.noCertificates': 'لا تنتهي أي شهادة خلال الـ30 يومًا القادمة.',
  'monitoring.overview.slowest': 'الأبطأ حاليًا',
  'monitoring.overview.slowestHint': 'آخر زمن استجابة لكل رابط.',
  'monitoring.incidents.title': 'الأعطال',
  'monitoring.incidents.all': 'المستمرة والمنتهية',
  'monitoring.incidents.open': 'ما زالت مستمرة',
  'monitoring.incidents.over': 'منتهية',
  'monitoring.incidents.empty': 'لا توجد أعطال',
  'monitoring.incidents.emptyHint': 'نسجّل العطل عندما يفشل الرابط في فحوصاته المتتالية.',
  'monitoring.chart.title': 'زمن الاستجابة',
  'monitoring.chart.subtitle': 'سرعة استجابته، ومتى فشلت فحوصاته.',
  'monitoring.chart.range.24h': '24 ساعة',
  'monitoring.chart.range.7d': '7 أيام',
  'monitoring.chart.range.30d': '30 يومًا',
  'monitoring.chart.range.90d': '90 يومًا',
  'monitoring.chart.noChecks': 'لا توجد فحوصات في هذه الفترة بعد.',
  'monitoring.chart.average': 'المتوسط',
  'monitoring.chart.slowest': 'الأبطأ {ms}',
  'monitoring.chart.failedOf': 'فشلت من أصل {n} فحص',
  'monitoring.chart.noneHere': 'لم يتم الفحص في هذا الوقت',
  'monitoring.chart.averageBucket': 'متوسط الاستجابة لكل فترة',
  'monitoring.chart.averageDaily': 'متوسط الاستجابة لكل يوم',
  'monitoring.chart.failed': 'فحوصات فاشلة',
  'monitoring.chart.ninety': 'التوفر في آخر 90 يومًا',
  'monitoring.chart.ninetySummary': 'نسبة الفحوصات الناجحة في آخر 90 يومًا: {pct}',
  'monitoring.chart.noData': 'لا فحوصات',
  'monitoring.chart.dayUp': 'يعمل بنسبة {pct}',
  'monitoring.chart.dayChecks': 'الفحوصات: {n}',
  'monitoring.chart.today': 'اليوم',
  'monitoring.chart.key.up': 'يعمل طوال اليوم',
  'monitoring.chart.key.some': 'توقف قصير',
  'monitoring.chart.key.down': 'توقف أطول',
  'monitoring.chart.key.none': 'لم يتم الفحص',

  // محفظة النقاط (CR-WALLET-001، 2026-10-20) — من القائمة تحت اسم الموظف.
  'wallet.menu': 'محفظة النقاط',
  'wallet.title': 'محفظة النقاط',
  'wallet.subtitle': 'نقاطك، وما تستبدلها به، وكل حركة عليها.',
  'wallet.balance': 'رصيد نقاطك',
  'wallet.pointUnit': 'نقطة',
  'wallet.pointsUnit': 'نقطة',
  'wallet.zeroBalance': 'رصيدك الحالي 0 نقطة. عند إضافة نقاط إلى محفظتك لاحقًا، يمكنك استبدالها بالمكافآت المعروضة أدناه.',
  'wallet.balanceHint': 'لا نخصم النقاط من رصيدك إلا عند استبدالها، ونطلب تأكيدك قبل كل استبدال.',
  'wallet.redeemTitle': 'استبدال النقاط',
  'wallet.redeemSubtitle':
    'خمس مكافآت بالأسعار المحددة حاليًا. يعرض زر «استبدال» التكلفة، ورصيدك بعد الاستبدال، وما ستحصل عليه بالضبط، ولا نخصم شيئًا قبل أن تؤكد.',
  'wallet.type.REMOVE_QUARTER_DAY_PENALTY': 'إلغاء خصم ربع يوم',
  'wallet.type.REMOVE_HALF_DAY_PENALTY': 'إلغاء خصم نصف يوم',
  'wallet.type.REMOVE_FULL_DAY_PENALTY': 'إلغاء خصم يوم كامل',
  'wallet.type.ADD_ONE_LEAVE_DAY': 'إضافة يوم إجازة',
  'wallet.type.CASH_REWARD': 'استبدال النقاط بمبلغ مالي',
  'wallet.desc.REMOVE_QUARTER_DAY_PENALTY': 'يلغي خصمًا واحدًا بقيمة ربع يوم من خصوماتك المعتمدة التي لم تدخل كشف رواتب بعد، وأنت تختار أيّها.',
  'wallet.desc.REMOVE_HALF_DAY_PENALTY': 'يلغي خصمًا واحدًا بقيمة نصف يوم من خصوماتك المعتمدة التي لم تدخل كشف رواتب بعد، وأنت تختار أيّها.',
  'wallet.desc.REMOVE_FULL_DAY_PENALTY': 'يلغي خصمًا واحدًا بقيمة يوم كامل من خصوماتك المعتمدة التي لم تدخل كشف رواتب بعد، وأنت تختار أيّها.',
  'wallet.desc.ADD_ONE_LEAVE_DAY': 'يضيف يومًا واحدًا إلى رصيد {type} لعام {year}. تطلبه مثل أي إجازة، ويتم اعتماده كالمعتاد.',
  'wallet.desc.CASH_REWARD': 'يحوّل حزمًا من النقاط إلى مبلغ مالي تحصل عليه مع راتبك.',
  'wallet.annualLeave': 'الإجازة السنوية',
  'wallet.price': 'السعر',
  'wallet.rate': 'سعر الاستبدال',
  'wallet.cashRate': 'كل {points} = {amount}',
  'wallet.notPriced': 'ستحدد الإدارة تكلفة هذه المكافأة لاحقًا.',
  'wallet.redeem': 'استبدال',
  'wallet.eligibleCount': 'يمكن إلغاؤها: {n}',
  'wallet.held.awaitingDecision': 'بانتظار قرار: {n}',
  'wallet.held.dayNotOver': 'لم ينته يومها بعد: {n}',
  'wallet.held.onPayroll': 'في كشف رواتب بالفعل: {n}',
  'wallet.reason.NOT_CONFIGURED': 'لم يتم تحديد تكلفة هذه المكافأة بعد.',
  'wallet.reason.INSUFFICIENT_POINTS': 'رصيدك الحالي لا يكفي لهذه المكافأة.',
  'wallet.reason.NO_ELIGIBLE_PENALTY.REMOVE_QUARTER_DAY_PENALTY': 'ليس عليك خصم ربع يوم يمكن إلغاؤه الآن.',
  'wallet.reason.NO_ELIGIBLE_PENALTY.REMOVE_HALF_DAY_PENALTY': 'ليس عليك خصم نصف يوم يمكن إلغاؤه الآن.',
  'wallet.reason.NO_ELIGIBLE_PENALTY.REMOVE_FULL_DAY_PENALTY': 'ليس عليك خصم يوم كامل يمكن إلغاؤه الآن.',
  'wallet.reason.LEAVE_GRANT_UNAVAILABLE': 'لا يمكن إضافة أيام إجازة حاليًا.',
  'wallet.reason.OTHER_CURRENCY': 'يتم صرف المكافآت المالية بعملة {currency} مع الراتب، وراتبك بعملة أخرى.',
  'wallet.reason.NO_SALARY': 'يتم صرف المكافآت المالية مع الراتب، ولا يوجد راتب مسجّل لك بعد.',
  'wallet.howTitle': 'كيف تعمل محفظة النقاط؟',
  'wallet.howText':
    'هنا تتابع رصيد نقاطك، ومتى تمت إضافة النقاط أو استبدالها. تظهر كل حركة جديدة في السجل بتاريخها وعدد نقاطها. عندما تكفي نقاطك، يمكنك استبدالها لإلغاء خصم عليك، أو إضافة يوم إلى رصيد إجازاتك، أو الحصول على مكافأة مالية، حسب الأسعار المحددة في إعدادات النظام.',
  'wallet.howNeeds': 'يحتاج إلى {points}',
  'wallet.howCash': 'كل {points} = {amount}',
  'wallet.howUnpriced': 'ستحدد الإدارة تكلفة هذه المكافأة لاحقًا',
  'wallet.howExample': 'مثلًا: {points} تساوي {amount} نقدًا.',
  'wallet.historyTitle': 'سجل النقاط',
  'wallet.historyEmpty': 'لا توجد حركات نقاط حتى الآن.',
  'wallet.kind.CREDIT': 'تمت إضافة نقاط',
  'wallet.kind.REDEMPTION_DEBIT': 'استبدال: {reward}',
  'wallet.kind.REFUND_CREDIT': 'إعادة نقاط من استبدال',
  'wallet.date': 'التاريخ',
  'wallet.time': 'الوقت',
  'wallet.status.COMPLETED': 'تمت',
  'wallet.status.PENDING_SETTLEMENT': 'قيد الصرف',
  'wallet.status.PAID': 'تم الصرف',
  'wallet.status.CANCELLED': 'ملغاة',
  'wallet.penaltyKind.SLA_VIEW': 'عدم الاطلاع على تذكرة في موعدها',
  'wallet.penaltyKind.SLA_ASSIGNMENT': 'عدم إسناد تذكرة في موعدها',
  'wallet.penaltyKind.BREAK': 'تجاوز مدة الاستراحة',
  'wallet.penaltyKind.IDLE': 'عدم العمل خلال الوردية',
  'wallet.penaltyKind.SHORT_DAY': 'عدم إكمال ساعات العمل اليومية',
  'wallet.penaltyKind.EARLY_END': 'إنهاء الوردية مبكرًا',
  'wallet.penaltyKind.OTHER': 'خصم',
  'wallet.penaltyDays': '{days} يوم',
  'wallet.leaveAdded': 'تمت إضافة يوم إلى {type}، {year}',
  'wallet.cashPending': 'مكافأة مالية قيد الصرف: {amount}',
  'wallet.cashOnPayroll': 'مكافأة مالية قيد الصرف: {amount} · في كشف رواتب {month}',
  'wallet.cashPaid': 'تم الصرف: {amount}',
  'wallet.cashPaidWith': 'تم الصرف: {amount} · مع راتب {month}',
  'wallet.cashCancelled': 'تم إلغاء {amount}، وعادت النقاط إلى المحفظة',
  'wallet.redeemPageHint': 'راجع التكلفة وما ستحصل عليه، ثم أكّد. لن نخصم شيئًا قبل تأكيدك.',
  'wallet.noSuchReward': 'هذه المكافأة غير موجودة.',
  'wallet.cost': 'التكلفة',
  'wallet.youGet': 'تحصل على',
  'wallet.balanceNow': 'رصيدك الآن',
  'wallet.balanceAfterRedeem': 'بعد الاستبدال',
  'wallet.get.penalty': 'إلغاء خصم واحد قدره {days} يوم',
  'wallet.get.leave': 'يوم من {type}، {year}',
  'wallet.get.cash': '{amount}، تحصل عليه مع راتبك',
  'wallet.pickPenalty': 'الخصم الذي تريد إلغاءه',
  'wallet.pickPenaltyHint': 'خصوماتك المعتمدة بهذه القيمة التي لم تدخل كشف رواتب بعد، الأقدم أولًا.',
  'wallet.bundles': 'عدد الحزم',
  'wallet.bundlesHint': 'كل حزمة {points} مقابل {amount}. أقصى عدد يسمح به رصيدك: {max}.',
  'wallet.bundlesInvalid': 'عدد صحيح من 1 إلى {max}.',
  'wallet.cashNote': 'تحصل عليها مع راتبك في أقرب كشف رواتب تعدّه المالية، وتظهر «قيد الصرف» حتى يتم صرفها.',
  'wallet.leaveBalance': 'المتبقي من إجازتك السنوية: {before} الآن، و{after} بعد الاستبدال.',
  'wallet.confirmQuestion': 'هل تؤكد استبدال {points} مقابل «{reward}»؟',
  'wallet.confirmSubmit': 'استبدال {points}',
  'wallet.priceChanged': 'تغيّر السعر. راجع التكلفة الجديدة وأكّد مرة ثانية.',
  'wallet.done.penalty': 'تم إلغاء الخصم، وخصم النقاط من محفظتك.',
  'wallet.done.leave': 'تمت إضافة يوم إجازة إلى رصيدك.',
  'wallet.done.cash': 'تم تسجيل مكافأتك المالية، وهي قيد الصرف.',
  'wallet.done.replayed': 'تم هذا الاستبدال من قبل، ولم يتم خصم أي نقاط إضافية.',
  'wallet.error.INSUFFICIENT_POINTS': 'رصيدك الحالي لا يكفي لهذه المكافأة. لم يتم خصم نقاطك.',
  'wallet.error.REWARD_NOT_CONFIGURED': 'هذه المكافأة بلا سعر حتى الآن. لم يتم خصم نقاطك.',
  'wallet.error.NO_ELIGIBLE_PENALTY': 'لا يمكن إلغاء هذا الخصم الآن. لم يتم خصم نقاطك.',
  'wallet.error.NOT_CHARGED': 'هذا الخصم ما زال بانتظار قرار قد يلغيه. لم يتم خصم نقاطك.',
  'wallet.error.DAY_NOT_OVER': 'يوم هذا الخصم لم ينته بعد، وإكمال ساعات العمل اليومية قد يلغيه. لم يتم خصم نقاطك.',
  'wallet.error.PENALTY_ALREADY_REVERSED': 'تم إلغاء هذا الخصم من قبل. لم يتم خصم نقاطك.',
  'wallet.error.PAYROLL_PERIOD_LOCKED': 'هذا الخصم دخل كشف رواتب بالفعل، فلا يمكن إلغاؤه الآن. لم يتم خصم نقاطك.',
  'wallet.error.LEAVE_GRANT_UNAVAILABLE': 'لا يمكن إضافة يوم إجازة حاليًا. لم يتم خصم نقاطك.',
  'wallet.error.PRICE_CHANGED': 'تغيّر السعر. راجع التكلفة الجديدة وأكّد مرة ثانية. لم يتم خصم نقاطك.',
  'wallet.error.IDEMPOTENCY_CONFLICT': 'تعارض هذا التأكيد مع تأكيد آخر. لم يتم خصم نقاطك، أكّد مرة ثانية.',
  'wallet.error.CONCURRENT_CHANGE': 'محفظتك مشغولة بطلب آخر. لم يتم خصم نقاطك، حاول مرة ثانية بعد لحظات.',
  'wallet.error.general': 'لم نتمكن من إتمام الاستبدال. لم يتم خصم نقاطك.',
  'wallet.error.network': 'لم يصل رد. اضغط «استبدال» مرة ثانية، ولن نخصم النقاط مرتين للتأكيد نفسه.',
  'settings.wallet': 'المحفظة والنقاط',
  'settings.walletNote':
    'تكلفة كل مكافأة في محافظ الموظفين بالنقاط. المكافأة التي تتركها فارغة لا تكون متاحة. أي تغيير يسري على الاستبدالات من الآن فصاعدًا، والاستبدال الذي تم قبل ذلك يبقى بسعره.',
  'settings.wallet.quarterDayPenaltyPoints': 'نقاط إلغاء خصم ربع يوم',
  'settings.wallet.halfDayPenaltyPoints': 'نقاط إلغاء خصم نصف يوم',
  'settings.wallet.fullDayPenaltyPoints': 'نقاط إلغاء خصم يوم كامل',
  'settings.wallet.leaveDayPoints': 'نقاط الحصول على يوم إجازة',
  'settings.wallet.cashPointsPerBundle': 'عدد النقاط لكل حزمة نقدية',
  'settings.wallet.cashAmountPerBundle': 'المبلغ النقدي لكل حزمة',
  'settings.wallet.cashCurrency': 'عملة المكافأة النقدية',
  'settings.wallet.unsetHint': 'فارغ: غير متاحة.',
  'settings.wallet.leaveHint': 'تتم إضافة اليوم إلى الإجازة السنوية في سنة الاستبدال. فارغ: غير متاحة.',
  'settings.wallet.cashHint':
    'يتم صرف المبلغ مع الراتب في أقرب كشف رواتب تحسبه المالية، ولا يحصل عليه إلا من راتبه بهذه العملة، دون استخدام أي سعر صرف. وتعرض «المالية › مكافآت النقاط» المبالغ المستحقة.',
  'settings.wallet.cashExample': 'مثلًا: {points} = {amount}.',
  'settings.wallet.noCurrency': '— غير محددة —',
  'settings.wallet.offered': 'متاحة',
  'settings.wallet.notOffered': 'غير متاحة',
  'settings.wallet.earning':
    'لم يتم إعداد طريقة كسب الموظفين للنقاط بعد، وستأتي في تحديث لاحق. لا يوجد حاليًا ما يضيف نقاطًا، لذلك يبقى رصيد كل محفظة كما هو.',
  'settings.wallet.cashReach':
    'يتم صرف المكافأة بعملة {currency}: {n} من الموظفين النشطين رواتبهم بعملة أخرى ({list})، و{m} ليس لهم راتب مسجّل. هؤلاء لا يمكنهم استبدال النقاط بمبلغ مالي.',
  'settings.wallet.ledger': 'مطابقة السجل',
  'settings.wallet.ledgerOk': 'جميع المحافظ ({n}) تطابق سجلها (المجموع {points}).',
  'settings.wallet.ledgerBad': '{bad} من {n} محفظة لا تطابق سجلها. أبلغ المطورين قبل أي استبدال.',
  'settings.problem.TOO_MANY_DECIMALS': '{limit} منازل عشرية على الأكثر.',
  'settings.problem.UNKNOWN_CURRENCY': 'عملة نتعامل بها («المالية › الإعدادات › العملات»).',
  'violations.walletCancelled': 'تم إلغاؤه مقابل {points} نقطة من المحفظة',
  'leaves.grantedDays': '+{n} مضافة بالنقاط',
  'employees.del.walletRedemptions': 'عمليات استبدال النقاط، مع محفظتها وسجلها',
  'nav.meetings': 'تقويم الاجتماعات',
  'meetings.title': 'تقويم الاجتماعات',
  'meetings.subtitle': 'اجتماعاتك، وكل دعوة بانتظار ردّك.',
  'meetings.subtitleLeader': 'اجتماعاتك، ومع «فريقي» تظهر اجتماعات العاملين في أقسامك.',
  'meetings.subtitleAdmin': 'اجتماعاتك، ومع «الجميع» تظهر كل اجتماعات الشركة.',
  'meetings.new': 'اجتماع جديد',
  'meetings.newShort': 'جديد',
  'meetings.newAt': 'اجتماع جديد في {time}',
  'meetings.today': 'اليوم',
  'meetings.previous': 'السابق',
  'meetings.next': 'التالي',
  'meetings.viewLabel': 'طريقة العرض',
  'meetings.view.month': 'شهر',
  'meetings.view.week': 'أسبوع',
  'meetings.view.list': 'قائمة',
  'meetings.scopeLabel': 'اجتماعات من',
  'meetings.scope.mine': 'اجتماعاتي',
  'meetings.scope.team': 'فريقي',
  'meetings.scope.all': 'الجميع',
  'meetings.person': 'اجتماعات شخص واحد',
  'meetings.personAll': 'الجميع',
  'meetings.waitingTitle': 'بانتظار ردّك',
  'meetings.waitingHint': 'كل مدعوّ إلى اجتماع يقبل الدعوة أو يعتذر عنها.',
  'meetings.waitingCount': 'دعوات اجتماعات بانتظار ردّك: {n}',
  'meetings.fromName': 'من {name}',
  'meetings.accept': 'قبول',
  'meetings.decline': 'اعتذار',
  'meetings.acceptedToast': 'تم قبول الدعوة إلى الاجتماع.',
  'meetings.declinedToast': 'تم تسجيل اعتذارك، ووصل إشعار إلى من دعا إلى الاجتماع.',
  'meetings.calledToast': 'تم إرسال الدعوة إلى الاجتماع، ووصل إشعار إلى كل المدعوّين.',
  'meetings.savedToast': 'تم حفظ الاجتماع.',
  'meetings.cancelledToast': 'تم إلغاء الاجتماع، ووصل إشعار إلى كل المدعوّين.',
  'meetings.more': '+{n} أخرى',
  'meetings.dayCount': 'الاجتماعات: {n}',
  'meetings.dayAgenda': 'اجتماعات اليوم',
  'meetings.noneDay': 'لا توجد اجتماعات في هذا اليوم.',
  'meetings.noneMonth': 'لا توجد اجتماعات في هذا الشهر',
  'meetings.noneMonthHint': 'تظهر هنا الاجتماعات التي تدعو إليها، أو التي تصلك دعوة إليها.',
  'meetings.legend': 'معنى الألوان',
  'meetings.look.organizer': 'دعوت إليه',
  'meetings.look.accepted': 'قبلت الدعوة',
  'meetings.look.pending': 'بانتظار ردّك',
  'meetings.look.declined': 'اعتذرت',
  'meetings.look.viewer': 'اجتماع لغيرك',
  'meetings.look.cancelled': 'ملغى',
  'meetings.answersShort': 'وافق {accepted} من {total}',
  'meetings.place.OFFICE': 'في المكتب',
  'meetings.place.OUTSIDE': 'خارج المكتب',
  'meetings.place.ONLINE': 'عبر الإنترنت',
  'meetings.placeHint.OFFICE': 'داخل الشركة، في قاعة اجتماعات مثلًا.',
  'meetings.placeHint.OUTSIDE': 'عند عميل، أو في أي مكان خارج المكتب.',
  'meetings.placeHint.ONLINE': 'مكالمة فيديو.',
  'meetings.locationLabel.OFFICE': 'القاعة أو المكان (اختياري)',
  'meetings.locationLabel.OUTSIDE': 'العنوان',
  'meetings.locationLabel.ONLINE': 'رابط الاجتماع (اختياري)',
  'meetings.linkHint': 'يبدأ الرابط بـ https:// ويمكنك تركه فارغًا وإضافته لاحقًا إذا لم يكن لديك بعد.',
  'meetings.locationRequired': 'الاجتماع خارج المكتب يحتاج إلى عنوان.',
  'meetings.linkInvalid': 'رابط يبدأ بـ https://',
  'meetings.audience.ONE_TO_ONE': 'فردي',
  'meetings.audience.PEOPLE': 'بعض الموظفين',
  'meetings.audience.DEPARTMENTS': 'قسم',
  'meetings.audience.EVERYONE': 'الجميع',
  'meetings.audienceHint.ONE_TO_ONE': 'أنت وشخص واحد آخر.',
  'meetings.audienceHint.PEOPLE': 'من تختارهم.',
  'meetings.audienceHint.DEPARTMENTS': 'كل من في الأقسام التي تختارها.',
  'meetings.audienceHint.EVERYONE': 'كل الموظفين ({n}).',
  'meetings.audienceHintOwn.PEOPLE': 'من تختارهم من أقسامك.',
  'meetings.audienceHintOwn.DEPARTMENTS': 'كل من في أقسامك التي تختارها.',
  'meetings.answer.ACCEPTED': 'وافقوا',
  'meetings.answer.PENDING': 'لم يردّوا',
  'meetings.answer.DECLINED': 'اعتذروا',
  'meetings.mine.ACCEPTED': 'قبلت الدعوة',
  'meetings.mine.PENDING': 'بانتظار ردّك',
  'meetings.mine.DECLINED': 'اعتذرت',
  'meetings.yourAnswer.PENDING': 'لديك دعوة إلى هذا الاجتماع: اقبلها أو اعتذر عنها. سيرى من دعا إليه ردّك.',
  'meetings.yourAnswer.ACCEPTED': 'قبلت الدعوة. يمكنك الاعتذار عنها حتى ينتهي الاجتماع.',
  'meetings.yourAnswer.DECLINED': 'اعتذرت عن الاجتماع. يمكنك قبول الدعوة حتى ينتهي.',
  'meetings.yourNote': 'ملاحظتك',
  'meetings.status.SCHEDULED': 'مجدول',
  'meetings.status.STARTED': 'بدأ',
  'meetings.status.IN_PROGRESS': 'قيد الانعقاد',
  'meetings.status.DONE': 'منجز',
  'meetings.status.CANCELLED': 'ملغى',
  'meetings.status.OVER': 'انتهى',
  'meetings.status.NOW': 'حان موعده',
  'meetings.statusTitle': 'الحالة',
  'meetings.statusNotStarted': 'لم يبدأ بعد.',
  'meetings.statusHintRun': 'حدّث حالته مع سير الاجتماع، والحالة تتقدم إلى الأمام فقط.',
  'meetings.statusStartsFrom': 'يمكنك بدؤه من {time}، أي قبل موعده بنصف ساعة.',
  'meetings.statusSince': 'منذ {when}',
  'meetings.statusSinceBy': 'منذ {when} · بواسطة {name}',
  'meetings.statusClosedAtEnd': 'تم إغلاقه عند موعد انتهائه · {when}',
  'meetings.statusToast': 'حالة الاجتماع: {status}.',
  'meetings.move.STARTED': 'بدء الاجتماع',
  'meetings.move.IN_PROGRESS': 'قيد الانعقاد',
  'meetings.move.DONE': 'إنهاء الاجتماع',
  'meetings.moveTitle': 'نقل الاجتماع إلى «{status}»؟',
  'meetings.moveStartNote': 'بعد بدء الاجتماع لا يمكن تعديله أو إلغاؤه، وتتقدم حالته إلى الأمام فقط.',
  'meetings.moveForwardNote': 'تتقدم الحالة إلى الأمام فقط، ولا يمكن إرجاعها.',
  'meetings.moveDoneNote': 'إنهاء الاجتماع يغلقه: تتوقف الردود، ولا يمكن التراجع عنه.',
  'meetings.editLockedStarted': 'بدأ الاجتماع، فلا يمكن تعديله أو إلغاؤه الآن. اضغط «إنهاء الاجتماع» عندما ينتهي.',
  'meetings.cancelledBanner': 'ألغاه {name} · {when}',
  'meetings.edit': 'تعديل',
  'meetings.cancel': 'إلغاء الاجتماع',
  'meetings.details': 'الاجتماع',
  'meetings.date': 'التاريخ',
  'meetings.time': 'الوقت',
  'meetings.where': 'المكان',
  'meetings.calledBy': 'الدعوة من',
  'meetings.forLabel': 'المدعوّون',
  'meetings.reminder': 'التذكير',
  'meetings.reminderBefore': 'قبل بدئه بـ {time}',
  'meetings.reminderOff': 'لا يوجد — التذكيرات متوقفة في «الإعدادات › الاجتماعات»',
  'meetings.agenda': 'جدول الأعمال',
  'meetings.noAgenda': 'لا يوجد جدول أعمال.',
  'meetings.answers': 'الردود',
  'meetings.you': 'أنت',
  'meetings.newTitle': 'اجتماع جديد',
  'meetings.editTitle': 'تعديل الاجتماع',
  'meetings.formSubtitle': 'يصل إشعار إلى كل من تدعوه، ليقبل الدعوة أو يعتذر عنها.',
  'meetings.titleLabel': 'العنوان',
  'meetings.titleHint': 'موضوع الاجتماع.',
  'meetings.whoFor': 'المدعوّون',
  'meetings.pickPerson': 'اختر الشخص.',
  'meetings.pickPeople': 'اختر شخصًا واحدًا على الأقل.',
  'meetings.pickDepartments': 'اختر قسمًا واحدًا على الأقل.',
  'meetings.personFull': 'شخص واحد فقط، احذفه لتختار شخصًا آخر.',
  'meetings.noDepartments': 'لست في أي قسم.',
  'meetings.noReach': 'لست في أي قسم، فلا يوجد من تدعوه. يمكن لمدير النظام أن يضيفك إلى قسم.',
  'meetings.everyoneAdmins': 'تدعو العاملين في أقسامك. دعوة الجميع إلى اجتماع متاحة لمديري النظام فقط.',
  'meetings.starts': 'يبدأ',
  'meetings.ends': 'ينتهي',
  'meetings.length': 'المدة',
  'meetings.reminderNote': 'يصل تذكير قبل بدئه بـ {time} إليك وإلى كل من لم يعتذر («الإعدادات › الاجتماعات»).',
  'meetings.reminderNoteOff': 'لن يصل تذكير، لأن التذكيرات متوقفة في «الإعدادات › الاجتماعات».',
  'meetings.busyTitle': 'مشغولون في هذا الوقت',
  'meetings.busyMeeting': 'اجتماع آخر',
  'meetings.busyLeave': 'في إجازة',
  'meetings.busyYou': 'أنت',
  'meetings.busyHint': 'يمكنك إرسال الدعوة رغم ذلك، وكل شخص يرد عن نفسه.',
  'meetings.agendaLabel': 'جدول الأعمال (اختياري)',
  'meetings.agendaHint': 'الغرض منه، وما يلزم إحضاره.',
  'meetings.askAgainNote': 'تغيير الموعد أو المكان يعيد إرسال الدعوة إلى الجميع، وتبدأ ردودهم من جديد.',
  'meetings.call': 'إرسال الدعوة',
  'meetings.declineTitle': 'الاعتذار عن الاجتماع',
  'meetings.declineNote': 'السبب؟ (اختياري)',
  'meetings.declineNoteHint': 'يقرؤه من دعا إليه.',
  'meetings.cancelTitle': 'إلغاء الاجتماع',
  'meetings.cancelNote': 'السبب؟ (اختياري)',
  'meetings.cancelNoteHint': 'يقرؤه كل المدعوّين.',
  'meetings.notAllowed': 'لا يمكن تنفيذ هذا على الاجتماع الآن: تم إلغاؤه، أو إنهاؤه، أو انتهى وقته، أو ليست لديك صلاحية لتعديله.',
  'meetings.refusal.CANCELLED': 'تم إلغاء هذا الاجتماع.',
  'meetings.refusal.OVER': 'انتهى هذا الاجتماع.',
  'meetings.refusal.DONE': 'تم إنهاء هذا الاجتماع.',
  'meetings.refusal.STARTED': 'بدأ هذا الاجتماع، فلا يمكن تعديله أو إلغاؤه الآن.',
  'meetings.refusal.BACKWARDS': 'تتقدم حالة الاجتماع إلى الأمام فقط.',
  'meetings.refusal.TOO_EARLY': 'يمكن بدء الاجتماع قبل موعده بنصف ساعة على الأكثر.',
  'meetings.refusal.NOT_STARTED': 'ابدأ الاجتماع قبل إنهائه.',
  'meetings.refusal.NOBODY': 'لا يوجد فيما اخترته أحد غيرك لتدعوه.',
  'meetings.refusal.INACTIVE': 'أحد من اخترتهم لم يعد موظفًا نشطًا.',
  'meetings.refusal.DEPARTMENT': 'أحد الأقسام التي اخترتها غير موجود أو متوقف.',
  'meetings.refusal.EVERYONE': 'دعوة الجميع إلى اجتماع متاحة لمديري النظام فقط.',
  'meetings.refusal.INVALID': 'حدّد التاريخ ووقت البدء ووقت الانتهاء.',
  'meetings.refusal.ENDS_BEFORE_START': 'يجب أن ينتهي بعد أن يبدأ.',
  'meetings.refusal.TOO_SHORT': '5 دقائق على الأقل.',
  'meetings.refusal.TOO_LONG': 'يوم واحد على الأكثر.',
  'meetings.refusal.IN_THE_PAST': 'هذا الوقت مضى، اختر وقتًا لاحقًا للاجتماع.',
  'meetings.refusal.CONCURRENT_CHANGE': 'تغيّر هذا الاجتماع وأنت تعرضه. افتحه مرة ثانية لتراه كما هو الآن.',
  'meetings.refusal.outside': 'هذا الشخص ليس في أقسامك، ويمكنك دعوة العاملين في أقسامك فقط.',
  'meetings.refusal.outsideDepartment': 'هذا القسم ليس من أقسامك، ويمكنك الدعوة إلى اجتماعات لأقسامك فقط.',
  'settings.meetings': 'الاجتماعات',
  'settings.meetingsNote': '«تقويم الاجتماعات» تحت «قائمة المطلوب إنجازه»: يقبل كل مدعوّ الدعوة أو يعتذر عنها، ويصل تذكير قبل كل اجتماع.',
  'settings.meetingReminder': 'التذكير قبل الاجتماع، بالدقائق',
  'settings.meetingReminderHint': '15 افتراضيًا، و0 يعني بلا تذكير. يصل التذكير إلى من دعا إلى الاجتماع وإلى كل من لم يعتذر، في الجرس الذي يظهر في تطبيق الهاتف أيضًا.',
  'settings.meetingEmailInvitations': 'إرسال الدعوات وتغييراتها وإلغائها بالبريد الإلكتروني',
  'settings.meetingEmailInvitationsHint': 'عند إيقافه، تصل إلى الجرس فقط.',
  'settings.meetingEmailReminders': 'إرسال التذكير بالبريد الإلكتروني أيضًا',
  'employees.del.meetings': 'الاجتماعات التي دعا إليها، مع ردود الجميع',
  // روابط الحجز (2026-11-05).
  'meetings.audience.GUEST': 'ضيف (حجز)',
  'meetings.refusal.GUEST': 'الاجتماع الذي حجزه ضيف يبقى في الموعد الذي اختاره. ألغِه، ويمكن للضيف حجز موعد آخر.',
  'settings.meetingBookingLinks': 'روابط الحجز',
  'settings.meetingBookingLinksHint': 'يمكن لكل موظف تشغيل رابط حجز من «تقويم الاجتماعات › رابط الحجز». إذا أوقفتها، لا يقبل أي رابط حجوزات.',
  'booking.button': 'رابط الحجز',
  'booking.title': 'رابط الحجز',
  'booking.subtitle': 'أرسل رابطك إلى أي شخص: يختار موعدًا متاحًا في ساعاتك دون تسجيل دخول، ويُضاف الاجتماع إلى تقويمك.',
  'booking.offEverywhere': 'روابط الحجز متوقفة من «الإعدادات › الاجتماعات»، لذلك لا يقبل رابطك أي حجز الآن. ساعاتك محفوظة.',
  'booking.linkTitle': 'رابطك',
  'booking.linkHint': 'أي شخص لديه الرابط يمكنه حجز اجتماع معك. أنشئ رابطًا جديدًا لإيقاف الرابط القديم.',
  'booking.enabled': 'قبول الحجوزات من رابطي',
  'booking.enabledHint': 'إذا أوقفته، يعرض الرابط أنك لا تقبل حجوزات الآن.',
  'booking.copy': 'نسخ الرابط',
  'booking.copied': 'تم نسخ الرابط.',
  'booking.open': 'فتح',
  'booking.newLink': 'رابط جديد',
  'booking.newLinkTitle': 'إنشاء رابط جديد؟',
  'booking.newLinkBody': 'سيتوقف رابطك الحالي فورًا. وتبقى الاجتماعات المحجوزة منه في تقويمك.',
  'booking.newLinkToast': 'تم إنشاء رابط جديد، ولم يعد الرابط القديم يعمل.',
  'booking.upcoming': 'اجتماعات قادمة تم حجزها من رابطك: {n}',
  'booking.hoursTitle': 'متى تقبل الاجتماعات',
  'booking.hoursHint': 'بتوقيت الشركة ({timezone}). ويرى الزوار المواعيد بتوقيتهم.',
  'booking.dayOff': 'لا اجتماعات',
  'booking.addHours': 'إضافة ساعات',
  'booking.removeHours': 'حذف هذه الساعات',
  'booking.copyToAll': 'نسخ إلى كل الأيام',
  'booking.copiedToAll': 'تم النسخ إلى كل الأيام. احذف الأيام التي لا تريدها.',
  'booking.from': 'من',
  'booking.to': 'إلى',
  'booking.hoursOrder': 'يجب أن تنتهي بعد أن تبدأ.',
  'booking.hoursShort': 'أقصر من اجتماع واحد مدته {time}.',
  'booking.hoursOverlap': 'تتداخل مع الساعات التي قبلها.',
  'booking.noHours': 'أضف الساعات التي تقبل فيها الاجتماعات قبل تشغيل رابطك.',
  'booking.meetingTitle': 'الاجتماعات',
  'booking.pageTitle': 'العنوان في الصفحة (اختياري)',
  'booking.pageTitleHint': 'إذا تركته فارغًا، يظهر «{title}».',
  'booking.intro': 'كلمات قليلة للزوار (اختياري)',
  'booking.introHint': 'هدف الاجتماع، وما الذي يجب تحضيره.',
  'booking.duration': 'مدة كل اجتماع',
  'booking.place': 'المكان',
  'booking.locationHint': 'لا يراه الزوار إلا بعد الحجز.',
  'booking.buffer': 'وقت فارغ حول اجتماعاتك',
  'booking.bufferHint': 'قبل كل اجتماع لك وبعده، سواء كان محجوزًا من الرابط أو لا.',
  'booking.notice': 'الحجز قبل الموعد بـ',
  'booking.noticeHint': 'أقل وقت قبل الاجتماع يمكن الحجز فيه.',
  'booking.noticeNone': 'أي وقت قبله',
  'booking.noticeBefore': '{time} على الأقل',
  'booking.daysAhead': 'الحجز حتى',
  'booking.daysAheadValue': '{days} من اليوم',
  'booking.none': 'لا يوجد',
  'booking.saved': 'تم حفظ صفحة الحجز.',
  'booking.saveFailed': 'لم يتم حفظ صفحة الحجز. حاول مرة أخرى.',
  'booking.unsaved': 'لديك تغييرات لم يتم حفظها.',
  'booking.save': 'حفظ',
  'booking.guest': 'الضيف',
  'booking.guestBooked': 'تم الحجز من رابط الحجز · {when}',
  'booking.guestCancelled': 'ألغاه الضيف · {when}',
  'booking.guestLocked': 'اختار ضيف هذا الموعد، لذلك لا يمكن تغييره. إذا ألغيته، يصله بريد إلكتروني ويمكنه حجز موعد آخر.',

  // ورديات الموظف في شهر، من اسمه في لوحة الفريق (2026-10-27).
  'attendance.monthShifts': 'ورديات الشهر',
  'attendance.noShiftsInMonth': 'لا توجد ورديات في {month}.',
  'attendance.daysWorked': 'أيام العمل',
  'attendance.previousMonth': 'الشهر السابق',
  'attendance.nextMonth': 'الشهر التالي',

  // CR-004 (2026-10-28): اليوم الناقص بأكثر من ساعة تقرر فيه الإدارة؛ مكافآت بجزء من أجر يوم؛ نقاط تضيفها الإدارة.
  'nav.rewards': 'المكافآت والنقاط',
  'shortfall.tab': 'الأيام الناقصة',
  'shortfall.waitingCount': 'أيام ناقصة بانتظار القرار: {n}',
  'shortfall.queueNote':
    'أيام عمل نقصت عن ساعاتها بأكثر من ساعة. لا نخصم شيئًا حتى يقرر مدير النظام: جزء من أجر يوم، أو يوم من الإجازة السنوية بدلًا منه، أو إعفاء بلا خصم. أما النقص بساعة أو أقل فيبقى خصمه التلقائي ربع يوم.',
  'shortfall.filter.PENDING': 'بانتظار القرار',
  'shortfall.filter.RESOLVED': 'تم القرار',
  'shortfall.filter.ALL': 'الكل',
  'shortfall.search': 'اسم الموظف أو رمزه',
  'shortfall.from': 'من',
  'shortfall.to': 'إلى',
  'shortfall.workDate': 'اليوم',
  'shortfall.required': 'المطلوب',
  'shortfall.worked': 'المنجز',
  'shortfall.missing': 'النقص',
  'shortfall.decisionCol': 'القرار',
  'shortfall.nothingWaiting': 'لا يوجد يوم ناقص بانتظار القرار.',
  'shortfall.none': 'لا توجد أيام ناقصة.',
  'shortfall.decide': 'اتخاذ القرار',
  'shortfall.reverse': 'إلغاء القرار',
  'shortfall.status.PENDING_ADMIN_REVIEW': 'بانتظار القرار',
  'shortfall.status.RESOLVED_SALARY': 'خصم من الراتب',
  'shortfall.status.RESOLVED_LEAVE': 'يوم من الإجازة',
  'shortfall.status.RESOLVED_EXCUSED': 'إعفاء',
  'shortfall.decision.salary': '{percent}٪ من أجر يوم',
  'shortfall.decision.leave': 'يوم واحد من الإجازة السنوية',
  'shortfall.decision.excused': 'إعفاء — بلا خصم',
  'shortfall.title': 'يوم ناقص',
  'shortfall.theDay': 'اليوم',
  'shortfall.rule': 'نقص بأكثر من {minutes}: يقرر مدير النظام، ولا نخصم شيئًا قبل ذلك.',
  'shortfall.inForce': 'القرار الساري',
  'shortfall.noDecision': 'لا يوجد قرار بعد، فلا خصم من الراتب ولا من الإجازة.',
  'shortfall.decidedBy': 'قرار {name}، {date}',
  'shortfall.note': 'ملاحظة لمديري النظام',
  'shortfall.noteHint': 'اختيارية. لا يقرؤها إلا مديرو النظام، ولا يراها الموظف أبدًا.',
  'shortfall.posting': 'ما تم تسجيله',
  'shortfall.openPenalty': 'فتح الجزاء',
  'shortfall.penaltyOnPayroll': 'تم خصمه في كشف رواتب {month}',
  'shortfall.penaltyWaiting': 'سيتم خصمه في كشف رواتب {month}',
  'shortfall.penaltyCancelled': 'تم إلغاؤه لاحقًا: {reason}',
  'shortfall.penaltyWallet': 'تم إلغاؤه لاحقًا مقابل {points} نقطة من المحفظة',
  'shortfall.leaveTaken': 'تم خصم يوم من {type}، {year}',
  'shortfall.history': 'السجل',
  'shortfall.action.RESOLVED': 'قرار',
  'shortfall.action.REVERSED': 'إلغاء',
  'shortfall.noHistory': 'لم يتم اتخاذ أي قرار بعد.',
  'shortfall.ownDay': 'هذا يومك أنت، ويقرر فيه مدير نظام آخر.',
  'shortfall.context': 'قبل أن تقرر',
  'shortfall.dayRate': 'أجر يوم',
  'shortfall.decideTitle': 'القرار بشأن يوم ناقص',
  'shortfall.choose': 'القرار',
  'shortfall.salaryOption': 'خصم من الراتب (٪)',
  'shortfall.salaryOptionHint': 'نسبة من أجر يوم واحد: 25 تعني ربع يوم، و100 يومًا كاملًا، و200 يومين.',
  'shortfall.leaveOption': 'خصم يوم من الإجازة',
  'shortfall.leaveOptionHint': 'يوم واحد من الإجازة السنوية بدلًا من ذلك، ولا شيء من الراتب.',
  'shortfall.excuseOption': 'إعفاء — بلا خصم',
  'shortfall.excuseOptionHint': 'لا شيء من الراتب ولا من الإجازة. اذكر السبب أدناه.',
  'shortfall.excuseReason': 'سبب الإعفاء',
  'shortfall.excuseReasonHint': 'مطلوب، من 3 إلى 1,000 حرف. لا يقرؤه إلا مديرو النظام، ويصل إلى الموظف خبر الإعفاء دون سببه.',
  'shortfall.confirmExcuse': 'أؤكد: إعفاء {name} عن يوم {date}، بلا خصم من الراتب أو الإجازة.',
  'shortfall.percent': 'النسبة من أجر يوم',
  'shortfall.percentHint': 'رقم صحيح من 1 إلى {max}.',
  'shortfall.equivalent': '= {days} من الأجر',
  'shortfall.amountPreview': '≈ {amount} حسب أجر اليوم في ذلك التاريخ. يحسبه كشف الرواتب بأجر اليوم في شهره.',
  'shortfall.noSalaryPreview': 'يظهر المبلغ لمن يتولى الرواتب. يحسبه كشف الرواتب بأجر اليوم في شهره.',
  'shortfall.leaveBalance': 'الإجازة السنوية {year}: الأيام المتبقية {remaining}',
  'shortfall.leavePending': '{n} أخرى بانتظار الموافقة',
  'shortfall.leaveProblem.INSUFFICIENT_BALANCE': 'المتبقي من الإجازة السنوية لعام {year} أقل من يوم، فاختر خصم جزء من أجر يوم بدلًا من ذلك.',
  'shortfall.leaveProblem.LEAVE_TYPE_UNAVAILABLE':
    'الإجازة السنوية متوقفة، أو غير مدفوعة، أو بلا رصيد سنوي («الإعدادات › الإجازات»)، فلا يمكن الخصم منها.',
  'shortfall.monthSignedOff': 'تم اعتماد رواتب {month}، فلا يمكن إضافة خصم من الراتب إليها. اخصم يومًا من الإجازة بدلًا من ذلك.',
  'shortfall.confirmSalary': 'أؤكد: خصم {percent}٪ من أجر يوم {name} عن {date}.',
  'shortfall.confirmLeave': 'أؤكد: خصم يوم من الإجازة السنوية لـ {name} عن {date}، ولا شيء من الراتب.',
  'shortfall.submit': 'اعتماد القرار',
  'shortfall.decidedToast': 'تم القرار، ووصل إشعار إلى {name}.',
  'shortfall.changed': 'تغيّر هذا اليوم وأنت تعرضه. راجعه من جديد.',
  'shortfall.reverseTitle': 'إلغاء القرار',
  'shortfall.reverseReason': 'السبب',
  'shortfall.reverseHint': 'يبقى القرار في السجل. ويصل إلى الموظف إشعار بسحب القرار، دون سببه.',
  'shortfall.reverseSalaryNote': 'سيتم إلغاء الخصم، إلا إذا كان كشف رواتب قد أخذه بالفعل.',
  'shortfall.reverseLeaveNote': 'سيتم إرجاع يوم الإجازة.',
  'shortfall.reverseExcuseNote': 'سيتم سحب الإعفاء.',
  'shortfall.reverseThen': 'ثم ينتظر اليوم قرارًا جديدًا.',
  'shortfall.reversedToast': 'تم إلغاء القرار، واليوم ينتظر قرارًا جديدًا.',
  'shortfall.mineTitle': 'أيامي الناقصة',
  'shortfall.minePending': 'بانتظار قرار مدير النظام، ولا خصم قبل ذلك.',
  'shortfall.mineSalary': 'القرار: {percent}٪ من أجر يوم',
  'shortfall.mineLeave': 'القرار: يوم من الإجازة السنوية، ولا شيء من الراتب',
  'shortfall.mineExcused': 'إعفاء — بلا خصم',
  'violations.kind.shortfall': 'يوم ناقص — بقرار مدير النظام',
  'violations.shortfallDecision': 'قرار مدير النظام: {percent}٪ من أجر يوم',
  'violations.openShortDay': 'فتح اليوم الناقص',
  'rewards.title': 'المكافآت والنقاط',
  'rewards.tabRewards': 'مكافآت الأيام',
  'rewards.tabPoints': 'النقاط المضافة',
  'rewards.grant': 'منح مكافأة',
  'rewards.addPoints': 'إضافة نقاط',
  'rewards.size': 'المكافأة',
  'rewards.size.quarter': 'ربع يوم',
  'rewards.size.half': 'نصف يوم',
  'rewards.status.GRANTED': 'بانتظار الصرف',
  'rewards.status.PAID': 'تم الصرف',
  'rewards.status.CANCELLED': 'مسحوبة',
  'rewards.month': 'شهر الرواتب',
  'rewards.grantedBy': 'منحها',
  'rewards.note': 'ملاحظة للموظف',
  'rewards.noteHint': 'اختيارية. يقرؤها الموظف في الإشعار وبجانب قسائم راتبه.',
  'rewards.none': 'لا توجد مكافآت بعد.',
  'rewards.withdraw': 'سحب',
  'rewards.paidWith': 'تم صرفها مع راتب {month}',
  'rewards.onPayroll': 'في كشف رواتب {month}',
  'rewards.employee': 'الموظف',
  'rewards.pickEmployee': 'اختر موظفًا',
  'rewards.sizeHint': 'يتم صرفها مع الراتب حسب أجره اليومي، وليست ساعات عمل ولا إجازة ولا نقاطًا.',
  'rewards.preview': '≈ {amount} حسب أجره اليومي الحالي. يحسبها كشف الرواتب بأجر اليوم في شهرها.',
  'rewards.previewHidden': 'يظهر مبلغها لمن يتولى الرواتب.',
  'rewards.confirm': 'أؤكد منح {name} هذه المكافأة: {size} من أجره.',
  'rewards.grantedToast': 'تم منح المكافأة وإبلاغ {name}.',
  'rewards.alreadyGranted': 'سبق منحها، ولم نضف شيئًا جديدًا.',
  'rewards.cannot.SELF': 'لا يمنح أحد نفسه مكافأة.',
  'rewards.cannot.INACTIVE': 'حساب هذا الموظف موقوف.',
  'rewards.cancelTitle': 'سحب مكافأة',
  'rewards.cancelReason': 'السبب',
  'rewards.cancelHint': 'لا يراه إلا مديرو النظام. نبلغ الموظف بسحب المكافأة، دون ذكر السبب.',
  'rewards.cancelledToast': 'تم سحب المكافأة.',
  'rewards.mineTitle': 'مكافآتي',
  'rewards.mineGranted': 'سيتم صرفها مع راتبك',
  'points.grantTitle': 'إضافة نقاط إلى محفظة',
  'points.points': 'النقاط',
  'points.pointsHint': 'رقم صحيح من 1 إلى 1,000,000.',
  'points.reason': 'السبب',
  'points.reasonHint': 'مطلوب، من 3 إلى 500 حرف. لا يقرؤه إلا مديرو النظام، أما الموظف فيرى عدد النقاط وتاريخها، ولا يرى السبب أبدًا.',
  'points.confirm': 'أؤكد إضافة {points} نقطة إلى محفظة {name}.',
  'points.addedToast': 'تمت إضافة {points} نقطة إلى محفظة {name}.',
  'points.alreadyAdded': 'سبقت إضافتها، ولم نضف شيئًا جديدًا.',
  'points.none': 'لم تتم إضافة نقاط يدويًا بعد.',
  'points.balanceAfter': 'الرصيد بعد الإضافة',
  'points.by': 'أضافها',
  'points.self': 'لا يضيف أحد نقاطًا إلى محفظته.',
  // AI daily points (CR-007).
  'nav.aiPoints': 'نقاط الأداء اليومية',
  'aiPoints.title': 'نقاط الأداء اليومية بالذكاء الاصطناعي',
  'aiPoints.subtitle': 'العمل المتوقع = 0 نقاط إضافية. لا يكسب النقاط الإضافية إلا الإسهام الموثق الذي يتجاوز المعتاد.',
  'aiPoints.tabReview': 'للمراجعة',
  'aiPoints.tabHistory': 'السجل اليومي',
  'aiPoints.tabSettings': 'الإعدادات',
  'aiPoints.runNow': 'شغّل تقييم أمس الآن',
  'aiPoints.runStarted': 'بدأ التشغيل ليوم {date}، وستظهر الاقتراحات تباعًا كلما أعدّها الذكاء الاصطناعي.',
  'aiPoints.recentRuns': 'آخر عمليات التشغيل',
  'aiPoints.pendingCount': 'للمراجعة: {n}',
  'aiPoints.noRuns': 'لا توجد عمليات تشغيل بعد',
  'aiPoints.noRunsHint': 'يقيّم أول تشغيل يوم أمس فور بدء الميزة، ثم يعمل تشغيل كل صباح الساعة 06:00.',
  'aiPoints.noRunForDate': 'لا يوجد تشغيل ليوم {date}',
  'aiPoints.noRunForDateHint': 'اختر تاريخًا آخر من الأعلى.',
  'aiPoints.noRows': 'لا شيء هنا',
  'aiPoints.noRowsHint': 'لا يوجد موظف يطابق هذه الفلاتر في هذا التاريخ.',
  'aiPoints.noHistory': 'لا سجلات في هذه الفترة',
  'aiPoints.noHistoryHint': 'وسّع الفترة أو أزل أحد الفلاتر.',
  'aiPoints.blockedTitle': 'متوقف: اضبط الربط الموجود مع OpenAI',
  'aiPoints.blockedBody': 'لا يوجد مفتاح OpenAI على الخادم، لذلك لا يمكن إعداد أي اقتراح، ولا نختلق أي اقتراح أبدًا. اضبط OPENAI_API_KEY في apps/backend/.env (وهو المفتاح الذي يستخدمه «المكالمات › ملخص الذكاء الاصطناعي»)، ثم شغّل ./deploy.sh api، ثم أعد محاولة التشغيل من هنا.',
  'aiPoints.disabledBody': 'نقاط الأداء اليومية متوقفة من إعداداتها، فلا يعمل أي تشغيل ولا نرسل شيئًا إلى OpenAI. والمحفظة تعمل كما كانت.',
  'aiPoints.nextRun': 'يعمل يوميًا الساعة {time} ({timezone})، والتشغيل التالي {at}.',
  'aiPoints.serverReady': 'OpenAI مضبوط على الخادم',
  'aiPoints.serverReadyBody': 'هو الاتصال نفسه الذي يستخدمه «المكالمات › ملخص الذكاء الاصطناعي». نعدّ به الاقتراحات، ولا نضيف أي نقاط قبل موافقة مدير النظام.',
  'aiPoints.scheduleLine': 'يعمل كل يوم الساعة {time} بتوقيت الشركة ({timezone})، ويقيّم يوم العمل السابق.',
  'aiPoints.jobsOff': 'العمليات الخلفية متوقفة على هذا الخادم (JOBS_ENABLED=false)، ويجب أن يشغّلها خادم آخر.',
  'aiPoints.runTimes': 'بدأ {started} · البيانات حتى {cutoff}',
  'aiPoints.runUsage': 'طلبات الذكاء الاصطناعي: {calls} · الرموز: {tokensIn} مدخلات، {tokensOut} مخرجات',
  'aiPoints.runCredited': 'النقاط المضافة: {n}',
  'aiPoints.retryRun': 'أعد محاولة المؤجل / الفاشل ({n})',
  'aiPoints.regenerateUnapproved': 'أعد توليد غير المعتمد',
  'aiPoints.stat.evaluated': 'تم التقييم',
  'aiPoints.stat.pending': 'للمراجعة',
  'aiPoints.stat.approved': 'معتمد',
  'aiPoints.stat.zeroApprovals': 'معتمد بصفر نقاط',
  'aiPoints.stat.rejected': 'مرفوض',
  'aiPoints.stat.deferred': 'مؤجل',
  'aiPoints.stat.failed': 'فشل',
  'aiPoints.stat.notEligible': 'غير مؤهل',
  'aiPoints.stat.inProgress': 'قيد التنفيذ',
  'aiPoints.searchEmployee': 'الاسم أو الرقم الوظيفي',
  'aiPoints.minScore': 'الدرجة',
  'aiPoints.anyScore': 'أي درجة',
  'aiPoints.scoreAtLeast': 'الدرجة {n} فأكثر',
  'aiPoints.col.hours': 'ساعات العمل / المعتادة',
  'aiPoints.col.free': 'الورديات الحرة',
  'aiPoints.col.work': 'المهام · التذاكر',
  'aiPoints.col.collab': 'المكالمات · المراجعات · التعليقات',
  'aiPoints.col.score': 'الدرجة',
  'aiPoints.col.date': 'يوم العمل',
  'aiPoints.col.decision': 'قرره',
  'aiPoints.notOwed': 'لا ساعات مطلوبة',
  'aiPoints.sessions': 'الورديات: {n}',
  'aiPoints.tasksShort': 'المهام: {n} ({v} موثقة)',
  'aiPoints.opsShort': 'التذاكر: {k} · مهام الطابور: {q}',
  'aiPoints.collabShort': '{c} · {r} · {m}',
  'aiPoints.credited': 'تمت إضافة {n}',
  'aiPoints.nothingCredited': 'لم تتم إضافة شيء',
  'aiPoints.approve': 'اعتماد',
  'aiPoints.reject': 'رفض',
  'aiPoints.retry': 'إعادة المحاولة',
  'aiPoints.regenerate': 'إعادة التوليد',
  'aiPoints.approveTitle': 'اعتماد هذه النقاط؟',
  'aiPoints.approveAll': 'اعتماد الكل ({n})',
  'aiPoints.approveAllTitle': 'اعتماد كل الاقتراحات ليوم {date}؟',
  'aiPoints.approveAllFigures': 'الاقتراحات: {n} · مجموع النقاط: {points} · موظفون لهم نقاط: {people} · بصفر نقاط: {zero}',
  'aiPoints.approveAllWithPoints': 'من لهم نقاط',
  'aiPoints.approveAllMore': '+{n} آخرون',
  'aiPoints.approveAllLow': 'ثقة منخفضة: {n}. اقرأ أسبابها أولًا، أو اعتمد الاقتراحات عالية الثقة فقط.',
  'aiPoints.approveAllLeft': 'تبقى كما هي: {list}.',
  'aiPoints.approveAllLeftSelf': 'ما يخصك: {n}',
  'aiPoints.approveAllLeftAdmin': 'مديرو النظام: {n}',
  'aiPoints.approveAllLeftInactive': 'حسابات موقوفة لها نقاط: {n}',
  'aiPoints.approveAllEach': 'يتم اعتماد كل اقتراح على حدة، تمامًا كالاعتماد اليدوي: يصل كل موظف له نقاط إشعار شكر واحد، ومن نقاطه صفر لا نسجّل شيئًا في محفظته ولا نرسل له إشعارًا. القرارات نهائية.',
  'aiPoints.approveAllNothing': 'لا شيء هنا بانتظار اعتمادك.',
  'aiPoints.approveAllConfirm': 'اعتماد الكل ({n})',
  'aiPoints.approveConfidentOnly': 'عالية الثقة فقط ({n})',
  'aiPoints.approveAllDone': 'المعتمدة: {n} · النقاط المضافة: {points}.',
  'aiPoints.approveAllPartial': 'المعتمدة: {n} · النقاط المضافة: {points} · بقيت كما هي: {failed}، لأنها تغيرت أو صدر فيها قرار في هذه الأثناء.',
  'aiPoints.reason.ADMINISTRATOR': 'مدير النظام — بلا نقاط',
  'aiPoints.approveCredit': 'اعتمد وأضف {n}',
  'aiPoints.approveZero': 'اعتمد بصفر نقاط',
  'aiPoints.approveBody': 'إضافة {n} نقطة إلى محفظة {name} عن عمل يوم {date}؟ القرار نهائي.',
  'aiPoints.approveZeroBody': 'اعتماد سجل {name} ليوم {date} بصفر نقاط، دون قيد في المحفظة ودون إشعار. القرار نهائي.',
  'aiPoints.approveNotice': 'يصله إشعار واحد فيه عدد النقاط واليوم وكلمة شكر، دون ذكر الأسباب أبدًا.',
  'aiPoints.lowConfidenceWarning': 'ثقة منخفضة: الذكاء الاصطناعي غير متأكد من هذا الاقتراح، أو بياناته غير مؤكدة. اقرأ أسبابه وأدلته قبل الاعتماد.',
  'aiPoints.approvedToast': '{name}: تمت إضافة {n} نقطة.',
  'aiPoints.approvedZeroToast': '{name}: تم الاعتماد بصفر نقاط، ولم نضف شيئًا.',
  'aiPoints.alreadyApproved': 'سبق اعتماده، ولم نضف شيئًا آخر.',
  'aiPoints.changed': 'تمت إعادة توليد الاقتراح بعد أن فتحته، فراجع الاقتراح الجديد أولًا.',
  'aiPoints.uncertainty.LOW': 'شك قليل',
  'aiPoints.uncertainty.MEDIUM': 'بعض الشك',
  'aiPoints.uncertainty.HIGH': 'شك كبير',
  'aiPoints.confidence.HIGH': 'مرتفعة',
  'aiPoints.confidence.MEDIUM': 'متوسطة',
  'aiPoints.confidence.LOW': 'منخفضة',
  'aiPoints.confidenceLine': 'ثقة الذكاء الاصطناعي {confidence} · {uncertainty}',
  'aiPoints.self.OTHER_REVIEWER': 'يخصك، والقرار لمراجع آخر',
  'aiPoints.self.NO_OTHER_REVIEWER': 'يخصك، ولا يوجد مراجع آخر يقرره',
  'aiPoints.selfLong.OTHER_REVIEWER': 'هذا يومك أنت، ولا أحد يعتمد نقاطه بنفسه. القرار لمدير نظام آخر لديه صلاحية مراجعة نقاط الأداء اليومية.',
  'aiPoints.selfLong.NO_OTHER_REVIEWER': 'هذا يومك أنت، ولا أحد غيرك لديه صلاحية مراجعة نقاط الأداء اليومية، لذلك يبقى معلقًا. امنح مدير نظام آخر صلاحية المراجعة ليقرره.',
  'aiPoints.runKindLabel': 'التشغيل',
  'aiPoints.runKind.SCHEDULED': 'تشغيل 06:00',
  'aiPoints.runKind.INITIAL_YESTERDAY_TRIAL': 'التجربة الأولى لأمس',
  'aiPoints.runKind.MANUAL': 'تشغيل يدوي',
  'aiPoints.runStatus.EVALUATING': 'جاري التقييم',
  'aiPoints.runStatus.READY': 'جاهز',
  'aiPoints.runStatus.BLOCKED_CONFIGURATION': 'متوقف',
  'aiPoints.status.QUEUED': 'في الانتظار',
  'aiPoints.status.EVALUATING': 'جاري التقييم',
  'aiPoints.status.PENDING_ADMIN_APPROVAL': 'للمراجعة',
  'aiPoints.status.APPROVED': 'معتمد',
  'aiPoints.status.REJECTED': 'مرفوض',
  'aiPoints.status.DEFERRED': 'مؤجل',
  'aiPoints.status.FAILED_AI': 'فشل التقييم',
  'aiPoints.status.BLOCKED_CONFIGURATION': 'متوقف',
  'aiPoints.status.NOT_ELIGIBLE': 'غير مؤهل',
  'aiPoints.reason.REGULAR_SHIFT_OPEN': 'بانتظار: الوردية الأساسية ما زالت مفتوحة',
  'aiPoints.reason.REGULAR_END_UNCONFIRMED': 'بانتظار: لم يتم تأكيد نهاية الوردية',
  'aiPoints.reason.DAY_NOT_JUDGED': 'بانتظار: لم يتم حساب ساعات اليوم بعد',
  'aiPoints.reason.FREE_SHIFT_OPEN': 'بانتظار: وردية حرة ما زالت مفتوحة',
  'aiPoints.reason.FREE_END_UNCONFIRMED': 'بانتظار: لم يتم تأكيد نهاية وردية حرة',
  'aiPoints.reason.ON_LEAVE': 'في إجازة',
  'aiPoints.reason.HOLIDAY': 'عطلة رسمية',
  'aiPoints.reason.DAY_OFF': 'يوم راحة',
  'aiPoints.reason.NO_WORK_RECORDED': 'لا وردية ولا عمل مسجل',
  'aiPoints.reason.NOT_EMPLOYED_YET': 'لم يكن موظفًا بعد في ذلك اليوم',
  'aiPoints.reason.OPENAI_NOT_CONFIGURED': 'OpenAI غير مضبوط على الخادم',
  'aiPoints.reason.NO_MODEL': 'لا يقدّم الخادم أي نموذج ذكاء اصطناعي',
  'aiPoints.reason.OPENAI_KEY_REFUSED': 'رفض OpenAI مفتاح الخادم',
  'aiPoints.reason.MODEL_UNAVAILABLE': 'نموذج الذكاء الاصطناعي غير متاح لهذا المفتاح',
  'aiPoints.reason.INVALID_OUTPUT': 'استمر الذكاء الاصطناعي في الإجابة خارج القواعد',
  'aiPoints.reason.PROVIDER_RATE_LIMITED': 'كان OpenAI مشغولًا، فأعدنا المحاولة لاحقًا',
  'aiPoints.reason.PROVIDER_UNAVAILABLE': 'لم يردّ OpenAI',
  'aiPoints.reason.PROVIDER_TIMEOUT': 'تأخر OpenAI في الإجابة',
  'aiPoints.reason.PROVIDER_QUOTA': 'نفدت ميزانية OpenAI',
  'aiPoints.reason.PROVIDER_REJECTED': 'رفض OpenAI الطلب',
  'aiPoints.reason.JOB_TIMEOUT': 'استغرق التقييم وقتًا طويلًا',
  'aiPoints.reason.INTERNAL': 'خطأ في الخادم',
  'aiPoints.reason.NO_SNAPSHOT': 'لم يتم حفظ بيانات اليوم',
  'aiPoints.adminOnly': 'لمديري النظام فقط. لا يرى الموظف إلا النقاط المضافة ورسالة شكر، ولا يرى هذه الأسباب أبدًا.',
  'aiPoints.proposal': 'الاقتراح',
  'aiPoints.dimension': 'المحور',
  'aiPoints.points': 'النقاط',
  'aiPoints.reasonCol': 'السبب',
  'aiPoints.capOfMax': 'الحد {cap} من {max}',
  'aiPoints.noProposal': 'لا يوجد اقتراح بعد.',
  'aiPoints.summary': 'الملخص',
  'aiPoints.excluded': 'ما تم استبعاده ولماذا',
  'aiPoints.dim.REGULAR_EXTRA_HOURS': 'ساعات فوق المعتاد',
  'aiPoints.dim.FREE_SHIFT': 'إسهام الورديات الحرة',
  'aiPoints.dim.TASKS_ABOVE_BASELINE': 'مهام فوق المعدل المعتاد',
  'aiPoints.dim.EXTRA_OPERATIONAL': 'عمل تشغيلي إضافي',
  'aiPoints.dim.CALLS_COLLABORATION': 'المكالمات والتعاون',
  'aiPoints.capWhy.NO_SHIFT': 'لا وردية أساسية في ذلك اليوم',
  'aiPoints.capWhy.NO_SURPLUS': 'لا دقائق زائدة عن الساعات المعتادة',
  'aiPoints.capWhy.SURPLUS_BELOW_STEP': 'الدقائق الإضافية لا تكفي لنقطة واحدة',
  'aiPoints.capWhy.SURPLUS_HOURS': 'دقائق موثقة زائدة عن الساعات المعتادة',
  'aiPoints.capWhy.LONG_SHIFT_LOW_OUTPUT': 'يوم طويل بنتائج قليلة — نقطة واحدة على الأكثر',
  'aiPoints.capWhy.NO_FREE_SHIFT': 'لا وردية حرة في ذلك اليوم',
  'aiPoints.capWhy.FREE_BELOW_STEP': 'دقائق الوردية الحرة لا تكفي لنقطة واحدة',
  'aiPoints.capWhy.FREE_MINUTES': 'دقائق موثقة في الورديات الحرة',
  'aiPoints.capWhy.AT_OR_BELOW_BASELINE': 'في حدود معدله المعتاد أو أقل',
  'aiPoints.capWhy.NOT_VERIFIED': 'فوق المعتاد، لكن لا شيء موثق بعد',
  'aiPoints.capWhy.ABOVE_BASELINE': 'عمل موثق أكثر من المعتاد',
  'aiPoints.capWhy.NO_TRUSTED_BASELINE': 'سجل قصير لا يكفي لحساب معدل — البيانات الموثقة فقط',
  'aiPoints.capWhy.MICRO_TASKS': 'مهام صغيرة كثيرة أنشأها بنفسه — نقطة واحدة على الأكثر',
  'aiPoints.capWhy.NO_OPERATIONAL_WORK': 'لا تذاكر ولا مهام طابور ولا مساعدة لفريق آخر',
  'aiPoints.capWhy.NO_COLLABORATION': 'لا مكالمات ذات معنى ولا مراجعات ولا تعليقات',
  'aiPoints.capWhy.COLLABORATION': 'مكالمات أو مراجعات أو تعليقات على عمل الآخرين',
  'aiPoints.evidence': 'الأدلة',
  'aiPoints.evidenceHint': 'ما سجّله النظام حتى {at}. يمكنك فتح كل سجل في صفحته ما دام موجودًا.',
  'aiPoints.noEvidence': 'لم تتم قراءة أي أدلة.',
  'aiPoints.sourceGone': 'السجل غير متاح الآن، ونحتفظ به هنا كما كان',
  'aiPoints.openSource': 'افتح السجل',
  'aiPoints.kind.ATTENDANCE': 'الوردية الأساسية',
  'aiPoints.kind.FREE_SHIFT': 'وردية حرة',
  'aiPoints.kind.TASK': 'مهمة',
  'aiPoints.kind.TICKET': 'تذكرة',
  'aiPoints.kind.Q_TASK': 'مهمة طابور',
  'aiPoints.kind.CALL': 'مكالمة',
  'aiPoints.kind.REVIEW': 'مراجعة',
  'aiPoints.kind.COMMENTS': 'تعليقات',
  'aiPoints.kind.BASELINE': 'المعدل المعتاد',
  'aiPoints.ev.attendance': 'عمل {worked} من {standard} — {surplus} إضافية',
  'aiPoints.ev.attendanceOff': 'استراحات {breaks}، إيقاف مؤقت {paused}، لا يعمل {idle}',
  'aiPoints.ev.free': 'عمل {worked}، لا يعمل {idle}',
  'aiPoints.ev.onTime': 'في الموعد',
  'aiPoints.ev.late': 'متأخرة',
  'aiPoints.ev.sentBack': 'تمت إعادتها {n} مرة',
  'aiPoints.ev.crossTeam': 'لفريق آخر',
  'aiPoints.ev.selfCreated': 'أنشأها بنفسه',
  'aiPoints.ev.open': 'مفتوحة {time}',
  'aiPoints.ev.call': '{time} · المشاركون: {n}',
  'aiPoints.ev.callSummary': 'ملخص الذكاء الاصطناعي — القرارات: {d}، مهام المتابعة: {a}',
  'aiPoints.ev.shortCall': 'أقصر من المطلوب',
  'aiPoints.ev.reviewApproved': 'راجع مهمة زميل واعتمدها',
  'aiPoints.ev.reviewSentBack': 'راجع مهمة زميل وأعادها',
  'aiPoints.ev.comments': 'تعليقات على عمل الآخرين: {t} مهام، {k} تذاكر، {q} مهام طابور',
  'aiPoints.ev.baseline': 'المعدل المعتاد في أيام العمل ({days}): المهام {tasks} والتذاكر / مهام الطابور {ops} يوميًا',
  'aiPoints.ev.baselineThin': 'أيام العمل السابقة: {days}، ولا تكفي لحساب معدل',
  'aiPoints.ev.team': 'الزملاء ({n}): {tasks} في اليوم',
  'aiPoints.verification.APPROVED': 'اعتمدها المراجع',
  'aiPoints.verification.WORKFLOW_STEP': 'تم تسليم خطوة سير العمل',
  'aiPoints.verification.AWAITING_REVIEW': 'بانتظار المراجعة',
  'aiPoints.verification.SELF_MARKED_DONE': 'أنهاها بنفسه',
  'aiPoints.priority.LOW': 'منخفضة',
  'aiPoints.priority.MEDIUM': 'متوسطة',
  'aiPoints.priority.HIGH': 'مرتفعة',
  'aiPoints.priority.URGENT': 'عاجلة',
  'aiPoints.attempts': 'المحاولات والنسخ',
  'aiPoints.attemptsHint': 'كل تقييم لهذا اليوم: إجابات الذكاء الاصطناعي، والمرفوض منها وسبب رفضه، ومن طلب إعادة المحاولة.',
  'aiPoints.noAttempts': 'لا محاولات بعد.',
  'aiPoints.when': 'متى',
  'aiPoints.outcome': 'النتيجة',
  'aiPoints.detailsCol': 'التفاصيل',
  'aiPoints.revisionAttempt': 'النسخة {r}، المحاولة {a}',
  'aiPoints.askedBy': 'طلبها {name}',
  'aiPoints.tokens': 'الرموز: {i} مدخلات، {o} مخرجات',
  'aiPoints.outcome.PROPOSED': 'تم الاقتراح',
  'aiPoints.outcome.INVALID_OUTPUT': 'إجابة مرفوضة',
  'aiPoints.outcome.PROVIDER_ERROR': 'خطأ من OpenAI',
  'aiPoints.outcome.BLOCKED_CONFIGURATION': 'متوقف',
  'aiPoints.outcome.DEFERRED': 'مؤجل',
  'aiPoints.outcome.NOT_ELIGIBLE': 'غير مؤهل',
  'aiPoints.outcome.REQUEUED': 'تم طلب إعادة المحاولة',
  'aiPoints.trigger.SCHEDULED': 'تشغيل 06:00',
  'aiPoints.trigger.TRIAL': 'التجربة الأولى',
  'aiPoints.trigger.MANUAL': 'تشغيل يدوي',
  'aiPoints.trigger.RECHECK': 'إعادة فحص تلقائية',
  'aiPoints.trigger.RETRY': 'إعادة محاولة',
  'aiPoints.decision': 'القرار',
  'aiPoints.proposed': 'النقاط المقترحة',
  'aiPoints.creditedLabel': 'النقاط المضافة',
  'aiPoints.reviewer': 'قرره',
  'aiPoints.decidedAt': 'وقت القرار',
  'aiPoints.walletTx': 'قيد المحفظة',
  'aiPoints.rejectNote': 'ملاحظة الرفض',
  'aiPoints.figures': 'الأرقام',
  'aiPoints.fig.day': 'اليوم',
  'aiPoints.fig.standard': 'الساعات المعتادة',
  'aiPoints.fig.worked': 'ساعات العمل الصافية',
  'aiPoints.fig.surplus': 'زيادة عن المعتاد',
  'aiPoints.fig.free': 'الورديات الحرة',
  'aiPoints.fig.tasks': 'المهام المنجزة',
  'aiPoints.fig.tasksValue': '{f} — {v} موثقة، {r} بانتظار المراجعة',
  'aiPoints.fig.ops': 'التذاكر ومهام الطابور',
  'aiPoints.fig.calls': 'مكالمات ذات معنى',
  'aiPoints.fig.callsValue': '{n} ({time})',
  'aiPoints.fig.collab': 'التعاون',
  'aiPoints.fig.collabValue': 'المراجعات: {r} · التعليقات على عمل الآخرين: {c}',
  'aiPoints.fig.baseline': 'المعدل المعتاد',
  'aiPoints.dayKind.WORKDAY': 'يوم عمل',
  'aiPoints.dayKind.DAY_OFF': 'عمل في يوم راحة',
  'aiPoints.dayKind.HOLIDAY': 'عمل في عطلة',
  'aiPoints.dayKind.LEAVE': 'عمل أثناء إجازة',
  'aiPoints.dayKind.NO_SHIFT': 'لا وردية مسجلة',
  'aiPoints.flag.INSUFFICIENT_BASELINE': 'معدل غير كاف',
  'aiPoints.flag.NO_SHIFT_RECORDED': 'لا وردية مسجلة',
  'aiPoints.flag.AGENT_UNTRACKED': 'لم يتابع التطبيق المكتبي الوردية',
  'aiPoints.flag.AUTO_CLOSED_SHIFT': 'تم إغلاق الوردية تلقائيًا',
  'aiPoints.flag.WORKED_ON_DAY_OFF': 'عمل في يوم راحة',
  'aiPoints.flag.NEW_EMPLOYEE': 'موظف جديد',
  'aiPoints.flag.CALL_SUMMARIES_OFF': 'لم تتم قراءة ملخصات المكالمات',
  'aiPoints.flag.MICRO_TASKS': 'مهام صغيرة كثيرة',
  'aiPoints.flag.SELF_CREATED_TASKS': 'معظم المهام أنشأها بنفسه',
  'aiPoints.flag.LONG_SHIFT_LOW_OUTPUT': 'يوم طويل بنتائج قليلة',
  'aiPoints.flag.CALL_TIME_NO_OUTCOME': 'مكالمات طويلة بلا نتيجة',
  'aiPoints.flag.REPEATED_MOVES': 'تغييرات متكررة في الحالة',
  'aiPoints.flag.EVIDENCE_TRUNCATED': 'قائمة الأدلة مختصرة',
  'aiPoints.flagHint.INSUFFICIENT_BASELINE': 'أيام العمل قبل هذا اليوم قليلة، ولا تكفي لمعرفة إنتاجيته المعتادة.',
  'aiPoints.flagHint.NO_SHIFT_RECORDED': 'تم إنجاز عمل دون وردية، فالساعات غير معروفة.',
  'aiPoints.flagHint.AGENT_UNTRACKED': 'لم يتابع التطبيق المكتبي الوردية، فوقت «لا يعمل» غير معروف.',
  'aiPoints.flagHint.AUTO_CLOSED_SHIFT': 'لم يتم إنهاء الوردية، فأغلقها النظام.',
  'aiPoints.flagHint.WORKED_ON_DAY_OFF': 'لم تكن عليه ساعات مطلوبة في ذلك اليوم، فكل ما عمله إضافي.',
  'aiPoints.flagHint.NEW_EMPLOYEE': 'انضم قبل هذا اليوم بأقل من 30 يومًا.',
  'aiPoints.flagHint.CALL_SUMMARIES_OFF': 'الإعدادات تمنع قراءة ملخصات المكالمات بالذكاء الاصطناعي.',
  'aiPoints.flagHint.MICRO_TASKS': 'أنهى عدة مهام أنشأها بنفسه بعد دقائق من إنشائها.',
  'aiPoints.flagHint.SELF_CREATED_TASKS': 'معظم المهام المنجزة أنشأها الموظف بنفسه.',
  'aiPoints.flagHint.LONG_SHIFT_LOW_OUTPUT': 'ساعتان إضافيتان أو أكثر دون إنجاز أو مراجعة أو تعليق.',
  'aiPoints.flagHint.CALL_TIME_NO_OUTCOME': 'ساعة ونصف أو أكثر في المكالمات دون إنجاز شيء.',
  'aiPoints.flagHint.REPEATED_MOVES': 'تم نقل مهمة إلى «منجزة» ثلاث مرات أو أكثر في ذلك اليوم.',
  'aiPoints.flagHint.EVIDENCE_TRUNCATED': 'أرسلنا إلى الذكاء الاصطناعي أول 25 سجلًا فقط من كل نوع، لكن الأعداد كاملة.',
  'aiPoints.snapshot': 'تم إعداده وفق',
  'aiPoints.rubricVersion': 'نسخة الإعدادات',
  'aiPoints.defaultsVersion': 'الافتراضية (لم يتم حفظ شيء بعد)',
  'aiPoints.model': 'نموذج الذكاء الاصطناعي',
  'aiPoints.promptVersion': 'نسخة التعليمات',
  'aiPoints.dataCutoff': 'البيانات حتى',
  'aiPoints.generated': 'وقت الاقتراح',
  'aiPoints.revision': 'النسخة',
  'aiPoints.timezone': 'المنطقة الزمنية · بداية اليوم',
  'aiPoints.digest': 'بصمة المدخلات',
  'aiPoints.backToProposal': 'الاقتراح',
  'aiPoints.rejectTitle': 'رفض هذا الاقتراح',
  'aiPoints.rejectNoteLabel': 'ملاحظة',
  'aiPoints.rejectNoteHint': 'اختيارية. لا يراها إلا مديرو النظام، ولا نبلغ الموظف بالرفض أبدًا.',
  'aiPoints.rejectedToast': 'تم الرفض، ولم نضف شيئًا إلى {name}.',
  'aiPoints.alreadyRejected': 'سبق رفضه.',
  'aiPoints.notRejectable': 'لا يمكن رفض اقتراح إلا إذا كان بانتظار القرار.',
  'aiPoints.retryTitle': 'قيّم هذا اليوم من جديد',
  'aiPoints.regenerateTitle': 'أعد توليد هذا الاقتراح',
  'aiPoints.retryRunTitle': 'أعد محاولة هذا التشغيل',
  'aiPoints.retrySubtitle': 'سنقرأ بيانات اليوم من جديد ونرسلها إلى الذكاء الاصطناعي. ستظهر نسخة جديدة، وتبقى القديمة في سجله. ولن نضيف أي نقاط.',
  'aiPoints.retryReason': 'السبب',
  'aiPoints.retryReasonHint': 'مطلوب، من 3 إلى 500 حرف، ونحفظه مع إعادة المحاولة.',
  'aiPoints.retriedToast': 'تمت إعادته للتقييم من جديد.',
  'aiPoints.runRetriedToast': 'تمت إعادتها للتقييم من جديد: {n}.',
  'aiPoints.includePending': 'أعد أيضًا توليد الاقتراحات التي ما زالت بانتظار القرار',
  'aiPoints.includePendingHint': 'لن نغيّر ما صدر فيه قرار، ولا ما يخصك.',
  'aiPoints.settingsTitle': 'إعدادات نقاط الأداء اليومية',
  'aiPoints.settingsSubtitle': 'النسخة {v}. ما تحفظه يسري على عمليات التشغيل القادمة فقط.',
  'aiPoints.settingsSaved': 'تم حفظ النسخة {v}، وتسري على عمليات التشغيل القادمة.',
  'aiPoints.enabledLabel': 'إعداد اقتراحات يومية',
  'aiPoints.enabledHint': 'عند الإيقاف: لا يعمل أي تشغيل ولا نرسل شيئًا إلى OpenAI. ولا تتأثر المحفظة ولا ملخصات المكالمات بالذكاء الاصطناعي.',
  'aiPoints.rubricLabel': 'النقاط لكل محور',
  'aiPoints.rubricHint': 'أقصى ما يمنحه كل محور، بنقاط صحيحة مجموعها 10 بالضبط. قد يمنح الذكاء الاصطناعي أقل، لكنه لا يمنح أكثر أبدًا.',
  'aiPoints.rubricSum': 'المجموع: {n} من 10',
  'aiPoints.setting.regularMinutesPerPoint': 'الدقائق الزائدة عن الساعات المعتادة لكل نقطة',
  'aiPoints.setting.regularMinutesPerPointHint': 'دقائق موثقة في الوردية الأساسية تزيد على الساعات المعتادة للوردية نفسها.',
  'aiPoints.setting.freeMinutesPerPoint': 'دقائق الوردية الحرة لكل نقطة',
  'aiPoints.setting.freeMinutesPerPointHint': 'دقائق العمل الموثقة في الورديات الحرة ليوم العمل.',
  'aiPoints.setting.baselineWindowDays': 'المعدل: عدد الأيام السابقة',
  'aiPoints.setting.baselineWindowDaysHint': 'نحسب إنتاجيته المعتادة من أيام عمله خلال هذا العدد من الأيام السابقة.',
  'aiPoints.setting.minBaselineDays': 'المعدل: أيام العمل المطلوبة',
  'aiPoints.setting.minBaselineDaysHint': 'إذا كانت أقل، فلا يكسب نقاط المهام إلا ما هو موثق مباشرة.',
  'aiPoints.setting.minCallMinutes': 'أقصر مكالمة ذات معنى (بالدقائق)',
  'aiPoints.setting.minCallMinutesHint': 'لا نحسب المكالمات الأقصر من ذلك، حتى لو تم الرد عليها.',
  'aiPoints.setting.model': 'نموذج الذكاء الاصطناعي',
  'aiPoints.setting.modelHint': 'أحد النماذج التي يقدمها الخادم لملخصات الذكاء الاصطناعي.',
  'aiPoints.setting.modelDefault': 'النموذج الافتراضي للخادم ({model})',
  'aiPoints.setting.includeCallSummaries': 'قراءة ملخصات الذكاء الاصطناعي الموجودة للمكالمات',
  'aiPoints.setting.includeCallSummariesHint': 'عنوان الملخص وعدد القرارات ومهام المتابعة فيه فقط، دون نصه أبدًا. ولا نحوّل أي مكالمة إلى نص لهذا الغرض.',
  'aiPoints.resetDefaults': 'العودة إلى الافتراضي',
  'aiPoints.versions': 'النسخ',
  'aiPoints.versionsHint': 'نحتفظ بكل نسخة تحفظها، ويذكر كل تشغيل النسخة التي استخدمها.',
  'aiPoints.noVersions': 'نستخدم الإعدادات الافتراضية، ولم يتم حفظ شيء بعد.',
  'aiPoints.version': 'النسخة',
  'aiPoints.savedBy': 'حفظها',
  'leaves.deductedDays': '−{n} عن أيام ناقصة',
  // المكالمات (2026-10-29).
  'nav.calls': 'المكالمات',
  'settings.calls': 'المكالمات',
  'calls.title': 'المكالمات',
  'calls.subtitle': 'مكالمات صوتية مع أي موظف، فردية أو جماعية. كل مكالمة مسجّلة.',
  'calls.newCall': 'مكالمة جديدة',
  'calls.newCallSubtitle': 'اختر شخصًا لمكالمة صوتية، أو عدة أشخاص لمكالمة جماعية.',
  'calls.myHistory': 'سجلّي',
  'calls.allCalls': 'كل المكالمات',
  'calls.clientCalls': 'مكالمات العملاء',
  'calls.employeeCalls': 'مكالمات الموظفين',
  'calls.client': 'العميل',
  'calls.anyClient': 'أي عميل',
  'calls.clientBadge': 'عميل',
  'calls.fromClient': 'مكالمة من عميل',
  'calls.noClientCalls': 'لا توجد مكالمات عملاء تطابق هذه الفلاتر',
  'calls.unavailableOff': 'المكالمات غير مفعّلة بعد، لكن يمكنك فتح السجل أدناه.',
  'calls.unavailableNotReady': 'لا يمكن إجراء المكالمات الآن، لأن خدمة التسجيل غير جاهزة، ولا نجري أي مكالمة دون تسجيل.',
  'calls.unavailableMissing': 'ما ينقص الخادم:',
  'calls.filter.all': 'الكل',
  'calls.filter.missed': 'الفائتة',
  'calls.filter.outgoing': 'الصادرة',
  'calls.filter.incoming': 'الواردة',
  'calls.noneYet': 'لا مكالمات بعد',
  'calls.noneMatch': 'لا مكالمات تطابق هذه الفلاتر',
  'calls.recordedHint': 'كل مكالمة مسجّلة، ويمكن لمن كان متصلًا بالمكالمة تشغيل تسجيلها من هنا.',
  'calls.employee': 'الموظف',
  'calls.everyone': 'الجميع',
  'calls.status': 'الحالة',
  'calls.anyStatus': 'أي حالة',
  'calls.type': 'النوع',
  'calls.anyType': 'أي نوع',
  'calls.from': 'من',
  'calls.to': 'إلى',
  'calls.with': 'مع',
  'calls.people': 'الأشخاص',
  'calls.outcome': 'النتيجة',
  'calls.when': 'الوقت',
  'calls.duration': 'المدة',
  'calls.recording': 'التسجيل',
  'calls.loadMore': 'عرض المزيد',
  'calls.outgoing': 'مكالمة صادرة',
  'calls.incoming': 'مكالمة واردة',
  'calls.voiceCall': 'مكالمة صوتية',
  'calls.groupVoiceCall': 'مكالمة صوتية جماعية',
  'calls.joinUnderway': 'يدعوك إلى مكالمة جارية',
  'calls.peopleCount': '{n} أشخاص',
  'calls.incomingFrom': 'مكالمة واردة من {name}',
  'calls.accept': 'رد',
  'calls.decline': 'رفض',
  'calls.moreRinging': '{n} مكالمة أخرى ترن',
  'calls.recordedNotice': 'هذه المكالمة مسجّلة',
  'calls.recordingNow': 'يجري تسجيل هذه المكالمة',
  'calls.recordingBadge': 'تسجيل',
  'calls.currentCall': 'مكالمتك',
  'calls.move': 'تحريك نافذة المكالمة',
  'calls.moveHint': 'اسحبها إلى أي مكان، أو استخدم مفاتيح الأسهم. النقر المزدوج يعيدها إلى الزاوية.',
  'calls.state.calling': 'جاري الاتصال…',
  'calls.state.preparing': 'جاري بدء التسجيل…',
  'calls.state.connecting': 'جاري الاتصال بالمكالمة…',
  'calls.state.reconnecting': 'جاري إعادة الاتصال…',
  'calls.state.ending': 'جاري الإنهاء…',
  'calls.state.otherDevice': 'تم الرد من تبويب أو جهاز آخر',
  'calls.endingSoon': 'تنتهي هذه المكالمة بعد 5 دقائق، فهذه أطول مدة مسموحة للمكالمة.',
  'calls.micDenied': 'المتصفح يمنع الميكروفون. اسمح به من شريط العنوان حتى يسمعك الآخرون.',
  'calls.enableAudio': 'انقر لسماع المكالمة',
  'calls.inCall': 'في المكالمة',
  'calls.joining': 'جاري الانضمام…',
  'calls.mute': 'كتم',
  'calls.unmute': 'إلغاء الكتم',
  'calls.addPeople': 'إضافة أشخاص',
  'calls.addPeopleSubtitle': 'سندعوهم إلى المكالمة نفسها، وبالتسجيل نفسه.',
  'calls.whoToAdd': 'من تريد إضافته',
  'calls.add': 'إضافة إلى المكالمة',
  'calls.added': 'تمت دعوتهم إلى المكالمة.',
  'calls.notInCall': 'لست في مكالمة.',
  'calls.screensShared': 'مشاركات الشاشة',
  'calls.screensSharedHint': 'من شارك شاشته خلال المكالمة، ومتى. لا نسجّل ما ظهر على الشاشة، فالتسجيل لصوت المكالمة فقط.',
  'calls.shareFrom': 'من',
  'calls.shareUntil': 'حتى',
  'calls.share.start': 'مشاركة شاشتك',
  'calls.share.stop': 'إيقاف مشاركة شاشتك',
  'calls.share.yours': 'أنت تشارك شاشتك',
  'calls.share.failed': 'لم نتمكن من مشاركة شاشتك. حاول مرة ثانية، أو اختر نافذة أو تبويبًا آخر.',
  'calls.screen.label': 'الشاشات المشتركة',
  'calls.screen.of': 'شاشة {name}',
  'calls.screen.show': 'عرض شاشة {name}',
  'calls.screen.smaller': 'تصغير',
  'calls.screen.larger': 'تكبير',
  'calls.screen.full': 'ملء الشاشة',
  'calls.screen.exitFull': 'الخروج من ملء الشاشة',
  'calls.screen.hide': 'إخفاء',
  'calls.screen.float': 'عرضها فوق النوافذ الأخرى',
  'calls.screen.move': 'تحريك الشاشة المشتركة',
  'calls.screen.moveHint': 'اسحبها إلى أي مكان من أعلاها أو من الصورة، أو استخدم مفاتيح الأسهم. النقر المزدوج يعيدها إلى مكانها.',
  'calls.screen.resize': 'تغيير حجم الشاشة المشتركة',
  'calls.screen.resizeHint': 'اسحب هذه الزاوية، أو استخدم مفاتيح الأسهم.',
  'calls.screen.notRecorded': 'نسجّل صوت المكالمة فقط، ولا نسجّل الشاشة المشتركة.',
  'calls.screen.waiting': 'بانتظار الصورة…',
  'calls.hangUp': 'إنهاء',
  'calls.cancel': 'إلغاء',
  'calls.openCall': 'فتح المكالمة',
  'calls.whoToCall': 'بمن تتصل',
  'calls.whoToCallHint': 'حتى {n} أشخاص.',
  'calls.full': 'تتسع المكالمة لـ {n} أشخاص على الأكثر',
  'calls.startCall': 'اتصال',
  'calls.startGroupCall': 'بدء مكالمة جماعية',
  'calls.notFound': 'هذه المكالمة غير موجودة.',
  'calls.participants': 'الأشخاص',
  'calls.participantsHint': 'من تمت دعوته، ومن ردّ، ومتى كان كل واحد منهم في المكالمة.',
  'calls.person': 'الشخص',
  'calls.invitation': 'الدعوة',
  'calls.joined': 'الانضمام',
  'calls.left': 'المغادرة',
  'calls.caller': 'المتصل',
  'calls.neverJoined': 'لم ينضم',
  'calls.dropped': 'انقطع الاتصال',
  'calls.details': 'التفاصيل',
  'calls.madeBy': 'المتصل',
  'calls.formerEmployee': 'موظف سابق',
  'calls.madeAt': 'وقت الاتصال',
  'calls.started': 'البدء',
  'calls.ended': 'الانتهاء',
  'calls.why': 'سبب الانتهاء',
  'calls.keptUntil': 'محفوظ حتى {date}.',
  'calls.keptForever': 'محفوظ حتى يغيّر مدير النظام مدة الاحتفاظ بالتسجيلات.',
  'calls.noRecordingHint': 'المكالمات التي لم يردّ عليها أحد، أو التي انتهت قبل بدء التسجيل، ليس لها تسجيل.',
  'calls.partialWarning': 'هذا التسجيل لا يحتوي على المكالمة كاملة: أنهينا المكالمة لأننا لم نتمكن من التأكد من تسجيلها.',
  'calls.playRecording': 'تشغيل التسجيل',
  'calls.cannotPlay': 'لم نتمكن من تشغيل التسجيل. حاول مرة ثانية.',
  'calls.accessLog': 'من طلب تشغيله',
  'calls.accessLogHint': 'كل طلب لتشغيل هذا التسجيل، سواء سمحنا به أو رفضناه. لا يرى هذا إلا من يشرف على هذه المكالمات.',
  'calls.accessLogEmpty': 'لم يطلب أحد تشغيله بعد.',
  'calls.accessResult': 'النتيجة',
  'calls.accessFrom': 'من',
  'calls.accessAllowed': 'تم التشغيل',
  'calls.accessRefused': 'مرفوض',
  'calls.viaPhone': 'تطبيق الهاتف',
  'calls.viaWeb': 'الويب',
  'calls.outcome.LIVE': 'جارية',
  'calls.outcome.ANSWERED': 'تم الرد',
  'calls.outcome.NO_ANSWER': 'لا رد',
  'calls.outcome.MISSED': 'فائتة',
  'calls.outcome.DECLINED': 'مرفوضة',
  'calls.outcome.BUSY': 'مشغول',
  'calls.outcome.CANCELLED': 'ملغاة',
  'calls.outcome.FAILED': 'فشلت',
  'calls.recording.NONE': 'لا تسجيل',
  'calls.recording.RECORDING': 'قيد التسجيل',
  'calls.recording.PROCESSING': 'قيد المعالجة',
  'calls.recording.READY': 'جاهز',
  'calls.recording.PARTIAL': 'غير مكتمل',
  'calls.recording.FAILED': 'فشل',
  'calls.recording.EXPIRED': 'منتهي المدة',
  'calls.participant.RINGING': 'يرن',
  'calls.participant.ACCEPTED': 'ردّ',
  'calls.participant.DECLINED': 'رفض',
  'calls.participant.MISSED': 'فائتة',
  'calls.participant.BUSY': 'مشغول',
  'calls.participant.CANCELLED': 'تم إلغاء الدعوة',
  'calls.participant.LEFT': 'غادر',
  'calls.callStatus.RINGING': 'ترن',
  'calls.callStatus.PREPARING': 'جاري البدء',
  'calls.callStatus.ACTIVE': 'جارية',
  'calls.callStatus.ENDING': 'جاري الإنهاء',
  'calls.callStatus.COMPLETED': 'مكتملة',
  'calls.callStatus.MISSED': 'فائتة',
  'calls.callStatus.REJECTED': 'مرفوضة',
  'calls.callStatus.CANCELLED': 'ملغاة',
  'calls.callStatus.BUSY': 'مشغول',
  'calls.callStatus.FAILED': 'فشلت',
  'calls.ended.normal': 'انتهت المكالمة.',
  'calls.ended.recordingNotStarted': 'لم نتمكن من بدء المكالمة لأن تسجيلها لم يبدأ، ولا تتم أي مكالمة بدون تسجيل.',
  'calls.ended.recordingLost': 'انتهت المكالمة لأننا لم نتمكن من التأكد من تسجيلها.',
  'calls.ended.maxDuration': 'وصلت المكالمة إلى أقصى مدة مسموح بها.',
  'calls.ended.lostServer': 'انقطع الاتصال بفيجانيوم، فتم إنهاء المكالمة لأننا لم نتمكن من التأكد من تسجيلها.',
  'calls.ended.lostServerRinging': 'انقطع الاتصال بفيجانيوم أثناء رنين المكالمة، فتم إلغاؤها.',
  'calls.refusal.NOT_JOINED': 'تشغيل تسجيل هذه المكالمة متاح فقط لمن كانوا متصلين بها.',
  'calls.refusal.INCOMPLETE': 'هذا التسجيل لا يحتوي على المكالمة كاملة، لذلك هو متاح لمديري النظام فقط.',
  'calls.refusal.NOT_READY': 'التسجيل ما زال قيد المعالجة.',
  'calls.refusal.EXPIRED': 'تم حذف التسجيل عند انتهاء مدة الاحتفاظ به، وتبقى المكالمة في السجل.',
  'calls.refusal.EXPIRED_ON': 'تم حذف التسجيل في {date} عند انتهاء مدة الاحتفاظ به، وتبقى المكالمة في السجل.',
  'calls.refusal.FAILED': 'لا يوجد تسجيل صالح لهذه المكالمة.',
  'calls.refusal.NO_RECORDING': 'لا يوجد تسجيل لهذه المكالمة.',
  'calls.refusalShort.NOT_A_PARTICIPANT': 'ليس في المكالمة',
  'calls.refusalShort.NOT_JOINED': 'لم ينضم',
  'calls.refusalShort.NO_RECORDING': 'لا تسجيل',
  'calls.refusalShort.NOT_READY': 'غير جاهز',
  'calls.refusalShort.INCOMPLETE': 'غير مكتمل',
  'calls.refusalShort.EXPIRED': 'منتهي المدة',
  'calls.refusalShort.FAILED': 'فشل',
  'calls.error.unavailable': 'لا يمكن إجراء المكالمات الآن لأن خدمة التسجيل غير جاهزة.',
  'calls.error.alreadyInCall': 'أنت في مكالمة أخرى، أنهها أولًا.',
  'calls.error.full': 'أقصى عدد للأشخاص في المكالمة هو {n}.',
  'calls.error.otherDevice': 'تم الرد على هذه المكالمة من تبويب أو جهاز آخر لك.',
  'calls.error.answeredElsewhere': 'رددت على هذه المكالمة من تبويب أو جهاز آخر.',
  'calls.error.declined': 'رفضت هذه المكالمة.',
  'calls.error.noLongerRinging': 'لم تعد هذه المكالمة ترن.',
  'calls.error.micBlocked': 'متصفحك يمنع هذا الموقع من استخدام الميكروفون. اضغط على أيقونة الميكروفون في شريط العنوان، واسمح باستخدامه، ثم حاول مرة ثانية.',
  'calls.error.micMissing': 'لم نتمكن من فتح أي ميكروفون. تأكّد أنه موصول ولا يستخدمه تطبيق آخر، ثم حاول مرة ثانية.',
  'calls.retention.title': 'مدة الاحتفاظ بتسجيلات المكالمات',
  'calls.retention.subtitle': 'المدة التي نحتفظ فيها بكل تسجيل بعد انتهاء مكالمته.',
  'calls.retention.forever': 'الاحتفاظ دائمًا',
  'calls.retention.days': '{n} يومًا',
  'calls.retention.custom': 'مدة مخصصة',
  'calls.retention.customDays': 'الأيام',
  'calls.retention.customHint': 'عدد صحيح من الأيام، من 1 إلى {max}. يمكن أن تزيد المدة على سنة.',
  'calls.retention.invalid': 'أدخل عددًا صحيحًا من الأيام، 1 أو أكثر.',
  'calls.retention.explain': 'عندما يصبح التسجيل أقدم من هذه المدة، يتم حذف صوته نهائيًا. أما المكالمة نفسها فلا يتم حذفها أبدًا، بل تبقى في سجل الجميع مع من كان فيها ووقتها ومدتها. واختيار مدة أقصر يحذف أيضًا التسجيلات الموجودة الأقدم منها.',
  'calls.retention.confirmTitle': 'حذف التسجيلات الأقدم؟',
  'calls.retention.confirmBody': 'الاحتفاظ بالتسجيلات {days} يومًا سيحذف الآن ونهائيًا صوت التسجيلات الأقدم من ذلك، وعددها {n}.',
  'calls.retention.confirmKeep': 'تبقى المكالمات في السجل، ويتم حذف صوتها فقط. لا يمكن التراجع عن ذلك.',
  'calls.retention.confirmButton': 'حذف {n} والحفظ',
  'calls.settings.callsTitle': 'المكالمات',
  'calls.settings.callsSubtitle': 'مدة رنين المكالمة، وعدد الأشخاص فيها، وأقصى مدة لها.',
  'calls.settings.ring': 'مدة الرنين (بالثواني)',
  'calls.settings.ringHint': 'من 15 إلى 120، وبعدها تصبح المكالمة فائتة.',
  'calls.settings.size': 'أقصى عدد للأشخاص في المكالمة',
  'calls.settings.sizeHint': 'من 2 إلى 16، بمن فيهم المتصل.',
  'calls.settings.length': 'أقصى مدة للمكالمة (بالدقائق)',
  'calls.settings.lengthHint': 'من 10 إلى 720. تنتهي المكالمة تلقائيًا عند هذه المدة، مع تنبيه قبلها بـ 5 دقائق. اجعلها أقل من الحد الذي تسمح به خدمة التسجيل.',
  // Calls › AI summary (2026-10-29)
  'callAi.title': 'ملخص المكالمة بالذكاء الاصطناعي',
  'callAi.disclaimer': 'كتبه الذكاء الاصطناعي من تسجيل المكالمة، وقد يحتوي على أخطاء. راجع أي شيء مهم بالاستماع إلى التسجيل.',
  'callAi.notJoined': 'قراءة ملخص هذه المكالمة متاحة فقط لمن كانوا متصلين بها.',
  'callAi.regenerate': 'نسخة جديدة',
  'callAi.retryAt': 'لم تكتمل المحاولة الأخيرة، وسنحاول مرة ثانية حوالي {date}.',
  'callAi.retrying': 'لم تكتمل المحاولة الأخيرة، وسنحاول مرة ثانية قريبًا.',
  'callAi.failed': 'لم نتمكن من إعداد ملخص الذكاء الاصطناعي لهذه المكالمة.',
  'callAi.noSpeech': 'لم نجد كلامًا ذا معنى في هذه المكالمة، فلا يوجد ما نلخصه.',
  'callAi.attempts': 'المحاولة {n} من {max}',
  'callAi.newVersionBeingMade': 'جاري إعداد نسخة جديدة: {step}.',
  'callAi.keyPoints': 'نقاط النقاش الرئيسية',
  'callAi.noKeyPoints': 'لا توجد نقاط رئيسية.',
  'callAi.decisions': 'القرارات المتخذة',
  'callAi.noDecisions': 'لا توجد قرارات.',
  'callAi.actionItems': 'المهام المطلوبة',
  'callAi.actionItemsHint': 'للعلم فقط، ولا تتحول أي منها إلى مهمة.',
  'callAi.noActionItems': 'لا توجد مهام مطلوبة.',
  'callAi.owner': 'المسؤول',
  'callAi.due': 'موعد التسليم',
  'callAi.notSpecified': 'غير محدد',
  'callAi.openQuestions': 'أسئلة معلّقة',
  'callAi.versionUpdated': 'النسخة {n}، تم تحديثها في {date}',
  'callAi.writtenAt': 'تمت كتابته في {date}',
  'callAi.progress': 'مراحل إعداد الملخص',
  'callAi.transcript': 'النص الكامل للمكالمة',
  'callAi.transcriptDisclaimer': 'كتب الذكاء الاصطناعي هذا النص من كلام المكالمة، باللغات المستخدمة فيها. قد يحتوي على أخطاء، ولا يوضح من المتحدث.',
  'callAi.transcriptExpired': 'انتهت مدة حفظ النص، وتم حذفه مع التسجيل في نهاية مدة الاحتفاظ. أما الملخص فيبقى.',
  'callAi.transcriptExpiredOn': 'انتهت مدة حفظ النص، وتم حذفه مع التسجيل في {date} عند نهاية مدة الاحتفاظ. أما الملخص فيبقى.',
  'callAi.transcriptSilent': '(لا يوجد كلام في هذا الجزء)',
  'callAi.revisions': 'النسخ السابقة',
  'callAi.version': 'النسخة {n}',
  'callAi.current': 'الحالية',
  'callAi.automatic': 'تلقائية',
  'callAi.reason': 'السبب',
  'callAi.regenerateTitle': 'إعداد نسخة جديدة من الملخص؟',
  'callAi.regenerateBody': 'سيتم إعداد نسخة جديدة من نص هذه المكالمة، وتبقى النسخة الحالية ضمن النسخ السابقة مع السبب الذي تكتبه.',
  'callAi.reasonHint': 'لماذا تحتاج إلى نسخة جديدة؟ من 3 إلى 500 حرف. نحفظ السبب معها، ولا يراه إلا مديرو النظام.',
  'callAi.retranscribe': 'إعادة نسخ التسجيل أولًا (أبطأ وأعلى تكلفة)',
  'callAi.retranscribeForced': 'سيتم نسخ التسجيل من جديد أولًا، لأنه لا يوجد نص محفوظ لهذه المكالمة',
  'callAi.regenerateQueued': 'جاري إعداد نسخة جديدة من الملخص.',
  'callAi.accessLog': 'من قرأ ملخص الذكاء الاصطناعي',
  'callAi.accessLogHint': 'كل قراءة للملخص أو للنص، وكل طلب لنسخة جديدة، سواء تم السماح به أو رفضه.',
  'callAi.accessWhat': 'الإجراء',
  'callAi.access.SUMMARY_VIEWED': 'تمت قراءة الملخص',
  'callAi.access.SUMMARY_DENIED': 'تم رفض قراءة الملخص',
  'callAi.access.TRANSCRIPT_VIEWED': 'تمت قراءة النص',
  'callAi.access.TRANSCRIPT_DENIED': 'تم رفض قراءة النص',
  'callAi.access.REGENERATE_REQUESTED': 'تم طلب نسخة جديدة',
  'callAi.access.REGENERATE_DENIED': 'تم رفض طلب نسخة جديدة',
  'callAi.accessReason.NOT_A_PARTICIPANT': 'ليس في المكالمة',
  'callAi.accessReason.NOT_JOINED': 'لم ينضم',
  'callAi.accessReason.NOT_ADMIN': 'ليس من مديري النظام',
  'callAi.accessReason.NOT_ELIGIBLE': 'مكالمة سابقة لتفعيل الملخصات',
  'callAi.accessReason.EXPIRED': 'انتهت مدته',
  'callAi.accessReason.NONE': 'لا يوجد نص',
  'callAi.accessReason.JOB_ACTIVE': 'قيد الإعداد بالفعل',
  'callAi.accessReason.LIMIT': 'الحد اليومي',
  'callAi.accessReason.UNAVAILABLE': 'غير متاح على الخادم',
  'callAi.accessReason.OTHER': 'غير ممكن',
  'callAi.summaryReady': 'ملخص الذكاء الاصطناعي جاهز',
  'callAi.state.NOT_AVAILABLE': 'غير متاح',
  'callAi.state.WAITING_FOR_RECORDING': 'بانتظار التسجيل',
  'callAi.state.QUEUED': 'في الانتظار',
  'callAi.state.TRANSCRIBING': 'جاري النسخ',
  'callAi.state.SUMMARIZING': 'جاري إعداد الملخص',
  'callAi.state.RETRY_PENDING': 'بانتظار إعادة المحاولة',
  'callAi.state.FAILED': 'فشل',
  'callAi.state.READY': 'جاهز',
  'callAi.state.NO_SPEECH': 'لا يوجد كلام ذو معنى',
  'callAi.na.NOT_ELIGIBLE': 'تمت هذه المكالمة قبل تفعيل ملخصات الذكاء الاصطناعي، لذلك لا يوجد لها ملخص.',
  'callAi.na.NO_WHOLE_RECORDING': 'لا يوجد تسجيل كامل لهذه المكالمة لتلخيصه.',
  'callAi.na.RECORDING_EXPIRED': 'تم حذف التسجيل في نهاية مدة الاحتفاظ به قبل إعداد الملخص.',
  'callAi.na.NOT_MADE': 'لم يتم إعداد ملخص لهذه المكالمة.',
  'callAi.error.AUDIO_INVALID': 'لم نتمكن من قراءة التسجيل كملف صوتي.',
  'callAi.error.AUDIO_MISSING': 'ملف التسجيل غير موجود في مساحة التخزين.',
  'callAi.error.AUDIO_TOOLS_MISSING': 'أداة ffmpeg غير مثبتة على الخادم (apt-get install -y ffmpeg).',
  'callAi.error.AUDIO_PROCESSING_FAILED': 'لم نتمكن من تجهيز التسجيل على الخادم (فشلت أداة ffmpeg، أو القرص ممتلئ).',
  'callAi.error.STORAGE_UNAVAILABLE': 'لم تستجب خدمة تخزين التسجيلات.',
  'callAi.error.PROVIDER_UNAVAILABLE': 'كانت OpenAI مشغولة أو لم تستجب في الوقت المحدد.',
  'callAi.error.PROVIDER_AUTH': 'رفضت OpenAI مفتاح API الخاص بالخادم (OPENAI_API_KEY).',
  'callAi.error.PROVIDER_QUOTA': 'نفد رصيد حساب OpenAI أو حصته.',
  'callAi.error.PROVIDER_REJECTED': 'رفضت OpenAI الطلب. راجع النماذج المختارة في «الإعدادات › المكالمات».',
  'callAi.error.SUMMARY_INVALID': 'وصل الملخص ناقصًا أو بغير الصيغة المتوقعة.',
  'callAi.error.RECORDING_EXPIRED': 'تم حذف التسجيل أثناء إعداد الملخص.',
  'callAi.error.INTERRUPTED': 'توقفت العملية قبل أن تكتمل (بسبب إعادة تشغيل، أو لأنها استغرقت وقتًا طويلًا).',
  'callAi.error.INTERNAL': 'حدث خطأ في الخادم.',
  'callAi.blocker.CALLS_AI_DISABLED': 'قيمة CALLS_AI_ENABLED ليست true في ملف .env الخاص بالـAPI.',
  'callAi.blocker.OPENAI_KEY_MISSING': 'قيمة OPENAI_API_KEY غير مضبوطة في ملف .env الخاص بالـAPI.',
  'callAi.blocker.STORAGE_NOT_SET_UP': 'لم يتم إعداد تخزين التسجيلات (R2).',
  'callAi.blocker.AUDIO_TOOLS_MISSING': 'أداة ffmpeg غير مثبتة على الخادم (apt-get install -y ffmpeg).',
  'callAi.blocker.MODELS_NOT_CONFIGURED': 'لم يتم إعداد نموذج للنسخ أو التلخيص.',
  'callAi.blocker.SETTING_OFF': 'ملخصات الذكاء الاصطناعي متوقفة هنا.',
  'callAi.settings.title': 'ملخص المكالمة بالذكاء الاصطناعي',
  'callAi.settings.subtitle': 'نص وملخص تلقائيان لكل مكالمة جديدة، بعد أن يصبح تسجيلها جاهزًا.',
  'callAi.settings.notReady': 'لا يمكن تشغيل ملخصات الذكاء الاصطناعي على هذا الخادم حتى الآن:',
  'callAi.settings.running': 'ملخصات الذكاء الاصطناعي مفعّلة، ويتم تلخيص كل مكالمة جديدة.',
  'callAi.settings.ready': 'الخادم جاهز لملخصات الذكاء الاصطناعي.',
  'callAi.settings.enabled': 'تلخيص المكالمات الجديدة تلقائيًا',
  'callAi.settings.enabledHint': 'يشمل فقط المكالمات التي تتم منذ لحظة التفعيل، ولا يشمل المكالمات السابقة أبدًا. عند الإيقاف يتوقف تلخيص المكالمات الجديدة، وتبقى الملخصات الموجودة.',
  'callAi.settings.firstEnabledAt': 'تم التفعيل أول مرة في {date}.',
  'callAi.settings.queue': 'قائمة الانتظار: {queued} تنتظر، {running} قيد التنفيذ، {retrying} لإعادة المحاولة، {failed} فشلت خلال اليوم الماضي',
  'callAi.settings.howTitle': 'اللغة والنماذج',
  'callAi.settings.howSubtitle': 'يبقى النص باللغات المستخدمة في المكالمة، ويكون الملخص باللغة التي تختارها هنا.',
  'callAi.settings.language': 'لغة الملخص',
  'callAi.settings.languageHint': 'تبقى المصطلحات التقنية الإنجليزية كما قيلت.',
  'callAi.settings.arabic': 'العربية',
  'callAi.settings.english': 'الإنجليزية',
  'callAi.settings.transcriptionModel': 'نموذج النسخ',
  'callAi.settings.provider': 'المزوّد: OpenAI.',
  'callAi.settings.summaryModel': 'نموذج التلخيص',
  'callAi.settings.modelsHint': 'النماذج المتاحة على الخادم (في ملف .env).',
  'callAi.settings.retry': 'إعادة محاولة العمليات الفاشلة',
  'callAi.settings.retryHint': 'بضع مرات على الأكثر، مع انتظار أطول في كل مرة.',
  'callAi.settings.concurrent': 'العمليات في وقت واحد',
  'callAi.settings.concurrentHint': 'من {min} إلى {max}.',
  'callAi.settings.keywords': 'كلمات متوقعة',
  'callAi.settings.keywordsHint': 'مصطلحات الشركة والمنتجات، كلمة في كل سطر (حتى {max})، لتساعد في النسخ.',
  'callAi.settings.keywordsInvalid': 'حتى {max} كلمة، كل كلمة في سطر لا يزيد على {length} حرفًا، وبدون العلامتين < و>.',
  'callAi.settings.pricesTitle': 'الأسعار لتقدير التكلفة',
  'callAi.settings.pricesSubtitle': 'انسخها من صفحة أسعار المزوّد مع تاريخ اليوم الذي قرأتها فيه. بدون الأسعار لن يعرض تقرير الاستخدام أي تقدير.',
  'callAi.settings.pricesAsOf': 'تاريخ قراءة الأسعار',
  'callAi.settings.priceTranscription': 'النسخ، دولار لكل دقيقة',
  'callAi.settings.priceInput': 'مدخلات التلخيص، دولار لكل مليون رمز',
  'callAi.settings.priceOutput': 'مخرجات التلخيص، دولار لكل مليون رمز',
  'callAi.settings.priceInvalid': 'أدخل سعرًا من 0 إلى 1,000، أو اتركه فارغًا.',
  'callAi.settings.confirmTitle': 'تفعيل ملخصات المكالمات بالذكاء الاصطناعي؟',
  'callAi.settings.confirmBody': 'من الآن فصاعدًا، سيتم إرسال تسجيل كل مكالمة جديدة إلى OpenAI لتحويله إلى نص وتلخيصه. أكّد أنه تم إبلاغ الموظفين بذلك في إشعارات التسجيل والخصوصية، وأنه تمت الموافقة على إرسال صوت المكالمات إلى مزوّد خارجي.',
  'callAi.settings.confirmNewOnly': 'لن يتم تلخيص المكالمات التي تمت قبل الآن أبدًا.',
  'callAi.settings.confirmButton': 'تأكيد وتفعيل',
  'callAi.usage.title': 'استخدام الذكاء الاصطناعي',
  'callAi.usage.subtitle': 'ما استخدمته عمليات الذكاء الاصطناعي. أرقام فقط، ولا شيء مما قيل في المكالمات.',
  'callAi.usage.period': 'الفترة',
  'callAi.usage.lastDays': 'الأيام الـ{n} الماضية',
  'callAi.usage.summaries': 'الملخصات المكتوبة',
  'callAi.usage.noSpeech': '{n} دون كلام ذي معنى',
  'callAi.usage.jobs': 'العمليات',
  'callAi.usage.jobsHint': 'فاشلة: {failed} · ملغاة: {cancelled} · مرات إعادة المحاولة: {retries}',
  'callAi.usage.minutes': 'الدقائق المنسوخة',
  'callAi.usage.tokens': 'رموز التلخيص: {input} مدخلات، {output} مخرجات',
  'callAi.usage.cost': 'التكلفة التقديرية',
  'callAi.usage.costAtTheTime': 'كل عملية بالأسعار المدخلة وقت انتهائها',
  'callAi.usage.costPartial': 'عمليات بلا سعر غير محسوبة: {n}',
  'callAi.usage.noPrices': 'أدخل الأسعار أعلاه للحصول على تقدير',
  'callAi.usage.estimateNote': 'هذا تقدير من الأسعار المدخلة، والمعتمد هو فاتورة المزوّد.',
  // Viganium Drive (2026-11-02).
  'nav.drive': 'درايف',
  'drive.title': 'فيجانيوم درايف',
  'drive.tab': 'درايف',
  'drive.myDrive': 'درايف الخاص بي',
  'drive.shared': 'تمت مشاركته معي',
  'drive.trash': 'سلة المحذوفات',
  'drive.newFolder': 'مجلد جديد',
  'drive.upload': 'رفع ملفات',
  'drive.usage': 'الملفات: {files} · {size}',
  'drive.searchPlaceholder': 'ابحث في درايف الخاص بك وفي ما تمت مشاركته معك',
  'drive.searchResults': 'نتائج «{q}»',
  'drive.clearSearch': 'مسح البحث',
  'drive.where': 'مكانه',
  'drive.col.name': 'الاسم',
  'drive.col.owner': 'المالك',
  'drive.col.where': 'المكان',
  'drive.col.about': 'يخص',
  'drive.col.modified': 'آخر تعديل',
  'drive.col.trashed': 'في سلة المحذوفات منذ',
  'drive.col.size': 'الحجم',
  'drive.items': 'العناصر: {n}',
  'drive.you': 'أنت',
  'drive.menu': 'الإجراءات',
  'drive.emptyMine': 'درايف الخاص بك فارغ',
  'drive.emptyMineHint': 'أنشئ مجلدًا أو ارفع ملفات. تبقى لك وحدك حتى تشاركها.',
  'drive.emptyShared': 'لم يشاركك أحد شيئًا بعد',
  'drive.emptySharedHint': 'عندما يشاركك أحد مجلدًا أو ملفًا، يظهر هنا.',
  'drive.emptyTrash': 'سلة المحذوفات فارغة',
  'drive.emptyTrashHint': 'كل ما ينتقل إلى سلة المحذوفات يبقى فيها حتى تستعيده أو تحذفه نهائيًا.',
  'drive.emptySearch': 'لا شيء يطابق البحث',
  'drive.emptyFolder': 'هذا المجلد فارغ',
  'drive.emptyFolderHint': 'اسحب الملفات إلى هنا، أو اضغط «رفع ملفات».',
  'drive.emptyFolderViewOnly': 'لا شيء فيه بعد.',
  'drive.dropTop': 'درايف الخاص بك',
  'drive.dropHere': 'أفلت الملفات لرفعها إلى {folder}',
  'drive.dropHint': 'اسحب الملفات وأفلتها في أي مكان من هذه الصفحة لرفعها هنا. يمكنك رفع أي نوع من الملفات، حتى {size} للملف الواحد.',
  'drive.uploading': 'جاري رفع {n}',
  'drive.uploadsDone': 'الرفع',
  'drive.uploaded': 'تم الرفع',
  'drive.uploadFailed': 'لم يتم الرفع',
  'drive.waiting': 'في الانتظار',
  'drive.dismiss': 'إخفاء',
  'drive.fileTooLarge': 'أكبر من {mb} ميجابايت، وهو أقصى ما يقبله درايف.',
  'drive.storageUnreachable': 'لا يمكن الوصول إلى مساحة تخزين الملفات. حاول مرة ثانية بعد لحظات.',
  'drive.open': 'فتح',
  'drive.download': 'تنزيل',
  'drive.downloadZip': 'تنزيل كملف ‎.zip',
  'drive.downloadFolderHint': 'تنزيل هذا المجلد بكل ما فيه في ملف ‎.zip',
  'drive.downloadRefused.EMPTY': 'لا توجد ملفات في هذا المجلد لتنزيلها.',
  'drive.downloadRefused.TOO_BIG': 'الحد الأقصى للتنزيل الواحد 4 جيجابايت من الملفات. نزّل المجلدات الموجودة داخل هذا المجلد واحدًا تلو الآخر.',
  'drive.downloadRefused.TOO_MANY': 'الحد الأقصى للتنزيل الواحد 65,535 من المجلدات والملفات. نزّل المجلدات الموجودة داخل هذا المجلد واحدًا تلو الآخر.',
  'drive.openInTab': 'فتح في علامة تبويب جديدة',
  'drive.share': 'مشاركة',
  'drive.edit': 'إعادة التسمية والتفاصيل',
  'drive.move': 'نقل',
  'drive.toTrash': 'نقل إلى سلة المحذوفات',
  'drive.restore': 'استعادة',
  'drive.purge': 'حذف نهائي',
  'drive.emptyTrashAction': 'إفراغ سلة المحذوفات',
  'drive.confirmTrashTitle': 'نقل «{name}» إلى سلة المحذوفات؟',
  'drive.confirmTrashFolder': 'سينتقل كل ما فيه معه. ولن يراه من تمت مشاركته معهم، وستتوقف روابطه حتى تتم استعادته.',
  'drive.confirmTrashFile': 'لن يراه من تمت مشاركته معهم، وستتوقف روابطه حتى تتم استعادته.',
  'drive.confirmTrashShared': 'سينتقل إلى سلة محذوفات مالكه {owner}، وهو وحده من يمكنه استعادته.',
  'drive.confirmRestoreTitle': 'استعادة «{name}»؟',
  'drive.confirmRestoreBody': 'يعود إلى مكانه بكل ما فيه، وتعمل مشاركاته وروابطه من جديد.',
  'drive.confirmPurgeTitle': 'حذف «{name}» نهائيًا؟',
  'drive.confirmPurgeBody': 'سيتم حذفه مع كل ما فيه، بما في ذلك الملفات. لا يمكن التراجع عن ذلك.',
  'drive.confirmEmptyTitle': 'إفراغ سلة المحذوفات؟',
  'drive.confirmEmptyBody': 'سيتم حذف كل محتويات سلة محذوفاتك نهائيًا، بما في ذلك الملفات. لا يمكن التراجع عن ذلك.',
  'drive.trashed': 'تم النقل إلى سلة المحذوفات.',
  'drive.restored': 'تمت الاستعادة.',
  'drive.restoredToTop': 'تمت استعادته إلى أعلى درايف الخاص بك، لأن المجلد الذي كان فيه موجود في سلة المحذوفات.',
  'drive.purged': 'تم الحذف نهائيًا.',
  'drive.emptied': 'تم إفراغ سلة المحذوفات.',
  'drive.inTrashNotice': 'هذا في سلة محذوفاتك. استعده لتستخدمه من جديد.',
  'drive.inTrashFolderNotice': 'المجلد الذي يحتوي على هذا العنصر موجود في سلة محذوفاتك. استعد ذلك المجلد لتستخدم هذا العنصر من جديد.',
  'drive.access': 'صلاحيتك',
  'drive.access.OWNER': 'لك',
  'drive.access.EDIT': 'يمكن التعديل',
  'drive.access.VIEW': 'يمكن العرض',
  'drive.myItem': 'في درايف الخاص بك',
  'drive.ownedBy': 'في درايف {name}',
  'drive.changed': 'تغيّر {when}',
  'drive.sharedPeople': 'تمت مشاركته مع {n}',
  'drive.sharedEveryone': 'الجميع',
  'drive.publicLinkOn': 'رابط عام',
  'drive.contactsOn': 'تم إرساله بالبريد إلى {n}',
  'drive.details': 'التفاصيل',
  'drive.size': 'الحجم',
  'drive.owner': 'المالك',
  'drive.addedBy': 'أضافه',
  'drive.created': 'تاريخ الإضافة',
  'drive.modified': 'آخر تعديل',
  'drive.about': 'يخص',
  'drive.aboutNothing': 'لا عميل ولا مشروع',
  'drive.pdfHint': 'افتح ملف PDF في علامة تبويب جديدة، أو نزّله.',
  'drive.downloadOnly': 'لا يمكن عرض هذا النوع من الملفات هنا، نزّله لتفتحه.',
  'drive.sharing': 'المشاركة',
  'drive.notShared': 'أنت فقط.',
  'drive.manageSharing': 'إدارة المشاركة',
  'drive.through': 'تمت مشاركته أيضًا عبر {folder}',
  'drive.relatedEmptyClient': 'لا يوجد بعد مجلد أو ملف في درايف يخص هذا العميل',
  'drive.relatedEmptyProject': 'لا يوجد بعد مجلد أو ملف في درايف يخص هذا المشروع',
  'drive.relatedHint': 'لا يظهر هنا إلا ما يمكنك فتحه. ويمكن تحديد ما يخصه المجلد أو الملف من صفحة «إعادة التسمية والتفاصيل» الخاصة به.',
  'drive.newFolderFor': 'مجلد جديد يخصه',
  'drive.folderIn': 'في {folder}',
  'drive.name': 'الاسم',
  'drive.client': 'العميل',
  'drive.project': 'المشروع',
  'drive.noClient': 'بلا عميل',
  'drive.noProject': 'بلا مشروع',
  'drive.aboutHint': 'اختياري. المشروع يحدد عميله تلقائيًا، وما في المجلد يخص ما يخصه المجلد.',
  'drive.createFolder': 'إنشاء المجلد',
  'drive.folderCreated': 'تم إنشاء المجلد.',
  'drive.renameFileHint': 'سيتم تنزيله بالاسم الجديد. وإذا كان الاسم بلا امتداد، يبقى امتداده السابق.',
  'drive.save': 'حفظ',
  'drive.saved': 'تم الحفظ.',
  'drive.viewOnly': 'تمت مشاركته معك للعرض فقط، وليس للتعديل.',
  'drive.moveTitle': 'نقل «{name}»',
  'drive.moveHere': 'النقل إلى هنا',
  'drive.moveTop': 'أعلى الدرايف',
  'drive.moveCurrent': 'موجود هنا الآن',
  'drive.moved': 'تم النقل.',
  'drive.noFolders': 'لا يوجد مجلد آخر يمكن نقله إليه.',
  'drive.moveRefused.INTO_ITSELF': 'لا يمكن نقل المجلد إلى داخله، ولا إلى مجلد بداخله.',
  'drive.moveRefused.NOT_A_FOLDER': 'يمكن النقل إلى المجلدات فقط، وليس إلى الملفات.',
  'drive.moveRefused.OTHER_DRIVE': 'يمكن نقله داخل درايف مالكه فقط.',
  'drive.moveRefused.TOO_DEEP': 'لا يمكن أن تتجاوز المجلدات 30 مستوى.',
  'drive.shareTitle': 'مشاركة «{name}»',
  'drive.shareSubtitle': 'من يرى ما تملكه غيرك. ومن تسمح له بتعديله يمكنه مشاركته أيضًا.',
  'drive.editorsShare': 'تمت مشاركته معك للعرض فقط. يمكن لمالكه مشاركته، وكذلك من لديه صلاحية التعديل.',
  'drive.people': 'الأشخاص',
  'drive.peopleHint': 'أي موظف، أيًّا كان قسمه. ومشاركة المجلد تعني مشاركة كل ما فيه.',
  'drive.addPerson': 'أضف أشخاصًا بالاسم',
  'drive.roleView': 'يمكن العرض',
  'drive.roleEdit': 'يمكن التعديل',
  'drive.roleFor': 'ما يمكن لـ{name} فعله',
  'drive.remove': 'إزالة',
  'drive.removePerson': 'إزالة {name}',
  'drive.inactive': 'لم يعد نشطًا',
  'drive.everyone': 'كل من في الشركة',
  'drive.everyoneOff': 'بدون مشاركة مع الجميع',
  'drive.everyoneView': 'يمكن للجميع العرض',
  'drive.everyoneEdit': 'يمكن للجميع التعديل',
  'drive.roleHint': '«يمكن العرض»: الفتح والتنزيل. «يمكن التعديل»: كل ذلك، مع الإضافة وإعادة التسمية والنقل والنقل إلى سلة المحذوفات والمشاركة.',
  'drive.saveSharing': 'حفظ المشاركة',
  'drive.sharingSaved': 'تم حفظ المشاركة.',
  'drive.publicLink': 'الرابط العام',
  'drive.publicLinkHint': 'يستطيع كل من لديه الرابط فتحه والتنزيل منه، دون تسجيل دخول.',
  'drive.makePublicLink': 'إنشاء رابط عام',
  'drive.publicLinkExpired': 'انتهت صلاحية آخر رابط له، والرابط الجديد سيكون بعنوان مختلف.',
  'drive.copyLink': 'نسخ الرابط',
  'drive.linkCopied': 'تم نسخ الرابط.',
  'drive.stopLink': 'إيقاف الرابط',
  'drive.confirmStopTitle': 'إيقاف هذا الرابط؟',
  'drive.confirmStopBody': 'لن يتمكن من لديه الرابط من فتحه بعد الآن، وأي رابط جديد سيكون بعنوان مختلف.',
  'drive.linkStopped': 'تم إيقاف الرابط.',
  'drive.linkCreated': 'تم إنشاء الرابط العام.',
  'drive.linkUpdated': 'تم الحفظ.',
  'drive.availableUntil': 'متاح حتى',
  'drive.noEnd': 'بلا تاريخ انتهاء.',
  'drive.untilHint': 'اختياري. يتوقف عن العمل بعد هذا اليوم.',
  'drive.setEnd': 'تعيين',
  'drive.endsOn': 'حتى {date}',
  'drive.linkStats': 'مرات الفتح: {opens} · مرات التنزيل: {downloads}',
  'drive.lastOpened': 'آخر فتح {when}',
  'drive.outsideNeedsPermission': 'الروابط العامة وإرسال البريد إلى جهات اتصال العملاء متاحة لمديري النظام، ولمن لديه «مشاركة درايف خارج الشركة» في نموذج الموظف.',
  'drive.contacts': 'جهات اتصال العملاء',
  'drive.contactsHint': 'أرسله بالبريد إلى جهات اتصال أحد العملاء. يحصل كل منهم على رابط خاص به، ويمكنك إيقافه في أي وقت.',
  'drive.sendToContacts': 'إرسال إلى جهات اتصال العميل',
  'drive.noContactLinks': 'لم يتم إرساله إلى أحد بعد.',
  'drive.linkLive': 'يعمل',
  'drive.linkExpired': 'منتهي الصلاحية',
  'drive.contactRemoved': 'تمت إزالة جهة الاتصال',
  'drive.sentOn': 'أول إرسال {when}',
  'drive.sendTitle': 'إرسال «{name}» إلى جهات اتصال العميل',
  'drive.pickClient': 'اختر عميلًا',
  'drive.sendContacts': 'جهات الاتصال',
  'drive.sendContactsHint': 'حتى {n} في المرة الواحدة. وجهة الاتصال التي سبق الإرسال إليها تحتفظ برابطها نفسه.',
  'drive.noContacts': 'لا توجد جهات اتصال لهذا العميل. أضفها من صفحة العميل.',
  'drive.noEmail': 'بلا بريد إلكتروني',
  'drive.message': 'الرسالة',
  'drive.messageHint': 'اختياري. تظهر في البريد فوق الرابط.',
  'drive.emailLanguage': 'لغة البريد',
  'drive.emailLanguageHint': 'بيانات جهات اتصال العملاء محفوظة بالإنجليزية، لذلك تكون الرسالة بالإنجليزية ما لم تختر لغة أخرى.',
  'drive.send': 'إرسال',
  'drive.sentQueued': 'تم الإرسال إلى {n}، ولكل منهم رابط خاص به.',
  'drive.sentOff': 'البريد الإلكتروني متوقف في «الإعدادات › البريد الإلكتروني». تم إنشاء الروابط، فانسخ كل رابط من هذه الصفحة وأرسله بنفسك.',
  'drive.sentNotConfigured': 'لم يتم إعداد البريد الإلكتروني على هذا الخادم. تم إنشاء الروابط، فانسخ كل رابط من هذه الصفحة وأرسله بنفسك.',
  'settings.maxDriveFileMb': 'أكبر حجم لملف في درايف (ميجابايت)',
  'settings.maxDriveFileMbHint': 'لأي نوع من الملفات في فيجانيوم درايف.',
  'employees.del.driveItems': 'مجلدات درايف وملفاته',
  'employees.addOnDriveHint': 'إنشاء رابط عام لعنصر في درايف، وإرساله بالبريد إلى جهات اتصال العملاء. من دونها لا يفعل ذلك إلا مديرو النظام.',
  'drive.everyoneTab': 'درايف الموظفين',
  'drive.everyoneHint': 'درايف كل موظف. بصفتك مدير النظام يمكنك فتح ما فيه ومشاركته، أما تعديله وسلة محذوفاته فيبقيان لمالكه.',
  'drive.everyoneEmpty': 'لم يضع أحد أي شيء في درايف بعد',
  'drive.col.employee': 'الموظف',
  'drive.col.files': 'الملفات',
  'drive.col.lastChange': 'آخر تغيير',
  'drive.driveOf': 'درايف {name}',
  'drive.access.ADMIN': 'بصفتك مدير النظام',
  'drive.shareSubtitleAdmin': 'في درايف {name}. بصفتك مدير النظام يمكنك رؤيته ومشاركته، ويرى مالكه مع من تمت مشاركته.',
  'drive.shareSubtitleEditor': 'في درايف {name}. تمت مشاركته معك للتعديل، لذلك يمكنك مشاركته أيضًا، ويرى مالكه مع من تمت مشاركته.',
  'drive.ownPlace': 'مالكه وحده يستطيع تغيير صلاحيتك.',
  'drive.ownerDriveHint': 'ترى هذا الدرايف بصفتك مدير النظام، فيمكنك فتح ما فيه ومشاركته. ولا يغيّره إلا مالكه ومن لديهم صلاحية التعديل.',
  'drive.ownerDriveEmpty': 'لا شيء في هذا الدرايف',
  'drive.departments': 'الأقسام',
  'drive.departmentsHint': 'يستطيع فتحه كل من في القسم الآن، ومن ينضم إليه لاحقًا أيضًا، أما من يغادر القسم فلن يستطيع فتحه بعد ذلك.',
  'drive.addDepartment': 'أضف قسمًا',
  'drive.departmentPeople': 'أعضاؤه الآن: {n}',
  'drive.yourDepartment': 'قسمك',
  'drive.sharedDepartments': 'الأقسام: {n}',
  'nav.sites': 'المواقع',
  'sites.title': 'المواقع',
  'sites.subtitle': 'مواقع إلكترونية، كل موقع منشور في مجلد خاص به على العنوان',
  'sites.whoPublishes': 'من يمكنه النشر',
  'sites.new': 'موقع جديد',
  'sites.empty': 'لا توجد مواقع بعد',
  'sites.emptyHint': 'أنشئ موقعًا، ثم ضع فيه صفحاته وتنسيقاته وسكربتاته وصوره.',
  'sites.site': 'الموقع',
  'sites.owner': 'المسؤول عنه',
  'sites.files': 'الملفات',
  'sites.status': 'الحالة',
  'sites.updated': 'آخر تحديث',
  'sites.fileCount': 'الملفات: {n}',
  'sites.live': 'منشور',
  'sites.offline': 'متوقف',
  'sites.noFrontPage': 'بلا index.html',
  'sites.copied': 'تم نسخ العنوان.',
  'sites.where': 'مكانك في الموقع',
  'sites.copyAddress': 'نسخ العنوان',
  'sites.lookedAfterBy': 'المسؤول عنه: {name}',
  'sites.changed': 'تغيّر {when}',
  'sites.open': 'فتح الموقع',
  'sites.menu': 'المزيد',
  'sites.settings': 'الإعدادات',
  'sites.delete': 'حذف الموقع',
  'sites.offlineNotice': 'هذا الموقع متوقف، لذلك يعرض عنوانه «الصفحة غير موجودة» حتى يتم تشغيله من جديد من إعداداته.',
  'sites.noIndexNotice': 'أضف ملف index.html في المجلد الرئيسي للموقع، فهو الصفحة التي يفتحها العنوان. وحتى تضيفه، يعرض العنوان «الصفحة غير موجودة».',
  'sites.summary': 'الملفات: {n} · {size} — الحد الأقصى للموقع {max} ملف.',
  'sites.newMenu': 'جديد',
  'sites.newFile': 'ملف جديد',
  'sites.newFolder': 'مجلد جديد',
  'sites.upload': 'رفع',
  'sites.uploadFiles': 'ملفات…',
  'sites.uploadFolder': 'مجلد كامل…',
  'sites.uploadZip': 'ملف ‎.zip…',
  'sites.emptySite': 'هذا الموقع فارغ',
  'sites.emptyFolder': 'هذا المجلد فارغ',
  'sites.emptyHintDrop': 'اسحب إلى هنا ملفات أو مجلدًا أو ملف \u200E.zip، أو اضغط «رفع». يظهر المجلد عندما يكون فيه ملف واحد على الأقل.',
  'sites.name': 'الاسم',
  'sites.kind': 'النوع',
  'sites.size': 'الحجم',
  'sites.dropHint':
    'اسحب ملفات أو مجلدًا أو ملف \u200E.zip إلى أي مكان في هذه الصفحة، وسيتم رفعها إلى المجلد الذي أنت فيه، مع استبدال أي ملف موجود بالاسم نفسه. اربط بين الصفحات بمسارات نسبية (css/site.css وليس \u200E/css/site.css).',
  'sites.openFolder': 'فتح',
  'sites.deleteFolder': 'حذف المجلد',
  'sites.by': 'بواسطة {name}',
  'sites.openFile': 'فتح في الموقع',
  'sites.editFile': 'تعديل',
  'sites.moveFile': 'إعادة تسمية أو نقل',
  'sites.deleteFile': 'حذف',
  'sites.download': 'تنزيل',
  'sites.downloadSiteHint': 'تنزيل الموقع كاملًا في ملف ‎.zip',
  'sites.downloadFolderHint': 'تنزيل هذا المجلد بكل ما فيه في ملف ‎.zip',
  'sites.downloadZip': 'تنزيل كملف ‎.zip',
  'sites.downloadFile': 'تنزيل',
  'sites.downloadTooBig': 'الحد الأقصى للتنزيل الواحد 4 جيجابايت من الملفات. نزّل مجلدات الموقع واحدًا تلو الآخر.',
  'sites.deleted': 'تم الحذف.',
  'sites.confirmDeleteFile': 'حذف {name}؟',
  'sites.confirmDeleteFolder': 'حذف المجلد {name}؟',
  'sites.confirmDeleteFileBody': 'سيتم حذف الملف نهائيًا، وسيعرض عنوانه «الصفحة غير موجودة».',
  'sites.confirmDeleteFolderBody': 'سيتم حذف كل ما فيه نهائيًا. عدد الملفات: {n}.',
  'sites.skipHidden': 'الملفات المخفية ليست جزءًا من الموقع.',
  'sites.skipType': 'لا يقبل الموقع هذا النوع من الملفات. الأنواع المقبولة: الصفحات والتنسيقات والسكربتات والصور والخطوط والبيانات والصوت والفيديو.',
  'sites.skipName': 'لا يمكن أن يحتوي الاسم على \\ : * ? " < > | # أو %، ولا أن يبدأ أو ينتهي بمسافة.',
  'sites.zipNotZip': 'هذا ليس ملف ‎.zip.',
  'sites.zipTooBig': 'ملف \u200E.zip هذا كبير جدًا ولا يمكن فتحه هنا (أكثر من 4 جيجابايت أو 65,535 ملفًا).',
  'sites.zipEncrypted': 'ملف \u200E.zip هذا محمي بكلمة مرور. أعد ضغط الموقع بدون كلمة مرور.',
  'sites.zipMethod': 'ملف \u200E.zip هذا مضغوط بطريقة لا يستطيع المتصفح فتحها. أعد ضغطه، أو ارفع المجلد.',
  'sites.zipBroken': 'ملف ‎.zip هذا تالف.',
  'sites.zipUnsupported': 'هذا المتصفح لا يستطيع فتح ملفات \u200E.zip. ارفع المجلد بدلًا من ذلك.',
  'sites.tooLarge': 'أكبر من {mb} ميجابايت، وهو أقصى حجم للملف.',
  'sites.uploadFailed': 'لم يتم الرفع',
  'sites.readFailed': 'لم نتمكن من قراءة الملفات التي أفلتها. حاول مرة ثانية.',
  'sites.reading': 'جاري قراءة الملفات…',
  'sites.dropHere': 'أفلت الملفات لرفعها إلى {folder}',
  'sites.uploading': 'جاري رفع {n}',
  'sites.uploadsDone': 'تم الرفع: {n}',
  'sites.skippedCount': 'تم التجاوز: {n}',
  'sites.failedCount': 'لم يتم الرفع: {n}',
  'sites.uploaded': 'تم الرفع',
  'sites.replaced': 'تم الاستبدال',
  'sites.skipped': 'تم تجاوزه',
  'sites.folderEmpty': 'اكتب اسم مجلد الموقع.',
  'sites.folderTooShort': 'حرفان على الأقل.',
  'sites.folderTooLong': '63 حرفًا على الأكثر.',
  'sites.folderShape': 'حروف إنجليزية صغيرة وأرقام وشرطات فقط، ولا يبدأ أو ينتهي بشرطة.',
  'sites.folderReserved': 'هذا الاسم محجوز لعنوان المواقع نفسه. اختر غيره.',
  'sites.folderTaken': 'هذا المجلد لموقع آخر. اختر غيره.',
  'sites.newTitle': 'موقع جديد',
  'sites.newSubtitle': 'موقع إلكتروني مستقل في مجلد خاص به، واسم المجلد هو آخر جزء من عنوان الموقع.',
  'sites.settingsTitle': '{name} — الإعدادات',
  'sites.create': 'إنشاء الموقع',
  'sites.created': 'تم إنشاء الموقع.',
  'sites.saved': 'تم الحفظ.',
  'sites.folder': 'المجلد',
  'sites.folderHint': 'آخر جزء من العنوان: حروف إنجليزية صغيرة وأرقام وشرطات.',
  'sites.addressWillBe': 'عنوانه:',
  'sites.folderMoves': 'تغيير المجلد ينقل الموقع، ويتوقف العنوان القديم عن العمل فورًا.',
  'sites.description': 'الوصف',
  'sites.descriptionHint': 'الغرض من الموقع، ليعرفه من يديره. ولا يراه الزوار أبدًا.',
  'sites.isLive': 'الموقع منشور',
  'sites.isLiveHint': 'عند إيقافه يعرض عنوانه «الصفحة غير موجودة»، وتبقى ملفاته هنا.',
  'sites.searchable': 'السماح لمحركات البحث بإدراجه',
  'sites.searchableHint': 'عند إيقافه تطلب كل صفحة من محركات البحث ألا تدرجها، وهذا مناسب لمسودة أو لموقع خاص بعميل واحد.',
  'sites.siteDeleted': 'تم حذف الموقع.',
  'sites.deleteTitle': 'حذف {name}',
  'sites.deleteSubmit': 'حذف نهائي',
  'sites.deleteWarning': 'سيتم حذف الموقع وكل ملفاته نهائيًا، ويتوقف عنوانه عن العمل فورًا. لا يمكن التراجع عن ذلك.',
  'sites.deleteConfirm': 'اكتب اسم مجلده، {folder}، لحذفه',
  'sites.inFolder': 'في {folder}',
  'sites.createFolder': 'فتح المجلد',
  'sites.folderName': 'اسم المجلد',
  'sites.newFolderHint': 'يظهر المجلد فارغًا: اسحب إليه ملفات أو أنشئ فيه ملفًا. ويبقى المجلد بمجرد أن يكون فيه ملف.',
  'sites.pathTaken': 'يوجد ملف أو مجلد في هذا المسار بالفعل.',
  'sites.moveTitle': 'إعادة تسمية أو نقل {name}',
  'sites.path': 'المسار في الموقع',
  'sites.pathHint': 'المجلدات ثم الاسم، وبينها /، مثل images/logo.png. ولن تتغير الروابط إليه في الصفحات تلقائيًا.',
  'sites.typeChanges': 'تغيير الامتداد يغيّر نوع الملف الذي يرسله الموقع للمتصفح.',
  'sites.fileGone': 'لم يعد هذا الملف موجودًا في الموقع.',
  'sites.fileSaved': 'تم حفظ الملف.',
  'sites.fileChanged': 'حفظ أحدهم هذا الملف بعد أن فتحته. افتحه من جديد لترى تغييره قبل أن تحفظ تغييرك.',
  'sites.unsaved': 'غير محفوظ',
  'sites.newFileHint': 'صفحة أو ملف تنسيق أو سكربت أو ملف نصي آخر، في المجلد الرئيسي للموقع.',
  'sites.fileName': 'اسم الملف',
  'sites.fileNameHint': 'مع امتداده: index.html أو style.css أو script.js.',
  'sites.notText': 'لا يكتب المحرر إلا ملفات نصية: \u200E.html و\u200E.css و\u200E.js و\u200E.json و\u200E.svg و\u200E.txt وما يشبهها.',
  'sites.content': 'المحتوى',
  'sites.editorHint': '{size} من 1 ميجابايت كحد أقصى. للحفظ اضغط Ctrl+S.',
  'analytics.button': 'الإحصاءات',
  'analytics.buttonHint': 'الزوار والصفحات والمصادر والروبوتات وفحص هذا الموقع',
  'analytics.title': 'الإحصاءات',
  'analytics.pageTitle': '{name} — الإحصاءات',
  'analytics.siteFiles': 'الملفات',
  'analytics.offline': 'هذا الموقع متوقف، فلا يمكن لأحد زيارته، وتبقى أرقامه كما هي حتى تعيد تشغيله من إعداداته.',
  'analytics.tab.traffic': 'حركة الزوار',
  'analytics.tab.requests': 'الطلبات',
  'analytics.tab.site': 'فحص الموقع',
  'analytics.privacy':
    'نحسب الزوار دون ملفات تعريف الارتباط ودون حفظ عناوينهم: نتعرف على كل زائر ليوم واحد برمز ننشئه من عنوانه ومتصفحه، ولا يمكن الرجوع منه إلى الزائر، لذلك من يعود غدًا نحسبه من جديد. أما الروبوتات، مثل محركات البحث ومعاينات الروابط والسكربتات، فنحسبها وحدها في «الطلبات».',
  'analytics.liveHint': 'الزوار الذين فتحوا صفحة خلال آخر {n} دقائق.',
  'analytics.liveNow': '{n} على الموقع الآن',
  'analytics.lastVisit': 'آخر زيارة {when}',
  'analytics.noVisitsYet': 'لا توجد زيارات بعد',
  'analytics.liveTitle': 'على الموقع الآن',
  'analytics.liveEmptyLast': 'لا أحد الآن. كانت آخر زيارة {when}، على {page}.',
  'analytics.rangeLabel': 'الفترة',
  'analytics.range.24h': '24 ساعة',
  'analytics.range.7d': '7 أيام',
  'analytics.range.30d': '30 يومًا',
  'analytics.range.90d': '90 يومًا',
  'analytics.range.12m': '12 شهرًا',
  'analytics.vsPrevious.24h': 'مقارنة بالـ 24 ساعة السابقة',
  'analytics.vsPrevious.7d': 'مقارنة بالأيام السبعة السابقة',
  'analytics.vsPrevious.30d': 'مقارنة بالـ 30 يومًا السابقة',
  'analytics.vsPrevious.90d': 'مقارنة بالـ 90 يومًا السابقة',
  'analytics.vsPrevious.12m': 'مقارنة بالـ 12 شهرًا السابقة',
  'analytics.nothingBefore': 'لا توجد بيانات سابقة للمقارنة',
  'analytics.noChange': 'بلا تغيير',
  'analytics.pointsChange': '{n} نقطة',
  'analytics.countingSince': 'بدأنا العد في {date}، ولا توجد زيارات مسجلة قبل ذلك.',
  'analytics.notCountingYet': 'لم نحسب أي زيارة بعد. من الآن سنحسب كل صفحة يفتحها الزوار، فافتح الموقع لترى أول زيارة.',
  'analytics.visitors': 'الزوار',
  'analytics.visits': 'الزيارات',
  'analytics.pageViews': 'مشاهدات الصفحات',
  'analytics.viewsPerVisit': 'صفحات لكل زيارة',
  'analytics.bounceRate': 'معدل الارتداد',
  'analytics.bounceHint': 'زيارات غادرت بعد صفحة واحدة',
  'analytics.visitDuration': 'مدة الزيارة',
  'analytics.visitDurationHint': 'في المتوسط، من أول صفحة في الزيارة إلى آخرها',
  'analytics.chartTitle': 'الزوار ومشاهدات الصفحات',
  'analytics.chartSubtitle': 'دون الروبوتات، بتوقيت {zone}.',
  'analytics.chartEmpty': 'لا توجد زيارات في هذه الفترة.',
  'analytics.none': 'لا شيء في هذه الفترة.',
  'analytics.showAll': 'عرض الكل ({n})',
  'analytics.showFewer': 'عرض أقل',
  'analytics.openPage': 'افتح {page} على الموقع',
  'analytics.pages': 'الصفحات',
  'analytics.pages.top': 'الأكثر مشاهدة',
  'analytics.pages.entry': 'صفحات الدخول',
  'analytics.pages.exit': 'صفحات الخروج',
  'analytics.timeHint':
    'الوقت: متوسط البقاء في الصفحة قبل الانتقال إلى الصفحة التالية في الزيارة نفسها. صفحات الدخول: حيث بدأت الزيارات، وكم زيارة غادرت منها فورًا. صفحات الخروج: حيث انتهت الزيارات.',
  'analytics.sources': 'المصادر',
  'analytics.sources.referrers': 'المصادر',
  'analytics.sources.campaigns': 'الحملات',
  'analytics.sourcesHint': 'من أين جاءت كل زيارة. الحملات هي روابط فيها utm_source وutm_medium وutm_campaign.',
  'analytics.direct': 'مباشر أو غير معروف',
  'analytics.directHint': 'كتابة العنوان مباشرة، أو المفضلة، أو تطبيق محادثة لا يذكر أي صفحة، أو رابط من صفحة تخفي عنوانها.',
  'analytics.noCampaigns': 'لا توجد روابط حملات في هذه الفترة. أضف \u200E?utm_source=…\u200E إلى الروابط التي تشاركها لتراها هنا.',
  'analytics.countries': 'الدول',
  'analytics.geoCredit': 'نعرف الدولة من عنوان الزائر، ولا نحفظ هذا العنوان. بيانات IP2Location LITE من lite.ip2location.com.',
  'analytics.unknown': 'غير معروف',
  'analytics.devices': 'الأجهزة',
  'analytics.devices.devices': 'الأجهزة',
  'analytics.devices.browsers': 'المتصفحات',
  'analytics.devices.systems': 'أنظمة التشغيل',
  'analytics.device.desktop': 'كمبيوتر',
  'analytics.device.mobile': 'هاتف',
  'analytics.device.tablet': 'جهاز لوحي',
  'analytics.inApp': 'متصفح داخل تطبيق',
  'analytics.languages': 'اللغات',
  'analytics.languagesHint': 'أول لغة يطلبها كل متصفح.',
  'analytics.hours': 'متى يأتي الزوار',
  'analytics.hoursHint': 'مشاهدات الصفحات حسب اليوم والساعة، بتوقيت {zone}.',
  'analytics.hoursShortRange': 'اختر 7 أيام أو أكثر لترى أيام الأسبوع.',
  'analytics.fewer': 'أقل',
  'analytics.more': 'أكثر',
  'analytics.cell': '{day} {hour}:00 — مشاهدات الصفحات: {n}',
  'analytics.col.page': 'الصفحة',
  'analytics.col.visitors': 'الزوار',
  'analytics.col.views': 'المشاهدات',
  'analytics.col.time': 'الوقت',
  'analytics.col.visits': 'الزيارات',
  'analytics.col.bounce': 'الارتداد',
  'analytics.col.source': 'المصدر',
  'analytics.col.share': 'النسبة',
  'analytics.col.campaign': 'المصدر / الوسيلة / الحملة',
  'analytics.col.path': 'العنوان',
  'analytics.col.kind': 'النوع',
  'analytics.col.times': 'عدد المرات',
  'analytics.col.lastSeen': 'آخر مرة',
  'analytics.col.robot': 'الروبوت',
  'analytics.col.file': 'الملف',
  'analytics.col.requests': 'الطلبات',
  'analytics.col.data': 'البيانات',
  'analytics.col.cached': 'من النسخة المحفوظة',
  'analytics.col.address': 'العنوان',
  'analytics.col.pages': 'الصفحات',
  'analytics.col.links': 'الروابط',
  'analytics.ms': '{n} ملي ثانية',
  'analytics.req.requests': 'الطلبات',
  'analytics.req.requestsHint': 'الصفحات والملفات التي تحمّلها، بما فيها طلبات الروبوتات',
  'analytics.req.data': 'البيانات المرسلة',
  'analytics.req.cached': 'من نسخة المتصفح',
  'analytics.req.cachedHint': 'كان الرد «لم يتغير»، فلم يرسل الموقع شيئًا من جديد',
  'analytics.req.speed': 'زمن الرد على الصفحات',
  'analytics.req.speedHint': 'نصف الردود خلال هذا الوقت، و95% منها خلال {ms}',
  'analytics.req.notFound': 'غير موجود',
  'analytics.req.notFoundHint': 'طلبها أشخاص ولم تكن موجودة',
  'analytics.req.robots': 'مشاهدات الروبوتات',
  'analytics.req.robotsHint': 'ملفات أخرى جلبتها: {n}',
  'analytics.req.daily': 'البيانات المرسلة يوميًا',
  'analytics.req.fileDays': 'الصفحات والملفات التي تحمّلها، بما فيها طلبات الروبوتات، من {from} إلى {to}.',
  'analytics.req.dailyRequests': 'الطلبات: {n}',
  'analytics.req.nothingSent': 'لم يرسل الموقع أي شيء في هذه الفترة.',
  'analytics.req.notFoundTitle': 'مطلوبة وغير موجودة',
  'analytics.req.notFoundNote':
    'صفحات وملفات طلبها الزوار وليست في الموقع، مثل رابط معطل أو عنوان قديم أو صورة مفقودة. ولا نعرض تخمينات الروبوتات.',
  'analytics.req.notFoundEmpty': 'لم يطلب أحد صفحة أو ملفًا غير موجود في هذه الفترة.',
  'analytics.req.kindPage': 'صفحة',
  'analytics.req.kindFile': 'ملف',
  'analytics.req.robotsTitle': 'الروبوتات ومعاينات الروابط',
  'analytics.req.robotsNote': 'محركات البحث وزواحف الذكاء الاصطناعي وأدوات المراقبة والسكربتات. ومعاينة الرابط تعني أن أحدًا شارك رابط الموقع في ذلك التطبيق.',
  'analytics.req.robotsEmpty': 'لم يزر الموقع أي روبوت في هذه الفترة.',
  'analytics.req.botPages': 'صفحات مختلفة: {n}',
  'analytics.req.filesTitle': 'الملفات المرسلة',
  'analytics.req.filesNote': 'التنسيقات والسكربتات والصور وغيرها، والأكثر طلبًا أولًا. «من النسخة المحفوظة»: استخدم المتصفح نسخته المحفوظة من الملف.',
  'analytics.req.filesEmpty': 'لم يرسل الموقع أي ملف في هذه الفترة.',
  'analytics.bot.search': 'محرك بحث',
  'analytics.bot.preview': 'معاينة رابط',
  'analytics.bot.ai': 'ذكاء اصطناعي',
  'analytics.bot.seo': 'أداة تحسين البحث',
  'analytics.bot.monitor': 'أداة مراقبة',
  'analytics.bot.script': 'سكربت',
  'analytics.bot.other': 'أخرى',
  'analytics.botName.noBrowser': 'بلا اسم متصفح',
  'analytics.botName.other': 'زاحف آخر',
  'analytics.botName.headless': 'متصفح بلا واجهة',
  'analytics.botName.script': 'سكربت',
  'analytics.botName.previewService': 'خدمة معاينة روابط',
  'analytics.check.reading': 'جاري قراءة صفحات الموقع…',
  'analytics.check.errors': 'المشكلات',
  'analytics.check.warnings': 'التحذيرات',
  'analytics.check.suggestions': 'الاقتراحات',
  'analytics.check.passed': 'الناجحة',
  'analytics.check.title': 'فحص الموقع',
  'analytics.check.scanned': 'الصفحات المقروءة: {pages} · ملفات التنسيق: {styles} · {when}',
  'analytics.check.again': 'إعادة الفحص',
  'analytics.check.skipped': 'ملفات لم نقرأها: {n}، لأنها أكبر من 1 ميجابايت أو تتجاوز أقصى عدد يقرؤه الفحص الواحد.',
  'analytics.check.more': 'و{n} أخرى',
  'analytics.check.front-page.title': 'الصفحة الرئيسية',
  'analytics.check.front-page.pass': 'ملف index.html في المجلد الرئيسي للموقع، ويفتحه عنوان الموقع.',
  'analytics.check.front-page.fail': 'لا يوجد ملف index.html في المجلد الرئيسي، لذلك يعرض عنوان الموقع «الصفحة غير موجودة».',
  'analytics.check.broken-links.title': 'روابط معطلة',
  'analytics.check.broken-links.pass': 'كل صفحة وصورة وتنسيق وسكربت تذكره الصفحات موجود في الموقع.',
  'analytics.check.broken-links.fail': 'روابط وملفات تذكرها الصفحات وليست في الموقع: {n}.',
  'analytics.check.outside-links.title': 'مسارات تخرج من الموقع',
  'analytics.check.outside-links.pass': 'لا يوجد مسار يبدأ بـ / فيخرج من مجلد الموقع.',
  'analytics.check.outside-links.fail': 'مسارات تبدأ بـ / فتخرج من مجلد الموقع: {n}. اكتبها نسبية، مثل css/site.css وليس \u200E/css/site.css.',
  'analytics.check.insecure.title': 'تحميل عبر http غير آمن',
  'analytics.check.insecure.pass': 'كل ما تحمّله الصفحات يأتي عبر https.',
  'analytics.check.insecure.fail': 'صور أو سكربتات أو تنسيقات تحمّلها الصفحات عبر http العادي: {n}. تحجبها المتصفحات أو تنبّه إليها في صفحة https.',
  'analytics.check.titles.title': 'عناوين الصفحات',
  'analytics.check.titles.pass': 'لكل صفحة عنوان.',
  'analytics.check.titles.fail': 'صفحات بلا <title>: {n}. يعرض تبويب المتصفح ونتائج البحث رابط الصفحة بدلًا منه.',
  'analytics.check.duplicate-titles.title': 'عناوين مكررة',
  'analytics.check.duplicate-titles.pass': 'لكل صفحة عنوانها الخاص.',
  'analytics.check.duplicate-titles.fail': 'عناوين تتكرر في عدة صفحات: {n}. لا تستطيع نتائج البحث التمييز بين هذه الصفحات.',
  'analytics.check.descriptions.title': 'الأوصاف',
  'analytics.check.descriptions.pass': 'لكل صفحة وصف meta.',
  'analytics.check.descriptions.fail': 'صفحات بلا وصف meta: {n}. فتكتب محركات البحث ومعاينات الروابط وصفًا من عندها.',
  'analytics.check.viewport.title': 'العرض على الهواتف',
  'analytics.check.viewport.pass': 'كل صفحة تضبط عرضها للهواتف (meta viewport).',
  'analytics.check.viewport.fail': 'صفحات بلا meta viewport: {n}. تعرضها الهواتف مصغّرة لتتسع في الشاشة.',
  'analytics.check.lang.title': 'اللغة',
  'analytics.check.lang.pass': 'كل صفحة تذكر لغتها.',
  'analytics.check.lang.fail': 'صفحات لا تذكر لغتها (<html lang>): {n}. تخمّنها قارئات الشاشة وأدوات الترجمة.',
  'analytics.check.alt.title': 'صور بلا وصف',
  'analytics.check.alt.pass': 'لكل صورة نص بديل.',
  'analytics.check.alt.fail': 'صور بلا نص بديل: {n}. تقرؤه قارئات الشاشة، ويظهر مكان الصورة إذا لم تظهر.',
  'analytics.check.icon.title': 'أيقونة التبويب',
  'analytics.check.icon.pass': 'كل صفحة تذكر أيقونتها.',
  'analytics.check.icon.fail': 'صفحات لا تذكر أيقونة (<link rel="icon">): {n}. ولا تظهر لها أيقونة في تبويب المتصفح.',
  'analytics.check.large-files.title': 'ملفات كبيرة',
  'analytics.check.large-files.pass': 'لا توجد صورة أو سكربت أو تنسيق أو خط أكبر من 1 ميجابايت.',
  'analytics.check.large-files.fail': 'ملفات أكبر من 1 ميجابايت: {n}. يكون تحميلها بطيئًا على الهاتف.',
  'analytics.check.not-found-page.title': 'صفحة «غير موجود» خاصة',
  'analytics.check.not-found-page.pass': 'للموقع صفحة 404.html خاصة به للعناوين غير الموجودة فيه.',
  'analytics.check.not-found-page.fail': 'لا يوجد ملف 404.html، لذلك يعرض أي عنوان خطأ رسالة «الصفحة غير موجودة» العادية.',
  'analytics.check.unused.title': 'ملفات غير مستخدمة',
  'analytics.check.unused.pass': 'كل ملف تذكره صفحة أو ملف تنسيق أو سكربت.',
  'analytics.check.unused.fail': 'ملفات لا تذكرها أي صفحة أو ملف تنسيق أو سكربت: {n}. قد تكون ملفات قديمة لم تعد لازمة.',
  'analytics.content.title': 'ممّ يتكون الموقع',
  'analytics.content.summary': 'الملفات: {files} · {size} · المجلدات: {folders}',
  'analytics.content.created': 'تم إنشاء الموقع في {when} بواسطة {name}.',
  'analytics.content.largest': 'أكبر الملفات',
  'analytics.content.pagesTitle': 'الصفحات ({n})',
  'analytics.content.noPages': 'لا توجد صفحات بعد',
  'analytics.content.pageTitle': 'العنوان',
  'analytics.content.noTitle': 'بلا عنوان',
  'analytics.content.thirdParty': 'يحمّل من عناوين أخرى',
  'analytics.content.thirdPartyHint': 'الخطوط والسكربتات والتحليلات والخرائط والفيديو التي تجلبها الصفحات من مواقع أخرى.',
  'analytics.content.thirdPartyNone': 'لا تحمّل الصفحات شيئًا من عناوين أخرى.',
  'analytics.content.http': 'http غير آمن',
  'analytics.content.outbound': 'روابط إلى مواقع أخرى',
  'analytics.content.outboundNone': 'لا توجد في الصفحات روابط إلى مواقع أخرى.',
  'analytics.kind.pages': 'الصفحات',
  'analytics.kind.styles': 'ملفات التنسيق',
  'analytics.kind.scripts': 'السكربتات',
  'analytics.kind.images': 'الصور',
  'analytics.kind.fonts': 'الخطوط',
  'analytics.kind.media': 'الصوت والفيديو',
  'analytics.kind.documents': 'المستندات',
  'analytics.kind.data': 'البيانات وغيرها',
  'analytics.ref.link': 'روابط',
  'analytics.ref.style': 'تنسيقات',
  'analytics.ref.script': 'سكربتات',
  'analytics.ref.image': 'صور',
  'analytics.ref.media': 'صوت وفيديو',
  'analytics.ref.frame': 'إطارات',
  'analytics.ref.font': 'خطوط',
  'analytics.ref.icon': 'أيقونات',
  'analytics.ref.data': 'بيانات',
  'analytics.ref.other': 'أخرى',
  'analytics.changes.title': 'التغييرات',
  'analytics.changes.subtitle': 'أحدث التغييرات على الموقع وملفاته، من سجل التدقيق.',
  'analytics.changes.none': 'لا توجد تغييرات مسجلة.',
  'analytics.changes.system': 'النظام',
  'analytics.change.created': 'تم إنشاء الموقع',
  'analytics.change.updated': 'تم تغيير الإعدادات',
  'analytics.change.added': 'تمت إضافة ملف',
  'analytics.change.replaced': 'تم استبدال ملف',
  'analytics.change.moved': 'تم نقل ملف',
  'analytics.change.deleted': 'تم حذف ملفات: {n}',
  'analytics.field.isLive': 'التشغيل',
  'analytics.field.searchable': 'محركات البحث',
  'settings.sites': 'المواقع',
  'settings.sitesPublishers': 'من يمكنه نشر المواقع',
  'settings.sitesPublishersNote':
    'ينشر مديرو النظام المواقع ويديرونها كلها. اختر من يمكنه النشر أيضًا: أقسامًا كاملة أو موظفين بأسمائهم. يدير كل منهم المواقع التي ينشئها، وإذا أزلته من القائمة تبقى مواقعه منشورة ويديرها مدير النظام.',
  'settings.sitesDepartments': 'الأقسام',
  'settings.sitesDepartmentsHint': 'يمكن لكل من في القسم المختار أن ينشر، وكذلك من ينضم إليه لاحقًا.',
  'settings.sitesNoDepartments': 'لا توجد أقسام بعد.',
  'settings.sitesPeople': 'الموظفون',
  'settings.sitesPeopleMax': '{n} شخصًا على الأكثر.',
  'settings.sitesPeopleHint': 'تختارهم بأسمائهم، أيًّا كان قسمهم.',
  'settings.sitesAddress': 'ننشر المواقع على',
  'employees.del.sites': 'المواقع التي يديرها',
  'settings.executive': 'اللوحة التنفيذية',
  'exec.title': 'اللوحة التنفيذية — dashboard.viganium.net',
  'exec.intro': 'بوابة للقراءة فقط تعرض مالية الشركة وعملها وموظفيها ومشاريعها، مع ملخصات الذكاء الاصطناعي. وتستخدم حسابات لوحة الإدارة وكلمات مرورها نفسها.',
  'exec.rule': 'لا يسجّل الدخول إليها إلا مدير نظام نشط حصل على صلاحية الوصول للوحة من هنا، والشرطان مطلوبان دائمًا. ولا تكفي صلاحية المالية أو سجل الشريك أبدًا، ولا يحصل أحد على الصلاحية تلقائيًا.',
  'exec.open': 'فتح اللوحة التنفيذية',
  'exec.accessTitle': 'صلاحية الوصول للوحة',
  'exec.accessSubtitle': 'كل مديري النظام النشطين، وكل من كانت لديه الصلاحية من قبل. يسري منح الصلاحية وسحبها فورًا.',
  'exec.searchPeople': 'ابحث بالاسم أو البريد الإلكتروني',
  'exec.noPeople': 'لا يوجد أحد لعرضه',
  'exec.noPeopleHint': 'يمكن منح صلاحية الوصول للوحة لمديري النظام النشطين فقط.',
  'exec.col.role': 'الدور في لوحة الإدارة',
  'exec.col.email': 'البريد الإلكتروني',
  'exec.col.access': 'صلاحية الوصول للوحة',
  'exec.col.grantedBy': 'منحها',
  'exec.col.lastSignIn': 'آخر دخول للوحة',
  'exec.col.sessions': 'الجلسات المفتوحة',
  'exec.state.GRANTED': 'ممنوحة',
  'exec.state.NOT_GRANTED': 'غير ممنوحة',
  'exec.state.ENDED': 'منتهية',
  'exec.stale': 'لم يعد مدير نظام نشطًا، لذلك نرفض دخوله وتنتهي صلاحيته عند طلبه التالي.',
  'exec.inactive': 'غير نشط',
  'exec.event.GRANTED': 'تم المنح',
  'exec.event.REVOKED': 'تم السحب',
  'exec.event.ROLE_REMOVED': 'انتهت: لم يعد مدير نظام',
  'exec.event.DEACTIVATED': 'انتهت: تم إيقاف الحساب',
  'exec.event.INELIGIBLE': 'انتهت: لم يعد مؤهلًا',
  'exec.grant': 'منح الصلاحية',
  'exec.revoke': 'سحب',
  'exec.back': 'العودة إلى الإعدادات',
  'exec.grantTitle': 'منح صلاحية الوصول للوحة',
  'exec.grantSubtitle': 'يسجّل الدخول إلى اللوحة التنفيذية ببريده الإلكتروني وكلمة مروره في لوحة الإدارة.',
  'exec.grantReason': 'السبب (اختياري)',
  'exec.grantReasonHint': 'نحفظه مع المنح وفي سجل التدقيق.',
  'exec.granted': 'تم منح {name} صلاحية الوصول للوحة.',
  'exec.alreadyGranted': 'لديه صلاحية الوصول للوحة بالفعل.',
  'exec.notEligible': 'يمكن منح صلاحية الوصول للوحة لمدير نظام نشط فقط.',
  'exec.revokeTitle': 'سحب صلاحية الوصول للوحة',
  'exec.revokeSubtitle': 'تنتهي جلساته في اللوحة فورًا، وتبقى صلاحياته في لوحة الإدارة والمالية كما هي.',
  'exec.revokeReason': 'السبب',
  'exec.revokeReasonHint': 'مطلوب، ونحفظه مع المنح وفي سجل التدقيق.',
  'exec.revoked': 'تم سحب صلاحية الوصول للوحة من {name}.',
  'exec.historyTitle': 'السجل',
  'exec.settingsTitle': 'إعدادات اللوحة',
  'exec.settingsSubtitle': 'السارية الآن: النسخة {v} · آخر نسخة محفوظة: النسخة {latest}. كل حفظ ينشئ نسخة جديدة تسري من يومها، ولا نعيد حساب التقارير النهائية أبدًا.',
  'exec.saved': 'تم حفظ النسخة {v}، وتسري من {day}.',
  'exec.scoring': 'أوزان أداء العمل',
  'exec.scoringHint': 'قيم افتراضية للبداية، وليست معيارًا تم التحقق منه. يجب أن يكون مجموع الأبعاد المحتسبة 100. ولا تدخل في الدرجة أبدًا: الحضور والورديات والورديات الحرة والإجازات ووقت الخمول والأجر والنقاط.',
  'exec.dim.completion': 'الإنجاز',
  'exec.dim.deadline': 'المواعيد النهائية',
  'exec.dim.quality': 'المراجعة والجودة',
  'exec.dim.tickets': 'التذاكر ومستوى الخدمة',
  'exec.dim.collaboration': 'التعاون',
  'exec.enabled': 'محتسب',
  'exec.sum': 'الإجمالي: {n}% (يجب أن يكون 100%)',
  'exec.minEvents': 'أقل عدد من أحداث العمل',
  'exec.minEventsHint': 'إذا كان العدد أقل من ذلك تظهر الدرجة «لا توجد أدلة كافية».',
  'exec.minWeight': 'أقل وزن قابل للقياس (%)',
  'exec.minWeightHint': 'نسبة الوزن التي يجب أن تكون لها أدلة.',
  'exec.reviewHours': 'مدة انتظار المراجعة (ساعات)',
  'exec.reviewHoursHint': 'المراجعة التي تنتظر أكثر من ذلك تنقص من درجة المراجع.',
  'exec.label.excellent': 'ممتاز من',
  'exec.label.veryGood': 'جيد جدًا من',
  'exec.label.good': 'جيد من',
  'exec.overrides': 'أوزان الأقسام',
  'exec.overridesHint': 'تقيّم أوزان القسم الخاصة الموظفين الذين قسمهم الأساسي هو هذا القسم.',
  'exec.addOverride': 'إضافة قسم',
  'exec.health': 'صحة الشركة',
  'exec.healthHint': 'أوزان المحاور (يجب أن يكون مجموعها 100). المحور الذي ليست لديه بيانات كافية نستبعده، ولا نحسبه صفرًا أبدًا.',
  'exec.pillar.financial': 'المالية',
  'exec.pillar.work': 'عمل الفريق',
  'exec.pillar.delivery': 'تسليم المشاريع',
  'exec.pillar.service': 'خدمة العملاء ومستوى الخدمة',
  'exec.health.minMeasurableWeightPercent': 'أقل وزن قابل للقياس (%)',
  'exec.health.minScoredEmployees': 'أقل عدد من الموظفين الذين لهم درجات',
  'exec.health.minTickets': 'أقل عدد من التذاكر',
  'exec.health.minDatedTasks': 'أقل عدد من مهام المشاريع التي لها موعد تسليم',
  'exec.risks': 'حدود المخاطر',
  'exec.risk.blockedQTaskDays': 'مهمة الطابور معلّقة منذ (أيام)',
  'exec.risk.stalledWorkflowDays': 'سير عمل متوقف منذ (أيام)',
  'exec.risk.contractExpiryDays': 'ينتهي العقد خلال (أيام)',
  'exec.risk.overdueReceivableSharePercent': 'حصة المتأخر من المستحقات (%)',
  'exec.risk.expenseSpikePercent': 'ارتفاع المصروفات عن الفترة السابقة (%)',
  'exec.risk.receivableConcentrationPercent': 'حصة عميل واحد من المستحقات (%)',
  'exec.ai': 'شروح الذكاء الاصطناعي',
  'exec.aiNoticeTitle': 'قبل تشغيل الذكاء الاصطناعي',
  'exec.aiNotice': 'عند تشغيله نرسل إلى OpenAI بيانات وصفية عن العمل، مجمّعة ومنسوبة إلى أصحابها، لكتابة الملخصات: أرقام وأعداد وتواريخ ومعرّفات مستعارة، ولا نرسل أبدًا الأسماء أو العناوين أو الرسائل أو المكالمات أو الرواتب أو أسباب حركات المحفظة. أنجز أولًا إخطار الموظفين وأخذ موافقاتهم. الذكاء الاصطناعي يشرح فقط، ولا يمكنه تغيير أي رقم أو درجة.',
  'exec.aiServerOn': 'يسمح الخادم بالذكاء الاصطناعي (EXECUTIVE_AI_ENABLED)',
  'exec.aiServerOff': 'الذكاء الاصطناعي متوقف على الخادم (EXECUTIVE_AI_ENABLED ليست true)',
  'exec.aiKeySet': 'تم إعداد مفتاح OpenAI',
  'exec.aiKeyMissing': 'لا يوجد مفتاح OpenAI على الخادم',
  'exec.aiBudgetServer': 'ميزانية الخادم {n} رمز شهريًا',
  'exec.aiEnabled': 'كتابة شروح الذكاء الاصطناعي',
  'exec.aiAcknowledge': 'أؤكد أنه تم إخطار الموظفين وأخذ موافقاتهم على إرسال هذه البيانات الوصفية إلى OpenAI.',
  'exec.aiConsentAt': 'تم التأكيد في {at}.',
  'exec.aiModel': 'النموذج',
  'exec.aiModelDefault': 'النموذج الافتراضي للخادم ({model})',
  'exec.aiBudget': 'ميزانية الرموز الشهرية',
  'exec.aiBudgetHint': 'اتركها فارغة لاستخدام ميزانية الخادم. وإذا نفدت، تنتظر الشروح الجديدة حتى الشهر التالي.',
  'exec.aiPriceIn': 'السعر التقديري للمدخلات (لكل مليون رمز)',
  'exec.aiPriceOut': 'السعر التقديري للمخرجات (لكل مليون رمز)',
  'exec.aiPriceAsOf': 'الأسعار بتاريخ',
  'exec.aiPriceHint': 'لتقدير التكلفة فقط، وإذا تركته فارغًا فلن تظهر أي تكلفة.',
  'exec.aiReport.daily': 'الملخص اليومي',
  'exec.aiReport.weekly': 'الملخص الأسبوعي',
  'exec.aiReport.monthly': 'التقرير الشهري',
  'exec.aiReport.employees': 'الشروح الشهرية للموظفين',
  'exec.dailyAt': 'التقرير اليومي في',
  'exec.dailyAtHint': 'بتوقيت الشركة، عن اليوم السابق.',
  'exec.monthlyAt': 'التقرير الشهري في (يوم 1 من كل شهر)',
  'exec.settleDays': 'الاعتماد النهائي بعد (أيام)',
  'exec.settleDaysHint': 'يصبح التقرير المبدئي نهائيًا بعد مرور هذه المدة على انتهاء فترته.',
  'exec.weekly': 'التقارير الأسبوعية',
  'exec.retainSnapshots': 'الاحتفاظ بالتقارير (أشهر)',
  'exec.retainHint': 'اكتب 0 للاحتفاظ بها دائمًا.',
  'exec.retainExports': 'الاحتفاظ بسجل التصدير (أشهر)',
  'exec.effectiveFrom': 'يسري من',
  'exec.effectiveFromHint': 'اليوم أو يوم لاحق.',
  'exec.history': 'النسخ',
  'exec.historyRow': 'النسخة {v} · من {day} · {who} · {at}',
  'exec.problem.WHOLE_PERCENT': 'عدد صحيح من 0 إلى 100',
  'exec.problem.ENABLED_WEIGHTS_SUM_100': 'يجب أن يكون مجموع الأوزان المحتسبة 100',
  'exec.problem.WEIGHTS_SUM_100': 'يجب أن يكون مجموع الأوزان 100',
  'exec.problem.ONE_DIMENSION_AT_LEAST': 'احتسب بعدًا واحدًا على الأقل',
  'exec.problem.DESCENDING_PERCENTS': 'ممتاز > جيد جدًا > جيد',
  'exec.problem.CONSENT_REQUIRED': 'أكّد الإخطار لتشغيل الذكاء الاصطناعي',
  'exec.problem.UNKNOWN_MODEL': 'نموذج يوفّره الخادم',
  'exec.problem.TIME': 'وقت (HH:MM)',
  'exec.problem.DUPLICATE': 'كل قسم مرة واحدة',
};

const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = { en, ar };

interface I18nContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  /** Picks the Arabic field when the UI is Arabic and one exists. */
  localized: (row: { name?: string | null; nameAr?: string | null; fullName?: string; fullNameAr?: string | null } | null | undefined) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const LOCALE_KEY = 'viganium.locale';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(
    () => (localStorage.getItem(LOCALE_KEY) as Locale | null) ?? 'en',
  );

  const dir: 'ltr' | 'rtl' = locale === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    localStorage.setItem(LOCALE_KEY, locale);
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      let text: string = DICTIONARIES[locale][key] ?? en[key] ?? key;
      // Every copy of a placeholder, as the dashboard does: "the first {n}" repeats one.
      if (vars) for (const [k, v] of Object.entries(vars)) text = text.split(`{${k}}`).join(String(v));
      return text;
    },
    [locale],
  );

  const localized = useCallback<I18nContextValue['localized']>(
    (row) => {
      if (!row) return '—';
      if (locale === 'ar') return row.nameAr || row.fullNameAr || row.name || row.fullName || '—';
      return row.name || row.fullName || '—';
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, dir, setLocale, t, localized }), [locale, dir, setLocale, t, localized]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>.');
  return ctx;
}

/** Enum labels come from the API as SCREAMING_SNAKE; render them readably in both languages. */
const ENUM_AR: Record<string, string> = {
  // Ticket status
  NEW: 'جديدة', ASSIGNED: 'مسندة', IN_PROGRESS: 'قيد التنفيذ', WAITING_REQUESTER: 'بانتظار مقدّم الطلب',
  RESOLVED: 'تم الحل', CLOSED: 'مغلقة', REOPENED: 'أعيد فتحها', CANCELLED: 'ملغاة',
  // Task status
  TO_DO: 'قيد الانتظار', BLOCKED: 'معلّقة', REVIEW: 'مراجعة', DONE: 'منجزة',
  // Q Task
  QUEUED: 'في الطابور', AVAILABLE: 'متاحة', SKIPPED: 'متخطاة', CONVERTED: 'تحوّلت إلى مهام',
  // Priority
  LOW: 'منخفضة', MEDIUM: 'متوسطة', HIGH: 'عالية', URGENT: 'عاجلة',
  // Shifts
  MORNING: 'صباحي', NIGHT: 'ليلي', CUSTOM: 'مخصص', OFF: 'إجازة',
  // Project
  DRAFT: 'مسودة', ACTIVE: 'نشط', TESTING: 'قيد الاختبار', ON_HOLD: 'متوقف', SUPPORT: 'قيد الدعم', COMPLETED: 'مكتمل', ARCHIVED: 'مؤرشف',
  // Payroll / finance
  CALCULATED: 'محتسب', APPROVED: 'معتمد', PAID: 'مدفوع', LOCKED: 'مقفل',
  PENDING: 'قيد الانتظار', APPLIED_TO_PAYROLL: 'مطبّق على الراتب',
  ISSUED: 'صادرة', PARTIALLY_PAID: 'مدفوعة جزئيًا', OVERDUE: 'متأخرة',
  RECEIVABLE: 'مستحقة لنا', PAYABLE: 'مستحقة علينا', INCOMING: 'واردة', OUTGOING: 'صادرة',
  // Violations
  VIEW_SLA_MISSED: 'تجاوز مهلة الاطلاع',
  ASSIGNMENT_SLA_MISSED: 'تجاوز مهلة الإسناد',
  DEPARTMENT_VIEW_SLA_BREACH: 'تجاوز مهلة الاطلاع على مستوى القسم',
  OPEN: 'مفتوحة', VOIDED: 'ملغاة',
  // Work logs
  SUBMITTED: 'مرسل', REJECTED: 'مرفوض',
  // Member type
  MEMBER: 'عضو', LEADER: 'قائد',
  // CR-001 — client status and the source of a payment's exchange rate (§28).
  INACTIVE: 'غير نشط',
  MANUAL: 'يدوي', BANK: 'بنك', EXCHANGE_OFFICE: 'مكتب صرافة', OTHER: 'أخرى',
  // Attendance, email and snapshot reasons
  // The Desktop Agent: presence and why someone is Not working.
  NOT_WORKING: 'لا يعمل', IDLE_INACTIVITY: 'خامل', DEVICE_LOCKED: 'الشاشة مقفلة', AGENT_DISCONNECTED: 'التطبيق غير متصل', DEVICE_SLEEPING: 'الجهاز في وضع السكون',
  WORKING: 'في العمل', ON_BREAK: 'في استراحة', ENDED: 'انتهت', ON_LEAVE: 'في إجازة', ABSENT: 'غائب', NOT_STARTED: 'لم يبدأ', UPCOMING: 'لم يحن موعده', NO_SCHEDULE: 'بلا جدول', SENT: 'تم الإرسال', FAILED: 'فشلت', ELIGIBLE: 'مشمول', ON_SHIFT: 'في الوردية', OFF_DAY: 'يوم راحة', NOT_ON_SHIFT: 'خارج الوردية', SHIFT_NOT_STARTED: 'لم يبدأ ورديته', OUTSIDE_SHIFT_HOURS: 'خارج ساعات الوردية', USER_INACTIVE: 'غير نشط', NO_TICKET_HANDLING_PERMISSION: 'لا يملك صلاحية التذاكر', EMPLOYEE_EXCLUDED_BY_OVERRIDE: 'مستثنى', APPROVED_ABSENCE: 'غياب معتمد', NO_PROJECT_ACCESS: 'لا يملك صلاحية المشروع', SUPPORT_MODE: 'وضع الدعم',
};

export function enumLabel(value: string | null | undefined, locale: Locale): string {
  if (!value) return '—';
  if (locale === 'ar' && ENUM_AR[value]) return ENUM_AR[value];
  return value
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

/** «مشروع» is masculine, where the shared labels are a ticket's and a task's («ملغاة»). */
const PROJECT_STATUS_AR: Record<string, string> = { CANCELLED: 'ملغى' };

/** A project's status, in the reader's language. */
export function projectStatusLabel(value: string | null | undefined, locale: Locale): string {
  if (value && locale === 'ar' && PROJECT_STATUS_AR[value]) return PROJECT_STATUS_AR[value];
  return enumLabel(value, locale);
}
